import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { auth, db, secondaryAuth, messagingPromise } from './firebase';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getToken, onMessage } from 'firebase/messaging';
import {
  onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut as firebaseSignOut, signInAnonymously, sendPasswordResetEmail, sendEmailVerification,
  EmailAuthProvider, reauthenticateWithCredential, updatePassword
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, collection, addDoc, updateDoc, deleteDoc, deleteField, serverTimestamp, orderBy, query, limit, where, getDocs, writeBatch } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const HORIA_ICON_SRC = '/icon-512.png?v=3'; // icono real de Horidia (ya generado en public/), usado en cabecera, login y planes

// Clave pública (VAPID) de notificaciones push, generada en Firebase Console →
// Configuración del proyecto → Cloud Messaging → Configuración web. No es un
// secreto — está pensada para ir en el código del cliente, al contrario que la
// clave privada, que solo usa el servidor (Cloud Functions) y nunca va aquí.
const VAPID_KEY = 'BCTDvj2BZe_uIsWV5lvjR1moPuDYaQpyuddcKKYBRKLjcqA7oRkkn4I8OPiBcdpsQD73B28S1ikTXMDCcdQLMPo';

// Firebase Storage, sobre la misma app que ya usan Firestore/Auth (no hace falta configurarlo
// aparte). Se usa solo para la foto de la carta (pestaña "Menú y Mapa") — el resto de la app
// sigue guardando imágenes pequeñas (el logo) como base64 dentro de Firestore.
const storage = getStorage(db.app);

// Los correos que manda Firebase (verificar email, recuperar contraseña) salen en español.
auth.languageCode = 'es';

// VERIFICACIÓN DE EMAIL (19 oct 2026). Quien crea una cuenta de Encargado o de Dueño tiene que
// confirmar su correo antes de entrar: así, si se equivoca al escribirlo, no se queda con una
// cuenta a la que nadie puede volver a entrar ni recuperar. Solo afecta a cuentas con email real
// creadas a partir de esta fecha: las que ya existían (Duo Tapas, las tuyas de prueba) entran
// como siempre. Las cuentas con PIN (correo interno ".horiaapp.local") y las anónimas no se tocan.
// OJO: es una barrera de la app, pensada para evitar errores al escribir el correo; no sustituye
// a las reglas de seguridad de Firestore.
const VERIFICAR_EMAIL_DESDE = Date.parse('2026-10-19T00:30:00+02:00');
const necesitaVerificarEmail = (user) => {
  if (!user || user.isAnonymous || !user.email || user.email.endsWith('.horiaapp.local')) return false;
  if (user.emailVerified) return false;
  const creada = Date.parse(user.metadata && user.metadata.creationTime);
  return Number.isFinite(creada) && creada >= VERIFICAR_EMAIL_DESDE;
};
const CONTRASENA_MIN = 8; // longitud mínima al crear una cuenta de Encargado o de Dueño

// Fecha de HOY en hora de España ('YYYY-MM-DD'). Antes se usaba toISOString(), que va en hora
// de Londres (UTC): entre las 00:00 y las 02:00 en España —justo al cerrar— daba el día de ayer,
// y el día 1 de cada mes a esas horas, el mes anterior.
const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// =====================================================================================
// ESTADO DE LA CUENTA: PRUEBA, SUSCRIPCIÓN Y BLOQUEO (19 oct 2026, con Horidia Panel)
//
// Decide si el bar puede entrar y hasta qué día. Es una COPIA EXACTA de estadoCuenta() en
// functions/panel.js (el panel usa esa): si se cambia una, hay que cambiar la otra.
// Campos del documento del restaurante que cuentan (los escribe solo el panel de Fran, las
// reglas de Firestore no dejan que el bar los toque):
//   creadoEn (fecha de alta) · cuentaActivada · pagadoHasta 'AAAA-MM-DD' · pruebaHasta
//   'AAAA-MM-DD' · bloqueadaAMano.
// Las fechas van por DÍAS en hora de España: alta el día 10 → último día de prueba el 23 → el
// 24, a las 00:00, salen las pantallas de pago (antes se contaban horas desde el alta).
// =====================================================================================
const DIAS_PRUEBA_GRATIS = 14;
const FMT_FECHA_MADRID = (() => {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }); }
  catch (_) { return null; }
})();
const fechaMadridCuenta = (fecha = new Date()) => {
  if (FMT_FECHA_MADRID) {
    const s = FMT_FECHA_MADRID.format(fecha);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  }
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
};
const esFechaTextoCuenta = (s) => {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const f = new Date(Date.UTC(y, m - 1, d));
  return f.getUTCFullYear() === y && f.getUTCMonth() === m - 1 && f.getUTCDate() === d;
};
const utcDeFechaCuenta = (s) => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const sumarDiasCuenta = (s, n) => new Date(utcDeFechaCuenta(s) + n * 86400000).toISOString().slice(0, 10);
const diasEntreCuenta = (a, b) => Math.round((utcDeFechaCuenta(b) - utcDeFechaCuenta(a)) / 86400000);
const fechaJSDe = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  return null;
};
const finDePruebaCuenta = (d) => {
  if (esFechaTextoCuenta(d.pruebaHasta)) return d.pruebaHasta;
  const alta = fechaJSDe(d.creadoEn);
  if (!alta) return null;
  return sumarDiasCuenta(fechaMadridCuenta(alta), DIAS_PRUEBA_GRATIS - 1);
};
// estado: 'desactivada' | 'pagada' | 'activa' | 'prueba' | 'vencida' | 'caducada' | 'sinFecha'
// bloqueada: true → pantallas de pago · ultimoDia: último día con acceso · quedan: días que
// quedan contando hoy (el último día, 1) · tipo: 'prueba' | 'suscripcion' (para los textos).
const estadoCuentaBar = (d, hoy) => {
  d = d || {};
  if (d.bloqueadaAMano === true) return { estado: 'desactivada', bloqueada: true, ultimoDia: null, quedan: 0, tipo: 'suscripcion' };
  const pagado = esFechaTextoCuenta(d.pagadoHasta) ? d.pagadoHasta : null;
  if (pagado && hoy <= pagado) return { estado: 'pagada', bloqueada: false, ultimoDia: pagado, quedan: diasEntreCuenta(hoy, pagado) + 1, tipo: 'suscripcion' };
  if (!pagado && d.cuentaActivada === true) return { estado: 'activa', bloqueada: false, ultimoDia: null, quedan: null, tipo: 'suscripcion' };
  const fin = finDePruebaCuenta(d);
  if (fin && hoy <= fin) return { estado: 'prueba', bloqueada: false, ultimoDia: fin, quedan: diasEntreCuenta(hoy, fin) + 1, tipo: 'prueba' };
  if (pagado) return { estado: 'vencida', bloqueada: true, ultimoDia: pagado, quedan: 0, tipo: 'suscripcion' };
  if (!fin) return { estado: 'sinFecha', bloqueada: false, ultimoDia: null, quedan: null, tipo: 'prueba' };
  return { estado: 'caducada', bloqueada: true, ultimoDia: fin, quedan: 0, tipo: 'prueba' };
};
// "viernes 23 de octubre"
const DIAS_SEMANA_CUENTA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_CUENTA = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fechaLargaCuenta = (s) => {
  if (!esFechaTextoCuenta(s)) return '';
  const f = new Date(utcDeFechaCuenta(s));
  return `${DIAS_SEMANA_CUENTA[f.getUTCDay()]} ${f.getUTCDate()} de ${MESES_CUENTA[f.getUTCMonth()]}`;
};
// Las Cloud Functions de invitaciones (crearInvitacion, misInvitaciones, comprobarInvitacion,
// usarInvitacion) viven en functions/panel.js. El código se escribe "XXXX-XXXX".
const normalizarCodigoInvitacion = (c) => String(c || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);

// Reduce una foto de la carta (que en un móvil puede pesar 3-5 MB) a un JPEG ligero antes de
// subirla a Storage: la redimensiona a un máximo de 1600px de lado y la comprime al 80% de
// calidad. Sin esto, subir varias fotos seguidas desde el móvil sería lento y caro.
const comprimirFotoCarta = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const MAX = 1600;
    let { width, height } = img;
    if (width > height && width > MAX) { height = Math.round(height * MAX / width); width = MAX; }
    else if (height > MAX) { width = Math.round(width * MAX / height); height = MAX; }
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('No se pudo procesar la imagen')), 'image/jpeg', 0.8);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
  img.src = url;
});


// ===== PALETA — CLARA ORIGINAL, tal como estaba antes de los experimentos de hoy =====
const C = {
  primary: '#7C3AED', primaryLight: '#8B5CF6', primaryPale: '#EDE9FE',
  primaryFaint: '#F5F3FF', primaryBorder: '#D8B4FE', primaryDark: '#4C1D95',
  danger: '#DC2626', dangerBg: '#FEE2E2', success: '#059669', successBg: '#D1FAE5',
  warning: '#D97706', warningBg: '#FEF3C7', text: '#1F2937', textMuted: '#6B7280',
  textLight: '#9CA3AF', bg: '#F8F7FF', white: '#FFFFFF', border: '#E5E7EB',
  accent: '#D97706', accentLight: '#FCD34D',
  // Cian, solo para diferenciar visualmente el botón "Mi plan" del resto de acciones del negocio.
  cyan: '#0891B2', cyanBg: '#ECFEFF', cyanBorder: '#67E8F9', cyanDark: '#0E7490',
};

// Pequeño componente reutilizable para el nombre "Horidia": "Hori" en morado,
// "dia" en naranja — "hora" + "día" ("Tus horarios día a día").
const Wordmark = ({ size = 30, weight = 800, dark = false }) => (
  <span style={{ fontSize: `${size}px`, fontWeight: weight, letterSpacing: '-0.5px', fontFamily: "'Nunito', sans-serif" }}>
    <span style={{ color: dark ? C.white : C.primaryDark }}>Hori</span>
    <span style={{ color: dark ? C.accentLight : C.accent }}>dia</span>
  </span>
);

// ICONOS PROPIOS de las pestañas (2 oct 2026): dibujados aquí mismo como SVG de trazo fino, sin
// ninguna librería externa — nada de fuera entra en tu código. Heredan el color del texto
// (currentColor), así que se vuelven blancos solos en la pestaña activa.
const TAB_ICON_PATHS = {
  empleados: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><circle cx="17" cy="9" r="2.4" /><path d="M17 14.2c2.4.3 4 2.2 4 4.8" /></>,
  horario: <><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" /><path d="M8 14h.01M12 14h.01M16 14h.01M8 17.2h.01M12 17.2h.01" /></>,
  solicitudes: <><rect x="3" y="5.5" width="18" height="13" rx="3" /><path d="M3.5 8l8.5 6 8.5-6" /></>,
  vacaciones: <><circle cx="12" cy="12" r="4" /><path d="M12 2.5V5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" /></>,
  bajas: <path d="M9 3.5h6V9h5.5v6H15v5.5H9V15H3.5V9H9z" />,
  turnos: <><path d="M9 6.5h11.5M9 12h11.5M9 17.5h11.5" /><path d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" /></>,
  chat: <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3.5V17H6.5A2.5 2.5 0 0 1 4 14.5z" />,
  historial: <><path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" /><path d="M3.5 4.5v4h4" /><path d="M12 7.5V12l3 2" /></>,
  gastos: <><circle cx="12" cy="12" r="8.5" /><path d="M15 8.8a4 4 0 1 0 0 6.4" /><path d="M8 11h5.5M8 13.2h5.5" /></>,
  carta: <><path d="M6 3v5a2.5 2.5 0 0 0 5 0V3" /><path d="M8.5 10.5V21" /><path d="M17 21V3c-2.2 1.6-3.5 4.4-3.5 7.5V14H17" /></>,
};
// Color de cada icono cuando su pestaña NO está seleccionada (2 oct 2026, pedido por Fran: colores
// clásicos, sin cambiar el dibujo). En la pestaña seleccionada el icono se vuelve blanco para
// leerse sobre el fondo morado.
const TAB_ICON_COLORS = {
  empleados: '#2563EB',    // azul
  horario: '#16A34A',      // verde
  solicitudes: '#EA580C',  // naranja
  vacaciones: '#0891B2',   // cian (el mismo de Vacaciones en el horario)
  bajas: '#DC2626',        // rojo
  turnos: '#7C3AED',       // morado
  chat: '#DB2777',         // rosa
  historial: '#475569',    // gris pizarra
  gastos: '#EAB308',       // amarillo
  carta: '#D97706',        // ámbar (solo la ve el empleado)
};
const TabIcon = ({ name, size = 24, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0, color: color || 'inherit' }}>
    {TAB_ICON_PATHS[name]}
  </svg>
);
// Numerito rojo-naranja en la esquina de una pestaña (solicitudes pendientes, mensajes sin leer).
const TabBadge = ({ n }) => n > 0 ? (
  <span style={{ position: 'absolute', top: '5px', right: '7px', background: '#F59E0B', color: '#FFFFFF', borderRadius: '10px', minWidth: '18px', height: '18px', padding: '0 5px', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{n}</span>
) : null;

// =====================================================================================
// PANTALLAS DE FIN DE PRUEBA GRATUITA (3 oct 2026, diseñadas con Fran)
//
// Cuando pasan los 14 días y la cuenta no está activada, se ve primero un aviso ("Se ha
// terminado la prueba gratuita de 14 días" + botón "Continuar con Horidia") y, al pulsarlo, una
// pantalla de gracias con la cuota mensual y dos métodos de pago: Bizum (cian) y transferencia
// (verde). El número de Bizum o el IBAN solo aparecen al elegir el método. No hay botón de
// "ya he pagado": Fran ve el pago en su banco (con el nombre del local en el concepto) y activa
// la cuenta a mano poniendo `cuentaActivada: true` en la consola de Firebase.
//
// DATOS DE COBRO — LO ÚNICO QUE HAY QUE EDITAR AQUÍ:
//  · PAGO_BIZUM_TELEFONO: el número al que harán el Bizum.
//  · PAGO_IBAN y PAGO_TITULAR: se rellenan cuando Fran tenga su cuenta del negocio (después del
//    alta de autónomo). Mientras PAGO_IBAN esté vacío, al pulsar "Transferencia" se pide al
//    cliente que escriba al correo de contacto para recibir los datos, en vez de mostrar un
//    IBAN en blanco. Lo mismo vale para el teléfono si se dejara vacío.
// OJO: estos datos viajan en el código de la web (lo ve cualquiera que inspeccione la página),
// que es lo normal para un número de Bizum o un IBAN de cobro, y solo se muestran de cara al
// cliente cuando su prueba ha terminado.
// =====================================================================================
const PAGO_BIZUM_TELEFONO = '653 53 13 34';
const PAGO_IBAN = '';       // ejemplo: 'ES00 0000 0000 0000 0000 0000'
const PAGO_TITULAR = '';    // nombre del titular de la cuenta
const PAGO_EMAIL_CONTACTO = 'horidiapp@gmail.com';

// Horito (la mascota), estático, con el mismo dibujo que el asistente de la app.
const HoritoEstatico = ({ size = 88 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="260 230 740 740" width={size} height={size} aria-hidden="true"><defs><linearGradient id="horitoGradPago" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7C4FE9"></stop><stop offset="100%" stopColor="#5732C9"></stop></linearGradient></defs><g><circle cx="627.1" cy="597.4" r="307" fill="#E8E0F8"></circle><path d="M804.5,899.7 A350.5,350.5 0 1 1 885.1,360.2" fill="none" stroke="#5F19FD" strokeWidth="19" strokeLinecap="round"></path><path d="M973.2,652.8 A350.5,350.5 0 0 1 924.7,782.6" fill="none" stroke="#5F19FD" strokeWidth="19" strokeLinecap="round"></path><path d="M802.0,420.0 L777.0,403.0 L750.0,393.0 L723.0,389.0 L701.0,390.0 L492.0,418.0 L462.0,430.0 L436.0,450.0 L421.0,468.0 L410.0,488.0 L402.0,514.0 L400.0,539.0 L412.0,716.0 L425.0,749.0 L441.0,770.0 L464.0,789.0 L492.0,802.0 L516.0,807.0 L541.0,807.0 L545.0,816.0 L544.0,823.0 L548.0,827.0 L566.0,822.0 L591.0,808.0 L644.0,808.0 L676.0,824.0 L689.0,827.0 L693.0,824.0 L695.0,810.0 L758.0,808.0 L782.0,797.0 L830.0,751.0 L847.0,722.0 L856.0,692.0 L858.0,666.0 L846.0,492.0 L829.0,451.0 Z M350.0,797.0 L350.0,828.0 L384.0,864.0 L429.0,899.0 L464.0,899.0 L434.0,881.0 L402.0,856.0 L372.0,826.0 Z M457.0,300.0 L424.0,300.0 L392.0,325.0 L350.0,369.0 L350.0,398.0 L369.0,373.0 L398.0,343.0 L436.0,312.0 Z" fill="url(#horitoGradPago)"></path><path d="M825.0,449.0 L817.0,450.0 L817.0,455.0 L811.0,460.0 L813.0,474.0 L819.0,477.0 L815.0,480.0 L810.0,657.0 L802.0,692.0 L789.0,716.0 L766.0,742.0 L746.0,757.0 L725.0,767.0 L697.0,772.0 L698.0,740.0 L693.0,734.0 L678.0,737.0 L652.0,751.0 L650.0,810.0 L670.0,821.0 L691.0,826.0 L695.0,809.0 L747.0,810.0 L777.0,800.0 L828.0,753.0 L846.0,723.0 L857.0,683.0 L849.0,513.0 L835.0,463.0 L830.0,464.0 L832.0,457.0 Z" fill="#432BBA"></path><path d="M590.0,742.0 L528.0,771.0 L590.0,800.0 Z" fill="#5B38E3"></path><path d="M670.0,742.0 L732.0,771.0 L670.0,800.0 Z" fill="#3D25B2"></path><rect x="590" y="742" width="80" height="58" rx="14" fill="#351C9C"></rect><g><path d="M538.0,521.0 L515.0,519.0 L502.0,522.0 L486.0,531.0 L475.0,542.0 L467.0,556.0 L463.0,569.0 L463.0,587.0 L466.0,599.0 L474.0,613.0 L486.0,624.0 L496.0,629.0 L508.0,632.0 L521.0,632.0 L538.0,627.0 L558.0,612.0 L570.0,593.0 L573.0,583.0 L574.0,566.0 L569.0,548.0 L556.0,531.0 Z" fill="#FFFFFF"></path><path d="M526.0,541.0 L514.0,545.0 L505.0,552.0 L498.0,564.0 L496.0,578.0 L499.0,589.0 L507.0,600.0 L519.0,606.0 L531.0,607.0 L544.0,602.0 L553.0,594.0 L560.0,579.0 L560.0,565.0 L555.0,554.0 L547.0,546.0 L538.0,542.0 Z" fill="#332F54"></path><path d="M547.0,554.0 L539.0,554.0 L535.0,557.0 L533.0,561.0 L533.0,566.0 L536.0,571.0 L543.0,573.0 L551.0,568.0 L552.0,560.0 L550.0,556.0 Z" fill="#FFFFFF"></path></g><g><path d="M719.0,503.0 L697.0,505.0 L678.0,515.0 L663.0,532.0 L656.0,549.0 L654.0,568.0 L661.0,591.0 L678.0,609.0 L695.0,616.0 L717.0,616.0 L739.0,606.0 L756.0,588.0 L764.0,568.0 L764.0,544.0 L755.0,524.0 L738.0,509.0 Z" fill="#FFFFFF"></path><path d="M715.0,526.0 L700.0,533.0 L691.0,544.0 L688.0,553.0 L688.0,568.0 L693.0,579.0 L701.0,587.0 L714.0,592.0 L722.0,592.0 L734.0,588.0 L744.0,580.0 L751.0,567.0 L752.0,552.0 L747.0,539.0 L742.0,533.0 L731.0,527.0 Z" fill="#332F54"></path><path d="M732.0,538.0 L726.0,542.0 L724.0,549.0 L725.0,553.0 L729.0,557.0 L735.0,558.0 L742.0,553.0 L744.0,547.0 L739.0,539.0 Z" fill="#FFFFFF"></path></g><path d="M695.0,663.0 L692.0,659.0 L686.0,658.0 L653.0,674.0 L618.0,682.0 L584.0,682.0 L550.0,674.0 L544.0,678.0 L543.0,686.0 L552.0,694.0 L577.0,702.0 L592.0,704.0 L622.0,703.0 L646.0,698.0 L676.0,685.0 L693.0,673.0 Z" fill="#FFFFFF"></path><path d="M879.0,775.0 L865.0,774.0 L859.0,777.0 L805.0,831.0 L800.0,839.0 L799.0,849.0 L802.0,857.0 L835.0,890.0 L912.0,811.0 L916.0,810.0 L924.0,818.0 Z" fill="#FDA935"></path><path d="M915.0,810.0 L836.0,891.0 L860.0,914.0 L872.0,917.0 L884.0,913.0 L939.0,858.0 L943.0,846.0 L941.0,837.0 Z" fill="#FF9335"></path></g></svg>
);

// (19 oct 2026, Horidia Panel) `motivo` dice por qué se ha cortado el acceso, para que el texto
// sea el correcto: 'prueba' (se acabó la prueba gratuita), 'suscripcion' (se acabó el mes pagado)
// o 'desactivada' (Fran la ha pausado desde el panel). `diasPrueba`: cuántos días duró la prueba
// (14, o más si se alargó con días regalados). Los métodos de pago son los mismos en los tres.
const PantallaFinPrueba = ({ nombreNegocio, numEmpleados, precioMes, cuotaParcial = false, onSalir, motivo = 'prueba', diasPrueba = 14 }) => {
  const [pantalla, setPantalla] = useState('aviso');   // 'aviso' -> 'gracias'
  const [metodo, setMetodo] = useState(null);          // null | 'bizum' | 'iban'
  const [copiado, setCopiado] = useState('');          // '' | 'bizum' | 'iban'

  const CIAN = '#0E7490';    // tonos oscuros a propósito: con el texto blanco se leen bien (5,4:1 y 5,0:1)
  const VERDE = '#15803D';
  const nombre = (nombreNegocio || '').trim();
  const precioIVA = conIVA(precioMes);
  const fmtEur = (v) => v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const precioTexto = fmtEur(precioIVA.total);   // lo que hay que pagar, con el IVA ya incluido
  const fondo = { minHeight: '100vh', background: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' };
  const tarjeta = { background: C.white, borderRadius: '20px', padding: '28px 22px 18px', maxWidth: '400px', width: '100%', boxSizing: 'border-box', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' };
  const etiqueta = { fontSize: '11px', fontWeight: '800', letterSpacing: '1px', color: C.textMuted, textTransform: 'uppercase' };

  // Copia al portapapeles y SOLO dice "¡Copiado!" si de verdad se ha copiado: si el navegador
  // no deja (ni por la vía moderna ni por la antigua), avisa de que se copie a mano.
  const copiar = async (clave, texto) => {
    let copiadoOk = false;
    try {
      await navigator.clipboard.writeText(texto);
      copiadoOk = true;
    } catch (_) {
      try {
        const t = document.createElement('textarea');
        t.value = texto; t.style.position = 'fixed'; t.style.opacity = '0';
        document.body.appendChild(t); t.select();
        copiadoOk = document.execCommand('copy') === true;
        document.body.removeChild(t);
      } catch (__) { copiadoOk = false; }
    }
    setCopiado(copiadoOk ? clave : `${clave}-error`);
    setTimeout(() => setCopiado(''), 2500);
  };

  const botonCopiar = (clave, texto) => (
    <button type="button" onClick={() => copiar(clave, texto)}
      style={{ marginTop: '8px', padding: '6px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', background: C.white, color: C.primary, fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>
      {copiado === clave ? '¡Copiado!' : copiado === `${clave}-error` ? 'Cópialo a mano' : 'Copiar'}
    </button>
  );

  const botonMetodo = (clave, texto, color) => {
    const elegido = metodo === clave;
    const otroElegido = metodo !== null && !elegido;
    return (
      <button type="button" aria-pressed={elegido} onClick={() => setMetodo(clave)}
        style={{ flex: '1 1 0', padding: '14px 8px', borderRadius: '12px', fontSize: '16px', fontWeight: '800', cursor: 'pointer',
          background: otroElegido ? C.white : color, color: otroElegido ? color : C.white,
          border: `2px solid ${color}`, boxShadow: elegido ? `0 0 0 4px ${color}33` : 'none' }}>
        {texto}
      </button>
    );
  };

  const contacto = (
    <a href={`mailto:${PAGO_EMAIL_CONTACTO}`} style={{ color: C.primary, fontWeight: '800', textDecoration: 'none' }}>{PAGO_EMAIL_CONTACTO}</a>
  );
  const caja = { width: '100%', boxSizing: 'border-box', background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '14px', padding: '14px', marginTop: '12px' };
  // Concepto del pago (19 oct 2026): Fran ve en su banco el nombre de la PERSONA que paga, no el
  // del bar, así que el nombre del bar tiene que ir en el concepto. Va en un recuadro destacado
  // con el texto exacto y botón de copiar, para que no se pase por alto.
  const nombreBar = (nombre || '').trim();
  const textoConcepto = nombreBar ? `Horidia ${nombreBar}` : 'Horidia + el nombre de tu bar';
  const concepto = (
    <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '10px', background: C.white, border: `2px dashed ${C.primary}`, textAlign: 'center' }}>
      <div style={etiqueta}>Pon esto en el concepto</div>
      <div style={{ fontSize: '17px', fontWeight: '800', lineHeight: 1.3, color: C.text, marginTop: '4px', wordBreak: 'break-word' }}>{textoConcepto}</div>
      {nombreBar && botonCopiar('concepto', textoConcepto)}
      <p style={{ margin: '8px 0 0', fontSize: '12px', fontWeight: '600', lineHeight: 1.4, color: C.textMuted }}>
        Así sabemos qué bar ha pagado. En el banco solo vemos el nombre de quien envía el pago.
      </p>
    </div>
  );
  const aviso = (
    <p style={{ margin: '6px 0 0', fontSize: '12px', fontWeight: '700', lineHeight: 1.4, color: C.primaryDark }}>En cuanto veamos tu pago, reactivamos tu cuenta.</p>
  );
  // Importe exacto a enviar (con IVA), para que nadie mande solo la base.
  const importe = (
    <div style={{ marginTop: '10px' }}>
      <div style={etiqueta}>Importe</div>
      <div style={{ fontSize: '20px', fontWeight: '900', lineHeight: 1.2, color: C.text, marginTop: '2px' }}>{precioTexto} €</div>
      <div style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted }}>IVA incluido</div>
    </div>
  );

  // ---------- Pantalla 1: aviso ----------
  if (pantalla === 'aviso') {
    return (
      <div style={fondo}>
        <div style={{ ...tarjeta, padding: '32px 26px 24px' }}>
          <div style={{ width: '76px', height: '76px', boxSizing: 'border-box', borderRadius: '20px', background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '22px' }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 2h12M6 22h12" /><path d="M7 2v4.5a5 5 0 0 0 1.5 3.5L12 12l-3.5 2A5 5 0 0 0 7 17.5V22" /><path d="M17 2v4.5a5 5 0 0 1-1.5 3.5L12 12l3.5 2a5 5 0 0 1 1.5 3.5V22" />
            </svg>
          </div>
          <h1 style={{ margin: '0 0 10px', fontSize: '24px', fontWeight: '900', lineHeight: 1.2, color: C.text }}>
            {motivo === 'suscripcion' ? 'Se ha terminado tu suscripción'
              : motivo === 'desactivada' ? 'Tu cuenta está en pausa'
              : `Se ha terminado la prueba gratuita de ${diasPrueba} días`}
          </h1>
          <p style={{ margin: '0 0 28px', fontSize: '15px', fontWeight: '600', lineHeight: 1.55, color: C.textMuted }}>
            Pulsa para reanudar tu cuenta{nombre ? ' de ' : ''}{nombre && <strong style={{ color: C.text, fontWeight: '800' }}>{nombre}</strong>}.
          </p>
          <button type="button" onClick={() => setPantalla('gracias')}
            style={{ width: '100%', padding: '16px', border: 'none', borderRadius: '12px', background: C.primary, color: C.white, fontSize: '16px', fontWeight: '800', cursor: 'pointer' }}>
            Continuar con Horidia
          </button>
          <button type="button" onClick={onSalir} style={{ ...btn('ghost'), marginTop: '6px' }}>Salir</button>
        </div>
      </div>
    );
  }

  // ---------- Pantalla 2: gracias + cuota + métodos de pago ----------
  const telefonoLimpio = PAGO_BIZUM_TELEFONO.replace(/\s/g, '');
  const ibanLimpio = PAGO_IBAN.replace(/\s/g, '');
  return (
    <div style={fondo}>
      <div style={tarjeta}>
        <div style={{ marginBottom: '6px' }}><HoritoEstatico size={88} /></div>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: '900', lineHeight: 1.2, color: C.text }}>Gracias por usar Horidia</h1>
        {motivo === 'prueba' ? (
          <>
            <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: '600', lineHeight: 1.5, color: '#4B5563' }}>
              Han sido {diasPrueba} días organizando {nombre ? 'los turnos de ' : 'tus turnos'}{nombre && <strong style={{ color: C.text, fontWeight: '800' }}>{nombre}</strong>}. Gracias por confiar en nosotros.
            </p>
            <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: '600', lineHeight: 1.5, color: '#4B5563' }}>Si Horidia te ha ahorrado tiempo y quebraderos de cabeza, sigamos juntos.</p>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: '600', lineHeight: 1.5, color: '#4B5563' }}>
              Gracias por organizar con nosotros {nombre ? 'los turnos de ' : 'tus turnos'}{nombre && <strong style={{ color: C.text, fontWeight: '800' }}>{nombre}</strong>}.
            </p>
            <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: '600', lineHeight: 1.5, color: '#4B5563' }}>Para seguir con Horidia, renueva tu suscripción con cualquiera de estos métodos.</p>
          </>
        )}

        <div style={{ width: '100%', boxSizing: 'border-box', background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '14px', padding: '12px', marginBottom: '14px' }}>
          <div style={etiqueta}>Cuota mensual</div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px', marginTop: '2px' }}>
            <span style={{ fontSize: '30px', fontWeight: '900', lineHeight: 1.1, color: C.primary }}>{precioTexto} €</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: C.textMuted }}>/mes</span>
          </div>
          <div style={{ fontSize: '12px', fontWeight: '800', color: C.primaryDark, marginTop: '2px' }}>IVA incluido</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted, marginTop: '4px' }}>{numEmpleados} empleado{numEmpleados === 1 ? '' : 's'} · {fmtEur(precioIVA.base)} € + {fmtEur(precioIVA.iva)} € de IVA ({IVA_PORCENTAJE} %)</div>
          {cuotaParcial && <div style={{ fontSize: '11px', fontWeight: '600', lineHeight: 1.4, color: C.textMuted, marginTop: '4px' }}>No hemos podido contar a todo tu equipo: la cuota final se confirma al activar tu cuenta.</div>}
        </div>

        <div style={{ ...etiqueta, marginBottom: '8px' }}>Métodos de pago</div>
        <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
          {botonMetodo('bizum', 'Bizum', CIAN)}
          {botonMetodo('iban', 'Transferencia', VERDE)}
        </div>

        {metodo === 'bizum' && (
          <div style={caja}>
            {PAGO_BIZUM_TELEFONO ? (
              <>
                <div style={etiqueta}>Haz un Bizum al</div>
                <div style={{ fontSize: '24px', fontWeight: '800', lineHeight: 1.2, letterSpacing: '1px', color: C.text, marginTop: '6px' }}>{PAGO_BIZUM_TELEFONO}</div>
                {botonCopiar('bizum', telefonoLimpio)}
                {importe}{concepto}{aviso}
              </>
            ) : (
              <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', lineHeight: 1.5, color: C.text }}>Escríbenos a {contacto} y te damos los datos para el Bizum.</p>
            )}
          </div>
        )}
        {metodo === 'iban' && (
          <div style={caja}>
            {PAGO_IBAN ? (
              <>
                <div style={etiqueta}>Transferencia a</div>
                <div style={{ fontSize: '15px', fontWeight: '800', lineHeight: 1.3, letterSpacing: '1px', color: C.text, marginTop: '6px', wordBreak: 'break-word' }}>{PAGO_IBAN}</div>
                {PAGO_TITULAR && <p style={{ margin: '6px 0 0', fontSize: '12px', fontWeight: '600', color: C.textMuted }}>Titular: {PAGO_TITULAR}</p>}
                {botonCopiar('iban', ibanLimpio)}
                {importe}{concepto}{aviso}
              </>
            ) : (
              <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', lineHeight: 1.5, color: C.text }}>Escríbenos a {contacto} y te damos los datos de la cuenta.</p>
            )}
          </div>
        )}

        <p style={{ margin: '12px 0 0', fontSize: '12px', fontWeight: '600', lineHeight: 1.4, color: C.textMuted }}>¿Dudas? Escríbenos a {contacto}</p>
        <button type="button" onClick={onSalir} style={{ ...btn('ghost'), marginTop: '4px' }}>Salir</button>
      </div>
    </div>
  );
};

// =====================================================================================
// AVISOS DE LA CUENTA (19 oct 2026, diseñados con Fran) — solo Encargado y Dueño
//
//  · 'fin': desde 5 días antes de que acabe la prueba gratuita o el mes pagado, una vez al día.
//    Educado, sin precio, solo dando las gracias y diciendo qué día termina. El último día:
//    "Hoy es tu último día". Al día siguiente ya salen las pantallas de pago (PantallaFinPrueba).
//  · 'regalo': cuando Fran da desde el panel los 15 días gratis por haber invitado a otro
//    restaurante. Sale una sola vez por regalo (además de la notificación al móvil).
// =====================================================================================
const AvisoCuentaModal = ({ tipo, datos, onCerrar }) => {
  const [saliendo, setSaliendo] = useState(false);
  const cerrar = () => { if (saliendo) return; setSaliendo(true); setTimeout(onCerrar, 180); };
  const negrita = { color: C.text, fontWeight: 700 };
  const p = { fontSize: '14.5px', fontWeight: 500, lineHeight: 1.55, color: '#4B4560', margin: '14px 0 0' };
  const nombre = (datos.nombre || '').trim();
  let titulo; let cuerpo; let puntos = null; let etiqueta = null; let icono;

  if (tipo === 'regalo') {
    icono = (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.5" y="8.5" width="17" height="4" rx="1" /><path d="M5 12.5v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7M12 8.5v12" />
        <path d="M12 8.5c-1.5-3.5-5.5-4-5.5-1.5 0 1.5 2.5 1.5 5.5 1.5zM12 8.5c1.5-3.5 5.5-4 5.5-1.5 0 1.5-2.5 1.5-5.5 1.5z" />
      </svg>
    );
    titulo = <>¡{datos.dias || 15} días gratis<br />para ti!</>;
    cuerpo = (
      <>
        <p style={p}>Te hemos regalado {datos.dias || 15} días gratis por invitar a <span style={negrita}>{datos.invitadoNombre || 'otro restaurante'}</span>. ¡Gracias por recomendar Horidia!</p>
        {datos.hasta && <p style={p}>Ahora tienes Horidia hasta el <span style={negrita}>{fechaLargaCuenta(datos.hasta)}</span>.</p>}
      </>
    );
  } else {
    const esPrueba = datos.tipoCuenta === 'prueba';
    const ultimo = datos.quedan <= 1;
    titulo = esPrueba
      ? (ultimo ? <>Hoy es tu último día<br />de prueba gratuita</> : <>Te quedan {datos.quedan} días de<br />prueba gratuita</>)
      : (ultimo ? <>Hoy es el último día<br />de tu suscripción</> : <>Te quedan {datos.quedan} días de<br />tu suscripción</>);
    const conNombre = nombre ? <>los turnos de <span style={negrita}>{nombre}</span></> : 'tus turnos';
    if (esPrueba) {
      cuerpo = (
        <>
          <p style={p}>
            {ultimo
              ? (datos.totalDias === 14
                ? <>Han sido dos semanas organizando {conNombre}. Muchas gracias por probar Horidia.</>
                : <>Muchas gracias por probar Horidia para organizar {conNombre}.</>)
              : <>Gracias por confiar en Horidia para organizar {conNombre}. Esperamos que te esté ahorrando tiempo.</>}
          </p>
          <p style={p}>{ultimo ? 'Tu prueba gratuita termina hoy.' : <>Tu prueba gratuita termina el <span style={negrita}>{fechaLargaCuenta(datos.ultimoDia)}</span>.</>}</p>
        </>
      );
      // Puntitos de la prueba (solo si es la de 14 días o poco más: con muchos no caben).
      if (datos.totalDias >= 2 && datos.totalDias <= 21) {
        const hoyN = datos.totalDias - datos.quedan + 1; // qué día de la prueba es hoy (1..total)
        puntos = Array.from({ length: datos.totalDias }, (_, i) => {
          const n = i + 1;
          const estilo = n < hoyN ? { background: C.primary } : n === hoyN ? { background: '#F59E0B' } : { background: C.white, boxShadow: 'inset 0 0 0 2px #C4B5FD' };
          return <i key={n} style={{ width: datos.totalDias > 16 ? '10px' : '13px', height: datos.totalDias > 16 ? '10px' : '13px', borderRadius: '50%', display: 'block', ...estilo }} />;
        });
        etiqueta = `Día ${hoyN} de ${datos.totalDias}`;
      }
    } else {
      cuerpo = (
        <>
          <p style={p}>Gracias por seguir confiando en Horidia para organizar {conNombre}.</p>
          <p style={p}>{ultimo ? 'Tu suscripción termina hoy.' : <>Tu suscripción termina el <span style={negrita}>{fechaLargaCuenta(datos.ultimoDia)}</span>.</>}</p>
        </>
      );
    }
    icono = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 11px)', gridTemplateRows: 'repeat(2, 11px)', gap: '4px' }}>
        {[0, 1, 2, 3, 4, 5].map(i => <i key={i} style={{ display: 'block', borderRadius: '3px', border: `2.2px solid ${i === 5 ? '#F59E0B' : '#fff'}`, background: i === 5 ? '#F59E0B' : 'transparent' }} />)}
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" onClick={cerrar}
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(36,21,66,0.5)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px', animation: `${saliendo ? 'hdAvisoFondoOut' : 'hdAvisoFondoIn'} .2s ease both` }}>
      <style>{`@keyframes hdAvisoFondoIn{from{opacity:0}to{opacity:1}}@keyframes hdAvisoFondoOut{from{opacity:1}to{opacity:0}}@keyframes hdAvisoIn{from{opacity:0;transform:translateY(14px) scale(.96)}to{opacity:1;transform:none}}@keyframes hdAvisoOut{from{opacity:1;transform:none}to{opacity:0;transform:translateY(8px) scale(.97)}}@keyframes hdAvisoLogo{0%{transform:scale(.6) rotate(-8deg);opacity:0}60%{transform:scale(1.08) rotate(2deg);opacity:1}100%{transform:none}}`}</style>
      <div onClick={e => e.stopPropagation()}
        style={{ background: C.white, borderRadius: '26px', padding: '28px 24px 20px', width: '100%', maxWidth: '380px', boxSizing: 'border-box', textAlign: 'center', boxShadow: '0 30px 70px -20px rgba(36,21,66,0.55)', animation: `${saliendo ? 'hdAvisoOut' : 'hdAvisoIn'} .28s cubic-bezier(.2,.8,.2,1) both` }}>
        <div style={{ width: '62px', height: '62px', borderRadius: '18px', background: tipo === 'regalo' ? 'linear-gradient(135deg, #F59E0B, #D97706)' : 'linear-gradient(135deg, #7C3AED, #5B21B6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', animation: 'hdAvisoLogo .5s .08s cubic-bezier(.2,.8,.2,1) both' }}>
          {icono}
        </div>
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1.25, color: '#241542' }}>{titulo}</h2>
        {puntos && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '5px', margin: '16px 0 6px' }}>{puntos}</div>
            <div style={{ fontSize: '12px', fontWeight: 500, color: '#8A8399' }}>{etiqueta}</div>
          </>
        )}
        {cuerpo}
        <button type="button" onClick={cerrar}
          style={{ marginTop: '22px', width: '100%', height: '50px', border: 'none', borderRadius: '15px', background: tipo === 'regalo' ? '#D97706' : '#6D28D9', color: C.white, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          {tipo === 'regalo' ? '¡Genial, gracias!' : 'Entendido'}
        </button>
        <div style={{ marginTop: '12px', fontSize: '12.5px', fontWeight: 500, color: '#8A8399' }}>
          ¿Alguna duda? Escríbenos a <a href={`mailto:${PAGO_EMAIL_CONTACTO}`} style={{ color: '#6D28D9', fontWeight: 600, textDecoration: 'none' }}>{PAGO_EMAIL_CONTACTO}</a>
        </div>
      </div>
    </div>
  );
};

const card = { background: C.white, borderRadius: '12px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };

// ===== TOKENS "D" — HOY EN ESPEJO DEL TEMA CLARO =====
// Hoy probamos varios temas oscuros para la cabecera y el horario del Encargado (con estas
// piezas ya reestructuradas: franja de color por turno, días escritos enteros, filas separadas).
// Al volver a los colores originales, en vez de deshacer esa reestructuración a mano en cada
// bloque, se hace que D apunte a los MISMOS valores que el tema claro — así todo lo reescrito hoy
// hereda el aspecto original sin tocarlo pieza por pieza. Si más adelante se retoma el oscuro,
// basta con volver a poner aquí valores oscuros.
const D = {
  deep: C.bg,
  panel: C.white,
  panelSoft: C.primaryFaint,
  panelHi: C.primaryPale,
  line: C.border,
  lineStrong: C.primaryBorder,
  blue: C.primary,
  blueBright: C.primaryLight,
  blueLight: C.primaryDark,
  text: C.text,
  textMuted: C.textMuted,
  textFaint: C.textLight,
  glow: '0 2px 8px rgba(124,58,237,0.18)',
};

const darkCard = { background: D.panel, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${D.line}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };

// Color de la franja lateral de cada turno en la tabla. La franja es lo que permite leer el
// cuadrante de un vistazo sin leer el texto de cada celda, así que los colores tienen que
// distinguirse entre sí de un golpe de vista — no basta con que sean "de la gama".
const shiftAccent = (tipo) => {
  if (tipo === 'apertura') return '#EAB308';   // amarillo
  if (tipo === 'comida') return '#EC4899';     // rosa
  if (tipo === 'noche') return '#7C3AED';      // morado
  if (tipo === 'partido') return '#3B82F6';    // azul
  if (tipo === 'enlace') return '#22C55E';     // verde claro (celda entera)
  if (tipo === 'vacaciones') return '#06B6D4'; // cian (celda entera)
  if (tipo === 'baja') return '#F97316';       // naranja (celda entera)
  return '#7DD3FC';                             // libre: celeste
};

const btn = (variant = 'primary', size = 'md') => {
  const bg = variant === 'primary' ? C.primary : variant === 'danger' ? C.danger : variant === 'success' ? C.success : variant === 'ghost' ? 'transparent' : C.white;
  const color = ['primary','danger','success'].includes(variant) ? C.white : variant === 'ghost' ? C.textMuted : C.primary;
  const border = variant === 'outline' ? `1px solid ${C.primaryBorder}` : 'none';
  const pad = size === 'sm' ? '6px 12px' : size === 'lg' ? '14px 24px' : '10px 18px';
  const fontSize = size === 'sm' ? '13px' : size === 'lg' ? '16px' : '14px';
  return { background: bg, color, border, padding: pad, borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize, display: 'inline-flex', alignItems: 'center', gap: '6px' };
};

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
// Instancia de Firebase Functions para el asistente "Horito" (6 sep 2026, renombrado 21 sep 2026) — llama
// a la Cloud Function `preguntarHoria`, que es la única que habla con Gemini.
const functionsInstance = getFunctions(getApp());
const WEEKEND = ['Viernes', 'Sábado', 'Domingo'];

// Editor del mapa de mesas (pestaña "Mapa" dentro de "Carta y Mesas"): plano libre, no rejilla.
// El encargado dibuja la FORMA de su local (una o varias paredes, cada una un polígono cerrado
// hecho a base de puntos) y coloca mesas/barra/entrada donde quiera, con coordenadas libres
// (0-70 de ancho, 0-100 de alto — el "papel" del plano, en VERTICAL como la pantalla de un
// móvil, para aprovechar el alto en vez de dejar el plano bajito). Las mesas llevan además un
// número editable y ya dibujan sus propias sillas alrededor (no hace falta colocarlas aparte).
// Se guarda por LOCAL (ver `datosLocalMenuMapa`/`actualizarDatosLocalMenuMapa`), dentro de
// `config.localesData[localId]`, así que un restaurante con varios locales tiene un plano y una
// carta distintos para cada uno.
const MAPA_VB_W = 70;
const MAPA_VB_H = 100;
// Separación de la rejilla de puntos de fondo del plano (referencia visual, como en Figma/Canva)
// y a la que se "imantan" los toques, para que salga fácil dibujar formas rectas y alinear mesas.
const MAPA_GRID = 5;
const MAPA_HERRAMIENTAS = [
  { id: 'pared', emoji: '🧱', label: 'Pared', bg: '#F3F4F6', border: '#9CA3AF', color: '#374151' },
  { id: 'mesa', emoji: '🍽️', label: 'Mesa', bg: '#EDE9FE', border: '#D8B4FE', color: '#4C1D95' },
  { id: 'barra', emoji: '🍸', label: 'Barra', bg: '#FEF3C7', border: '#FCD34D', color: '#FBBF24' },
  { id: 'entrada', emoji: '🚪', label: 'Entrada', bg: '#ECFEFF', border: '#67E8F9', color: '#0E7490' },
  // AÑADIDO (1 oct 2026, a petición de Fran, tras ver un plano de ejemplo con salas rotuladas
  // como "Kitchen"/"Office"/"Ladies"): una etiqueta de texto libre, para escribir "Cocina",
  // "Baños", "Terraza"... directamente sobre el plano. No lleva número ni forma, solo texto —
  // el Encargado ya puede delimitar la sala dibujando paredes alrededor con la herramienta que
  // ya existía; esto solo le pone el nombre encima, visible de un vistazo.
  { id: 'etiqueta', emoji: '🏷️', label: 'Nombre de sala', bg: '#F0FDF4', border: '#86EFAC', color: '#15803D' },
  { id: 'borrar', emoji: '🧹', label: 'Borrar', bg: '#FEE2E2', border: '#FCA5A5', color: '#991B1B' },
];
const MAPA_HERRAMIENTAS_POR_ID = MAPA_HERRAMIENTAS.reduce((acc, h) => { acc[h.id] = h; return acc; }, {});
// GASTOS DEL NEGOCIO (1 oct 2026, pedido por Fran tras ver a su jefe apuntar gastos en papel en
// Duo Tapas). Tres categorías: personal (nóminas), pedidos y proveedores, y un cajón "otros" para
// lo que no encaje en las dos anteriores — así el primer mes no se queda nadie sin sitio donde
// apuntar algo. Viven en su propia colección (`gastos`, una por restaurante/cocina), nunca dentro
// de `config`: a diferencia del mapa de mesas o la carta, esto NO tiene techo — crece cada mes,
// para siempre, así que no puede vivir en el documento que se reescribe entero en cada autoguardado.
const GASTO_CATEGORIAS = [
  { id: 'personal', emoji: '👤', label: 'Personal', color: '#7C3AED', bg: '#EDE9FE' },
  { id: 'pedidos', emoji: '📦', label: 'Pedidos y proveedores', color: '#D97706', bg: '#FEF3C7' },
  { id: 'otros', emoji: '🧾', label: 'Otros', color: '#0891B2', bg: '#ECFEFF' },
];
const GASTO_CATEGORIAS_POR_ID = GASTO_CATEGORIAS.reduce((acc, g) => { acc[g.id] = g; return acc; }, {});
// Ray-casting estándar: ¿el punto {x,y} cae dentro del polígono (array de {x,y})? Se usa para
// saber si un toque con la herramienta "Borrar" cae encima de una pared ya dibujada.
const puntoEnPoligono = (pt, poligono) => {
  let dentro = false;
  for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i++) {
    const pi = poligono[i], pj = poligono[j];
    const cruza = (pi.y > pt.y) !== (pj.y > pt.y) &&
      pt.x < ((pj.x - pi.x) * (pt.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (cruza) dentro = !dentro;
  }
  return dentro;
};
// PLANO REALISTA (12 oct 2026, pedido por Fran: "más realista y menos animado"). Mismo lenguaje
// visual que los planos de sala de los TPV de hostelería y que un plano de arquitecto visto desde
// arriba: paredes gruesas en gris oscuro, suelo de baldosa clara, mesas de madera con sombra,
// sillas con su respaldo mirando hacia fuera, barra con taburetes y puerta con su arco de apertura.
const PLANO = {
  suelo: '#FAF7F2', junta: '#ECE5D8', pared: '#3F3F46',
  madera: '#E8D7BD', maderaBorde: '#B08F64', maderaOscura: '#7A5A3A', maderaOscuraBorde: '#4E3923',
  asiento: '#F4F4F5', respaldo: '#C9CBD1', sillaBorde: '#8E929C', texto: '#3B3328', sombra: 'rgba(0,0,0,0.10)',
};
// Silla vista desde arriba en (cx, cy), mirando hacia `ang` grados (0 = mira hacia arriba, es
// decir, el respaldo queda abajo). Asiento redondeado + respaldo más oscuro en el lado de fuera.
const sillaPlano = (cx, cy, ang, key) => (
  <g key={key} transform={`translate(${cx} ${cy}) rotate(${ang})`}>
    <rect x="-1.15" y="-1" width="2.3" height="2.1" rx="0.45" fill={PLANO.asiento} stroke={PLANO.sillaBorde} strokeWidth="0.22" />
    <rect x="-1.3" y="0.85" width="2.6" height="0.6" rx="0.3" fill={PLANO.respaldo} stroke={PLANO.sillaBorde} strokeWidth="0.22" />
  </g>
);
// Fondo de todos los planos: patrón de baldosas para el suelo de dentro de las paredes.
const DefsPlano = () => (
  <defs>
    <pattern id="planoBaldosa" width="5" height="5" patternUnits="userSpaceOnUse">
      <rect width="5" height="5" fill={PLANO.suelo} />
      <path d="M5 0H0V5" fill="none" stroke={PLANO.junta} strokeWidth="0.18" />
    </pattern>
  </defs>
);
// Una pared (polígono cerrado): suelo de baldosa dentro y muro grueso alrededor.
const paredPlano = (pared, i) => (
  <polygon key={i} points={(pared.puntos || []).map(pt => `${pt.x},${pt.y}`).join(' ')}
    fill="url(#planoBaldosa)" stroke={PLANO.pared} strokeWidth="1.3" strokeLinejoin="miter" />
);
// Medio tamaño (en unidades del plano, sin girar) de cada cosa, para saber si un toque le cae encima.
const medidaElementoPlano = (el) => {
  if (el.tipo === 'barra') return { hw: 8.5, hh: 4.4 };
  if (el.tipo === 'entrada') return { hw: 4, hh: 4 };
  if (el.tipo === 'mesa' && (el.forma === 'rectangular' || el.forma === 'ovalada')) return { hw: 6.4, hh: 4.6 };
  return { hw: 4.6, hh: 4.6 };
};
const tocaElementoPlano = (el, pt) => {
  const r = -((el.rot || 0) * Math.PI) / 180;
  const dx = pt.x - el.x, dy = pt.y - el.y;
  const lx = dx * Math.cos(r) - dy * Math.sin(r), ly = dx * Math.sin(r) + dy * Math.cos(r);
  const { hw, hh } = medidaElementoPlano(el);
  return Math.abs(lx) <= hw && Math.abs(ly) <= hh;
};
// Viernes y Sábado son "los días más importantes" del negocio (pedido explícito de Fran, 8 ago
// 2026): se protegen siempre que se pueda a la hora de repartir los descansos obligatorios de
// la semana, para que sean los días con más gente trabajando.
const DIAS_PROTEGIDOS_DESCANSO = ['Viernes', 'Sábado'];
// Cuántos turnos "vale" trabajar un día: con turno partido permitido, CUALQUIER día (entre
// semana o fin de semana) vale 2 (comida+noche, 4h cada uno = 8h); sin turno partido permitido,
// cualquier día vale 1 (turno seguido de 8h). No hay ninguna razón de negocio para que el fin de
// semana sea distinto entre semana en esto — antes del 8 ago 2026 el generador SÍ lo trataba
// distinto (nunca repartía partido en Viernes/Sábado/Domingo) por un fallo suyo, no porque
// Horidia lo pidiera así: Fran confirmó que el fin de semana también admite turno partido igual
// que cualquier otro día.
const capacidadPorDiaTurno = (dia, permitePartido) => permitePartido ? 2 : 1;
// FALLO REAL ENCONTRADO (8 ago 2026): a un empleado se le podían configurar hasta 14 turnos
// contratados a la semana, pero con los descansos obligatorios y el hecho de que el fin de
// semana nunca da turno partido, el máximo REAL que se puede llegar a cumplir siempre ha sido
// mucho más bajo (normalmente 8-9). El generador nunca avisaba de esto: simplemente se quedaba
// corto en silencio (a veces mucho, si el reparto aleatorio de descansos era desafortunado).
// Esta función calcula ese techo real, colocando los descansos obligatorios en los días que
// menos turnos "cuesten" y protegiendo Viernes/Sábado — se usa tanto para no perseguir un
// objetivo imposible dentro del generador como para limitar el selector de turnos contratados
// en la pestaña Empleados, y así nunca se pueda configurar un número inalcanzable.
const capacidadMaximaTurnos = (config) => {
  const diasCierre = config?.diasCierreSemana || [];
  const permitePartido = config?.permiteTurnoPartido !== false;
  const objetivoDescansos = Math.max(1, Math.min(6, config?.diasDescansoSemana || 2));
  const abiertos = DAYS.filter(d => !diasCierre.includes(d));
  if (abiertos.length === 0) return 0;
  const candidatosDescanso = [...abiertos].sort((a, b) => {
    const protA = DIAS_PROTEGIDOS_DESCANSO.includes(a) ? 1 : 0;
    const protB = DIAS_PROTEGIDOS_DESCANSO.includes(b) ? 1 : 0;
    if (protA !== protB) return protA - protB;
    return capacidadPorDiaTurno(a, permitePartido) - capacidadPorDiaTurno(b, permitePartido);
  });
  const diasDescanso = candidatosDescanso.slice(0, Math.min(objetivoDescansos, abiertos.length));
  const diasQueQuedan = abiertos.filter(d => !diasDescanso.includes(d));
  return diasQueQuedan.reduce((sum, d) => sum + capacidadPorDiaTurno(d, permitePartido), 0);
};

// Fecha (YYYY-MM-DD) de cada día de la SIGUIENTE semana a generar (o de la actual, si hoy
// mismo es lunes) — nunca mira hacia atrás, para no mezclar días que ya han pasado.
// Es el mismo criterio que usa la app para saber si un día de vacaciones/baja cae dentro
// de la semana que se está preparando.
const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// "2026-09-24" → "24 de septiembre". Para mostrar la fecha exacta de una solicitud/cobertura
// junto al nombre del día ("Miércoles"), que por sí solo no dice a qué semana se refiere.
const MESES_CORTOS = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
const fechaLegible = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${d} de ${MESES_CORTOS[m - 1]}`;
};
// "2026-10-24" → "Sábado 24 de octubre" (16 oct 2026). Las solicitudes de día libre solo guardan la
// fecha y se enseñaba tal cual ("2026-10-24"); así se leen igual que las coberturas. Si el texto no
// tiene ese formato, se enseña tal cual, sin romper nada.
const fechaConDia = (dateStr) => {
  if (typeof dateStr !== 'string') return dateStr || '';
  const corta = fechaLegible(dateStr);
  if (!corta) return dateStr || '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const f = new Date(y, m - 1, d);
  return `${DAYS[f.getDay() === 0 ? 6 : f.getDay() - 1]} ${corta}`;
};
// Rango compacto para vacaciones/bajas: "de 9 a 20 de septiembre" (mismo mes) o
// "de 28 de agosto a 3 de septiembre" (a caballo entre meses). Sin "end" (ausencia
// indefinida), solo "desde 9 de septiembre".
const rangoFechasLegible = (start, end) => {
  if (!start) return '';
  if (!end) return `desde ${fechaLegible(start)}`;
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  if (ys === ye && ms === me) return `de ${ds} a ${de} de ${MESES_CORTOS[ms - 1]}`;
  return `de ${fechaLegible(start)} a ${fechaLegible(end)}`;
};
const getWeekDates = () => {
  const hoy = new Date();
  const diaSemana = hoy.getDay(); // 0=domingo ... 6=sábado
  // Días hacia delante hasta el próximo lunes (0 si hoy ya es lunes, si no, siempre hacia adelante).
  const offsetLunes = (8 - diaSemana) % 7;
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() + offsetLunes);
  const mapa = {};
  DAYS.forEach((day, i) => {
    const d = new Date(lunes);
    d.setDate(lunes.getDate() + i);
    mapa[day] = toDateStr(d);
  });
  return mapa;
};
// Fechas reales (lunes a domingo) del horario guardado en una entrada del historial. Las entradas
// nuevas las llevan guardadas (`fechas`); en las antiguas se deducen de cuándo se publicó, con la
// misma regla de getWeekDates (el horario es siempre el de la semana que empieza el próximo lunes,
// o ese mismo día si se publicó en lunes).
const fechaPublicacionHistorial = (h) => {
  if (!h) return null;
  if (h.publishedAtTimestamp) return new Date(h.publishedAtTimestamp);
  const datePart = (h.publishedAt || '').split(',')[0]?.trim();
  const [d, m, y] = (datePart || '').split('/').map(Number);
  if (d && m && y) return new Date(y, m - 1, d);
  return null;
};
const fechasDeEntradaHistorial = (h) => {
  if (h?.fechas?.Lunes) return h.fechas;
  const pub = fechaPublicacionHistorial(h);
  if (!pub) return {};
  const lunes = new Date(pub.getFullYear(), pub.getMonth(), pub.getDate() + ((8 - pub.getDay()) % 7));
  const mapa = {};
  DAYS.forEach((day, i) => { mapa[day] = toDateStr(new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + i)); });
  return mapa;
};
// Una sola entrada por semana y local (la última publicada o retocada): así un horario que se
// publicó dos veces no cuenta doble.
const historialSinDuplicados = (historial) => {
  const porSemana = {};
  (historial || []).forEach(h => {
    const clave = `${h.localId || LOCAL_PRINCIPAL}|${fechasDeEntradaHistorial(h).Lunes || h.id}`;
    const marca = Math.max(h.actualizadoTs || 0, h.publishedAtTimestamp || 0);
    const previa = porSemana[clave];
    if (!previa || marca >= Math.max(previa.actualizadoTs || 0, previa.publishedAtTimestamp || 0)) porSemana[clave] = h;
  });
  return Object.values(porSemana);
};
// Turnos que vale una casilla guardada, con SUS propias horas (un Enlace de 16:00 a 00:30 vale 2).
const pesoCasillaGuardada = (s, config) => {
  const t = s?.tipo;
  if (t === 'partido') return 2;
  if (t === 'enlace') return pesoEnlaceDeHoras(s.horaEntrada || horariosGenerales(config).enlaceIni, s.horaSalida || horariosGenerales(config).enlaceFin, horariosGenerales(config).dur);
  if (t === 'apertura' || t === 'comida' || t === 'noche') return 1;
  return 0;
};
// BOTE DE PROPINAS (28 oct 2026): turnos que ha hecho cada empleado entre dos fechas (ambas
// incluidas), sacados del horario publicado (con los cambios posteriores, que se copian al
// historial) y solo hasta hoy. Mismo peso que el recuento del mes: partido = 2, Enlace según sus
// horas y el resto 1. Devuelve { [empId]: turnos }.
const turnosEnRango = (historial, config, desde, hasta) => {
  const hoyStr = toDateStr(new Date());
  const tope = hasta < hoyStr ? hasta : hoyStr;
  const mapa = {};
  historialSinDuplicados(historial).forEach(h => {
    const fechas = fechasDeEntradaHistorial(h);
    Object.entries(h.schedule || {}).forEach(([empId, semana]) => {
      DAYS.forEach(day => {
        const f = fechas[day];
        if (!f || f < desde || f > tope) return;
        const w = pesoCasillaGuardada(semana?.[day], config);
        if (w) mapa[empId] = (mapa[empId] || 0) + w;
      });
    });
  });
  return mapa;
};
// Texto que identifica un objeto sin depender del orden de sus claves (el servidor puede devolver los
// mapas en otro orden que el de la app). Para comparar horarios sin falsos "distintos" (29 oct 2026).
const firmaEstable = (v) => {
  if (Array.isArray(v)) return `[${v.map(firmaEstable).join(',')}]`;
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${firmaEstable(v[k])}`).join(',')}}`;
  return JSON.stringify(v === undefined ? null : v);
};
const formatoEuros = (n) => `${(Number(n) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
// ¿Cae la fecha dateStr (YYYY-MM-DD) dentro de algún periodo de vacaciones del empleado?
const estaAusenteEse = (emp, dateStr) => {
  if (!dateStr) return null;
  return (emp.vacaciones || []).find(v => dateStr >= v.start && (!v.end || dateStr <= v.end)) || null;
};
// Igual que arriba, pero para bajas — guardadas aparte (emp.bajas), sin mezclar con vacaciones.
// Una baja sin "end" se considera abierta (indefinida) hasta que alguien la cierre a mano.
const estaDeBajaEse = (emp, dateStr) => {
  if (!dateStr) return null;
  return (emp.bajas || []).find(b => dateStr >= b.start && (!b.end || dateStr <= b.end)) || null;
};

// Color del "avatar" (círculo con la inicial) según el rol: Encargado en morado sólido,
// Gerente en naranja sólido (mismo naranja de la marca), el resto en morado clarito.
const avatarBg = (p) => p.esEncargado ? C.accent : p.esGerente ? C.primary : C.primaryPale;
const avatarColor = (p) => (p.esEncargado || p.esGerente) ? C.white : C.primary;

// FICHA DE PERSONA (11 oct 2026, pedido por Fran: "el círculo con la inicial es muy común").
// Inspirada en el propio logo de Horidia (una cuadrícula de casillas redondeadas): cada persona es
// una "casilla" con sus DOS primeras letras, como un elemento de la tabla periódica ("Fe" Fernando,
// "Fr" Fran, "Da" Damian), así dos nombres que empiezan igual ya no se confunden. Cada persona
// tiene siempre el mismo color (sale de su nombre), para reconocerla de un vistazo en cualquier
// pantalla. Si se pasa `turnos` (asignados/objetivo), un pequeño marcador en la esquina dice si
// cuadra (verde) o no (rojo).
// Colores (fondo, tinta) de las fichas. 12 oct 2026, pedido por Fran: en Personal se repetían colores.
// El Gerente va siempre en naranja y los Encargados en morado; al resto se les reparten estos
// colores SIN repetir (mientras haya menos de 17 personas), en un orden que parece aleatorio pero
// es siempre el mismo para cada persona. Ninguno es naranja ni morado, para no confundirse con los roles.
const COLOR_FICHA_GERENTE = ['#FFE2C7', '#C2410C'];
const COLOR_FICHA_ENCARGADO = ['#EDE7FF', '#5B21B6'];
const PALETA_FICHAS = [
  ['#FFE3EA', '#BE123C'], ['#DDF5EF', '#0F766E'], ['#DCF0FF', '#0369A1'], ['#E1F7E6', '#15803D'],
  ['#FFF1C7', '#92600A'], ['#E2E8FF', '#3730A3'], ['#EAE0D5', '#5C4033'], ['#D8F3F5', '#155E75'],
  ['#E9F5C9', '#4D7C0F'], ['#E9EEF4', '#334155'], ['#FBD5D5', '#991B1B'], ['#D3F0E6', '#047857'],
  ['#E0E7FF', '#1D4ED8'], ['#F3E8D8', '#78350F'], ['#CFFAFE', '#0E7490'], ['#F0F4C3', '#827717'],
];
const hashTexto = (t = '') => { let h = 0; for (const ch of String(t).toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h; };
const REGISTRO_COLORES_FICHA = {};
const claveFicha = (x) => (x == null ? '' : String(x).toLowerCase());
// Se llama cada vez que cambia la plantilla: reparte los colores sin repetir.
const asignarColoresFichas = (empleados = []) => {
  Object.keys(REGISTRO_COLORES_FICHA).forEach(k => delete REGISTRO_COLORES_FICHA[k]);
  let usados = 0;
  [...empleados].sort((a, b) => hashTexto(a.id ?? a.name) - hashTexto(b.id ?? b.name)).forEach(e => {
    const par = e.esEncargado ? COLOR_FICHA_GERENTE : e.esGerente ? COLOR_FICHA_ENCARGADO : PALETA_FICHAS[usados++ % PALETA_FICHAS.length];
    REGISTRO_COLORES_FICHA[claveFicha(e.id)] = par;
    REGISTRO_COLORES_FICHA['n:' + claveFicha(e.name)] = par;
  });
};
const colorFicha = (nombre = '', p = null) => {
  if (p?.id != null && REGISTRO_COLORES_FICHA[claveFicha(p.id)]) return REGISTRO_COLORES_FICHA[claveFicha(p.id)];
  if (REGISTRO_COLORES_FICHA['n:' + claveFicha(nombre)]) return REGISTRO_COLORES_FICHA['n:' + claveFicha(nombre)];
  if (p?.esEncargado) return COLOR_FICHA_GERENTE;
  if (p?.esGerente) return COLOR_FICHA_ENCARGADO;
  return PALETA_FICHAS[hashTexto(nombre) % PALETA_FICHAS.length];
};
const monograma = (nombre = '?') => {
  const limpio = String(nombre).trim().replace(/\s+/g, ' ');
  if (!limpio) return '?';
  const letras = [...limpio.replace(/[^\p{L}\p{N} ]/gu, '')];
  if (letras.length === 0) return limpio.charAt(0).toUpperCase();
  const espacio = letras.indexOf(' ');
  // "Ana López" → "AL"; "Fernando" → "Fe".
  if (espacio > 0 && letras[espacio + 1]) return (letras[0] + letras[espacio + 1]).toUpperCase();
  return letras[0].toUpperCase() + (letras[1] ? letras[1].toLowerCase() : '');
};
const FichaPersona = ({ p, nombre, size = 36, turnos = null, morada = false, style = {} }) => {
  const n = nombre ?? p?.name ?? '?';
  const gerente = morada && p?.esEncargado; // (campo esEncargado = Gerente)
  const [fondo, tinta] = morada ? [gerente ? 'linear-gradient(135deg, #FB923C, #EA580C)' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)', '#FFFFFF'] : colorFicha(n, p);
  const px = typeof size === 'number' ? `${size}px` : size;
  const numerico = typeof size === 'number';
  const marcador = turnos && turnos.objetivo != null ? (turnos.asignados === turnos.objetivo ? '#10B981' : '#EF4444') : null;
  return (
    <div aria-hidden="true" className="hd-ficha" style={{
      position: 'relative', width: px, height: px, flexShrink: 0, boxSizing: 'border-box',
      borderRadius: numerico ? `${Math.round(size * 0.3)}px` : '30%',
      background: fondo, border: morada ? 'none' : `1.5px solid ${tinta}33`,
      boxShadow: morada ? (gerente ? '0 2px 6px rgba(234,88,12,0.4)' : '0 2px 6px rgba(109,40,217,0.35)') : `inset 0 -2px 0 ${tinta}14`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: tinta, fontWeight: 900, letterSpacing: '-0.3px', lineHeight: 1,
      fontSize: numerico ? `${Math.max(9, Math.round(size * 0.4))}px` : `calc(${px} * 0.42)`,
      ...style,
    }}>
      {monograma(n)}
      {marcador && (
        <span style={{ position: 'absolute', right: '-3px', bottom: '-3px', width: numerico ? `${Math.max(8, Math.round(size * 0.28))}px` : '30%', height: numerico ? `${Math.max(8, Math.round(size * 0.28))}px` : '30%', borderRadius: '50%', background: marcador, border: '2px solid #FFFFFF' }} />
      )}
    </div>
  );
};
// Posición en la lista de empleados: 0 = Gerente (campo esEncargado), 1 = Encargado (campo
// esGerente; los nombres de los campos están cruzados por razones históricas), 2 = el resto.
const rangoListaEmpleado = (p) => p.esEncargado ? 0 : p.esGerente ? 1 : 2;

const ENTRADAS_COMIDA = ['07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00'];
const SALIDAS_COMIDA  = ['15:30', '16:00', '16:30', '17:00', '17:30', '18:00'];
const ENTRADAS_NOCHE  = ['19:00', '19:30', '20:00', '20:30', '21:00'];
const SALIDAS_NOCHE   = ['22:30', '23:00', '23:30', '00:00', '00:30', '01:00', '01:30', '02:00'];
// ENLACE (2 oct 2026): cada persona puede tener su propia hora de entrada y de salida, así que
// los rangos son más amplios que los de comida/cena (que están pensados para turnos fijos).
const ENLACE_ENTRADAS = ['12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00'];
const ENLACE_SALIDAS  = ['21:00', '21:30', '22:00', '22:30', '23:00', '23:30', '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00'];

// Identificador del local por defecto. Un restaurante que nunca haya creado locales sigue
// teniendo uno solo, invisible, con este id — así nada cambia para quien no use varios locales.
const LOCAL_PRINCIPAL = 'principal';

// Devuelve siempre una lista de locales usable, aunque el negocio no haya creado ninguno.
const listaLocales = (config) => {
  const l = config?.locales;
  if (Array.isArray(l) && l.length > 0) return l;
  return [{ id: LOCAL_PRINCIPAL, nombre: config?.restaurantName || 'Mi local' }];
};

const DEFAULT_CONFIG = {
  restaurantName: '',
  locales: [],
  aperturaHora: '12:30',
  comidaStart: '13:30', comidaEnd: '17:00',
  nocheStart: '20:30', nocheEnd: '00:30',
  personasApertura: 2,
  permiteTurnoPartido: true,
  diasCierreSemana: [],
  diasDescansoSemana: 2,
  diasDescansoConsecutivos: true,
  logoEmpresaUrl: null,
  // Foto de la carta del restaurante (pestaña "Menú y Mapa"). A diferencia del logo, esta NO
  // se guarda como base64 en Firestore — pesaría demasiado. Aquí solo vive la URL de descarga
  // de Firebase Storage; el archivo real está en `restaurantes/{uid}/menu/carta.jpg`.
  menuFotoUrl: null,
  // Mapa de mesas dibujado por el encargado (pestaña "Mapa"): array de celdas ocupadas,
  // `{ x, y, tipo }` con tipo en 'mesa'|'silla'|'barra'|'entrada'. Vacío hasta que se dibuje algo.
  mapaMesas: [],
  configurado: false,
  // 'basico' | 'estandar' | 'premium' — hoy se cambia a mano desde Configuración;
  // cuando conectemos Stripe, será el pago confirmado el que escriba este valor.
  plan: 'basico',
};

const DEFAULT_TEMPORADAS = {
  baja:   { nombre: 'Temporada baja',   minComida: 3, minNoche: 4 },
  normal: { nombre: 'Temporada normal', minComida: 4, minNoche: 5 },
  alta:   { nombre: 'Temporada alta',   minComida: 5, minNoche: 7 },
};

// Modelo de precios (13 ago 2026): ya NO hay planes con funciones distintas — todo restaurante
// tiene acceso a TODO desde el primer empleado (Cocina, logo, varios locales...). Se paga por
// empleado, con precio reducido para los primeros empleados. Sin descuento por volumen público
// (quitado el 9 oct 2026, a petición de Fran; las rebajas a clientes grandes se negocian a mano).
// Único límite que queda es técnico, no comercial: un tope de empleados por restaurante para no
// arriesgar el tamaño del documento de Firestore.
// OJO — esto es solo el CÁLCULO del precio que se enseña en la app. El cobro real en Stripe
// (metered billing) hay que configurarlo aparte, y el "margen" para que sumar 1 empleado no
// dispare de golpe el tramo de precio queda pendiente a propósito: necesita guardar cada mes
// cuántos empleados se facturaron (snapshot mensual), que es una pieza aparte todavía sin construir.
// CAMBIO (5 oct 2026, a petición de Fran): precio bajo 4,70€ (antes 4,50€) y umbral en 6 (antes 8).
// (OBSOLETO desde el 16 oct 2026: ya no hay tramos; ver subtotalPorTramos más abajo. Menos de 6
// empleados → todos a 4,70€; 6 o más → todos a 5,30€.) La app enseña "5,30€ por empleado" como
// tarifa estándar y, debajo, la bonificación de 4,70€ para locales de menos de 6 empleados.
// OJO: estos números hay que mantenerlos IGUALES en functions/index.js (es lo que factura).
const PRECIO_EMPLEADO_BAJO = 4.70;              // locales con menos de 6 empleados (todos a este precio)
const PRECIO_EMPLEADO_ALTO = 5.30;              // tarifa estándar: locales con 6 o más empleados
const UMBRAL_EMPLEADOS_PRECIO_ALTO = 6;
// IVA (29 oct 2026, decisión de Fran): los precios se ENSEÑAN sin IVA ("18,80 € + IVA"), como es
// normal al vender a negocios, y en la pantalla de PAGO sale el total final con el IVA ya sumado
// (22,75 €), para que quien paga por Bizum o transferencia mande la cantidad exacta sin calcular nada.
const IVA_PORCENTAJE = 21;
// { base, iva, total } en euros, redondeado al céntimo como en una factura: el IVA se calcula sobre
// la base en céntimos y el total es base + IVA (18,80 → IVA 3,95 → 22,75).
const conIVA = (base) => {
  const baseCent = Math.round((Number(base) || 0) * 100);
  const ivaCent = Math.round(baseCent * IVA_PORCENTAJE / 100);
  return { base: baseCent / 100, iva: ivaCent / 100, total: (baseCent + ivaCent) / 100 };
};
const PRECIO_MINIMO_MENSUAL = 15;               // 22 ago 2026: 20€; bajado a 15€ el 16 oct 2026 (a petición de Fran). Sin IVA, por cliente
const MAX_EMPLEADOS_POR_RESTAURANTE = 700;      // tope técnico, no es un plan
const MAX_LOCALES_POR_RESTAURANTE = 50;         // tope técnico, no es un plan

// PRECIO POR TRAMOS (29 sep 2026, a petición de Fran — "margen inteligente"): antes, al cruzar
// el umbral, TODOS los empleados saltaban de golpe al precio alto (una subida enorme por sumar
// solo 1 persona). Ahora es como un tramo fiscal: los primeros 5 empleados (desde el 5 oct 2026;
// antes 7) siempre se pagan al precio bajo, y SOLO el empleado 6 en adelante se paga al
// precio alto — así sumar un empleado que cruza el umbral solo encarece ESE empleado, no toda
// la plantilla. Ya no hay descuento por volumen (quitado el 9 oct 2026).
// CAMBIO (16 oct 2026, a petición de Fran): ya NO es por tramos. La tarifa depende del tamaño
// del local y se aplica a TODA la plantilla: menos de 6 personas → todas a 4,70€; 6 o más →
// todas a 5,30€ (ej.: 5 personas = 23,50€; 8 personas = 8 × 5,30€ = 42,40€). OJO: esto hace que
// pasar de 5 a 6 personas suba el total de golpe (de 23,50€ a 31,80€). Es lo que se ha decidido.
const subtotalPorTramos = (n) => n * (n < UMBRAL_EMPLEADOS_PRECIO_ALTO ? PRECIO_EMPLEADO_BAJO : PRECIO_EMPLEADO_ALTO);
const calcularPrecioMensual = (n) => {
  const subtotal = subtotalPorTramos(n);
  return Math.max(PRECIO_MINIMO_MENSUAL, Math.round(subtotal * 100) / 100);
};
// Texto del desglose por tramos, para no enseñar una "media" confusa (29 sep 2026, a
// petición de Fran — quedó claro que la media por empleado, aunque el cálculo de abajo ya era
// correcto, se leía como si el precio de cada persona cambiara solo. Esto enseña los DOS precios
// fijos tal cual son: los primeros 5 siempre a PRECIO_EMPLEADO_BAJO, el resto siempre a
// PRECIO_EMPLEADO_ALTO — nunca varía ninguno de los dos números en sí.
const textoDesglosePorTramos = (n) => {
  const fmt = (v) => v.toLocaleString('es-ES', { minimumFractionDigits: 2 });
  return `${n} a ${fmt(n < UMBRAL_EMPLEADOS_PRECIO_ALTO ? PRECIO_EMPLEADO_BAJO : PRECIO_EMPLEADO_ALTO)}€`;
};

// Versión en dos etiquetas planas del desglose de arriba (29 sep 2026, a petición de Fran:
// "que quede bien estéticamente pero no con brillos ni nada"). Fondo liso, sin degradado ni
// sombra — solo dos pastillas de texto, una por tramo de precio.
const DesglosePrecioBadges = () => {
  // CAMBIO (3 oct 2026, a petición de Fran): antes enseñaba cuántos empleados caían en cada
  // tramo ("7 × 4,50€", "13 × 5,30€"). Después enseñaba la tarifa de cada tramo. CAMBIO (5 oct
  // 2026, a petición de Fran, que lo veía "demasiado raro"): ahora el precio de referencia es
  // "5,30€ por empleado" y debajo se dice que los 5 primeros salen a 4,70€. Se enseña el PRECIO
  // y no un porcentaje a propósito: 4,70€ sobre 5,30€ es un 11,3% de descuento, un número
  // feo que nadie va a comprobar, y un "10%" daría 4,77€ y no 4,70€. El cálculo del total
  // (subtotalPorTramos): menos de 6 empleados → todos a 4,70€; 6 o más → todos a 5,30€ (16 oct 2026).
  const fmt = (v) => v.toLocaleString('es-ES', { minimumFractionDigits: 2 });
  const badgeStyle = { display: 'inline-flex', alignItems: 'center', background: C.bg, border: `1px solid ${C.border}`, color: C.textMuted, fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '6px' };
  const badgePrincipal = { ...badgeStyle, color: C.primaryDark, borderColor: C.primaryBorder, background: C.primaryPale, fontWeight: '800' };
  // CAMBIO (11 oct 2026, textos exactos pedidos por Fran): dos líneas, una debajo de otra.
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '6px', verticalAlign: 'middle', maxWidth: '100%' }}>
      <span style={{ ...badgePrincipal, fontSize: '12px', padding: '4px 11px' }}>Tarifa Estándar: {fmt(PRECIO_EMPLEADO_ALTO)} € / empleado al mes + IVA.</span>
      <span style={{ ...badgeStyle, fontSize: '11.5px', padding: '4px 11px', textAlign: 'center', lineHeight: 1.45, display: 'inline-block' }}>
        <strong style={{ color: C.text }}>Bonificación Locales Pequeños:</strong> {fmt(PRECIO_EMPLEADO_BAJO)} € / empleado al mes + IVA. (Para locales con menos de {UMBRAL_EMPLEADOS_PRECIO_ALTO} empleados)
      </span>
    </span>
  );
};


// Cuántos mensajes se descargan al abrir el chat. Antes se descargaba el historial ENTERO cada
// vez que alguien abría la app o se reconectaba, y eso crece para siempre: con el tiempo son
// miles de lecturas por persona y por día, que se pagan. Con un tope, da igual que haya 100
// mensajes o 10.000 — siempre se leen los últimos.
// El chat privado lleva un tope más alto porque en tu buzón están todas tus conversaciones
// privadas juntas (desde el 29 oct 2026 cada persona solo descarga su propio buzón).
const MAX_MENSAJES_GRUPO = 50;
const MAX_MENSAJES_PRIVADOS = 200;

// Cuánto tiempo se conservan los mensajes antes de borrarse solos. Cada mensaje nuevo guarda su
// fecha de caducidad y Firestore lo elimina por su cuenta al llegar (política TTL, que hay que
// activar una vez en la consola de Firebase). Sirve para no guardar conversaciones para siempre,
// que es lo que pide el RGPD: los datos no deben conservarse más tiempo del necesario.
const MESES_CONSERVAR_MENSAJES = 12;
const fechaCaducidadMensaje = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + MESES_CONSERVAR_MENSAJES);
  return d;
};

// Límite del historial de horarios publicados, PARA EVITAR que el documento del restaurante
// crezca sin fin (cada publicación añade una entrada y hoy no se borra nada). Se agrupa por MES
// DE CALENDARIO real (no por cantidad de publicaciones) y por LOCAL por separado, para que un
// restaurante con varios locales no vea cómo el historial de uno se come el hueco del otro.
// Cuando un local acumula HISTORIAL_MESES_MAX meses distintos con horarios guardados, se borran
// los meses más antiguos y se dejan solo los HISTORIAL_MESES_TRAS_LIMPIAR más recientes.
// Ejemplo con HISTORIAL_MESES_MAX=4 y HISTORIAL_MESES_TRAS_LIMPIAR=2: hay horarios de Enero,
// Febrero, Marzo y Abril (4 meses) -> se borra TODO Enero y TODO Febrero -> quedan Marzo y Abril.
// El mes en curso, al ser siempre el más reciente, nunca se borra con esta regla — por eso el
// cómputo de "turnos trabajados este mes" (que usa el Dueño para la nómina) sigue siendo fiable.
const HISTORIAL_MESES_MAX = 4;
const HISTORIAL_MESES_TRAS_LIMPIAR = 2;
const limpiarHistorialAntiguo = (historial) => {
  const claveMes = (h) => {
    if (!h.publishedAtTimestamp) return null; // sin fecha fiable: no se agrupa, no se borra
    const d = new Date(h.publishedAtTimestamp);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const porLocal = {};
  historial.forEach(h => {
    const loc = h.localId || LOCAL_PRINCIPAL;
    (porLocal[loc] = porLocal[loc] || []).push(h);
  });
  let resultado = [];
  Object.values(porLocal).forEach(entradas => {
    const mesesDistintos = [...new Set(entradas.map(claveMes).filter(Boolean))].sort();
    let mesesABorrar = new Set();
    if (mesesDistintos.length >= HISTORIAL_MESES_MAX) {
      mesesABorrar = new Set(mesesDistintos.slice(0, mesesDistintos.length - HISTORIAL_MESES_TRAS_LIMPIAR));
    }
    resultado = resultado.concat(entradas.filter(h => !mesesABorrar.has(claveMes(h))));
  });
  return resultado;
};

const Badge = ({ children, color = 'primary' }) => {
  const colors = {
    primary: { bg: C.primaryPale, text: C.primary }, success: { bg: C.successBg, text: C.success },
    warning: { bg: C.warningBg, text: C.warning }, danger: { bg: C.dangerBg, text: C.danger },
    muted: { bg: C.border, text: C.textMuted }, info: { bg: '#DBEAFE', text: '#1D4ED8' },
  };
  const c = colors[color] || colors.primary;
  return <span style={{ background: c.bg, color: c.text, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{children}</span>;
};

// === ICONOS PROPIOS DE HORIDIA (4 oct 2026) ===
// Sustituyen a los emojis que había repartidos por la app (que se veían como los de WhatsApp y
// cambiaban de aspecto según el móvil). Mismo estilo que los iconos de las pestañas principales
// (TAB_ICON_PATHS): dibujo de línea, trazo redondeado y, por defecto, del mismo color que el
// texto que los rodea (currentColor), así que funcionan igual sobre fondo blanco o sobre un botón
// morado. Su tamaño va en "em", así que crecen o encogen solos con el tamaño de letra de donde
// estén — un icono dentro de un título grande sale grande sin tener que tocar nada.
//
// Cada icono es una lista de piezas: ['p', d] = trazo, ['c', cx, cy, r] = círculo,
// ['r', x, y, ancho, alto, radio] = rectángulo, ['e', cx, cy, rx, ry] = elipse.
const ICONOS = {
  refresh: [['p', 'M20 11a8 8 0 0 0-14.6-4.5L4 8'], ['p', 'M4 4v4h4'], ['p', 'M4 13a8 8 0 0 0 14.6 4.5L20 16'], ['p', 'M20 20v-4h-4']],
  trash: [['p', 'M4 7h16'], ['p', 'M10 11v6M14 11v6'], ['p', 'M5.5 7l1 12.5A2 2 0 0 0 8.5 21h7a2 2 0 0 0 2-1.5L18.5 7'], ['p', 'M9 7V4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V7']],
  alert: [['p', 'M12 3.5 2.8 19.5a1 1 0 0 0 .9 1.5h16.6a1 1 0 0 0 .9-1.5L12 3.5z'], ['p', 'M12 10v4.5'], ['p', 'M12 17.5h.01']],
  calendar: [['r', 3.5, 5, 17, 15.5, 3], ['p', 'M3.5 10h17M8 3v4M16 3v4'], ['p', 'M8 14h.01M12 14h.01M16 14h.01M8 17.2h.01M12 17.2h.01']],
  utensils: [['p', 'M7 3v8'], ['p', 'M4.5 3v5a2.5 2.5 0 0 0 5 0V3'], ['p', 'M7 11v10'], ['p', 'M17 21V3c-2.2 1.2-3.5 3.6-3.5 7v3.5H17']],
  hourglass: [['p', 'M6 3h12M6 21h12'], ['p', 'M7 3c0 4 5 5.5 5 9s-5 5-5 9'], ['p', 'M17 3c0 4-5 5.5-5 9s5 5 5 9']],
  star: [['p', 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z']],
  users: [['c', 9, 8, 3.2], ['p', 'M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6'], ['c', 17, 9, 2.4], ['p', 'M17 14.2c2.4.3 4 2.2 4 4.8']],
  pin: [['p', 'M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z'], ['c', 12, 10, 2.3]],
  home: [['p', 'M3.5 10.5 12 4l8.5 6.5'], ['p', 'M5.5 9v11h13V9'], ['p', 'M10 20v-5.5h4V20']],
  briefcase: [['r', 3.5, 7.5, 17, 12, 2], ['p', 'M9 7.5V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5v2'], ['p', 'M3.5 13h17']],
  pan: [['c', 9, 13.5, 5.5], ['c', 9, 13.5, 1.8], ['p', 'M14 11.3l6.5-3.6']],
  sun: [['c', 12, 12, 4], ['p', 'M12 2.5V5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8']],
  baja: [['p', 'M9 3.5h6V9h5.5v6H15v5.5H9V15H3.5V9H9z']],
  crown: [['p', 'M3.5 8l4.2 4 4.3-6 4.3 6 4.2-4-1.8 10.5H5.3z'], ['p', 'M6 21h12']],
  moon: [['p', 'M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z']],
  link: [['p', 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.2 1.2'], ['p', 'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.2-1.2']],
  plus: [['p', 'M12 5v14M5 12h14']],
  checkCircle: [['c', 12, 12, 9], ['p', 'M8 12.3l2.7 2.7L16 9.6']],
  xCircle: [['c', 12, 12, 9], ['p', 'M9 9l6 6M15 9l-6 6']],
  door: [['p', 'M6 21V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21'], ['p', 'M4 21h16'], ['p', 'M14.5 12.5h.01']],
  user: [['c', 12, 8, 3.5], ['p', 'M5 20.5c0-3.9 3.1-7 7-7s7 3.1 7 7']],
  gear: [['p', 'M4 7h9.5M18.5 7H20M4 17h3.5M12.5 17H20'], ['c', 16, 7, 2.5], ['c', 10, 17, 2.5]],
  clipboard: [['r', 9, 3.5, 6, 3, 1], ['p', 'M8 5H6.5A1.5 1.5 0 0 0 5 6.5v13A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 17.5 5H16'], ['p', 'M9 12h6M9 16h4']],
  coffee: [['p', 'M4.5 9h12v5a5 5 0 0 1-5 5h-2a5 5 0 0 1-5-5z'], ['p', 'M16.5 10.5h1.5a2.5 2.5 0 0 1 0 5h-1.7'], ['p', 'M8 3.5V6M12 3.5V6']],
  key: [['c', 8, 15, 4], ['p', 'M10.8 12.2 20 3'], ['p', 'M17 6l2.5 2.5M14.5 8.5l2 2']],
  card: [['r', 3, 6, 18, 12, 2], ['p', 'M3 10.5h18M7 14.5h3']],
  hash: [['p', 'M5 9h14M5 15h14M10 4 8 20M16 4l-2 16']],
  clock: [['c', 12, 12, 9], ['p', 'M12 7.5V12l3 2']],
  folder: [['p', 'M3.5 6.5A1.5 1.5 0 0 1 5 5h4.5l2 2.5H19a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18z']],
  eraser: [['p', 'M8.5 20h11'], ['p', 'M4.7 13.3l8.6-8.6a1.5 1.5 0 0 1 2.1 0l4 4a1.5 1.5 0 0 1 0 2.1L12 18.2a3.5 3.5 0 0 1-2.5 1H8.7a3.5 3.5 0 0 1-2.5-1l-1.5-1.5a1.5 1.5 0 0 1 0-2.1z'], ['p', 'M9 9l6 6']],
  chef: [['p', 'M7 14.5V20h10v-5.5'], ['p', 'M7 14.5a4 4 0 0 1-.6-7.9A4.5 4.5 0 0 1 12 3.5a4.5 4.5 0 0 1 5.6 3.1 4 4 0 0 1-.6 7.9z'], ['p', 'M7 17.5h10']],
  shuffle: [['p', 'M3.5 7h3.5c4 0 6 10 10 10h3.5'], ['p', 'M18 14.5 20.5 17 18 19.5'], ['p', 'M3.5 17h3.5c1.6 0 2.8-1.6 3.8-3.6'], ['p', 'M13.2 10.6c1-2 2.2-3.6 3.8-3.6h3.5'], ['p', 'M18 4.5 20.5 7 18 9.5']],
  sunrise: [['p', 'M4 18h16'], ['p', 'M7 18a5 5 0 0 1 10 0'], ['p', 'M12 9.5V4M9.5 6.5 12 4l2.5 2.5'], ['p', 'M5.2 11.2l1.4 1.4M18.8 11.2l-1.4 1.4M2.5 15h1.5M20 15h1.5']],
  mic: [['r', 9, 3, 6, 11, 3], ['p', 'M5.5 11a6.5 6.5 0 0 0 13 0'], ['p', 'M12 17.5V21M9 21h6']],
  camera: [['p', 'M3.5 8.5A1.5 1.5 0 0 1 5 7h2.5l1.5-2.5h6L16.5 7H19a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18z'], ['c', 12, 13, 3.5]],
  lock: [['r', 5, 11, 14, 10, 2], ['p', 'M8 11V7.5a4 4 0 0 1 8 0V11'], ['p', 'M12 15v2.5']],
  unlock: [['r', 5, 11, 14, 10, 2], ['p', 'M8 11V7.5a4 4 0 0 1 7.7-1.5'], ['p', 'M12 15v2.5']],
  map: [['p', 'M3.5 6.5 9 4l6 2.5L20.5 4v13.5L15 20l-6-2.5-5.5 2.5z'], ['p', 'M9 4v13.5M15 6.5V20']],
  wand: [['p', 'M4.5 19.5 13 11'], ['p', 'M16.5 3.5l1.1 2.9 2.9 1.1-2.9 1.1-1.1 2.9-1.1-2.9-2.9-1.1 2.9-1.1z']],
  party: [['p', 'M4 20l3.6-10.2 6.6 6.6z'], ['p', 'M11.5 5c.9 1.4.4 3-.7 3.7'], ['p', 'M19 12.5c-1.4-.9-3-.4-3.7.7'], ['p', 'M15 3.5l.4 1.6M20.5 8.5l-1.6.4M17.5 6.5h.01']],
  cocktail: [['p', 'M4.5 4h15L12 12.5z'], ['p', 'M12 12.5V20M8.5 20h7'], ['p', 'M7.2 7h9.6']],
  receipt: [['p', 'M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21z'], ['p', 'M9 8h6M9 12h6M9 16h3']],
  chat: [['p', 'M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3.5V17H6.5A2.5 2.5 0 0 1 4 14.5z']],
  bell: [['p', 'M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15z'], ['p', 'M10 20.5a2 2 0 0 0 4 0']],
  euro: [['c', 12, 12, 8.5], ['p', 'M15 8.8a4 4 0 1 0 0 6.4'], ['p', 'M8 11h5.5M8 13.2h5.5']],
  building: [['p', 'M5 21V4.5A1.5 1.5 0 0 1 6.5 3h7A1.5 1.5 0 0 1 15 4.5V21'], ['p', 'M15 9h3.5A1.5 1.5 0 0 1 20 10.5V21'], ['p', 'M3 21h18'], ['p', 'M8.5 7h3M8.5 11h3M8.5 15h3']],
  hand: [['p', 'M8 13V5.5a1.5 1.5 0 0 1 3 0V11'], ['p', 'M11 10.5V4a1.5 1.5 0 0 1 3 0v7'], ['p', 'M14 10.5V6a1.5 1.5 0 0 1 3 0v8a7 7 0 0 1-7 7h-.5a6 6 0 0 1-4.6-2.2l-1.4-1.7a1.6 1.6 0 0 1 2.5-2L8 15.5']],
  bed: [['p', 'M3 19V6'], ['p', 'M3 13.5h18V19'], ['p', 'M21 13.5V12a3 3 0 0 0-3-3h-7v4.5'], ['c', 7, 10.5, 1.8]],
  pencil: [['p', 'M14.5 5.5l4 4'], ['p', 'M4 20l1-4.5L16 4.5a1.4 1.4 0 0 1 2 0l1.5 1.5a1.4 1.4 0 0 1 0 2L8.5 19z']],
  search: [['c', 11, 11, 6.5], ['p', 'M16 16l4.5 4.5']],
  eye: [['p', 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z'], ['c', 12, 12, 3]],
  cap: [['p', 'M2.5 9 12 4.5 21.5 9 12 13.5z'], ['p', 'M6.5 11v4.5c0 1.5 2.5 3 5.5 3s5.5-1.5 5.5-3V11'], ['p', 'M21.5 9v5']],
  wall: [['r', 3, 5, 18, 14, 1.5], ['p', 'M3 9.7h18M3 14.3h18'], ['p', 'M9 5v4.7M15 9.7v4.6M9 14.3V19M15 5v4.7']],
  tag: [['p', 'M3.5 12.5V4.5a1 1 0 0 1 1-1h8l8 8a1.4 1.4 0 0 1 0 2l-7 7a1.4 1.4 0 0 1-2 0z'], ['c', 8, 8, 1.3]],
  box: [['p', 'M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5z'], ['p', 'M3.5 7.5 12 12l8.5-4.5'], ['p', 'M12 12v9']],
  shield: [['p', 'M12 3 4.5 6v5.5c0 4.5 3.2 8 7.5 9.5 4.3-1.5 7.5-5 7.5-9.5V6z']],
  gift: [['r', 4, 9.5, 16, 4, 1], ['p', 'M5.5 13.5V20.5h13v-7'], ['p', 'M12 9.5v11'], ['p', 'M12 9.5C10.2 6 6.2 5.8 7 8.3c.5 1.2 2.8 1.2 5 1.2z'], ['p', 'M12 9.5c1.8-3.5 5.8-3.7 5-1.2-.5 1.2-2.8 1.2-5 1.2z']],
  mail: [['r', 3, 5.5, 18, 13, 3], ['p', 'M3.5 8l8.5 6 8.5-6']],
  inbox: [['p', 'M3.5 13 6 5h12l2.5 8v6h-17z'], ['p', 'M3.5 13h5l1.5 2.5h4l1.5-2.5h5']],
  tie: [['p', 'M9.5 3h5l-1 3 2.5 11-4 4-4-4 2.5-11z']],
  flame: [['p', 'M12 21a6 6 0 0 0 6-6c0-4-3-6-4-10-2 2-3.5 4-3.5 6.5-1-.7-1.5-2-1.5-3C7.2 10 6 12.4 6 15a6 6 0 0 0 6 6z']],
  snow: [['p', 'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9'], ['p', 'M9.5 4.5 12 6l2.5-1.5M9.5 19.5 12 18l2.5 1.5']],
  cake: [['r', 4, 12, 16, 9, 1.5], ['p', 'M4 16c1.3 0 1.3 1 2.7 1s1.3-1 2.6-1 1.4 1 2.7 1 1.3-1 2.7-1 1.3 1 2.6 1 1.4-1 2.7-1'], ['p', 'M12 12V8.5M12 5.5v.5']],
  help: [['c', 12, 12, 9], ['p', 'M9.6 9.5a2.5 2.5 0 0 1 4.8.8c0 1.7-2.4 2.2-2.4 3.7'], ['p', 'M12 17h.01']],
  info: [['c', 12, 12, 9], ['p', 'M12 11v5.5'], ['p', 'M12 7.8h.01']],
  square: [['r', 5, 5, 14, 14, 2.5]],
  circle: [['c', 12, 12, 7.5]],
  rect: [['r', 3, 7.5, 18, 9, 2]],
  oval: [['e', 12, 12, 9, 5.5]],
  upload: [['p', 'M12 15V4'], ['p', 'M7.5 8.5 12 4l4.5 4.5'], ['p', 'M4 14v5.5A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5V14']],
  file: [['p', 'M6 3h8l4.5 4.5V21H6z'], ['p', 'M14 3v4.5h4.5'], ['p', 'M9 13h6M9 17h6']],
  trophy: [['p', 'M8 4h8v5a4 4 0 0 1-8 0z'], ['p', 'M8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4'], ['p', 'M12 13v4M8.5 21h7'], ['p', 'M10 17h4v4h-4z']],
  sad: [['c', 12, 12, 9], ['p', 'M9 9.5h.01M15 9.5h.01'], ['p', 'M8.5 16c2-1.7 5-1.7 7 0']],
  timer: [['c', 12, 13.5, 7.5], ['p', 'M12 10v3.5l2 1.5'], ['p', 'M10 3h4M12 3v3']],
  send: [['p', 'M4 12 20 4l-6 16-2.5-6.5z'], ['p', 'M11.5 13.5 20 4']],
  expand: [['p', 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5']],
  image: [['r', 3.5, 5, 17, 14, 2], ['p', 'M3.5 16l5-5 4 4 2.5-2.5 5.5 5.5'], ['c', 15.5, 9, 1.5]],
  undo: [['p', 'M9 14 4 9l5-5'], ['p', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11']],
};

// Qué icono corresponde a cada emoji que se usaba antes. Se guardan sin el "selector de variante"
// invisible (U+FE0F) que llevan algunos emojis, y se buscan con y sin él.
const EMOJI_A_ICONO = {
  '🔄': 'refresh', '🗑': 'trash', '⚠': 'alert', '📅': 'calendar', '🗓': 'calendar', '📆': 'calendar',
  '🍽': 'utensils', '⏳': 'hourglass', '⭐': 'star', '👥': 'users', '📍': 'pin', '🏠': 'home',
  '💼': 'briefcase', '🍳': 'chef', '🏖': 'sun', '🩼': 'baja', '👑': 'crown', '🌙': 'moon', '🔗': 'link',
  '➕': 'plus', '✅': 'checkCircle', '🚪': 'door', '👤': 'user', '⚙': 'gear', '📋': 'clipboard',
  '☕': 'coffee', '🔑': 'key', '💳': 'card', '🔢': 'hash', '🕓': 'clock', '🕐': 'clock', '📁': 'folder',
  '🗂': 'folder', '🧹': 'eraser', '👨\u200D🍳': 'chef', '🔀': 'shuffle', '🌅': 'sunrise', '🎙': 'mic',
  '📷': 'camera', '🔒': 'lock', '🔐': 'lock', '🔓': 'unlock', '🗺': 'map', '✨': 'wand', '🎉': 'party',
  '🍸': 'cocktail', '🧾': 'receipt', '💬': 'chat', '🔔': 'bell', '💰': 'euro', '🏢': 'building',
  '🏨': 'building', '🙋': 'hand', '👋': 'hand', '🛌': 'bed', '✏': 'pencil', '🔍': 'search', '👁': 'eye',
  '🧠': 'cap', '🧱': 'wall', '🏷': 'tag', '📦': 'box', '🛡': 'shield', '🎁': 'gift', '✉': 'mail',
  '📬': 'inbox', '👔': 'tie', '🔥': 'flame', '❄': 'snow', '🍰': 'cake', '❓': 'help', 'ℹ': 'info',
  '⬜': 'square', '⚪': 'circle', '▭': 'rect', '⬭': 'oval', '📤': 'upload', '📄': 'file',
  '🟢': 'checkCircle', '🔴': 'xCircle', '🏆': 'trophy', '😕': 'sad', '⏱': 'timer', '➤': 'send',
  '⛶': 'expand', '🖼': 'image', '↩': 'undo',
};
const escaparRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Primero los más largos, para que "👨‍🍳" no se parta en dos.
const EMOJI_REGEX = new RegExp(
  Object.keys(EMOJI_A_ICONO).sort((a, b) => b.length - a.length).map(e => escaparRegex(e) + '\\uFE0F?').join('|') + '|\\uFE0F',
  'g'
);

// Las piezas de un icono como elementos SVG (sirve tanto para <Ico> como para meter un icono
// dentro de otro dibujo SVG, como el plano de mesas).
const piezasIcono = (n) => (ICONOS[n] || []).map((p, i) => p[0] === 'p' ? <path key={i} d={p[1]} />
  : p[0] === 'c' ? <circle key={i} cx={p[1]} cy={p[2]} r={p[3]} />
  : p[0] === 'r' ? <rect key={i} x={p[1]} y={p[2]} width={p[3]} height={p[4]} rx={p[5]} />
  : <ellipse key={i} cx={p[1]} cy={p[2]} rx={p[3]} ry={p[4]} />);

// Un icono suelto. n = nombre en ICONOS; s = tamaño (por defecto, el de la letra); c = color.
const Ico = ({ n, s = '1.15em', c = 'currentColor', w = 2, style }) => {
  if (!ICONOS[n]) return null;
  return (
    <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke={c} strokeWidth={w}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
      style={{ display: 'inline-block', verticalAlign: '-0.2em', flexShrink: 0, ...style }}>
      {piezasIcono(n)}
    </svg>
  );
};

// Nombre del icono que corresponde a un texto que sea solo un emoji ('🗑️' → 'trash'), o null.
const iconoDeEmoji = (texto) => {
  if (!texto || typeof texto !== 'string') return null;
  return EMOJI_A_ICONO[texto.replace(/\uFE0F/g, '').trim()] || null;
};

// Convierte un texto que lleva emojis en una mezcla de texto + iconos, lista para pintarse en
// JSX: conIconos('🔄 Regenerar') → [<Ico n="refresh"/>, ' Regenerar']. Si el texto no lleva
// ningún emoji conocido, devuelve el mismo texto tal cual (no cambia nada).
const conIconos = (texto) => {
  if (typeof texto !== 'string' || !texto) return texto;
  EMOJI_REGEX.lastIndex = 0;
  if (!EMOJI_REGEX.test(texto)) return texto;
  EMOJI_REGEX.lastIndex = 0;
  const partes = [];
  let ultimo = 0, m, k = 0;
  while ((m = EMOJI_REGEX.exec(texto)) !== null) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    const nombre = EMOJI_A_ICONO[m[0].replace(/\uFE0F/g, '')];
    if (nombre) partes.push(<Ico key={'i' + (k++)} n={nombre} />);
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
};

const EmptyState = ({ icon, text }) => (
  <div style={{ textAlign: 'center', padding: '40px 20px', color: C.textLight }}>
    {iconoDeEmoji(icon) ? (
      <div style={{ width: '60px', height: '60px', borderRadius: '18px', background: C.primaryFaint, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
        <Ico n={iconoDeEmoji(icon)} s="28px" />
      </div>
    ) : <div style={{ fontSize: '40px', marginBottom: '10px' }}>{icon}</div>}
    <p style={{ margin: 0, fontSize: '14px' }}>{text}</p>
  </div>
);

const calcHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return +(mins / 60).toFixed(1);
};

// Fondo y texto de cada turno donde se pinta la celda entera de color (pestañas del Empleado,
// historial…). Comparte la codificación de color con shiftAccent, pero en versión translúcida
// Fondo y texto de cada turno donde se pinta la celda entera de color (pestañas del Empleado,
// historial…). Vuelta a los pasteles claros originales.
// NUEVA PALETA (2 oct 2026, pedido por Fran). Dos formas de pintar un turno:
//  - Turnos de trabajo "normales" (apertura amarillo, almuerzo azul, cena morado, partido rosa):
//    celda neutra + franja vertical de color (`stripe`) — el color da la lectura rápida sin
//    teñir toda la celda.
//  - Enlace (verde claro), vacaciones (cian), baja (naranja) y libre (celeste): SIN franja,
//    se tiñe la celda entera (`bg`/`text`).
// Todos los sitios que pintan un turno pasan por esta función, así que el color es el mismo
// en el panel del Encargado, en la vista del empleado y en el historial.
const shiftColor = (tipo) => {
  if (!tipo || tipo === 'libre') return { bg: '#E0F2FE', text: '#0369A1' };
  if (tipo === 'apertura') return { bg: C.primaryFaint, text: C.text, stripe: '#EAB308' };
  if (tipo === 'comida') return { bg: C.primaryFaint, text: C.text, stripe: '#EC4899' };
  if (tipo === 'noche') return { bg: C.primaryFaint, text: C.text, stripe: '#7C3AED' };
  if (tipo === 'partido') return { bg: C.primaryFaint, text: C.text, stripe: '#3B82F6' };
  if (tipo === 'enlace') return { bg: '#BBF7D0', text: '#166534' };
  if (tipo === 'vacaciones') return { bg: '#A5F3FC', text: '#155E75' };
  if (tipo === 'baja') return { bg: '#FED7AA', text: '#9A3412' };
  return { bg: C.primaryPale, text: C.primaryDark };
};

const shiftLabel = (tipo) => {
  if (tipo === 'apertura') return 'Apertura';
  if (tipo === 'comida') return 'Almuerzo';
  if (tipo === 'noche') return 'Cena';
  if (tipo === 'partido') return 'Turno Partido';
  if (tipo === 'enlace') return 'Enlace';
  if (tipo === 'vacaciones') return 'Vacaciones';
  if (tipo === 'baja') return 'De baja';
  return 'Libre';
};
// Nombre del turno para meterlo DENTRO de una frase (16 oct 2026): "turno de cena", "turno partido".
// shiftLabel da la etiqueta con mayúscula ("Cena", "Turno Partido"), que dentro de una frase quedaba
// "turno de Cena" o, peor, "turno de Turno Partido".
const turnoEnTexto = (tipo) => (tipo === 'partido' ? 'turno partido' : `turno de ${shiftLabel(tipo).toLowerCase()}`);

// =====================================================================================
// CASILLA DE TURNO (10 oct 2026, rediseño pedido por Fran). Una sola pieza para pintar un turno
// en TODAS las tablas de la app (Encargado, empleados, historial, locales), así se ve igual en
// todas partes. Lo importante se lee de un vistazo: el color y el icono dicen qué turno es, y la
// HORA va grande, en números de ancho fijo para que las columnas queden alineadas. A la izquierda,
// una franja con el color del turno (la "línea del día" de debajo se quitó el 11 oct 2026).
// =====================================================================================
const PALETA_TURNO = {
  // ESTILO LAVANDA (11 oct 2026, pedido por Fran): todas las casillas con el MISMO fondo lavanda y
  // texto morado oscuro; lo único que cambia de color según el turno es la barrita de la izquierda
  // (`acento`). Libre: fondo más claro y barrita fina negra.
  // 17 oct 2026: la casilla pasa a ser blanca con una pista abajo (ver CeldaTurno, "Línea del día");
  // de esta tabla se siguen usando `acento` (color de la barra), `nombre`, `corto` y, en Libre, `fondo`/`texto`.
  apertura:   { acento: '#F59E0B', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'sunrise', nombre: 'Apertura', corto: 'Apert.' },
  comida:     { acento: '#EC4899', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'sun', nombre: 'Almuerzo', corto: 'Alm.' },
  noche:      { acento: '#8B5CF6', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'moon', nombre: 'Cena', corto: 'Cena' },
  partido:    { acento: '#3B82F6', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'clock', nombre: 'Partido', corto: 'Part.' },
  enlace:     { acento: '#22C55E', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'link', nombre: 'Enlace', corto: 'Enl.' },
  vacaciones: { acento: '#06B6D4', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'sun', nombre: 'Vacaciones', corto: 'Vac.' },
  baja:       { acento: '#F97316', texto: '#2E1A6B', fondo: '#E9E3FF', borde: '#E9E3FF', icono: 'baja', nombre: 'De baja', corto: 'Baja' },
  libre:      { acento: '#111111', texto: '#7C73A6', fondo: '#F4F1FF', borde: '#F4F1FF', icono: 'bed', nombre: 'Libre', corto: 'Libre' },
};
const paletaDe = (tipo) => PALETA_TURNO[tipo] || PALETA_TURNO.libre;

// Tramos horarios [inicio, fin] de una casilla, en minutos desde las 00:00 de ese día (un fin
// pasada la medianoche cuenta como 24:xx). Partido devuelve sus dos tramos.
const tramosDeTurno = (s, conf) => {
  if (!s || !['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(s.tipo)) return [];
  const tramo = (ini, fin) => {
    const a = aMinutos(ini), b = aMinutos(fin);
    if (a == null || b == null) return null;
    return [a, b <= a ? b + 1440 : b];
  };
  if (s.tipo === 'partido') {
    const H = horariosGenerales(conf || {});
    return [tramo(s.horaEntrada, s.horaSalida || H.comidaFin), tramo(s.horaEntradaNoche || H.nocheIni, s.horaSalidaNoche || H.nocheFin)].filter(Boolean);
  }
  const t = tramo(s.horaEntrada, s.horaSalida);
  return t ? [t] : [];
};

// DISEÑO "COLOR SUAVE + LÍNEA ENTERA" (19 oct 2026, elegido por Fran entre varias propuestas).
// Cada turno: fondo muy suave del color del turno, una línea ENTERA de ese color abajo, el nombre del
// turno en su color (más oscuro, para que se lea) y la hora en letra normal, nada de negritas gruesas.
// Vacaciones y De baja: casilla blanca con la línea rayada. Libre: casilla blanca con un punto verde y
// "Día libre", para que se vea de un vistazo qué días libra cada persona.
const TINTA_TURNO = {
  apertura: '#B45309', comida: '#BE185D', noche: '#6D28D9', partido: '#1D4ED8',
  enlace: '#15803D', vacaciones: '#0E7490', baja: '#C2410C',
};
// Mezcla un color con blanco (a = cuánto color queda: 0.09 = un 9 %). Se calcula aquí y no con
// color-mix() de CSS porque algunos Android antiguos todavía no lo entienden.
const tinteSuave = (hex, a) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  if (!Number.isFinite(n)) return '#FFFFFF';
  const m = (c) => Math.round(c * a + 255 * (1 - a));
  return `rgb(${m((n >> 16) & 255)}, ${m((n >> 8) & 255)}, ${m(n & 255)})`;
};
const VERDE_LIBRE = '#22C55E';

// tam: 'normal' (tabla del Encargado), 'compacta' (tablas de consulta) o 'mini' (pantalla
// completa en modo mini: todo cabe sin desplazar).
const CeldaTurno = ({ s, conf, tam = 'normal', onClick, retraso = null, titulo }) => {
  const tipo = s?.tipo || 'libre';
  const p = paletaDe(tipo);
  const trabaja = ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(tipo);
  const tramos = trabaja ? tramosDeTurno(s, conf) : [];
  const mini = tam === 'mini', compacta = tam === 'compacta';
  const hasB = s?.tareas?.includes('recargaBarra');
  const hasS = s?.tareas?.includes('recargaSalon');
  const clicable = typeof onClick === 'function';
  const libre = tipo === 'libre';
  const ausencia = tipo === 'vacaciones' || tipo === 'baja';
  const tinta = TINTA_TURNO[tipo] || '#2E2640';
  const altoLinea = mini ? 3 : compacta ? 4 : 5;
  const estilo = {
    position: 'relative',
    boxSizing: 'border-box', width: '100%', height: '100%', textAlign: 'left',
    display: 'flex', flexDirection: 'column',
    background: (libre || ausencia) ? '#FFFFFF' : tinteSuave(p.acento, 0.09),
    border: (libre || ausencia) ? '1px solid #E4DEEE' : '1px solid transparent',
    borderRadius: mini ? '7px' : compacta ? '10px' : '14px',
    padding: mini ? `3px 4px ${3 + altoLinea}px` : compacta ? `6px 8px ${6 + altoLinea}px` : `10px 11px ${10 + altoLinea}px`,
    minHeight: mini ? '34px' : compacta ? '50px' : '84px', overflow: 'hidden',
    cursor: clicable ? 'pointer' : 'default',
    ...(libre || mini ? { justifyContent: 'center', alignItems: 'center', textAlign: 'center' } : {}),
    ...(libre ? { paddingBottom: mini ? '3px' : compacta ? '6px' : '10px' } : {}),
    ...(retraso != null ? { animationDelay: `${retraso}ms` } : {}),
  };
  const fsEtiqueta = mini ? 'clamp(7px, 1.6vw, 13px)' : compacta ? '11px' : '12.5px';
  const fsHora = mini ? 'clamp(6.5px, 1.3vw, 11px)' : compacta ? '12px' : (tramos.length > 1 ? '13.5px' : '14.5px');
  const pastilla = (texto) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#4B4560', background: 'rgba(255,255,255,0.75)', borderRadius: '999px', padding: '1px 7px' }}>{texto}</span>
  );
  const punto = (tamPx) => (
    <span aria-hidden="true" style={{ width: `${tamPx}px`, height: `${tamPx}px`, flex: 'none', borderRadius: '999px', background: VERDE_LIBRE, boxShadow: `0 0 0 ${Math.max(2, Math.round(tamPx * 0.4))}px #DCFCE7` }} />
  );
  return (
    <div className={`hd-celda${retraso != null ? ' hd-celda-in' : ''}${clicable ? ' hd-celda-clic' : ''}${libre ? ' hd-celda-libre' : ''}`} style={estilo}
      onClick={onClick} title={titulo}
      {...(clicable ? { role: 'button', tabIndex: 0, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } } : {})}>
      {libre ? (
        <span className="hd-c-libre" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: mini ? '4px' : '7px', fontSize: fsEtiqueta, fontWeight: 600, color: '#241542', whiteSpace: 'nowrap' }}>
          {punto(mini ? 6 : compacta ? 8 : 9)}
          {mini ? <><span className="hd-mini-ancho">Libre</span></> : <><span className="hd-libre-largo">Día libre</span><span className="hd-libre-corto">Libre</span></>}
        </span>
      ) : (
        <>
          <span className="hd-c-etq" style={{ fontSize: fsEtiqueta, fontWeight: 600, color: tinta, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {mini ? <><span className="hd-mini-ancho">{p.nombre}</span><span className="hd-mini-estrecho">{p.corto}</span></> : p.nombre}
          </span>
          {tramos.length > 0 && (
            <div style={{ marginTop: mini ? '1px' : '2px' }}>
              {tramos.map(([a, b], i) => (
                <div key={i} className="hd-c-hora" style={{ fontSize: fsHora, fontWeight: 500, color: '#2E2640', lineHeight: 1.35, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {/* En "mini" solo cabe la hora de entrada de cada tramo. */}
                  {mini ? <><span className="hd-mini-ancho">{aHora(a)} – {aHora(b)}</span><span className="hd-mini-estrecho">{aHora(a)}</span></> : <>{aHora(a)} – {aHora(b)}</>}
                </div>
              ))}
            </div>
          )}
          {ausencia && !mini && (
            <div className="hd-c-hora" style={{ fontSize: compacta ? '11px' : '12.5px', fontWeight: 500, color: '#8A8399', marginTop: '2px', whiteSpace: 'nowrap' }}>Todo el día</div>
          )}
          {!mini && (hasB || hasS || s?.descanso) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', paddingTop: '6px' }}>
              {hasB && pastilla(<><Ico n="refresh" s="9px" /> Barra</>)}
              {hasS && pastilla(<><Ico n="refresh" s="9px" /> Salón</>)}
              {s?.descanso && pastilla(<><Ico n="coffee" s="9px" /> {s.descanso.hora} · {s.descanso.minutos} min</>)}
            </div>
          )}
          <span aria-hidden="true" style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height: `${altoLinea}px`,
            background: ausencia
              ? `repeating-linear-gradient(-45deg, ${p.acento} 0 6px, ${tinteSuave(p.acento, 0.35)} 6px 10px)`
              : p.acento,
          }} />
        </>
      )}
    </div>
  );
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const calcularDiasFindeLibresRecientes = (scheduleHistory, semanasAtras = 4) => {
  const conteo = {};
  if (!scheduleHistory || scheduleHistory.length === 0) return conteo;

  const recientes = [...scheduleHistory].slice(-semanasAtras);
  recientes.forEach(h => {
    (h.employeesSnapshot || []).forEach(emp => {
      const diasFindeLibres = WEEKEND.filter(day => {
        const s = h.schedule?.[emp.id]?.[day];
        return !s || s.tipo === 'libre';
      }).length;
      conteo[emp.name] = (conteo[emp.name] || 0) + diasFindeLibres;
    });
  });
  return conteo;
};

// FASE 3 — PULIDO DEL HORARIO (24 ago 2026). Primera pieza: reparto justo de turnos incómodos
// (noche y partido) mirando varias semanas hacia atrás, no solo la que se está generando ahora.
// El desempate que ya existía dentro del generador ("quien menos noches lleve ESTA semana") es
// ciego a lo que pasó la semana pasada — si alguien tuvo mala suerte y le tocaron todas las
// noches la semana anterior, esta semana vuelve a empezar de cero con las mismas posibilidades
// que cualquiera. Con esto, quien más turnos incómodos ha tenido recientemente pasa a tener
// preferencia por los turnos de comida, y viceversa. Mismo patrón de lectura del historial que
// ya usa `calcularDiasFindeLibresRecientes`, por nombre (no por id, para que sobreviva aunque el
// empleado se haya dado de baja y de alta de nuevo con distinto id).
const calcularTurnosIncomodosRecientes = (scheduleHistory, semanasAtras = 4) => {
  const conteo = {};
  if (!scheduleHistory || scheduleHistory.length === 0) return conteo;

  const recientes = [...scheduleHistory].slice(-semanasAtras);
  recientes.forEach(h => {
    (h.employeesSnapshot || []).forEach(emp => {
      const incomodos = DAYS.filter(day => {
        const tipo = h.schedule?.[emp.id]?.[day]?.tipo;
        return tipo === 'noche' || tipo === 'partido' || tipo === 'enlace';
      }).length;
      conteo[emp.name] = (conteo[emp.name] || 0) + incomodos;
    });
  });
  return conteo;
};

// GENERADOR CLÁSICO (el de siempre, hasta el 10 oct 2026). Ya NO es el que se usa al pulsar
// "Generar": queda solo como RESPALDO por si el generador nuevo (generarHorarioOptimo, más abajo)
// produjera algo que no pasa el validador de reglas duras. Ver generateSchedule.
const generateScheduleClasico = (employees, specialDays, approvedRequests, config, temporadaConfig, scheduleHistory, isCocina) => {
  if (employees.length === 0) return {};
  const weekDates = getWeekDates();
  const minComidaConfigurado = temporadaConfig.minComida ?? temporadaConfig.minPersonas ?? 4;
  const minNocheConfigurado = temporadaConfig.minNoche ?? temporadaConfig.minPersonas ?? 4;
  const numAperturaConfigurado = config.personasApertura || 2;

  // AUTO-AJUSTE A LA PLANTILLA REAL (6 sep 2026, pedido por Fran: "si tenemos 12 trabajadores no
  // pueden trabajar todos los días todas las personas"). Antes de generar nada, se calcula cuánta
  // gente puede razonablemente trabajar a la vez sin dejar a nadie sin sus días de descanso
  // obligatorios esa semana. Si la temporada activa (ej. "Alta") pide más gente de la que la
  // plantilla real puede dar respetando los descansos, se recorta SOLO para este horario — nunca
  // se toca lo que Fran configuró, así que en cuanto haya más plantilla o cambie de temporada
  // vuelve a generar con sus números de siempre.
  const deBajaOVacacionesTodaLaSemana = emp => DAYS.every(d => estaDeBajaEse(emp, weekDates[d]) || estaAusenteEse(emp, weekDates[d]));
  const empleadosDisponiblesSemana = employees.filter(e => !deBajaOVacacionesTodaLaSemana(e)).length;
  const diasAbiertosSemana = 7 - (config.diasCierreSemana || []).length;
  const diasDescansoObligatorio = Math.max(1, Math.min(6, config.diasDescansoSemana || 2));
  const techoSostenible = diasAbiertosSemana > diasDescansoObligatorio
    ? Math.max(1, Math.floor(empleadosDisponiblesSemana * (diasAbiertosSemana - diasDescansoObligatorio) / diasAbiertosSemana))
    : empleadosDisponiblesSemana;

  let minComida = minComidaConfigurado;
  let minNoche = minNocheConfigurado;
  if (minComida + minNoche > techoSostenible) {
    const ratio = techoSostenible / (minComida + minNoche);
    minComida = Math.max(1, Math.round(minComida * ratio));
    minNoche = Math.max(1, Math.round(minNoche * ratio));
  }
  const numApertura = Math.min(numAperturaConfigurado, Math.max(1, minComida));

  // DÍAS ESPECIALES CON PRIORIDAD REAL (10 oct 2026, pedido por Fran al marcar un martes como
  // especial y ver que salía con casi nadie: "se supone que Horidia tiene que poner ese día
  // especial como prioridad y poner más camareros, o que trabajen el día entero"). Hasta hoy la
  // estrella solo cambiaba el ORDEN de los turnos dentro del día; ni el reparto de descansos ni
  // el de turnos la miraban, así que un día especial podía salir con MENOS gente que uno normal.
  // Un día cuenta como especial si tiene la estrella o si tiene número propio en "Ajustar
  // personas por día". Abajo, ese día: (1) no se elige para descanso algorítmico, (2) se reparte
  // PRIMERO y con tope propio, (3) se le pide más gente que a un día normal.
  const diasCierreEspecial = config.diasCierreSemana || [];
  const tieneNumerosPropios = (d) => { const ov = config.personasPorDia?.[d]; return !!(ov && (ov.comida != null || ov.noche != null || ov.completo != null)); };
  const diasEspeciales = DAYS.filter(d => {
    if (diasCierreEspecial.includes(d)) return false;
    return !!specialDays?.[d] || tieneNumerosPropios(d);
  });
  const esDiaEspecial = d => diasEspeciales.includes(d);
  const esDiaProtegidoDescanso = d => DIAS_PROTEGIDOS_DESCANSO.includes(d) || esDiaEspecial(d);
  // Objetivo por turno de un día especial: su número propio si lo tiene; si no, el normal +50%
  // (mínimo +1). Nunca más que la plantilla real disponible esa semana.
  const objetivoTurnoEspecial = (d) => {
    const ov = config.personasPorDia?.[d];
    const tope = Math.max(1, empleadosDisponiblesSemana);
    const c = ov?.comida != null ? Math.round(ov.comida) : minComida + Math.max(1, Math.ceil(minComida * 0.5));
    const n = ov?.noche != null ? Math.round(ov.noche) : minNoche + Math.max(1, Math.ceil(minNoche * 0.5));
    // "Día completo" (10 oct 2026): cuántas personas hacen almuerzo Y cena ese día (turno
    // partido). Solo existe si el local permite partido; cuenta dentro de los totales de almuerzo
    // y de cena, así que ninguno de los dos puede ser menor que él.
    const completo = (config.permiteTurnoPartido !== false && ov?.completo != null)
      ? Math.max(0, Math.min(Math.round(ov.completo), tope)) : 0;
    return {
      comida: Math.max(1, completo, Math.min(c, tope)),
      noche: Math.max(1, completo, Math.min(n, tope)),
      completo,
    };
  };

  const schedule = {};
  employees.forEach(emp => {
    schedule[emp.id] = {};
    DAYS.forEach(day => {
      const baja = estaDeBajaEse(emp, weekDates[day]);
      const vacacion = !baja && estaAusenteEse(emp, weekDates[day]);
      schedule[emp.id][day] = baja
        ? { tipo: 'baja', tareas: [], horaEntrada: '', horaSalida: '' }
        : vacacion
        ? { tipo: 'vacaciones', tareas: [], horaEntrada: '', horaSalida: '' }
        : { tipo: 'libre', tareas: [], horaEntrada: '', horaSalida: '' };
    });
  });

  const diasLibres = {};
  employees.forEach(emp => {
    // Empareja por `senderId` (el id real del empleado) siempre que la solicitud lo tenga; solo
    // cae al nombre para solicitudes antiguas creadas antes de este arreglo, que no lo llevan.
    // Emparejar por nombre es lo que causaba que un empleado nuevo heredara en silencio los días
    // libres aprobados de alguien borrado con el mismo nombre.
    const diasSolicitados = (approvedRequests || [])
      .filter(r => r.status === 'approved' && (r.senderId ? r.senderId === emp.id : r.sender === emp.name))
      .map(r => { const d = new Date(r.date); return DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]; });
    const diasFijos = emp.diasFijosLibres || [];
    const diasVacaciones = DAYS.filter(day => estaAusenteEse(emp, weekDates[day]));
    const diasBaja = DAYS.filter(day => estaDeBajaEse(emp, weekDates[day]));
    diasLibres[emp.id] = [...new Set([...diasSolicitados, ...diasFijos, ...diasVacaciones, ...diasBaja])];
  });

  // NUEVO (6 sep 2026): diasLibres mezcla ausencias REALES (baja, vacaciones, solicitud
  // aprobada, día fijo) con los descansos que el propio algoritmo va a repartir un poco más abajo
  // solo para cumplir el número de días libres a la semana. Sin distinguirlos, si más adelante
  // hace falta gente para cubrir un mínimo legal (apertura, comida, noche) y nadie más está
  // disponible, el generador no podía tocar a NADIE libre ese día — ni siquiera a quien solo
  // estaba libre por sorteo del algoritmo. Aquí se guarda aparte, por empleado, qué días de
  // descanso son "inventados" por el algoritmo (los únicos que se podrán recuperar como último
  // recurso más abajo). Las ausencias reales de arriba JAMÁS se tocan, pase lo que pase.
  const diasDescansoAlgoritmico = {};
  employees.forEach(emp => { diasDescansoAlgoritmico[emp.id] = new Set(); });

  // Garantiza el número de días de descanso configurado para el restaurante
  // (aunque alguien tenga muchos turnos semanales, o ya tenga algún día libre fijo).
  // Por defecto, intenta darlos seguidos (ej: lunes y martes juntos, no sueltos).
  //
  // FALLO REAL ENCONTRADO (8 ago 2026): antes esto elegía los días de descanso completamente al
  // azar entre los 7 de la semana. Como el fin de semana (Viernes/Sábado/Domingo) nunca da turno
  // partido, un descanso ahí "cuesta" solo 1 turno, pero un descanso entre semana cuesta 2 (se
  // pierde la posibilidad de partido ese día). Cuando el azar colocaba los dos descansos entre
  // semana (le pasó a un empleado con 10 turnos contratados, que se quedó en 7), la persona
  // perdía hasta 4 turnos de golpe sin que nadie lo supiera. Ahora se colocan a propósito en los
  // días que menos cuesten, protegiendo SIEMPRE Viernes y Sábado (los días más importantes,
  // pedido explícito de Fran) — y para que no todo el mundo intente librar el mismo día barato
  // (eso dejaba el domingo sin nadie trabajando en las primeras pruebas), hay un tope de cuánta
  // gente puede librar el mismo día, y quien más turnos tenga contratados elige primero.
  const diasCierreParaDescanso = config.diasCierreSemana || [];
  const objetivoDescansos = Math.max(1, Math.min(6, config.diasDescansoSemana || 2));
  const descansosSeguidos = config.diasDescansoConsecutivos !== false;
  const permitePartidoCfg = config.permiteTurnoPartido !== false;
  const costeDiaDescanso = (d) => capacidadPorDiaTurno(d, permitePartidoCfg);
  const descansosPorDia = {};
  DAYS.forEach(d => { descansosPorDia[d] = 0; });
  // TOPE DE DESCANSOS POR DÍA (10 oct 2026, caso real de Fran: un martes con casi toda la
  // plantilla en "Libre"). El tope de antes era "plantilla menos el mínimo", o sea que con 12
  // personas permitía que 9 libraran el MISMO día — todos los descansos se amontonaban en los
  // días "baratos" y esos días se quedaban vacíos mientras otros iban sobrados. Ahora el tope es
  // el reparto justo (descansos totales entre días abiertos, redondeado hacia arriba). Probado con
  // 2.500 semanas simuladas con bajas, vacaciones, días fijos libres, solicitudes aprobadas,
  // cierres, Enlace y encargados: mismos turnos por persona, ninguna ausencia pisada, y un 10 %
  // menos de días con el mínimo sin cubrir (en plantillas medias, a la mitad).
  const topeDescansoPorDia = Math.max(1, Math.min(
    employees.length - Math.max(minComida, minNoche),
    Math.ceil(employees.length * objetivoDescansos / Math.max(1, 7 - diasCierreParaDescanso.length))
  ));
  // En un día especial el tope de descansos es más bajo: tienen que quedar libres para trabajar
  // al menos las personas distintas que pide ese día (almuerzo + cena menos los de día completo,
  // que cubren los dos).
  const topeDe = (d) => {
    if (!esDiaEspecial(d)) return topeDescansoPorDia;
    const o = objetivoTurnoEspecial(d);
    const necesarias = Math.min(employees.length, o.comida + o.noche - o.completo);
    return Math.max(0, Math.min(topeDescansoPorDia, employees.length - necesarias));
  };
  const empleadosPorPrioridadDescanso = shuffle(employees).sort((a, b) => (b.turnosSemanales || 5) - (a.turnosSemanales || 5));
  empleadosPorPrioridadDescanso.forEach(emp => {
    const diasYaLibres = new Set([...(diasLibres[emp.id] || []), ...diasCierreParaDescanso]);
    const faltan = objetivoDescansos - diasYaLibres.size;
    if (faltan <= 0) return;

    // Opción "barata": los días que menos turnos cuesten, evitando Viernes/Sábado y sin
    // amontonarse todos en el mismo día ya lleno (tope de arriba).
    // FALLO REAL ENCONTRADO (24 ago 2026, con las pruebas automáticas): el tope solo se usaba
    // para ORDENAR (los días llenos al final), no para EXCLUIRLOS — así que si no quedaban
    // suficientes días "libres de sobra", se seguía amontonando gente en un día ya lleno de
    // todas formas. En casos raros, esto podía dejar casi a toda la plantilla librando el mismo
    // día, sin que el resto del generador pudiera rescatarlo después (respeta los días libres ya
    // asignados). Ahora se EXCLUYEN de verdad los días llenos al elegir — y solo si ni así
    // quedan suficientes días para cubrir el objetivo de descansos, se cae al comportamiento de
    // antes (permitir sobrepasar el tope) como red de seguridad, igual que en el resto del
    // generador: garantizar siempre que sea posible, honesto si de verdad no lo es.
    const diasDisponibles = DAYS.filter(d => !diasYaLibres.has(d));
    const diasDisponiblesSinLlenos = diasDisponibles.filter(d => descansosPorDia[d] < topeDe(d));
    const hayBastantesSinLlenos = diasDisponiblesSinLlenos.length >= faltan;
    const poolBarata = hayBastantesSinLlenos ? diasDisponiblesSinLlenos : diasDisponibles;
    const sinProteger = shuffle(poolBarata.filter(d => !esDiaProtegidoDescanso(d)))
      .sort((a, b) => {
        const llenoA = descansosPorDia[a] >= topeDe(a) ? 1 : 0;
        const llenoB = descansosPorDia[b] >= topeDe(b) ? 1 : 0;
        if (llenoA !== llenoB) return llenoA - llenoB;
        return costeDiaDescanso(a) - costeDiaDescanso(b);
      });
    const protegidos = shuffle(poolBarata.filter(d => esDiaProtegidoDescanso(d)));
    const opcionBarata = [...sinProteger, ...protegidos].slice(0, faltan);
    const costeBarata = opcionBarata.reduce((s, d) => s + costeDiaDescanso(d), 0);

    // Opción "seguidos": un bloque consecutivo, para quien prefiera librar juntos — pero solo
    // gana si sale igual de barata o más que la de arriba; si ser consecutivos sale más caro en
    // turnos perdidos, gana la opción barata. MISMO ARREGLO que arriba: antes esta opción
    // ignoraba el tope por completo (elegía el bloque más barato en turnos perdidos, sin mirar
    // si esos días ya estaban llenos de gente librando) — ahora prefiere bloques SIN ningún día
    // ya lleno, y solo si no existe ninguno así, cae a la lista completa.
    let opcionSeguidos = null, costeSeguidos = Infinity;
    if (descansosSeguidos && diasYaLibres.size === 0 && faltan >= 2) {
      const posiblesInicios = [];
      for (let i = 0; i <= DAYS.length - faltan; i++) {
        const bloque = DAYS.slice(i, i + faltan);
        if (bloque.every(d => !diasCierreParaDescanso.includes(d))) posiblesInicios.push(i);
      }
      if (posiblesInicios.length > 0) {
        // Barajado ANTES de ordenar por coste: si varios bloques empatan en coste (ahora que
        // todos los días valen lo mismo con turno partido, es lo normal), sin este barajado
        // siempre ganaba el primero de la lista (Lunes-Martes) — y con muchos empleados a la
        // vez, eso dejaba esos días sin nadie trabajando y machacaba solo esos dos.
        const bloques = shuffle(posiblesInicios.map(i => DAYS.slice(i, i + faltan)));
        const bloquesSinLlenos = bloques.filter(b => b.every(d => descansosPorDia[d] < topeDe(d)));
        const poolBloques = bloquesSinLlenos.length > 0 ? bloquesSinLlenos : bloques;
        const conCoste = poolBloques.map(b => ({
          b, coste: b.reduce((s, d) => s + costeDiaDescanso(d) + (esDiaProtegidoDescanso(d) ? 100 : 0), 0),
        }));
        conCoste.sort((a, b) => a.coste - b.coste);
        opcionSeguidos = conCoste[0].b;
        costeSeguidos = opcionSeguidos.reduce((s, d) => s + costeDiaDescanso(d), 0);
      }
    }

    const elegido = (opcionSeguidos && costeSeguidos <= costeBarata) ? opcionSeguidos : opcionBarata;
    elegido.forEach(d => {
      descansosPorDia[d] = (descansosPorDia[d] || 0) + 1;
      diasDescansoAlgoritmico[emp.id].add(d);
    });
    diasLibres[emp.id] = [...new Set([...(diasLibres[emp.id] || []), ...elegido])];
  });

  // NUEVO (6 sep 2026): antes de rescatar a alguien de un descanso puesto por el algoritmo (los
  // tres puntos de rescate de más abajo: mínimo diario, apertura, y comida/noche), se prefiere
  // SIEMPRE a quien tenga más de un descanso algorítmico esa semana — así se le quita uno pero le
  // queda al menos otro, en vez de dejarlo la semana entera sin ningún día libre (esto fue justo
  // lo que pasó al principio: exigir un mínimo muy alto vació el descanso de casi todo el mundo).
  // Solo si NADIE cumple esto se cae a cualquiera con descanso algorítmico ese día, como último
  // recurso honesto — mismo criterio que el resto del generador: garantizar cobertura siempre que
  // sea posible, sin fingir que sale gratis cuando de verdad no lo es.
  const tieneDescansoDeSobra = (empId) => (diasDescansoAlgoritmico[empId]?.size || 0) > 1;

  const aperturasHechas = {};
  const turnosUsados = {};
  employees.forEach(e => { aperturasHechas[e.id] = 0; turnosUsados[e.id] = 0; });

  const maxTurnosPorDia = config.permiteTurnoPartido === false ? 1 : 2;
  const diasTrabajoAsignados = {};
  const turnosRestantesPorEmp = {};
  const techoCapacidad = capacidadMaximaTurnos(config);
  employees.forEach(emp => {
    diasTrabajoAsignados[emp.id] = {};
    const raw = emp.turnosSemanales || 5;
    const base = (config.permiteTurnoPartido === false && raw < 7) ? Math.min(raw, 6) : raw;
    // Nunca se persigue un objetivo por encima del techo real (ver capacidadMaximaTurnos):
    // intentarlo solo hacía que el reparto se enredara sin conseguir nada más.
    turnosRestantesPorEmp[emp.id] = Math.min(base, techoCapacidad);
  });

  const diasFindeLibresRecientes = calcularDiasFindeLibresRecientes(scheduleHistory, 4);
  const diasFindeLibresDe = (emp) => diasFindeLibresRecientes[emp.name] || 0;

  // FASE 3: turnos incómodos (noche+partido) de las últimas 4 semanas, por nombre — se usa más
  // abajo para desempatar quién hace comida y quién hace noche cuando la propia semana en curso
  // no basta para decidir (ver los dos bloques de desempate).
  const incomodosRecientes = calcularTurnosIncomodosRecientes(scheduleHistory, 4);
  const incomodosDe = (emp) => incomodosRecientes[emp.name] || 0;

  const diasCierre = config.diasCierreSemana || [];
  // FALLO REAL ENCONTRADO (8 ago 2026): hasta ahora el reparto iba en dos fases separadas —
  // fin de semana primero, con como mucho 1 turno por persona y día (nunca turno partido), y
  // entre semana después, con hasta 2 (partido). Fran confirmó que NO hay ninguna razón de
  // negocio para esa diferencia: el turno partido (dos turnos de 4h, comida+noche) funciona
  // igual cualquier día de la semana, incluido Sábado y Domingo. Ahora es una sola fase que
  // trata todos los días abiertos por igual, con el mismo tope de `maxTurnosPorDia` en todos.
  const DIAS_TRABAJABLES = DAYS.filter(d => !diasCierre.includes(d));
  // Este otro lo sigue necesitando SOLO el sorteo de "descanso de finde por rotación" de abajo
  // (para que quien tenga margen de sobra pueda librar algún fin de semana de vez en cuando) —
  // no tiene relación con cuántos turnos admite cada día.
  const DIAS_FINDE = DAYS.filter(d => WEEKEND.includes(d) && !diasCierre.includes(d));

  const empleadosConDescansoFindeEstaSemana = new Set();
  employees.forEach(emp => {
    if (emp.esEncargado) return;
    // Si esta persona ya necesita CADA día disponible para llegar a sus turnos contratados (su
    // objetivo, ya recortado al techo real, iguala el techo), no se le puede quitar encima un
    // día de finde al azar — se quedaría corta sin motivo. Este sorteo es para quien tiene
    // margen de sobra, no para quien va al límite (fallo real encontrado el 8 ago 2026: le
    // pasaba justo a los empleados con más turnos contratados).
    if (turnosRestantesPorEmp[emp.id] >= techoCapacidad) return;
    const diasLibresUltimoMes = diasFindeLibresDe(emp);
    if (diasLibresUltimoMes >= 2) return;
    const probabilidad = diasLibresUltimoMes === 0 ? 0.35 : 0.18;
    if (Math.random() < probabilidad) {
      empleadosConDescansoFindeEstaSemana.add(emp.id);
    }
  });

  const diaDescansoElegido = {};
  empleadosConDescansoFindeEstaSemana.forEach(empId => {
    // Un día especial nunca se elige como descanso de finde por rotación (si hay otro finde abierto).
    const findeSinEspecial = DIAS_FINDE.filter(d => !esDiaEspecial(d));
    diaDescansoElegido[empId] = shuffle(findeSinEspecial.length > 0 ? findeSinEspecial : DIAS_FINDE)[0];
  });

  // CUPO DE LOS DÍAS ESPECIALES (10 oct 2026): cuántos "huecos de turno" (partido = 2) se le dan a
  // cada día especial en el reparto de abajo. Parte del mínimo normal del día (siempre
  // garantizado) y le suma lo extra que pide el día especial, PERO solo con los turnos que sobran
  // tras reservar el mínimo normal de todos los demás días — así un día especial nunca deja sin
  // cobertura a los demás. Si sobra poco, el día especial recibe menos extra; nunca menos que
  // un día normal.
  const cupoEspecial = {};
  const baseDia = minComida + minNoche;
  // Holgura real de turnos esa semana (turnos contratados que sobran tras reservar el mínimo
  // normal de TODOS los días). Un día con número propio en "Ajustar personas por día" (pedido
  // explícito del Encargado) puede usar toda la holgura; un día solo con estrella usa como mucho
  // la MITAD de lo que quede (la otra mitad es colchón para que los días normales no se queden
  // justos por el reparto del azar).
  let extraDisponible = Math.max(0, Object.values(turnosRestantesPorEmp).reduce((a, b) => a + b, 0) - baseDia * DIAS_TRABAJABLES.length);
  const conNumeroPropio = tieneNumerosPropios;
  [...DIAS_TRABAJABLES.filter(esDiaEspecial)].sort((a, b) => (conNumeroPropio(b) ? 1 : 0) - (conNumeroPropio(a) ? 1 : 0)).forEach(d => {
    const obj = objetivoTurnoEspecial(d);
    const extraPedido = Math.max(0, obj.comida + obj.noche - baseDia);
    const tope = conNumeroPropio(d) ? extraDisponible : Math.floor(extraDisponible * 0.5);
    const extra = Math.max(0, Math.min(extraPedido, tope));
    extraDisponible -= extra;
    // Con "día completo" el cupo nunca baja de lo que ocupan esos partidos más las personas de
    // apertura (que no pueden ser las mismas que hacen partido), si no la apertura se quedaría
    // quitándoles la cena a los del día completo.
    const minimoConCompleto = obj.completo > 0 ? 2 * obj.completo + numApertura : 0;
    cupoEspecial[d] = Math.min(Math.max(baseDia + extra, minimoConCompleto), employees.length * maxTurnosPorDia);
  });
  let usarCupoEspecial = true;

  // DÍA COMPLETO (10 oct 2026): si el Encargado pide N personas "de día completo" en un día
  // especial, se eligen ya N personas para hacer partido ese día (dos turnos de su cupo), antes
  // del reparto normal. Se escogen entre quien puede (no libra ese día, no es Enlace, le quedan
  // al menos 2 turnos contratados), dando preferencia a quien menos turnos incómodos (noche/
  // partido) lleva las últimas semanas, y después a quien más turnos tiene por gastar. Esos
  // turnos salen del cupo contratado de cada persona, así que NUNCA se pasa de lo contratado.
  // En ese día el resto de la plantilla queda con un máximo de 1 turno (solo los elegidos hacen
  // partido) para que el número pedido sea el que sale. Si no hay tantas personas posibles, salen
  // las que haya — la burbuja de la app lo avisa antes de guardar.
  const diasConCompletoFijado = new Set();
  if (config.permiteTurnoPartido !== false) {
    DIAS_TRABAJABLES.filter(esDiaEspecial).forEach(d => {
      const objC = objetivoTurnoEspecial(d).completo;
      if (objC <= 0) return;
      diasConCompletoFijado.add(d);
      const elegidos = shuffle(employees.filter(emp => {
        if (emp.hacesEnlace) return false;
        if (diasLibres[emp.id]?.includes(d)) return false;
        if (diaDescansoElegido[emp.id] === d) return false;
        if ((diasTrabajoAsignados[emp.id][d] || 0) > 0) return false;
        return (turnosRestantesPorEmp[emp.id] || 0) >= 2;
      })).sort((a, b) => (incomodosDe(a) - incomodosDe(b)) || ((turnosRestantesPorEmp[b.id] || 0) - (turnosRestantesPorEmp[a.id] || 0)))
        .slice(0, objC);
      elegidos.forEach(emp => {
        diasTrabajoAsignados[emp.id][d] = 2;
        turnosRestantesPorEmp[emp.id] -= 2;
      });
    });
  }

  let rondas = 0;
  let quedaTrabajoPorRepartir = true;

  while (quedaTrabajoPorRepartir && rondas < 20) {
    rondas++;
    // Los días especiales se reparten PRIMERO cada ronda (los demás, en orden aleatorio como antes).
    const diasOrdenAleatorio = [
      ...shuffle(DIAS_TRABAJABLES.filter(esDiaEspecial)),
      ...shuffle(DIAS_TRABAJABLES.filter(d => !esDiaEspecial(d))),
    ];
    let asignadosEnRonda = 0;

    diasOrdenAleatorio.forEach(day => {
      const candidatosBase = employees.filter(emp => {
        if (diasLibres[emp.id]?.includes(day)) return false;
        if (turnosRestantesPorEmp[emp.id] <= 0) return false;
        if (diaDescansoElegido[emp.id] === day) return false;
        const actual = diasTrabajoAsignados[emp.id][day] || 0;
        // ENLACE (2 oct 2026): tope de 1 turno al día SIEMPRE, nunca 2 — un Enlace es un turno
        // único de corrido hasta el cierre, jamás un partido. El resto de la plantilla sigue
        // usando el tope normal (maxTurnosPorDia, 1 ó 2 según si el partido está activado).
        const topeHoy = (emp.hacesEnlace || diasConCompletoFijado.has(day)) ? 1 : maxTurnosPorDia;
        return actual < topeHoy;
      });
      if (candidatosBase.length === 0) return;

      // FALLO REAL ENCONTRADO (8 ago 2026): antes solo se apuntaba a UNA persona por día en
      // cada ronda ("quien menos lleve"). Con objetivos altos y varias personas compitiendo por
      // los mismos días, hacían falta muchísimas rondas para que todos llegaran a su objetivo, y
      // con el límite de 20 rondas a veces se quedaba corto sin motivo real (alguien con turnos
      // de sobra sin usar y hueco real ese día). Ahora se apunta a TODAS las personas elegibles
      // ese día en la misma pasada — sigue siendo "quien menos lleve, primero" para no perder la
      // parte justa del reparto, pero converge mucho más rápido y de forma fiable.
      const ordenado = shuffle(candidatosBase).sort((a, b) => {
        const turnosA = Object.values(diasTrabajoAsignados[a.id]).reduce((s, n) => s + n, 0);
        const turnosB = Object.values(diasTrabajoAsignados[b.id]).reduce((s, n) => s + n, 0);
        return turnosA - turnosB;
      });

      // FASE 3 — SOBRESTAFFING: intentado el 24 ago 2026, DESHECHO el mismo día tras probarlo con
      // cientos de escenarios simulados automáticamente. Capar el total de gente al máximo de
      // minComida/minNoche parecía buena idea, pero cuando esos dos números son distintos (el
      // caso normal), dejaba corto al que pide más — empeoraba los fallos de mínimos un 13,5% en
      // la comparación con los mismos escenarios exactos. Se prioriza fiabilidad sobre
      // eficiencia: se retoma en el futuro con un diseño mejor, validado con el mismo script de
      // pruebas automáticas antes de volver a intentarlo.
      let aAsignar = ordenado;
      if (usarCupoEspecial && cupoEspecial[day] != null) {
        const yaHoy = employees.reduce((sum, e) => sum + (diasTrabajoAsignados[e.id][day] || 0), 0);
        const libre = Math.max(0, cupoEspecial[day] - yaHoy);
        // El cupo NUNCA deja a nadie sin colocar un turno contratado: quien ya no tiene ningún
        // otro día con hueco entra igualmente en el día especial aunque el cupo esté lleno.
        const sinOtroHueco = (emp) => !DIAS_TRABAJABLES.some(d2 => d2 !== day
          && !diasLibres[emp.id]?.includes(d2) && diaDescansoElegido[emp.id] !== d2
          && (diasTrabajoAsignados[emp.id][d2] || 0) < ((emp.hacesEnlace || diasConCompletoFijado.has(d2)) ? 1 : maxTurnosPorDia));
        aAsignar = [...ordenado.slice(0, libre), ...ordenado.slice(libre).filter(sinOtroHueco)];
        if (aAsignar.length === 0) return;
      }
      aAsignar.forEach(emp => {
        const actual = diasTrabajoAsignados[emp.id][day] || 0;
        diasTrabajoAsignados[emp.id][day] = actual + 1;
        turnosRestantesPorEmp[emp.id]--;
        asignadosEnRonda++;
      });
    });
    // Si el cupo de los días especiales dejó turnos contratados sin colocar en ningún sitio, se
    // levanta el cupo: repartir TODOS los turnos contratados manda sobre el extra del día especial.
    if (asignadosEnRonda === 0 && usarCupoEspecial) { usarCupoEspecial = false; quedaTrabajoPorRepartir = true; continue; }

    quedaTrabajoPorRepartir = employees.some(e => {
      const diasDisp = DIAS_TRABAJABLES.filter(d => !diasLibres[e.id]?.includes(d) && diaDescansoElegido[e.id] !== d);
      const tieneHueco = diasDisp.some(d => (diasTrabajoAsignados[e.id][d] || 0) < maxTurnosPorDia);
      return tieneHueco && turnosRestantesPorEmp[e.id] > 0;
    });
  }

  DAYS.forEach(day => {
    if (diasCierre.includes(day)) return;
    // NOTA (6 sep 2026): se probó exigir aquí la SUMA de comida+noche para garantizar el mínimo
    // de un tirón, pero con equipos pequeños (ej. 11 disponibles necesitando 4+7=11) eso obliga a
    // que trabajen TODOS los días, dejando a la plantilla sin ningún descanso semanal — peor que
    // el fallo que arreglaba. Revertido. El mínimo de verdad (comida y noche por separado) ahora
    // se refuerza DESPUÉS de repartir tipos, con cupo limitado, igual que ya se hace con la
    // apertura más abajo — ver el bloque "REFUERZO DE COMIDA/NOCHE" tras el reparto de tipos.
    const objEsp = esDiaEspecial(day) ? objetivoTurnoEspecial(day) : null;
    const minDia = objEsp ? Math.max(minComida, minNoche, objEsp.comida, objEsp.noche) : Math.max(minComida, minNoche);
    let trabajandoHoy = employees.filter(e => (diasTrabajoAsignados[e.id][day] || 0) > 0).length;

    while (trabajandoHoy < minDia) {
      // LÍMITES DUROS (6 sep 2026, tras el caso real de Fran: gente saliendo con más turnos de
      // los contratados y sin ningún descanso semanal). A partir de aquí, NINGÚN refuerzo de este
      // generador puede pasarse de los turnos contratados de una persona ni dejarla sin su último
      // descanso semanal — ni siquiera como último recurso. Si de verdad no queda nadie que cumpla
      // esto, el mínimo se queda corto (se verá en el aviso de antes de publicar) en vez de
      // arreglarlo rompiendo a alguien. Mismo criterio en los otros dos puntos de refuerzo
      // (apertura y comida/noche, más abajo).
      const candidatos = shuffle(employees.filter(emp => {
        if ((diasTrabajoAsignados[emp.id][day] || 0) > 0) return false; // ya trabaja hoy
        if ((turnosRestantesPorEmp[emp.id] || 0) <= 0) return false; // ya cubrió sus turnos contratados
        const enDiasLibres = diasLibres[emp.id]?.includes(day);
        if (!enDiasLibres) return true; // no descansa hoy, candidato normal
        // Si descansa hoy, solo es candidato si es un descanso puesto por el ALGORITMO (nunca
        // baja/vacaciones/solicitud aprobada) y le queda descanso de sobra esa semana.
        return diasDescansoAlgoritmico[emp.id]?.has(day) && tieneDescansoDeSobra(emp.id);
      }));
      if (candidatos.length === 0) break;
      const elegido = candidatos[0];
      diasDescansoAlgoritmico[elegido.id]?.delete(day);
      diasTrabajoAsignados[elegido.id][day] = 1;
      turnosRestantesPorEmp[elegido.id] = Math.max(0, turnosRestantesPorEmp[elegido.id] - 1);
      trabajandoHoy++;
    }
  });

  // DESCANSO MÍNIMO DE 12 HORAS ENTRE TURNOS (24 ago 2026, pedido por Fran: quiere que el
  // GENERADOR nunca cree el problema, no un aviso al publicar). Por ley (art. 34.3 del Estatuto
  // de los Trabajadores) tienen que pasar al menos 12h entre el fin de un turno y el principio
  // del siguiente. A propósito NO aplica al turno partido (mañana y noche el MISMO día): eso es
  // un patrón de turno válido con su propio hueco de descanso en medio, no el caso que preocupa.
  // Solo hay riesgo real cuando el turno de AYER terminó tarde (noche o partido, sobre las
  // 00:xx-05:xx) y el de HOY empieza pronto (apertura o comida) — cualquier otra combinación
  // deja de sobra más de 12h, así que no hace falta comprobar nada en esos casos.
  const finDeAyerSiTerminoTarde = (empId, dayIdx) => {
    if (dayIdx === 0) return null; // Lunes no tiene "ayer" dentro de esta semana
    const sAyer = schedule[empId]?.[DAYS[dayIdx - 1]];
    // 'enlace' se añade aquí por seguridad (2 oct 2026): aunque hoy Fran confirma que quien hace
    // Enlace SOLO hace Enlace (nunca choca con esto en la práctica), si algún día alguien deja de
    // estar marcado como Enlace a media semana, este cálculo tiene que seguir sabiendo que AYER
    // acabó tarde — si no, podría colarle una apertura hoy sin las 12h de descanso.
    if (!sAyer || !['noche', 'partido', 'enlace'].includes(sAyer.tipo)) return null;
    // 'partido' ignora su propio horaSalida a propósito (ahí guarda el fin del TRAMO de comida,
    // no el del día) y usa la hora de cierre del negocio en su lugar. 'enlace', en cambio, SÍ
    // guarda ya la hora de cierre real en su propio horaSalida al crearse — se trata como 'noche'.
    const horaFin = sAyer.tipo === 'partido' ? (config.nocheEnd || '00:30') : (sAyer.horaSalida || '00:30');
    const [h, m] = horaFin.split(':').map(Number);
    if (h >= 6) return null; // no debería pasar, pero por seguridad no se considera "tarde"
    return h * 60 + (m || 0); // minutos desde medianoche de HOY
  };
  const respetaDescanso12h = (empId, dayIdx, horaInicioHoyStr) => {
    const finAyerMin = finDeAyerSiTerminoTarde(empId, dayIdx);
    if (finAyerMin === null) return true; // sin riesgo real, no hace falta comprobar horas
    const [h, m] = (horaInicioHoyStr || '00:00').split(':').map(Number);
    const inicioHoyMin = h * 60 + (m || 0);
    return (inicioHoyMin - finAyerMin) >= 12 * 60;
  };

  DAYS.forEach((day, dayIdx) => {
    const isWeekend = WEEKEND.includes(day);
    // PERSONAS POR DÍA Y TURNO (29 sep 2026, pedido por Fran): el Encargado puede fijar, día a
    // día, cuánta gente hace falta en almuerzo y en cena — p. ej. "viernes 5 en almuerzo, 7 en
    // cena" para un concierto cerca del bar. Vive en config.personasPorDia[día] = {comida, noche},
    // ambos opcionales; si un día no tiene número propio, sigue usando el de la temporada de
    // siempre (minComida/minNoche, ya calculados arriba con el techo sostenible de la semana —
    // ESO no se toca, es un ajuste semanal, no de un día suelto).
    // Poner un número propio para un día lo marca como especial automáticamente (isSpecial más
    // abajo lo recoge solo): un día que pide más gente de lo normal ya es, por definición, un
    // día distinto — no hace falta marcar las dos cosas por separado.
    const overrideDia = config.personasPorDia?.[day];
    const tieneOverride = tieneNumerosPropios(day);
    const objEspDia = esDiaEspecial(day) ? objetivoTurnoEspecial(day) : null;
    const completoDia = objEspDia ? objEspDia.completo : 0;
    const minComidaDia = Math.max(completoDia, overrideDia?.comida != null ? Math.max(1, Math.min(Math.round(overrideDia.comida), employees.length)) : (objEspDia ? objEspDia.comida : minComida));
    const minNocheDia = Math.max(completoDia, overrideDia?.noche != null ? Math.max(1, Math.min(Math.round(overrideDia.noche), employees.length)) : (objEspDia ? objEspDia.noche : minNoche));
    const isSpecial = !!specialDays[day] || tieneOverride;
    const isImportant = isWeekend || isSpecial;

    const trabajanHoy = employees.filter(e => (diasTrabajoAsignados[e.id][day] || 0) > 0);
    if (trabajanHoy.length === 0) return;

    const encargadosHoy = trabajanHoy.filter(e => e.esEncargado);
    // ENLACE (2 oct 2026): se separan de "noEncargadosHoy" AQUÍ, en el origen — así ni entran en
    // la bolsa de candidatos a apertura, ni en el reparto comida/noche/partido, ni en ningún
    // refuerzo posterior. No hace falta excluirlos uno a uno en cada bloque de más abajo: como
    // nunca aparecen en "noEncargadosHoy", todo lo que parte de esa lista los deja fuera solo.
    // Si alguien tiene esEncargado Y hacesEnlace a la vez (caso raro), manda esEncargado — sigue
    // el camino normal de gestor, no el de Enlace.
    const enlaceHoy = trabajanHoy.filter(e => !e.esEncargado && e.hacesEnlace);
    const noEncargadosHoy = trabajanHoy.filter(e => !e.esEncargado && !e.hacesEnlace);

    // Turno de Enlace: SIEMPRE el mismo, sin pasar por ningún reparto ni cálculo de descanso de
    // 12h (Fran lo confirmó: como esta persona SOLO hace turnos de Enlace, nunca hay un cambio de
    // tipo de un día para otro que pueda chocar con las 12h — siempre entra a la misma hora).
    enlaceHoy.forEach(emp => {
      schedule[emp.id][day] = {
        tipo: 'enlace', tareas: [],
        horaEntrada: emp.horaEntradaEnlace || config.enlaceHoraInicio || '16:00',
        horaSalida: emp.horaSalidaEnlace || emp.horaSalidaNoche || config.nocheEnd || '00:30',
      };
      turnosUsados[emp.id] += diasTrabajoAsignados[emp.id][day] || 1;
    });

    const aptosApertura = shuffle(noEncargadosHoy.filter(e =>
      diasTrabajoAsignados[e.id][day] === 1 && respetaDescanso12h(e.id, dayIdx, config.aperturaHora || '12:30')
    ));
    // FALLO REAL ARREGLADO (24 ago 2026, encontrado por Fran probando la app antes de
    // enseñársela a su jefe): antes, si ese día no había suficiente gente con UN SOLO turno
    // (por ejemplo, porque varios tenían turno partido), la apertura se quedaba con menos
    // personas de las configuradas en "Personas apertura" — a veces con nadie. `numApertura`
    // era, en la práctica, un techo sobre un grupo que podía ser demasiado pequeño, no un
    // mínimo garantizado. Ahora, si faltan candidatos, se añade gente extra que ese día NO
    // estaba trabajando todavía — mismo patrón que el bucle de arriba que ya garantiza el
    // mínimo de comida/noche del día.
    // RONDA DE ARREGLO (6 sep 2026, caso real de Fran: "Martes 0 de 2 en apertura" con toda la
    // plantilla apta libre ese día). El refuerzo de arriba fallaba entero cuando TODA la gente sin
    // trabajo ese día estaba en diasLibres (aunque fuera solo descanso puesto por el algoritmo, no
    // una baja o vacación real). Ahora hay dos intentos antes de rendirse, en este orden:
    //  1) Convertir a alguien que HOY YA trabaja con turno partido a un turno único de apertura
    //     (pierde la mitad de noche ese día) — no hace falta tocar el día libre de nadie, mismo
    //     criterio de "cobertura real manda" que ya se usa con el descanso de 12h más abajo.
    //  2) Si eso no basta, recuperar a alguien de un descanso puesto por el ALGORITMO — nunca de
    //     baja, vacaciones o solicitud aprobada, eso no se toca jamás.
    if (aptosApertura.length < numApertura) {
      const faltanTrasConversion = numApertura - aptosApertura.length;
      const convertibles = shuffle(noEncargadosHoy.filter(e =>
        diasTrabajoAsignados[e.id][day] === 2 && respetaDescanso12h(e.id, dayIdx, config.aperturaHora || '12:30')
      ))
        .sort((a, b) => (aperturasHechas[a.id] || 0) - (aperturasHechas[b.id] || 0))
        .slice(0, faltanTrasConversion);
      convertibles.forEach(emp => {
        diasTrabajoAsignados[emp.id][day] = 1;
        turnosRestantesPorEmp[emp.id] = (turnosRestantesPorEmp[emp.id] || 0) + 1; // se le devuelve la mitad de noche que pierde
        aptosApertura.push(emp);
      });
    }
    if (aptosApertura.length < numApertura) {
      const faltan = numApertura - aptosApertura.length;
      // LÍMITES DUROS (6 sep 2026, tras el caso real de Fran: gente por encima de sus turnos
      // contratados y sin ningún descanso). Igual que en el mínimo diario: nunca se recluta a
      // alguien que ya cubrió sus turnos contratados, ni se le quita a nadie su último descanso de
      // la semana. Si no basta con quien cumple esto, se queda corto — se verá en el aviso.
      const elegibles = employees.filter(emp => {
        if (emp.esEncargado) return false;
        if (emp.hacesEnlace) return false; // ENLACE: nunca apertura, solo su turno de siempre.
        if ((diasTrabajoAsignados[emp.id][day] || 0) > 0) return false; // ya trabaja hoy
        if ((turnosRestantesPorEmp[emp.id] || 0) <= 0) return false; // ya cubrió sus turnos contratados
        const enDiasLibres = diasLibres[emp.id]?.includes(day);
        if (enDiasLibres) {
          // Solo candidato si es descanso puesto por el ALGORITMO (nunca baja/vacaciones/
          // solicitud) y le queda descanso de sobra esa semana.
          if (!diasDescansoAlgoritmico[emp.id]?.has(day) || !tieneDescansoDeSobra(emp.id)) return false;
        }
        if (!respetaDescanso12h(emp.id, dayIdx, config.aperturaHora || '12:30')) return false;
        return true;
      });
      const refuerzos = shuffle(elegibles)
        .sort((a, b) => (aperturasHechas[a.id] || 0) - (aperturasHechas[b.id] || 0))
        .slice(0, faltan);
      refuerzos.forEach(emp => {
        diasDescansoAlgoritmico[emp.id]?.delete(day);
        diasTrabajoAsignados[emp.id][day] = 1;
        turnosRestantesPorEmp[emp.id] = Math.max(0, (turnosRestantesPorEmp[emp.id] || 0) - 1);
        aptosApertura.push(emp);
      });
    }
    const porAperturas = [...aptosApertura].sort((a, b) => (aperturasHechas[a.id] || 0) - (aperturasHechas[b.id] || 0));
    const hacenApertura = porAperturas.slice(0, Math.min(numApertura, aptosApertura.length));
    const idsApertura = new Set(hacenApertura.map(e => e.id));

    hacenApertura.forEach(emp => {
      schedule[emp.id][day] = {
        tipo: 'apertura', tareas: [],
        horaEntrada: config.aperturaHora || '12:30',
        horaSalida: emp.horaSalidaComida || config.comidaEnd || '17:00'
      };
      turnosUsados[emp.id] += 1;
      aperturasHechas[emp.id] = (aperturasHechas[emp.id] || 0) + 1;
    });

    encargadosHoy.forEach(enc => {
      let numTurnos = diasTrabajoAsignados[enc.id][day];
      let tipo;
      // FALLO REAL ARREGLADO (24 ago 2026, encontrado en la auditoría): antes, con el turno
      // partido desactivado, se fijaba SIEMPRE 'comida' sin excepción — nunca se llegaba a
      // comprobar el mínimo de noche, así que el servicio de noche se quedaba sin nadie de
      // forma sistemática en cualquier restaurante con esa opción desactivada. Ahora, sin
      // partido disponible, se sigue decidiendo entre comida y noche según lo que falte cubrir
      // ese día — exactamente igual que cuando el partido SÍ está activado — simplemente sin la
      // posibilidad de partido como tercera opción.
      if (numTurnos === 2 && config.permiteTurnoPartido !== false) tipo = 'partido';
      else if (isImportant) {
        const comidas = Object.values(schedule[enc.id]).filter(s => ['comida','apertura'].includes(s.tipo)).length;
        const noches = Object.values(schedule[enc.id]).filter(s => s.tipo === 'noche').length;
        tipo = noches <= comidas ? 'noche' : 'comida';
      } else {
        const comidas = Object.values(schedule[enc.id]).filter(s => ['comida','apertura'].includes(s.tipo)).length;
        const noches = Object.values(schedule[enc.id]).filter(s => s.tipo === 'noche').length;
        tipo = comidas <= noches ? 'comida' : 'noche';
      }
      // DESCANSO MÍNIMO DE 12H: 'comida' y 'partido' empiezan pronto (~13:00) y son los únicos
      // tipos que pueden chocar con un turno de ayer que terminó tarde. 'noche' siempre empieza
      // sobre las 20:30, así que nunca tiene este problema — es la salida segura. Si era
      // 'partido', se prioriza el descanso legal sobre completar el segundo turno de ese día:
      // se queda solo con la noche, y numTurnos baja a 1 en consecuencia.
      const horaInicioComida = enc.horaEntradaComida || config.comidaStart || '13:00';
      if ((tipo === 'comida' || tipo === 'partido') && !respetaDescanso12h(enc.id, dayIdx, horaInicioComida)) {
        tipo = 'noche';
        numTurnos = 1;
      }
      const horaEntrada = tipo === 'noche'
        ? (enc.horaEntradaNoche || config.nocheStart || '20:30')
        : horaInicioComida;
      const horaSalida = tipo === 'noche'
        ? (enc.horaSalidaNoche || config.nocheEnd || '00:30')
        : (enc.horaSalidaComida || config.comidaEnd || '17:00');
      schedule[enc.id][day] = { tipo, tareas: [], horaEntrada, horaSalida };
      turnosUsados[enc.id] += numTurnos;
    });

    const restantes = noEncargadosHoy.filter(e => !idsApertura.has(e.id));
    // Sin valoraciones (22 oct 2026, pedido por Fran): ya no se prioriza a nadie por nota en los
    // días fuertes; el orden es aleatorio y el reparto justo lo deciden las fases de abajo.
    const ordenados = shuffle(restantes);

    // FASE 3: media de turnos incómodos recientes del equipo, para poder comparar a cada
    // persona contra "lo normal" en el desempate de abajo (no tiene sentido comparar a alguien
    // solo consigo mismo).
    const mediaIncomodosRecientes = noEncargadosHoy.length > 0
      ? noEncargadosHoy.reduce((s, e) => s + incomodosDe(e), 0) / noEncargadosHoy.length
      : 0;

    let countComida = hacenApertura.length + encargadosHoy.filter(e => schedule[e.id][day]?.tipo === 'comida').length;
    // ENLACE (2 oct 2026): quien hace Enlace trabaja de 16:00 al cierre, o sea que cubre TODA la
    // cena. Antes no contaba para el mínimo de cena, así que el generador mandaba a cenar a
    // más gente de la necesaria (y el panel lo pintaba en rojo aunque hubiera cobertura real).
    // Ahora cuenta como una persona de cena. Si prefieres lo contrario, basta con quitar el
    // "+ enlaceHoy.length" de esta línea y el "'enlace'" del contador de cena en el panel.
    let countNoche = encargadosHoy.filter(e => schedule[e.id][day]?.tipo === 'noche').length + enlaceHoy.length;

    ordenados.forEach((emp, idx) => {
      let numTurnos = diasTrabajoAsignados[emp.id][day];
      let tipo;

      // FALLO REAL ARREGLADO (24 ago 2026, encontrado en la auditoría): mismo fallo que en el
      // bloque de encargados de arriba — con el partido desactivado se fijaba siempre 'comida'
      // sin mirar el mínimo de noche. Ahora sigue decidiendo entre comida y noche igual que con
      // el partido activado, solo que sin esa tercera opción disponible.
      if (numTurnos === 2 && config.permiteTurnoPartido !== false) {
        tipo = 'partido';
        countComida++; countNoche++;
      } else {
        const faltaComida = countComida < minComidaDia;
        const faltaNoche = countNoche < minNocheDia;

        if (faltaComida && !faltaNoche) tipo = 'comida';
        else if (faltaNoche && !faltaComida) tipo = 'noche';
        else if (faltaComida && faltaNoche) {
          tipo = (minComidaDia - countComida) >= (minNocheDia - countNoche) ? 'comida' : 'noche';
        } else {
          const comidasHist = Object.values(schedule[emp.id]).filter(s => ['comida','apertura'].includes(s.tipo)).length;
          const nochesHist = Object.values(schedule[emp.id]).filter(s => s.tipo === 'noche').length;
          if (comidasHist !== nochesHist) {
            tipo = comidasHist <= nochesHist ? 'comida' : 'noche';
          } else {
            // FASE 3: la semana en curso está empatada para esta persona — se desempata con
            // quién ha tenido más turnos incómodos (noche+partido) en las últimas 4 semanas,
            // comparado contra la media del equipo. Quien ha tenido más de lo normal
            // últimamente, hoy tiene preferencia por la comida (el turno más cómodo).
            tipo = incomodosDe(emp) >= mediaIncomodosRecientes ? 'comida' : 'noche';
          }
        }

        if (tipo === 'comida') countComida++; else countNoche++;
      }

      // DESCANSO MÍNIMO DE 12H: mismo criterio que con los encargados — 'comida' y 'partido'
      // empiezan pronto y son los únicos que pueden chocar con un turno de ayer que terminó
      // tarde. Si toca, se cambia a 'noche' (siempre segura) y se corrigen los contadores de
      // cobertura del día para que el resto de la plantilla se siga repartiendo bien.
      const horaInicioComida = emp.horaEntradaComida || config.comidaStart || '13:30';
      if ((tipo === 'comida' || tipo === 'partido') && !respetaDescanso12h(emp.id, dayIdx, horaInicioComida)) {
        countComida--; // se deshace la mitad de "comida" de este empleado, sea del tipo que fuera
        if (tipo === 'comida') countNoche++; // 'partido' ya había sumado countNoche antes, no se vuelve a sumar
        tipo = 'noche';
        numTurnos = 1;
      }

      const horaEntrada = tipo === 'noche'
        ? (emp.horaEntradaNoche || config.nocheStart || '20:30')
        : horaInicioComida;
      const horaSalida = tipo === 'noche'
        ? (emp.horaSalidaNoche || config.nocheEnd || '00:30')
        : (emp.horaSalidaComida || config.comidaEnd || '17:00');

      schedule[emp.id][day] = { tipo, tareas: [], horaEntrada, horaSalida };
      turnosUsados[emp.id] += numTurnos;
    });

    // LÍMITES DUROS (6 sep 2026, tras el caso real de Fran: gente por encima de sus turnos
    // contratados y sin ningún descanso). Dos intentos antes de rendirse, ninguno de los dos
    // puede nunca pasar a alguien de sus turnos contratados ni dejarlo sin su último descanso:
    //  1) Convertir a alguien que hoy YA trabaja con un solo turno (comida o noche) a turno
    //     partido, añadiéndole la mitad que falta — solo si le queda margen de turnos esa semana.
    //  2) Si eso no basta, recuperar a alguien de un descanso puesto por el ALGORITMO, solo si le
    //     queda margen de turnos Y descanso de sobra. Si ni así se cubre, se queda corto — se verá
    //     en el aviso de antes de publicar.
    if (countComida < minComidaDia || countNoche < minNocheDia) {
      const horaInicioComidaDe = (e) => e.horaEntradaComida || config.comidaStart || '13:30';

      if (countNoche < minNocheDia) {
        const faltanNoche = minNocheDia - countNoche;
        const convertibles = shuffle(noEncargadosHoy.filter(e =>
          config.permiteTurnoPartido !== false && // con turno partido desactivado NUNCA se convierte a nadie en partido
          diasTrabajoAsignados[e.id][day] === 1 && schedule[e.id][day]?.tipo === 'comida' &&
          (turnosRestantesPorEmp[e.id] || 0) > 0
        )).slice(0, faltanNoche);
        convertibles.forEach(e => {
          schedule[e.id][day] = {
            tipo: 'partido', tareas: [],
            horaEntrada: horaInicioComidaDe(e),
            horaSalida: e.horaSalidaComida || config.comidaEnd || '17:00',
          };
          diasTrabajoAsignados[e.id][day] = 2;
          turnosUsados[e.id] += 1;
          turnosRestantesPorEmp[e.id] = Math.max(0, (turnosRestantesPorEmp[e.id] || 0) - 1);
          countNoche++;
        });
      }
      if (countComida < minComidaDia) {
        const faltanComida = minComidaDia - countComida;
        const convertibles = shuffle(noEncargadosHoy.filter(e =>
          config.permiteTurnoPartido !== false && // ídem: sin partido permitido, no se convierte a nadie
          diasTrabajoAsignados[e.id][day] === 1 && schedule[e.id][day]?.tipo === 'noche' &&
          (turnosRestantesPorEmp[e.id] || 0) > 0 &&
          respetaDescanso12h(e.id, dayIdx, horaInicioComidaDe(e))
        )).slice(0, faltanComida);
        convertibles.forEach(e => {
          schedule[e.id][day] = {
            tipo: 'partido', tareas: [],
            horaEntrada: horaInicioComidaDe(e),
            horaSalida: e.horaSalidaComida || config.comidaEnd || '17:00',
          };
          diasTrabajoAsignados[e.id][day] = 2;
          turnosUsados[e.id] += 1;
          turnosRestantesPorEmp[e.id] = Math.max(0, (turnosRestantesPorEmp[e.id] || 0) - 1);
          countComida++;
        });
      }

      // Paso 2: si convertir a gente que ya trabaja no ha bastado, se recupera a alguien nuevo,
      // asignado solo al tipo que falta (no partido, para no cargarle más de lo necesario).
      const reforzarTipo = (tipoFaltante, cuantosFaltan) => {
        if (cuantosFaltan <= 0) return 0;
        const elegibles = employees.filter(emp => {
          if (emp.esEncargado) return false;
          if (emp.hacesEnlace) return false; // ENLACE: nunca comida/noche de refuerzo, solo su turno.
          if ((diasTrabajoAsignados[emp.id][day] || 0) > 0) return false; // ya trabaja hoy
          if ((turnosRestantesPorEmp[emp.id] || 0) <= 0) return false; // ya cubrió sus turnos contratados
          const enDiasLibres = diasLibres[emp.id]?.includes(day);
          if (enDiasLibres) {
            if (!diasDescansoAlgoritmico[emp.id]?.has(day) || !tieneDescansoDeSobra(emp.id)) return false;
          }
          const horaEntradaNueva = tipoFaltante === 'noche'
            ? (emp.horaEntradaNoche || config.nocheStart || '20:30')
            : horaInicioComidaDe(emp);
          if (!respetaDescanso12h(emp.id, dayIdx, horaEntradaNueva)) return false;
          return true;
        });
        const elegidos = shuffle(elegibles).slice(0, cuantosFaltan);
        elegidos.forEach(emp => {
          diasDescansoAlgoritmico[emp.id]?.delete(day);
          diasTrabajoAsignados[emp.id][day] = 1;
          turnosRestantesPorEmp[emp.id] = Math.max(0, (turnosRestantesPorEmp[emp.id] || 0) - 1);
          const horaEntrada = tipoFaltante === 'noche'
            ? (emp.horaEntradaNoche || config.nocheStart || '20:30')
            : horaInicioComidaDe(emp);
          const horaSalida = tipoFaltante === 'noche'
            ? (emp.horaSalidaNoche || config.nocheEnd || '00:30')
            : (emp.horaSalidaComida || config.comidaEnd || '17:00');
          schedule[emp.id][day] = { tipo: tipoFaltante, tareas: [], horaEntrada, horaSalida };
          turnosUsados[emp.id] += 1;
        });
        return elegidos.length;
      };
      if (countNoche < minNocheDia) countNoche += reforzarTipo('noche', minNocheDia - countNoche);
      if (countComida < minComidaDia) countComida += reforzarTipo('comida', minComidaDia - countComida);
    }
  });

  if (!isCocina) {
    const NO_TRABAJA = ['libre', 'vacaciones', 'baja'];
    DAYS.forEach(day => {
      const trabajando = shuffle(employees.filter(e => !NO_TRABAJA.includes(schedule[e.id][day]?.tipo)));
      if (trabajando.length === 0) return;
      // Recargas repartidas al azar entre quien trabaja ese día (antes iban a la mejor nota).
      const quienBarra = trabajando[0];
      if (quienBarra) schedule[quienBarra.id][day].tareas.push('recargaBarra');
      const resto = shuffle(trabajando.filter(e => e.id !== quienBarra?.id));
      if (resto.length > 0) {
        const quienSalon = resto[0];
        if (quienSalon) schedule[quienSalon.id][day].tareas.push('recargaSalon');
      }
    });
  }

  return schedule;
};

// =====================================================================================
// MODELO DE HORAS (10 oct 2026, pedido por Fran) — UNA sola fuente de verdad para las horas.
// El Encargado decide en Configuración cuánto dura un turno (p. ej. 4 h) y, si quiere, la hora
// exacta de cada turno. Todo lo demás sale de aquí: lo que se pinta en cada casilla, cuánto
// "vale" un Enlace en turnos y la regla de las 12 h de descanso. Antes cada sitio leía las horas
// por su cuenta (y cada empleado guardaba una copia de las horas del día en que se creó), así que
// al cambiar la configuración unos empleados salían con unas horas y otros con otras.
// =====================================================================================
const aMinutos = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
};
const aHora = (min) => {
  const x = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};
// Minutos entre dos horas, cruzando medianoche si hace falta (20:30 → 00:30 = 240).
const duracionEntre = (ini, fin) => {
  const a = aMinutos(ini), b = aMinutos(fin);
  if (a == null || b == null) return 0;
  let d = b - a;
  if (d <= 0) d += 1440;
  return d;
};
// "4 h", "4 h 30", "8 h 30".
const formatoDuracion = (min) => {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60), r = m % 60;
  if (h === 0) return `${r} min`;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
};
// Todas las horas cada 15 minutos, para los selectores del modo "horas personalizadas".
const OPCIONES_HORA = Array.from({ length: 96 }, (_, i) => aHora(i * 15));

// TURNO SEGUIDO — NADIE TRABAJA MÁS DE 8 H (16 oct 2026, fallo real: un local nuevo con turno
// seguido abierto de 12:30 a 00:00 sacaba turnos de 12 h). En un local de turno seguido, el
// horario de apertura→cierre es el del LOCAL, no el de una persona. Cada turno dura como mucho
// 8 h: la mañana arranca a la apertura y la tarde termina al cierre (se solapan en el medio).
// El Encargado elige cuánto dura el turno (por defecto 8 h; puede ser 8 h 15, 8 h 30, 9 h…) y Horidia
// no se lo impide: cumplir o no la jornada es cosa suya. El tope de 10 h solo es un límite de
// cordura, y 8 h es la duración por defecto y a la que se reparan los turnos absurdos de 12 h.
const MAX_TURNO_SEGUIDO_MIN = 10 * 60;
const DURACION_TURNO_SEGUIDO_DEFECTO_MIN = 8 * 60;
const OPCIONES_DURACION_SEGUIDO = Array.from({ length: (MAX_TURNO_SEGUIDO_MIN - 6 * 60) / 15 + 1 }, (_, i) => 6 * 60 + i * 15);
// Horas de los turnos de un local de turno seguido a partir de su apertura y su cierre.
// Devuelve null si alguna de las dos horas no es válida.
const horarioTurnoSeguido = (apertura, cierre, durElegida = DURACION_TURNO_SEGUIDO_DEFECTO_MIN) => {
  const ini = aMinutos(apertura), fin = aMinutos(cierre);
  if (ini == null || fin == null) return null;
  const dur = Math.min(duracionEntre(apertura, cierre), Math.min(Math.max(durElegida, 120), MAX_TURNO_SEGUIDO_MIN));
  const h = { comidaStart: apertura, comidaEnd: aHora(ini + dur), nocheStart: aHora(fin - dur), nocheEnd: cierre, duracionTurnoMin: dur };
  // El Enlace sale al cierre y trabaja lo mismo que un turno (más 30 min de descanso si pasa de
  // 6 h): entra a la hora que lo hace cuadrar. Si esa hora no está entre las que se pueden
  // elegir, se deja la de siempre.
  const entradaEnlace = aHora(fin - (dur + (dur > 360 ? 30 : 0)));
  if (ENLACE_ENTRADAS.includes(entradaEnlace)) h.enlaceHoraInicio = entradaEnlace;
  return h;
};
// Arregla una configuración de turno seguido que tenga turnos absurdos (más de 10 h). Si la configuración
// está bien, devuelve EXACTAMENTE el mismo objeto (así no provoca guardados ni cambios).
//  · Sin duración guardada (locales creados con el fallo del asistente): la "comida" guardaba
//    todo el horario del local, así que su salida es el CIERRE de verdad.
//  · Con duración guardada de más de 10 h: se pone a 8 h y se recolocan las horas.
//    (Hasta 10 h se respeta: es lo que haya elegido el Encargado.)
// Con "horas personalizadas" no se toca nada: ahí las horas las ha puesto el Encargado a mano.
const repararHorarioSeguido = (cfg) => {
  if (!cfg || cfg.permiteTurnoPartido !== false || cfg.horariosPersonalizados) return cfg;
  const comidaIni = cfg.comidaStart || '13:30';
  const hayDuracion = cfg.duracionTurnoMin > 0;
  const durGuardada = hayDuracion ? cfg.duracionTurnoMin : (duracionEntre(comidaIni, cfg.comidaEnd || '17:00') || 240);
  if (durGuardada <= MAX_TURNO_SEGUIDO_MIN) return cfg;
  if (hayDuracion) {
    const ini = aMinutos(comidaIni), fin = aMinutos(cfg.nocheEnd || '00:30');
    if (ini == null || fin == null) return cfg;
    return { ...cfg, duracionTurnoMin: DURACION_TURNO_SEGUIDO_DEFECTO_MIN, comidaEnd: aHora(ini + DURACION_TURNO_SEGUIDO_DEFECTO_MIN), nocheStart: aHora(fin - DURACION_TURNO_SEGUIDO_DEFECTO_MIN) };
  }
  const h = horarioTurnoSeguido(comidaIni, cfg.comidaEnd);
  if (!h) return cfg;
  const { enlaceHoraInicio, ...horas } = h;
  return { ...cfg, ...horas, ...(!cfg.enlaceHoraInicio && enlaceHoraInicio ? { enlaceHoraInicio } : {}) };
};

// Horario GENERAL del restaurante, ya resuelto. Duración del turno: la que eligió el Encargado
// (config.duracionTurnoMin); en configuraciones antiguas que aún no la tienen, la que se deduce
// del almuerzo (entrada → salida), que es lo que el Encargado puso en su día.
const horariosGenerales = (configEntrada = {}) => {
  const config = repararHorarioSeguido(configEntrada) || {};
  const comidaIni = config.comidaStart || '13:30';
  const comidaFin = config.comidaEnd || '17:00';
  const nocheIni = config.nocheStart || '20:30';
  const nocheFin = config.nocheEnd || '00:30';
  const durLegacy = duracionEntre(comidaIni, comidaFin) || 240;
  const dur = config.duracionTurnoMin > 0 ? config.duracionTurnoMin : durLegacy;
  const aperturaIni = config.aperturaHora || '12:30';
  // La apertura dura un turno: quien abre a las 12:30 con turnos de 4 h sale a las 16:30. Solo
  // con "horas personalizadas" activadas se respeta una salida de apertura escrita a mano.
  const aperturaFin = (config.horariosPersonalizados && config.aperturaFin)
    ? config.aperturaFin
    : aHora(aMinutos(aperturaIni) + dur);
  const enlaceIni = config.enlaceHoraInicio || '16:00';
  const enlaceFin = config.enlaceHoraFin || nocheFin;
  return { dur, aperturaIni, aperturaFin, comidaIni, comidaFin, nocheIni, nocheFin, enlaceIni, enlaceFin };
};

// ¿Tiene esta persona un horario PROPIO de almuerzo/cena? Solo si el Encargado lo activó a
// propósito en su ficha. Las copias de horas que se guardaban al crear cada empleado ya no
// cuentan: así, al cambiar la configuración, TODO el equipo sigue el horario nuevo.
const usaHorarioPropio = (emp) => emp?.horarioPropio === true;
// ¿Tiene su propio horario de Enlace? Sí si se marcó a propósito; en fichas antiguas (sin la
// marca), solo si sus horas guardadas son DISTINTAS del Enlace general (señal de que se
// eligieron a mano para esa persona).
const usaEnlacePropio = (emp, H) => {
  if (emp?.enlaceHorarioPropio === true) return true;
  if (emp?.enlaceHorarioPropio === false) return false;
  if (!emp?.horaEntradaEnlace) return false;
  return emp.horaEntradaEnlace !== H.enlaceIni || (emp.horaSalidaEnlace || H.enlaceFin) !== H.enlaceFin;
};

// Horas reales de un tipo de turno para una persona. Partido devuelve el tramo de almuerzo en
// horaEntrada/horaSalida (como siempre) y además el de cena en horaEntradaNoche/horaSalidaNoche.
const horasDeTipo = (tipo, emp, config) => {
  const H = horariosGenerales(config);
  const propio = usaHorarioPropio(emp);
  const cIni = (propio && emp.horaEntradaComida) || H.comidaIni;
  const cFin = (propio && emp.horaSalidaComida) || H.comidaFin;
  const nIni = (propio && emp.horaEntradaNoche) || H.nocheIni;
  const nFin = (propio && emp.horaSalidaNoche) || H.nocheFin;
  if (tipo === 'apertura') return { horaEntrada: H.aperturaIni, horaSalida: H.aperturaFin };
  if (tipo === 'comida') return { horaEntrada: cIni, horaSalida: cFin };
  if (tipo === 'noche') return { horaEntrada: nIni, horaSalida: nFin };
  if (tipo === 'partido') return { horaEntrada: cIni, horaSalida: cFin, horaEntradaNoche: nIni, horaSalidaNoche: nFin };
  if (tipo === 'enlace') {
    if (usaEnlacePropio(emp, H)) return { horaEntrada: emp.horaEntradaEnlace || H.enlaceIni, horaSalida: emp.horaSalidaEnlace || H.enlaceFin };
    return { horaEntrada: H.enlaceIni, horaSalida: H.enlaceFin };
  }
  return { horaEntrada: '', horaSalida: '' };
};

// Cuántos turnos "vale" un Enlace: sus horas de TRABAJO divididas entre lo que dura un turno,
// redondeado. Un Enlace de más de 6 h lleva media hora de descanso que no cuenta como trabajo
// (16:00 → 00:30 son 8 h 30 = 8 h de trabajo + 30 min de descanso; con turnos de 4 h, 2 turnos).
const DESCANSO_ENLACE_MIN = 30;
const minutosTrabajoEnlace = (ini, fin) => {
  const total = duracionEntre(ini, fin);
  return total > 360 ? total - DESCANSO_ENLACE_MIN : total;
};
const pesoEnlaceDeHoras = (ini, fin, durTurno) => Math.max(1, Math.round(minutosTrabajoEnlace(ini, fin) / Math.max(30, durTurno || 240)));
const pesoEnlace = (emp, config) => {
  const H = horariosGenerales(config);
  const h = horasDeTipo('enlace', emp, config);
  return pesoEnlaceDeHoras(h.horaEntrada, h.horaSalida, H.dur);
};
// Turnos que vale una casilla ya generada.
const pesoTurno = (s, emp, config) => {
  const t = s?.tipo;
  if (t === 'partido') return 2;
  if (t === 'enlace') return pesoEnlace(emp, config);
  if (t === 'apertura' || t === 'comida' || t === 'noche') return 1;
  return 0;
};
const turnosAsignados = (semana, emp, config) => DAYS.reduce((a, d) => a + pesoTurno(semana?.[d], emp, config), 0);

// Días de la semana que se genera con una solicitud de día libre APROBADA para esta persona.
// FALLO REAL ARREGLADO (10 oct 2026): antes una solicitud aprobada bloqueaba ese DÍA DE LA
// SEMANA para siempre (pedir libre el martes 13 dejaba a la persona sin trabajar ningún martes
// mientras la solicitud existiera). Ahora solo cuenta si su fecha cae en la semana generada.
const diasSolicitudesSemana = (emp, approvedRequests, weekDates) => {
  const fechas = new Set(Object.values(weekDates || {}));
  const dias = new Set();
  (approvedRequests || []).forEach(r => {
    if (r.status !== 'approved') return;
    if (r.senderId ? String(r.senderId) !== String(emp.id) : r.sender !== emp.name) return;
    if (!fechas.has(r.date)) return;
    const dia = DAYS.find(d => weekDates[d] === r.date);
    if (dia) dias.add(dia);
  });
  return dias;
};

// Turnos que le TOCAN a una persona esta semana, y por qué si son menos que su contrato.
// Es la misma cuenta que usa el generador, el validador y la pantalla (el "8/10" de cada fila).
const turnosObjetivo = (emp, config, approvedRequests, weekDates = getWeekDates()) => {
  const cierre = config.diasCierreSemana || [];
  const partidoOk = config.permiteTurnoPartido !== false;
  const descansos = Math.max(1, Math.min(6, config.diasDescansoSemana || 2));
  const solicitudes = diasSolicitudesSemana(emp, approvedRequests, weekDates);
  const fijos = new Set(emp.diasFijosLibres || []);
  const ausencia = {};
  const disponibles = [];
  DAYS.forEach(d => {
    if (estaDeBajaEse(emp, weekDates[d])) ausencia[d] = 'baja';
    else if (estaAusenteEse(emp, weekDates[d])) ausencia[d] = 'vacaciones';
    if (!cierre.includes(d) && !ausencia[d] && !solicitudes.has(d) && !fijos.has(d)) disponibles.push(d);
  });
  const maxDias = Math.max(0, Math.min(disponibles.length, 7 - descansos));
  const contrato = Math.max(0, Math.round(emp.turnosSemanales || 5)); // mismo criterio que el resto de la app (sin número = 5)
  const esEnlace = !!emp.hacesEnlace && !emp.esEncargado;
  if (esEnlace) {
    const w = pesoEnlace(emp, config);
    const enlaces = Math.min(Math.floor(contrato / w), maxDias);
    return { objetivo: enlaces * w, contrato, maxDias, disponibles, ausencia, solicitudes, esEnlace, peso: w, enlaces,
      motivo: enlaces * w < contrato ? (Math.floor(contrato / w) > maxDias ? 'dias' : 'enlace') : null };
  }
  const porDia = partidoOk ? 2 : 1;
  const base = (!partidoOk && contrato < 7) ? Math.min(contrato, 6) : contrato;
  const objetivo = Math.min(base, maxDias * porDia);
  return { objetivo, contrato, maxDias, disponibles, ausencia, solicitudes, esEnlace, peso: 1, enlaces: 0,
    motivo: objetivo < contrato ? 'dias' : null };
};

// Pone a cada casilla de un horario las horas que le tocan según su tipo (se usa también sobre
// el resultado del generador antiguo de respaldo, para que las horas salgan siempre iguales).
const normalizarHorasHorario = (schedule, employees, config) => {
  const out = {};
  employees.forEach(emp => {
    const semana = schedule?.[emp.id] || {};
    out[emp.id] = {};
    DAYS.forEach(d => {
      const s = semana[d] || { tipo: 'libre', tareas: [] };
      if (['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(s.tipo)) {
        const base = { tipo: s.tipo, tareas: s.tareas || [], ...horasDeTipo(s.tipo, emp, config) };
        if (s.descanso) base.descanso = s.descanso;
        out[emp.id][d] = base;
      } else {
        out[emp.id][d] = { ...s, tipo: s.tipo || 'libre', tareas: s.tareas || [], horaEntrada: '', horaSalida: '' };
      }
    });
  });
  return out;
};

// ¿Respeta este par de casillas seguidas (ayer → hoy) las 12 h de descanso del art. 34.3 ET?
// Se calcula con las horas REALES de cada casilla (no con suposiciones), así vale para cualquier
// configuración: cierres tardíos, aperturas tempranas, Enlaces con horario propio…
const finTurnoMin = (s) => {
  // minuto (desde las 00:00 del día del turno) en que termina el ÚLTIMO tramo del turno.
  const ini = aMinutos(s.tipo === 'partido' ? (s.horaEntradaNoche || s.horaEntrada) : s.horaEntrada);
  const fin = aMinutos(s.tipo === 'partido' ? (s.horaSalidaNoche || s.horaSalida) : s.horaSalida);
  if (ini == null || fin == null) return null;
  return fin <= ini ? fin + 1440 : fin;
};
const respeta12h = (ayer, hoy) => {
  const trabaja = t => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(t);
  if (!ayer || !hoy || !trabaja(ayer.tipo) || !trabaja(hoy.tipo)) return true;
  const finAyer = finTurnoMin(ayer);
  const iniHoy = aMinutos(hoy.horaEntrada);
  if (finAyer == null || iniHoy == null) return true;
  return (1440 + iniHoy) - finAyer >= 720;
};

// VALIDADOR (10 oct 2026). Revisa un horario ya generado contra TODAS las reglas duras. Si
// devuelve algún problema, el horario no se usa (se cae al generador de respaldo). Reglas:
//  1. Cada persona tiene EXACTAMENTE los turnos que le tocan (ni uno más ni uno menos).
//  2. Nadie trabaja un día de baja, vacaciones, día libre fijo, solicitud aprobada o cierre.
//  3. Nadie trabaja más días de los que le permiten sus descansos semanales.
//  4. Sin turno partido si el local no lo permite; Enlace solo quien es Enlace (y siempre Enlace).
//  5. Los encargados no hacen apertura.
//  6. 12 h de descanso entre turnos.
const validarHorario = (schedule, employees, config, approvedRequests, weekDates = getWeekDates(), reducciones12h = {}) => {
  const problemas = [];
  const partidoOk = config.permiteTurnoPartido !== false;
  const TIPOS_TRABAJO = ['apertura', 'comida', 'noche', 'partido', 'enlace'];
  employees.forEach(emp => {
    const semana = schedule?.[emp.id];
    if (!semana) { problemas.push(`${emp.name}: no aparece en el horario`); return; }
    const obj = turnosObjetivo(emp, config, approvedRequests, weekDates);
    let turnos = 0, dias = 0;
    DAYS.forEach((d, i) => {
      const s = semana[d];
      if (!s || !s.tipo) { problemas.push(`${emp.name}: ${d} vacío`); return; }
      const trabaja = TIPOS_TRABAJO.includes(s.tipo);
      if (trabaja) { dias++; turnos += pesoTurno(s, emp, config); }
      if (trabaja && !obj.disponibles.includes(d)) problemas.push(`${emp.name}: trabaja ${d} y no está disponible`);
      if (obj.ausencia[d] && s.tipo !== obj.ausencia[d]) problemas.push(`${emp.name}: ${d} debería salir como ${obj.ausencia[d]}`);
      if (s.tipo === 'partido' && !partidoOk) problemas.push(`${emp.name}: partido con el partido desactivado`);
      if (obj.esEnlace && trabaja && s.tipo !== 'enlace') problemas.push(`${emp.name}: es Enlace y tiene ${s.tipo}`);
      if (!obj.esEnlace && s.tipo === 'enlace') problemas.push(`${emp.name}: tiene Enlace sin ser Enlace`);
      if (emp.esEncargado && s.tipo === 'apertura') problemas.push(`${emp.name}: encargado en apertura`);
      if (i > 0 && !respeta12h(semana[DAYS[i - 1]], s)) problemas.push(`${emp.name}: menos de 12 h de descanso antes del ${d}`);
    });
    const permitidos = obj.objetivo - (reducciones12h[emp.id] || 0);
    if (turnos !== permitidos) problemas.push(`${emp.name}: ${turnos} turnos y le tocan ${permitidos}`);
    if (dias > obj.maxDias) problemas.push(`${emp.name}: trabaja ${dias} días y como máximo puede ${obj.maxDias}`);
  });
  return problemas;
};

// =====================================================================================
// GENERADOR DE HORARIOS v2 (10 oct 2026) — optimización matemática con reglas duras.
//
// Cómo funciona, en dos fases:
//  1. CONSTRUCCIÓN EXACTA. A cada persona se le da desde el principio EXACTAMENTE el número de
//     turnos que le toca (turnosObjetivo), solo en días en los que puede trabajar y sin pasarse
//     de los días que le permiten sus descansos. Esto no es un objetivo "a intentar": es la forma
//     de la solución, y ninguna fase posterior puede romperlo.
//  2. MEJORA (recocido simulado, "simulated annealing"). Se prueban miles de pequeños cambios que
//     por construcción NO cambian los turnos de nadie (mover un día de una persona a otro día suyo
//     libre, cambiar almuerzo↔cena, partir o juntar un partido, intercambiar dos personas el mismo
//     día…) y se quedan los que mejoran la puntuación del horario. Al principio se aceptan también
//     algunos cambios peores (para no quedarse atascado en una solución mediocre) y cada vez menos,
//     hasta quedarse con la mejor encontrada.
//
// La puntuación (cuanto más baja, mejor), por orden de importancia:
//  - 12 h de descanso entre turnos (prácticamente prohibido incumplirlo).
//  - Mínimo de gente en almuerzo y cena de CADA día (1000 por persona que falte).
//  - Personas de apertura (900 por persona que falte).
//  - Lo que el Encargado pide de más un día concreto (250 por persona, también las de "día
//    completo") y el extra de un día con estrella (180). Están calculados para que ni siquiera un
//    partido entero (que suma almuerzo + cena + día completo = 3 × 250 = 750) pese más que UNA
//    persona de mínimo en otro día (900-1000): así un día especial se lleva la gente que SOBRA y
//    nunca deja otro día por debajo de su mínimo.
//  - Reparto de la gente que sobra (más en viernes, sábado, domingo y días especiales).
//  - Equidad: descansos seguidos, no librar viernes/sábado, almuerzos y cenas equilibrados, quien
//    más noches ha hecho las últimas semanas hace menos, aperturas repartidas, rotación de findes.
// =====================================================================================
const generarHorarioOptimo = (employees, specialDays, approvedRequests, config, temporadaConfig, scheduleHistory, isCocina, opciones = {}) => {
  const weekDates = getWeekDates();
  const ND = 7;
  const cierre = config.diasCierreSemana || [];
  const abierto = DAYS.map(d => !cierre.includes(d));
  const partidoOk = config.permiteTurnoPartido !== false;
  const descansosSeguidos = config.diasDescansoConsecutivos !== false;
  const E = employees.length;
  if (E === 0) return {};

  // Tipos internos (números, para que sea rápido).
  const L = 0, CO = 1, NO = 2, AP = 3, PA = 4, EN = 5;
  const NOMBRE_TIPO = ['libre', 'comida', 'noche', 'apertura', 'partido', 'enlace'];
  const PESO = [0, 1, 1, 1, 2, 0]; // EN se pesa por persona (w)

  // --- Personas ---
  const P = employees.map(emp => {
    const o = turnosObjetivo(emp, config, approvedRequests, weekDates);
    const disp = DAYS.map(d => o.disponibles.includes(d));
    return {
      emp, o, disp, enc: !!emp.esEncargado, enl: o.esEnlace, w: o.peso,
      T: o.objetivo, maxDias: o.maxDias,
      pMin: o.esEnlace ? 0 : Math.max(0, o.objetivo - o.maxDias),
    };
  });

  // --- Mínimos de la semana (mismo auto-ajuste a la plantilla real que el generador clásico) ---
  const minComidaCfg = temporadaConfig?.minComida ?? temporadaConfig?.minPersonas ?? 4;
  const minNocheCfg = temporadaConfig?.minNoche ?? temporadaConfig?.minPersonas ?? 4;
  const disponiblesSemana = P.filter(p => p.o.disponibles.length > 0).length;
  const diasAbiertos = abierto.filter(Boolean).length;
  const descansosObl = Math.max(1, Math.min(6, config.diasDescansoSemana || 2));
  // MÍNIMO SIEMPRE (12 oct 2026, pedido por Fran: "tiene que haber siempre el mínimo"). Antes el
  // techo se calculaba por PERSONAS (12 personas × 5 días / 7 = 8 al día) y, si Almuerzo + Cena
  // pedían más (3 + 6 = 9), se recortaban los mínimos en silencio: la Cena de 6 se quedaba en 5.
  // Pero una persona con turno partido cubre Almuerzo y Cena el mismo día, así que lo que limita
  // de verdad son los TURNOS que suman los contratos: solo se recorta si no hay turnos para cubrir
  // los mínimos de todos los días (y cada mínimo cabe siempre en las personas que hay).
  const personasPorDiaMax = diasAbiertos > descansosObl
    ? Math.max(1, Math.floor(disponiblesSemana * (diasAbiertos - descansosObl) / diasAbiertos))
    : disponiblesSemana;
  const turnosTotales = P.reduce((a, p) => a + p.T, 0);
  const turnosPorDia = Math.max(1, Math.floor(turnosTotales / Math.max(1, diasAbiertos)));
  let minC = Math.min(minComidaCfg, personasPorDiaMax), minN = Math.min(minNocheCfg, personasPorDiaMax);
  if (minC + minN > turnosPorDia) {
    const r = turnosPorDia / (minC + minN);
    minC = Math.max(1, Math.round(minC * r));
    minN = Math.max(1, Math.round(minN * r));
  }
  // MÁXIMO OPCIONAL (12 oct 2026): "maxComida"/"maxNoche" de la temporada. Sin número = sin máximo.
  const maxCfgC = Number.isFinite(temporadaConfig?.maxComida) ? Math.max(1, Math.round(temporadaConfig.maxComida)) : null;
  const maxCfgN = Number.isFinite(temporadaConfig?.maxNoche) ? Math.max(1, Math.round(temporadaConfig.maxNoche)) : null;
  const numApertura = Math.min(config.personasApertura || 2, Math.max(1, minC));

  // --- Objetivos por día ---
  const tope = Math.max(1, disponiblesSemana);
  const DIA = DAYS.map((d, i) => {
    if (!abierto[i]) return null;
    const ov = config.personasPorDia?.[d];
    const conNumeros = !!(ov && (ov.comida != null || ov.noche != null || ov.completo != null));
    const estrella = !!specialDays?.[d] || conNumeros;
    const completo = (partidoOk && ov?.completo != null) ? Math.max(0, Math.min(Math.round(ov.completo), tope)) : 0;
    const cPide = ov?.comida != null ? Math.round(ov.comida) : (estrella ? minC + Math.max(1, Math.ceil(minC * 0.5)) : minC);
    const nPide = ov?.noche != null ? Math.round(ov.noche) : (estrella ? minN + Math.max(1, Math.ceil(minN * 0.5)) : minN);
    const Ct = Math.max(1, completo, Math.min(cPide, tope));
    const Nt = Math.max(1, completo, Math.min(nPide, tope));
    // Si el día tiene número propio MENOR que el mínimo general, ese número es su mínimo.
    const Cmin = Math.min(minC, Ct), Nmin = Math.min(minN, Nt);
    const wExtra = conNumeros ? 250 : 180;
    // Reparto parejo (12 oct 2026): todos los días normales reciben lo mismo; solo los días
    // especiales (con estrella o con número propio) reciben más. Antes el viernes, el sábado y el
    // domingo se llevaban más gente de forma automática y parecía que el número era caprichoso.
    // El máximo opcional no se aplica a los días especiales: ahí manda el número que se les pidió.
    const Cmax = (!estrella && maxCfgC != null) ? Math.max(Ct, maxCfgC) : null;
    const Nmax = (!estrella && maxCfgN != null) ? Math.max(Nt, maxCfgN) : null;
    return { Cmin, Nmin, Ct, Nt, Cmax, Nmax, Pt: completo, A: numApertura, wExtra, omega: estrella ? 1.8 : 1 };
  });

  // --- Horas en minutos por persona y tipo (para la regla de 12 h) ---
  const tramo = P.map(p => {
    const r = [];
    for (let t = 0; t < 6; t++) {
      if (t === L) { r.push(null); continue; }
      const h = horasDeTipo(NOMBRE_TIPO[t], p.emp, config);
      const ini = aMinutos(h.horaEntrada);
      const finRaw = aMinutos(t === PA ? h.horaSalidaNoche : h.horaSalida);
      const iniUlt = aMinutos(t === PA ? h.horaEntradaNoche : h.horaEntrada);
      const fin = finRaw == null || iniUlt == null ? null : (finRaw <= iniUlt ? finRaw + 1440 : finRaw);
      r.push({ ini, fin });
    }
    return r;
  });
  // choca12[i][t1][t2]: ¿trabajar t1 ayer y t2 hoy deja menos de 12 h?
  const choca12 = tramo.map(r => {
    const m = [];
    for (let a = 0; a < 6; a++) {
      m.push([]);
      for (let b = 0; b < 6; b++) {
        if (a === L || b === L || !r[a] || !r[b] || r[a].fin == null || r[b].ini == null) { m[a].push(false); continue; }
        m[a].push((1440 + r[b].ini) - r[a].fin < 720);
      }
    }
    return m;
  });

  // --- Datos de equidad (historial) ---
  const findeLibres = calcularDiasFindeLibresRecientes(scheduleHistory, 4);
  const incomodos = calcularTurnosIncomodosRecientes(scheduleHistory, 4);
  const incs = P.map(p => incomodos[p.emp.name] || 0);
  const mediaInc = incs.reduce((a, b) => a + b, 0) / E;
  const sdInc = Math.sqrt(incs.reduce((a, b) => a + (b - mediaInc) ** 2, 0) / E) || 1;
  const zInc = incs.map(v => Math.max(-2, Math.min(2, (v - mediaInc) / sdInc)));
  const bonoFinde = P.map(p => 12 * Math.max(0, 2 - (findeLibres[p.emp.name] || 0)));
  const aptosApertura = P.filter(p => !p.enc && !p.enl).length;
  const mediaAperturas = aptosApertura > 0 ? (DIA.reduce((s, x) => s + (x ? x.A : 0), 0) / aptosApertura) : 0;
  const IDX_VIE = DAYS.indexOf('Viernes'), IDX_SAB = DAYS.indexOf('Sábado');
  const IDX_FINDE = [DAYS.indexOf('Viernes'), DAYS.indexOf('Sábado'), DAYS.indexOf('Domingo')];

  // --- Estado ---
  const X = P.map(() => new Array(ND).fill(L));
  const cC = new Array(ND).fill(0), cN = new Array(ND).fill(0), cA = new Array(ND).fill(0), cP = new Array(ND).fill(0);
  const contribuye = (i, d, t, s) => {
    if (t === CO) cC[d] += s;
    else if (t === AP) { cC[d] += s; cA[d] += s; }
    else if (t === NO) cN[d] += s;
    else if (t === PA) { cC[d] += s; cN[d] += s; cP[d] += s; }
    else if (t === EN) cN[d] += s;
  };
  const poner = (i, d, t) => { contribuye(i, d, X[i][d], -1); X[i][d] = t; contribuye(i, d, t, 1); };

  // --- Puntuación ---
  const costeDia = (d) => {
    const x = DIA[d];
    if (!x) return 0;
    const c = cC[d], n = cN[d];
    let k = 1000 * (Math.max(0, x.Cmin - c) + Math.max(0, x.Nmin - n));
    k += x.wExtra * ((Math.max(0, x.Ct - c) - Math.max(0, x.Cmin - c)) + (Math.max(0, x.Nt - n) - Math.max(0, x.Nmin - n)));
    k += 250 * Math.max(0, x.Pt - cP[d]);
    k += 900 * Math.max(0, x.A - cA[d]) + 60 * Math.max(0, cA[d] - x.A);
    if (x.Cmax != null) k += 700 * Math.max(0, c - x.Cmax);
    if (x.Nmax != null) k += 700 * Math.max(0, n - x.Nmax);
    const dc = c - x.Ct, dn = n - x.Nt;
    if (dc > 0 && dn > 0) k += 2 * (dc - dn) * (dc - dn); // que lo que sobra no se vaya todo a un turno
    else if (dc > 0 || dn > 0) k += 2 * Math.max(dc, dn, 0) ** 2;
    return k;
  };
  const costeGlobal = () => {
    // Reparto de lo que sobra entre días, proporcional a su importancia (omega).
    let sumS = 0, sumW = 0;
    for (let d = 0; d < ND; d++) {
      const x = DIA[d]; if (!x) continue;
      sumS += Math.max(0, cC[d] - x.Ct) + Math.max(0, cN[d] - x.Nt);
      sumW += x.omega;
    }
    if (sumW === 0) return 0;
    const mu = sumS / sumW;
    let k = 0;
    for (let d = 0; d < ND; d++) {
      const x = DIA[d]; if (!x) continue;
      const s = Math.max(0, cC[d] - x.Ct) + Math.max(0, cN[d] - x.Nt);
      const z = s / x.omega - mu;
      k += 4 * x.omega * z * z;
    }
    return k;
  };
  const costePersona = (i) => {
    const p = P[i], fila = X[i], ch = choca12[i];
    let k = 0, nC = 0, nN = 0, nA = 0, nP = 0;
    let bloques = 0, prevLibre = false, findeDescansado = false;
    for (let d = 0; d < ND; d++) {
      const t = fila[d];
      if (d > 0 && ch[fila[d - 1]][t]) k += 100000;
      const libre = t === L;
      if (libre) {
        if (!prevLibre) bloques++;
        if (p.disp[d]) {
          if (d === IDX_VIE || d === IDX_SAB) k += 45;
          if (IDX_FINDE.includes(d)) findeDescansado = true;
        }
      }
      prevLibre = libre;
      if (t === CO) nC++; else if (t === NO) nN++; else if (t === AP) { nA++; nC++; } else if (t === PA) nP++;
    }
    if (descansosSeguidos && bloques > 1) k += 60 * (bloques - 1);
    if (findeDescansado) k -= bonoFinde[i];
    if (!p.enl) {
      k += 3 * (nN - nC) * (nN - nC);
      k += 6 * zInc[i] * (nN + nP);
      k += 15 * Math.max(0, nP - p.pMin);
      if (!p.enc) k += 6 * (nA - mediaAperturas) * (nA - mediaAperturas);
    }
    return k;
  };

  // --- Construcción exacta ---
  const necesidadDia = (d) => {
    const x = DIA[d]; if (!x) return -1e9;
    return (x.Ct - cC[d]) + (x.Nt - cN[d]);
  };
  const construir = () => {
    for (let i = 0; i < E; i++) for (let d = 0; d < ND; d++) X[i][d] = L;
    cC.fill(0); cN.fill(0); cA.fill(0); cP.fill(0);
    const orden = shuffle(P.map((_, i) => i)).sort((a, b) => P[a].o.disponibles.length - P[b].o.disponibles.length);
    orden.forEach(i => {
      const p = P[i];
      const dias = shuffle(DAYS.map((_, d) => d).filter(d => p.disp[d]));
      if (p.enl) {
        const k = p.w > 0 ? Math.round(p.T / p.w) : 0;
        dias.sort((a, b) => (DIA[b].Nt - cN[b]) - (DIA[a].Nt - cN[a]));
        dias.slice(0, k).forEach(d => poner(i, d, EN));
        return;
      }
      const k = Math.min(p.maxDias, p.T);
      const nPart = partidoOk ? p.T - k : 0;
      dias.sort((a, b) => necesidadDia(b) - necesidadDia(a));
      const elegidos = dias.slice(0, k);
      elegidos.forEach(d => {
        const x = DIA[d];
        const faltaC = x.Ct - cC[d], faltaN = x.Nt - cN[d];
        poner(i, d, faltaC > faltaN ? CO : (faltaN > faltaC ? NO : (Math.random() < 0.5 ? CO : NO)));
      });
      [...elegidos].sort((a, b) => necesidadDia(b) - necesidadDia(a)).slice(0, nPart).forEach(d => poner(i, d, PA));
    });
    // Aperturas: entre quien hace solo almuerzo ese día (y no es encargado).
    for (let d = 0; d < ND; d++) {
      if (!DIA[d]) continue;
      const cand = shuffle(P.map((_, i) => i).filter(i => !P[i].enc && !P[i].enl && X[i][d] === CO));
      cand.slice(0, DIA[d].A).forEach(i => poner(i, d, AP));
      if (cA[d] < DIA[d].A) {
        const extra = shuffle(P.map((_, i) => i).filter(i => !P[i].enc && !P[i].enl && X[i][d] === NO));
        extra.slice(0, DIA[d].A - cA[d]).forEach(i => poner(i, d, AP));
      }
    }
  };

  // --- Movimientos (todos conservan los turnos de cada persona) ---
  const trabajaDias = (i) => { let k = 0; for (let d = 0; d < ND; d++) if (X[i][d] !== L) k++; return k; };
  const individualesPermitidos = (i) => (P[i].enc ? [CO, NO] : [CO, NO, AP]);
  const azar = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const proponer = () => {
    const i = Math.floor(Math.random() * E);
    const p = P[i];
    if (p.T === 0) return null;
    const fila = X[i];
    const trab = [], libresDisp = [];
    for (let d = 0; d < ND; d++) {
      if (fila[d] !== L) trab.push(d); else if (p.disp[d]) libresDisp.push(d);
    }
    const r = Math.random();
    if (p.enl) {
      if (trab.length === 0 || libresDisp.length === 0) return null;
      const d1 = azar(trab), d2 = azar(libresDisp);
      return [[i, d1, L], [i, d2, EN]];
    }
    if (r < 0.22) { // M1: cambiar el tipo de un turno suelto (almuerzo ↔ cena ↔ apertura)
      const sueltos = trab.filter(d => fila[d] !== PA);
      if (sueltos.length === 0) return null;
      const d = azar(sueltos);
      const ops = individualesPermitidos(i).filter(t => t !== fila[d]);
      return [[i, d, azar(ops)]];
    }
    if (r < 0.44) { // M2: mover un día de trabajo a otro día libre suyo
      if (trab.length === 0 || libresDisp.length === 0) return null;
      const d1 = azar(trab), d2 = azar(libresDisp);
      const t = fila[d1] === PA ? PA : azar(individualesPermitidos(i));
      return [[i, d1, L], [i, d2, t]];
    }
    if (r < 0.56 && partidoOk) { // M3: pasar el partido de un día a otro que ya trabaja
      const parts = trab.filter(d => fila[d] === PA), sueltos = trab.filter(d => fila[d] !== PA);
      if (parts.length === 0 || sueltos.length === 0) return null;
      const d1 = azar(parts), d2 = azar(sueltos);
      return [[i, d1, azar(individualesPermitidos(i))], [i, d2, PA]];
    }
    if (r < 0.66 && partidoOk) { // M4: partir un partido en dos días, o juntar dos días en un partido
      const parts = trab.filter(d => fila[d] === PA), sueltos = trab.filter(d => fila[d] !== PA);
      if (Math.random() < 0.5) {
        if (parts.length === 0 || libresDisp.length === 0 || trab.length >= p.maxDias) return null;
        return [[i, azar(parts), azar(individualesPermitidos(i))], [i, azar(libresDisp), azar(individualesPermitidos(i))]];
      }
      if (sueltos.length < 2) return null;
      const d1 = azar(sueltos);
      const d2 = azar(sueltos.filter(d => d !== d1));
      return [[i, d1, L], [i, d2, PA]];
    }
    if (r < 0.84) { // M5: intercambiar el tipo con otra persona el mismo día
      if (trab.length === 0) return null;
      const d = azar(trab);
      if (fila[d] === PA || fila[d] === EN) return null;
      const otros = [];
      for (let j = 0; j < E; j++) {
        if (j === i || P[j].enl) continue;
        const t2 = X[j][d];
        if (t2 === L || t2 === PA || t2 === fila[d]) continue;
        if ((t2 === AP && p.enc) || (fila[d] === AP && P[j].enc)) continue;
        otros.push(j);
      }
      if (otros.length === 0) return null;
      const j = azar(otros);
      return [[i, d, X[j][d]], [j, d, fila[d]]];
    }
    // M6: intercambiar días entre dos personas (yo trabajo d1 y libro d2; la otra al revés)
    if (trab.length === 0 || libresDisp.length === 0) return null;
    const d1 = azar(trab), d2 = azar(libresDisp);
    const t1 = fila[d1];
    const otros = [];
    for (let j = 0; j < E; j++) {
      if (j === i || P[j].enl) continue;
      const t2 = X[j][d2];
      if (t2 === L || X[j][d1] !== L || !P[j].disp[d1]) continue;
      if (PESO[t2] !== PESO[t1]) continue;
      if ((t1 === AP && P[j].enc) || (t2 === AP && p.enc)) continue;
      otros.push(j);
    }
    if (otros.length === 0) return null;
    const j = azar(otros);
    const t2 = X[j][d2];
    return [[i, d1, L], [i, d2, t2], [j, d2, L], [j, d1, t1]];
  };

  // Solo para las pruebas automáticas (comparar con un resolvedor matemático exacto): devuelve
  // el modelo sin generar nada. La app nunca pasa esta opción.
  if (opciones.soloModelo) return { P: P.map(p => ({ T: p.T, maxDias: p.maxDias, disp: p.disp, enc: p.enc, enl: p.enl, w: p.w })), DIA, choca12, partidoOk };

  // --- Recocido simulado ---
  // VARIOS INTENTOS (11 oct 2026, pedido por Fran): se generan varios horarios completos desde
  // puntos de partida distintos y se queda el de mejor puntuación. Todo ocurre en el móvil del
  // Encargado, en memoria: no se guarda nada en el servidor ni cuesta nada. Cada intento tiene su
  // parte del tiempo total, así un móvil lento simplemente hace menos intentos.
  const iterBase = opciones.rapido ? 1500 + 150 * E : 6000 + 700 * E;
  const ITER = Math.min(opciones.rapido ? 6000 : 30000, iterBase);
  const REINICIOS = opciones.rapido ? 1 : 6;
  // Tope de tiempo por seguridad (móviles lentos): pase lo que pase, a los ~1,6 s se queda con
  // la mejor solución encontrada hasta ese momento.
  const LIMITE_MS = opciones.rapido ? 300 : 1600;
  const PRESUPUESTO_INTENTO = LIMITE_MS / REINICIOS;
  const t0 = Date.now();
  let mejor = null, mejorCoste = Infinity;
  for (let rep = 0; rep < REINICIOS; rep++) {
    if (rep > 0 && Date.now() - t0 > LIMITE_MS) break;
    const tIntento = Date.now();
    construir();
    const cd = DAYS.map((_, d) => costeDia(d));
    const cp = P.map((_, i) => costePersona(i));
    let SD = cd.reduce((x, y) => x + y, 0), SP = cp.reduce((x, y) => x + y, 0);
    let actual = SD + SP + costeGlobal();
    let mejorLocal = actual, copiaLocal = X.map(f => f.slice());
    const T0 = 400, T1 = 0.5;
    for (let it = 0; it < ITER; it++) {
      if ((it & 255) === 0 && it > 0 && (Date.now() - tIntento > PRESUPUESTO_INTENTO || Date.now() - t0 > LIMITE_MS)) break;
      const temp = T0 * Math.pow(T1 / T0, it / ITER);
      const mov = proponer();
      if (!mov) continue;
      // Comprobaciones duras del movimiento: disponibilidad y días máximos.
      let valido = true;
      for (const [i, d, t] of mov) { if (t !== L && !P[i].disp[d]) { valido = false; break; } }
      if (!valido) continue;
      const viejo = mov.map(([i, d]) => X[i][d]);
      const dias = [...new Set(mov.map(m => m[1]))];
      const pers = [...new Set(mov.map(m => m[0]))];
      mov.forEach(([i, d, t]) => poner(i, d, t));
      if (pers.some(i => trabajaDias(i) > P[i].maxDias)) { mov.forEach(([i, d], k) => poner(i, d, viejo[k])); continue; }
      const cdN = dias.map(d => costeDia(d));
      const cpN = pers.map(i => costePersona(i));
      const SDn = SD - dias.reduce((x, d) => x + cd[d], 0) + cdN.reduce((x, y) => x + y, 0);
      const SPn = SP - pers.reduce((x, i) => x + cp[i], 0) + cpN.reduce((x, y) => x + y, 0);
      const nuevo = SDn + SPn + costeGlobal();
      const delta = nuevo - actual;
      if (delta <= 0 || Math.random() < Math.exp(-delta / temp)) {
        dias.forEach((d, k) => { cd[d] = cdN[k]; });
        pers.forEach((i, k) => { cp[i] = cpN[k]; });
        SD = SDn; SP = SPn;
        actual = nuevo;
        if (actual < mejorLocal - 1e-9) { mejorLocal = actual; copiaLocal = X.map(f => f.slice()); }
      } else {
        for (let k = mov.length - 1; k >= 0; k--) poner(mov[k][0], mov[k][1], viejo[k]);
      }
    }
    if (mejorLocal < mejorCoste) { mejorCoste = mejorLocal; mejor = copiaLocal; }
  }

  // --- Reparación legal de las 12 h (solo en configuraciones donde es IMPOSIBLE cumplir los
  // turnos contratados sin romperla, p. ej. cierre a las 02:00 y almuerzo a las 13:00 con alguien
  // a 5 partidos). La ley manda: el turno que choca pasa a cena (si era partido, pierde el tramo de
  // almuerzo). Se apunta cuántos turnos pierde cada persona por esto, para que el validador y la
  // pantalla lo sepan y lo expliquen. En configuraciones normales esto nunca se ejecuta.
  const reducciones12h = {};
  P.forEach((p, i) => {
    for (let d = 1; d < ND; d++) {
      const ayer = mejor[i][d - 1], hoy = mejor[i][d];
      if (!choca12[i][ayer][hoy]) continue;
      if (hoy === EN) continue; // un Enlace no se puede convertir (no debería pasar)
      if (!choca12[i][ayer][NO]) {
        if (hoy === PA) reducciones12h[p.emp.id] = (reducciones12h[p.emp.id] || 0) + 1;
        mejor[i][d] = NO;
      }
    }
  });
  generarHorarioOptimo.ultimoInforme = { reducciones12h };

  // --- Resultado ---
  const schedule = {};
  P.forEach((p, i) => {
    schedule[p.emp.id] = {};
    DAYS.forEach((d, di) => {
      const t = mejor[i][di];
      if (t === L) {
        const aus = p.o.ausencia[d];
        schedule[p.emp.id][d] = { tipo: aus || 'libre', tareas: [], horaEntrada: '', horaSalida: '' };
      } else {
        schedule[p.emp.id][d] = { tipo: NOMBRE_TIPO[t], tareas: [], ...horasDeTipo(NOMBRE_TIPO[t], p.emp, config) };
      }
    });
  });
  // Tareas de recarga (barra / salón), igual que siempre — solo en Sala.
  if (!isCocina) {
    const NO_TRABAJA = ['libre', 'vacaciones', 'baja'];
    DAYS.forEach(day => {
      const trabajando = shuffle(employees.filter(e => !NO_TRABAJA.includes(schedule[e.id][day]?.tipo)));
      if (trabajando.length === 0) return;
      // Recargas repartidas al azar entre quien trabaja ese día (antes iban a la mejor nota).
      const quienBarra = trabajando[0];
      if (quienBarra) schedule[quienBarra.id][day].tareas.push('recargaBarra');
      const resto = shuffle(trabajando.filter(e => e.id !== quienBarra?.id));
      if (resto.length > 0) {
        const quienSalon = resto[0];
        if (quienSalon) schedule[quienSalon.id][day].tareas.push('recargaSalon');
      }
    });
  }
  return schedule;
};

// GARANTÍA DE TURNOS EXACTOS (11 oct 2026, pedido por Fran: "si X empleado está a 8 turnos tiene
// 8 turnos"). Última red de seguridad para cualquier horario que salga del generador: deja a cada
// persona EXACTAMENTE con los turnos que le tocan (turnosObjetivo), sin tocar a nadie que ya esté
// bien. Quita turnos donde más gente sobra y los pone donde más falta, siempre en días en los que
// puede trabajar, sin pasarse de sus días máximos y respetando las 12 h de descanso. Es pura
// cuenta en memoria: no lee ni escribe nada en el servidor.
const ajustarTurnosExactos = (schedule, employees, config, approvedRequests, weekDates = getWeekDates(), reducciones12h = {}) => {
  const TRABAJO = ['apertura', 'comida', 'noche', 'partido', 'enlace'];
  const partidoOk = config.permiteTurnoPartido !== false;
  const out = {};
  employees.forEach(emp => { out[emp.id] = { ...(schedule?.[emp.id] || {}) }; });
  const cuenta = (d) => {
    let c = 0, n = 0;
    employees.forEach(e => {
      const t = out[e.id]?.[d]?.tipo;
      if (t === 'partido') { c++; n++; } else if (t === 'comida' || t === 'apertura') c++; else if (t === 'noche' || t === 'enlace') n++;
    });
    return { c, n };
  };
  const celda = (tipo, emp, previa) => ({ tipo, tareas: previa?.tareas || [], ...horasDeTipo(tipo, emp, config) });
  const libreDe = (obj, d) => ({ tipo: obj.ausencia[d] || 'libre', tareas: [], horaEntrada: '', horaSalida: '' });
  employees.forEach(emp => {
    const fila = out[emp.id];
    const obj = turnosObjetivo(emp, config, approvedRequests, weekDates);
    const objetivo = Math.max(0, obj.objetivo - (reducciones12h[emp.id] || 0));
    const idx = (d) => DAYS.indexOf(d);
    const cabe12 = (d, s) => {
      const i = idx(d);
      const ayer = i > 0 ? fila[DAYS[i - 1]] : null, man = i < 6 ? fila[DAYS[i + 1]] : null;
      return respeta12h(ayer, s) && respeta12h(s, man);
    };
    // 1. Casillas imposibles fuera: trabajo en un día no disponible, o tipo que no le corresponde.
    DAYS.forEach(d => {
      const s = fila[d];
      if (!s || !s.tipo) { fila[d] = libreDe(obj, d); return; }
      if (obj.ausencia[d] && s.tipo !== obj.ausencia[d]) { fila[d] = libreDe(obj, d); return; }
      if (!TRABAJO.includes(s.tipo)) return;
      if (!obj.disponibles.includes(d)) { fila[d] = libreDe(obj, d); return; }
      if (obj.esEnlace && s.tipo !== 'enlace') fila[d] = celda('enlace', emp, s);
      else if (!obj.esEnlace && s.tipo === 'enlace') fila[d] = celda('noche', emp, s);
      else if (s.tipo === 'partido' && !partidoOk) fila[d] = celda('noche', emp, s);
      else if (s.tipo === 'apertura' && emp.esEncargado) fila[d] = celda('comida', emp, s);
    });
    // 1a. 12 h de descanso: si un turno choca con el del día anterior, se cambia por otro que no
    // choque (cena en vez de almuerzo/partido) o, si ninguno vale, se libra ese día. Los turnos
    // que se pierdan aquí se recolocan después en otro día.
    for (let i = 1; i < DAYS.length; i++) {
      const d = DAYS[i], s = fila[d];
      if (!s || !TRABAJO.includes(s.tipo) || respeta12h(fila[DAYS[i - 1]], s)) continue;
      const alternativas = obj.esEnlace ? [] : ['noche', 'comida'].filter(t => t !== s.tipo);
      const vale = alternativas.map(t => celda(t, emp, s)).find(c => cabe12(d, c));
      fila[d] = vale || libreDe(obj, d);
    }
    const dias = () => DAYS.filter(d => TRABAJO.includes(fila[d]?.tipo));
    const total = () => turnosAsignados(fila, emp, config);
    // Gente que hay ese día en almuerzo ('c') o cena ('n'): se quita de donde más hay y se pone
    // donde menos.
    const holgura = (d, turno) => { const { c, n } = cuenta(d); return turno === 'c' ? c : n; };
    let guardia = 40;
    // 1b. Más días de trabajo de los que le permiten sus descansos → se juntan dos días sueltos
    // en un partido (mismos turnos, un día menos).
    while (!obj.esEnlace && partidoOk && dias().length > obj.maxDias && guardia-- > 0) {
      const sueltos = dias().filter(d => fila[d].tipo !== 'partido')
        .sort((a, b) => (holgura(b, 'c') + holgura(b, 'n')) - (holgura(a, 'c') + holgura(a, 'n')));
      if (sueltos.length < 2) break;
      const quitar = sueltos[0];
      fila[quitar] = libreDe(obj, quitar);
      const destino = sueltos.slice(1).find(x => cabe12(x, celda('partido', emp)));
      if (!destino) break;
      fila[destino] = celda('partido', emp, fila[destino]);
    }
    while (dias().length > obj.maxDias && guardia-- > 0) {
      const d = [...dias()].sort((a, b) => (holgura(b, 'c') + holgura(b, 'n')) - (holgura(a, 'c') + holgura(a, 'n')))[0];
      fila[d] = libreDe(obj, d);
    }
    guardia = 40;
    // 2. Sobran turnos → se quitan.
    while (total() > objetivo && guardia-- > 0) {
      const trab = dias();
      if (obj.esEnlace) {
        const d = [...trab].sort((a, b) => holgura(b, 'n') - holgura(a, 'n'))[0];
        if (!d) break; fila[d] = libreDe(obj, d); continue;
      }
      const partidos = trab.filter(d => fila[d].tipo === 'partido');
      if (partidos.length > 0) {
        // Un partido se queda en el tramo donde más falta gente.
        const d = [...partidos].sort((a, b) => (holgura(b, 'c') + holgura(b, 'n')) - (holgura(a, 'c') + holgura(a, 'n')))[0];
        const tipo = holgura(d, 'c') > holgura(d, 'n') ? 'noche' : 'comida';
        const nueva = celda(tipo, emp, fila[d]);
        fila[d] = cabe12(d, nueva) ? nueva : celda(tipo === 'noche' ? 'comida' : 'noche', emp, fila[d]);
        continue;
      }
      const d = [...trab].sort((a, b) => (holgura(b, 'c') + holgura(b, 'n')) - (holgura(a, 'c') + holgura(a, 'n')))[0];
      if (!d) break; fila[d] = libreDe(obj, d);
    }
    // 3. Faltan turnos → se añaden.
    guardia = 40;
    while (total() < objetivo && guardia-- > 0) {
      const trab = dias();
      const libres = obj.disponibles.filter(d => !TRABAJO.includes(fila[d]?.tipo));
      let hecho = false;
      if (obj.esEnlace) {
        if (trab.length < obj.maxDias && total() + obj.peso <= objetivo) {
          const d = [...libres].sort((a, b) => holgura(a, 'n') - holgura(b, 'n')).find(x => cabe12(x, celda('enlace', emp)));
          if (d) { fila[d] = celda('enlace', emp, fila[d]); hecho = true; }
        }
        if (!hecho) break; continue;
      }
      // Primero, un día nuevo (si le quedan días); si no, convertir un turno suelto en partido.
      if (trab.length < obj.maxDias) {
        const opciones = [];
        libres.forEach(d => ['comida', 'noche'].forEach(t => { const s = celda(t, emp); if (cabe12(d, s)) opciones.push([d, t, holgura(d, t === 'comida' ? 'c' : 'n')]); }));
        opciones.sort((a, b) => a[2] - b[2]);
        if (opciones.length) { const [d, t] = opciones[0]; fila[d] = celda(t, emp, fila[d]); hecho = true; }
      }
      if (!hecho && partidoOk) {
        const sueltos = trab.filter(d => ['comida', 'noche', 'apertura'].includes(fila[d].tipo))
          .sort((a, b) => (holgura(a, 'c') + holgura(a, 'n')) - (holgura(b, 'c') + holgura(b, 'n')));
        const d = sueltos.find(x => cabe12(x, celda('partido', emp)));
        if (d) { fila[d] = celda('partido', emp, fila[d]); hecho = true; }
      }
      if (!hecho) break;
    }
  });
  return out;
};

// Punto de entrada ÚNICO para generar un horario (mismos parámetros que siempre).
// 1) Genera con el optimizador nuevo. 2) Lo pasa por el validador de reglas duras. 3) Si algo
// no cuadrase (no debería), usa el generador clásico de antes, con las horas ya normalizadas.
// Así, pase lo que pase, al pulsar "Generar" sale un horario.
const generateSchedule = (employees, specialDays, approvedRequests, config, temporadaConfig, scheduleHistory, isCocina, opciones = {}) => {
  if (!employees || employees.length === 0) return {};
  try {
    const nuevo = generarHorarioOptimo(employees, specialDays, approvedRequests, config, temporadaConfig, scheduleHistory, isCocina, opciones);
    const informe = generarHorarioOptimo.ultimoInforme || {};
    const problemas = validarHorario(nuevo, employees, config, approvedRequests, getWeekDates(), informe.reducciones12h || {});
    if (problemas.length === 0) return nuevo;
    // Antes de tirar a la basura un horario casi perfecto, se intenta dejarlo exacto.
    const ajustado = ajustarTurnosExactos(nuevo, employees, config, approvedRequests, getWeekDates(), informe.reducciones12h || {});
    if (validarHorario(ajustado, employees, config, approvedRequests, getWeekDates(), informe.reducciones12h || {}).length === 0) return ajustado;
    console.warn('Generador nuevo: se usa el de respaldo por estos problemas:', problemas);
  } catch (e) {
    console.error('Generador nuevo: error, se usa el de respaldo', e);
  }
  const semanaActual = getWeekDates();
  const fechas = new Set(Object.values(semanaActual));
  const solicitudesSemana = (approvedRequests || []).filter(r => fechas.has(r.date));
  // Al generador clásico se le pasan las horas YA resueltas (las del modelo de horas), para que
  // su regla de 12 h trabaje con las mismas horas que luego se pintan.
  const resueltos = employees.map(e => {
    const c = horasDeTipo('comida', e, config), n = horasDeTipo('noche', e, config), x = horasDeTipo('enlace', e, config);
    return { ...e, horaEntradaComida: c.horaEntrada, horaSalidaComida: c.horaSalida, horaEntradaNoche: n.horaEntrada, horaSalidaNoche: n.horaSalida, horaEntradaEnlace: x.horaEntrada, horaSalidaEnlace: x.horaSalida };
  });
  const clasico = generateScheduleClasico(resueltos, specialDays, solicitudesSemana, config, temporadaConfig, scheduleHistory, isCocina);
  // El de respaldo no garantiza los turnos exactos: se le pasa siempre la garantía.
  const ajustado = ajustarTurnosExactos(normalizarHorasHorario(clasico, employees, config), employees, config, approvedRequests, semanaActual);
  const restantes = validarHorario(ajustado, employees, config, approvedRequests, semanaActual);
  if (restantes.length) console.warn('Generador de respaldo: quedan avisos tras ajustar', restantes);
  return ajustado;
};

// BURBUJA DE DÍA ESPECIAL (10 oct 2026): comprueba, con el PROPIO generador y la plantilla real de
// esta semana, si lo que pide el Encargado para un día (personas solo almuerzo, solo cena y de día
// completo) se puede cumplir — y si no, hasta dónde se llega y por qué. No inventa nada ni usa una
// fórmula aparte: llama muchas veces a generateSchedule con ese día pedido y mide lo que sale.
// Es una función PURA de cálculo en el móvil: no escribe nada en Firestore ni en ningún sitio.
// Para que el resultado sea estable (que no cambie solo cada vez que se redibuja la pantalla) se
// sustituye Math.random por uno con semilla fija durante la prueba, y se restaura siempre al final.
const evaluarDiaEspecial = ({ day, comida, noche, completo, getEmpleados, specialDays, approvedRequests, config, temporadaConfig, scheduleHistory, isCocina }) => {
  // Con el generador nuevo (casi determinista en cobertura) bastan pocas pruebas, y en modo
  // rápido, para que la burbuja responda al instante.
  const PRUEBAS = 4;
  const aleatorioOriginal = Math.random;
  let estado = 20261010;
  Math.random = () => {
    estado = (estado + 0x6D2B79F5) | 0;
    let t = Math.imul(estado ^ (estado >>> 15), 1 | estado);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  try {
    const empleados = getEmpleados();
    const semana = getWeekDates();
    const partidoOk = config.permiteTurnoPartido !== false;
    const pide = {
      comida: Math.max(0, Math.round(comida || 0)),
      noche: Math.max(0, Math.round(noche || 0)),
      completo: partidoOk ? Math.max(0, Math.round(completo || 0)) : 0,
    };
    pide.comida = Math.max(pide.comida, pide.completo);
    pide.noche = Math.max(pide.noche, pide.completo);

    // Quién NO puede trabajar ese día, y por qué (nunca se toca: baja, vacaciones, libre aprobado, día fijo libre).
    const noDisponibles = [];
    empleados.forEach(emp => {
      let motivo = null;
      if (estaDeBajaEse(emp, semana[day])) motivo = 'de baja';
      else if (estaAusenteEse(emp, semana[day])) motivo = 'de vacaciones';
      else if (diasSolicitudesSemana(emp, approvedRequests, semana).has(day)) motivo = 'con el día libre aprobado';
      else if ((emp.diasFijosLibres || []).includes(day)) motivo = 'con ese día libre fijo';
      if (motivo) noDisponibles.push({ nombre: emp.name, motivo });
    });
    const personasEseDia = empleados.length - noDisponibles.length;
    const cerrado = (config.diasCierreSemana || []).includes(day);

    const spPide = { ...(specialDays || {}), [day]: 'Festivo' };
    const cfgBase = { ...config, personasPorDia: { ...(config.personasPorDia || {}) } };
    delete cfgBase.personasPorDia[day];
    const spBase = { ...(specialDays || {}) };
    delete spBase[day];

    const minC = temporadaConfig.minComida ?? temporadaConfig.minPersonas ?? 4;
    const minN = temporadaConfig.minNoche ?? temporadaConfig.minPersonas ?? 4;
    const cuentaDia = (sch, d) => {
      let c = 0, n = 0, p = 0;
      empleados.forEach(e => {
        const t = sch[e.id]?.[d]?.tipo;
        if (t === 'partido') { c++; n++; p++; }
        else if (t === 'comida' || t === 'apertura') c++;
        else if (t === 'noche' || t === 'enlace') n++;
      });
      return { c, n, p };
    };
    const faltaOtrosDias = (sch) => DAYS.filter(d => d !== day && !(config.diasCierreSemana || []).includes(d)).reduce((sum, d) => {
      const { c, n } = cuentaDia(sch, d);
      return sum + Math.max(0, minC - c) + Math.max(0, minN - n);
    }, 0);

    // Una "medición": genera varios horarios con ESTE pedido y cuenta cuántos lo cumplen.
    const medir = (pet, pruebas) => {
      estado = 20261017; // misma semilla en cada medición: el resultado depende solo del pedido (así lo que se ofrece como "máximo" sale igual al volver a comprobarlo)
      const cfgPide = { ...config, personasPorDia: { ...(config.personasPorDia || {}), [day]: { comida: pet.comida, noche: pet.noche, completo: pet.completo } } };
      const resultados = [];
      for (let i = 0; i < pruebas; i++) {
        const sch = generateSchedule(empleados, spPide, approvedRequests, cfgPide, temporadaConfig, scheduleHistory, isCocina, { rapido: true });
        const { c, n, p } = cuentaDia(sch, day);
        const falta = Math.max(0, pet.comida - c) + Math.max(0, pet.noche - n) + Math.max(0, pet.completo - p);
        resultados.push({ c, n, p, falta, otros: faltaOtrosDias(sch) });
      }
      const cumplen = resultados.filter(r => r.falta === 0);
      const otrosOrd = cumplen.map(r => r.otros).sort((a, b) => a - b);
      const impacto = otrosOrd.length > 0 ? Math.max(0, otrosOrd[Math.floor(otrosOrd.length / 2)] - baseOtros) : 0;
      const percentil = (arr) => [...arr].sort((a, b) => a - b)[Math.floor(arr.length * 0.2)];
      const logrado = {
        comida: Math.min(pet.comida, percentil(resultados.map(r => r.c))),
        noche: Math.min(pet.noche, percentil(resultados.map(r => r.n))),
        completo: Math.min(pet.completo, percentil(resultados.map(r => r.p))),
      };
      logrado.completo = Math.min(logrado.completo, logrado.comida, logrado.noche);
      return { exitos: cumplen.length, impacto, pruebas, logrado };
    };

    // Referencia: cómo queda el resto de la semana SIN pedir nada especial ese día (mediana).
    const otrosBase = [];
    for (let i = 0; i < PRUEBAS; i++) {
      otrosBase.push(faltaOtrosDias(generateSchedule(empleados, spBase, approvedRequests, cfgBase, temporadaConfig, scheduleHistory, isCocina, { rapido: true })));
    }
    const baseOtros = [...otrosBase].sort((a, b) => a - b)[Math.floor(PRUEBAS / 2)];

    const m = medir(pide, PRUEBAS);
    const cumple = m.exitos / PRUEBAS >= 0.75;
    // "Otros días más justos": cuántos puestos más de lo normal se quedan sin cubrir, de media,
    // en el resto de la semana por haber pedido este día. 1,5 o más ya se avisa.
    const afectaOtros = cumple && m.impacto >= 1.5;

    // Si no se llega, se busca lo MÁXIMO que sí se consigue casi siempre: se parte de lo pedido y se
    // va quitando UNA persona cada vez del turno que más se queda corto, midiendo de nuevo, hasta
    // que sale bien en casi todas las pruebas. Así nunca se ofrece menos de lo que de verdad cabe.
    let ajuste = null;
    if (!cumple && !cerrado) {
      let pet = { ...pide };
      for (let it = 0; it < 16; it++) {
        const mm = medir(pet, PRUEBAS);
        if (mm.exitos / PRUEBAS >= 0.75) { ajuste = { ...pet }; break; }
        const def = {
          completo: pet.completo - mm.logrado.completo,
          comida: pet.comida - mm.logrado.comida,
          noche: pet.noche - mm.logrado.noche,
        };
        const orden = ['comida', 'noche', 'completo'].sort((x, y) => def[y] - def[x]);
        const quitar = orden.find(k => k === 'completo' ? pet.completo > 0 : pet[k] > Math.max(1, pet.completo));
        if (!quitar) break;
        pet = { ...pet, [quitar]: pet[quitar] - 1 };
        if (quitar === 'completo') { /* los totales se mantienen: pasan a ser refuerzos de un solo turno */ }
      }
      if (!ajuste) ajuste = { comida: Math.min(pide.comida, minC), noche: Math.min(pide.noche, minN), completo: 0 };
      ajuste.completo = partidoOk ? ajuste.completo : 0;
    }

    // Sugerencias concretas, solo si no se llega.
    const sugerencias = [];
    const techo = capacidadMaximaTurnos(config);
    const nombresNoDisp = new Set(noDisponibles.map(x => x.nombre));
    if ((!cumple || afectaOtros) && !cerrado) {
      const conMargen = empleados
        .filter(e => !nombresNoDisp.has(e.name) && !e.hacesEnlace && (e.turnosSemanales || 5) < techo)
        .sort((a, b) => (a.turnosSemanales || 5) - (b.turnosSemanales || 5)).slice(0, 3);
      if (conMargen.length > 0) {
        sugerencias.push(`Subir los turnos contratados de ${conMargen.map(e => `${e.name} (ahora ${e.turnosSemanales || 5})`).join(', ')}: es la forma más directa de ganar gente ese día sin dejar otros días cojos.`);
      }
      if (pide.completo > 0 && ajuste && ajuste.completo < pide.completo) {
        sugerencias.push('Pedir menos personas de día completo y cubrir con refuerzos solo de almuerzo o solo de cena: el día completo gasta dos turnos de cada persona.');
      }
      if (pide.completo === 0 && partidoOk && personasEseDia < Math.max(pide.comida, pide.noche)) {
        sugerencias.push('Pasar a algunas personas a día completo: con la misma gente cubren almuerzo y cena a la vez.');
      }
      const movibles = noDisponibles.filter(x => x.motivo !== 'de baja').slice(0, 3);
      if (!cumple && movibles.length > 0) {
        sugerencias.push(`Si fuera posible, mover las vacaciones o los días libres de ${movibles.map(x => x.nombre).join(', ')}: ese día no están disponibles.`);
      }
    }

    return {
      pide, ajuste, noDisponibles, personasEseDia, cerrado, sugerencias,
      pruebas: PRUEBAS, exitos: m.exitos, impacto: m.impacto,
      cumple, afectaOtros,
    };
  } finally {
    Math.random = aleatorioOriginal;
  }
};

const FloatingTables = () => {
  const tables = [
    { top: '8%', left: '6%', rotate: '-8deg', delay: '0s', size: 0.85 },
    { top: '62%', left: '4%', rotate: '6deg', delay: '0.8s', size: 0.7 },
    { top: '15%', left: '80%', rotate: '10deg', delay: '0.4s', size: 0.9 },
    { top: '70%', left: '78%', rotate: '-6deg', delay: '1.2s', size: 0.75 },
    { top: '40%', left: '88%', rotate: '4deg', delay: '0.6s', size: 0.6 },
    { top: '85%', left: '40%', rotate: '-4deg', delay: '1s', size: 0.65 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <style>{`
        @keyframes floatTable {
          0%, 100% { transform: translateY(0px) rotate(var(--rot)); }
          50% { transform: translateY(-18px) rotate(var(--rot)); }
        }
      `}</style>
      {tables.map((t, i) => (
        <div key={i} style={{
          position: 'absolute', top: t.top, left: t.left,
          '--rot': t.rotate,
          animation: `floatTable 5s ease-in-out infinite`,
          animationDelay: t.delay,
          transform: `scale(${t.size}) rotate(${t.rotate})`,
          opacity: 0.16,
        }}>
          <svg width="110" height="80" viewBox="0 0 110 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="106" height="76" rx="8" fill="white" stroke="white" strokeWidth="2"/>
            <line x1="2" y1="22" x2="108" y2="22" stroke="#A78BFA" strokeWidth="2"/>
            <line x1="38" y1="2" x2="38" y2="78" stroke="#A78BFA" strokeWidth="1.5"/>
            <line x1="74" y1="2" x2="74" y2="78" stroke="#A78BFA" strokeWidth="1.5"/>
            <line x1="2" y1="42" x2="108" y2="42" stroke="#C4B5FD" strokeWidth="1"/>
            <line x1="2" y1="60" x2="108" y2="60" stroke="#C4B5FD" strokeWidth="1"/>
            <rect x="8" y="8" width="22" height="9" rx="2" fill="#A78BFA"/>
            <rect x="44" y="28" width="22" height="9" rx="2" fill="#C4B5FD"/>
            <rect x="80" y="46" width="22" height="9" rx="2" fill="#C4B5FD"/>
          </svg>
        </div>
      ))}
    </div>
  );
};

// Mascota reutilizable para las pantallas de arranque (splash y bienvenida). Mismo trazado que
// el botón flotante del chat (calcado de mascota-horia.png), pero como pieza aparte para poder
// montarle animaciones propias (saludo, aparición) sin tocar el botón que ya funciona bien.
// "expresion": 'normal' (sonrisa tranquila, por defecto), 'extranada' (boca plana, cara de duda)
// o 'contenta' (sonrisa abierta). "mostrarDedos": añade los deditos naranjas sobre el rombo.
const HoriaMascotSvg = ({ size = 140, expresion = 'normal', mostrarDedos = false, saludando = false, className = '', style = {} }) => (
  <svg viewBox="260 230 740 740" width={size} height={size} className={className} style={{ display: 'block', ...style }}>
    <defs>
      <linearGradient id="horiaHeadGradIntro" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7C4FE9" />
        <stop offset="100%" stopColor="#5732C9" />
      </linearGradient>
    </defs>
    <circle cx="627.1" cy="597.4" r="307" fill="#E8E0F8" />
    <path d="M804.5,899.7 A350.5,350.5 0 1 1 885.1,360.2" fill="none" stroke="#5F19FD" strokeWidth="19" strokeLinecap="round" />
    <path d="M973.2,652.8 A350.5,350.5 0 0 1 924.7,782.6" fill="none" stroke="#5F19FD" strokeWidth="19" strokeLinecap="round" />
    <path d="M802.0,420.0 L777.0,403.0 L750.0,393.0 L723.0,389.0 L701.0,390.0 L492.0,418.0 L462.0,430.0 L436.0,450.0 L421.0,468.0 L410.0,488.0 L402.0,514.0 L400.0,539.0 L412.0,716.0 L425.0,749.0 L441.0,770.0 L464.0,789.0 L492.0,802.0 L516.0,807.0 L541.0,807.0 L545.0,816.0 L544.0,823.0 L548.0,827.0 L566.0,822.0 L591.0,808.0 L644.0,808.0 L676.0,824.0 L689.0,827.0 L693.0,824.0 L695.0,810.0 L758.0,808.0 L782.0,797.0 L830.0,751.0 L847.0,722.0 L856.0,692.0 L858.0,666.0 L846.0,492.0 L829.0,451.0 Z M350.0,797.0 L350.0,828.0 L384.0,864.0 L429.0,899.0 L464.0,899.0 L434.0,881.0 L402.0,856.0 L372.0,826.0 Z M457.0,300.0 L424.0,300.0 L392.0,325.0 L350.0,369.0 L350.0,398.0 L369.0,373.0 L398.0,343.0 L436.0,312.0 Z" fill="url(#horiaHeadGradIntro)" />
    <path d="M825.0,449.0 L817.0,450.0 L817.0,455.0 L811.0,460.0 L813.0,474.0 L819.0,477.0 L815.0,480.0 L810.0,657.0 L802.0,692.0 L789.0,716.0 L766.0,742.0 L746.0,757.0 L725.0,767.0 L697.0,772.0 L698.0,740.0 L693.0,734.0 L678.0,737.0 L652.0,751.0 L650.0,810.0 L670.0,821.0 L691.0,826.0 L695.0,809.0 L747.0,810.0 L777.0,800.0 L828.0,753.0 L846.0,723.0 L857.0,683.0 L849.0,513.0 L835.0,463.0 L830.0,464.0 L832.0,457.0 Z" fill="#432BBA" />
    <path d="M590.0,742.0 L528.0,771.0 L590.0,800.0 Z" fill="#5B38E3" />
    <path d="M670.0,742.0 L732.0,771.0 L670.0,800.0 Z" fill="#3D25B2" />
    <rect x="590" y="742" width="80" height="58" rx="14" fill="#351C9C" />
    <g>
      <path d="M538.0,521.0 L515.0,519.0 L502.0,522.0 L486.0,531.0 L475.0,542.0 L467.0,556.0 L463.0,569.0 L463.0,587.0 L466.0,599.0 L474.0,613.0 L486.0,624.0 L496.0,629.0 L508.0,632.0 L521.0,632.0 L538.0,627.0 L558.0,612.0 L570.0,593.0 L573.0,583.0 L574.0,566.0 L569.0,548.0 L556.0,531.0 Z" fill="#FFFFFF" />
      <path d="M526.0,541.0 L514.0,545.0 L505.0,552.0 L498.0,564.0 L496.0,578.0 L499.0,589.0 L507.0,600.0 L519.0,606.0 L531.0,607.0 L544.0,602.0 L553.0,594.0 L560.0,579.0 L560.0,565.0 L555.0,554.0 L547.0,546.0 L538.0,542.0 Z" fill="#332F54" />
      <path d="M547.0,554.0 L539.0,554.0 L535.0,557.0 L533.0,561.0 L533.0,566.0 L536.0,571.0 L543.0,573.0 L551.0,568.0 L552.0,560.0 L550.0,556.0 Z" fill="#FFFFFF" />
    </g>
    <g>
      <path d="M719.0,503.0 L697.0,505.0 L678.0,515.0 L663.0,532.0 L656.0,549.0 L654.0,568.0 L661.0,591.0 L678.0,609.0 L695.0,616.0 L717.0,616.0 L739.0,606.0 L756.0,588.0 L764.0,568.0 L764.0,544.0 L755.0,524.0 L738.0,509.0 Z" fill="#FFFFFF" />
      <path d="M715.0,526.0 L700.0,533.0 L691.0,544.0 L688.0,553.0 L688.0,568.0 L693.0,579.0 L701.0,587.0 L714.0,592.0 L722.0,592.0 L734.0,588.0 L744.0,580.0 L751.0,567.0 L752.0,552.0 L747.0,539.0 L742.0,533.0 L731.0,527.0 Z" fill="#332F54" />
      <path d="M732.0,538.0 L726.0,542.0 L724.0,549.0 L725.0,553.0 L729.0,557.0 L735.0,558.0 L742.0,553.0 L744.0,547.0 L739.0,539.0 Z" fill="#FFFFFF" />
    </g>
    {expresion === 'extranada' && (
      <path d="M560,672 Q618,662 676,672" fill="none" stroke="#FFFFFF" strokeWidth="9" strokeLinecap="round" />
    )}
    {expresion === 'normal' && (
      <path d="M695.0,663.0 L692.0,659.0 L686.0,658.0 L653.0,674.0 L618.0,682.0 L584.0,682.0 L550.0,674.0 L544.0,678.0 L543.0,686.0 L552.0,694.0 L577.0,702.0 L592.0,704.0 L622.0,703.0 L646.0,698.0 L676.0,685.0 L693.0,673.0 Z" fill="#FFFFFF" />
    )}
    {expresion === 'contenta' && (
      <g>
        <ellipse cx="618" cy="688" rx="52" ry="27" fill="#332F54" />
        <path d="M568,672 Q618,648 668,672 Q618,668 568,672 Z" fill="#FFFFFF" />
      </g>
    )}
    <g className={saludando ? 'horia-saludo-brazo' : ''}>
      {saludando && (
        <animateTransform attributeName="transform" type="rotate"
          values="0 871 846; -24 871 846; 22 871 846; -24 871 846; 22 871 846; 0 871 846"
          keyTimes="0; 0.2; 0.4; 0.6; 0.8; 1" dur="1.7s" repeatCount="4" fill="freeze" />
      )}
      <path d="M879.0,775.0 L865.0,774.0 L859.0,777.0 L805.0,831.0 L800.0,839.0 L799.0,849.0 L802.0,857.0 L835.0,890.0 L912.0,811.0 L916.0,810.0 L924.0,818.0 Z" fill="#FDA935" />
      <path d="M915.0,810.0 L836.0,891.0 L860.0,914.0 L872.0,917.0 L884.0,913.0 L939.0,858.0 L943.0,846.0 L941.0,837.0 Z" fill="#FF9335" />
      {mostrarDedos && (
        <g>
          <ellipse cx="869" cy="770" rx="15" ry="32" fill="#FDA935" transform="rotate(178 869 770)" style={{ animation: 'horiaDedosIntro 0.4s ease-out both', animationDelay: '0s' }} />
          <ellipse cx="887" cy="779" rx="15" ry="34" fill="#FDA935" transform="rotate(-166 887 779)" style={{ animation: 'horiaDedosIntro 0.4s ease-out both', animationDelay: '0.06s' }} />
          <ellipse cx="905" cy="790" rx="15" ry="34" fill="#FDA935" transform="rotate(-149 905 790)" style={{ animation: 'horiaDedosIntro 0.4s ease-out both', animationDelay: '0.12s' }} />
          <ellipse cx="924" cy="802" rx="14" ry="30" fill="#FDA935" transform="rotate(-130 924 802)" style={{ animation: 'horiaDedosIntro 0.4s ease-out both', animationDelay: '0.18s' }} />
        </g>
      )}
    </g>
  </svg>
);

// Secuencia de la bienvenida (21 sep 2026, ajustada tras el primer intento: Fran pidió que
// sonría en vez de poner cara de extrañado, dedos más grandes y bien pegados al rombo (antes
// quedaban sueltos, "cutre"), y que dure más). El rombo saluda de un lado a otro TODO el rato
// que está en pantalla (no solo un instante), y a los 3,2s le salen los deditos y se pone aún
// más contento, mientras sigue saludando.
const IntroSaludo = () => {
  const [fase, setFase] = useState('saludando');
  useEffect(() => {
    const t1 = setTimeout(() => setFase('contenta'), 3200);
    return () => clearTimeout(t1);
  }, []);
  return (
    <div style={{ position: 'relative', zIndex: 1, marginBottom: '4px' }}>
      <HoriaMascotSvg
        size={150}
        expresion={fase === 'contenta' ? 'contenta' : 'normal'}
        mostrarDedos={fase === 'contenta'}
        saludando
      />
    </div>
  );
};

const IntroScreen = ({ onComplete, department, onSwitchDepartment, firebaseUser, userRole, blockedMessage, onVolverInicio }) => {
  const isCocina = department === 'cocina';
  const IL = {
    jefeRoleName: isCocina ? 'Jefe de Cocina' : 'Encargado',
    jefeRoleNamePlural: isCocina ? 'jefes de cocina' : 'encargados',
    empleadoRoleName: isCocina ? 'Cocinero' : 'Empleado',
    jefeButtonLabel: isCocina ? '👨‍🍳 Jefe de Cocina' : '🏠 Encargado',
    empleadoButtonLabel: isCocina ? '🍳 Cocinero' : '👤 Empleado',
  };
  const [slide, setSlide] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [saliendoAlInicio, setSaliendoAlInicio] = useState(false);
  const [form, setForm] = useState({
    restaurantName: '',
    encargados: [''],
    aperturaHora: '12:30',
    personasApertura: 2,
    comidaStart: '13:30', comidaEnd: '17:00',
    nocheStart: '20:30', nocheEnd: '00:30',
    // El cierre por defecto tiene que ser una de las horas que se pueden elegir en la lista (antes
    // era '17:00', que no estaba en ella: se veía otra hora pero se guardaba esa).
    horarioSeguidoStart: '12:30', horarioSeguidoEnd: '00:30',
    duracionSeguido: DURACION_TURNO_SEGUIDO_DEFECTO_MIN,
    permiteTurnoPartido: true,
    diasCierreSemana: [],
    diasDescansoSemana: 2,
    accessCode: '',
  });

  const nextSlide = useCallback((target) => {
    setOpacity(0);
    setTimeout(() => {
      setSlide(s => target !== undefined ? target : s + 1);
      setOpacity(1);
    }, 600);
  }, []);

  // Trae la configuración ya puesta por Sala, para que Cocina no tenga que repetirla — es lo
  // mismo que hacía antes handleJefeChoice, movido aquí porque ya no hace falta esperar a que
  // alguien pulse un botón de "soy Encargado": el rol ya se sabe de la pantalla anterior.
  const heredarConfigDeSala = async () => {
    try {
      if (firebaseUser) {
        const salaSnap = await getDoc(doc(db, 'restaurantes', firebaseUser.uid));
        // Se repara antes de heredar (16 oct 2026): si Sala tenía el fallo de los turnos de más de
        // 8 h en turno seguido, Cocina hereda ya las horas buenas.
        const sc = salaSnap.exists() ? repararHorarioSeguido(salaSnap.data()?.config) : null;
        if (sc) {
          setForm(f => ({
            ...f,
            restaurantName: sc.restaurantName || f.restaurantName,
            aperturaHora: sc.aperturaHora || f.aperturaHora,
            personasApertura: sc.personasApertura ?? f.personasApertura,
            comidaStart: sc.comidaStart || f.comidaStart,
            comidaEnd: sc.comidaEnd || f.comidaEnd,
            nocheStart: sc.nocheStart || f.nocheStart,
            nocheEnd: sc.nocheEnd || f.nocheEnd,
            horarioSeguidoStart: sc.comidaStart || f.horarioSeguidoStart,
            // En turno seguido el CIERRE del local es la salida de la cena si la configuración ya
            // lleva duración de turno (todas las nuevas y las reparadas); en las antiguas sin ella
            // sigue siendo la salida de la comida, como se guardaba entonces.
            horarioSeguidoEnd: ((sc.permiteTurnoPartido === false && sc.duracionTurnoMin > 0) ? sc.nocheEnd : sc.comidaEnd) || f.horarioSeguidoEnd,
            duracionSeguido: (sc.permiteTurnoPartido === false && sc.duracionTurnoMin >= 120) ? sc.duracionTurnoMin : f.duracionSeguido,
            permiteTurnoPartido: sc.permiteTurnoPartido !== false,
            diasCierreSemana: sc.diasCierreSemana || [],
            diasDescansoSemana: sc.diasDescansoSemana || 2,
            accessCode: sc.accessCode || f.accessCode,
          }));
        }
      }
    } catch (_) {}
  };

  // A dónde ir después de la bienvenida — ANTES esto lo decidía la persona pulsando "Encargado"
  // o "Empleado" en una pantalla propia ("¿Quién eres?"). Esa pantalla ya no existe (21 sep
  // 2026, a petición de Fran: era redundante, el rol ya se eligió en la pantalla de fuera, antes
  // de crear la cuenta) — así que ahora se decide sola, mirando el rol que ya se sabe.
  const avanzarTrasBienvenida = async () => {
    if (userRole !== 'jefe') {
      // Empleado que entró con el código antes de que su Encargado terminara de configurar:
      // no hay nada que rellenar aquí, solo avisarle de que espere.
      onComplete(null, 'empleado');
      return;
    }
    if (isCocina) {
      await heredarConfigDeSala();
      nextSlide(3); // Cocina se salta el nombre del restaurante (ya lo tiene Sala) y las
                     // preguntas de horario (también heredadas) — solo pide su propio nombre.
    } else {
      nextSlide(2);
    }
  };

  useEffect(() => {
    if (slide === 0) {
      const timer = setTimeout(nextSlide, 3200);
      return () => clearTimeout(timer);
    }
    if (slide === 1) {
      const timer = setTimeout(avanzarTrasBienvenida, 7000);
      return () => clearTimeout(timer);
    }
  }, [slide]); // eslint-disable-line react-hooks/exhaustive-deps

  const addEncargado = () => setForm(f => ({ ...f, encargados: [...f.encargados, ''] }));
  const removeEncargado = (i) => setForm(f => ({ ...f, encargados: f.encargados.filter((_, idx) => idx !== i) }));
  const updateEncargado = (i, val) => setForm(f => ({ ...f, encargados: f.encargados.map((e, idx) => idx === i ? val : e) }));

  const canNext = () => {
    if (slide === 2) return form.restaurantName.trim().length > 0;
    if (slide === 3) return (form.encargados[0] || '').trim().length > 0;
    // La slide 4 (otros encargados, plural) es opcional a propósito: no todo el mundo tiene
    // más de un encargado, y obligar a rellenarla sería fricción sin sentido.
    return true;
  };

  const handleFinish = () => {
    // Turno seguido (16 oct 2026): apertura→cierre es el horario del LOCAL; los turnos de cada
    // persona se calculan dentro de él con la duración que elija el Encargado (8 h por defecto). Antes se
    // guardaba el horario entero como si fuera el almuerzo, y salían turnos de 12 h.
    const seguido = form.permiteTurnoPartido ? null : horarioTurnoSeguido(form.horarioSeguidoStart, form.horarioSeguidoEnd, form.duracionSeguido);
    const comidaStart = seguido ? seguido.comidaStart : (form.permiteTurnoPartido ? form.comidaStart : form.horarioSeguidoStart);
    const comidaEnd = seguido ? seguido.comidaEnd : (form.permiteTurnoPartido ? form.comidaEnd : form.horarioSeguidoEnd);

    onComplete({
      restaurantName: form.restaurantName,
      encargados: form.encargados.filter(e => e.trim()),
      aperturaHora: form.aperturaHora,
      personasApertura: form.personasApertura,
      comidaStart,
      comidaEnd,
      nocheStart: seguido ? seguido.nocheStart : form.nocheStart,
      nocheEnd: seguido ? seguido.nocheEnd : form.nocheEnd,
      ...(seguido ? { duracionTurnoMin: seguido.duracionTurnoMin } : {}),
      ...(seguido && seguido.enlaceHoraInicio ? { enlaceHoraInicio: seguido.enlaceHoraInicio } : {}),
      horarioSeguidoStart: form.horarioSeguidoStart,
      horarioSeguidoEnd: form.horarioSeguidoEnd,
      permiteTurnoPartido: form.permiteTurnoPartido,
      diasCierreSemana: form.diasCierreSemana || [],
      diasDescansoSemana: form.diasDescansoSemana || 2,
      accessCode: form.accessCode || undefined,
      configurado: true,
    }, 'jefe');
  };

  const gradBg = { minHeight: '100vh', background: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', position: 'relative', overflow: 'hidden' };
  const trans = { transition: 'opacity 0.6s ease', opacity, position: 'relative', zIndex: 1 };
  // Botón "← Atrás" compartido por las slides de preguntas, para poder corregir un error sin
  // tener que cerrar la app y volver a entrar (21 sep 2026, a petición de Fran).
  const BotonAtras = ({ a }) => (
    <button onClick={() => nextSlide(a)}
      style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 2, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: C.white, borderRadius: '10px', padding: '8px 14px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
      ← Atrás
    </button>
  );

  // "← Atrás" de la PRIMERA pregunta (4 oct 2026, a petición de Fran). Antes volvía a la pantalla
  // de bienvenida, que a los 7 segundos avanza sola otra vez a la misma pregunta: no había salida.
  // Esto es un caso real y recurrente: tras borrar un perfil en Firebase y volver a entrar, la app
  // te trata como una cuenta nueva y te lleva a estas pantallas. Ahora "Atrás" cierra esa sesión
  // y te deja en el inicio, donde se elige "Soy Encargado" o "Soy Empleado" (lo mismo que hace
  // "Salir"; no borra nada de la cuenta ni de Firestore).
  const BotonVolverInicio = () => (
    <button onClick={() => {
        if (saliendoAlInicio) return;
        setSaliendoAlInicio(true);
        Promise.resolve(onVolverInicio()).catch(() => {}).finally(() => setSaliendoAlInicio(false));
      }}
      disabled={saliendoAlInicio}
      style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 2, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: C.white, borderRadius: '10px', padding: '8px 14px', fontSize: '13px', fontWeight: '700', cursor: saliendoAlInicio ? 'default' : 'pointer', opacity: saliendoAlInicio ? 0.6 : 1 }}>
      {saliendoAlInicio ? 'Saliendo…' : '← Atrás'}
    </button>
  );

  const SelectTime = ({ label, value, options, field }) => (
    <div>
      <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
      <select value={value} onChange={e => setForm(f => ({...f, [field]: e.target.value}))}
        style={{ padding: '10px 12px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: C.primaryDark, background: 'rgba(255,255,255,0.92)', cursor: 'pointer', outline: 'none', width: '110px' }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  // BLINDAJE (29 sep 2026): si el padre ha detectado que este restaurante/Cocina ya tenía datos
  // reales guardados, NUNCA se muestra el asistente — se muestra este aviso en su lugar, para
  // que quede claro que no se ha hecho nada (y no un botón que parece no responder). Va AQUÍ,
  // después de todos los hooks de arriba (useState/useCallback/useEffect) y no antes: un
  // `return` antes de un hook rompe las reglas de React (hooks condicionales) y no compila.
  if (blockedMessage) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', borderRadius: '20px', padding: '40px', maxWidth: '440px', width: '100%', textAlign: 'center', border: '2px solid rgba(255,255,255,0.2)' }}>
        <div style={{ fontSize: '52px', marginBottom: '16px' }}><Ico n="shield" /></div>
        <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#fff', margin: '0 0 12px' }}>No se ha tocado nada</h2>
        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.5 }}>{blockedMessage}</p>
      </div>
    </div>
  );

  if (slide === 0) return (
    <div style={gradBg}>
      <style>{`
        @keyframes horiaIntroCaeIA {
          0%   { transform: translateY(-260px); opacity: 0; }
          55%  { transform: translateY(14px); opacity: 1; }
          72%  { transform: translateY(-6px); }
          86%  { transform: translateY(3px); }
          100% { transform: translateY(0); }
        }
        @keyframes horiaIntroMascotaPop {
          0%   { transform: scale(0.3); opacity: 0; }
          70%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div style={{ ...trans, textAlign: 'center' }}>
        {/* El nombre tiene dos letras más que antes: el tamaño se adapta al ancho de la pantalla
            (máximo 100px, como antes) para que no se salga en móviles estrechos. */}
        <div style={{ fontSize: 'clamp(52px, 19vw, 100px)', fontWeight: '900', letterSpacing: '-4px', lineHeight: 1, textShadow: '0 4px 30px rgba(0,0,0,0.2)', fontFamily: "'Nunito', sans-serif", whiteSpace: 'nowrap' }}>
          <span style={{ color: C.white }}>HORI</span>
          <span style={{ color: C.accentLight, display: 'inline-block', animation: 'horiaIntroCaeIA 1.1s cubic-bezier(0.22,1.6,0.36,1) both' }}>DIA</span>
        </div>
        <div style={{ marginTop: '14px', fontSize: '17px', fontWeight: '700', color: 'rgba(255,255,255,0.85)', letterSpacing: '0.2px' }}>
          Tus horarios día a día
        </div>
        <div style={{ width: '60px', height: '3px', background: 'rgba(255,255,255,0.4)', borderRadius: '2px', margin: '20px auto 26px' }} />
        {/* El asistente aparece de golpe justo después de que "DIA" termine de caer. */}
        <div style={{ animation: 'horiaIntroMascotaPop 0.5s cubic-bezier(0.34,1.56,0.64,1) both', animationDelay: '1.3s', opacity: 0 }}>
          <HoriaMascotSvg size={96} />
        </div>
      </div>
    </div>
  );

  if (slide === 1) return (
    <div style={gradBg}>
      <FloatingTables />
      <style>{`
        @keyframes horiaDedosIntro { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <IntroSaludo />
      <div style={{ ...trans, textAlign: 'center', maxWidth: '480px', marginTop: '18px' }}>
        <h2 style={{ fontSize: '30px', fontWeight: '800', color: C.white, margin: '0 0 16px', lineHeight: 1.2 }}>
          Te damos la bienvenida a la app de gestión de horarios
        </h2>
        <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
          Vamos a configurar todo en un momento...
        </p>
      </div>
    </div>
  );

  if (slide === 2) return (
    <div style={gradBg}>
      {onVolverInicio ? <BotonVolverInicio /> : <BotonAtras a={1} />}
      <div style={{ ...trans, maxWidth: '500px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 12px' }}>Pregunta 1 de 4</p>
          <h2 style={{ fontSize: '28px', fontWeight: '800', color: C.white, margin: 0, lineHeight: 1.3 }}>
            ¿Cómo se llama tu restaurante?
          </h2>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', borderRadius: '16px', padding: '28px', border: '2px solid rgba(255,255,255,0.2)' }}>
          <input
            type="text"
            autoFocus
            maxLength={60}
            value={form.restaurantName}
            onChange={e => setForm(f => ({...f, restaurantName: e.target.value}))}
            onKeyPress={e => e.key === 'Enter' && canNext() && nextSlide()}
            placeholder="Ej: Bar La Esquina, Restaurante Dúo..."
            style={{ width: '100%', padding: '16px 18px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '12px', fontSize: '18px', fontWeight: '600', color: C.primaryDark, background: 'rgba(255,255,255,0.92)', outline: 'none', boxSizing: 'border-box' }}
          />
          <button onClick={() => nextSlide()} disabled={!canNext()}
            style={{ ...btn('primary', 'lg'), marginTop: '16px', width: '100%', justifyContent: 'center', background: canNext() ? C.white : 'rgba(255,255,255,0.3)', color: canNext() ? C.primary : 'rgba(255,255,255,0.5)', border: 'none', fontSize: '16px' }}>
            Continuar →
          </button>
        </div>
      </div>
    </div>
  );

  if (slide === 3) return (
    <div style={gradBg}>
      {!isCocina ? <BotonAtras a={2} /> : (onVolverInicio ? <BotonVolverInicio /> : null)}
      <div style={{ ...trans, maxWidth: '500px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 12px' }}>{isCocina ? 'Última pregunta' : 'Pregunta 2 de 4'}</p>
          <h2 style={{ fontSize: '28px', fontWeight: '800', color: C.white, margin: '0 0 8px', lineHeight: 1.3 }}>
            ¿Cómo te llamas?
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.65)', margin: 0 }}>
            {isCocina ? 'Los horarios del local ya los configuró el encargado de Sala' : `Como ${IL.jefeRoleName.toLowerCase()} de este restaurante`}
          </p>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', borderRadius: '16px', padding: '28px', border: '2px solid rgba(255,255,255,0.2)' }}>
          <input
            type="text"
            autoFocus
            maxLength={40}
            value={form.encargados[0] || ''}
            onChange={e => updateEncargado(0, e.target.value)}
            onKeyPress={e => e.key === 'Enter' && canNext() && (isCocina ? handleFinish() : nextSlide())}
            placeholder={isCocina ? IL.jefeRoleName : 'Gerente'}
            style={{ width: '100%', padding: '16px 18px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '12px', fontSize: '18px', fontWeight: '600', color: C.primaryDark, background: 'rgba(255,255,255,0.92)', outline: 'none', boxSizing: 'border-box' }}
          />
          <button onClick={() => { if (isCocina) { handleFinish(); } else { nextSlide(); } }} disabled={!canNext()}
            style={{ ...btn('primary', 'lg'), marginTop: '16px', width: '100%', justifyContent: 'center', background: canNext() ? C.white : 'rgba(255,255,255,0.3)', color: canNext() ? C.primary : 'rgba(255,255,255,0.5)', border: 'none', fontSize: '16px' }}>
            {isCocina ? '✓ ¡Todo listo, empezamos!' : 'Continuar →'}
          </button>
        </div>
      </div>
    </div>
  );

  if (slide === 4) return (
    <div style={gradBg}>
      <BotonAtras a={3} />
      <div style={{ ...trans, maxWidth: '500px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 12px' }}>Pregunta 3 de 4</p>
          <h2 style={{ fontSize: '28px', fontWeight: '800', color: C.white, margin: '0 0 8px', lineHeight: 1.3 }}>
            ¿Cómo se llaman los encargados?
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.65)', margin: 0 }}>
            Si hay más aparte de ti — puedes dejarlo en blanco si eres el único
          </p>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', borderRadius: '16px', padding: '28px', border: '2px solid rgba(255,255,255,0.2)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
            {form.encargados.slice(1).map((enc, idx) => {
              const i = idx + 1;
              return (
                <div key={i} style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    autoFocus={i === 1}
                    maxLength={40}
                    value={enc}
                    onChange={e => updateEncargado(i, e.target.value)}
                    placeholder="Encargado"
                    style={{ flex: 1, padding: '13px 16px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '10px', fontSize: '16px', fontWeight: '600', color: C.primaryDark, background: 'rgba(255,255,255,0.92)', outline: 'none' }}
                  />
                  <button onClick={() => removeEncargado(i)}
                    style={{ width: '46px', height: '46px', borderRadius: '10px', border: '2px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', color: C.white, fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              );
            })}
          </div>
          <button onClick={addEncargado}
            style={{ ...btn('ghost', 'sm'), color: 'rgba(255,255,255,0.8)', border: '1px dashed rgba(255,255,255,0.4)', width: '100%', justifyContent: 'center', marginBottom: '14px' }}>
            + Añadir otro encargado
          </button>
          <button onClick={() => nextSlide()}
            style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', background: C.white, color: C.primary, border: 'none', fontSize: '16px' }}>
            Continuar →
          </button>
        </div>
      </div>
    </div>
  );

  if (slide === 5) return (
    <div style={gradBg}>
      <BotonAtras a={4} />
      <div style={{ ...trans, maxWidth: '580px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <p style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 12px' }}>Pregunta 4 de 4</p>
          <h2 style={{ fontSize: '26px', fontWeight: '800', color: C.white, margin: '0 0 6px', lineHeight: 1.3 }}>
            ¿Qué horario tiene{' '}
            <span style={{ color: '#E9D5FF' }}>{form.restaurantName}</span>?
          </h2>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
            La IA generará los horarios basándose en esto
          </p>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', borderRadius: '16px', padding: '28px', border: '2px solid rgba(255,255,255,0.2)' }}>

          <div style={{ marginBottom: '22px' }}>
            <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.white, fontSize: '15px' }}><Ico n="shuffle" /> ¿Tu restaurante tiene turno seguido?</p>
            <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
              Turno seguido = un único horario corrido, sin partir el día. Si vuestro local cierra entre comida y noche y la gente entra y sale dos veces, elige "No".
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setForm(f => ({...f, permiteTurnoPartido: false}))}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: !form.permiteTurnoPartido ? 'none' : '2px solid rgba(255,255,255,0.3)', background: !form.permiteTurnoPartido ? C.white : 'rgba(255,255,255,0.1)', color: !form.permiteTurnoPartido ? C.primary : C.white, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                ✓ Sí, turno seguido
              </button>
              <button onClick={() => setForm(f => ({...f, permiteTurnoPartido: true}))}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: form.permiteTurnoPartido ? 'none' : '2px solid rgba(255,255,255,0.3)', background: form.permiteTurnoPartido ? C.white : 'rgba(255,255,255,0.1)', color: form.permiteTurnoPartido ? C.primary : C.white, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                No, turno partido
              </button>
            </div>
          </div>

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.15)', margin: '0 0 22px' }} />

          <div style={{ marginBottom: '22px' }}>
            <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.white, fontSize: '15px' }}><Ico n="sunrise" /> Apertura del local</p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <SelectTime label="Hora de apertura" value={form.aperturaHora} options={ENTRADAS_COMIDA} field="aperturaHora" />
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Personas en apertura</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button onClick={() => setForm(f => ({...f, personasApertura: Math.max(1, f.personasApertura - 1)}))}
                    style={{ width: '36px', height: '36px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', color: C.white, fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: '26px', fontWeight: '900', color: C.white, minWidth: '32px', textAlign: 'center' }}>{form.personasApertura}</span>
                  <button onClick={() => setForm(f => ({...f, personasApertura: f.personasApertura + 1}))}
                    style={{ width: '36px', height: '36px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', color: C.white, fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.15)', margin: '0 0 22px' }} />

          <div style={{ opacity: !form.permiteTurnoPartido ? 0.35 : 1, pointerEvents: !form.permiteTurnoPartido ? 'none' : 'auto', transition: 'opacity 0.3s ease' }}>
            <div style={{ marginBottom: '22px' }}>
              <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.white, fontSize: '15px' }}><Ico n="utensils" /> Servicio de comida</p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <SelectTime label="Entrada" value={form.comidaStart} options={ENTRADAS_COMIDA} field="comidaStart" />
                <span style={{ color: 'rgba(255,255,255,0.5)', paddingBottom: '10px', fontSize: '18px' }}>→</span>
                <SelectTime label="Salida" value={form.comidaEnd} options={SALIDAS_COMIDA} field="comidaEnd" />
                <div style={{ paddingBottom: '10px' }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', color: C.white, padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>
                    {calcHours(form.comidaStart, form.comidaEnd)}h
                  </span>
                </div>
              </div>
            </div>

            <div style={{ height: '1px', background: 'rgba(255,255,255,0.15)', margin: '0 0 22px' }} />

            <div style={{ marginBottom: '8px' }}>
              <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.white, fontSize: '15px' }}><Ico n="moon" /> Servicio de noche</p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <SelectTime label="Entrada" value={form.nocheStart} options={ENTRADAS_NOCHE} field="nocheStart" />
                <span style={{ color: 'rgba(255,255,255,0.5)', paddingBottom: '10px', fontSize: '18px' }}>→</span>
                <SelectTime label="Cierre" value={form.nocheEnd} options={SALIDAS_NOCHE} field="nocheEnd" />
                <div style={{ paddingBottom: '10px' }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', color: C.white, padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>
                    {calcHours(form.nocheStart, form.nocheEnd)}h
                  </span>
                </div>
              </div>
            </div>
          </div>

          {!form.permiteTurnoPartido && (
            <div style={{ marginTop: '22px', paddingTop: '22px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.white, fontSize: '15px' }}><Ico n="clock" /> Horario de tu restaurante</p>
              <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                Es el horario del local, desde la apertura hasta el cierre. Horidia reparte los turnos de cada persona dentro de este horario, con la duración de turno que elijas aquí abajo.
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <SelectTime label="Apertura" value={form.horarioSeguidoStart} options={ENTRADAS_COMIDA} field="horarioSeguidoStart" />
                <span style={{ color: 'rgba(255,255,255,0.5)', paddingBottom: '10px', fontSize: '18px' }}>→</span>
                <SelectTime label="Cierre" value={form.horarioSeguidoEnd} options={SALIDAS_NOCHE} field="horarioSeguidoEnd" />
                <div style={{ paddingBottom: '10px' }}>
                  <span style={{ background: 'rgba(255,255,255,0.25)', color: C.white, padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>
                    {calcHours(form.horarioSeguidoStart, form.horarioSeguidoEnd)}h
                  </span>
                </div>
              </div>
              <div style={{ marginTop: '14px' }}>
                <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>¿Cuánto dura el turno de cada persona?</p>
                <select aria-label="Duración del turno" value={form.duracionSeguido} onChange={e => setForm(f => ({ ...f, duracionSeguido: parseInt(e.target.value, 10) }))}
                  style={{ padding: '10px 12px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: C.primaryDark, background: 'rgba(255,255,255,0.92)', cursor: 'pointer', outline: 'none', width: '110px' }}>
                  {OPCIONES_DURACION_SEGUIDO.map(m => <option key={m} value={m}>{formatoDuracion(m)}</option>)}
                </select>
              </div>
            </div>
          )}

          <button onClick={handleFinish}
            style={{ width: '100%', padding: '16px', background: C.white, color: C.primary, border: 'none', borderRadius: '12px', fontSize: '17px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', marginTop: '24px' }}>
            ✓ ¡Todo listo, empezamos!
          </button>
        </div>

        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '14px' }}>
          Puedes modificar estos horarios en cualquier momento desde <Ico n="gear" /> Configuración
        </p>
      </div>
    </div>
  );

  return null;
};

const PLANS_FEATURES_TODO_INCLUIDO = [
  { texto: 'Creación y gestión de horarios', destacado: true },
  { texto: 'Chat interno y notificaciones al móvil' },
  { texto: 'Departamento de Cocina, con horario independiente', destacado: true },
  { texto: 'Varios locales, cada uno con su propio encargado' },
  { texto: 'Sistema de solicitudes para cubrir turnos' },
  { texto: 'Vista del Propietario con historial en tiempo real' },
  { texto: 'Espacio para el logo de tu negocio', destacado: true },
  { texto: 'Exportar el horario a PDF' },
  { texto: 'Regeneraciones de horario ilimitadas', destacado: true },
  { texto: '14 días de prueba gratis', destacado: true },
];

const PlansScreen = ({ onBack }) => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [numEmpleados, setNumEmpleados] = useState(10);
  const contactEmail = 'horidiapp@gmail.com';

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(contactEmail).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const precioTotal = calcularPrecioMensual(numEmpleados);
  const enTramoBajo = numEmpleados < UMBRAL_EMPLEADOS_PRECIO_ALTO;
  // ARREGLO (4 oct 2026): antes se comparaba numEmpleados × precioUnitario, pero precioUnitario
  // sale del total que YA lleva el mínimo aplicado, así que la cuenta nunca bajaba del mínimo y la
  // nota del mínimo no salía jamás (un bar de 4 personas veía "20 €" junto a "4,50 € por
  // empleado" sin explicación). Ahora se compara el precio real antes de aplicar el mínimo.
  const precioSinMinimo = subtotalPorTramos(numEmpleados);
  const aplicaMinimo = precioSinMinimo < PRECIO_MINIMO_MENSUAL;

  return (
  <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)`, padding: '24px 20px' }}>
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        ← Volver
      </button>

      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ width: '48px', height: '48px', margin: '0 auto 10px' }}>
          <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '100%', height: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.25))' }} />
        </div>
        <h1 style={{ fontSize: '24px', fontWeight: '800', color: C.white, margin: '0 0 4px' }}>Planes y precios</h1>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', margin: 0 }}>Un solo plan, todo incluido. Pagas por las personas que tienes.</p>
      </div>

      <div style={{ background: C.white, borderRadius: '20px', padding: '24px 22px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Calculadora — compacta */}
        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '1px' }}>
            ¿Cuántas personas trabajan en tu negocio?
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '10px' }}>
            <button onClick={() => setNumEmpleados(n => Math.max(1, n - 1))}
              style={{ width: '38px', height: '38px', borderRadius: '50%', border: `2px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontSize: '18px', fontWeight: '800', cursor: 'pointer', flexShrink: 0 }}>
              −
            </button>
            <div style={{ minWidth: '80px' }}>
              <span style={{ fontSize: '32px', fontWeight: '900', color: C.text }}>{numEmpleados}</span>
              <p style={{ margin: 0, fontSize: '11px', color: C.textMuted, fontWeight: '600' }}>{numEmpleados === 1 ? 'empleado' : 'empleados'}</p>
            </div>
            <button onClick={() => setNumEmpleados(n => Math.min(300, n + 1))}
              style={{ width: '38px', height: '38px', borderRadius: '50%', border: 'none', background: C.primary, color: C.white, fontSize: '18px', fontWeight: '800', cursor: 'pointer', flexShrink: 0 }}>
              +
            </button>
          </div>

          <input type="range" min={1} max={400} value={Math.min(numEmpleados, 400)} onChange={e => setNumEmpleados(Number(e.target.value))}
            style={{ display: 'block', width: '100%', maxWidth: '340px', margin: '0 auto', accentColor: C.primary }} />
        </div>

        {/* Precio calculado — compacto, con aviso del mínimo cuando aplica */}
        <div style={{ background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '14px', padding: '18px', textAlign: 'center', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '8px' }}>
            <span style={{ fontSize: '38px', fontWeight: '900', color: C.primary }}>{precioTotal.toLocaleString('es-ES', { minimumFractionDigits: precioTotal % 1 === 0 ? 0 : 2 })}€</span>
            <span style={{ fontSize: '14px', color: C.textMuted, fontWeight: '600' }}>/mes</span>
            <span style={{ fontSize: '14px', color: C.textMuted, fontWeight: '600' }}>+ IVA</span>
          </div>
          <div style={{ margin: '6px 0 0', fontSize: '12px', color: C.textMuted }}>
            {aplicaMinimo ? (
              <span>Mínimo de {PRECIO_MINIMO_MENSUAL}€/mes + IVA por cliente</span>
            ) : (
              <>
                <DesglosePrecioBadges />
              </>
            )}
          </div>
        </div>

        {/* CTA principal, visible sin desplazar — antes vivía debajo de las 10 ventajas y había
            que bajar mucho para verla; ahora va justo después del precio. */}
        <button style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: C.primary, color: C.white, fontWeight: '800', fontSize: '15px', cursor: 'pointer', marginBottom: '8px' }}>
          Empezar prueba gratis de 14 días
        </button>
        <button onClick={() => setShowContactModal(true)}
          style={{ display: 'block', width: '100%', background: 'transparent', border: 'none', textAlign: 'center', fontSize: '12px', color: C.primary, textDecoration: 'underline', cursor: 'pointer', padding: 0, marginBottom: '16px' }}>
          ¿Cadena grande o negocio con muchos locales? Hablemos
        </button>

        {/* Recuadro de referidos — plano a propósito, sin degradados ni brillos, para que no
            compita visualmente con el precio ni con el botón de prueba gratis. */}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', textAlign: 'center', marginBottom: '18px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: C.text }}>
            <Ico n="gift" /> <strong>Invita a otro restaurante</strong> y llévate <strong>15 días gratis</strong> cuando pague su primer mes
          </p>
        </div>

        {/* Funciones — lista condensada en vez de 10 cuadraditos grandes, para que quepa todo
            sin tener que desplazar demasiado. */}
        <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>
          Todo incluido
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', columnGap: '10px', rowGap: '6px', marginBottom: '4px' }}>
          {PLANS_FEATURES_TODO_INCLUIDO.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
              <span style={{ color: C.success, fontWeight: '800', fontSize: '12px', flexShrink: 0, marginTop: '2px' }}>✓</span>
              <span style={{ fontSize: '13px', color: C.text, lineHeight: 1.4 }}>{f.texto}</span>
            </div>
          ))}
        </div>
      </div>

      <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.65)', fontSize: '11px', marginTop: '20px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
        Horidia es una herramienta de planificación de turnos. El cumplimiento del registro de jornada (Real Decreto-ley 8/2019) es responsabilidad exclusiva del cliente.
      </p>
    </div>

    {showContactModal && (
      <div onClick={() => setShowContactModal(false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
        <div onClick={e => e.stopPropagation()}
          style={{ background: C.white, borderRadius: '18px', padding: '32px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}><Ico n="building" /></div>
          <h3 style={{ margin: '0 0 10px', fontSize: '19px', fontWeight: '800', color: C.text }}>
            ¿Cadena con muchos locales o mucha plantilla?
          </h3>
          <p style={{ margin: '0 0 22px', fontSize: '14px', color: C.textMuted, lineHeight: 1.55 }}>
            Si gestionas un negocio grande, hablamos directamente y ajustamos el precio a tu caso real, sin sorpresas.
          </p>
          <div style={{ background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '12px', padding: '16px', marginBottom: '18px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Escríbenos a</p>
            <p style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: C.primaryDark }}>{contactEmail}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleCopyEmail}
              style={{ flex: 1, padding: '12px', borderRadius: '10px', border: `2px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
              {copied ? '✓ Copiado' : conIconos('📋 Copiar email')}
            </button>
            <a href={`mailto:${contactEmail}?subject=Consulta%20plan%20Horidia`}
              style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: C.primary, color: C.white, fontWeight: '700', fontSize: '14px', cursor: 'pointer', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ico n="mail" /> Abrir correo
            </a>
          </div>
          <button onClick={() => setShowContactModal(false)}
            style={{ marginTop: '16px', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Cerrar
          </button>
        </div>
      </div>
    )}
  </div>
  );
};

// =====================================================================================
// CONDICIONES DE CONTRATACIÓN (5 oct 2026) — versión que se muestra al crear la cuenta.
// GENERADO a partir del documento Word «Horia-Condiciones-de-Contratacion.docx» (incluido su
// Anexo I, el contrato de encargado del tratamiento): hay que mantenerlos IGUALES. Cuando se
// cambie el Word, hay que cambiar aquí el texto y subir CONDICIONES_VERSION, para que cada
// cuenta nueva guarde qué versión aceptó. Las tablas de datos del cliente y de firmas del Word
// no se muestran aquí: al aceptar en la app, los datos del cliente son los de su cuenta.
// =====================================================================================
const CONDICIONES_VERSION = '2026-10-29b'; // 29 oct 2026: código de Dueño (13); y después PIN personal y privados (2, 12, 13, Anexo I 5 y 7)
const CONDITIONS_SECTIONS = [
  {
    title: "Datos del Cliente",
    body: "A cumplimentar por el establecimiento que contrata el Servicio. Estos datos son necesarios para poder emitir las facturas correspondientes, por lo que deben coincidir con los que figuran en su documentación fiscal. Si el Cliente acepta estas condiciones en la propia Aplicación, los datos del Cliente son los de la cuenta con la que las acepta y los datos fiscales que facilite para recibir la factura, que el prestador le pedirá por correo electrónico con el primer pago."
  },
  {
    title: "1. Quién presta el servicio",
    body: "El servicio Horidia (en adelante, «Horidia» o «el Servicio») es prestado por Francisco José Carmona Senra, con NIF 29540157S, con domicilio en C/ Parque del Torcal de Antequera nº 7, 41015 Sevilla, y correo de contacto horidiapp@gmail.com.\n\nEstas Condiciones de Contratación regulan la relación comercial entre el prestador del Servicio y el negocio que lo contrata (en adelante, «el Cliente»). Se complementan con el Aviso Legal y la Política de Privacidad publicados en la Aplicación, que forman parte inseparable de este acuerdo.\n\nEl Servicio se dirige exclusivamente a empresas y profesionales que lo contratan para su actividad empresarial o profesional, y no a personas consumidoras. Quien acepta estas condiciones declara ser mayor de edad y tener capacidad y facultades suficientes para obligar al Cliente."
  },
  {
    title: "2. Qué es y qué no es Horidia",
    body: "Horidia es una aplicación web para organizar los turnos de trabajo de un establecimiento de hostelería. Permite crear y publicar horarios, gestionar solicitudes de días libres, comunicar al equipo mediante chat interno (grupal y privado; los mensajes privados solo pueden usarse entrando con la clave personal de cada persona) y enviar avisos automáticos al personal. Incluye también un asistente conversacional («Horito») que utiliza un servicio de inteligencia artificial de terceros únicamente para interpretar las preguntas del personal sobre sus turnos y días libres, y un registro de los gastos del negocio.\n\nHoridia NO es un sistema de registro de jornada a efectos del artículo 34.9 del Estatuto de los Trabajadores. No registra la hora real de entrada y salida de las personas trabajadoras y, por tanto, no sustituye ni cubre la obligación legal de llevar un registro horario. El cumplimiento de esa obligación corresponde exclusivamente al Cliente.\n\nLos horarios que Horidia muestra reflejan la planificación introducida por el Cliente, no las horas efectivamente trabajadas.\n\nHoridia incluye un generador automático de horarios que propone un reparto de turnos según los criterios que configura el Cliente. Es una herramienta de apoyo: corresponde al Cliente revisar el horario antes de publicarlo y comprobar que respeta el convenio colectivo, la jornada y los descansos legales y las limitaciones aplicables a menores. El asistente conversacional puede cometer errores de interpretación; en caso de duda, prevalece el horario publicado en la Aplicación.\n\nEl Servicio se presta mediante una licencia de uso no exclusiva e intransferible, durante la vigencia de la suscripción. El código, el diseño y la marca de Horidia siguen siendo propiedad del prestador, conforme al Aviso Legal."
  },
  {
    title: "3. Precio y forma de pago",
    body: "• Menos de 6 personas en plantilla: 4,70 € por persona al mes\n• 6 o más personas en plantilla: 5,30 € por persona al mes\n• Cuota mínima: 15,00 € al mes por establecimiento\n\nEl precio mensual del establecimiento resulta de multiplicar el número de personas en plantilla por la tarifa que corresponda al tamaño de la plantilla, que se aplica a todas las personas: 4,70 € por persona si hay menos de seis y 5,30 € por persona si hay seis o más. Por ejemplo, con 5 personas el precio es 5 × 4,70 € = 23,50 € al mes, y con 8 personas es 8 × 5,30 € = 42,40 € al mes. Si el importe resultante es inferior a 15 €, se cobra la cuota mínima de 15 € al mes, de modo que hasta 3 personas se abona siempre esa cuota mínima.\n\nSala y Cocina de un mismo establecimiento forman una única suscripción: el número de personas se cuenta en conjunto entre ambos departamentos, y el periodo de prueba y la activación del Servicio se aplican a ambos a la vez. Si el Cliente no utiliza Cocina, solo se cuentan las personas dadas de alta en Sala.\n\nEl Servicio incluye siempre, con independencia del número de personas en plantilla, todas sus funcionalidades: gestión de turnos, chat interno, solicitudes de días libres y notificaciones, departamento de Cocina, gestión de varios locales, asistente conversacional, registro de gastos, carta y plano del local, y logotipo propio del negocio.\n\nEl Servicio tiene un límite técnico, no comercial, de setecientas (700) personas en plantilla y cincuenta (50) locales por establecimiento.\n\nLos precios indicados son mensuales, por persona en plantilla, y NO INCLUYEN EL IVA. A todos ellos se les añadirá el Impuesto sobre el Valor Añadido al tipo vigente en cada momento, que se reflejará desglosado en la factura correspondiente.\n\nEl número de personas en plantilla que se toma para calcular cada cuota es el registrado en el último cierre automático, que se realiza a las 00:00 (hora peninsular española) del día 1 de cada mes con la plantilla activa en ese instante (Sala y Cocina en conjunto) y, para la primera cuota, el existente en el momento de activar la cuenta. Las altas o bajas de personal posteriores no modifican la cuota ya calculada y se aplican, en su caso, a la siguiente. Cada cuota cubre un periodo completo de un mes desde la fecha de activación o de renovación, sin prorrateos.\n\nForma de pago. El Cliente abona cada cuota por Bizum o por transferencia bancaria, a los datos que el prestador le facilita en la propia Aplicación o por correo electrónico, indicando en el concepto el nombre del establecimiento. Una vez recibido el pago, el prestador activa o mantiene el acceso al Servicio.\n\nFacturas. El prestador emitirá una factura por cada pago recibido y la enviará al correo electrónico del Cliente, que debe mantener actualizados sus datos fiscales.\n\nOtros medios de pago. Si el prestador incorpora en el futuro otros medios de pago (por ejemplo, el pago con tarjeta mediante una pasarela de pago), lo comunicará al Cliente con antelación y actualizará, en su caso, estas condiciones conforme a la cláusula 15. Si ese medio implicara cobros periódicos automáticos, antes de confirmar el pago se informará al Cliente, de forma clara y junto al botón de pago, del importe, de la periodicidad de la renovación automática y de cómo cancelarla, y la cancelación podrá hacerse en cualquier momento desde la propia Aplicación."
  },
  {
    title: "4. Periodo de prueba",
    body: "El Cliente dispone de un periodo de prueba gratuito de catorce (14) días naturales desde el alta, durante el cual puede usar el Servicio sin coste alguno y darse de baja sin que se genere ningún cargo.\n\nPara empezar la prueba no se solicitan datos de pago ni se realiza ningún cargo automático. Al finalizar el periodo de prueba, el acceso a la Aplicación queda suspendido, para todo el personal del establecimiento, hasta que el Cliente abone la primera cuota y el prestador active la cuenta. Si transcurridos treinta (30) días naturales desde el fin de la prueba el Cliente no ha activado el Servicio, se procederá con los datos conforme a la cláusula 11."
  },
  {
    title: "5. Duración y renovación",
    body: "La suscripción no tiene permanencia mínima. Se contrata por periodos mensuales: cada periodo se abona por adelantado y la suscripción continúa mientras el Cliente siga abonando la cuota y no se dé de baja. No existe cobro automático: el prestador no carga ningún importe que el Cliente no haya pagado por sí mismo.\n\nEl prestador podrá enviar al Cliente, por correo electrónico, un recordatorio con el importe y la fecha de la siguiente cuota. La falta de recepción de ese recordatorio no exime del pago."
  },
  {
    title: "6. Cómo darse de baja",
    body: "El Cliente puede darse de baja en cualquier momento desde la propia Aplicación, en el apartado «Mi plan», sin necesidad de justificar el motivo ni de comunicarlo por ningún otro medio.\n\nLa baja surte efecto al finalizar el periodo mensual ya abonado. Durante el tiempo restante de ese periodo, el Cliente conserva el acceso completo al Servicio.\n\nNo se aplica preaviso: basta con solicitar la baja antes de abonar la siguiente cuota. Mientras dure el periodo ya abonado, el Cliente puede anular la baja desde la propia Aplicación y seguir con el Servicio."
  },
  {
    title: "7. Devoluciones",
    body: "No procede la devolución del importe correspondiente al periodo mensual ya iniciado. El Cliente mantiene el acceso al Servicio durante todo el periodo que ha abonado, por lo que recibe íntegramente aquello que ha pagado.\n\nEl periodo de prueba gratuito de catorce días existe precisamente para que el Cliente pueda valorar el Servicio antes de realizar ningún pago.\n\nSe hace constar que el derecho de desistimiento previsto en la normativa de consumidores y usuarios no resulta de aplicación a esta contratación, por celebrarse entre empresarios en el marco de su actividad profesional."
  },
  {
    title: "8. Variación del número de empleados",
    body: "El precio del Servicio se ajusta de forma automática según el número de personas en plantilla del establecimiento (Sala y Cocina en conjunto), conforme a la tabla de la cláusula 3. El Cliente puede consultar en cualquier momento, desde el apartado «Mi plan» de la Aplicación, el precio correspondiente a su plantilla actual.\n\nEl aumento o la disminución de personal a lo largo de un mismo periodo de facturación no genera, por sí solo, un nuevo cobro dentro de ese periodo: el precio se recalcula para el periodo siguiente."
  },
  {
    title: "9. Modificación de precios",
    body: "El prestador podrá modificar los precios del Servicio, por ejemplo como consecuencia de la incorporación de nuevas funcionalidades.\n\nCualquier modificación de precio se comunicará al Cliente con una antelación mínima de treinta (30) días respecto de la fecha del siguiente cobro. Si el Cliente no acepta el nuevo precio, podrá darse de baja antes de esa fecha sin coste alguno."
  },
  {
    title: "10. Impago",
    body: "Si el pago de una cuota no se recibe en la fecha prevista, el prestador se lo comunicará al Cliente por correo electrónico. Si transcurridos diez (10) días naturales desde esa comunicación el pago sigue sin recibirse, el prestador podrá suspender el acceso al Servicio hasta su regularización.\n\nLa suspensión por impago no implica la eliminación inmediata de los datos, que se regirá por lo previsto en la cláusula siguiente."
  },
  {
    title: "11. Qué ocurre con los datos al terminar",
    body: "Una vez causada la baja por el Cliente —o si el periodo de prueba termina sin que se active el Servicio, o si el acceso permanece suspendido por falta de pago durante treinta (30) días naturales—, se procederá de la siguiente forma:\n\n• El acceso a la Aplicación se desactiva al finalizar el periodo abonado (o, en los demás casos, desde el fin de la prueba o desde la suspensión).\n• Los datos del Cliente y de su personal se conservan durante treinta (30) días naturales desde la fecha efectiva de la baja, del fin de la prueba o de la suspensión. Durante ese plazo, el Cliente puede solicitar su recuperación si se reincorpora al Servicio o si necesita obtener una copia.\n• Transcurridos esos treinta días, los datos se eliminan o anonimizan de los sistemas activos del prestador, sin perjuicio de las copias de seguridad que puedan mantenerse durante el periodo técnicamente necesario para su sobrescritura.\n\nSe exceptúa de lo anterior la documentación que el prestador deba conservar por imperativo legal, como la información fiscal y contable derivada de la facturación, que se conservará durante los plazos legalmente exigidos."
  },
  {
    title: "12. Disponibilidad y responsabilidad",
    body: "El prestador pondrá los medios razonables a su alcance para que el Servicio esté disponible de forma continuada, pero no garantiza su disponibilidad ininterrumpida ni la ausencia total de errores.\n\nEl Servicio se apoya en infraestructura de terceros y puede verse afectado por incidencias ajenas al prestador, así como por tareas de mantenimiento, que se procurará realizar en horarios de baja actividad.\n\nEn la medida en que la ley lo permita, la responsabilidad total del prestador frente al Cliente por cualquier causa derivada del Servicio queda limitada al importe efectivamente abonado por el Cliente en los doce (12) meses anteriores al hecho que la origine, y no alcanza al lucro cesante ni a los daños indirectos. Esta limitación no se aplica en caso de dolo ni cuando la ley no permita limitar la responsabilidad.\n\nLas credenciales de acceso (contraseñas, PIN y código de Dueño) son responsabilidad de quien las custodia conforme a la cláusula 13. En la medida en que la ley lo permita, el prestador no responde de los accesos a la Aplicación ni del conocimiento de la información del negocio o de sus conversaciones que se produzcan con credenciales que el Cliente o su personal hayan facilitado, perdido o revelado, incluso a otras personas del mismo establecimiento, ni de los perjuicios que se deriven de ello. Todo ello sin perjuicio de las obligaciones de seguridad que corresponden al prestador como encargado del tratamiento.\n\nHoridia es una herramienta de apoyo a la planificación y no efectúa ningún control de legalidad, ni previo ni posterior, sobre los horarios, turnos, duraciones de jornada, descansos u otras condiciones laborales que el Encargado/a configure, genere o publique en la App. Todos los parámetros de planificación —incluida la duración de los turnos, que es configurable por el restaurante— son determinados exclusivamente por este, en su condición de empleador, que es el único responsable de que la planificación respete el Estatuto de los Trabajadores (en particular, los límites de jornada ordinaria y de jornada diaria, los descansos mínimos entre jornadas y semanales y el régimen aplicable a personas menores de edad), el convenio colectivo aplicable y cualquier otra normativa laboral o de prevención de riesgos laborales. Los valores por defecto, los límites, los avisos y las sugerencias de la App tienen carácter meramente orientativo y no constituyen asesoramiento jurídico ni laboral. En consecuencia, y en la máxima medida permitida por la ley, Francisco José Carmona Senra no asume responsabilidad alguna por las sanciones administrativas, reclamaciones, recargos, indemnizaciones u otros perjuicios que pudieran derivarse del incumplimiento de dicha normativa por parte del restaurante. El Cliente se obliga a mantener indemne al prestador frente a las reclamaciones de terceros, incluidas las de su propio personal y las de la Administración, que traigan causa de un incumplimiento de la normativa laboral imputable al Cliente."
  },
  {
    title: "13. Obligaciones del Cliente",
    body: "• Facilitar datos veraces y mantenerlos actualizados.\n• Custodiar las credenciales de acceso y velar por que su personal haga lo propio con sus claves personales, que son individuales e intransferibles.\n• Gestionar los PIN de acceso de su personal. Cada PIN lo genera el Encargado/a, o la persona gestora que este designe, desde la Aplicación, y debe entregarse en persona a quien corresponda. La persona trabajadora puede cambiarlo después, desde «Mi PIN», por otro que solo conozca ella; hasta que lo haga, el PIN también lo conoce quien lo generó. El PIN es personal, confidencial e intransferible: mientras no se ha entregado, su custodia corresponde al Cliente y, una vez entregado, a la persona trabajadora. El prestador no conoce ni puede consultar ningún PIN. Si un PIN se pierde o se sospecha que lo conoce otra persona, el Encargado/a puede generar uno nuevo, con lo que el anterior deja de funcionar al momento.\n• Usar el Servicio conforme a la legislación vigente y, en particular, cumplir por su cuenta las obligaciones laborales y de registro horario que le correspondan.\n• Revisar el horario propuesto por el generador automático antes de publicarlo y comprobar que respeta el convenio colectivo, la jornada, los descansos y las limitaciones legales aplicables a menores.\n• Informar a su personal del uso de la Aplicación y del tratamiento de sus datos, en los términos previstos en el Anexo I.\n• No introducir en los campos de ausencias o bajas ningún diagnóstico ni información médica: basta con registrar la ausencia como «Baja», sin detallar el motivo.\n• No introducir datos personales innecesarios en los campos de texto libre (por ejemplo, el concepto de un gasto) y subir únicamente imágenes sobre las que tenga derechos y en las que no aparezcan personas identificables.\n• Custodiar el código de Dueño, que el prestador envía únicamente al correo electrónico de la cuenta del Encargado/a y que permite crear una cuenta de consulta con acceso a la información del negocio, incluidos los gastos. Es confidencial: el Cliente decide a quién se lo facilita y responde del acceso de las personas a las que lo entregue. Si sospecha que lo conoce alguien que no debe, puede solicitar uno nuevo desde «Mi plan», con lo que el anterior deja de servir para crear cuentas nuevas.\n\nContenidos del Cliente. El Cliente conserva la titularidad de los textos e imágenes que introduce o sube a la Aplicación (por ejemplo, su logotipo, su carta, el plano del local o los mensajes del chat) y concede al prestador únicamente el permiso necesario para almacenarlos y mostrarlos dentro del Servicio. El Cliente garantiza que dispone de los derechos necesarios sobre ellos y responderá de cualquier reclamación de terceros derivada de su incumplimiento. Si el prestador tiene conocimiento efectivo de que un contenido es ilícito o lesiona derechos de terceros, podrá retirarlo o impedir el acceso a él, informando al Cliente, conforme al artículo 16 de la LSSI-CE."
  },
  {
    title: "14. Protección de datos",
    body: "En la prestación del Servicio, el prestador trata datos personales del personal del Cliente por cuenta de este. A efectos del Reglamento (UE) 2016/679, el Cliente actúa como responsable del tratamiento y el prestador como encargado del tratamiento.\n\nLas condiciones de ese tratamiento se recogen en el Anexo I de este documento, que forma parte inseparable del mismo y que ambas partes suscriben con la firma de las presentes condiciones.\n\nRespecto de los datos de contacto y de facturación del propio Cliente, el prestador actúa como responsable del tratamiento, conforme a la Política de Privacidad publicada en la Aplicación.\n\nComunicaciones. El prestador enviará al Cliente las comunicaciones necesarias para la prestación del Servicio, como avisos de la cuenta, facturas, recordatorios de pago o cambios en estas condiciones. Solo le enviará comunicaciones comerciales sobre productos o servicios propios similares a los contratados, conforme al artículo 21.2 de la LSSI-CE, y cada una de ellas incluirá un procedimiento sencillo y gratuito para dejar de recibirlas."
  },
  {
    title: "15. Modificación de estas condiciones",
    body: "El prestador podrá actualizar estas Condiciones de Contratación. Cualquier modificación sustancial se comunicará al Cliente con una antelación mínima de quince días, pudiendo este darse de baja sin coste si no las acepta."
  },
  {
    title: "16. Legislación aplicable y jurisdicción",
    body: "Estas condiciones se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales que resulten competentes conforme a la normativa vigente."
  },
  {
    title: "Anexo I · Contrato de encargado del tratamiento (artículo 28 del RGPD)",
    body: "Este Anexo forma parte inseparable de las Condiciones de Contratación y regula cómo trata el prestador los datos del personal del Cliente por cuenta de este."
  },
  {
    title: "Anexo I · 1. Partes",
    body: "RESPONSABLE DEL TRATAMIENTO: el Cliente, cuyos datos identificativos constan en el encabezado de las Condiciones de Contratación firmadas o, si las acepta en la Aplicación, son los de su cuenta y los datos de facturación que facilite al prestador.\n\nENCARGADO DEL TRATAMIENTO: Francisco José Carmona Senra — 29540157S — C/ Parque del Torcal de Antequera nº 7, 41015 Sevilla"
  },
  {
    title: "Anexo I · 2. Objeto",
    body: "El encargado tratará, por cuenta del responsable, los datos personales necesarios para prestar el servicio Horidia de planificación y comunicación de turnos de trabajo."
  },
  {
    title: "Anexo I · 3. Duración",
    body: "Este acuerdo estará vigente mientras lo esté la suscripción al Servicio. Una vez finalizada, el encargado procederá conforme a lo previsto en la cláusula 8."
  },
  {
    title: "Anexo I · 4. Naturaleza y finalidad del tratamiento",
    body: "Alojamiento, conservación, consulta, organización y comunicación interna de la información de planificación laboral del establecimiento (incluida la interpretación de las preguntas del personal mediante el asistente conversacional), con la única finalidad de prestar el Servicio contratado."
  },
  {
    title: "Anexo I · 5. Tipo de datos personales",
    body: "• Nombre de las personas trabajadoras.\n• Turnos asignados, días libres, vacaciones y ausencias.\n• Clave de acceso personal (PIN), almacenada mediante un algoritmo de resumen criptográfico irreversible (hash), y en su caso el identificador de la credencial biométrica del dispositivo (la huella o el rostro nunca salen del dispositivo ni son accesibles para el prestador).\n• Contenido de los mensajes intercambiados en el chat interno.\n• Vinculación técnica de cada cuenta de PIN con la ficha de la persona trabajadora, para que solo ella pueda acceder a sus mensajes privados.\n• Identificador técnico del dispositivo para el envío de notificaciones.\n• Texto de las preguntas formuladas al asistente conversacional, que puede incluir nombres de personas trabajadoras y referencias a sus turnos. El dictado por voz lo procesa el navegador del dispositivo: el audio no llega al encargado.\n• Datos técnicos de funcionamiento (dirección IP, identificadores técnicos de la cuenta y de la solicitud), conservados en los registros de la infraestructura durante un periodo limitado."
  },
  {
    title: "Anexo I · 6. Categorías de interesados",
    body: "Personal empleado del establecimiento del Cliente, así como las personas designadas por este como encargadas, gestoras o propietarias con acceso a la Aplicación.\n\nNo se tratan categorías especiales de datos de las previstas en el artículo 9 del RGPD. El responsable se compromete a no introducir en la Aplicación diagnósticos médicos ni información sobre el estado de salud de su personal."
  },
  {
    title: "Anexo I · 7. Obligaciones del encargado",
    body: "El encargado del tratamiento se obliga a:\n\n• Tratar los datos personales únicamente siguiendo instrucciones documentadas del responsable, incluidas las transferencias internacionales.\n• Garantizar que las personas autorizadas a tratar los datos se han comprometido a respetar la confidencialidad, o están sujetas a una obligación de confidencialidad de naturaleza legal.\n• Aplicar las medidas técnicas y organizativas apropiadas conforme al artículo 32 del RGPD, entre ellas el cifrado de las comunicaciones, el control de acceso por credenciales individuales y reglas de seguridad que impiden que un establecimiento acceda a los datos de otro y que reservan cada conversación privada a las personas que participan en ella, identificadas con su cuenta personal (correo y contraseña del Encargado/a o PIN de cada persona trabajadora).\n• No recurrir a otro encargado sin la autorización del responsable, en los términos de la cláusula 9.\n• Asistir al responsable, en la medida de lo posible, para que pueda atender las solicitudes de ejercicio de derechos de las personas interesadas (acceso, rectificación, supresión, limitación, portabilidad y oposición).\n• Asistir al responsable en el cumplimiento de sus obligaciones en materia de seguridad, notificación de violaciones de seguridad y evaluaciones de impacto.\n• Notificar al responsable, sin dilación indebida y en cuanto tenga conocimiento de ello, cualquier violación de la seguridad de los datos personales, facilitándole la información necesaria para que pueda cumplir con sus propias obligaciones de notificación.\n• Poner a disposición del responsable la información necesaria para demostrar el cumplimiento de estas obligaciones y permitir auditorías razonables.\n• Suprimir o devolver los datos al finalizar la prestación, conforme a la cláusula 8."
  },
  {
    title: "Anexo I · 8. Devolución o supresión de los datos",
    body: "Finalizada la prestación del Servicio, el encargado conservará los datos durante treinta días naturales a efectos de recuperación, a elección del responsable, y transcurrido ese plazo los suprimirá o anonimizará de forma definitiva, sin perjuicio de las copias de seguridad que puedan mantenerse durante el periodo técnicamente necesario para su sobrescritura.\n\nA petición del responsable formulada dentro de ese plazo, el encargado le facilitará una copia de los datos en un formato legible.\n\nSe conservarán bloqueados los datos que resulten necesarios para atender responsabilidades legales, durante los plazos de prescripción aplicables."
  },
  {
    title: "Anexo I · 9. Subencargados autorizados",
    body: "El responsable autoriza expresamente al encargado a recurrir a los siguientes subencargados, con los que el encargado ha suscrito los correspondientes acuerdos que les imponen las mismas obligaciones de protección de datos:\n\n• Google Ireland Limited (Firebase y Google Cloud) — alojamiento de la base de datos (región europea), almacenamiento de imágenes, alojamiento web, autenticación de las cuentas, ejecución de funciones del servidor y envío de notificaciones. Algunas de estas operaciones —ejecución de funciones, notificaciones y registros técnicos— pueden realizarse en infraestructura de Google situada fuera de la Unión Europea, incluido Estados Unidos, al amparo de las cláusulas contractuales tipo de la Comisión Europea.\n• Google Ireland Limited — servicio reCAPTCHA integrado en Firebase App Check, como medida de seguridad frente a accesos automatizados.\n• Google (API de Gemini) — interpretación, mediante inteligencia artificial, de las preguntas que el personal formula al asistente conversacional. Se envían el texto de la pregunta y las últimas líneas de la conversación en pantalla; el resto de los datos permanece en la base de datos del encargado. Puede implicar tratamiento por Google LLC (Estados Unidos), al amparo de las cláusulas contractuales tipo de la Comisión Europea.\n• Functional Software, Inc. (Sentry) — registro de errores técnicos de la Aplicación, con almacenamiento configurado en la región europea del proveedor.\n\nEl encargado informará al responsable de cualquier cambio previsto en la relación de subencargados con antelación suficiente para que este pueda oponerse."
  },
  {
    title: "Anexo I · 10. Obligaciones del responsable",
    body: "• Entregar al encargado únicamente los datos necesarios para la prestación del Servicio.\n• Informar a su personal del tratamiento de sus datos y disponer de una base jurídica válida para ello.\n• Velar por el cumplimiento del RGPD con carácter previo y durante todo el tratamiento.\n• Supervisar el tratamiento y realizar, cuando proceda, la evaluación de impacto."
  },
  {
    title: "Firma o aceptación electrónica",
    body: "Las partes suscriben las presentes Condiciones de Contratación y su Anexo I, en el lugar y fecha indicados a continuación, en dos ejemplares y a un solo efecto. Cuando el Cliente las acepta en la Aplicación, la firma se sustituye por la aceptación electrónica mediante la casilla correspondiente al crear la cuenta, de la que el prestador conserva constancia de la fecha y de la versión aceptada; esa aceptación surte los mismos efectos que la firma."
  },
];

const ConditionsScreen = ({ onBack }) => (
  <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)`, padding: '40px 20px' }}>
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        ← Volver
      </button>
      <div style={{ background: C.white, borderRadius: '20px', padding: '40px 36px', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800', color: C.text, margin: '0 0 6px' }}>Condiciones de Contratación</h1>
        <p style={{ fontSize: '12px', color: C.textMuted, margin: '0 0 6px', fontWeight: '600' }}>Versión de 29 de octubre de 2026</p>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 28px' }}>
          Condiciones del servicio Horidia, con el contrato de encargado del tratamiento de datos (Anexo I). Se aceptan al crear la cuenta de Encargado/a.
        </p>
        {CONDITIONS_SECTIONS.map((s, i) => (
          <div key={i} style={{ marginBottom: '22px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', color: C.primaryDark, margin: '0 0 6px' }}>{s.title}</h3>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const PRIVACY_SECTIONS = [
  {
    title: '1. Responsable del tratamiento',
    body: `Francisco José Carmona Senra, con NIF/DNI 29540157S, y domicilio a efectos de notificaciones en C/ Parque del Torcal de Antequera nº 7, 41015 Sevilla, es el responsable del tratamiento de los datos personales de los Encargados/as que se registran en la aplicación Horidia ("la App"). Puede contactar con nosotros en horidiapp@gmail.com.\n\nRespecto a los datos del personal del restaurante (nombres, turnos, mensajes, etc.), el responsable del tratamiento es el propio restaurante y Horidia actúa como encargado del tratamiento, tal y como se explica en el punto 9.\n\nDe acuerdo con el artículo 37 del RGPD, no resulta obligatorio nombrar un Delegado de Protección de Datos (DPO) dado el volumen y la naturaleza de los tratamientos realizados. Cualquier consulta relativa a protección de datos puede dirigirse igualmente a horidiapp@gmail.com.`
  },
  {
    title: '2. ¿Qué datos recogemos?',
    body: `a) Del Encargado/a que registra el restaurante: email, contraseña (almacenada mediante un algoritmo de resumen criptográfico irreversible, nunca en texto plano ni de forma reversible), nombre del restaurante, configuración del negocio (horarios, días de cierre, etc.), en su caso el logo que decida subir y la constancia de que ha aceptado las Condiciones de Contratación al registrarse (versión aceptada y fecha).\n\nb) De los empleados/as que el Encargado/a da de alta: nombre, y datos de organización del trabajo (turnos asignados, criterios organizativos para la planificación configurados por el Encargado —disponibilidad, cualificación, puesto y horarios—, solicitudes de días libres, periodos de vacaciones o ausencia, y mensajes enviados a través del chat interno —grupal o privado— de la App). Si el Encargado/a le genera un PIN personal de acceso, también la cuenta técnica asociada a ese PIN y su vinculación con su ficha, que sirve para que solo esa persona pueda acceder a sus mensajes privados (ver apartado 13).\n\nc) Del Dueño/a del negocio, si crea su propia cuenta de consulta: email, contraseña (con el mismo tratamiento criptográfico que la del Encargado/a) la constancia de que ha aceptado las condiciones (versión y fecha) y el negocio al que queda vinculada. Para vincularla se usa el código de Dueño, que Horidia envía únicamente al correo electrónico del Encargado/a y que este facilita a quien decida; el código se comprueba en el servidor y no se guarda en la cuenta del Dueño/a. Esta cuenta solo tiene acceso de lectura a información agregada del negocio (plan contratado, plantilla, turnos eliminados e historial de horarios), nunca a los mensajes del chat ni a datos personales detallados de cada empleado/a.\n\nd) De cualquier persona usuaria que active las notificaciones: un identificador técnico del dispositivo (token de notificaciones push) y el identificador de la cuenta desde la que se activan, usados únicamente para poder enviarle avisos dentro de la App (por ejemplo, la publicación de un horario nuevo) y para que el aviso de un mensaje privado solo llegue al dispositivo de la persona a la que va dirigido.\n\nNo solicitamos ni necesitamos datos especialmente protegidos (salud, ideología, orientación sexual, afiliación sindical, etc.). En particular, las ausencias o bajas deben registrarse únicamente como tales (por ejemplo, «Baja»), sin diagnósticos ni información médica asociada. Pedimos a las personas usuarias que no introduzcan este tipo de información en los campos de texto libre, como notas, motivos de ausencia o mensajes del chat. Si detectamos accidentalmente datos de esta naturaleza, procederemos a su eliminación.\n\ne) Del Encargado/a, los gastos del negocio que decida registrar en la App (concepto, importe, categoría, fecha y, si quiere, una fotografía de la factura). Se trata de información económica del negocio; pedimos no incluir en el concepto datos personales de terceros, como el nombre de una persona junto a un importe de nómina.\n\nf) Las imágenes que el Encargado/a decida subir: el logo del negocio y fotografías de la carta y del plano del local, que se almacenan en Firebase Storage. Pedimos no subir imágenes en las que aparezcan personas identificables.\n\ng) Las preguntas que la persona usuaria formula al asistente "Horito", escritas o dictadas por voz (ver apartados 5, 6 y 7). La conversación no se guarda en la base de datos de la App y desaparece al cerrar el chat, aunque el texto de las preguntas puede quedar en los registros técnicos descritos a continuación.\n\nh) Registros técnicos de funcionamiento de la infraestructura (Google Cloud / Firebase), que pueden incluir la dirección IP, identificadores técnicos de la cuenta y de la solicitud, y el texto de las preguntas al asistente. Se conservan durante un periodo limitado (por defecto, 30 días).\n\ni) De la persona o entidad que contrata el servicio: datos de contacto y de facturación (nombre o razón social, NIF/CIF, domicilio fiscal, teléfono y correo electrónico), necesarios para emitir las facturas y gestionar el cobro. Si el pago se hace por Bizum o transferencia, también los datos que la entidad bancaria comunique al titular sobre la operación.\n\nj) Estadísticas de uso del servicio, sin contenido personal. De cada restaurante se anotan datos agregados: cuántas veces ha entrado el Encargado/a, la fecha de su última entrada y cuántos horarios ha generado. Del personal empleado solo se cuenta CUÁNTAS personas han entrado: para no contar dos veces a la misma, se guarda una marca técnica seudónima (un resumen criptográfico irreversible que no permite saber quién es) junto con el día —sin hora— de su última entrada. No se registran nombres ni ningún dato personal en este contador, esas marcas se eliminan transcurridos 35 días y el titular solo ve el número total de empleados que han entrado en cada restaurante, nunca quiénes son. Como la marca corresponde a la cuenta de acceso, una misma persona que entre desde dos dispositivos distintos puede contarse dos veces.\n\nk) Visitas a la web horidia.com. Un contador propio anota, sin cookies, sin guardar la dirección IP y sin ningún identificador de la persona visitante: la página visitada, la procedencia por categorías (por ejemplo, Instagram, Google o acceso directo), el tipo de dispositivo (móvil u ordenador), las etiquetas de campaña del enlace (utm) y cuántas veces se pulsa el botón «Probar gratis» y desde qué zona de la página. Son únicamente recuentos por día.\n\nl) Informes de fallos. Si una persona usuaria envía un informe de fallo desde la App o a través de Horito, se guardan el texto que escribe, la sección, su nombre y rol en el restaurante, el departamento y el tipo de dispositivo, y se avisa por correo al titular para poder solucionarlo. Pedimos no incluir datos personales de terceros ni datos de salud en ese texto.\n\nm) Códigos de invitación entre restaurantes. Cuando un Encargado/a genera un código de invitación para otro negocio se guarda el nombre de su restaurante, y cuando alguien lo usa, el correo de la cuenta que lo canjea.\n\nn) Panel privado de gestión del titular. Para atender el servicio, el titular dispone de un panel de acceso restringido (protegido con verificación en dos pasos) donde consulta el estado de las cuentas, los pagos registrados, las notas internas de seguimiento, los fallos reportados y las estadísticas de uso descritas en los puntos j) y k). Cada acceso desde un dispositivo nuevo genera un aviso por correo al titular. El titular puede recibir además, como notificación en su propio móvil, un aviso al momento de estos mismos hechos (por ejemplo, una visita a la web, una cuenta nueva o la entrada en la App de alguien del restaurante, sin indicar quién), con el nombre del negocio y sin ningún otro dato personal.`
  },
  {
    title: '3. ¿Con qué finalidad tratamos los datos?',
    body: `• Permitir el acceso y uso de la App.\n• Generar y gestionar los cuadrantes de turnos de trabajo, incluidas vacaciones y ausencias.\n• Facilitar la comunicación interna entre encargados, gerentes y empleados (chat grupal y privado, solicitudes de días libres).\n• Permitir al Dueño/a del negocio, si crea su cuenta, consultar en modo de solo lectura información agregada de su negocio (plan, plantilla, turnos eliminados, historial de horarios).\n• Enviar notificaciones push al dispositivo de la persona usuaria cuando lo autorice, para avisarle de la publicación de un horario o de un mensaje nuevo.\n• Dejar constancia de la aceptación de las Condiciones de Contratación al registrarse.\n• Permitir al Encargado/a registrar y consultar los gastos de su negocio.\n• Responder, mediante el asistente "Horito", a las preguntas de la persona usuaria sobre sus vacaciones, turnos y días libres.\n• Calcular y aplicar el precio de la suscripción según el número de personas en plantilla del restaurante.\n• Atender al cliente durante el periodo de prueba: el titular recibe un aviso interno cuando se crea una cuenta nueva (nombre del local, tipo de cuenta, fecha y correo con el que se registró) para poder ofrecer soporte y seguimiento del servicio.\n• Conocer de forma agregada el uso del servicio (entradas del Encargado/a, horarios generados y número de empleados que entran en cada restaurante, sin identificarlos) para poder ofrecer soporte y mejorar la App.\n• Medir las visitas a la web horidia.com y las pulsaciones del botón «Probar gratis», sin cookies ni datos personales, para saber qué campañas funcionan.\n• Recibir y resolver los fallos que comunican las personas usuarias, y gestionar las invitaciones entre restaurantes.\n• Garantizar la seguridad del servicio y prevenir accesos fraudulentos o automatizados.\n• Emitir las facturas y cumplir las obligaciones fiscales y contables.\n• Enviar comunicaciones necesarias para el funcionamiento del servicio (por ejemplo, la recuperación de contraseña o avisos sobre publicación de horarios).\n• Gestionar el cobro de la suscripción cuando el Encargado/a active la suscripción de pago (actualmente, mediante Bizum o transferencia bancaria).\n• Enviar a los clientes, por correo electrónico, información sobre novedades y mejoras de Horidia, conforme al artículo 21.2 de la LSSI-CE. Cada envío incluye un enlace sencillo y gratuito para darse de baja, y es posible oponerse en cualquier momento escribiendo a horidiapp@gmail.com.`
  },
  {
    title: '4. ¿Cuál es la base legal?',
    body: `Para el Encargado/a: la ejecución del contrato de prestación de servicios que acepta al registrarse en la App (artículo 6.1.b del RGPD).\n\nPara el Dueño/a que crea su propia cuenta de consulta: su propio consentimiento, prestado al registrarse voluntariamente con su email, contraseña y el código de acceso del restaurante (artículo 6.1.a del RGPD).\n\nPara los datos de los empleados/as: corresponde al restaurante, en su condición de responsable del tratamiento y como empleador, determinar y garantizar la base jurídica aplicable a los datos de su personal (habitualmente, la ejecución del contrato de trabajo, artículo 6.1.b del RGPD). Horidia trata esos datos únicamente siguiendo las instrucciones documentadas del restaurante, conforme al encargo de tratamiento descrito en el punto 9.\n\nPara la facturación y las obligaciones fiscales y contables: el cumplimiento de una obligación legal (artículo 6.1.c del RGPD).\n\nPara la seguridad del servicio, los registros técnicos y la atención al cliente durante el periodo de prueba (incluido el aviso interno de alta de cuenta): la ejecución del contrato y el interés legítimo del titular en proteger el servicio y atender a sus usuarios (artículos 6.1.b y 6.1.f del RGPD).\n\nPara las estadísticas de uso agregadas, el contador de visitas de la web, la gestión de informes de fallos y el panel de gestión del titular: el interés legítimo en conocer el funcionamiento del servicio y atender a sus usuarios (artículo 6.1.f del RGPD), con el mínimo de datos posible (sin nombres en los contadores, sin cookies y sin direcciones IP).\n\nPara las comunicaciones comerciales a clientes sobre servicios propios similares a los contratados: el interés legítimo (artículo 6.1.f del RGPD y artículo 21.2 de la LSSI-CE). A quien no sea cliente solo se le enviarán con su consentimiento previo (artículo 6.1.a del RGPD).\n\nFacilitar estos datos es un requisito necesario para poder usar la App: sin ellos no es posible generar horarios, gestionar solicitudes ni utilizar el chat interno.`
  },
  {
    title: '5. ¿Se toman decisiones automatizadas?',
    body: `Horidia incluye un algoritmo propio de generación de horarios (no basado en modelos de lenguaje ni en inteligencia artificial de terceros) que propone un reparto de turnos según los criterios que configura el Encargado/a (mínimos de personal, turnos contratados, vacaciones, etc.).\n\nEste algoritmo no toma decisiones automatizadas con efectos jurídicos sobre las personas empleadas en el sentido del artículo 22 del RGPD: la propuesta de horario generada siempre queda sujeta a la revisión, edición manual y publicación por parte de una persona (el Encargado/a) antes de tener efecto real sobre el personal. No se elaboran perfiles de las personas empleadas más allá de los criterios organizativos para la planificación que el propio Encargado/a introduce manualmente para ese fin.\n\nHoridia incluye además un asistente conversacional ("Horito") que informa a la persona usuaria, en su primer uso, de que está interactuando con un sistema de inteligencia artificial, conforme al artículo 50 del Reglamento (UE) 2024/1689 de Inteligencia Artificial. Este asistente solo interpreta preguntas formuladas en lenguaje natural; nunca genera por sí mismo los datos que muestra, que proceden siempre de la información real almacenada en la App. Las preguntas pueden escribirse o dictarse por voz; el dictado es opcional y solo se activa cuando la persona usuaria pulsa el botón del micrófono y concede el permiso del navegador.`
  },
  {
    title: '6. ¿A quién se comunican los datos?',
    body: `Los datos se alojan en servidores de Google Firebase (Google Ireland Limited), que actúa como encargado del tratamiento conforme al correspondiente contrato de encargo (Data Processing Agreement) suscrito con Google. La base de datos está ubicada en la Unión Europea; los matices sobre dónde se ejecutan otros servicios de Google se explican en el apartado 7.\n\nLa App se apoya además en los siguientes proveedores tecnológicos, que actúan como encargados del tratamiento y están sujetos a su propio contrato de encargo:\n\n• Google Ireland Limited (Firebase y Google Cloud): alojamiento de la base de datos, almacenamiento de imágenes (Firebase Storage), alojamiento y distribución de los archivos de la App (Firebase Hosting), autenticación de las cuentas, ejecución de las funciones del servidor y envío de las notificaciones push.\n• Google Ireland Limited (reCAPTCHA v3, integrado en Firebase App Check): medida de seguridad que analiza de forma automática e invisible la interacción con la App para distinguir a las personas usuarias reales de accesos automatizados. Para ello, Google puede recoger información técnica del dispositivo y del navegador. El uso de reCAPTCHA está sujeto además a la Política de Privacidad (https://policies.google.com/privacy) y a las Condiciones del Servicio (https://policies.google.com/terms) de Google, y puede implicar el tratamiento de esa información técnica por Google LLC en Estados Unidos (ver apartado 7). Base jurídica: interés legítimo en proteger el servicio y los datos de los negocios frente a accesos fraudulentos o automatizados (artículo 6.1.f del RGPD).\n• Google (API de Gemini, a través de Google AI Studio): utilizado por el asistente conversacional "Horito" para interpretar la intención de las preguntas que la persona usuaria escribe en lenguaje natural (por ejemplo, sobre sus vacaciones, sus turnos o quién trabaja un día concreto). Se envía el texto de la pregunta y, para poder entender preguntas de seguimiento (por ejemplo, «¿y el domingo?»), las últimas líneas de la conversación en pantalla, que pueden incluir nombres o turnos que ya se habían mostrado; no se envía el resto de la información de la base de datos. La respuesta final se construye siempre con datos reales obtenidos directamente de la base de datos de Horidia, nunca generados ni redactados por la inteligencia artificial. Este servicio puede implicar tratamiento por parte de Google LLC (Estados Unidos) — ver apartado 7.\n• Functional Software, Inc. (Sentry): registro automático de los errores técnicos de la App para poder detectarlos y corregirlos, con el almacenamiento configurado en su región de la Unión Europea. Este servicio está configurado expresamente para no recoger datos identificativos de las personas usuarias ni el contenido de sus comunicaciones: únicamente información técnica del fallo (mensaje de error, tipo de navegador y momento en que se produjo).\n\nIntervienen además otros servicios que no son proveedores de Horidia como encargados, sino que se rigen por sus propias condiciones:\n\n• Gmail (Google): servicio de correo electrónico que utiliza el titular para comunicarse con las personas usuarias y para recibir avisos internos del servicio, como el de alta de una cuenta nueva.\n• Proveedor del navegador (reconocimiento de voz): si la persona usuaria utiliza el dictado por voz del asistente, la conversión de la voz en texto la realiza el propio navegador y, según el navegador y el dispositivo, el audio puede enviarse a los servidores de su proveedor (por ejemplo, Google en Chrome o Apple en Safari). Horidia no recibe ni almacena el audio: solo recibe el texto resultante, que la persona puede revisar antes de enviarlo.\n\nNo cedemos ni vendemos datos a terceros con fines comerciales o publicitarios.\n\nLas tipografías de la App y de este sitio web se sirven desde nuestro propio servidor: no se cargan desde Google Fonts ni desde ningún otro servicio externo, por lo que no se comunica tu dirección IP a terceros con ese fin.\n\nActualmente el cobro de la suscripción se realiza por Bizum o transferencia bancaria: los datos del pago (nombre de quien lo realiza, importe y concepto) llegan a Horidia a través de la entidad bancaria, que actúa como responsable independiente de los datos de la operación. Si en el futuro se activa un cobro mediante pasarela de pago, se actualizará esta política antes de ponerlo en marcha.`
  },
  {
    title: '7. Transferencias internacionales',
    body: `La base de datos de Horidia (Cloud Firestore) y el almacenamiento de imágenes están ubicados en la Unión Europea. Sin embargo, algunos servicios de Google que forman parte del funcionamiento de la App —la ejecución de las funciones del servidor (como el asistente Horito, los avisos automáticos y las tareas programadas), el envío de notificaciones push y los registros técnicos asociados— pueden ejecutarse en infraestructura de Google situada fuera de la Unión Europea, incluido Estados Unidos. En ese caso, Google puede tratar allí, de forma temporal, los datos que cada función necesita para realizar su cometido.\n\nEstas transferencias, y cualesquiera otras que pudieran producirse con proveedores tecnológicos auxiliares, se realizan únicamente hacia países u organizaciones que garanticen un nivel de protección adecuado, mediante las cláusulas contractuales tipo de la Comisión Europea incorporadas a los términos de tratamiento de datos del proveedor u otro mecanismo válido conforme al Capítulo V del RGPD.\n\nEn el caso de reCAPTCHA (Firebase App Check), la información técnica del dispositivo y del navegador puede ser tratada por Google LLC en Estados Unidos, al amparo del Marco de Privacidad de Datos UE-EE. UU., en el que Google LLC está certificada, o de las cláusulas contractuales tipo de la Comisión Europea.\n\nEn el caso concreto de Sentry, su sociedad matriz tiene sede en Estados Unidos, si bien el almacenamiento de los datos está configurado en su región europea. Cualquier acceso a dichos datos desde fuera del Espacio Económico Europeo queda amparado por las cláusulas contractuales tipo de la Comisión Europea incorporadas al contrato de encargo del proveedor.\n\nEn el caso de la API de Gemini utilizada por el asistente "Horito", el texto de las preguntas formuladas por la persona usuaria puede ser tratado por Google LLC, con sede en Estados Unidos. Dicho tratamiento queda amparado por las cláusulas contractuales tipo de la Comisión Europea incorporadas a las condiciones del servicio de Google. Recuerde que solo se envían el texto de la pregunta y las últimas líneas de la conversación en pantalla; el resto de datos del restaurante o del personal permanece en la base de datos de Horidia, en la Unión Europea.\n\nSi la persona usuaria utiliza el dictado por voz, el audio puede ser tratado por el proveedor de su navegador, incluso fuera del Espacio Económico Europeo, conforme a las condiciones de ese proveedor. Horidia no interviene en ese tratamiento y no recibe el audio.`
  },
  {
    title: '8. ¿Cuánto tiempo conservamos los datos?',
    body: `Los datos se conservan mientras la cuenta del restaurante permanezca activa.\n\nSi el Encargado/a solicita la baja o cancela su suscripción, el acceso a la aplicación se desactiva al finalizar el periodo abonado y los datos se conservan durante TREINTA (30) DÍAS NATURALES desde la fecha efectiva de la baja. Ese plazo existe para que el restaurante pueda recuperarlos si se reincorpora al servicio, o solicitar una copia si la necesita.\n\nLo mismo ocurre si finaliza el periodo de prueba de catorce días y el restaurante no activa la suscripción, o si el acceso permanece suspendido por falta de pago: los datos se conservan treinta (30) días naturales desde el fin de la prueba o desde la suspensión.\n\nTranscurridos esos treinta días, los datos se eliminan o anonimizan de los sistemas activos, sin perjuicio de las copias de seguridad que puedan mantenerse durante el periodo técnicamente necesario para su sobrescritura ni de las obligaciones legales de conservación.\n\nAdemás, con independencia de lo anterior: los mensajes del chat interno se conservan un máximo de doce (12) meses desde su envío y se eliminan automáticamente transcurrido ese plazo; el historial de horarios publicados se conserva un máximo de cuatro meses de calendario por local, para permitir el cálculo de turnos trabajados a efectos de nómina; y los registros técnicos de la infraestructura se conservan por un periodo limitado (por defecto, 30 días).\n\nSe exceptúa la documentación que deba conservarse por obligación legal durante un plazo superior, como los datos de facturación, que se conservan durante los plazos exigidos por la normativa fiscal y mercantil (con carácter general, hasta seis años).`
  },
  {
    title: '9. Relación entre Horidia y el restaurante (encargo de tratamiento)',
    body: `Respecto a los datos de los empleados/as, el restaurante (a través de su Encargado/a) es el responsable del tratamiento, y Horidia actúa como encargado del tratamiento, procesando dichos datos únicamente siguiendo las instrucciones del restaurante y para la finalidad de gestión de turnos, conforme al artículo 28 del RGPD. El Encargado/a acepta estas condiciones, en representación del restaurante, al crear su cuenta, marcando la casilla de aceptación de las Condiciones de Contratación, cuyo Anexo I recoge el contrato de encargo de tratamiento previsto en el artículo 28 del RGPD. Horidia conserva constancia de esa aceptación (versión y fecha).\n\nCuando una solicitud de ejercicio de derechos se refiera a datos tratados por cuenta del restaurante, Horidia podrá remitirla al restaurante responsable y prestará la asistencia necesaria para que este pueda atenderla.`
  },
  {
    title: '10. Propiedad y responsabilidad sobre los datos introducidos',
    body: `El restaurante conserva la responsabilidad sobre los datos de su personal que introduce en la App. Garantiza que dispone de base jurídica para tratarlos y que la información introducida es exacta. Horidia no utilizará esos datos para fines distintos de la prestación del servicio, salvo que exista una base jurídica independiente que lo permita.`
  },
  {
    title: '11. Derechos de las personas usuarias',
    body: `Cualquier persona cuyos datos sean tratados a través de la App tiene derecho a acceder a sus datos, solicitar su rectificación si son inexactos, solicitar su supresión cuando ya no sean necesarios, solicitar la limitación de su tratamiento, oponerse a él, y solicitar su portabilidad. Cuando el tratamiento se base en el consentimiento (por ejemplo, en la cuenta de Dueño/a), puede retirarlo en cualquier momento, sin que ello afecte a la licitud del tratamiento anterior a su retirada.\n\nEstos derechos pueden ejercerse escribiendo a horidiapp@gmail.com, indicando el derecho que se desea ejercer. Si se trata de datos de un empleado/a, también puede dirigirse directamente al restaurante donde trabaja, como responsable de dichos datos.\n\nAsimismo, tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es) si considera que sus derechos no han sido debidamente atendidos.`
  },
  {
    title: '12. Uso de almacenamiento técnico (cookies y similares)',
    body: `La App utiliza únicamente almacenamiento técnico estrictamente necesario para su funcionamiento —principalmente para mantener la sesión iniciada (autenticación mediante Firebase) y para recordar preferencias elegidas por la propia persona usuaria en su dispositivo (por ejemplo, el acceso con huella o la densidad de la vista del horario)—, exento del deber de solicitar consentimiento conforme al artículo 22.2 de la LSSI-CE, al ser imprescindible para prestar el servicio expresamente solicitado por la persona usuaria.\n\nLa App no utiliza cookies ni tecnologías similares con fines analíticos, publicitarios o de elaboración de perfiles de terceros. El contador de visitas de la web horidia.com tampoco usa cookies ni guarda nada en el dispositivo de la persona visitante. Si en el futuro se incorporase alguna herramienta de este tipo, se actualizará esta política y se solicitará el consentimiento correspondiente mediante el mecanismo legalmente exigido.\n\nLa App utiliza asimismo Firebase App Check con reCAPTCHA v3 (Google) como medida de seguridad frente a accesos automatizados. Esta herramienta puede leer o almacenar información técnica en el dispositivo y recoger datos sobre la interacción con la App, con la finalidad exclusiva de detectar actividad fraudulenta o automatizada y proteger la seguridad del servicio. No se emplea con fines publicitarios, analíticos ni de elaboración de perfiles.`
  },
  {
    title: '13. Medidas de seguridad',
    body: `Aplicamos medidas técnicas y organizativas razonables: comunicación cifrada mediante HTTPS, y reglas de acceso restringido a la base de datos, de forma que cada restaurante solo puede acceder a sus propios datos.\n\nEl PIN de acceso de cada persona empleada se gestiona a través del sistema de autenticación de Firebase, que lo almacena mediante un algoritmo de resumen criptográfico irreversible (hash): ni Francisco José Carmona Senra ni terceros pueden acceder al PIN en su forma original. El PIN lo genera el Encargado/a desde la App y se lo entrega a la persona empleada, que puede cambiarlo desde «Mi PIN» por otro que solo conozca ella. Hasta que lo cambie, el PIN también lo conoce quien lo generó, por lo que se recomienda cambiarlo al entrar por primera vez. Si se olvida, el Encargado/a puede generar uno nuevo, y el anterior deja de funcionar al momento.\n\nDicho PIN es personal e intransferible. Mientras no se ha entregado, su custodia corresponde al Encargado/a que lo ha generado y, una vez entregado, a la persona usuaria, que no debe compartirlo con terceros, incluidos otros compañeros o empleados del mismo establecimiento. Se recomienda cambiarlo de inmediato si se sospecha que ha podido ser conocido por otra persona. En la medida en que la ley lo permita, Francisco José Carmona Senra no responde de los accesos realizados con un PIN, una contraseña o un código de Dueño que se haya facilitado, perdido o revelado a otra persona.\n\nMensajes privados. Dentro de la App, cada conversación privada solo pueden leerla las dos personas que participan en ella. Para usarlos hay que haber entrado con la cuenta personal: el Encargado/a, con su correo y contraseña, y cada persona empleada, con su PIN. Sin PIN, la App no da acceso a ningún mensaje privado. Esta restricción la aplican las reglas de seguridad del servidor, no solo la pantalla de la App.\n\nCuando una persona usuaria activa el acceso mediante huella dactilar o reconocimiento facial, esa verificación biométrica la realiza el propio dispositivo (a través del estándar WebAuthn): la huella o el rostro nunca salen del dispositivo ni son accesibles para Francisco José Carmona Senra. Únicamente se recibe y almacena un identificador técnico de esa credencial, no el dato biométrico en sí.\n\nNinguna medida de seguridad es infalible al 100%. En caso de una violación de seguridad que suponga un riesgo para los derechos de las personas afectadas, se notificará a la Agencia Española de Protección de Datos y, en su caso, a las personas afectadas, conforme a los artículos 33 y 34 del RGPD.`
  },
  {
    title: '14. Menores de edad',
    body: `La creación de una cuenta de Encargado/a en la App requiere ser mayor de 18 años. La normativa española permite trabajar desde los 16 años con autorización de padres o tutores. Si un Encargado/a diera de alta como empleado/a a una persona menor de 18 años, será responsabilidad exclusiva del restaurante contar con las autorizaciones correspondientes y respetar las limitaciones legales aplicables al asignarle turnos en la App, en particular la prohibición de trabajar entre las 22:00 y las 06:00 horas y de realizar horas extraordinarias.\n\nHoridia es un servicio dirigido a empresas y profesionales. No está destinado a menores de dieciséis (16) años, edad mínima para trabajar, y no deben darse de alta en la App personas por debajo de esa edad. Si tenemos conocimiento de que se han introducido datos de una persona menor de dieciséis años, los eliminaremos.`
  },
  {
    title: '15. Registro de jornada y cumplimiento de la normativa laboral',
    body: `Horidia es una herramienta de planificación de turnos. El cumplimiento de la obligación legal de registro horario diario es responsabilidad exclusiva del restaurante como empleador, no de Horidia (Real Decreto-ley 8/2019).\n\nHoridia es una herramienta de apoyo a la planificación y no efectúa ningún control de legalidad, ni previo ni posterior, sobre los horarios, turnos, duraciones de jornada, descansos u otras condiciones laborales que el Encargado/a configure, genere o publique en la App. Todos los parámetros de planificación —incluida la duración de los turnos, que es configurable por el restaurante— son determinados exclusivamente por este, en su condición de empleador, que es el único responsable de que la planificación respete el Estatuto de los Trabajadores (en particular, los límites de jornada ordinaria y de jornada diaria, los descansos mínimos entre jornadas y semanales y el régimen aplicable a personas menores de edad), el convenio colectivo aplicable y cualquier otra normativa laboral o de prevención de riesgos laborales. Los valores por defecto, los límites, los avisos y las sugerencias de la App tienen carácter meramente orientativo y no constituyen asesoramiento jurídico ni laboral. En consecuencia, y en la máxima medida permitida por la ley, Francisco José Carmona Senra no asume responsabilidad alguna por las sanciones administrativas, reclamaciones, recargos, indemnizaciones u otros perjuicios que pudieran derivarse del incumplimiento de dicha normativa por parte del restaurante. Las medidas de seguridad descritas en esta política se refieren a la protección de los datos y de la Aplicación, y no implican validación alguna del contenido laboral de los horarios.`
  },
  {
    title: '16. Cambios en esta política',
    body: `Esta Política de Privacidad puede actualizarse para adaptarse a novedades legislativas, a criterios publicados por la Agencia Española de Protección de Datos, o a cambios en el funcionamiento de la App. Cualquier cambio relevante será comunicado a los Encargados/as registrados.`
  },
  {
    title: '17. Contacto',
    body: `Para cualquier duda sobre esta Política de Privacidad, puede escribir a horidiapp@gmail.com.`
  },
];

const PrivacyPolicyScreen = ({ onBack }) => (
  <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)`, padding: '40px 20px' }}>
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        ← Volver
      </button>
      <div style={{ background: C.white, borderRadius: '20px', padding: '40px 36px', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800', color: C.text, margin: '0 0 6px' }}>Política de Privacidad</h1>
        <p style={{ fontSize: '12px', color: C.textMuted, margin: '0 0 6px', fontWeight: '600' }}>Última actualización: 3 de noviembre de 2026</p>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 28px' }}>
          Aplicable a la aplicación Horidia, redactada conforme al Reglamento (UE) 2016/679 (RGPD), la Ley Orgánica 3/2018 (LOPDGDD) y la Ley 34/2002 (LSSI-CE).
        </p>
        {PRIVACY_SECTIONS.map((s, i) => (
          <div key={i} style={{ marginBottom: '22px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', color: C.primaryDark, margin: '0 0 6px' }}>{s.title}</h3>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const LEGAL_SECTIONS = [
  {
    title: '1. Objeto y datos identificativos del titular',
    body: `En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa de los siguientes datos identificativos del titular de la aplicación Horidia ("la App"):\n\n• Titular: Francisco José Carmona Senra\n• NIF/CIF: 29540157S\n• Domicilio a efectos de notificaciones: C/ Parque del Torcal de Antequera nº 7, 41015 Sevilla\n• Correo electrónico de contacto: horidiapp@gmail.com\n• Actividad: desarrollo y explotación de una aplicación de software como servicio (SaaS) de gestión de horarios de personal para negocios de hostelería.`
  },
  {
    title: '2. Objeto del Aviso Legal',
    body: `El presente Aviso Legal regula el acceso y uso de la App por parte de las personas usuarias (Encargados/as, Gerentes, Empleados/as y Dueños/as de los negocios que la contratan). El acceso a la App atribuye la condición de persona usuaria e implica la aceptación plena de este Aviso Legal en el momento en que se produce dicho acceso.`
  },
  {
    title: '3. Condiciones de acceso y uso',
    body: `El acceso a la App es gratuito durante un periodo de prueba de catorce (14) días naturales desde el alta. Finalizado ese periodo, el acceso queda suspendido hasta que el restaurante active la suscripción de pago, cuyo precio depende del número de personas en plantilla y puede consultarse en cualquier momento desde el apartado "Mi plan" de la App. El uso de determinadas funciones requiere el registro de una cuenta y la aceptación de las presentes condiciones. La contratación del servicio se rige además por las Condiciones de Contratación, que están disponibles en la App antes de crear la cuenta y que el Encargado/a acepta al registrarse.\n\nLa persona usuaria se compromete a utilizar la App de forma diligente, conforme a la ley, la moral, el orden público y este Aviso Legal, y a no emplearla con fines ilícitos, lesivos de derechos de terceros o que de cualquier forma puedan dañar, inutilizar, sobrecargar o deteriorar la App o impedir su normal utilización por otras personas usuarias.\n\nEl Encargado/a que suba a la App el logotipo de su negocio u otras imágenes (como fotografías de la carta o del local) garantiza ser titular de los derechos necesarios sobre ellas, o contar con la autorización correspondiente para su uso, y responderá frente a Francisco José Carmona Senra de cualquier reclamación de terceros derivada del incumplimiento de esta garantía.\n\nLas credenciales de acceso —la contraseña de cada cuenta, el PIN personal de cada persona empleada y el código de Dueño— son personales y confidenciales. Su custodia corresponde a quien las tiene en su poder: al Encargado/a, hasta que entrega un PIN o el código de Dueño, y a la persona que lo recibe, a partir de ese momento. En la medida en que la ley lo permita, Francisco José Carmona Senra no será responsable de los accesos ni de los perjuicios derivados del uso de credenciales que se hayan facilitado, perdido o revelado a terceros, incluidas otras personas del mismo establecimiento.\n\nEl Servicio se dirige exclusivamente a negocios y profesionales. Quien crea una cuenta de Encargado/a declara ser mayor de edad y actuar en nombre del negocio que la contrata.`
  },
  {
    title: '4. Propiedad intelectual e industrial',
    body: `El código fuente, el diseño, la marca "Horidia", el logotipo y el resto de contenidos de la App son propiedad de Francisco José Carmona Senra y están protegidos por la normativa de propiedad intelectual e industrial vigente, en particular por el Real Decreto Legislativo 1/1996, de 12 de abril, por el que se aprueba el texto refundido de la Ley de Propiedad Intelectual.\n\nQueda prohibida la reproducción, distribución, comunicación pública, transformación o cualquier otra forma de explotación, total o parcial, de la App o de su código fuente sin autorización previa y expresa del titular, salvo en los casos permitidos por la ley.\n\nLos datos que cada restaurante introduce en la App (nombres de su personal, horarios, mensajes del chat interno, etc.) son de titularidad exclusiva de dicho restaurante. Francisco José Carmona Senra no ostenta ningún derecho de propiedad sobre esos datos, sin perjuicio del tratamiento que realiza para prestar el servicio conforme a la Política de Privacidad.\n\nComunicación de contenidos ilícitos. Si consideras que algún contenido alojado en la App o en este sitio web es ilícito o vulnera tus derechos (por ejemplo, una imagen subida sin permiso), puedes comunicarlo a horidiapp@gmail.com indicando el contenido concreto, el motivo y tus datos de contacto. Revisaremos la comunicación con diligencia y, si procede, retiraremos el contenido o impediremos el acceso a él, conforme al artículo 16 de la LSSI-CE y al Reglamento (UE) 2022/2065 de Servicios Digitales.`
  },
  {
    title: '5. Sobre el código que se ejecuta en tu navegador',
    body: `Por el propio funcionamiento de cualquier aplicación web, parte del código de la App se transmite y ejecuta temporalmente en el dispositivo de la persona usuaria para poder mostrar la interfaz. Esta circunstancia técnica no implica, en ningún caso, cesión de derechos sobre dicho código ni autorización para su copia, descompilación, modificación o reutilización, total o parcial, fuera de la propia App.`
  },
  {
    title: '6. Exclusión de garantías y de responsabilidad',
    body: `La App se ofrece "tal cual" ("as is"), sin garantías de disponibilidad ininterrumpida o libre de errores. Francisco José Carmona Senra pondrá los medios razonables a su alcance para subsanar o evitar posibles errores con la mayor brevedad posible, pero no garantiza la ausencia total de los mismos ni será responsable de los daños y perjuicios que pudieran derivarse de la falta de disponibilidad o de continuidad del funcionamiento de la App, de un uso fraudulento de la misma por parte de terceros, ni, en general, de daños derivados de causas ajenas a un funcionamiento normal y diligente.\n\nComo se explica en la Política de Privacidad, la App actúa como encargada del tratamiento de los datos de las personas empleadas que el restaurante (responsable del tratamiento) introduce en ella; el restaurante es responsable de la veracidad, exactitud y licitud de los datos que introduce en la App.\n\nHoridia es una herramienta de apoyo a la planificación y no efectúa ningún control de legalidad, ni previo ni posterior, sobre los horarios, turnos, duraciones de jornada, descansos u otras condiciones laborales que el Encargado/a configure, genere o publique en la App. Todos los parámetros de planificación —incluida la duración de los turnos, que es configurable por el restaurante— son determinados exclusivamente por este, en su condición de empleador, que es el único responsable de que la planificación respete el Estatuto de los Trabajadores (en particular, los límites de jornada ordinaria y de jornada diaria, los descansos mínimos entre jornadas y semanales y el régimen aplicable a personas menores de edad), el convenio colectivo aplicable y cualquier otra normativa laboral o de prevención de riesgos laborales. Los valores por defecto, los límites, los avisos y las sugerencias de la App tienen carácter meramente orientativo y no constituyen asesoramiento jurídico ni laboral. En consecuencia, y en la máxima medida permitida por la ley, Francisco José Carmona Senra no asume responsabilidad alguna por las sanciones administrativas, reclamaciones, recargos, indemnizaciones u otros perjuicios que pudieran derivarse del incumplimiento de dicha normativa por parte del restaurante.`
  },
  {
    title: '7. Registro de jornada',
    body: `Horidia NO es un sistema de registro de jornada a efectos del artículo 34.9 del Estatuto de los Trabajadores. La App permite planificar y consultar los turnos previstos, pero no registra la hora real de entrada y salida de las personas trabajadoras y, por tanto, no sustituye ni cubre la obligación legal de llevar un registro horario, que corresponde en exclusiva a cada restaurante.`
  },
  {
    title: '8. Sobre el generador automático de horarios',
    body: `Horidia incluye un algoritmo propio (no basado en modelos de lenguaje ni en inteligencia artificial de terceros) que propone un reparto de turnos según los criterios configurados por el Encargado/a. Este algoritmo es una herramienta de apoyo a la organización interna del negocio: no toma decisiones automatizadas con efectos jurídicos sobre las personas empleadas, y el horario propuesto siempre queda sujeto a la revisión, edición y publicación manual por parte del Encargado/a antes de tener efecto real sobre el personal.`
  },
  {
    title: '9. Sobre el asistente conversacional (Horito)',
    body: `Horidia incluye un asistente conversacional, "Horito", que utiliza un servicio de inteligencia artificial de terceros (API de Gemini, de Google) únicamente para interpretar las preguntas que la persona usuaria formula, por escrito o por voz, en lenguaje natural. Los datos que el asistente muestra proceden siempre de la información real almacenada en la App y no son generados por la inteligencia artificial. Aun así, pueden producirse errores de interpretación, por lo que el horario publicado en la App prevalece en todo caso sobre lo indicado por el asistente.\n\nEl asistente es una ayuda informativa: no toma decisiones sobre las personas empleadas. Se informa a la persona usuaria, en su primer uso, de que interactúa con un sistema de inteligencia artificial. El tratamiento de datos asociado se detalla en la Política de Privacidad.`
  },
  {
    title: '10. Enlaces a terceros',
    body: `En caso de que la App incluya enlaces a sitios web de terceros (por ejemplo, pasarelas de pago), dichos enlaces se facilitan únicamente a título informativo, sin que Francisco José Carmona Senra asuma responsabilidad alguna sobre el contenido, funcionamiento, disponibilidad o políticas de privacidad de dichos sitios.`
  },
  {
    title: '11. Protección de datos',
    body: `El tratamiento de los datos personales de las personas usuarias se rige por la Política de Privacidad de la App, disponible como documento independiente a este Aviso Legal.`
  },
  {
    title: '12. Modificaciones',
    body: `Francisco José Carmona Senra se reserva el derecho a modificar, en cualquier momento y sin previo aviso, la presentación y configuración de la App, así como el presente Aviso Legal. Se recomienda a las personas usuarias revisar este documento periódicamente.`
  },
  {
    title: '13. Legislación aplicable y jurisdicción',
    body: `Este Aviso Legal se rige por la legislación española. Para la resolución de cualquier controversia derivada del uso de la App, las partes se someterán a los Juzgados y Tribunales que correspondan conforme a la normativa aplicable, sin perjuicio de los fueros que pudieran corresponder a las personas consumidoras conforme a la legislación de protección de personas consumidoras y usuarias vigente.`
  },
  {
    title: '14. Contacto',
    body: `Para cualquier duda sobre este Aviso Legal, puede escribir a horidiapp@gmail.com.`
  },
];

const LegalNoticeScreen = ({ onBack }) => (
  <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)`, padding: '40px 20px' }}>
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        ← Volver
      </button>
      <div style={{ background: C.white, borderRadius: '20px', padding: '40px 36px', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800', color: C.text, margin: '0 0 6px' }}>Aviso Legal</h1>
        <p style={{ fontSize: '12px', color: C.textMuted, margin: '0 0 6px', fontWeight: '600' }}>Última actualización: 29 de octubre de 2026</p>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 28px' }}>
          Redactado conforme al artículo 10 de la Ley 34/2002 (LSSI-CE): titularidad, propiedad intelectual y condiciones de uso de la aplicación Horidia.
        </p>
        {LEGAL_SECTIONS.map((s, i) => (
          <div key={i} style={{ marginBottom: '22px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', color: C.primaryDark, margin: '0 0 6px' }}>{s.title}</h3>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

function DepartmentApp({ department, onSwitchDepartment }) {
  const isCocina = department === 'cocina';
  const STORAGE_KEY = isCocina ? 'horiaData_v7_cocina' : 'horiaData_v7';
  const FIRESTORE_COLLECTION = isCocina ? 'cocinas' : 'restaurantes';

  // LÍMITE DE ENVÍO DE MENSAJES — NIVEL 2, TERCER INTENTO (6 sep 2026). Los dos intentos
  // anteriores usaban un CONTADOR (mensajes por minuto) que dio fallos reales dos veces seguidas
  // sin poder verse la causa exacta (Firestore solo dice "Missing or insufficient permissions",
  // sin más detalle). Este diseño es mucho más simple a propósito: en vez de contar, solo guarda
  // "cuándo fue tu último mensaje" y exige que pase al menos 1 segundo hasta el siguiente. Al ser
  // un solo dato (una fecha) en vez de un contador que sube y reinicia, hay muchas menos formas
  // de que la lógica se equivoque — y ya NO hace falta leer nada antes de escribir (antes se leía
  // el contador actual para decidir si sumar o reiniciar; eso ya no existe). Lleva chivatos en la
  // consola (console.log) para ver el motivo exacto si vuelve a fallar, en vez de adivinar otra
  // vez a ciegas. Mismo patrón que antes: se escribe el mensaje y el contador en el mismo paquete
  // atómico, y las reglas de Firestore exigen esa segunda escritura para aceptar el mensaje —
  // también se aplica a los mensajes "del Sistema" para que nadie se lo salte fingiendo serlo.
  const enviarMensajeConLimite = async (nombreColeccion, datosMensaje) => {
    if (!restaurantDocId) return;
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Sin sesión activa');
    console.log('[límite mensajes] intentando enviar', { restaurantDocId, uid, nombreColeccion });
    const batch = writeBatch(db);
    const refMensaje = doc(collection(db, FIRESTORE_COLLECTION, restaurantDocId, nombreColeccion));
    const refLimite = doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'rateLimits', uid);
    batch.set(refMensaje, datosMensaje);
    batch.set(refLimite, { lastMessageAt: serverTimestamp() });
    try {
      await batch.commit();
      console.log('[límite mensajes] enviado correctamente');
    } catch (e) {
      console.error('[límite mensajes] FALLO — código:', e.code, '— mensaje:', e.message);
      throw e;
    }
  };
  const RECORDADO_KEY = `horia_empleado_recordado_${department}`;
  // Clave aparte, más "floja": solo dice a qué restaurante pertenece este dispositivo — nunca
  // se borra al pulsar "Salir", para no tener que volver a escribir el código cada vez.
  // La de arriba (RECORDADO_KEY) sí identifica a la persona concreta, y esa sí se borra al salir.
  const RESTAURANTE_RECORDADO_KEY = `horia_restaurante_recordado_${department}`;
  const L = {
    deptEmoji: isCocina ? '🍳' : '🏠',
    jefeRoleName: isCocina ? 'Jefe de Cocina' : 'Encargado',
    jefeButtonLabel: isCocina ? '👨‍🍳 Acceso Jefe de Cocina' : '🏠 Acceso Encargado',
    jefeBadge: isCocina ? '👨‍🍳 Jefe de Cocina' : '👔 Encargado',
    empleadoRoleName: isCocina ? 'Cocinero' : 'Empleado',
    empleadoButtonLabel: isCocina ? '🍳 Acceso Cocinero' : '👤 Acceso Empleado',
    empleadosTabLabel: isCocina ? 'Cocineros' : 'Empleados',
    addEmployeeTitle: isCocina ? '➕ Añadir cocinero' : '➕ Añadir empleado',
    addEmployeePlaceholder: isCocina ? 'Nombre del cocinero' : 'Nombre del empleado',
    emptyEmployeesText: isCocina ? 'No hay cocineros aún.' : 'No hay empleados aún.',
    heroLabel: isCocina ? '🍳 Cocina' : '🏠 Tu negocio',
    panelJefe: isCocina ? 'Panel de Cocina' : 'Panel del Encargado',
    panelEmpleado: isCocina ? 'Panel de Cocinero' : 'Panel del Empleado',
  };

  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [showLogin, setShowLogin] = useState(true);
  const [activeTab, setActiveTab] = useState('empleados');

  // Destino pendiente al que llevar a la persona cuando toca una notificación.
  // OJO, aquí estaba el fallo de la primera versión: si se cambiaba la pestaña nada más
  // arrancar, el propio proceso de login la volvía a poner en "horario" un instante después y
  // el salto al chat se perdía. Por eso ahora se ANOTA el destino y se aplica más abajo, solo
  // cuando el login ya ha terminado y los datos están cargados.
  const [destinoNotificacion, setDestinoNotificacion] = useState(null);



  // Se contemplan los dos casos posibles:
  //  1. La app estaba abierta -> el service worker manda un aviso interno (postMessage).
  //  2. La app estaba cerrada -> se abre con "?ir=chat" en la dirección y lo leemos aquí.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const destino = params.get('ir');
      if (destino) {
        setDestinoNotificacion(destino);
        // Limpiamos la dirección para que al recargar no vuelva a saltar al chat.
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (_) {}

    if (!('serviceWorker' in navigator)) return;
    const alRecibir = (event) => {
      if (event.data?.horiaIr) setDestinoNotificacion(event.data.horiaIr);
    };
    navigator.serviceWorker.addEventListener('message', alRecibir);
    return () => navigator.serviceWorker.removeEventListener('message', alRecibir);
  }, []);

  const [employees, setEmployees] = useState([]);
  asignarColoresFichas(employees); // colores de las fichas sin repetir (se recalcula en cada render, es barato)
  const [messages, setMessages] = useState([]);
  // Pantalla completa "Menú y Mapa" (botón junto a "Mi plan"): showMenuMapa abre/cierra la
  // pantalla, menuMapaTab elige entre las dos pestañas de dentro. subiendoMenuFoto/menuFotoError
  // controlan el estado de la subida a Firebase Storage. localVistoMenuMapaId es de qué LOCAL se
  // está viendo/editando la carta y el mapa (con varios locales, hay un selector tipo PowerPoint
  // a la izquierda; con uno solo, siempre es LOCAL_PRINCIPAL y no hace falta elegir nada).
  const [showMenuMapa, setShowMenuMapa] = useState(false);
  const [menuMapaTab, setMenuMapaTab] = useState('menu');
  const [subiendoMenuFoto, setSubiendoMenuFoto] = useState(false);
  const [menuFotoError, setMenuFotoError] = useState(null);
  // Foto de la carta mostrada a pantalla completa (encargado o empleado, se reutiliza el mismo
  // estado porque nunca están abiertos los dos a la vez). null = cerrada.
  const [fotoCartaAmpliada, setFotoCartaAmpliada] = useState(false);
  const [localVistoMenuMapaId, setLocalVistoMenuMapaId] = useState(LOCAL_PRINCIPAL);
  // Herramienta activa del editor del mapa (pestaña "Mapa"): qué se coloca al tocar el plano.
  // 'mesa' por defecto porque es lo primero que se suele colocar.
  const [herramientaMapa, setHerramientaMapa] = useState('mesa');
  // Pared que se está dibujando ahora mismo (array de puntos {x,y}), o null si no se está
  // dibujando ninguna. Se cierra con el botón "Cerrar forma" y pasa a la lista de paredes.
  const [mapaParedEnCurso, setMapaParedEnCurso] = useState(null);
  // Elemento (mesa/silla/barra/entrada) tocado para verlo seleccionado: muestra el panel de
  // abajo con su número (si es mesa) y los botones de mover/eliminar.
  const [mapaElementoSeleccionadoId, setMapaElementoSeleccionadoId] = useState(null);
  // Si no es null, el próximo toque en el plano MUEVE ese elemento ahí, sin importar la
  // herramienta activa — es el modo "mover" de dos pasos: seleccionar, luego tocar destino.
  const [mapaMoviendoId, setMapaMoviendoId] = useState(null);
  const mapaSvgRef = useRef(null);
  // ZONAS (22 ago 2026): a qué zona se asigna lo próximo que coloque el Encargado (null = sin
  // zona), y el texto del campo para crear una zona nueva.
  const [zonaActivaMapaId, setZonaActivaMapaId] = useState(null);
  const [nuevaZonaMapaNombre, setNuevaZonaMapaNombre] = useState('');
  // Filtro de zona en la vista de solo lectura del empleado ("Todas | Salón | Terraza | ...").
  const [filtroZonaEmpleado, setFiltroZonaEmpleado] = useState(null);
  // MODO APRENDIZAJE (22 ago 2026): quiz para que el empleado aprenda dónde está cada mesa.
  const [modoAprendizaje, setModoAprendizaje] = useState(false);
  const [aprendizajePreguntas, setAprendizajePreguntas] = useState([]);
  const [aprendizajeIndice, setAprendizajeIndice] = useState(0);
  const [aprendizajeAciertos, setAprendizajeAciertos] = useState(0);
  const [aprendizajeFeedback, setAprendizajeFeedback] = useState(null); // 'correcto' | 'incorrecto' | null
  // Qué chat está abierto en pantalla completa: null = se ve la lista de chats,
  // 'grupal' = el chat de grupo, o el ID del contacto si es una conversación privada.
  // FALLO REAL ARREGLADO (22 ago 2026): antes guardaba el NOMBRE del contacto, así que si se
  // despedía a un empleado (ej. Mario) y se contrataba a otro con el mismo nombre, abrir "Mario"
  // desde la lista de chats mezclaba las conversaciones de los dos, porque todo se comparaba por
  // nombre. Ahora se guarda el ID real de la ficha (o un id sintético "hist-Nombre" para
  // contactos históricos sin ficha actual), y el nombre a mostrar se busca en cada render — ver
  // sendPrivateMessage y renderChatAbierto más abajo para el resto del arreglo.
  const [chatAbierto, setChatAbierto] = useState(null);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [newPrivateMessage, setNewPrivateMessage] = useState('');
  const [schedule, setSchedule] = useState({});
  const [scheduleHistory, setScheduleHistory] = useState([]);
  // Se sube cada vez que el Encargado cambia a mano un horario (editar, intercambio, cobertura,
  // día libre aprobado). Si ese horario ya estaba publicado, el historial se pone al día para que
  // los turnos del mes (informe PDF, vista del Dueño) cuenten lo que de verdad se trabaja.
  const [cambiosTrasPublicar, setCambiosTrasPublicar] = useState(0);
  const [requests, setRequests] = useState([]);
  const [specialDays, setSpecialDays] = useState({});
  const [schedulePublished, setSchedulePublished] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Candado de verdad contra doble clic, aparte del estado `generating`. El estado de React NO
  // se actualiza al instante — hay un hueco de un render entero en el que el botón todavía no
  // está desactivado si se pulsa dos veces muy rápido, y con eso dos generaciones podrían
  // arrancar a la vez, calcular cada una su propio reparto por separado y acabar mezclando sus
  // resultados sin darse cuenta (sospecha real de un fallo del 13 ago 2026: gente con turnos
  // reales en dos locales el mismo día, sin que el algoritmo en sí pudiera producirlo solo).
  // Un ref cambia en el momento, sin esperar a ningún render, así que sí bloquea la segunda
  // pulsación aunque llegue en el mismo instante que la primera.
  const generandoRef = useRef(false);
  // Panel "Personas trabajando por día" en la pestaña Horario: empieza comprimido para no
  // ocupar tanto espacio nada más entrar; un botón lo despliega si hace falta el detalle.
  const [resumenDiaAbierto, setResumenDiaAbierto] = useState(false);
  const [personasPorDiaAbierto, setPersonasPorDiaAbierto] = useState(false);
  // Burbuja de "día especial" (10 oct 2026): {day, sc: solo almuerzo, sn: solo cena, cp: día completo, nuevo}.
  const [diaEspecialModal, setDiaEspecialModal] = useState(null);
  const evalDiaEspecialRef = useRef({ key: null, val: null });
  // Cuenta las veces que se genera un horario: al cambiar, las casillas entran con su animación.
  const [vueltaHorario, setVueltaHorario] = useState(0);
  // Pantalla completa del horario editable del Encargado (24 ago 2026, mismo patrón que ya
  // funciona bien en la Vista del Dueño): reutiliza la MISMA tabla editable tal cual, solo
  // cambia el contenedor a fullscreen + rotación — así no hay ningún riesgo de romper los clics
  // que abren el modal de descanso, porque no se toca esa lógica en absoluto.
  const [encargadoHorarioPantallaCompleta, setEncargadoHorarioPantallaCompleta] = useState(false);
  // Densidad de las filas SOLO dentro de la pantalla completa del horario (20 sep 2026, a
  // petición de Fran; simplificado a 2 niveles el mismo día tras verlo en el móvil — "compacto"
  // es ahora el tamaño de base (antes era el más pequeño de 3 opciones) y "mini" es el único
  // extra: encoge lo necesario para que quepan a la vez todos los nombres Y los 7 días sin
  // desplazamiento horizontal. Se recuerda entre sesiones porque es una preferencia personal.
  // La tabla normal (no pantalla completa) NUNCA usa esto — se ve exactamente igual que siempre.
  const [densidadHorarioFull, setDensidadHorarioFull] = useState(
    () => localStorage.getItem('horia_densidad_horario_full') || 'compacto'
  );
  const cambiarDensidadHorarioFull = (d) => {
    setDensidadHorarioFull(d);
    localStorage.setItem('horia_densidad_horario_full', d);
  };
  const abrirEncargadoHorarioPantallaCompleta = async () => {
    setEncargadoHorarioPantallaCompleta(true);
    try {
      if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
      if (window.screen.orientation && window.screen.orientation.lock) await window.screen.orientation.lock('landscape');
    } catch (_) { /* iPhone u otro navegador sin soporte: se sigue en vertical, sin aviso */ }
  };
  const cerrarEncargadoHorarioPantallaCompleta = async () => {
    setEncargadoHorarioPantallaCompleta(false);
    try {
      if (window.screen.orientation && window.screen.orientation.unlock) window.screen.orientation.unlock();
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
    } catch (_) { /* nada que hacer si no estaba en fullscreen */ }
  };
  const [temporada, setTemporada] = useState('normal');
  const [temporadas, setTemporadas] = useState(DEFAULT_TEMPORADAS);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [showConfig, setShowConfig] = useState(false);
  const [showPlanes, setShowPlanes] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [requestDate, setRequestDate] = useState('');
  const [requestNote, setRequestNote] = useState('');
  // Formulario de "pedir cobertura": el encargado dice qué día y qué turno necesita cubrir.
  const [cobDia, setCobDia] = useState('');
  const [cobTurno, setCobTurno] = useState('noche');
  const [cobNota, setCobNota] = useState('');
  const [cobBusy, setCobBusy] = useState(false);
  // Intercambio de turno entre compañeros (20 sep 2026, pestaña Solicitudes del empleado):
  // el empleado elige un día de SU propio horario y a quién se lo propone.
  const [intercDia, setIntercDia] = useState('');
  const [intercCompaneroId, setIntercCompaneroId] = useState('');
  const [intercNota, setIntercNota] = useState('');
  const [intercBusy, setIntercBusy] = useState(false);
  // Bote de propinas (28 oct 2026, pestaña Turnos). `propinas` son los repartos ya publicados
  // (campo `propinas` del documento); el resto es el formulario del Encargado.
  const [propinas, setPropinas] = useState([]);
  const [propForm, setPropForm] = useState(false);
  const [propDesde, setPropDesde] = useState('');
  const [propHasta, setPropHasta] = useState('');
  const [propBote, setPropBote] = useState('');
  const [propJuntos, setPropJuntos] = useState(false);
  const [propOtro, setPropOtro] = useState(undefined);   // undefined = cargando · null = no hay el otro departamento · objeto = sus datos
  const [propBusy, setPropBusy] = useState(false);
  const [propAbierto, setPropAbierto] = useState(null);  // id del reparto desplegado en la lista

  // --- Diálogos con la estética de Horidia ------------------------------------------------
  // Sustituyen a los window.confirm y window.alert del navegador, que sacaban un recuadro gris
  // con la dirección de la web arriba y rompían por completo el aspecto de la app.
  // El truco está en que window.confirm es SÍNCRONO y un modal de React no lo es, así que
  // `confirmar()` y `avisar()` devuelven una promesa: el código sigue leyéndose igual de bien
  // (`if (!(await confirmar(...))) return;`) pero por dentro espera a que la persona pulse.
  // --- VARIOS LOCALES -------------------------------------------------------------------
  // Un negocio puede tener más de un local (p. ej. "Duo Tapas" y "Terraza"). La plantilla, el
  // chat y las solicitudes siguen siendo COMUNES a todo el negocio: lo único que se multiplica
  // es el horario, porque una persona rota entre locales de una semana a otra.
  // Por dentro, `schedule` sigue siendo "el horario que estoy viendo ahora mismo" — así todo el
  // código que ya existía (generador, historial, vista del Dueño, coberturas) funciona igual sin
  // tocarlo. Los horarios de los demás locales se guardan aparte en `schedulesPorLocal`.
  const [schedulesPorLocal, setSchedulesPorLocal] = useState({});
  // Igual que el horario: `schedulePublished` es el del local que se está viendo, y aquí se
  // aparca el de los demás. Sin esto, publicar el horario de un local marcaba todos como
  // publicados, que era el fallo más visible de la primera fase.
  const [publicadosPorLocal, setPublicadosPorLocal] = useState({});
  // FECHAS REALES DEL HORARIO DE CADA LOCAL (29 oct 2026): { [idLocal]: { Lunes: 'AAAA-MM-DD', … } }.
  // Se fijan al GENERAR (la semana para la que se ha hecho) y al PUBLICAR. Antes no se guardaban y
  // todo lo que tocaba el horario calculaba "la semana que viene" según el día de HOY: con un
  // horario publicado el domingo y retocado el miércoles, el cambio se apuntaba en la semana
  // siguiente (jueves 12 en vez de jueves 5), el turno quitado seguía contando como trabajado y el
  // bote de propinas salía mal. Ver fechasDelHorario().
  const [fechasPorLocal, setFechasPorLocal] = useState({});
  // PUBLICACIÓN PROGRAMADA (13 oct 2026): { [idLocal]: { cuando (ms), fechas, semanaKey, creadoEn } }.
  // La publica el servidor (Cloud Function `publicarHorariosProgramados`) a su hora, aunque la app
  // esté cerrada. Aquí solo se guarda y se muestra.
  const [programadoPorLocal, setProgramadoPorLocal] = useState({});
  const [programarAbierto, setProgramarAbierto] = useState(false);
  const [progFecha, setProgFecha] = useState('');
  const [progHora, setProgHora] = useState('14:00');
  const [progError, setProgError] = useState('');
  const [progGuardando, setProgGuardando] = useState(false);
  const [localActivoId, setLocalActivoId] = useState(LOCAL_PRINCIPAL);
  // Local que el EMPLEADO está mirando en su propio móvil, totalmente independiente del que
  // tenga abierto el Encargado en su pantalla. Antes no existía: el empleado leía directamente
  // del horario "activo" compartido (`schedule`), así que cuando el Encargado cambiaba de local
  // para editar, el móvil del empleado cambiaba de horario solo, en tiempo real — fallo grave
  // reportado por Fran el 8 ago 2026. Ahora cada uno tiene su propia elección.
  const [miLocalVistaId, setMiLocalVistaId] = useState(LOCAL_PRINCIPAL);
  const miLocalInicializadoRef = useRef(false);
  // Selección de locales para el botón "Generar seleccionados" del panel de Generar horario —
  // solo tiene sentido cuando hay más de dos locales (con uno o dos ya están los otros botones).
  const [localesSeleccionados, setLocalesSeleccionados] = useState([]);
  const toggleLocalSeleccionado = (id) => {
    setLocalesSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const locales = listaLocales(config);
  const hayVariosLocales = locales.length > 1;
  const localActivo = locales.find(l => l.id === localActivoId) || locales[0];

  // GASTOS DEL NEGOCIO: un único bloque de estado sirve tanto para la pantalla del Encargado/Jefe
  // de Cocina (lectura y escritura) como para la vista del Dueño (solo lectura) — los dos viven en
  // el mismo componente y leen del mismo restaurante, solo cambia DE QUÉ LOCAL se piden los datos.
  const [gastosMesVisto, setGastosMesVisto] = useState(() => fechaLocalHoy().slice(0, 7)); // 'YYYY-MM'
  const [gastos, setGastos] = useState([]);
  const [gastoCategoria, setGastoCategoria] = useState('pedidos');
  const [gastoConcepto, setGastoConcepto] = useState('');
  const [gastoImporte, setGastoImporte] = useState('');
  const [gastoFecha, setGastoFecha] = useState(() => fechaLocalHoy()); // 'YYYY-MM-DD'
  const [gastoFoto, setGastoFoto] = useState(null);
  const [guardandoGasto, setGuardandoGasto] = useState(false);
  const [gastoError, setGastoError] = useState(null);
  const [totalGastosAnimado, setTotalGastosAnimado] = useState(0);
  const totalGastosAnimadoAnteriorRef = useRef(0);

  // Qué sección de la ficha está abierta para cada empleado: { [empId]: 'turnos'|'local'|... }
  // Solo una a la vez, para que la ficha no vuelva a convertirse en un scroll infinito.
  const [seccionFicha, setSeccionFicha] = useState({});

  const [duenoEmpAbierto, setDuenoEmpAbierto] = useState(null);
  // Vista del Dueño (22 ago 2026): al pulsar un local se abre su detalle a pantalla completa
  // (horario, carta, mapa, solicitudes y plantilla de ese local). null = no hay ninguno abierto.
  const [duenoLocalAbierto, setDuenoLocalAbierto] = useState(null);
  // Vista de horario a pantalla completa dentro del detalle de un local (22 ago 2026, de prueba
  // solo en Vista del Dueño): pedido de Fran porque en el recuadro normal hay que deslizar mucho
  // para ver todos los días. No se puede forzar la orientación del móvil desde una web (no está
  // soportado de forma fiable en todos los navegadores, sobre todo en iPhone), así que en vez de
  // prometer algo que puede fallar en silencio, se muestra un aviso pidiendo girar el móvil a
  // mano — la pantalla completa en sí ya ayuda aunque no se gire.
  const [duenoHorarioPantallaCompleta, setDuenoHorarioPantallaCompleta] = useState(false);
  const [duenoHistorialAbierto, setDuenoHistorialAbierto] = useState(null);
  const [dialogo, setDialogo] = useState(null);
  const resolverDialogoRef = useRef(null);

  const confirmar = (opciones) => new Promise((resolve) => {
    resolverDialogoRef.current = resolve;
    setDialogo({ tipo: 'confirmar', icono: '❓', textoOk: 'Sí, adelante', textoCancelar: 'Cancelar', ...opciones });
  });
  const avisar = (opciones) => new Promise((resolve) => {
    resolverDialogoRef.current = resolve;
    setDialogo({ tipo: 'aviso', icono: 'ℹ️', textoOk: 'Entendido', ...opciones });
  });
  const cerrarDialogo = (valor) => {
    setDialogo(null);
    const r = resolverDialogoRef.current;
    resolverDialogoRef.current = null;
    if (r) r(valor);
  };

  // === ASISTENTE "PREGÚNTALE A HORIDIA" (6 sep 2026) ===
  // Todo el trabajo de verdad (hablar con Gemini, buscar el dato real) vive en la Cloud
  // Function `preguntarHoria` — aquí solo se gestiona la conversación en pantalla.
  const [asistenteAbierto, setAsistenteAbierto] = useState(false);
  const [asistenteMensajes, setAsistenteMensajes] = useState([]);
  // Horito siempre habla con el perfil que está abierto (12 oct 2026): nadie puede elegir ser otra persona.
  const [asistentePregunta, setAsistentePregunta] = useState('');
  const [asistenteEnviando, setAsistenteEnviando] = useState(false);
  const [asistenteEscuchando, setAsistenteEscuchando] = useState(false);
  const asistenteMensajesRef = useRef(null);
  // Autoscroll del chat (20 sep 2026 — antes había que bajar a mano con cada mensaje nuevo,
  // un coñazo en una conversación de más de 2-3 líneas). Baja al fondo cada vez que se añade un
  // mensaje o que aparece/desaparece el "Pensando…".
  useEffect(() => {
    if (asistenteMensajesRef.current) {
      asistenteMensajesRef.current.scrollTop = asistenteMensajesRef.current.scrollHeight;
    }
  }, [asistenteMensajes, asistenteEnviando]);

  const abrirAsistente = () => {
    setAsistenteAbierto(true);
    // Aviso legal obligatorio (Reglamento de IA de la UE, art. 50): informar de que se habla
    // con una máquina, la primera vez que se usa — no escondido en ningún otro sitio.
    if (!asistenteAvisoVisto) {
      setAsistenteMensajes((prev) => [
        ...prev,
        { rol: 'aviso', texto: '¡Hola! Soy Horito, tu asistente de inteligencia artificial de Horidia. Puedo ayudarte con vacaciones, turnos, coberturas y días libres.' },
      ]);
      localStorage.setItem(asistenteAvisoKey, '1');
      setAsistenteAvisoVisto(true);
    }
  };

  // MICRÓFONO (4 oct 2026, a petición de Fran). ANTES: se lanzaba el reconocimiento de voz a pelo
  // y, si el navegador no tenía el permiso, salía "revísalo en los ajustes del navegador" — había
  // que irse a Ajustes → la app → Permisos a mano. AHORA: primero se pide el permiso con el
  // recuadro normal del navegador ("¿Permitir que Horidia use el micrófono?"), que aparece solo
  // mientras el permiso no se ha concedido ni bloqueado. Solo si la persona ya lo bloqueó antes
  // (ni una web ni una app puede saltarse eso: ningún navegador deja volver a preguntar), se
  // explica el camino más corto para desbloquearlo.
  const pidiendoMicRef = useRef(false);
  // PANTALLA DE MICRÓFONO BLOQUEADO (5 oct 2026, a petición de Fran): antes era un bloque de texto
  // largo; ahora son pasos cortos y numerados. OJO: para una web instalada como app, el micrófono
  // NO aparece en Ajustes → Aplicaciones → Horidia → Permisos (esa pantalla solo lista lo que la app
  // declara, y solo declara las notificaciones): el permiso vive en los ajustes de Chrome para ese
  // sitio. Por eso el camino que se enseña en Android es el de Chrome. `motivo === 'sistema'` es
  // el caso en que es Android quien bloquea el micrófono a Chrome entero (ahí sí es Aplicaciones →
  // Chrome → Permisos).
  const avisarMicrofonoBloqueado = (motivo) => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const esAndroid = /android/i.test(ua);
    const esIOS = /iphone|ipad|ipod/i.test(ua);
    const sitioWeb = (typeof window !== 'undefined' && window.location && window.location.hostname) || 'Horidia';
    let pasos;
    if (esAndroid && motivo === 'sistema') {
      pasos = ['Abre los Ajustes del móvil', 'Entra en Aplicaciones y busca Chrome', 'Pulsa Permisos y luego Micrófono', 'Elige «Permitir solo si la app está en uso»'];
    } else if (esAndroid) {
      pasos = ['Abre Chrome', 'Pulsa ⋮ y entra en Configuración', 'Configuración de sitios → Micrófono', `Busca ${sitioWeb} y pulsa Permitir`];
    } else if (esIOS) {
      pasos = ['Abre los Ajustes del iPhone', 'Entra en Safari', 'Pulsa Micrófono', 'Elige Preguntar o Permitir'];
    } else {
      pasos = ['Pulsa el candado junto a la dirección de la web', 'Entra en Permisos', 'Micrófono → Permitir'];
    }
    avisar({
      icono: '🎙️',
      titulo: 'Activa el micrófono',
      lineas: ['Está bloqueado. Sigue estos pasos y vuelve a pulsar el micrófono:'],
      pasos,
      textoOk: 'Entendido',
    });
  };
  const iniciarEscuchaAsistente = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      avisar({ icono: '🎙️', titulo: 'No disponible en este navegador', lineas: ['Puedes escribir la pregunta a mano en su lugar.'] });
      return;
    }
    if (pidiendoMicRef.current) return;
    pidiendoMicRef.current = true;
    try {
      // 1) ¿Cómo está el permiso? (no todos los navegadores dejan consultarlo: si falla, se sigue)
      let estado = 'desconocido';
      try {
        const permiso = await navigator.permissions.query({ name: 'microphone' });
        estado = permiso.state;
      } catch (_) {}
      // (5 oct 2026) Antes, si la consulta decía 'denied' se mostraba el aviso de bloqueo SIN llegar
      // a pedir nada, y esa consulta no siempre acierta (sobre todo en apps instaladas). Ahora se
      // intenta abrir el micrófono igualmente: si de verdad está bloqueado, falla al instante y se
      // explica; si no lo estaba, el navegador pregunta como debe.
      // 2) Si aún no está concedido, pedirlo AHORA con el recuadro del navegador. Se abre el
      // micrófono un instante solo para provocar la pregunta y se suelta enseguida.
      if (estado !== 'granted' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const flujo = await navigator.mediaDevices.getUserMedia({ audio: true });
          flujo.getTracks().forEach((t) => t.stop());
        } catch (e) {
          const msgError = String((e && e.message) || '').toLowerCase();
          if (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError' || e.name === 'PermissionDeniedError')) {
            // "Permission dismissed": cerró el recuadro sin elegir. No es un bloqueo, no hay nada que explicar.
            if (msgError.includes('dismiss')) return;
            avisarMicrofonoBloqueado(msgError.includes('system') ? 'sistema' : 'sitio');
          } else if (e && (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError')) {
            avisar({ icono: '🎙️', titulo: 'No encuentro el micrófono', lineas: ['No detecto ningún micrófono en este dispositivo. Puedes escribir la pregunta a mano.'] });
          } else {
            avisar({ icono: '🎙️', titulo: 'No se ha podido usar el micrófono', lineas: ['Prueba otra vez, o escribe la pregunta a mano.'] });
          }
          return;
        }
      }
    } finally {
      pidiendoMicRef.current = false;
    }
    // 3) Con el permiso ya concedido, ahora sí: escuchar.
    const recog = new SR();
    recog.lang = 'es-ES';
    recog.interimResults = false;
    recog.maxAlternatives = 1;
    setAsistenteEscuchando(true);
    recog.onresult = (e) => setAsistentePregunta(e.results[0][0].transcript);
    recog.onend = () => setAsistenteEscuchando(false);
    recog.onerror = (e) => {
      setAsistenteEscuchando(false);
      // 'not-allowed' aquí (ya con el permiso concedido) es raro, pero puede pasar si el
      // navegador bloquea solo el reconocimiento de voz: se explica igual que el bloqueo normal.
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') { avisarMicrofonoBloqueado(); return; }
      const motivos = {
        'no-speech': 'No he oído nada. Prueba a hablar más cerca del móvil.',
        'audio-capture': 'No encuentro ningún micrófono en este dispositivo.',
        'network': 'Fallo de conexión mientras escuchaba. Prueba otra vez.',
      };
      avisar({ icono: '🎙️', titulo: 'No se ha podido usar el micrófono', lineas: [motivos[e.error] || 'Prueba otra vez, o escribe la pregunta a mano.'] });
    };
    try { recog.start(); }
    catch (_) {
      setAsistenteEscuchando(false);
      avisar({ icono: '🎙️', titulo: 'No se ha podido usar el micrófono', lineas: ['Prueba otra vez, o escribe la pregunta a mano.'] });
    }
  };

  const enviarPreguntaAsistente = async () => {
    const texto = asistentePregunta.trim();
    if (!texto || asistenteEnviando) return;
    setAsistenteMensajes((prev) => [...prev, { rol: 'usuario', texto }]);
    setAsistentePregunta('');
    setAsistenteEnviando(true);
    try {
      const llamar = httpsCallable(functionsInstance, 'preguntarHoria');
      const historialCorto = asistenteMensajes
        .filter((m) => m.rol === 'usuario' || m.rol === 'asistente')
        .slice(-4);
      const resultado = await llamar({
        pregunta: texto,
        coleccion: FIRESTORE_COLLECTION,
        restaurantDocId,
        // Siempre el perfil que está abierto: un empleado habla como él mismo y el Encargado como Encargado.
        empId: userRole === 'empleado' ? (selectedEmployeeId || null) : null,
        historial: historialCorto,
      });
      setAsistenteMensajes((prev) => [...prev, { rol: 'asistente', texto: resultado.data?.respuesta || 'No he podido responder.' }]);
    } catch (e) {
      console.error('[asistente] error al preguntar:', e);
      // (31 oct 2026) Límite diario de preguntas por persona: el servidor lo dice con
      // 'resource-exhausted' y su propio texto, que se muestra tal cual.
      const limite = e?.code === 'functions/resource-exhausted';
      setAsistenteMensajes((prev) => [...prev, { rol: 'asistente', texto: limite
        ? (e?.message || 'Por hoy ya has llegado al máximo de preguntas. Mañana sigo contigo.')
        : 'No he podido responder ahora mismo. Inténtalo de nuevo en un momento.' }]);
    } finally {
      setAsistenteEnviando(false);
    }
  };

  // Burbuja flotante + panel de chat. Es una función normal (no un componente con mayúscula
  // usado como <RenderAsistenteHoria />) a propósito: así React no la desmonta y remonta en
  // cada render, que perdería el foco del campo de texto mientras escribes.
  const renderAsistenteHoria = () => (
    <>
      <button
        onClick={() => (asistenteAbierto ? setAsistenteAbierto(false) : abrirAsistente())}
        title="Horito, el asistente de Horidia"
        style={{
          position: 'fixed', bottom: '32px', right: '16px', width: '76px', height: '76px',
          borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0,
          background: 'transparent',
          boxShadow: asistenteAbierto ? '0 4px 14px rgba(0,0,0,0.25)' : 'none',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        <svg viewBox="260 230 740 740" width="76" height="76" style={{ display: 'block' }}>
              <style>{`
                @keyframes horiaFlotarApp { 0%,100%{transform:translateY(0px);} 50%{transform:translateY(-6px);} }
                @keyframes horiaPestaneoApp { 0%,92%,100%{transform:scaleY(1);} 96%{transform:scaleY(0.08);} }
                @keyframes horiaHablarApp { 0%,100%{transform:scaleY(0.3);} 15%{transform:scaleY(1);} 30%{transform:scaleY(0.5);} 45%{transform:scaleY(0.9);} 60%{transform:scaleY(0.35);} 75%{transform:scaleY(0.75);} 90%{transform:scaleY(0.4);} }
                .horia-flotar-app { animation: horiaFlotarApp 3s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
                .horia-parpado-app { animation: horiaPestaneoApp 2s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
                .horia-boca-hablar-app { transition: opacity 0.35s ease; }
                .horia-boca-hablar-app rect { animation: horiaHablarApp 0.9s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
              `}</style>
              <defs>
                <linearGradient id="horiaHeadGradApp" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#7C4FE9" />
                  <stop offset="100%" stopColor="#5732C9" />
                </linearGradient>
              </defs>

              <g className="horia-flotar-app">
                {/* Asistente calcado de mascota-horia.png, ya no como <image> — cada pieza (anillo,
                    cabeza, pajarita, ojos, boca, diamante) trazada directamente sobre los píxeles
                    de la imagen original y reconstruida como paths reales. Al ser vector de
                    verdad (no una foto), los ojos son piezas propias: el pestañeo escala el
                    grupo del ojo, no tapa nada encima con una elipse del color de la piel.
                    Ya no desaparece detrás de una "✕" al abrir el chat: se queda siempre visible. */}

                {/* Círculo lavanda de fondo */}
                <circle cx="627.1" cy="597.4" r="307" fill="#E8E0F8" />

                {/* Anillo violeta (con sus dos huecos reales, tal cual la imagen). Se probó a
                    girarlo (primero con CSS, luego con animateTransform) pero no acababa de
                    verse bien, así que se deja fijo. */}
                <path d="M804.5,899.7 A350.5,350.5 0 1 1 885.1,360.2" fill="none" stroke="#5F19FD" strokeWidth="19" strokeLinecap="round" />
                <path d="M973.2,652.8 A350.5,350.5 0 0 1 924.7,782.6" fill="none" stroke="#5F19FD" strokeWidth="19" strokeLinecap="round" />

                {/* Cabeza: cuerpo con degradado + banda lateral más oscura (el "canto" 3D) */}
                <path d="M802.0,420.0 L777.0,403.0 L750.0,393.0 L723.0,389.0 L701.0,390.0 L492.0,418.0 L462.0,430.0 L436.0,450.0 L421.0,468.0 L410.0,488.0 L402.0,514.0 L400.0,539.0 L412.0,716.0 L425.0,749.0 L441.0,770.0 L464.0,789.0 L492.0,802.0 L516.0,807.0 L541.0,807.0 L545.0,816.0 L544.0,823.0 L548.0,827.0 L566.0,822.0 L591.0,808.0 L644.0,808.0 L676.0,824.0 L689.0,827.0 L693.0,824.0 L695.0,810.0 L758.0,808.0 L782.0,797.0 L830.0,751.0 L847.0,722.0 L856.0,692.0 L858.0,666.0 L846.0,492.0 L829.0,451.0 Z M350.0,797.0 L350.0,828.0 L384.0,864.0 L429.0,899.0 L464.0,899.0 L434.0,881.0 L402.0,856.0 L372.0,826.0 Z M457.0,300.0 L424.0,300.0 L392.0,325.0 L350.0,369.0 L350.0,398.0 L369.0,373.0 L398.0,343.0 L436.0,312.0 Z" fill="url(#horiaHeadGradApp)" />
                <path d="M825.0,449.0 L817.0,450.0 L817.0,455.0 L811.0,460.0 L813.0,474.0 L819.0,477.0 L815.0,480.0 L810.0,657.0 L802.0,692.0 L789.0,716.0 L766.0,742.0 L746.0,757.0 L725.0,767.0 L697.0,772.0 L698.0,740.0 L693.0,734.0 L678.0,737.0 L652.0,751.0 L650.0,810.0 L670.0,821.0 L691.0,826.0 L695.0,809.0 L747.0,810.0 L777.0,800.0 L828.0,753.0 L846.0,723.0 L857.0,683.0 L849.0,513.0 L835.0,463.0 L830.0,464.0 L832.0,457.0 Z" fill="#432BBA" />

                {/* Pajarita */}
                <path d="M590.0,742.0 L528.0,771.0 L590.0,800.0 Z" fill="#5B38E3" />
                <path d="M670.0,742.0 L732.0,771.0 L670.0,800.0 Z" fill="#3D25B2" />
                <rect x="590" y="742" width="80" height="58" rx="14" fill="#351C9C" />

                {/* Ojo izquierdo — pestañea de verdad (escala su propio grupo, no una elipse encima) */}
                <g className="horia-parpado-app">
                  <path d="M538.0,521.0 L515.0,519.0 L502.0,522.0 L486.0,531.0 L475.0,542.0 L467.0,556.0 L463.0,569.0 L463.0,587.0 L466.0,599.0 L474.0,613.0 L486.0,624.0 L496.0,629.0 L508.0,632.0 L521.0,632.0 L538.0,627.0 L558.0,612.0 L570.0,593.0 L573.0,583.0 L574.0,566.0 L569.0,548.0 L556.0,531.0 Z" fill="#FFFFFF" />
                  <path d="M526.0,541.0 L514.0,545.0 L505.0,552.0 L498.0,564.0 L496.0,578.0 L499.0,589.0 L507.0,600.0 L519.0,606.0 L531.0,607.0 L544.0,602.0 L553.0,594.0 L560.0,579.0 L560.0,565.0 L555.0,554.0 L547.0,546.0 L538.0,542.0 Z" fill="#332F54" />
                  <path d="M547.0,554.0 L539.0,554.0 L535.0,557.0 L533.0,561.0 L533.0,566.0 L536.0,571.0 L543.0,573.0 L551.0,568.0 L552.0,560.0 L550.0,556.0 Z" fill="#FFFFFF" />
                </g>
                {/* Ojo derecho */}
                <g className="horia-parpado-app">
                  <path d="M719.0,503.0 L697.0,505.0 L678.0,515.0 L663.0,532.0 L656.0,549.0 L654.0,568.0 L661.0,591.0 L678.0,609.0 L695.0,616.0 L717.0,616.0 L739.0,606.0 L756.0,588.0 L764.0,568.0 L764.0,544.0 L755.0,524.0 L738.0,509.0 Z" fill="#FFFFFF" />
                  <path d="M715.0,526.0 L700.0,533.0 L691.0,544.0 L688.0,553.0 L688.0,568.0 L693.0,579.0 L701.0,587.0 L714.0,592.0 L722.0,592.0 L734.0,588.0 L744.0,580.0 L751.0,567.0 L752.0,552.0 L747.0,539.0 L742.0,533.0 L731.0,527.0 Z" fill="#332F54" />
                  <path d="M732.0,538.0 L726.0,542.0 L724.0,549.0 L725.0,553.0 L729.0,557.0 L735.0,558.0 L742.0,553.0 L744.0,547.0 L739.0,539.0 Z" fill="#FFFFFF" />
                </g>

                {/* Boca: sonrisa tranquila normalmente. Con el chat abierto (y sin estar "escribiendo"),
                    una sonrisa más abierta y emocionada — boca abierta enseñando dientes, en vez de la
                    sonrisa cerrada de siempre. Esta expresión es nueva (no calcada de la foto original,
                    que solo tiene la sonrisa cerrada), pensada para transmitir más ganas al abrir el chat. */}
                {!asistenteEnviando && !asistenteAbierto && (
                  <path d="M695.0,663.0 L692.0,659.0 L686.0,658.0 L653.0,674.0 L618.0,682.0 L584.0,682.0 L550.0,674.0 L544.0,678.0 L543.0,686.0 L552.0,694.0 L577.0,702.0 L592.0,704.0 L622.0,703.0 L646.0,698.0 L676.0,685.0 L693.0,673.0 Z" fill="#FFFFFF" />
                )}
                {!asistenteEnviando && asistenteAbierto && (
                  <g>
                    <ellipse cx="618" cy="688" rx="52" ry="27" fill="#332F54" />
                    <path d="M568,672 Q618,648 668,672 Q618,668 568,672 Z" fill="#FFFFFF" />
                  </g>
                )}

                {/* Boca "escribiendo": solo mientras asistenteEnviando */}
                {asistenteEnviando && (
                  <g className="horia-boca-hablar-app">
                    <rect x="520" y="650" width="240" height="70" rx="30" fill="#6440E6" />
                    <rect x="578" y="668" width="98" height="34" rx="16" fill="#F5F3FF" />
                  </g>
                )}

                {/* Diamante naranja */}
                <path d="M879.0,775.0 L865.0,774.0 L859.0,777.0 L805.0,831.0 L800.0,839.0 L799.0,849.0 L802.0,857.0 L835.0,890.0 L912.0,811.0 L916.0,810.0 L924.0,818.0 Z" fill="#FDA935" />
                <path d="M915.0,810.0 L836.0,891.0 L860.0,914.0 L872.0,917.0 L884.0,913.0 L939.0,858.0 L943.0,846.0 L941.0,837.0 Z" fill="#FF9335" />
              </g>
            </svg>
      </button>
      {asistenteAbierto && (
        <div style={{
          position: 'fixed', bottom: '118px', right: '16px', width: '320px', maxWidth: 'calc(100vw - 32px)',
          height: '440px', maxHeight: 'calc(100vh - 200px)', background: C.white, borderRadius: '16px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
          zIndex: 1000, overflow: 'hidden',
        }}>
          <div style={{ background: C.primary, color: C.white, padding: '10px 14px', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <span><Ico n="chat" /> Horito</span>
          </div>
          <div ref={asistenteMensajesRef} style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {asistenteMensajes.length === 0 && (
              <p style={{ fontSize: '13px', color: C.textMuted, textAlign: 'center', marginTop: '20px' }}>
                Pregúntame cuándo trabajas o libras, quién trabaja un día, tus vacaciones, quién es el encargado… o pide una cobertura o un día libre.
              </p>
            )}
            {asistenteMensajes.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.rol === 'usuario' ? 'flex-end' : 'flex-start',
                background: m.rol === 'usuario' ? C.primary : (m.rol === 'aviso' ? C.border : C.primaryPale),
                color: m.rol === 'usuario' ? C.white : C.text,
                padding: '8px 12px', borderRadius: '12px', fontSize: '13px', maxWidth: '85%',
                fontStyle: m.rol === 'aviso' ? 'italic' : 'normal',
              }}>
                {m.texto}
              </div>
            ))}
            {asistenteEnviando && <div style={{ fontSize: '12px', color: C.textMuted, alignSelf: 'flex-start' }}>Pensando…</div>}
          </div>
          <div style={{ display: 'flex', gap: '6px', padding: '10px', borderTop: `1px solid ${C.border}` }}>
            <button onClick={iniciarEscuchaAsistente} disabled={asistenteEscuchando}
              style={{
                width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${C.border}`,
                background: asistenteEscuchando ? C.accent : C.white, color: asistenteEscuchando ? C.white : C.primary,
                cursor: 'pointer', flexShrink: 0, fontSize: '16px',
              }}>
              <Ico n="mic" />
            </button>
            <input
              value={asistentePregunta}
              onChange={(e) => setAsistentePregunta(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enviarPreguntaAsistente(); }}
              placeholder="Escribe tu pregunta…"
              style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '0 10px', fontSize: '13px', minWidth: 0 }}
            />
            <button onClick={enviarPreguntaAsistente} disabled={asistenteEnviando || !asistentePregunta.trim()}
              style={{
                width: '38px', height: '38px', borderRadius: '10px', border: 'none', background: C.primary,
                color: C.white, cursor: 'pointer', flexShrink: 0, fontSize: '16px',
                opacity: (asistenteEnviando || !asistentePregunta.trim()) ? 0.5 : 1,
              }}>
              <Ico n="send" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  // Cambiar de local: se aparca el horario que se estaba viendo en su hueco y se carga el del
  // local nuevo. Si ese local aún no tiene horario, se abre vacío y se genera desde Empleados.
  // SALVAGUARDA ESTRUCTURAL (12 ago 2026): `schedulesPorLocal[localActivoId]` tiene que reflejar
  // SIEMPRE lo mismo que `schedule` — es lo que consultan las comprobaciones cruzadas entre
  // locales ("¿quién ya está ocupado en otro sitio?"). El fallo real de hoy fue justo que un
  // sitio del código escribía en `schedule` sin acordarse de sincronizar también el otro, y la
  // comprobación cruzada se quedó mirando un hueco vacío — 8 de 13 personas acabaron trabajando
  // en dos locales la misma semana sin que nadie lo pillara hasta que Fran lo vio en el móvil.
  // En vez de confiar en que CADA sitio que toque `schedule` se acuerde de hacer también el
  // segundo guardado (frágil: basta con que se añada un sitio nuevo mañana y se olvide), este
  // efecto los mantiene sincronizados siempre, solo, sin que nadie tenga que acordarse.
  useEffect(() => {
    if (!localActivoId) return;
    setSchedulesPorLocal(prev => (prev[localActivoId] === schedule ? prev : { ...prev, [localActivoId]: schedule }));
    setPublicadosPorLocal(prev => (prev[localActivoId] === schedulePublished ? prev : { ...prev, [localActivoId]: schedulePublished }));
  }, [schedule, schedulePublished, localActivoId]);

  const cambiarDeLocal = (nuevoId) => {
    if (!nuevoId || nuevoId === localActivoId) return;
    setSchedulesPorLocal(prev => ({ ...prev, [localActivoId]: schedule }));
    setPublicadosPorLocal(prev => ({ ...prev, [localActivoId]: schedulePublished }));
    setSchedule(schedulesPorLocal[nuevoId] || {});
    setSchedulePublished(!!publicadosPorLocal[nuevoId]);
    setLocalActivoId(nuevoId);
  };

  // FECHAS REALES DEL HORARIO (29 oct 2026, fallo real visto por Fran). Devuelve { Lunes: 'AAAA-MM-DD', …,
  // Domingo } de la semana a la que pertenece el horario de ese local, que NO siempre es "la semana
  // que viene": un horario publicado el domingo para el lunes 2 sigue siendo el del 2 al 8 aunque
  // se retoque el miércoles 4 (getWeekDates() ese día ya daría del 9 al 15). Todo lo que toca el
  // horario en vigor (quitar/añadir turnos, días libres, intercambios, coberturas, cabeceras, HOY,
  // publicar, programar y el historial) usa estas fechas. Generar un horario NUEVO sigue usando
  // getWeekDates(), porque ese sí es siempre para la semana que viene.
  //  1) Las fechas guardadas del local (se fijan al generar y al publicar).
  //     Si es un borrador sin publicar y su semana ya terminó entera, se prepara para la próxima.
  //  2) Documentos de antes de este arreglo, con el horario publicado: las fechas de su última
  //     publicación en el historial (que siempre se guardaron).
  //  3) Si no hay nada de lo anterior: la semana que viene, como se hacía siempre.
  const fechasDelHorario = (localId = localActivoId) => {
    const lid = localId || LOCAL_PRINCIPAL;
    const esActivo = lid === (localActivoId || LOCAL_PRINCIPAL);
    const publicado = esActivo ? !!schedulePublished : publicadosPorLocal[lid] === true;
    const guardadas = fechasPorLocal?.[lid];
    if (guardadas && typeof guardadas.Lunes === 'string' && typeof guardadas.Domingo === 'string'
      && DAYS.every(d => typeof guardadas[d] === 'string')) {
      if (publicado || guardadas.Domingo >= toDateStr(new Date())) return guardadas;
      return getWeekDates();
    }
    if (publicado) {
      let ultima = null;
      (scheduleHistory || []).forEach(h => {
        if ((h.localId || LOCAL_PRINCIPAL) !== lid) return;
        if (!ultima || (h.publishedAtTimestamp || 0) > (ultima.publishedAtTimestamp || 0)) ultima = h;
      });
      const f = ultima ? fechasDeEntradaHistorial(ultima) : null;
      if (f && f.Lunes && f.Domingo) return f;
    }
    return getWeekDates();
  };
  // Fija (o borra, con null) las fechas guardadas de un local.
  const fijarFechasHorario = (localId, fechas) => {
    const lid = localId || LOCAL_PRINCIPAL;
    setFechasPorLocal(prev => {
      if (!fechas) {
        if (!(lid in prev)) return prev;
        const n = { ...prev }; delete n[lid]; return n;
      }
      if (prev[lid] && DAYS.every(d => prev[lid][d] === fechas[d])) return prev;
      return { ...prev, [lid]: { ...fechas } };
    });
  };

  // Alta, renombrado y borrado de locales. El primero de la lista no se puede borrar: siempre
  // tiene que quedar uno.
  const anadirLocal = () => {
    const actuales = listaLocales(config);
    if (actuales.length >= MAX_LOCALES_POR_RESTAURANTE) return;
    const nuevo = { id: `local_${Date.now()}`, nombre: `Local ${actuales.length + 1}` };
    setConfig(c => ({ ...c, locales: [...actuales, nuevo] }));
  };
  const renombrarLocal = (id, nombre) => {
    setConfig(c => ({ ...c, locales: listaLocales(c).map(l => l.id === id ? { ...l, nombre } : l) }));
  };
  // Cuántas personas quiere el encargado en cada local, como mucho, al generar su horario.
  // 0 o vacío = sin tope (se deja al algoritmo decidir, como hasta ahora). Vive en
  // `config.personasPorLocal = { [localId]: numero }`, junto al resto de ajustes del negocio.
  const setPersonasParaLocal = (id, valor) => {
    const n = Math.max(0, parseInt(valor, 10) || 0);
    setConfig(c => ({ ...c, personasPorLocal: { ...(c.personasPorLocal || {}), [id]: n } }));
  };
  const borrarLocal = async (id) => {
    const actuales = listaLocales(config);
    if (actuales.length <= 1) return;
    const l = actuales.find(x => x.id === id);
    const ok = await confirmar({
      icono: '🗑️',
      titulo: `¿Borrar «${l?.nombre || 'este local'}»?`,
      lineas: ['Se perderá su horario. La plantilla y el chat no se tocan, porque son comunes a todo el negocio.'],
      textoOk: 'Sí, borrarlo',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (!ok) return;
    setSchedulesPorLocal(prev => { const n = { ...prev }; delete n[id]; return n; });
    setPublicadosPorLocal(prev => { const n = { ...prev }; delete n[id]; return n; });
    setFechasPorLocal(prev => { if (!(id in prev)) return prev; const n = { ...prev }; delete n[id]; return n; });
    setConfig(c => ({ ...c, locales: listaLocales(c).filter(x => x.id !== id) }));
    // FALLO REAL ENCONTRADO (9 ago 2026): quien tuviera este local como local fijo se quedaba
    // apuntando a un local que ya no existe. El filtro de disponibilidad por local es
    // `e.localFijo && e.localFijo !== local.id`, así que esa persona desaparecía en SILENCIO de
    // TODOS los locales — nadie se enteraba hasta que echaba en falta a alguien en el horario. Se
    // les pasa a "Rota entre locales" (sin local fijo), que es lo más parecido a lo que tenían.
    const afectados = employees.filter(e => e.localFijo === id);
    if (afectados.length > 0) {
      setEmployees(prev => prev.map(e => e.localFijo === id ? { ...e, localFijo: null } : e));
    }
    if (localActivoId === id) {
      const queda = actuales.filter(x => x.id !== id)[0];
      setLocalActivoId(queda.id);
      setSchedule(schedulesPorLocal[queda.id] || {});
    }
    if (afectados.length > 0) {
      await avisar({
        icono: '👥',
        titulo: 'Aviso: algunas personas tenían este local como fijo',
        lineas: [
          `${afectados.map(e => e.name).join(', ')} ${afectados.length > 1 ? 'tenían' : 'tenía'} «${l?.nombre || 'este local'}» como local asignado.`,
          'Se han pasado a "Rota entre locales" — revisa si necesitan un local fijo distinto.',
        ],
        textoOk: 'Entendido',
      });
    }
  };

  const [viewingHistoryId, setViewingHistoryId] = useState(null);
  const [viewingFolderIndex, setViewingFolderIndex] = useState(null);
  const [descansoModal, setDescansoModal] = useState(null);
  // Burbuja para elegir la hora de Enlace de una persona (2 oct 2026): { empId, entrada, salida }.
  const [enlaceModal, setEnlaceModal] = useState(null);
  const [employeeWaitingNotice, setEmployeeWaitingNotice] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [showEquipoCompleto, setShowEquipoCompleto] = useState(false);
  const [empleadoHorarioPantallaCompleta, setEmpleadoHorarioPantallaCompleta] = useState(false);
  const abrirEmpleadoHorarioPantallaCompleta = async () => {
    setEmpleadoHorarioPantallaCompleta(true);
    try {
      if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
      if (window.screen.orientation && window.screen.orientation.lock) await window.screen.orientation.lock('landscape');
    } catch (_) { /* iPhone u otro navegador sin soporte: se sigue en vertical, sin aviso */ }
  };
  const cerrarEmpleadoHorarioPantallaCompleta = async () => {
    setEmpleadoHorarioPantallaCompleta(false);
    try {
      if (window.screen.orientation && window.screen.orientation.unlock) window.screen.orientation.unlock();
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
    } catch (_) { /* nada que hacer si no estaba en fullscreen */ }
  };
  const miSemanaScrollRef = useRef(null);
  const hoyColumnaRef = useRef(null);

  // --- Estado de autenticación real con Firebase (solo para Sala por ahora) ---
  // 22 sep 2026, a petición de Fran: nada de pantalla de "Cargando…" al abrir, ni 3 segundos ni
  // ninguno — la app tiene que arrancar mostrando algo desde el primer instante. Antes
  // authChecking empezaba en `true` y bloqueaba toda la pantalla hasta que Firebase confirmara
  // si había sesión guardada (con un margen de 3s antes de rendirse). Ahora empieza en `false`:
  // se ve la pantalla de siempre (login) desde el primer fotograma, y si SÍ hay una sesión
  // recordada, la confirmación llega por detrás sin bloquear nada — en cuanto Firebase responde
  // (normalmente casi al instante), la propia app te mete dentro sola, sin que hayas visto
  // ninguna espera de por medio.
  const [authChecking, setAuthChecking] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [restaurantDocId, setRestaurantDocId] = useState(null);
  // ARREGLO (4 oct 2026): "Mi plan" (en Sala) contaba solo la plantilla de Sala, pero la
  // facturación real suma Sala + Cocina (snapshotMensualEmpleados en functions/index.js), así que
  // un bar con cocineros veía en la app un precio más bajo que el que se le cobra. Ahora se lee
  // cuántos hay en Cocina al abrir "Mi plan" y se suman. Si no se puede leer, queda en null y se
  // avisa de que el precio mostrado es solo el de Sala.
  const [numEmpleadosCocinaPlan, setNumEmpleadosCocinaPlan] = useState(null);
  useEffect(() => {
    if (!showPlanes || isCocina || !restaurantDocId) return undefined;
    let cancelado = false;
    getDoc(doc(db, 'cocinas', restaurantDocId))
      .then(snap => { if (!cancelado) setNumEmpleadosCocinaPlan(snap.exists() ? (snap.data()?.employees || []).length : 0); })
      .catch(() => { if (!cancelado) setNumEmpleadosCocinaPlan(null); });
    return () => { cancelado = true; };
  }, [showPlanes, isCocina, restaurantDocId]);
  // INVITA A OTRO RESTAURANTE (19 oct 2026), dentro de "Mi plan". Los códigos los crea y los
  // guarda el servidor (functions/panel.js): aquí solo se piden y se enseñan.
  const [invitaciones, setInvitaciones] = useState(null);   // null = sin cargar · objeto = { codigos, puedeCrear, maxSinUsar }
  const [invitacionesError, setInvitacionesError] = useState('');
  const [invitacionBusy, setInvitacionBusy] = useState(false);
  const [invitacionCopiada, setInvitacionCopiada] = useState('');
  const cargarInvitaciones = useCallback(async () => {
    if (!restaurantDocId) return;
    try {
      const r = await httpsCallable(functionsInstance, 'misInvitaciones')({ restId: restaurantDocId });
      setInvitaciones(r.data || { codigos: [], puedeCrear: false, maxSinUsar: 3 });
      setInvitacionesError('');
    } catch (e) {
      setInvitacionesError(e && e.code === 'functions/permission-denied'
        ? 'Solo el Encargado principal puede invitar a otros restaurantes.'
        : 'No se han podido cargar tus invitaciones. Inténtalo en un momento.');
    }
  }, [restaurantDocId]);
  useEffect(() => {
    if (!showPlanes || isCocina || !restaurantDocId || userRole !== 'jefe') return;
    cargarInvitaciones();
  }, [showPlanes, isCocina, restaurantDocId, userRole, cargarInvitaciones]);
  // CÓDIGO DE DUEÑO (29 oct 2026): el Encargado principal pide uno nuevo y le llega al correo.
  const [codigoDuenoBusy, setCodigoDuenoBusy] = useState(false);
  // Correo con la mitad escondida para enseñarlo en pantalla (fr••••@gmail.com).
  const ocultarCorreoPantalla = (correo) => {
    const [u, d] = String(correo || '').split('@');
    if (!u || !d) return 'tu correo';
    return `${u.slice(0, 2)}••••@${d}`;
  };
  // Cuentas antiguas (de antes de pedir confirmar el correo): el código de Dueño solo se manda a
  // correos confirmados, así que les ofrecemos confirmarlo ahí mismo con un botón (29 oct 2026).
  const ofrecerConfirmarCorreoDueno = async () => {
    const u = auth.currentUser;
    const correo = ocultarCorreoPantalla(u?.email);
    const quiere = await confirmar({
      icono: '📧',
      titulo: 'No se ha podido enviar el código',
      lineas: [
        `Por tu seguridad, antes tienes que confirmar el correo de tu cuenta (${correo}).`,
        'Te mandamos un enlace: lo abres, y después vuelves a pulsar «Enviarme un código de Dueño nuevo».',
      ],
      textoOk: 'Enviarme el correo de confirmación',
      textoCancelar: 'Ahora no',
    });
    if (!quiere || !u) return;
    try {
      await sendEmailVerification(u);
      await avisar({ icono: '📧', titulo: 'Correo de confirmación enviado', lineas: [
        `Te lo hemos enviado a ${correo}. Mira también en la carpeta de spam.`,
        'Abre el enlace y, cuando veas que tu correo está verificado, vuelve aquí y pulsa otra vez «Enviarme un código de Dueño nuevo».',
      ] });
    } catch (e) {
      await avisar({ icono: '⚠️', titulo: 'No se ha podido enviar', lineas: [e && e.code === 'auth/too-many-requests'
        ? 'Has pedido demasiados correos seguidos. Espera unos minutos y vuelve a probar.'
        : 'No se ha podido enviar el correo de confirmación. Inténtalo otra vez en un momento.'] });
    }
  };
  const pedirCodigoDueno = async () => {
    if (codigoDuenoBusy) return;
    // Antes de nada miramos si el correo está confirmado (recargando el dato, por si lo acaba de
    // confirmar en el correo). Si no lo está, ofrecemos confirmarlo en vez de dar un error.
    const usuarioActual = auth.currentUser;
    if (usuarioActual && usuarioActual.email) {
      try { await usuarioActual.reload(); } catch (_) {}
      if (!(auth.currentUser || usuarioActual).emailVerified) { await ofrecerConfirmarCorreoDueno(); return; }
    }
    const ok = await confirmar({
      icono: '🔑',
      titulo: '¿Te enviamos un código de Dueño nuevo?',
      lineas: [
        'Te llegará al correo de tu cuenta. Dáselo solo al dueño o dueña del negocio.',
        'El código anterior dejará de servir para crear cuentas de Dueño. Las cuentas de Dueño que ya existen no cambian.',
      ],
      textoOk: 'Sí, enviármelo',
      textoCancelar: 'Cancelar',
    });
    if (!ok) return;
    setCodigoDuenoBusy(true);
    try {
      const r = await httpsCallable(functionsInstance, 'enviarCodigoDueno')({});
      await avisar({ icono: '📧', titulo: 'Código enviado', lineas: [`Te lo hemos enviado a ${r?.data?.correo || 'tu correo'}. Mira también en la carpeta de spam.`] });
    } catch (e) {
      if (e && e.code === 'functions/failed-precondition') {
        setCodigoDuenoBusy(false);
        await ofrecerConfirmarCorreoDueno();
        return;
      }
      await avisar({ icono: '⚠️', titulo: 'No se ha podido enviar', lineas: [(e && e.message && e.code !== 'functions/internal') ? e.message : 'Inténtalo otra vez en un momento.'] });
    }
    setCodigoDuenoBusy(false);
  };

  const crearCodigoInvitacion = async () => {
    if (invitacionBusy) return;
    setInvitacionBusy(true); setInvitacionesError('');
    try {
      await httpsCallable(functionsInstance, 'crearInvitacion')({ restId: restaurantDocId });
      await cargarInvitaciones();
    } catch (e) {
      setInvitacionesError((e && e.message && e.code !== 'functions/internal') ? e.message : 'No se ha podido crear el código. Inténtalo otra vez.');
    }
    setInvitacionBusy(false);
  };
  const textoInvitacion = (codigo) => `Te invito a probar Horidia para organizar los turnos de tu restaurante. Al crear tu cuenta de Encargado en https://app.horidia.com toca «¿Te ha invitado otro restaurante?» y pon este código: ${codigo}`;
  const compartirInvitacion = async (codigo) => {
    const texto = textoInvitacion(codigo);
    try {
      if (navigator.share) { await navigator.share({ title: 'Horidia', text: texto }); return; }
    } catch (e) { if (e && e.name === 'AbortError') return; }
    let ok = false;
    try { await navigator.clipboard.writeText(texto); ok = true; } catch (_) { ok = false; }
    setInvitacionCopiada(ok ? codigo : `${codigo}-error`);
    setTimeout(() => setInvitacionCopiada(''), 2500);
  };
  // PRUEBA DE 14 DÍAS (2 oct 2026, pedido por Fran): dos campos sueltos en el propio documento
  // del restaurante/cocina, FUERA de `config` a propósito — así nunca se tocan sin querer al
  // guardar la configuración normal. `creadoEn` se pone UNA sola vez, en el momento real de
  // creación (handleIntroComplete) — nunca se toca después. `cuentaActivada` la pone Fran a mano
  // desde la consola de Firebase cuando el cliente paga; no existe ningún botón dentro de la app
  // para activarla sola, a propósito.
  // SEGURO INCORPORADO: las cuentas que ya existían ANTES de este cambio (Duo Tapas, tus
  // pruebas…) no tienen `creadoEn` guardado, y nunca lo tendrán — solo se escribe al CREAR un
  // restaurante nuevo, no con retroactividad. Por eso el bloqueo más abajo exige
  // `!!creadoEn` como primera condición: sin fecha de alta, nunca se puede bloquear nada, pase
  // lo que pase con `cuentaActivada`. Ninguna cuenta de antes de hoy puede quedar atrapada.
  const [creadoEn, setCreadoEn] = useState(null);
  const [cuentaActivada, setCuentaActivada] = useState(false);
  // (19 oct 2026, Horidia Panel) Campos de cobro que escribe solo el panel de Fran: hasta qué día
  // está pagada la cuenta, la prueba alargada, la desactivación a mano y el aviso de días
  // regalados por invitar. Ver estadoCuentaBar() arriba del todo.
  const [pagadoHasta, setPagadoHasta] = useState(null);
  const [pruebaHasta, setPruebaHasta] = useState(null);
  const [bloqueadaAMano, setBloqueadaAMano] = useState(false);
  const [ultimoRegalo, setUltimoRegalo] = useState(null);
  // FIN DE PRUEBA: COBRO POR EL TOTAL DE EMPLEADOS, SALA + COCINA (5 oct 2026, pedido por Fran).
  // La pantalla de fin de prueba calculaba la cuota solo con la plantilla del departamento en el
  // que estás (`employees`), mientras que la facturación real y "Mi plan" suman Sala + Cocina.
  // Aquí se lee cuántos empleados hay en el OTRO departamento, pero solo cuando la prueba ya
  // ha caducado (no se hace ninguna lectura extra el resto del tiempo).
  //   undefined = todavía cargando · número = leído bien · null = no se ha podido leer
  // COCINA HEREDA LA ACTIVACIÓN DE SALA (5 oct 2026, decisión de Fran): Sala y Cocina son un
  // solo cliente y una sola suscripción — si Sala está activada, Cocina también, y la prueba de
  // 14 días corre desde el alta de Sala, no desde que se crea Cocina. Por eso, en una sesión de
  // Cocina se lee el documento de Sala (misma llave `uid`) y SUS dos campos mandan; solo si no
  // existe o no se puede leer se usan los propios de Cocina.
  //   undefined = leyendo · null = no hay documento de Sala (o no se pudo leer) · objeto = leído
  const [cuentaDeSala, setCuentaDeSala] = useState(undefined);
  // COCINA USA EL LOGO Y LA CARTA DE SALA (11 oct 2026, pedido por Fran): Sala y Cocina son el
  // mismo negocio. Con la misma lectura del documento de Sala que ya se hacía (no cuesta ninguna
  // lectura extra), Cocina se queda también con el logo, el nombre y la foto de la carta que
  // subió el Encargado. Si Cocina sube los suyos propios, mandan los suyos.
  const [datosDeSala, setDatosDeSala] = useState(null);
  useEffect(() => {
    if (!isCocina || !restaurantDocId) return undefined;
    let cancelado = false;
    setCuentaDeSala(undefined);
    getDoc(doc(db, 'restaurantes', restaurantDocId))
      .then(snap => {
        if (cancelado) return;
        const d = snap.exists() ? snap.data() : null;
        setCuentaDeSala(d ? {
          creadoEn: d.creadoEn || null, cuentaActivada: !!d.cuentaActivada,
          pagadoHasta: d.pagadoHasta || null, pruebaHasta: d.pruebaHasta || null,
          bloqueadaAMano: d.bloqueadaAMano === true, ultimoRegalo: d.ultimoRegalo || null,
        } : null);
        setDatosDeSala(d?.config ? { config: d.config } : null);
      })
      .catch(() => { if (!cancelado) setCuentaDeSala(null); });
    return () => { cancelado = true; };
  }, [isCocina, restaurantDocId]);
  const cuentaPropia = { creadoEn, cuentaActivada, pagadoHasta, pruebaHasta, bloqueadaAMano, ultimoRegalo };
  const cuentaEfectiva = (isCocina && cuentaDeSala) ? cuentaDeSala : cuentaPropia;
  // "Hoy" en hora de España. Se revisa cada minuto y al volver a la app (los móviles congelan
  // los temporizadores en segundo plano): si la app se queda abierta toda la noche, a las 00:00
  // del día siguiente al último día salen igualmente las pantallas de pago.
  const [hoyCuenta, setHoyCuenta] = useState(() => fechaMadridCuenta());
  useEffect(() => {
    const revisar = () => { const h = fechaMadridCuenta(); setHoyCuenta(prev => (prev === h ? prev : h)); };
    const t = setInterval(revisar, 60000);
    document.addEventListener('visibilitychange', revisar);
    window.addEventListener('focus', revisar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', revisar); window.removeEventListener('focus', revisar); };
  }, []);
  // (19 oct 2026) Antes: "bloqueada si tiene fecha de alta, no está activada y han pasado 14
  // días". Ahora lo decide estadoCuentaBar(), que además entiende el mes pagado (pagadoHasta),
  // la prueba alargada (pruebaHasta) y la desactivación desde el panel. Las cuentas sin fecha de
  // alta siguen sin bloquearse nunca, y las que tienen `cuentaActivada` sin fecha, tampoco.
  const estadoCuentaActual = estadoCuentaBar(cuentaEfectiva, hoyCuenta);
  const pruebaCaducada = estadoCuentaActual.bloqueada;
  // Mientras Cocina todavía no sabe lo que dice Sala, no se puede decidir si bloquear: si su
  // propia fecha ya estaría caducada se espera (evita un parpadeo de "prueba terminada" en un
  // cliente que sí ha pagado); si no estaría caducada no hay nada que esperar.
  const propioCaducado = estadoCuentaBar(cuentaPropia, hoyCuenta).bloqueada;

  const esperandoCuentaDeSala = isCocina && cuentaDeSala === undefined && propioCaducado;
  const [numEmpleadosOtroDepto, setNumEmpleadosOtroDepto] = useState(undefined);
  useEffect(() => {
    if (!pruebaCaducada || !restaurantDocId) return undefined;
    let cancelado = false;
    setNumEmpleadosOtroDepto(undefined);
    getDoc(doc(db, isCocina ? 'restaurantes' : 'cocinas', restaurantDocId))
      .then(snap => { if (!cancelado) setNumEmpleadosOtroDepto(snap.exists() ? (snap.data()?.employees || []).length : 0); })
      .catch(() => { if (!cancelado) setNumEmpleadosOtroDepto(null); });
    return () => { cancelado = true; };
  }, [pruebaCaducada, isCocina, restaurantDocId]);

  // --- CHAT: bajar solo al ultimo mensaje ---------------------------------------------
  // El contenedor del chat no bajaba nunca, asi que al abrirlo te quedabas en el mensaje mas
  // antiguo y habia que arrastrar a mano hasta abajo. Este "ref" apunta al recuadro que
  // scrollea (solo hay uno visible a la vez, el de Grupo o el de Privado).
  const chatScrollRef = useRef(null);
  // Input de archivo oculto para la foto de la carta — se dispara con .click() desde el botón
  // visible en vez de envolverlo en un <label>, que en algunos navegadores/PWA de Android no
  // siempre propaga el toque al input y se queda sin hacer nada.
  const menuFotoInputRef = useRef(null);
  const duenoPlantillaRef = useRef(null);
  const duenoTurnosEliminadosRef = useRef(null);
  const duenoHistorialRef = useRef(null);
  useEffect(() => {
    if (activeTab !== 'chat') return;
    const caja = chatScrollRef.current;
    if (!caja) return;
    // Un respiro minimo para que React haya pintado el mensaje nuevo antes de medir la altura.
    const t = setTimeout(() => { caja.scrollTop = caja.scrollHeight; }, 50);
    return () => clearTimeout(t);
  }, [activeTab, chatAbierto, messages, privateMessages]);

  // --- CHAT: contador REAL de mensajes sin leer ---------------------------------------
  // Antes la pestana ponia simplemente cuantos mensajes habia en total desde el principio
  // (por eso subia y subia y nunca bajaba, aunque estuvieran todos leidos). Ahora se guarda
  // en el propio dispositivo la hora de la ultima vez que se abrio el chat, y solo se cuentan
  // los mensajes posteriores a esa hora que no haya escrito uno mismo.
  const claveUltimaLectura = restaurantDocId && currentUser
    ? `horia_chat_leido_${restaurantDocId}_${currentUser}` : null;
  const [ultimaLecturaChat, setUltimaLecturaChat] = useState(0);

  useEffect(() => {
    if (!claveUltimaLectura) return;
    try {
      const guardado = localStorage.getItem(claveUltimaLectura);
      setUltimaLecturaChat(guardado ? Number(guardado) : 0);
    } catch (_) { setUltimaLecturaChat(0); }
  }, [claveUltimaLectura]);

  // Mientras el chat este abierto, todo lo que llegue cuenta como leido al momento.
  useEffect(() => {
    if (activeTab !== 'chat' || !claveUltimaLectura) return;
    const ahora = Date.now();
    setUltimaLecturaChat(ahora);
    try { localStorage.setItem(claveUltimaLectura, String(ahora)); } catch (_) {}
  }, [activeTab, messages, privateMessages, claveUltimaLectura]);

  // ¿Puede esta persona escribir en el chat de GRUPO? El Encargado y los gestores siempre.
  // Los empleados, solo si el Encargado no ha activado el modo "solo encargados" en Configuración.
  // Los chats privados NO se ven afectados: ahí puede escribir todo el mundo siempre.
  // Esto es solo la parte visual — quien manda de verdad son las reglas de Firestore, que
  // rechazan el mensaje aunque alguien se salte la pantalla con las herramientas del navegador.
  const puedeEscribirEnGrupo = userRole === 'jefe' || !config?.soloEncargadosEscribenChat;

  const mensajesSinLeer = useMemo(() => {
    const desde = ultimaLecturaChat || 0;
    const esNuevo = (m) => (m.createdAt || 0) > desde && m.sender !== currentUser;
    // privateMessages ya viene filtrado a las conversaciones de esta persona.
    return messages.filter(esNuevo).length + privateMessages.filter(esNuevo).length;
  }, [messages, privateMessages, ultimaLecturaChat, currentUser]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // AVISOS DE LA CUENTA (19 oct 2026): ver AvisoCuentaModal arriba. Solo Encargado y Dueño. Lo
  // que ya se ha visto se recuerda en este dispositivo (el aviso de fin, hasta el día siguiente;
  // el de regalo, para siempre). Si el navegador no deja guardar, se recuerda hasta que se
  // cierre la app, que es lo único que se puede hacer.
  const [avisosCuentaCerrados, setAvisosCuentaCerrados] = useState({});
  const avisoCuentaYaVisto = (clave) => {
    if (avisosCuentaCerrados[clave]) return true;
    try { return localStorage.getItem(clave) === '1'; } catch (_) { return false; }
  };
  const marcarAvisoCuentaVisto = (clave) => {
    try { localStorage.setItem(clave, '1'); } catch (_) { /* sin almacenamiento */ }
    setAvisosCuentaCerrados(prev => ({ ...prev, [clave]: true }));
  };
  const puedeVerAvisosCuenta = (userRole === 'jefe' || userRole === 'dueno') && !!restaurantDocId && dataLoaded && !pruebaCaducada && !(isCocina && cuentaDeSala === undefined);
  const regaloCuenta = cuentaEfectiva.ultimoRegalo;
  const claveRegaloCuenta = (regaloCuenta && regaloCuenta.id) ? `horidia-regalo-${restaurantDocId}-${regaloCuenta.id}` : null;
  const fechaRegaloCuenta = fechaJSDe(regaloCuenta && regaloCuenta.en);
  const mostrarAvisoRegalo = puedeVerAvisosCuenta && !!claveRegaloCuenta && !avisoCuentaYaVisto(claveRegaloCuenta)
    && (!fechaRegaloCuenta || Date.now() - fechaRegaloCuenta.getTime() < 30 * 86400000);
  const claveAvisoFinCuenta = ((estadoCuentaActual.estado === 'prueba' || estadoCuentaActual.estado === 'pagada')
    && estadoCuentaActual.quedan >= 1 && estadoCuentaActual.quedan <= 5)
    ? `horidia-aviso-fin-${restaurantDocId}-${estadoCuentaActual.ultimoDia}-${hoyCuenta}` : null;
  const mostrarAvisoFin = puedeVerAvisosCuenta && !mostrarAvisoRegalo && !!claveAvisoFinCuenta && !avisoCuentaYaVisto(claveAvisoFinCuenta);
  const nombreNegocioAviso = (config && config.restaurantName) || (datosDeSala && datosDeSala.config && datosDeSala.config.restaurantName) || '';
  const avisoCuentaUI = mostrarAvisoRegalo ? (
    <AvisoCuentaModal key={claveRegaloCuenta} tipo="regalo"
      datos={{ nombre: nombreNegocioAviso, dias: regaloCuenta.dias, invitadoNombre: regaloCuenta.invitadoNombre, hasta: regaloCuenta.hasta }}
      onCerrar={() => marcarAvisoCuentaVisto(claveRegaloCuenta)} />
  ) : mostrarAvisoFin ? (
    <AvisoCuentaModal key={claveAvisoFinCuenta} tipo="fin"
      datos={{
        nombre: nombreNegocioAviso, tipoCuenta: estadoCuentaActual.tipo, quedan: estadoCuentaActual.quedan, ultimoDia: estadoCuentaActual.ultimoDia,
        totalDias: (() => { const alta = fechaJSDe(cuentaEfectiva.creadoEn); return alta ? diasEntreCuenta(fechaMadridCuenta(alta), estadoCuentaActual.ultimoDia) + 1 : 0; })(),
      }}
      onCerrar={() => marcarAvisoCuentaVisto(claveAvisoFinCuenta)} />
  ) : null;

  // MENSAJE DE BIENVENIDA (25 oct 2026, pedido por Fran): una sola vez por bar, solo para el
  // Encargado y solo en Sala (que es donde se crea la cuenta). Se recuerda en la configuración
  // del bar en Firestore (`config.bienvenidaVista`), no en el móvil, para que no vuelva a salir
  // al entrar desde otro dispositivo. Mientras se ve, tapa a los avisos de la cuenta (regalo /
  // fin de prueba) para que nunca salgan dos ventanas a la vez; esos se ven al cerrarla.
  // (3 nov 2026) Además, solo la cuenta principal del Encargado (su uid es el id del bar): un
  // gerente con PIN también entra como 'jefe', y Fran quiere que este mensaje lo vea solo el Encargado.
  const mostrarBienvenida = userRole === 'jefe' && !isCocina && !!restaurantDocId && dataLoaded
    && auth.currentUser?.uid === restaurantDocId
    && !!config.configurado && !config.bienvenidaVista && !pruebaCaducada;
  // (3 nov 2026, pedido por Fran) Texto nuevo, aprobado por él, y una X para cerrarla además de
  // «Entendido». Cerrar de cualquiera de las dos formas cuenta como vista (no vuelve a salir).
  const cerrarBienvenida = () => setConfig(c => ({ ...c, bienvenidaVista: new Date().toISOString() }));
  const bienvenidaUI = mostrarBienvenida ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="bienvenida-titulo"
        style={{ position: 'relative', background: C.white, borderRadius: '18px', padding: '28px 26px', maxWidth: '400px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', textAlign: 'center' }}>
        <button type="button" onClick={cerrarBienvenida} aria-label="Cerrar"
          style={{ position: 'absolute', top: '10px', right: '10px', width: '36px', height: '36px', border: 'none', borderRadius: '50%', background: 'transparent', color: C.textMuted, fontSize: '22px', lineHeight: 1, cursor: 'pointer' }}>
          ×
        </button>
        <div style={{ fontSize: '38px', marginBottom: '8px' }} aria-hidden="true">👋</div>
        <h3 id="bienvenida-titulo" style={{ margin: '0 0 12px', fontSize: '19px', fontWeight: '800', color: C.text }}>¡Bienvenido a Horidia!</h3>
        <p style={{ margin: '0 0 12px', fontSize: '14px', color: C.textMuted, lineHeight: 1.55 }}>
          Gracias por probarla. Somos una app nueva y la estamos mejorando cada día. Si algo no te funciona o se te ocurre cómo hacerla mejor, ve a <strong style={{ color: C.text }}>Mi plan → Reportar un fallo</strong> y lo reviso lo antes posible.
        </p>
        <p style={{ margin: '0 0 22px', fontSize: '14px', color: C.textMuted, lineHeight: 1.55 }}>
          Si te lo prefieres decir en persona, escríbeme a <a href="mailto:horidiapp@gmail.com" style={{ color: C.primary, fontWeight: '700' }}>horidiapp@gmail.com</a>.
        </p>
        <button type="button" onClick={cerrarBienvenida}
          style={{ ...btn('primary'), width: '100%', justifyContent: 'center' }}>
          Entendido
        </button>
      </div>
    </div>
  ) : null;

  // REPORTAR UN FALLO (26 oct 2026, diseñado con Fran, opción B de la maqueta): ventana dentro
  // de la app, sin abrir el correo. Se elige (si se quiere) dónde ha pasado y se cuenta en una
  // frase. Lo recibe la función reportarFallo, que lo guarda para el Horidia Panel (pestaña
  // Fallos) y avisa a Fran por correo. Quién lo envía lo deduce el servidor, no la app.
  const SECCIONES_FALLO = ['Horario', 'Chat', 'Notificaciones', 'Solicitudes', 'Horito', 'Otro'];
  const [falloVentana, setFalloVentana] = useState(null); // null | { seccion, texto, enviando, enviado, error }
  const abrirFallo = () => setFalloVentana({ seccion: null, texto: '', enviando: false, enviado: false, error: '' });
  const describirDispositivo = () => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const so = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iPhone/iPad' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'otro';
    const nav = /Edg\//.test(ua) ? 'Edge' : /SamsungBrowser/.test(ua) ? 'Samsung Internet' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'navegador';
    let instalada = false;
    try { instalada = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true; } catch (_) { instalada = false; }
    return `${so} · ${nav}${instalada ? ' · app instalada' : ''}`;
  };
  const enviarFallo = async () => {
    if (!falloVentana || falloVentana.enviando) return;
    const texto = falloVentana.texto.trim();
    if (texto.length < 3) return;
    setFalloVentana(v => ({ ...v, enviando: true, error: '' }));
    try {
      await httpsCallable(functionsInstance, 'reportarFallo')({
        texto, seccion: falloVentana.seccion, coleccion: FIRESTORE_COLLECTION, restaurantDocId,
        empId: userRole === 'empleado' ? (selectedEmployeeId || null) : null, dispositivo: describirDispositivo(),
      });
      setFalloVentana(v => (v ? { ...v, enviando: false, enviado: true } : v));
    } catch (e) {
      const propio = e && e.message && (String(e.code).includes('resource-exhausted') || String(e.code).includes('invalid-argument'));
      setFalloVentana(v => (v ? { ...v, enviando: false, error: propio ? e.message : 'No se ha podido enviar. Revisa tu conexión e inténtalo otra vez.' } : v));
    }
  };
  const falloUI = falloVentana ? (
    <div onClick={() => { if (!falloVentana.enviando) setFalloVentana(null); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="fallo-titulo"
        style={{ background: C.white, borderRadius: '18px', padding: '24px 22px', maxWidth: '400px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', textAlign: 'center', maxHeight: '92vh', overflowY: 'auto' }}>
        {falloVentana.enviado ? (
          <>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#DCFCE7', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px auto 14px' }} aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            </div>
            <h3 id="fallo-titulo" style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '800', color: C.text }}>¡Recibido, gracias!</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: C.textMuted, lineHeight: 1.55 }}>
              Ya lo estamos mirando. Te escribiremos al correo de tu cuenta cuando esté arreglado.
            </p>
            <button type="button" onClick={() => setFalloVentana(null)} style={{ ...btn('primary'), width: '100%', justifyContent: 'center' }}>Cerrar</button>
          </>
        ) : (
          <>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: C.primaryFaint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', margin: '0 auto 12px' }} aria-hidden="true">🛠️</div>
            <h3 id="fallo-titulo" style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '800', color: C.text }}>Reportar un fallo</h3>
            <p style={{ margin: 0, fontSize: '13.5px', color: C.textMuted, lineHeight: 1.5 }}>¿Dónde has visto el fallo?</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', margin: '12px 0 4px' }}>
              {SECCIONES_FALLO.map(s => {
                const on = falloVentana.seccion === s;
                return (
                  <button key={s} type="button" aria-pressed={on} disabled={falloVentana.enviando}
                    onClick={() => setFalloVentana(v => ({ ...v, seccion: v.seccion === s ? null : s }))}
                    style={{ padding: '8px 13px', borderRadius: '20px', border: `1px solid ${on ? C.primary : C.primaryBorder}`, background: on ? C.primary : C.white, color: on ? C.white : C.primaryDark, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    {s}
                  </button>
                );
              })}
            </div>
            <textarea value={falloVentana.texto} maxLength={1000} disabled={falloVentana.enviando} aria-label="Qué ha pasado"
              placeholder="Cuéntanos qué ha pasado. Por ejemplo: el chat no me deja enviar mensajes"
              onChange={e => setFalloVentana(v => ({ ...v, texto: e.target.value, error: '' }))}
              style={{ width: '100%', boxSizing: 'border-box', height: '118px', margin: '12px 0 4px', padding: '12px', borderRadius: '12px', border: `1.5px solid ${C.primaryBorder}`, background: C.primaryFaint, fontSize: '14px', color: C.text, resize: 'none', fontFamily: 'inherit', lineHeight: 1.45, outlineColor: C.primary }} />
            <div style={{ fontSize: '11.5px', color: C.textMuted, textAlign: 'right', marginBottom: '12px' }}>{falloVentana.texto.length}/1000</div>
            {falloVentana.error && <p style={{ margin: '0 0 12px', fontSize: '12.5px', fontWeight: '600', color: C.danger, lineHeight: 1.45 }}>{falloVentana.error}</p>}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" disabled={falloVentana.enviando} onClick={() => setFalloVentana(null)} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Cancelar</button>
              <button type="button" disabled={falloVentana.enviando || falloVentana.texto.trim().length < 3} onClick={enviarFallo}
                style={{ ...btn('primary'), flex: 1, justifyContent: 'center', opacity: (falloVentana.enviando || falloVentana.texto.trim().length < 3) ? 0.55 : 1 }}>
                {falloVentana.enviando ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  // USO DE LA APP (26 oct 2026, para el Horidia Panel, pestaña Visitas): una vez cada vez que se
  // abre la app con un bar cargado, se avisa a la función registrarUso, que suma +1 a ese bar y
  // guarda su último uso. Si falla, no pasa nada (solo es un contador).
  // ARREGLO (29 oct 2026): un bar recién creado avisaba antes de que existiera su documento (se
  // guarda al terminar la configuración inicial); el servidor no lo contaba y la app ya no lo
  // volvía a intentar, así que la primera vez de cada bar nuevo se perdía. Ahora solo se da por
  // contado cuando el servidor responde que lo ha apuntado, y se vuelve a intentar al terminar
  // la configuración inicial. `usoEnCursoRef` evita mandar dos avisos a la vez.
  const usoRegistradoRef = useRef('');
  const usoEnCursoRef = useRef('');
  const barConfigurado = !!config?.configurado;
  const barConfiguradoRef = useRef(barConfigurado);
  barConfiguradoRef.current = barConfigurado;
  // Un solo reintento más por sesión si el bar terminó de configurarse mientras el primer aviso
  // aún iba de camino (nunca en bucle: el Dueño, por ejemplo, no cuenta y siempre recibe ok:false).
  const usoReintentadoRef = useRef('');
  const [usoReintento, setUsoReintento] = useState(0);
  useEffect(() => {
    if (!dataLoaded || !restaurantDocId || !userRole) return;
    const clave = `${FIRESTORE_COLLECTION}/${restaurantDocId}/${userRole}`;
    if (usoRegistradoRef.current === clave || usoEnCursoRef.current === clave) return;
    usoEnCursoRef.current = clave;
    const configuradoAlPedir = barConfiguradoRef.current;
    let contado = false;
    httpsCallable(functionsInstance, 'registrarUso')({ coleccion: FIRESTORE_COLLECTION, restaurantDocId })
      .then((r) => { if (r?.data?.ok) { contado = true; usoRegistradoRef.current = clave; } })
      .catch(() => {})
      .finally(() => {
        if (usoEnCursoRef.current === clave) usoEnCursoRef.current = '';
        if (!contado && !configuradoAlPedir && barConfiguradoRef.current && usoReintentadoRef.current !== clave) {
          usoReintentadoRef.current = clave;
          setUsoReintento(n => n + 1);
        }
      });
  }, [dataLoaded, restaurantDocId, userRole, FIRESTORE_COLLECTION, barConfigurado, usoReintento]);

  // CONTADOR DE HORARIOS GENERADOS (31 oct 2026, pedido por Fran, para el Horidia Panel): cada
  // vez que se genera un horario se suma +1 a ese bar (y uno por cada local si se generan varios
  // a la vez). Solo cuenta: no guarda nada del horario. Si falla, no pasa nada y nunca bloquea
  // la generación (no se espera la respuesta).
  const contarHorarioGenerado = (veces = 1) => {
    if (!restaurantDocId) return;
    httpsCallable(functionsInstance, 'registrarUso')({
      coleccion: FIRESTORE_COLLECTION, restaurantDocId, evento: 'horario_generado', veces,
    }).catch(() => {});
  };

  // Aquí se aplica el destino, ya con la sesión resuelta: así nada lo pisa después.
  useEffect(() => {
    if (!destinoNotificacion || !dataLoaded || !userRole) return;
    if (destinoNotificacion === 'chat') setActiveTab('chat');
    else if (destinoNotificacion === 'horario') setActiveTab('horario');
    else if (destinoNotificacion === 'solicitudes') setActiveTab('solicitudes');
    else if (destinoNotificacion === 'turnos') setActiveTab('turnos');
    setDestinoNotificacion(null);
  }, [destinoNotificacion, dataLoaded, userRole]);
  const [dataError, setDataError] = useState(false);
  const [cargaLenta, setCargaLenta] = useState(false); // si tarda demasiado en cargar, ofrecemos recargar a mano
  const [reconnectKey, setReconnectKey] = useState(0); // súbelo para forzar que las conexiones a Firestore se reconecten de verdad
  // Recuerda el restaurantDocId de la última vez que el efecto de carga de datos limpió el
  // estado, para poder distinguir "cambié de cuenta de verdad" de "solo se ha reconectado"
  // (ver el comentario largo junto a restaurantDocIdAnteriorRef.current más abajo).
  const restaurantDocIdAnteriorRef = useRef(null);
  // (29 oct 2026) Enlace directo para el Dueño: app.horidia.com/?dueno=1 abre "Crear cuenta de
  // Dueño" (es el enlace que va en el correo del código de Dueño). Solo cambia la pantalla de
  // entrada; si ya hay una sesión iniciada no tiene ningún efecto.
  const [authScreen, setAuthScreen] = useState(() => {
    try { if (new URLSearchParams(window.location.search).get('dueno') === '1') return 'duenoRegistro'; } catch (_) {}
    // (3 nov 2026) Enlace directo para quien llega desde "Probar gratis" de la web (horidia.com):
    // app.horidia.com/?registro=1 abre "Crear cuenta de Encargado" sin pasar por la pantalla de
    // inicio ni por "Iniciar sesión". Solo en Sala; sin el parámetro, todo igual que antes.
    try { if (!isCocina && new URLSearchParams(window.location.search).get('registro') === '1') return 'jefeRegistro'; } catch (_) {}
    return 'inicio';
  }); // inicio | jefeLogin | jefeRegistro | empleadoCodigo | duenoLogin | duenoRegistro
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  // Mensaje del blindaje anti-sobrescritura de handleIntroComplete (29 sep 2026) — distinto de
  // authError porque IntroScreen es un componente aparte y no lee authError del padre.
  const [introBlockedMessage, setIntroBlockedMessage] = useState('');
  // Botón "Guardar" junto al nombre del negocio (29 sep 2026, pedido por Fran): el nombre ya
  // se autoguardaba con el resto de la configuración, pero un fallo de guardado NUNCA se
  // enseñaba en pantalla — solo quedaba en la consola del navegador, que nadie mira. Este botón
  // da una confirmación visible de verdad (✓ Guardado, o un aviso claro si falla), sin tocar el
  // autoguardado del resto de ajustes.
  const [guardandoNombreNegocio, setGuardandoNombreNegocio] = useState(false);
  const [nombreNegocioGuardadoOk, setNombreNegocioGuardadoOk] = useState(false);
  const [accessCodeInput, setAccessCodeInput] = useState('');
  // Código de Dueño (29 oct 2026): distinto del de los empleados; solo lo recibe el Encargado por correo.
  const [codigoDuenoInput, setCodigoDuenoInput] = useState('');
  const [cambiandoCodigo, setCambiandoCodigo] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  // La clave de "ya visto" tiene que ser por PERSONA, no por dispositivo (20 sep 2026 — fallo
  // real: con la clave fija de antes, en una tablet compartida del bar solo el primer empleado
  // que abría el chat en su vida veía el aviso legal; el resto, entrando con su propio PIN en la
  // misma tablet, nunca lo veía). selectedEmployeeId identifica al empleado con PIN; si no hay
  // (Encargado/Dueño con su propia cuenta), se usa su uid de Firebase.
  // OJO (21 sep 2026): esto tiene que ir DESPUÉS de declarar selectedEmployeeId, nunca antes —
  // aquí mismo estuvo el fallo real que rompió la app entera ("Cannot access before
  // initialization"): antes vivía arriba del todo, mucho antes de que selectedEmployeeId
  // existiera, y cualquiera que abriera la app se encontraba una pantalla en blanco.
  const asistenteAvisoKey = `horia_asistente_aviso_ia_${selectedEmployeeId || auth.currentUser?.uid || 'sesion'}`;
  const [asistenteAvisoVisto, setAsistenteAvisoVisto] = useState(
    () => localStorage.getItem(asistenteAvisoKey) === '1'
  );
  // Si cambia quién está usando el dispositivo dentro de la misma sesión (otro PIN, sin recargar
  // la página entera), el "ya visto" de arriba solo se calculó una vez al montar el componente —
  // esto lo recalcula cada vez que cambia la identidad, para la persona nueva.
  useEffect(() => {
    setAsistenteAvisoVisto(localStorage.getItem(asistenteAvisoKey) === '1');
  }, [asistenteAvisoKey]);
  // El chat del asistente debe empezar en blanco cada vez que cambia QUIÉN ha entrado (de
  // Encargado a un empleado, o de un empleado a otro) — si no, se ve la conversación de la
  // persona anterior, como le pasó a Fran el 7 sep 2026 al mirar el perfil de un empleado justo
  // después de haber hablado con el asistente como Encargado.
  useEffect(() => {
    setAsistenteMensajes([]);
  }, [userRole, selectedEmployeeId]);
  // El "dueño real": la cuenta cuyo uid coincide exactamente con el documento del restaurante.
  // Solo esta persona puede conceder o quitar permisos de gestión a otros (Firestore lo exige igual).
  const esDuenoReal = !!(firebaseUser && !firebaseUser.isAnonymous && restaurantDocId && firebaseUser.uid === restaurantDocId);
  const [resetSent, setResetSent] = useState(false);
  // Email de la cuenta recién creada que aún no ha confirmado su correo (null = nada pendiente).
  const [emailPendiente, setEmailPendiente] = useState(null);
  const [verifBusy, setVerifBusy] = useState(false);
  const [verifMsg, setVerifMsg] = useState('');
  const [verifEspera, setVerifEspera] = useState(false); // pausa de 30 s entre reenvíos
  const [pendingPinEmployee, setPendingPinEmployee] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinIntentos, setPinIntentos] = useState(0); // fallos seguidos con el PIN actual
  const [pinBloqueadoHasta, setPinBloqueadoHasta] = useState(0); // timestamp hasta el que no se puede reintentar
  const [pinSegundosRestantes, setPinSegundosRestantes] = useState(0);
  useEffect(() => {
    if (!pinBloqueadoHasta) { setPinSegundosRestantes(0); return; }
    const actualizar = () => setPinSegundosRestantes(Math.max(0, Math.ceil((pinBloqueadoHasta - Date.now()) / 1000)));
    actualizar();
    const t = setInterval(actualizar, 500);
    return () => clearInterval(t);
  }, [pinBloqueadoHasta]);
  // --- Desbloqueo con huella / Face ID (WebAuthn) ---
  // Guardamos, por dispositivo, un identificador de la "llave" biométrica registrada para
  // una persona concreta (empId). El dato biométrico en sí nunca sale del móvil — solo
  // guardamos una referencia que el propio teléfono sabe comprobar.
  const HUELLA_KEY = `horia_huella_${department}`;
  const [huellaDisponible, setHuellaDisponible] = useState(false);
  const [bloqueadoHuella, setBloqueadoHuella] = useState(false);
  const [huellaError, setHuellaError] = useState('');
  const [huellaBusy, setHuellaBusy] = useState(false);
  const [ofertaHuella, setOfertaHuella] = useState(null); // { id, name } de quien acaba de entrar con PIN, o null
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  // ACEPTACIÓN DE CONDICIONES AL REGISTRARSE (5 oct 2026): la casilla del registro y la
  // constancia de la aceptación (versión + fecha), que se guarda junto a la cuenta al crearla.
  const [showConditions, setShowConditions] = useState(false);
  const [aceptaCondiciones, setAceptaCondiciones] = useState(false);
  // INVITACIONES (19 oct 2026): código opcional al crear la cuenta de Encargado, si a este
  // restaurante lo ha invitado otro. Se comprueba ANTES de crear la cuenta (si no vale, no se
  // crea nada) y se gasta justo DESPUÉS (usarInvitacion, en functions/panel.js).
  const [verCodigoInvitacion, setVerCodigoInvitacion] = useState(false);
  const [codigoInvitacion, setCodigoInvitacion] = useState('');
  const [aceptacionRegistro, setAceptacionRegistro] = useState(null);
  const [upgradeNotice, setUpgradeNotice] = useState(null); // texto del aviso, o null si no hay ninguno abierto
  const [vacacionesEmpleado, setVacacionesEmpleado] = useState(null); // id del empleado cuyo calendario de vacaciones está abierto, o null
  const [bajaEmpleado, setBajaEmpleado] = useState(null); // id del empleado cuyo panel de baja está abierto, o null

  // Traduce los códigos de error de Firebase a mensajes en español
  const traducirErrorAuth = (code) => {
    const mapa = {
      'auth/email-already-in-use': 'Ese email ya tiene una cuenta. Prueba a iniciar sesión.',
      'auth/invalid-email': 'El email no es válido.',
      'auth/weak-password': `La contraseña debe tener al menos ${CONTRASENA_MIN} caracteres.`,
      'auth/network-request-failed': 'No hay conexión. Comprueba tu internet e inténtalo de nuevo.',
      'auth/user-not-found': 'No existe ninguna cuenta con ese email.',
      'auth/wrong-password': 'Contraseña incorrecta.',
      'auth/invalid-credential': 'Email o contraseña incorrectos.',
      'auth/too-many-requests': 'Demasiados intentos. Espera un momento y prueba de nuevo.',
    };
    return mapa[code] || 'Ha ocurrido un error. Inténtalo de nuevo.';
  };

  const generarCodigoAcceso = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let c = '';
    for (let i = 0; i < 6; i++) c += chars[Math.floor(Math.random() * chars.length)];
    return c;
  };

  // "Carné de miembro": un documento en la colección "miembros" que dice a qué restaurante
  // pertenece esta sesión. Hace falta porque los empleados que entran solo con el código usan
  // una sesión anónima, que por sí sola no dice nada de a qué negocio pertenecen — y sin eso
  // las reglas de seguridad no pueden distinguir a un empleado de tu bar del de cualquier otro.
  // Las reglas comprueban al crearlo que el código aportado corresponde de verdad a ese
  // restaurante, así que no se puede falsificar un carné de un negocio ajeno.
  // Devuelve true si quedó creado, false si no (por ejemplo, si el código ya no existe porque
  // el Encargado lo cambió).
  const crearCarneDeMiembro = async (restId, codigo) => {
    try {
      if (!auth.currentUser || !restId || !codigo) return false;
      await setDoc(doc(db, 'miembros', auth.currentUser.uid), {
        uid: restId,
        codigo,
        createdAt: serverTimestamp(),
      });
      return true;
    } catch (_) {
      return false;
    }
  };

  // Cambiar el código de acceso del restaurante. Crea uno nuevo y BORRA el viejo: al
  // desaparecer el código antiguo, todos los carnés creados con él dejan de valer al instante.
  // Eso es lo que deja fuera, por ejemplo, a un ex-empleado que se hubiera apuntado el código.
  const cambiarCodigoAcceso = async () => {
    if (!restaurantDocId) return;
    const anterior = config.accessCode;
    const ok = await confirmar({
      icono: '🔄',
      titulo: '¿Cambiar el código del restaurante?',
      lineas: [
        'El código actual dejará de funcionar al instante.',
        'Tu equipo tendrá que volver a entrar escribiendo el código nuevo. Quien tenga PIN propio no notará nada.',
      ],
      textoOk: 'Cambiar el código',
    });
    if (!ok) return;
    setCambiandoCodigo(true);
    try {
      const nuevo = generarCodigoAcceso();
      await setDoc(doc(db, 'codigos', nuevo), { uid: restaurantDocId });
      setConfig(c => ({ ...c, accessCode: nuevo }));
      if (anterior && anterior !== nuevo) {
        try { await deleteDoc(doc(db, 'codigos', anterior)); } catch (_) {}
      }
      try { localStorage.setItem(RESTAURANTE_RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId, codigo: nuevo })); } catch (_) {}
    } catch (e) {
      await avisar({ icono: '⚠️', titulo: 'No se ha podido cambiar el código', lineas: ['Comprueba tu conexión e inténtalo de nuevo.'] });
    }
    setCambiandoCodigo(false);
  };

  // Escucha si hay sesión iniciada en Firebase (aplica a Sala y a Cocina).
  // Firebase ya recuerda la sesión sola entre visitas; aquí conectamos ese
  // "recuerdo" con el resto de la app para que no haga falta volver a entrar.
  // Entrada de una cuenta real (Encargado o Dueño) ya autorizada a pasar. Es el mismo código que
  // antes vivía dentro del listener de sesión; se ha sacado aquí para poder usarlo también
  // cuando alguien confirma su email y pulsa "Ya lo he confirmado".
  const entrarCuentaReal = async (user) => {
    // Cuenta real: puede ser el Encargado (uid = uid del propio restaurante) o el Dueño
    // (persona distinta, con su propia cuenta, enlazada al restaurante en la colección
    // "duenos" porque su uid NO coincide con el del restaurante). Miramos primero si es
    // Dueño antes de asumir que es el Encargado.
    try {
      const duenoSnap = await getDoc(doc(db, 'duenos', user.uid));
      if (duenoSnap.exists() && duenoSnap.data()?.uid) {
        setUserRole('dueno');
        setCurrentUser('Dueño');
        setShowLogin(false);
        setRestaurantDocId(duenoSnap.data().uid);
        return;
      }
    } catch (_) {}
    // En Cocina, si es el mismo dueño (Encargado) que ya inició sesión en Sala,
    // entra directo sin pedirle nada más.
    setUserRole('jefe');
    setCurrentUser(L.jefeRoleName);
    setShowLogin(false);
    setRestaurantDocId(user.uid);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      setAuthChecking(false);

      const esCuentaPin = !!(user && !user.isAnonymous && user.email && user.email.endsWith('.horiaapp.local'));
      if (!user || user.isAnonymous || esCuentaPin) setEmailPendiente(null);
      if (user && !user.isAnonymous && !esCuentaPin) {
        // Antes de dejar pasar a una cuenta nueva, comprobamos que haya confirmado su correo.
        // Firebase puede traer guardado "no verificado" aunque ya lo haya confirmado hace un
        // momento en otra pestaña o en la app del correo, así que primero se refresca el dato.
        if (necesitaVerificarEmail(user)) {
          try { await user.reload(); } catch (_) {}
          if (necesitaVerificarEmail(auth.currentUser || user)) {
            setEmailPendiente(user.email);
            return;
          }
        }
        setEmailPendiente(null);
        await entrarCuentaReal(user);
      } else if (user && (user.isAnonymous || esCuentaPin)) {
        // Empleado/Cocinero, con o sin PIN: comprobamos si en este dispositivo ya había elegido
        // su nombre antes. IMPORTANTE: para cuentas con PIN, el uid del restaurante NO es
        // user.uid (esa cuenta es propia del empleado, creada aparte) — hay que leerlo de lo
        // que se guardó al iniciar sesión con el PIN, o la app apuntaría al restaurante equivocado.
        try {
          const recordado = JSON.parse(localStorage.getItem(RECORDADO_KEY) || 'null');
          if (recordado && recordado.uid && recordado.empId && recordado.nombre) {
            setUserRole(recordado.esEncargado ? 'jefe' : 'empleado');
            setCurrentUser(recordado.nombre);
            setShowLogin(false);
            setRestaurantDocId(recordado.uid);
            setSelectedEmployeeId(recordado.empId);
            try {
              const huella = JSON.parse(localStorage.getItem(HUELLA_KEY) || 'null');
              if (huella?.empId === recordado.empId && huella?.credentialId) setBloqueadoHuella(true);
            } catch (_) {}
          } else if (esCuentaPin) {
            // Cuenta de PIN sin registro local (p.ej. se borraron los datos del navegador):
            // no podemos saber a qué restaurante pertenece, así que pedimos entrar de nuevo.
            firebaseSignOut(auth).catch(() => {});
          }
          // Si es un anónimo sin persona concreta recordada, NO saltamos solos a ningún
          // sitio — la pantalla de inicio (Encargado/Empleado/Cocina) debe verse siempre
          // primero. El botón "Soy Empleado" es quien decide si hace falta pedir el código.
        } catch (_) {}
      }
    });
    return () => unsub();
  }, [isCocina]);

  // Carga inicial: Sala y Cocina se conectan a Firestore, cada una a su propia colección.
  // Empleados, horario y configuración viven en el documento principal (solo lo escribe el Encargado).
  useEffect(() => {
    if (!restaurantDocId) {
      // Al cerrar sesión, olvidamos qué restaurante era "el de antes" — así, entres luego con
      // la cuenta que entres (la misma u otra), el próximo login siempre parte de limpio.
      restaurantDocIdAnteriorRef.current = null;
      return;
    }
    // FALLO REAL ARREGLADO AQUÍ (21 sep 2026, encontrado por Fran: al crear una cuenta nueva
    // vio los datos de Duo Tapas en pantalla). NO es una fuga de datos entre cuentas en
    // Firestore — cada restaurante sigue aislado por su propio uid, eso nunca estuvo en riesgo.
    // El fallo es más simple y más tonto: cuando restaurantDocId cambia (cuenta nueva, cambio de
    // sesión...), el documento nuevo puede tardar un instante en llegar, o directamente no
    // existir todavía (una cuenta recién creada no tiene documento hasta que se guarda algo). En
    // ninguno de los dos casos había código que LIMPIARA lo que ya hubiera en memoria de la
    // sesión anterior — así que la pantalla se quedaba enseñando la última "foto" cargada
    // (empleados, horario, configuración...) de quien fuera que hubiese usado el dispositivo
    // antes, hasta que unos datos nuevos de verdad llegaran a sobreescribirla. Con una cuenta
    // recién creada y sin datos propios todavía, esa sobreescritura nunca llegaba.
    // Se limpia a lo vacío INMEDIATAMENTE al cambiar de restaurante (no se espera a que
    // responda Firestore), y así no hay ninguna ventana en la que se pueda ver algo que no toca.
    //
    // SEGUNDO FALLO REAL, ARREGLADO AQUÍ TAMBIÉN (22 sep 2026, encontrado por Fran: en mitad de
    // usar la app —viendo el horario, mandando un mensaje, lo que fuera— le aparecía de repente
    // la pantalla de bienvenida "HORIDIA / Te damos la bienvenida", como si la cuenta se hubiera
    // desconfigurado sola). Causa: este mismo efecto también se vuelve a ejecutar cada vez que
    // `reconnectKey` sube — y reconnectKey sube constantemente durante el uso normal (cada vez
    // que el móvil vuelve a primer plano: desbloquear pantalla, volver de otra app...). El
    // reseteo de arriba se disparaba en CADA una de esas reconexiones, no solo al cambiar de
    // restaurante de verdad — así que `config` se vaciaba un instante (con `configurado: false`)
    // y eso hacía aparecer el asistente de bienvenida de la nada, en mitad de cualquier cosa.
    // Arreglo: solo se limpia si `restaurantDocId` es REALMENTE distinto al de la última vez
    // (usando una ref para recordarlo entre renders) — una reconexión con el mismo restaurante
    // ya no toca nada de lo que hay en pantalla.
    if (restaurantDocIdAnteriorRef.current !== restaurantDocId) {
      setEmployees([]);
      setSchedulesPorLocal({});
      setPublicadosPorLocal({});
      setFechasPorLocal({});
      setProgramadoPorLocal({});
      setLocalActivoId(LOCAL_PRINCIPAL);
      setSchedule({});
      setSchedulePublished(false);
      setScheduleHistory([]);
      setSpecialDays({});
      setTemporada('normal');
      setTemporadas(DEFAULT_TEMPORADAS);
      setConfig(DEFAULT_CONFIG);
      // (19 oct 2026) Los datos de la cuenta (prueba/pago) tampoco pueden "heredarse" del
      // restaurante anterior que se hubiera abierto en este dispositivo.
      setCreadoEn(null); setCuentaActivada(false);
      setPagadoHasta(null); setPruebaHasta(null); setBloqueadaAMano(false); setUltimoRegalo(null);
      setPropinas([]);
      // TERCER FALLO REAL, ARREGLADO AQUÍ (29 sep 2026, tras el incidente con Duo Tapas):
      // este bloque limpiaba `config` (dejando `configurado: false`) pero NUNCA volvía a
      // poner `dataLoaded` en `false`. Si `dataLoaded` ya era `true` de una sesión anterior
      // (lo normal: ya se había cargado un restaurante antes), la pantalla de "Cargando tus
      // datos…" (que depende de `!dataLoaded`) se saltaba por completo, y durante el rato en
      // que Firestore tardaba en responder con los datos reales, la app veía `dataLoaded: true`
      // + `config.configurado: false` — justo la combinación que hace aparecer el asistente de
      // bienvenida, con la sesión real ya autenticada. Si en ese instante alguien lo rellena,
      // `handleIntroComplete` sobrescribe el restaurante real (ver el blindaje añadido ahí
      // mismo). Arreglo: volver a poner `dataLoaded` en `false` aquí, para que se vea el
      // "Cargando tus datos…" en vez del asistente mientras llega la respuesta real.
      setDataLoaded(false);
    }
    restaurantDocIdAnteriorRef.current = restaurantDocId;
    const unsub = onSnapshot(doc(db, FIRESTORE_COLLECTION, restaurantDocId), (snap) => {
      setDataError(false);
      if (snap.exists()) {
        const d = snap.data();
        setEmployees(d.employees || []);

        // FALLO REAL ARREGLADO AQUÍ (6 ago 2026): antes esto hacía siempre
        // `setSchedule(d.schedule || {})`, que es un campo suelto con "el último horario que se
        // guardó fuera cual fuera el local". Ahora se calcula qué local corresponde de verdad
        // (el que venga guardado en Firestore, o 'principal' si el documento es de antes de
        // tener locales) y se carga SU horario desde `schedulesPorLocal`. `d.schedule` se
        // conserva solo como respaldo, por si un documento viejo aún no tiene `schedulesPorLocal`.
        const localGuardado = d.localActivoId || LOCAL_PRINCIPAL;
        const mapaSchedules = d.schedulesPorLocal || {};
        const mapaPublicados = d.publicadosPorLocal || {};
        setSchedulesPorLocal(mapaSchedules);
        setPublicadosPorLocal(mapaPublicados);
        setFechasPorLocal(d.fechasPorLocal && typeof d.fechasPorLocal === 'object' ? d.fechasPorLocal : {});
        setProgramadoPorLocal(d.programadoPorLocal || {});
        setLocalActivoId(localGuardado);
        setSchedule(mapaSchedules[localGuardado] || d.schedule || {});
        setSchedulePublished(
          localGuardado in mapaPublicados ? mapaPublicados[localGuardado] : (d.schedulePublished || false)
        );

        setScheduleHistory(d.scheduleHistory || []);
        setSpecialDays(d.specialDays || {});
        setTemporada(d.temporada || 'normal'); setTemporadas(d.temporadas || DEFAULT_TEMPORADAS);
        // Si el local es de turno seguido y se creó con el fallo de los turnos de más de 8 h, se
        // corrige aquí (ver repararHorarioSeguido); con cualquier otra configuración devuelve la misma.
        setConfig(repararHorarioSeguido(d.config || DEFAULT_CONFIG));
        setCreadoEn(d.creadoEn || null);
        setCuentaActivada(!!d.cuentaActivada);
        setPagadoHasta(d.pagadoHasta || null);
        setPruebaHasta(d.pruebaHasta || null);
        setBloqueadaAMano(d.bloqueadaAMano === true);
        setUltimoRegalo(d.ultimoRegalo || null);
        setPropinas(Array.isArray(d.propinas) ? d.propinas : []);
      }
      // Si el documento NO existe (cuenta recién creada, todavía sin guardar nada), no hace
      // falta hacer nada más aquí — ya se dejó todo vacío arriba, antes de suscribirse.
      setDataLoaded(true);
    }, (err) => { console.error('Error Firestore:', err); setDataLoaded(true); setDataError(true); });
    return () => unsub();
  }, [restaurantDocId, FIRESTORE_COLLECTION, reconnectKey]);

  // Intenta una recarga automática, pero con un límite (máximo 2 veces seguidas guardado en
  // este mismo dispositivo) para no entrar en bucle infinito si hay un problema de red real. Si
  // se agota el límite, se queda quieta y deja la salida manual (botón "recargar") como último
  // recurso. La usa solo el vigilante de "Cargando tus datos…" — el de autenticación ya no
  // recarga (ver más abajo por qué).
  const intentarAutoRecarga = () => {
    try {
      const intentos = parseInt(sessionStorage.getItem('horia_auto_recargas') || '0', 10);
      if (intentos >= 2) return false;
      sessionStorage.setItem('horia_auto_recargas', String(intentos + 1));
      window.location.reload();
      return true;
    } catch (_) {
      window.location.reload();
      return true;
    }
  };

  // (22 sep 2026: el vigilante de "Cargando…" que había aquí ya no hace falta — con
  // authChecking arrancando en `false`, nunca se bloquea la pantalla, así que no hay nada que
  // vigilar ni ningún cuelgue posible en este punto.)

  // Vigilante de la pantalla "Cargando tus datos…": la primera conexión a Firestore puede
  // tardar varios segundos de verdad en una red móvil (4G/5G, primera conexión del día...),
  // así que le damos 6 segundos de margen real antes de considerarlo un cuelgue — si lo
  // dejamos demasiado corto, confundimos "va lenta" con "está pillada" y recargamos sin
  // necesidad, cortando la carga a mitad de camino (esto es justo lo que estaba pasando).
  // En cuanto los datos cargan bien una vez, reseteamos el contador de intentos para que
  // un futuro atasco real vuelva a tener sus 2 intentos limpios de recarga.
  useEffect(() => {
    setCargaLenta(false);
    if (!restaurantDocId || dataLoaded) {
      if (dataLoaded) { try { sessionStorage.removeItem('horia_auto_recargas'); } catch (_) {} }
      return;
    }
    const t = setTimeout(() => {
      const recargó = intentarAutoRecarga();
      if (!recargó) setCargaLenta(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [restaurantDocId, dataLoaded]);

  // El arreglo de fondo, sin apaños: cada vez que el móvil trae la app de nuevo a
  // primer plano (al pulsar atrás, desbloquear el teléfono, volver de otra app...),
  // forzamos que las conexiones en tiempo real con Firestore se vuelvan a suscribir
  // de cero. Así, si el navegador había dejado la conexión muerta mientras la app
  // estaba en segundo plano, se repara sola y en silencio — sin recargar la página
  // ni que la persona note nada, simplemente los datos se refrescan solos.
  useEffect(() => {
    const reconectar = () => setReconnectKey(k => k + 1);
    const alCambiarVisibilidad = () => { if (document.visibilityState === 'visible') reconectar(); };
    const alVolverMostrar = () => reconectar();
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    window.addEventListener('pageshow', alVolverMostrar);
    window.addEventListener('focus', reconectar);
    return () => {
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('pageshow', alVolverMostrar);
      window.removeEventListener('focus', reconectar);
    };
  }, []);

  // Botón "Atrás" del navegador/móvil: sin esto, el navegador no sabe que dentro de esta
  // única página hay varias "pantallas" (inicio, código de empleado, cargando datos...),
  // así que al pulsar atrás hace lo que hace por defecto: recargar la página entera —
  // volviendo a descargar el JS y a reconectar todo desde cero, de ahí la tardanza.
  // Guardamos en una "ref" el estado actual de la pantalla para poder consultarlo desde
  // el listener de atrás sin tener que recrearlo cada vez que cambia algo.
  const pantallaActualRef = useRef({ restaurantDocId, authScreen, showLogin });
  useEffect(() => {
    pantallaActualRef.current = { restaurantDocId, authScreen, showLogin };
  }, [restaurantDocId, authScreen, showLogin]);

  useEffect(() => {
    // Dejamos "marcada" una entrada de historial nada más entrar en la app. Si luego
    // navegamos a una pantalla intermedia (código, login, cargando...) sin marcar una
    // entrada nueva, el primer "atrás" siempre nos deja en esa marca inicial: la
    // aprovechamos para, en vez de dejar que el navegador recargue, volver nosotros
    // mismos (al instante, sin red) a la pantalla de inicio.
    try { window.history.pushState({ horia: true }, ''); } catch (_) {}

    const alPulsarAtras = () => {
      const { restaurantDocId: rdId, authScreen: aScreen } = pantallaActualRef.current;
      // OJO, aquí estaba el fallo: antes esta línea exigía además "&& sLogin", y eso no podía
      // cumplirse NUNCA. La app tiene dos pantallas de inicio distintas y son excluyentes: si
      // showLogin está activo se muestra la clásica, y si no, la de authScreen === 'inicio'.
      // Pedir las dos a la vez hacía que el atrás nunca reconociera que ya estabas en el inicio,
      // así que siempre ejecutaba el reseteo completo — de ahí el rato cargando.
      const yaEstabaEnInicio = !rdId && aScreen === 'inicio';
      if (!yaEstabaEnInicio) {
        // Veníamos de una pantalla intermedia: cortamos aquí en vez de dejar que
        // el navegador recargue, y volvemos a la pantalla de inicio al momento.
        setRestaurantDocId(null);
        setDataLoaded(false);
        setUserRole('');
        setAuthScreen('inicio');
        setShowLogin(true);
        setSelectedEmployeeId(null);
        setAuthError('');
        // Volvemos a marcar una entrada de historial, para poder seguir capturando
        // el siguiente "atrás" del mismo modo.
        try { window.history.pushState({ horia: true }, ''); } catch (_) {}
      }
      // Si ya estábamos en la pantalla de inicio, no marcamos nada nuevo: así, el
      // siguiente "atrás" hace lo normal (salir de la app), como espera cualquiera.
    };
    window.addEventListener('popstate', alPulsarAtras);
    return () => window.removeEventListener('popstate', alPulsarAtras);
  }, []);

  // Notificaciones push: en cuanto sabemos quién es la persona y en qué restaurante está,
  // intentamos pedir permiso automáticamente. Esto funciona bien en Android y ordenador, pero
  // en iPhone Apple exige que el permiso se pida como reacción directa a un toque de la
  // persona — un intento automático ahí no hace nada, en silencio. Por eso la misma lógica
  // vive en una función aparte (activarNotificaciones), reutilizada también por un botón
  // manual en la cabecera ("🔔 Activar notificaciones"), que sí cuenta como un toque directo
  // y funciona en iPhone también.
  const [notifPermiso, setNotifPermiso] = useState('default');

  const activarNotificaciones = useCallback(async () => {
    if (!restaurantDocId || !currentUser) return;
    const notifDocIdRaw = selectedEmployeeId || firebaseUser?.uid;
    if (!notifDocIdRaw) return;
    const notifDocId = String(notifDocIdRaw);
    try {
      const messaging = await messagingPromise;
      if (!messaging || typeof Notification === 'undefined') return;
      const permiso = await Notification.requestPermission();
      setNotifPermiso(permiso);
      if (permiso !== 'granted') return;
      const token = await getToken(messaging, { vapidKey: VAPID_KEY });
      if (!token) return;
      // (29 oct 2026) `uid`: qué cuenta guarda este aviso. Las reglas exigen que sea la propia, y
      // el aviso de un mensaje privado solo se manda si es la cuenta de PIN de esa persona.
      const uidAviso = auth.currentUser?.uid;
      if (!uidAviso) return;
      await setDoc(
        doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'tokens', notifDocId),
        { name: currentUser, token, uid: uidAviso, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (_) {
      // Silencioso a propósito: un fallo aquí nunca debe impedir que la persona siga
      // usando Horidia con normalidad.
    }
  }, [restaurantDocId, currentUser, selectedEmployeeId, firebaseUser, FIRESTORE_COLLECTION]);

  useEffect(() => {
    if (!restaurantDocId || !dataLoaded || !currentUser) return;
    if (typeof Notification !== 'undefined') setNotifPermiso(Notification.permission);
    activarNotificaciones();
  }, [restaurantDocId, dataLoaded, currentUser, activarNotificaciones]);

  // Mensajes en primer plano: si Horidia está abierta cuando llega un aviso (publicación de
  // horario o mensaje privado), Firebase no muestra sola una notificación del sistema —
  // por eso la mostramos nosotros a mano, igual que se vería con la app cerrada.
  useEffect(() => {
    let unsub = () => {};
    let cancelado = false;
    (async () => {
      const messaging = await messagingPromise;
      if (!messaging || cancelado) return;
      unsub = onMessage(messaging, async (payload) => {
        try {
          if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
          const titulo = payload.data?.title || 'Horidia';
          // FALLO REAL ARREGLADO (13 oct 2026): antes se usaba `new Notification(...)`, que Chrome
          // en ANDROID no permite ("Illegal constructor") — fallaba en silencio y, con la app
          // abierta, el aviso de "Horario publicado" (o un mensaje) nunca se veía en el móvil.
          // En Android hay que mostrarlo a través del service worker, que además es quien sabe
          // llevar a la pantalla correcta al tocar el aviso. Mismo aspecto que con la app cerrada.
          const opciones = {
            body: payload.data?.body || '',
            icon: '/icon-512.png',
            badge: '/icon-badge-mono.png',
            data: payload.data || {},
            vibrate: [200, 100, 200],
            tag: payload.data?.tipo || 'horia',
            renotify: true,
          };
          let reg = null;
          try {
            if ('serviceWorker' in navigator) {
              reg = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope');
              if (!reg) {
                const todas = await navigator.serviceWorker.getRegistrations();
                reg = todas.find(r => (r.active?.scriptURL || '').includes('firebase-messaging-sw')) || todas[0] || null;
              }
            }
          } catch (_) {}
          if (reg && typeof reg.showNotification === 'function') {
            await reg.showNotification(titulo, opciones);
          } else {
            new Notification(titulo, { body: opciones.body, icon: opciones.icon }); // ordenador
          }
        } catch (_) {}
      });
    })();
    return () => { cancelado = true; unsub(); };
  }, []);

  // Mensajes del chat: viven en su propia colección, cada uno con el uid de quien lo escribió.
  // Así, aunque alguien intente saltarse la app, solo puede escribir mensajes en su propio nombre.
  useEffect(() => {
    if (!restaurantDocId) return;
    // Se piden los más NUEVOS primero (desc) para poder quedarnos con los últimos, y después se
    // le da la vuelta al array para mostrarlos en orden normal, del más viejo al más reciente.
    const q = query(
      collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'mensajes'),
      orderBy('createdAt', 'desc'),
      limit(MAX_MENSAJES_GRUPO),
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse());
    }, (err) => console.error('Error cargando mensajes:', err));
    return () => unsub();
  }, [restaurantDocId, FIRESTORE_COLLECTION, reconnectKey]);

  // === MENSAJES PRIVADOS SOLO CON PIN (29 oct 2026, pedido por Fran) ===================
  // Antes todos los privados del bar estaban juntos y los podía pedir a Firebase cualquiera del
  // bar; y sin PIN cualquiera podía entrar con el nombre de otro y leer los suyos. Ahora cada
  // persona tiene su BUZÓN (privados/{clave}/mensajesPrivados) y solo lo puede leer ella:
  //  · el Encargado/Jefe de Cocina principal (cuenta de email) → clave 'jefe';
  //  · quien ha entrado con SU PIN → clave 'e' + id de su ficha.
  // Sin PIN no hay privados: se le pide el PIN (o que se lo pida a su encargado).
  // Al abrir la app se llama a la función `prepararPrivados`: sella la cuenta de PIN con su ficha
  // (para que las reglas sepan quién es) y copia a los buzones los mensajes de antes.
  const miFichaSesion = employees.find(e => e.id === selectedEmployeeId) || null;
  const uidSesion = firebaseUser?.uid || null;
  const esSesionPinPropia = !!(firebaseUser && !firebaseUser.isAnonymous && miFichaSesion?.authEmail
    && firebaseUser.email === miFichaSesion.authEmail);
  let miClavePrivados = null;
  if (restaurantDocId && firebaseUser && userRole !== 'dueno') {
    if (userRole === 'jefe' && firebaseUser.uid === restaurantDocId) miClavePrivados = 'jefe';
    else if (esSesionPinPropia) miClavePrivados = `e${miFichaSesion.id}`;
  }
  const clavePrivadosDeFicha = (emp) => `e${emp.id}`;
  // ¿Puede recibir privados este contacto? La cuenta principal siempre; un empleado, solo si
  // tiene PIN; alguien que ya no está en la plantilla, nunca.
  const puedeRecibirPrivados = (c) => {
    if (!c) return false;
    if (c.clave === 'jefe') return true;
    const ficha = employees.find(e => clavePrivadosDeFicha(e) === c.clave);
    return !!(ficha && ficha.authUid);
  };
  const [privadosListos, setPrivadosListos] = useState(false);
  const [privadosError, setPrivadosError] = useState('');
  useEffect(() => {
    setPrivadosListos(false);
    setPrivadosError('');
    if (!miClavePrivados || !restaurantDocId || !uidSesion) return;
    let cancelado = false;
    // La cuenta principal no depende del sello: empieza a escuchar ya y la copia de los
    // mensajes antiguos se hace a la vez (van apareciendo según se copian).
    if (miClavePrivados === 'jefe') setPrivadosListos(true);
    (async () => {
      try {
        const r = await httpsCallable(functionsInstance, 'prepararPrivados')({ coleccion: FIRESTORE_COLLECTION, restId: restaurantDocId });
        if (r?.data?.refrescar && auth.currentUser) await auth.currentUser.getIdToken(true);
      } catch (e) {
        // Si falla, se intenta escuchar igualmente (puede que la cuenta ya estuviera sellada). Si
        // de verdad no puede, el error sale abajo, en la propia escucha.
        console.error('[privados] prepararPrivados:', e?.code, e?.message);
      }
      if (!cancelado) setPrivadosListos(true);
    })();
    return () => { cancelado = true; };
  }, [miClavePrivados, restaurantDocId, FIRESTORE_COLLECTION, uidSesion]);

  // Mi buzón: solo mis conversaciones, ya filtradas por las propias reglas de Firestore.
  useEffect(() => {
    if (!restaurantDocId || !miClavePrivados || !privadosListos) { setPrivateMessages([]); return; }
    const q = query(
      collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'privados', miClavePrivados, 'mensajesPrivados'),
      orderBy('createdAt', 'desc'),
      limit(MAX_MENSAJES_PRIVADOS),
    );
    const unsub = onSnapshot(q, (snap) => {
      setPrivadosError('');
      setPrivateMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse());
    }, (err) => {
      console.error('Error cargando mensajes privados:', err);
      setPrivadosError('No se han podido cargar tus mensajes privados. Cierra la app y vuelve a abrirla.');
    });
    return () => unsub();
  }, [restaurantDocId, FIRESTORE_COLLECTION, miClavePrivados, privadosListos, reconnectKey]);

  // Entrar con el PIN desde la propia pantalla de mensajes, sin salir de la app (para quien
  // eligió su nombre sin PIN y luego el encargado le creó uno).
  const [pinPrivados, setPinPrivados] = useState('');
  const [pinPrivadosBusy, setPinPrivadosBusy] = useState(false);
  const [pinPrivadosError, setPinPrivadosError] = useState('');
  const entrarConPinParaPrivados = async () => {
    const yo = miFichaSesion;
    if (!yo?.authEmail || pinPrivadosBusy) return;
    const pin = pinPrivados.trim();
    if (!/^\d{6}$/.test(pin)) { setPinPrivadosError('El PIN tiene 6 números.'); return; }
    setPinPrivadosBusy(true); setPinPrivadosError('');
    try {
      // Igual que en la pantalla de PIN normal: se guarda ANTES de entrar (ver handlePinSubmit).
      try { localStorage.setItem(RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId, empId: yo.id, nombre: yo.name, esEncargado: !!yo.esEncargado })); } catch (_) {}
      try { localStorage.setItem(RESTAURANTE_RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId })); } catch (_) {}
      await signInWithEmailAndPassword(auth, yo.authEmail, pin);
      setPinPrivados('');
    } catch (e) {
      setPinPrivadosError(e?.code === 'auth/too-many-requests'
        ? 'Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.'
        : 'Ese PIN no es correcto. Si no lo recuerdas, pídele a tu encargado que te genere uno nuevo.');
    }
    setPinPrivadosBusy(false);
  };

  // Solicitudes de días libres: también en su propia colección, cada una ligada al uid de quien la pidió.
  // Solo el Encargado puede aprobarlas o rechazarlas; cada empleado solo puede crear las suyas.
  useEffect(() => {
    if (!restaurantDocId) return;
    const q = query(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes'), orderBy('createdAtTs', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Error cargando solicitudes:', err));
    return () => unsub();
  }, [restaurantDocId, FIRESTORE_COLLECTION, reconnectKey]);

  // GASTOS DEL NEGOCIO: un local y un mes a la vez (claveMes = "{localId}_{YYYY-MM}"), igual de
  // local que Carta y Mesas. A propósito, la query NO lleva `orderBy` — combinar un `where` de
  // igualdad con un `orderBy` sobre OTRO campo distinto a veces exige crear un índice compuesto a
  // mano en la consola de Firestore (la app se rompería hasta que Fran lo creara, sin avisar
  // antes). Pidiendo solo el `where` nunca hace falta índice nuevo, y con unas pocas decenas de
  // apuntes al mes, ordenar en el propio navegador no tiene ningún coste perceptible.
  // `localParaGastosId`: el Encargado/Jefe de Cocina mira su propio local activo de siempre; el
  // Dueño (mismo componente, misma colección, SOLO LECTURA) mira el local que tenga abierto en su
  // pantalla — por eso ambos casos comparten esta única suscripción en vez de duplicarla.
  const localParaGastosId = userRole === 'dueno' ? duenoLocalAbierto : localActivoId;
  // FALLO REAL ARREGLADO (19 oct 2026): el panel de Gastos del Dueño está en su pantalla
  // principal, donde NO hay ningún local abierto (duenoLocalAbierto = null). Con eso esta
  // suscripción no pedía nada y el Dueño veía siempre "0,00 €" aunque hubiera gastos apuntados.
  // Ahora, en esa pantalla principal, el Dueño ve la suma de TODOS sus locales del mes (una sola
  // consulta con "in", que no necesita índices nuevos en Firestore).
  const clavesGastos = (userRole === 'dueno' && !duenoLocalAbierto)
    ? locales.slice(0, 30).map(l => `${l.id}_${gastosMesVisto}`)
    : (localParaGastosId ? [`${localParaGastosId}_${gastosMesVisto}`] : []);
  const clavesGastosKey = clavesGastos.join('|');
  useEffect(() => {
    const claves = clavesGastosKey ? clavesGastosKey.split('|') : [];
    if (!restaurantDocId || !claves.length) { setGastos([]); return; }
    const ref = collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'gastos');
    const q = claves.length === 1
      ? query(ref, where('claveMes', '==', claves[0]))
      : query(ref, where('claveMes', 'in', claves));
    const unsub = onSnapshot(q, (snap) => {
      const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      lista.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')); // más reciente primero
      setGastos(lista);
    }, (err) => console.error('Error cargando gastos:', err));
    return () => unsub();
  }, [restaurantDocId, FIRESTORE_COLLECTION, clavesGastosKey, reconnectKey]);

  const cambiarMesGastos = (delta) => {
    const [y, m] = gastosMesVisto.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setGastosMesVisto(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const anadirGasto = async () => {
    if (!restaurantDocId || !localActivoId) return;
    const importeNum = parseFloat((gastoImporte || '').replace(',', '.'));
    if (!gastoConcepto.trim()) { setGastoError('Escribe un concepto.'); return; }
    if (!importeNum || importeNum <= 0) { setGastoError('Pon un importe mayor que 0.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(gastoFecha || '')) { setGastoError('Pon la fecha del gasto.'); return; }
    setGuardandoGasto(true);
    setGastoError(null);
    try {
      let facturaUrl = null, facturaPath = null;
      if (gastoFoto) {
        // Misma compresora que ya usa la foto de la carta — reduce una foto de móvil (varios MB)
        // a 150-300 KB sin que se note al verla. Con eso, ni cientos de facturas al año llegan a
        // pesar nada perceptible en los 5 GB gratis de Firebase Storage.
        const comprimida = await comprimirFotoCarta(gastoFoto);
        facturaPath = `${FIRESTORE_COLLECTION}/${restaurantDocId}/gastos/${localActivoId}/${Date.now()}_${Math.random().toString(36).slice(2, 7)}.jpg`;
        const ref = storageRef(storage, facturaPath);
        await uploadBytes(ref, comprimida, { contentType: 'image/jpeg' });
        facturaUrl = await getDownloadURL(ref);
      }
      await addDoc(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'gastos'), {
        local: localActivoId,
        // El mes en que se archiva es el de la FECHA del gasto (antes era el mes que estuvieras
        // mirando en pantalla: un gasto del 30 de septiembre apuntado mirando octubre se iba a octubre).
        claveMes: `${localActivoId}_${gastoFecha.slice(0, 7)}`,
        fecha: gastoFecha,
        categoria: gastoCategoria,
        concepto: gastoConcepto.trim().slice(0, 80),
        importe: Math.round(importeNum * 100) / 100,
        facturaUrl, facturaPath,
        creadoEn: serverTimestamp(),
      });
      setGastoConcepto('');
      setGastoImporte('');
      setGastoFoto(null);
      // Si era de otro mes, se salta a ese mes para que se vea que se ha guardado.
      if (gastoFecha.slice(0, 7) !== gastosMesVisto) setGastosMesVisto(gastoFecha.slice(0, 7));
    } catch (err) {
      console.error('Error guardando gasto:', err);
      setGastoError('No se ha podido guardar. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setGuardandoGasto(false);
    }
  };

  const eliminarGasto = async (gasto) => {
    const ok = await confirmar({
      icono: '🗑️',
      titulo: '¿Borrar este gasto?',
      lineas: [`"${gasto.concepto}" — ${(gasto.importe || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}€`, 'No se puede deshacer.'],
      textoOk: 'Borrar', textoCancelar: 'Cancelar', peligro: true,
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'gastos', gasto.id));
      if (gasto.facturaPath) {
        try { await deleteObject(storageRef(storage, gasto.facturaPath)); } catch (_) {
          // si la foto ya no está en Storage por lo que sea, no pasa nada — lo que importa es
          // que el apunte ya se ha borrado de Firestore, que es lo de arriba.
        }
      }
    } catch (err) {
      console.error('Error borrando gasto:', err);
    }
  };

  // Contador animado del total del mes (sube/baja suavemente al cambiar de mes o al añadir/borrar
  // un apunte, en vez de cambiar de golpe). Usa una ref para recordar desde dónde partir, así no
  // hace falta meter `totalGastosAnimado` en las dependencias del efecto (evitaría un bucle: cada
  // fotograma cambia ese mismo estado que dispararía el efecto otra vez).
  const totalGastosMes = gastos.reduce((s, g) => s + (g.importe || 0), 0);
  useEffect(() => {
    const inicio = totalGastosAnimadoAnteriorRef.current;
    const fin = totalGastosMes;
    const t0 = performance.now();
    let frame;
    const paso = (t) => {
      const p = Math.min(1, (t - t0) / 500);
      const suavizado = 1 - Math.pow(1 - p, 3); // ease-out cúbico
      setTotalGastosAnimado(inicio + (fin - inicio) * suavizado);
      if (p < 1) frame = requestAnimationFrame(paso);
      else totalGastosAnimadoAnteriorRef.current = fin;
    };
    frame = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(frame);
  }, [totalGastosMes]);

  // Panel de Gastos, como función y no como JSX suelto, porque se usa en DOS sitios con el mismo
  // aspecto: la pestaña del Encargado/Jefe de Cocina (con formulario para añadir y borrar) y la
  // vista del Dueño (soloLectura=true, sin formulario ni botón de borrar). Así nunca se puede
  // quedar un sitio desactualizado respecto al otro.
  const renderGastosPanel = ({ soloLectura = false } = {}) => (
    <div>
      <div style={{ ...card, background: C.primaryFaint, border: `1px solid ${C.primaryBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <button onClick={() => cambiarMesGastos(-1)} style={btn('outline', 'sm')}>←</button>
          <p style={{ margin: 0, fontWeight: '800', fontSize: '15px', color: C.primaryDark }}>
            {/* 16 oct 2026: antes 'capitalize' ponía "Octubre De 2026"; ahora solo la primera letra. */}
            {(() => { const t = new Date(gastosMesVisto + '-02').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); return t.charAt(0).toUpperCase() + t.slice(1); })()}
          </p>
          <button onClick={() => cambiarMesGastos(1)} style={btn('outline', 'sm')}>→</button>
        </div>
        <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '1px' }}>Total del mes</p>
        <p style={{ margin: '0 0 16px', fontSize: '32px', fontWeight: '900', color: C.primaryDark }}>
          {totalGastosAnimado.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          {GASTO_CATEGORIAS.map(cat => {
            const totalCat = gastos.filter(g => g.categoria === cat.id).reduce((s, g) => s + (g.importe || 0), 0);
            const pct = totalGastosMes > 0 ? (totalCat / totalGastosMes * 100) : 0;
            return (
              <div key={cat.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', color: C.text, marginBottom: '3px' }}>
                  <span>{conIconos(cat.emoji)} {cat.label}</span>
                  <span>{totalCat.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div style={{ height: '7px', borderRadius: '4px', background: C.white, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: cat.color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!soloLectura && (
        <div style={card}>
          <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="plus" /> Añadir gasto</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {GASTO_CATEGORIAS.map(cat => (
              <button key={cat.id} onClick={() => setGastoCategoria(cat.id)}
                style={{
                  padding: '8px 14px', borderRadius: '999px', border: `1.5px solid ${gastoCategoria === cat.id ? cat.color : C.border}`,
                  background: gastoCategoria === cat.id ? cat.bg : C.white, color: gastoCategoria === cat.id ? cat.color : C.textMuted,
                  fontWeight: '700', fontSize: '13px', cursor: 'pointer',
                }}>
                {conIconos(cat.emoji)} {cat.label}
              </button>
            ))}
          </div>
          <input type="text" placeholder="Concepto (ej: Pedido Makro, Nómina Juan…)" value={gastoConcepto} maxLength={80}
            onChange={e => setGastoConcepto(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <input type="date" value={gastoFecha} onChange={e => setGastoFecha(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '14px' }} />
            <input type="text" inputMode="decimal" placeholder="Importe €" value={gastoImporte}
              onChange={e => setGastoImporte(e.target.value)}
              style={{ width: '110px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '14px' }} />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: C.textMuted, cursor: 'pointer', marginBottom: '12px' }}>
            <Ico n="camera" /> {gastoFoto ? gastoFoto.name.slice(0, 24) : 'Foto de la factura (opcional)'}
            <input type="file" accept="image/*" capture="environment" onChange={e => setGastoFoto(e.target.files?.[0] || null)} style={{ display: 'none' }} />
          </label>
          {gastoError && <p style={{ margin: '0 0 10px', fontSize: '13px', color: C.danger, fontWeight: '600' }}>{gastoError}</p>}
          <button onClick={anadirGasto} disabled={guardandoGasto} style={{ ...btn('primary'), width: '100%', opacity: guardandoGasto ? 0.7 : 1 }}>
            {guardandoGasto ? 'Guardando…' : '✓ Añadir gasto'}
          </button>
        </div>
      )}

      <div style={card}>
        <p style={{ margin: '0 0 12px', fontWeight: '700', color: C.text, fontSize: '15px' }}>Apuntes del mes{gastos.length > 0 && ` (${gastos.length})`}</p>
        {gastos.length === 0 ? (
          <EmptyState icon="💰" text="Todavía no hay ningún gasto apuntado este mes." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {gastos.map((g, i) => {
              const cat = GASTO_CATEGORIAS_POR_ID[g.categoria] || GASTO_CATEGORIAS_POR_ID.otros;
              return (
                <div key={g.id} className="horia-gasto-fila" style={{ animationDelay: `${Math.min(i, 10) * 0.03}s`, display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: C.bg }}>
                  <span style={{ width: '34px', height: '34px', borderRadius: '9px', background: cat.bg, display: 'grid', placeItems: 'center', fontSize: '16px', flexShrink: 0 }}>{conIconos(cat.emoji)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.concepto}</p>
                    <p style={{ margin: 0, fontSize: '11.5px', color: C.textMuted }}>{fechaLegible(g.fecha) || g.fecha}</p>
                  </div>
                  {g.facturaUrl && (
                    <a href={g.facturaUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', flexShrink: 0 }} title="Ver factura"><Ico n="receipt" /></a>
                  )}
                  <span style={{ fontWeight: '800', color: cat.color, fontSize: '14px', flexShrink: 0 }}>{(g.importe || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                  {!soloLectura && (
                    <button onClick={() => eliminarGasto(g)} style={{ border: 'none', background: 'none', color: C.textLight, fontSize: '16px', cursor: 'pointer', flexShrink: 0, padding: '4px' }}><Ico n="trash" /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes horiaGastoEntrada { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .horia-gasto-fila { animation: horiaGastoEntrada 0.35s ease backwards; }
      `}</style>
    </div>
  );

  const guardarNombreNegocio = async () => {
    if (!restaurantDocId) return;
    setGuardandoNombreNegocio(true);
    setNombreNegocioGuardadoOk(false);
    try {
      await setDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId), { config }, { merge: true });
      setNombreNegocioGuardadoOk(true);
      setTimeout(() => setNombreNegocioGuardadoOk(false), 2500);
    } catch (err) {
      console.error('Error guardando el nombre del negocio:', err);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido guardar', lineas: ['Revisa tu conexión e inténtalo de nuevo. Si sigue fallando, avisa antes de continuar.'] });
    } finally {
      setGuardandoNombreNegocio(false);
    }
  };

  const save = useCallback((extra = {}) => {
    const managerUids = employees.filter(e => e.esEncargado && e.authUid).map(e => e.authUid);
    // Lista plana de las cuentas de TODOS los empleados que tienen PIN. Las reglas de Firestore
    // no saben leer dentro de la lista de empleados (es una lista de objetos), así que se guarda
    // aparte para que puedan comprobar de un vistazo si quien pide los datos pertenece de verdad
    // a este restaurante. Sin esto, un empleado con PIN no se distingue del de cualquier otro.
    const authUids = employees.filter(e => e.authUid).map(e => e.authUid);
    // El horario que se está editando se guarda además en su hueco de `schedulesPorLocal`, para
    // que no se pierda al cambiar de local ni al recargar.
    const schedulesFinal = { ...schedulesPorLocal, [localActivoId]: schedule };
    const publicadosFinal = { ...publicadosPorLocal, [localActivoId]: schedulePublished };
    // FALLO REAL ENCONTRADO (6 ago 2026): antes `localActivoId` era solo estado de React y nunca
    // se guardaba. Al recargar la página siempre volvía a 'principal', pero el horario que se
    // veía era el último que se había guardado en el campo suelto `schedule` — así que el
    // selector decía un local y la tabla enseñaba el de otro. Ahora se persiste, para que al
    // recargar se sepa de verdad qué local se estaba viendo.
    const localActivoFinal = localActivoId;
    const payload = {
      employees, schedule, scheduleHistory, specialDays,
      schedulePublished, temporada, temporadas, config, managerUids, authUids,
      schedulesPorLocal: schedulesFinal, publicadosPorLocal: publicadosFinal, fechasPorLocal,
      localActivoId: localActivoFinal, ...extra
    };
    if (restaurantDocId) {
      setDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId), payload, { merge: true }).catch(err => console.error('Error guardando en Firestore:', err));
    }
  }, [employees, schedule, scheduleHistory, specialDays, schedulePublished, temporada, temporadas, config, restaurantDocId, FIRESTORE_COLLECTION, schedulesPorLocal, publicadosPorLocal, fechasPorLocal, localActivoId]);

  useEffect(() => {
    // Solo el Encargado (dueño o gestor con PIN) puede escribir el documento completo del
    // restaurante — las reglas de Firestore ya lo exigen así. Si alguien más (un empleado)
    // tiene la app abierta, no tiene sentido ni permiso intentar guardar, así que nos lo ahorramos.
    if (dataLoaded && restaurantDocId && userRole === 'jefe') save();
  }, [employees, schedule, scheduleHistory, specialDays, schedulePublished, temporada, temporadas, config, fechasPorLocal]);

  // La primera vez que se sabe qué empleado es (tras el login), si tiene un local fijo asignado
  // el móvil arranca mostrando ESE local en vez del primero de la lista. Solo una vez por sesión
  // (con el ref como cerrojo): si el propio empleado cambia de pestaña a mano después, no se le
  // vuelve a mover solo.
  useEffect(() => {
    if (miLocalInicializadoRef.current) return;
    if (userRole !== 'empleado' || !selectedEmployeeId) return;
    const yo = employees.find(e => e.id === selectedEmployeeId);
    if (!yo) return;
    if (yo.localFijo) setMiLocalVistaId(yo.localFijo);
    miLocalInicializadoRef.current = true;
  }, [userRole, employees, selectedEmployeeId]);

  const handleIntroComplete = async (newConfig, rolElegido) => {
    if (rolElegido === 'empleado') {
      setEmployeeWaitingNotice(true);
      return;
    }

    const updatedConfig = { ...newConfig };

    const nuevosEncargados = (newConfig.encargados || []).map(nombre => ({
      id: Date.now() + Math.random(),
      name: nombre,
      turnosSemanales: 5,
      esEncargado: true,
      diasFijosLibres: [],
      joinedAt: new Date().toLocaleDateString('es-ES')
    }));

    setAuthBusy(true);
    setAuthError('');

    if (isCocina) {
      try {
        const uid = firebaseUser.uid;
        // BLINDAJE (29 sep 2026, tras el incidente real con Duo Tapas): antes de escribir,
        // comprobamos que Cocina no tenga YA datos reales guardados. Si los tiene (ya estaba
        // configurada, con empleados de verdad), NO se sobrescribe nunca — se avisa y se para
        // ahí, por muy raro que sea que esta pantalla haya aparecido. Esto protege los datos
        // pase lo que pase con el motivo por el que se llegó hasta aquí.
        const cocinaSnapPrevio = await getDoc(doc(db, 'cocinas', uid));
        if (cocinaSnapPrevio.exists()) {
          const dPrevio = cocinaSnapPrevio.data();
          const yaTeniaDatos = dPrevio?.config?.configurado === true || (dPrevio?.employees || []).length > 0;
          if (yaTeniaDatos) {
            setIntroBlockedMessage('Esta Cocina ya tiene datos guardados. Por seguridad, Horidia no los ha sobrescrito. Cierra esta pantalla, vuelve a entrar y avisa antes de continuar.');
            setAuthBusy(false);
            return;
          }
        }
        // Heredamos el código de acceso de Sala, para no tener que generar uno nuevo
        let codigoHeredado = updatedConfig.accessCode;
        try {
          const salaSnap = await getDoc(doc(db, 'restaurantes', uid));
          if (salaSnap.exists()) codigoHeredado = salaSnap.data()?.config?.accessCode || codigoHeredado;
        } catch (_) {}
        const finalConfig = { ...updatedConfig, accessCode: codigoHeredado, configurado: true };
        await setDoc(doc(db, 'cocinas', uid), {
          employees: nuevosEncargados, schedule: {},
          specialDays: {}, schedulePublished: false, temporada: 'normal',
          temporadas: DEFAULT_TEMPORADAS, config: finalConfig,
          // Fecha de alta para la prueba de 14 días — se pone AQUÍ, solo al crear de verdad.
          creadoEn: serverTimestamp(),
        });
        setEmployees(nuevosEncargados);
        setConfig(finalConfig);
        setDataLoaded(true);
      } catch (e) {
        setAuthError('No se pudo crear Cocina: ' + e.message);
      }
      setAuthBusy(false);
      return;
    }

    // Sala: creamos el restaurante en Firestore con un código de acceso para los empleados
    try {
      const uid = firebaseUser.uid;
      // BLINDAJE (29 sep 2026, tras el incidente real con Duo Tapas): mismo blindaje que en
      // Cocina justo arriba. Antes de crear nada, comprobamos que este restaurante no tenga YA
      // datos reales. Es la última línea de defensa: pase lo que pase con por qué ha aparecido
      // esta pantalla, nunca puede volver a sobrescribir un restaurante que ya existía.
      const salaSnapPrevio = await getDoc(doc(db, 'restaurantes', uid));
      if (salaSnapPrevio.exists()) {
        const dPrevio = salaSnapPrevio.data();
        const yaTeniaDatos = dPrevio?.config?.configurado === true || (dPrevio?.employees || []).length > 0;
        if (yaTeniaDatos) {
          setIntroBlockedMessage('Este restaurante ya tiene datos guardados. Por seguridad, Horidia no los ha sobrescrito. Cierra esta pantalla, vuelve a entrar y avisa antes de continuar.');
          setAuthBusy(false);
          return;
        }
      }
      const codigo = generarCodigoAcceso();
      const finalConfig = { ...updatedConfig, accessCode: codigo, configurado: true };
      await setDoc(doc(db, 'codigos', codigo), { uid });
      await setDoc(doc(db, 'restaurantes', uid), {
        employees: nuevosEncargados, schedule: {},
        specialDays: {}, schedulePublished: false, temporada: 'normal',
        temporadas: DEFAULT_TEMPORADAS, config: finalConfig,
        // Fecha de alta para la prueba de 14 días — se pone AQUÍ, solo al crear de verdad.
        creadoEn: serverTimestamp(),
        // Constancia de que el Encargado aceptó las Condiciones (con su contrato de encargo de
        // datos) al registrarse. Si esta pantalla se completa en otra sesión distinta a la del
        // registro, ya no se conoce la fecha exacta, pero la casilla fue obligatoria igualmente.
        aceptacionCondiciones: aceptacionRegistro || { version: null, aceptadoEn: null, nota: 'Aceptado al registrarse, en una sesión anterior' },
      });
      setEmployees(nuevosEncargados);
      setConfig(finalConfig);
      setDataLoaded(true);
    } catch (e) {
      setAuthError('No se pudo crear el restaurante: ' + e.message);
    }
    setAuthBusy(false);
  };

  // OJO: esta función es un resto de una pantalla de login antigua (anterior a la
  // autenticación real con Firebase) que ya no debería usarse — ponía "Empleado"/"Cocinero"
  // como nombre en vez del nombre real de la persona. Si por lo que sea se llega a llamar,
  // en vez de fingir una identidad, mandamos a la persona a identificarse de verdad.
  const handleLogin = (role) => {
    setRestaurantDocId(null);
    setDataLoaded(false);
    setSelectedEmployeeId(null);
    setCurrentUser(null);
    setUserRole('');
    setAuthScreen('inicio');
    setShowLogin(false);
  };

  const handleJefeAuth = async (modo) => {
    // Sin la casilla marcada no se crea la cuenta. Esta comprobación va aquí y no solo en el
    // botón desactivado, porque pulsar Enter en un campo también llega hasta esta función.
    if (modo === 'registro' && !aceptaCondiciones) {
      setAuthError('Para crear la cuenta tienes que aceptar las Condiciones de Contratación, el Aviso Legal y la Política de Privacidad.');
      return;
    }
    if (modo === 'registro' && authPassword.length < CONTRASENA_MIN) {
      setAuthError(`La contraseña debe tener al menos ${CONTRASENA_MIN} caracteres.`);
      return;
    }
    setAuthBusy(true); setAuthError('');
    // Código de invitación (opcional): si lo han escrito, primero se mira que valga. Si no vale,
    // se dice por qué y NO se crea la cuenta, para que puedan corregirlo o borrarlo.
    const codigoInv = (modo === 'registro' && verCodigoInvitacion) ? normalizarCodigoInvitacion(codigoInvitacion) : '';
    if (codigoInv) {
      try {
        const r = await httpsCallable(functionsInstance, 'comprobarInvitacion')({ codigo: codigoInv });
        if (!r.data || r.data.valido !== true) {
          setAuthError((r.data && r.data.motivo) || 'Ese código de invitación no es válido.');
          setAuthBusy(false);
          return;
        }
      } catch (_) {
        setAuthError('No hemos podido comprobar el código de invitación. Revisa la conexión o quita el código.');
        setAuthBusy(false);
        return;
      }
    }
    try {
      const cred = modo === 'registro'
        ? await createUserWithEmailAndPassword(auth, authEmail.trim(), authPassword)
        : await signInWithEmailAndPassword(auth, authEmail.trim(), authPassword);
      if (modo === 'registro') setAceptacionRegistro({ version: CONDICIONES_VERSION, aceptadoEn: new Date().toISOString() });
      // La cuenta ya existe: se gasta el código. Si en este instante fallara (sin conexión, o
      // alguien lo ha usado a la vez), la cuenta sigue adelante igual: el código solo sirve para
      // que Fran sepa quién os invitó, nunca debe impedir entrar.
      if (modo === 'registro' && codigoInv) {
        try { await httpsCallable(functionsInstance, 'usarInvitacion')({ codigo: codigoInv }); }
        catch (errInv) { console.warn('[Horidia] no se pudo registrar la invitación:', errInv && errInv.message); }
      }
      // Cuenta nueva sin confirmar: no se entra todavía. Se manda el correo y se enseña la
      // pantalla de "Confirma tu email". (El listener de sesión hace la misma comprobación.)
      if (modo === 'registro') {
        try { await sendEmailVerification(cred.user); } catch (_) {}
      } else {
        try { await cred.user.reload(); } catch (_) {}
      }
      if (necesitaVerificarEmail(auth.currentUser || cred.user)) {
        setVerifMsg(''); setEmailPendiente(cred.user.email);
        setAuthBusy(false);
        return;
      }
      setUserRole('jefe');
      setCurrentUser(L.jefeRoleName);
      setActiveTab('empleados');
      setShowLogin(false);
      setRestaurantDocId(cred.user.uid);
    } catch (e) {
      setAuthError(traducirErrorAuth(e.code));
    }
    setAuthBusy(false);
  };

  // Acceso del Dueño: persona distinta del Encargado (no gestiona el día a día, solo consulta).
  // Se registra con su propio email+contraseña + el código de acceso del restaurante (el mismo
  // de 6 caracteres que usan los empleados) para enlazar su cuenta a ese restaurante concreto,
  // guardando esa relación en la colección "duenos" — nunca comparte login con el Encargado.
  const handleDuenoAuth = async (modo) => {
    if (modo === 'registro' && !aceptaCondiciones) {
      setAuthError('Para crear la cuenta tienes que aceptar el Aviso Legal y la Política de Privacidad.');
      return;
    }
    if (modo === 'registro' && authPassword.length < CONTRASENA_MIN) {
      setAuthError(`La contraseña debe tener al menos ${CONTRASENA_MIN} caracteres.`);
      return;
    }
    setAuthBusy(true); setAuthError('');
    try {
      if (modo === 'registro') {
        // (29 oct 2026) Ya no vale el código de los empleados: hace falta el CÓDIGO DE DUEÑO, que
        // solo le llega al Encargado por correo. Todo se comprueba en el servidor: primero si el
        // código vale (antes de crear la cuenta) y después se enlaza la cuenta con el negocio.
        const limpio = codigoDuenoInput.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (limpio.length !== 8) { setAuthError('Escribe el código de Dueño (8 caracteres, por ejemplo ABCD-EFGH). Le llega por correo al Encargado.'); setAuthBusy(false); return; }
        const codigo = `${limpio.slice(0, 4)}-${limpio.slice(4)}`;
        let comprobacion = null;
        try {
          comprobacion = (await httpsCallable(functionsInstance, 'comprobarCodigoDueno')({ codigo }))?.data;
        } catch (_) {
          setAuthError('No se ha podido comprobar el código. Revisa tu conexión e inténtalo de nuevo.');
          setAuthBusy(false);
          return;
        }
        if (!comprobacion?.valido) {
          setAuthError('Ese código de Dueño no es válido. Pídeselo al Encargado: le llega a su correo, y desde «Mi plan» puede pedir uno nuevo.');
          setAuthBusy(false);
          return;
        }
        const cred = await createUserWithEmailAndPassword(auth, authEmail.trim(), authPassword);
        let restId = null;
        try {
          const r = await httpsCallable(functionsInstance, 'vincularDueno')({ codigo, aceptacion: { version: CONDICIONES_VERSION } });
          restId = r?.data?.restId || null;
        } catch (eVinc) {
          // Si no se ha podido enlazar, se borra la cuenta recién creada para no dejarla a medias
          // (así la persona puede volver a intentarlo con el mismo correo).
          try { await cred.user.delete(); } catch (_) { await firebaseSignOut(auth).catch(() => {}); }
          setAuthError((eVinc && eVinc.message && eVinc.code !== 'functions/internal') ? eVinc.message : 'No se ha podido crear la cuenta de Dueño. Inténtalo de nuevo.');
          setAuthBusy(false);
          return;
        }
        if (!restId) {
          try { await cred.user.delete(); } catch (_) { await firebaseSignOut(auth).catch(() => {}); }
          setAuthError('No se ha podido crear la cuenta de Dueño. Inténtalo de nuevo.');
          setAuthBusy(false);
          return;
        }
        // Igual que el Encargado: hasta confirmar el correo no se entra.
        try { await sendEmailVerification(cred.user); } catch (_) {}
        if (necesitaVerificarEmail(auth.currentUser || cred.user)) {
          setVerifMsg(''); setEmailPendiente(cred.user.email);
          setAuthBusy(false);
          return;
        }
        setUserRole('dueno');
        setCurrentUser('Dueño');
        setShowLogin(false);
        setRestaurantDocId(restId);
        setCodigoDuenoInput('');
      } else {
        const cred = await signInWithEmailAndPassword(auth, authEmail.trim(), authPassword);
        try { await cred.user.reload(); } catch (_) {}
        if (necesitaVerificarEmail(auth.currentUser || cred.user)) {
          setVerifMsg(''); setEmailPendiente(cred.user.email);
          setAuthBusy(false);
          return;
        }
        const duenoSnap = await getDoc(doc(db, 'duenos', cred.user.uid));
        if (!duenoSnap.exists() || !duenoSnap.data()?.uid) {
          setAuthError('Esta cuenta no está registrada como Dueño de ningún restaurante.');
          await firebaseSignOut(auth).catch(() => {});
          setAuthBusy(false);
          return;
        }
        setUserRole('dueno');
        setCurrentUser('Dueño');
        setShowLogin(false);
        setRestaurantDocId(duenoSnap.data().uid);
      }
    } catch (e) {
      setAuthError(traducirErrorAuth(e.code));
    }
    setAuthBusy(false);
  };

  // --- Funciones de huella/Face ID (WebAuthn, estándar del propio navegador) ---
  const bufferToBase64url = (buf) => {
    let str = '';
    new Uint8Array(buf).forEach(b => { str += String.fromCharCode(b); });
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };
  const base64urlToBuffer = (b64) => {
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const str = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
    const buf = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) buf[i] = str.charCodeAt(i);
    return buf;
  };
  const registrarHuella = async (empId, nombre) => {
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          rp: { name: 'Horidia', id: window.location.hostname },
          user: { id: crypto.getRandomValues(new Uint8Array(16)), name: nombre, displayName: nombre },
          pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
          authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required' },
          timeout: 60000,
          attestation: 'none',
        }
      });
      localStorage.setItem(HUELLA_KEY, JSON.stringify({ empId, credentialId: bufferToBase64url(credential.rawId) }));
      return true;
    } catch (e) {
      return false;
    }
  };
  const verificarHuella = async (credentialId) => {
    await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [{ id: base64urlToBuffer(credentialId), type: 'public-key' }],
        userVerification: 'required',
        timeout: 60000,
      }
    });
    // Si no lanza error, el propio dispositivo ya ha confirmado la huella/Face ID.
    return true;
  };
  useEffect(() => {
    if (window.PublicKeyCredential && window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setHuellaDisponible).catch(() => setHuellaDisponible(false));
    }
  }, []);
  const handleDesbloquearHuella = async () => {
    setHuellaBusy(true); setHuellaError('');
    try {
      const huella = JSON.parse(localStorage.getItem(HUELLA_KEY) || 'null');
      if (!huella?.credentialId) throw new Error('no-huella');
      await verificarHuella(huella.credentialId);
      setBloqueadoHuella(false);
    } catch (e) {
      setHuellaError('No se ha podido confirmar. Inténtalo de nuevo o usa el PIN.');
    }
    setHuellaBusy(false);
  };
  // Para el botón "← Volver" del candado: solo lleva de nuevo al selector de nombre,
  // SIN cerrar la sesión real de Firebase (por eso, al volver a elegir el mismo nombre,
  // se puede ofrecer la huella otra vez sin pedir el PIN). "Salir"/"Usar el PIN" sí es
  // un cierre de sesión de verdad, ese usa handleLogout.
  const volverAlSelector = () => {
    setBloqueadoHuella(false); setHuellaError('');
    setSelectedEmployeeId(null); setCurrentUser(null);
    setUserRole('empleado');
  };

  const handlePasswordReset = async () => {
    setAuthBusy(true); setAuthError(''); setResetSent(false);
    try {
      if (!authEmail.trim()) { setAuthError('Escribe tu email primero.'); setAuthBusy(false); return; }
      await sendPasswordResetEmail(auth, authEmail.trim());
      setResetSent(true);
    } catch (e) {
      setAuthError(traducirErrorAuth(e.code));
    }
    setAuthBusy(false);
  };

  // --- Confirmar el email de una cuenta nueva ---
  // "silencioso" = comprobación automática al volver a la app (no enseña mensaje si aún no).
  const comprobarEmailVerificado = async (silencioso) => {
    const u = auth.currentUser;
    if (!u) return;
    if (!silencioso) { setVerifBusy(true); setVerifMsg(''); }
    try {
      await u.reload();
      try { await u.getIdToken(true); } catch (_) {}
      if (!necesitaVerificarEmail(auth.currentUser)) {
        setEmailPendiente(null);
        await entrarCuentaReal(auth.currentUser);
      } else if (!silencioso) {
        setVerifMsg('Todavía no aparece como confirmado. Abre el enlace del correo y vuelve aquí.');
      }
    } catch (_) {
      if (!silencioso) setVerifMsg('No se ha podido comprobar. Revisa tu conexión e inténtalo de nuevo.');
    }
    if (!silencioso) setVerifBusy(false);
  };

  const reenviarVerificacion = async () => {
    const u = auth.currentUser;
    if (!u || verifEspera) return;
    setVerifBusy(true); setVerifMsg('');
    try {
      await sendEmailVerification(u);
      setVerifMsg(`Correo reenviado a ${u.email}. Mira también en spam.`);
      setVerifEspera(true);
      setTimeout(() => setVerifEspera(false), 30000);
    } catch (e) {
      setVerifMsg(e && e.code === 'auth/too-many-requests'
        ? 'Has pedido demasiados correos seguidos. Espera unos minutos y vuelve a probar.'
        : 'No se ha podido enviar el correo. Inténtalo de nuevo en un momento.');
    }
    setVerifBusy(false);
  };

  // "Me he equivocado de email / Volver": cierra la sesión de la cuenta sin confirmar y deja
  // la pantalla de registro limpia para empezar de nuevo con el correo bueno.
  const salirDeVerificacion = async () => {
    try { await firebaseSignOut(auth); } catch (_) {}
    setEmailPendiente(null); setVerifMsg('');
    setRestaurantDocId(null);
    setAuthScreen('inicio'); setAuthEmail(''); setAuthPassword(''); setAccessCodeInput(''); setAuthError('');
  };

  // Mientras se espera la confirmación, cada vez que la persona vuelve a esta pestaña/app
  // (después de abrir el enlace en su correo) se comprueba sola, sin tener que pulsar nada.
  useEffect(() => {
    if (!emailPendiente) return undefined;
    const alVolver = () => { if (document.visibilityState === 'visible') comprobarEmailVerificado(true); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailPendiente]);

  const PIN_INTENTOS_MAX = 3;
  const PIN_BLOQUEO_MS = 120000; // 2 minutos de espera tras agotar los 3 intentos

  const handlePinSubmit = async () => {
    if (!pendingPinEmployee) return;
    if (Date.now() < pinBloqueadoHasta) return; // por si acaso, no debería poder llegar aquí con el botón desactivado
    setPinBusy(true); setPinError('');
    try {
      // Importante: esto se guarda ANTES de iniciar sesión, no después. Firebase avisa de
      // que ya has entrado casi al instante y en paralelo con el resto de este código — si
      // ese aviso llegaba antes de que guardásemos "quién eres" aquí, la app no te reconocía
      // y te cerraba la sesión sola (el fallo real de "hay que darle dos veces al PIN").
      try { localStorage.setItem(RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId, empId: pendingPinEmployee.id, nombre: pendingPinEmployee.name, esEncargado: !!pendingPinEmployee.esEncargado })); } catch (_) {}
      try { localStorage.setItem(RESTAURANTE_RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId })); } catch (_) {}
      await signInWithEmailAndPassword(auth, pendingPinEmployee.authEmail, pinInput.trim());
      setPinIntentos(0); setPinBloqueadoHasta(0);
      setSelectedEmployeeId(pendingPinEmployee.id);
      setCurrentUser(pendingPinEmployee.name);
      setUserRole(pendingPinEmployee.esEncargado ? 'jefe' : 'empleado');
      try {
        const huellaActual = JSON.parse(localStorage.getItem(HUELLA_KEY) || 'null');
        const yaDeclinada = localStorage.getItem(`horia_huella_no_${department}_${pendingPinEmployee.id}`);
        if (huellaDisponible && huellaActual?.empId !== pendingPinEmployee.id && !yaDeclinada) {
          setOfertaHuella({ id: pendingPinEmployee.id, name: pendingPinEmployee.name });
        }
      } catch (_) {}
      setPendingPinEmployee(null);
      setPinInput('');
    } catch (e) {
      // Si el PIN era incorrecto, quitamos lo que habíamos apuntado por adelantado.
      try { localStorage.removeItem(RECORDADO_KEY); } catch (_) {}
      const nuevosIntentos = pinIntentos + 1;
      setPinIntentos(nuevosIntentos);
      if (nuevosIntentos >= PIN_INTENTOS_MAX) {
        setPinBloqueadoHasta(Date.now() + PIN_BLOQUEO_MS);
        setPinError(`PIN incorrecto 3 veces. Espera 2 minutos antes de volver a intentarlo.`);
      } else {
        setPinError(`PIN incorrecto. Te queda${PIN_INTENTOS_MAX - nuevosIntentos === 1 ? '' : 'n'} ${PIN_INTENTOS_MAX - nuevosIntentos} intento${PIN_INTENTOS_MAX - nuevosIntentos === 1 ? '' : 's'}.`);
      }
    }
    setPinBusy(false);
  };

  // Botón "Soy Empleado" / "Entrar con el código": si este dispositivo ya conoce el
  // restaurante (lo usó antes), nos ahorramos pedir el código otra vez y vamos directos
  // al selector de nombre. Si no lo conoce (primera vez de verdad), sí pedimos el código.
  const handleEmpleadoBoton = async () => {
    try {
      const restauranteRecordado = JSON.parse(localStorage.getItem(RESTAURANTE_RECORDADO_KEY) || 'null');
      if (restauranteRecordado?.uid) {
        setAuthBusy(true); setAuthError('');
        try {
          // Si ya hay CUALQUIER sesión activa (aunque sea anónima de antes), no hace falta
          // crear una nueva — hacerlo sin necesidad es lo que provocaba el "hay que darle
          // dos veces al botón" (se cruzaba con el listener de autenticación a mitad de camino).
          if (!auth.currentUser) await signInAnonymously(auth);
          // La sesión anónima es distinta cada vez, así que el "carné" que demuestra a qué
          // restaurante pertenece esta persona hay que volver a crearlo con el código
          // recordado. Si el Encargado ha cambiado el código, esto fallará a propósito y le
          // pediremos el código nuevo — que es justo lo que queremos que pase.
          const carneOk = await crearCarneDeMiembro(restauranteRecordado.uid, restauranteRecordado.codigo);
          if (!carneOk) {
            try { localStorage.removeItem(RESTAURANTE_RECORDADO_KEY); } catch (_) {}
            setAuthBusy(false);
            setAuthError('El código de tu restaurante ha cambiado. Pídele el nuevo a tu encargado.');
            setAuthScreen('empleadoCodigo');
            return;
          }
          setUserRole('empleado');
          setDataLoaded(false);
          setShowLogin(false);
          setRestaurantDocId(restauranteRecordado.uid);
        } catch (e) {
          setAuthError('No se ha podido entrar. Vuelve a intentarlo.');
        }
        setAuthBusy(false);
        return;
      }
    } catch (_) {}
    setAuthScreen('empleadoCodigo');
  };

  const handleEmpleadoCodigo = async () => {
    setAuthBusy(true); setAuthError('');
    try {
      const codigo = accessCodeInput.trim().toUpperCase();
      if (!codigo) { setAuthError('Escribe el código que te ha dado tu encargado.'); setAuthBusy(false); return; }
      // Las reglas de seguridad ya no permiten consultar un código sin haber iniciado sesión
      // (antes cualquiera podía ir probando códigos desde fuera hasta acertar uno). Por eso
      // ahora abrimos primero la sesión anónima y consultamos después.
      if (!auth.currentUser) await signInAnonymously(auth);
      const codeSnap = await getDoc(doc(db, 'codigos', codigo));
      if (!codeSnap.exists()) {
        setAuthError('Ese código no existe. Pídeselo de nuevo a tu encargado.');
        setAuthBusy(false);
        return;
      }
      const restId = codeSnap.data().uid;
      // Si el carné no se puede crear, NO seguimos: si continuásemos, la app entraría y luego
      // Firestore le denegaría la lectura, dejando el aviso rojo de "no se ha podido conectar".
      const carneOk = await crearCarneDeMiembro(restId, codigo);
      if (!carneOk) {
        setAuthError('No se ha podido completar el acceso. Comprueba tu conexión y vuelve a intentarlo.');
        setAuthBusy(false);
        return;
      }
      setUserRole('empleado');
      setActiveTab('horario');
      setShowLogin(false);
      setRestaurantDocId(restId);
      try { localStorage.setItem(RESTAURANTE_RECORDADO_KEY, JSON.stringify({ uid: restId, codigo })); } catch (_) {}
    } catch (e) {
      setAuthError('Código incorrecto, vuelve a intentarlo.');
    }
    setAuthBusy(false);
  };

  // "¿No es tu restaurante?" (9 sep 2026): a diferencia de "Salir" (handleLogout), que a
  // propósito vuelve a entrar solo en el mismo restaurante recordado, esta función se usa
  // cuando ESE restaurante recordado es el equivocado — borra lo recordado del dispositivo
  // y fuerza la pantalla de escribir el código, sin reintentar sola. Antes, la única forma
  // de arreglar esto era borrar la caché del navegador entero.
  const handleRestauranteEquivocado = async () => {
    const usuarioActual = auth.currentUser;
    const eraAnonima = !!(usuarioActual && usuarioActual.isAnonymous);
    if (eraAnonima) {
      try { await deleteDoc(doc(db, 'miembros', usuarioActual.uid)); } catch (_) {}
      try { await usuarioActual.delete(); }
      catch (_) { try { await firebaseSignOut(auth); } catch (_) {} }
    }
    try { localStorage.removeItem(RECORDADO_KEY); } catch (_) {}
    try { localStorage.removeItem(RESTAURANTE_RECORDADO_KEY); } catch (_) {}
    setSelectedEmployeeId(null);
    setCurrentUser(null);
    setUserRole('');
    setRestaurantDocId(null);
    setDataLoaded(false);
    setShowLogin(true);
    setAuthScreen('empleadoCodigo');
    setAccessCodeInput('');
    setAuthError('');
  };

  const handleLogout = async () => {
    // Solo el dueño real (email y contraseña, no un PIN) tiene que volver a escribir sus datos
    // desde cero. A un empleado o Jefe de Cocina con PIN, "Salir" solo le olvida A ÉL —
    // el restaurante se queda recordado en el dispositivo, así no hay que teclear el código otra vez.
    const eraCuentaPropiaDelDueno = !!(firebaseUser && !firebaseUser.isAnonymous && firebaseUser.email && !firebaseUser.email.endsWith('.horiaapp.local'));

    // --- Limpieza de cuentas anónimas ---------------------------------------------------
    // Antes esto cerraba SIEMPRE la sesión y, un instante después, abría otra nueva. Como las
    // sesiones anónimas de Firebase son una cuenta de usuario real (sin correo, pero cuenta al
    // fin y al cabo) y nadie las borraba, cada vez que alguien pulsaba "Salir" quedaba una
    // cuenta fantasma para siempre. Con muchos restaurantes eso son miles de cuentas y miles de
    // documentos de "carné" basura. Ahora se distinguen tres casos.
    const usuarioActual = auth.currentUser;
    const eraAnonima = !!(usuarioActual && usuarioActual.isAnonymous);
    let restauranteRecordado = null;
    try { restauranteRecordado = JSON.parse(localStorage.getItem(RESTAURANTE_RECORDADO_KEY) || 'null'); } catch (_) {}
    const seguiremosDeEmpleado = !eraCuentaPropiaDelDueno && !!restauranteRecordado?.uid;

    if (eraAnonima && seguiremosDeEmpleado) {
      // CASO 1: entró con el código y va a seguir en este restaurante. La sesión anónima que
      // ya tiene sirve igual, así que NO la cerramos: la reutilizamos. Cero cuentas nuevas.
    } else if (eraAnonima) {
      // CASO 2: se marcha del todo. En vez de dejar la cuenta anónima huérfana, la BORRAMOS,
      // y de paso su carné de miembro. Si el borrado falla (por ejemplo, sin conexión),
      // cerramos sesión de la forma normal para no dejar a nadie dentro.
      try { await deleteDoc(doc(db, 'miembros', usuarioActual.uid)); } catch (_) {}
      try { await usuarioActual.delete(); }
      catch (_) { try { await firebaseSignOut(auth); } catch (_) {} }
    } else {
      // CASO 3: cuenta con PIN o cuenta propia del dueño. Estas no se borran nunca: son cuentas
      // de verdad que se seguirán usando. Cerrar sesión y ya está.
      try { await firebaseSignOut(auth); } catch (_) {}
    }

    try { localStorage.removeItem(RECORDADO_KEY); } catch (_) {}
    setSelectedEmployeeId(null);
    setCurrentUser(null); setShowLogin(true); setUserRole('');
    setBloqueadoHuella(false); setHuellaError('');
    if (seguiremosDeEmpleado) {
      try {
        // Solo se abre sesión nueva si de verdad no queda ninguna (caso del PIN, que sí tuvo
        // que cerrarse). Si veníamos del caso 1, aquí ya hay sesión y no se crea nada.
        if (!auth.currentUser) await signInAnonymously(auth);
        const carneOk = await crearCarneDeMiembro(restauranteRecordado.uid, restauranteRecordado.codigo);
        if (carneOk) {
          setUserRole('empleado');
          setDataLoaded(false);
          setShowLogin(false);
          setRestaurantDocId(restauranteRecordado.uid);
          return;
        }
        try { localStorage.removeItem(RESTAURANTE_RECORDADO_KEY); } catch (_) {}
      } catch (_) {}
    }
    setRestaurantDocId(null); setDataLoaded(false);
    setAuthScreen('inicio'); setAuthEmail(''); setAuthPassword(''); setAccessCodeInput(''); setAuthError('');
  };

  const addEmployee = () => {
    if (!newEmployeeName.trim()) return;
    if (employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE) {
      setUpgradeNotice(`Has llegado al máximo de ${MAX_EMPLEADOS_POR_RESTAURANTE} empleados por restaurante. Escríbenos si necesitas más.`);
      return;
    }
    setEmployees(prev => [...prev, {
      id: Date.now(), name: newEmployeeName.trim(),
      turnosSemanales: 5,
      esEncargado: false,
      esGerente: false,
      diasFijosLibres: [],
      joinedAt: new Date().toLocaleDateString('es-ES')
    }]);
    setNewEmployeeName('');
  };

  // Crea (o renueva) un PIN de acceso individual para un empleado, usando una
  // instancia secundaria de Firebase para no cerrar la sesión del Encargado.
  const crearPinEmpleado = async (emp) => {
    if (!restaurantDocId) return;
    if (emp.esEncargado && !esDuenoReal) {
      avisar({ icono: '🔒', titulo: 'Solo puede hacerlo el dueño de la cuenta', lineas: ['Crear o renovar el PIN de alguien marcado como encargado/a también le da permisos de gestión, así que solo puede hacerlo el dueño de la cuenta.'] });
      return;
    }
    // (29 oct 2026) Aleatorio de verdad (el del navegador para claves), no Math.random.
    const pin = String(100000 + (window.crypto.getRandomValues(new Uint32Array(1))[0] % 900000));
    const email = `emp${emp.id}_${Date.now()}@${restaurantDocId}.horiaapp.local`;
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, pin);
      const nuevoUid = cred.user.uid;
      try { await firebaseSignOut(secondaryAuth); } catch (_) {}
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, authEmail: email, authUid: nuevoUid } : e));
      // Antes salía en la ventana gris del navegador ("hito-640bd.web.app dice"). Ahora sale en la
      // ventana de Horidia, con el PIN grande, botón de copiar y sin cerrarse por un toque sin querer.
      await avisar({
        icono: '🔑',
        titulo: `PIN de ${emp.name}`,
        codigo: pin,
        sinCerrarFuera: true,
        lineas: [
          'Apúntalo y dáselo en persona: no se puede volver a mostrar.',
          'Con él podrá entrar y usar los mensajes privados. Después puede cambiarlo por uno que solo sepa él o ella, en «Mi PIN».',
          'Si lo pierde o lo olvida, puedes generarle uno nuevo con este mismo botón: el anterior deja de servir al momento.',
        ],
        textoOk: 'Ya lo he apuntado',
      });
    } catch (e) {
      avisar({ icono: '⚠️', titulo: 'No se ha podido crear el PIN', lineas: ['Comprueba tu conexión e inténtalo de nuevo.', `Detalle técnico: ${e.message}`] });
    }
  };

  // === MI PIN (29 oct 2026, pedido por Fran) =========================================
  // Quien ha entrado con el PIN que le dio su encargado puede cambiarlo por uno que solo sepa
  // él o ella. Si luego lo olvida, el encargado le genera otro (cuenta nueva) y vuelta a empezar.
  // Cambiar el PIN cierra la sesión en los demás dispositivos que entraron con el PIN anterior.
  const [showMiPin, setShowMiPin] = useState(false);
  const [miPinActual, setMiPinActual] = useState('');
  const [miPinNuevo, setMiPinNuevo] = useState('');
  const [miPinRepite, setMiPinRepite] = useState('');
  const [miPinError, setMiPinError] = useState('');
  const [miPinBusy, setMiPinBusy] = useState(false);
  const abrirMiPin = () => { setMiPinActual(''); setMiPinNuevo(''); setMiPinRepite(''); setMiPinError(''); setShowMiPin(true); };
  const pinDemasiadoFacil = (p) => {
    if (/^(\d)\1{5}$/.test(p)) return true; // 000000, 111111…
    const d = p.split('').map(Number);
    const sube = d.every((x, i) => i === 0 || x === (d[i - 1] + 1) % 10);
    const baja = d.every((x, i) => i === 0 || x === (d[i - 1] + 9) % 10);
    return sube || baja; // 123456, 654321, 890123…
  };
  const cambiarMiPin = async () => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous || !u.email || miPinBusy) return;
    const actual = miPinActual.trim();
    const nuevo = miPinNuevo.trim();
    if (!/^\d{6}$/.test(actual)) { setMiPinError('Escribe tu PIN actual (6 números).'); return; }
    if (!/^\d{6}$/.test(nuevo)) { setMiPinError('El PIN nuevo tiene que tener 6 números.'); return; }
    if (nuevo !== miPinRepite.trim()) { setMiPinError('Los dos PIN nuevos no coinciden.'); return; }
    if (nuevo === actual) { setMiPinError('El PIN nuevo tiene que ser distinto del actual.'); return; }
    if (pinDemasiadoFacil(nuevo)) { setMiPinError('Ese PIN es muy fácil de adivinar. Evita repetir el mismo número o series como 123456.'); return; }
    setMiPinBusy(true); setMiPinError('');
    try {
      await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, actual));
      await updatePassword(u, nuevo);
      setShowMiPin(false);
      setMiPinActual(''); setMiPinNuevo(''); setMiPinRepite('');
      avisar({ icono: '🔑', titulo: 'PIN cambiado', lineas: [
        'Desde ahora entra con tu PIN nuevo. Solo lo sabes tú: no se lo digas a nadie.',
        'Si lo olvidas, pídele a tu encargado que te genere uno nuevo.',
      ] });
    } catch (e) {
      const c = e?.code || '';
      setMiPinError(
        (c === 'auth/wrong-password' || c === 'auth/invalid-credential' || c === 'auth/invalid-login-credentials') ? 'El PIN actual no es correcto.'
        : c === 'auth/too-many-requests' ? 'Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.'
        : (c === 'auth/weak-password' || c === 'auth/password-does-not-meet-requirements') ? 'Ese PIN no se puede usar. Prueba con otro.'
        : 'No se ha podido cambiar el PIN. Revisa tu conexión e inténtalo otra vez.');
    }
    setMiPinBusy(false);
  };

  // FALLO REAL ENCONTRADO (8 ago 2026): al borrar un empleado solo se le quitaba de la lista y
  // del horario del local activo. Todo lo demás se quedaba huérfano en el servidor: sus
  // solicitudes (que es justo lo que hizo que un Mario nuevo heredase en silencio los días
  // libres aprobados de un Mario ya borrado, con el mismo nombre), su horario en los DEMÁS
  // locales (solo se limpiaba el que se estaba viendo), y su token de notificaciones. Ahora se
  // limpia todo eso, y de paso se pide confirmación antes de borrar (antes el botón borraba sin
  // preguntar nada).
  const deleteEmployee = async (id) => {
    const emp = employees.find(e => e.id === id);
    if (emp?.esEncargado && emp?.authUid && !esDuenoReal) {
      avisar({ icono: '🔒', titulo: 'Solo puede hacerlo el dueño de la cuenta', lineas: ['Solo el dueño de la cuenta puede eliminar a alguien marcado como encargado/a con acceso propio.'] });
      return;
    }
    const ok = await confirmar({
      icono: '🗑️',
      titulo: `¿Eliminar a ${emp?.name || 'esta persona'}?`,
      lineas: [
        'Se borra de la plantilla, de todos los horarios (incluidos los de otros locales) y de sus solicitudes pendientes o ya aprobadas.',
        'El historial de horarios ya publicados con su nombre no se toca — eso queda como registro.',
      ],
      textoOk: 'Sí, eliminar',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (!ok) return;

    setEmployees(prev => prev.filter(e => e.id !== id));
    setSchedule(prev => { const s = {...prev}; delete s[id]; return s; });
    // El horario activo vive también en schedulesPorLocal — limpiarlo en TODOS los locales, no
    // solo en el que se está viendo, o quedaría con turnos de alguien que ya no existe.
    setSchedulesPorLocal(prev => {
      const n = {};
      Object.entries(prev).forEach(([localId, sch]) => {
        const s = { ...(sch || {}) };
        delete s[id];
        n[localId] = s;
      });
      return n;
    });

    if (!restaurantDocId) return;
    try {
      // Solicitudes (día libre y coberturas) de esta persona, sea cual sea su estado — una vez
      // borrada, un día libre ya aprobado no significa nada y solo puede confundir al generador.
      // Se busca por las dos vías: `senderId` (solicitudes nuevas, desde el arreglo de hoy) y
      // por `sender` = nombre (solicitudes antiguas que todavía no lo llevan) — así se limpian
      // también los restos que ya hubiera de antes de este arreglo.
      const porId = await getDocs(query(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes'), where('senderId', '==', id)));
      const porNombre = emp?.name
        ? await getDocs(query(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes'), where('sender', '==', emp.name)))
        : { docs: [] };
      const refsUnicas = new Map([...porId.docs, ...porNombre.docs].map(d => [d.id, d.ref]));
      await Promise.all([...refsUnicas.values()].map(ref => deleteDoc(ref)));
      // Su token de notificaciones push, si lo tenía guardado (mismo id que el empleado).
      await deleteDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'tokens', id)).catch(() => {});
    } catch (e) {
      console.error('Error limpiando datos huérfanos al borrar empleado:', e);
    }
  };

  const updateEmployee = (id, field, value) =>
    setEmployees(prev => prev.map(e => e.id === id ? {...e, [field]: value} : e));

  const toggleEmployeeExpanded = (id) =>
    setExpandedEmployees(prev => ({ ...prev, [id]: !prev[id] }));

  const handleLogoEmpresaUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) {
      avisar({ icono: '⚠️', titulo: 'La imagen pesa demasiado', lineas: ['El máximo es 400 KB. Prueba con una versión más ligera del logo.'] });
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setConfig(c => ({ ...c, logoEmpresaUrl: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Carta y mapa son datos POR LOCAL desde el 21 ago 2026 (antes eran del restaurante entero).
  // Con un solo local, si aún no se ha tocado nada nuevo, se lee de los campos sueltos antiguos
  // (`config.menuFotoUrl`/`mapaElementos`/`mapaParedes`) para no perder lo que Fran ya tenía
  // subido de pruebas. En cuanto se guarda algo, pasa a vivir en `config.localesData[localId]`.
  const _datosLocalDe = (c, localId) => {
    const propio = c.localesData?.[localId];
    if (propio) return { menuFotoUrl: propio.menuFotoUrl || null, elementos: propio.elementos || [], paredes: propio.paredes || [], zonas: propio.zonas || [] };
    if (localId === LOCAL_PRINCIPAL) {
      return { menuFotoUrl: c.menuFotoUrl || null, elementos: c.mapaElementos || [], paredes: c.mapaParedes || [], zonas: [] };
    }
    return { menuFotoUrl: null, elementos: [], paredes: [], zonas: [] };
  };
  const datosLocalMenuMapa = (localId) => {
    const propios = _datosLocalDe(config, localId);
    // En Cocina, si no ha subido su propia foto de la carta, se ve la que subió Sala.
    if (isCocina && !propios.menuFotoUrl && datosDeSala?.config) {
      const deSala = _datosLocalDe(datosDeSala.config, localId);
      if (deSala.menuFotoUrl) return { ...propios, menuFotoUrl: deSala.menuFotoUrl, menuDeSala: true };
    }
    return propios;
  };
  // Logo que se enseña en pantalla: el propio y, en Cocina, si no tiene, el de Sala.
  const logoNegocio = config.logoEmpresaUrl || (isCocina ? datosDeSala?.config?.logoEmpresaUrl : null) || null;
  // Aplica `actualizar(datosActuales) => cambiosParciales` partiendo SIEMPRE del config más
  // fresco (el `c` del propio setConfig), para no perder un toque si hay dos muy seguidos.
  const actualizarDatosLocalMenuMapa = (localId, actualizar) => {
    setConfig(c => {
      const actuales = _datosLocalDe(c, localId);
      return { ...c, localesData: { ...(c.localesData || {}), [localId]: { ...actuales, ...actualizar(actuales) } } };
    });
  };

  // Sube la foto de la carta a Firebase Storage (pestaña "Menú y Mapa"). A diferencia del logo,
  // aquí SÍ merece la pena pasar por Storage en vez de guardar base64 en Firestore: una foto de
  // carta hecha con el móvil pesa varios MB, y Firestore tiene un límite de 1 MB por documento.
  // Se comprime primero (`comprimirFotoCarta`) y se sobrescribe siempre el mismo archivo por
  // local (`menu/{localId}.jpg`), así no se van acumulando fotos viejas sin usar en Storage.
  const handleMenuFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !restaurantDocId) return;
    const local = localVistoMenuMapaId;
    setMenuFotoError(null);
    setSubiendoMenuFoto(true);
    try {
      console.log('[Horidia] Iniciando subida de foto de menú:', { size: file.size, type: file.type, restaurantId: restaurantDocId, local });
      const comprimida = await comprimirFotoCarta(file);
      console.log('[Horidia] Foto comprimida. Tamaño:', comprimida.size, 'bytes');
      const ref = storageRef(storage, `${FIRESTORE_COLLECTION}/${restaurantDocId}/menu/${local}.jpg`);

      // Timeout de 30 segundos — si Storage no responde, probablemente es por reglas de seguridad.
      const uploadPromise = uploadBytes(ref, comprimida, { contentType: 'image/jpeg' });
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT: La subida tardó demasiado. Revisa que las reglas de Firebase Storage permitan la escritura.')), 30000)
      );
      await Promise.race([uploadPromise, timeoutPromise]);
      
      console.log('[Horidia] Subida exitosa. Obteniendo URL...');
      const url = await getDownloadURL(ref);
      console.log('[Horidia] URL obtenida:', url.slice(0, 80) + '...');
      actualizarDatosLocalMenuMapa(local, () => ({ menuFotoUrl: url }));
    } catch (err) {
      console.error('[Horidia] Error subiendo foto:', err);
      let mensaje = 'No se ha podido subir la foto.';
      if (err.message?.includes('TIMEOUT')) {
        mensaje = 'La subida tardó demasiado. Asegúrate de que las reglas de Firebase Storage estén configuradas (si acabas de crear el proyecto, pueden tardar unos minutos en activarse).';
      } else if (err.code === 'storage/unauthorized') {
        mensaje = 'No tienes permiso para subir fotos. Revisa las reglas de Firebase Storage.';
      } else if (err.code === 'storage/object-not-found') {
        mensaje = 'No se pudo obtener la URL de descarga. Inténtalo de nuevo.';
      }
      setMenuFotoError(mensaje);
    } finally {
      setSubiendoMenuFoto(false);
    }
  };

  const handleMenuFotoQuitar = async () => {
    if (!restaurantDocId) return;
    const local = localVistoMenuMapaId;
    actualizarDatosLocalMenuMapa(local, () => ({ menuFotoUrl: null }));
    try {
      await deleteObject(storageRef(storage, `${FIRESTORE_COLLECTION}/${restaurantDocId}/menu/${local}.jpg`));
    } catch (_) {
      // Si el archivo ya no existe en Storage no pasa nada; lo que importa es que el campo
      // en Firestore quede vacío, y eso ya se ha hecho arriba.
    }
  };

  // Convierte la posición del click/toque (en píxeles de pantalla) a coordenadas del plano
  // (0-100 de ancho, 0-70 de alto), con un margen para que nada quede pegado al borde.
  // Convierte el toque en coordenadas del plano Y las imanta a la rejilla de fondo (MAPA_GRID),
  // igual que hacen los editores de diagramas — así salen paredes rectas y mesas alineadas sin
  // necesidad de pulso firme en una pantalla pequeña.
  const imantar = (v, max) => Math.min(max - MAPA_GRID, Math.max(MAPA_GRID, Math.round(v / MAPA_GRID) * MAPA_GRID));
  const coordsDesdeClickMapa = (e) => {
    const rect = mapaSvgRef.current.getBoundingClientRect();
    const fx = (e.clientX - rect.left) / rect.width;
    const fy = (e.clientY - rect.top) / rect.height;
    return { x: imantar(fx * MAPA_VB_W, MAPA_VB_W), y: imantar(fy * MAPA_VB_H, MAPA_VB_H) };
  };

  // Qué pasa al tocar el plano, según la herramienta activa y si hay un movimiento pendiente.
  const handleMapaClick = (e) => {
    if (!mapaSvgRef.current) return;
    const pt = coordsDesdeClickMapa(e);
    const local = localVistoMenuMapaId;
    const datos = datosLocalMenuMapa(local);

    // Modo "mover" (segundo toque): el primer toque ya dijo QUÉ mover, este dice A DÓNDE.
    if (mapaMoviendoId) {
      actualizarDatosLocalMenuMapa(local, (d) => ({
        elementos: d.elementos.map(el => el.id === mapaMoviendoId ? { ...el, x: pt.x, y: pt.y } : el),
      }));
      setMapaMoviendoId(null);
      return;
    }

    // Dibujando una pared: cada toque añade un punto más al trazo en curso (se cierra con el
    // botón "Cerrar forma" que aparece debajo del plano).
    if (herramientaMapa === 'pared') {
      setMapaParedEnCurso(prev => [...(prev || []), pt]);
      return;
    }

    // ¿el toque cae encima de un elemento ya colocado? (tolerancia de unas 4-5 unidades)
    const tocado = [...datos.elementos].reverse().find(el => el.tipo === 'etiqueta' ? Math.hypot(el.x - pt.x, el.y - pt.y) < 4 : tocaElementoPlano(el, pt));
    if (tocado) {
      if (herramientaMapa === 'borrar') {
        actualizarDatosLocalMenuMapa(local, (d) => ({ elementos: d.elementos.filter(el => el.id !== tocado.id) }));
        if (mapaElementoSeleccionadoId === tocado.id) setMapaElementoSeleccionadoId(null);
      } else {
        setMapaElementoSeleccionadoId(tocado.id);
      }
      return;
    }

    // Con "Borrar" activa y el toque en el vacío: si cae dentro de una pared, se borra esa forma.
    if (herramientaMapa === 'borrar') {
      const idx = datos.paredes.findIndex(pared => puntoEnPoligono(pt, pared.puntos));
      if (idx !== -1) actualizarDatosLocalMenuMapa(local, (d) => ({ paredes: d.paredes.filter((_, i) => i !== idx) }));
      return;
    }

    // Colocar un elemento nuevo. Las mesas se numeran solas (siguiente número libre), nacen
    // cuadradas por defecto (la forma se cambia luego desde el panel de selección — ahora con
    // 4 formas disponibles, no solo 2), y todo lo que se coloque hereda la zona activa en ese
    // momento (o ninguna, si no hay zona elegida). Las etiquetas de sala nacen con el texto
    // "Sala" de partida, para que siempre haya algo que editar en vez de empezar en blanco.
    if (['mesa', 'barra', 'entrada', 'etiqueta'].includes(herramientaMapa)) {
      const id = `el_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const esMesa = herramientaMapa === 'mesa';
      const esEtiqueta = herramientaMapa === 'etiqueta';
      const numero = esMesa ? datos.elementos.filter(e => e.tipo === 'mesa').reduce((m, e) => Math.max(m, e.numero || 0), 0) + 1 : undefined;
      actualizarDatosLocalMenuMapa(local, (d) => ({
        elementos: [...d.elementos, {
          id, tipo: herramientaMapa, x: pt.x, y: pt.y, zonaId: zonaActivaMapaId || null,
          ...(esMesa ? { numero, forma: 'cuadrada' } : {}),
          ...(esEtiqueta ? { texto: 'Sala' } : {}),
        }],
      }));
      if (esMesa || esEtiqueta) setMapaElementoSeleccionadoId(id);
    }
  };

  const mapaCerrarPared = () => {
    if (!mapaParedEnCurso || mapaParedEnCurso.length < 3) return;
    // Firestore NO admite arrays anidados (un array directamente dentro de otro array) — por eso
    // cada pared se guarda como { puntos: [...] } (un mapa que CONTIENE un array), en vez de
    // guardar el array de puntos suelto directamente dentro de `paredes`. Sin este envoltorio,
    // el primer guardado tras cerrar una pared fallaría en Firestore.
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({ paredes: [...d.paredes, { puntos: mapaParedEnCurso }] }));
    setMapaParedEnCurso(null);
  };
  const mapaDeshacerPuntoPared = () => setMapaParedEnCurso(prev => (prev && prev.length > 1 ? prev.slice(0, -1) : null));
  const mapaCancelarPared = () => setMapaParedEnCurso(null);

  const actualizarNumeroMesa = (elId, valor) => {
    const numero = valor === '' ? '' : Math.max(1, parseInt(valor, 10) || 1);
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({
      elementos: d.elementos.map(el => el.id === elId ? { ...el, numero } : el),
    }));
  };
  const eliminarElementoMapa = (elId) => {
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({ elementos: d.elementos.filter(el => el.id !== elId) }));
    if (mapaElementoSeleccionadoId === elId) setMapaElementoSeleccionadoId(null);
  };
  // Cuadrada → redonda → rectangular → ovalada → cuadrada... (1 oct 2026: antes solo alternaba
  // entre cuadrada y redonda; ahora recorre las 4 formas, para las mesas de 6 (rectangular) y
  // los reservados tipo "booth" (ovalada) que Fran pidió tras ver un plano de ejemplo).
  const ORDEN_FORMAS_MESA = ['cuadrada', 'redonda', 'rectangular', 'ovalada'];
  // GIRAR (12 oct 2026): barra, entrada y mesas grandes se pueden girar de 90 en 90 grados.
  const girarElementoMapa = (elId) => {
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({
      elementos: d.elementos.map(el => el.id === elId ? { ...el, rot: ((el.rot || 0) + 90) % 360 } : el),
    }));
  };
  const alternarFormaMesa = (elId) => {
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({
      elementos: d.elementos.map(el => {
        if (el.id !== elId) return el;
        const actual = ORDEN_FORMAS_MESA.indexOf(el.forma) ?? 0;
        const siguiente = ORDEN_FORMAS_MESA[(Math.max(0, actual) + 1) % ORDEN_FORMAS_MESA.length];
        return { ...el, forma: siguiente };
      }),
    }));
  };
  // Texto de una etiqueta de sala ("Cocina", "Baños"...). Límite corto a propósito: es un
  // rótulo que se lee de un vistazo sobre el plano, no una frase.
  const actualizarTextoEtiqueta = (elId, texto) => {
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({
      elementos: d.elementos.map(el => el.id === elId ? { ...el, texto: texto.slice(0, 18) } : el),
    }));
  };
  // ZONAS: crear, asignar a un elemento, y borrar (al borrar una zona, los elementos que la
  // tenían asignada se quedan "sin zona", no se borran ellos).
  const anadirZonaMapa = () => {
    const nombre = (nuevaZonaMapaNombre || '').trim();
    if (!nombre) return;
    const id = `zona_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({ zonas: [...(d.zonas || []), { id, nombre }] }));
    setNuevaZonaMapaNombre('');
    setZonaActivaMapaId(id);
  };
  const borrarZonaMapa = (zonaId) => {
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({
      zonas: (d.zonas || []).filter(z => z.id !== zonaId),
      elementos: d.elementos.map(el => el.zonaId === zonaId ? { ...el, zonaId: null } : el),
    }));
    if (zonaActivaMapaId === zonaId) setZonaActivaMapaId(null);
  };
  const asignarZonaElemento = (elId, zonaId) => {
    actualizarDatosLocalMenuMapa(localVistoMenuMapaId, (d) => ({
      elementos: d.elementos.map(el => el.id === elId ? { ...el, zonaId: zonaId || null } : el),
    }));
  };

  const limpiarMapaLocal = async () => {
    const ok = await confirmar({
      icono: '🧹',
      titulo: '¿Vaciar el plano de este local?',
      lineas: ['Se borrará todo lo dibujado en este local: paredes, mesas, sillas, barra y entrada.', 'No se puede deshacer.'],
      textoOk: 'Vaciar plano',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (ok) {
      actualizarDatosLocalMenuMapa(localVistoMenuMapaId, () => ({ elementos: [], paredes: [] }));
      setMapaElementoSeleccionadoId(null);
      setMapaParedEnCurso(null);
      setMapaMoviendoId(null);
    }
  };

  // MODO APRENDIZAJE (22 ago 2026): quiz de "¿dónde está la mesa N?" para que el empleado
  // aprenda el mapa. Coge hasta 10 mesas numeradas al azar (todas si hay menos de 10), sin
  // repetir ninguna. Las coordenadas de la pregunta se guardan tal cual estaban al empezar el
  // quiz, así que si el Encargado mueve una mesa a mitad de partida no descoloca la pregunta ya
  // en curso (se lee un snapshot, no en vivo).
  const iniciarAprendizaje = (datosEmp) => {
    const mesas = datosEmp.elementos.filter(e => e.tipo === 'mesa' && e.numero);
    if (mesas.length < 2) return;
    const barajadas = [...mesas].sort(() => Math.random() - 0.5).slice(0, Math.min(10, mesas.length));
    setAprendizajePreguntas(barajadas);
    setAprendizajeIndice(0);
    setAprendizajeAciertos(0);
    setAprendizajeFeedback(null);
    setModoAprendizaje(true);
  };
  // Tolerancia de acierto algo mayor que el radio de una mesa (3.4), para que un dedo en una
  // pantalla pequeña no falle preguntas que en realidad ha acertado.
  const handleAprendizajeClick = (e) => {
    if (aprendizajeFeedback) return; // ya contestada, hay que pulsar "Siguiente" primero
    const preguntaActual = aprendizajePreguntas[aprendizajeIndice];
    if (!preguntaActual) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - rect.left) / rect.width;
    const fy = (e.clientY - rect.top) / rect.height;
    const px = fx * MAPA_VB_W, py = fy * MAPA_VB_H;
    const acierto = Math.hypot(preguntaActual.x - px, preguntaActual.y - py) < 6;
    setAprendizajeFeedback(acierto ? 'correcto' : 'incorrecto');
    if (acierto) setAprendizajeAciertos(a => a + 1);
  };
  const siguientePreguntaAprendizaje = () => {
    setAprendizajeFeedback(null);
    setAprendizajeIndice(i => i + 1);
  };

  // Icono de cada elemento del plano, en coordenadas SVG (el propio {x,y} del elemento). Las
  // mesas llevan un dibujo algo más currado (silla por cada comensal, número en el centro) en
  // vez del emoji suelto que llevaban barra/entrada. Hay 4 formas (1 oct 2026, antes solo 2):
  // cuadrada y redonda (4 sillas, para mesas de 2-4), rectangular y ovalada (6 sillas, para
  // mesas grandes o reservados tipo "booth" — lo que Fran pidió tras ver un plano de ejemplo).
  const iconoElementoMapa = (el, { interactivo = false, seleccionado = false } = {}) => {
    const giro = el.rot ? ` rotate(${el.rot})` : '';
    const marco = (hw, hh, rx = 1.4) => seleccionado && (
      <rect x={-hw} y={-hh} width={hw * 2} height={hh * 2} rx={rx} fill="none" stroke={C.accent} strokeWidth="0.6" strokeDasharray="1.2 0.9" />
    );
    if (el.tipo === 'mesa') {
      const forma = el.forma || 'cuadrada';
      const grande = forma === 'rectangular' || forma === 'ovalada';
      let sillas, tablero, sombra;
      const madera = { fill: PLANO.madera, stroke: PLANO.maderaBorde, strokeWidth: 0.35 };
      if (grande) {
        const hw = 4.8, hh = 2.7, d = hh + 1.55;
        sillas = [
          sillaPlano(-2.4, -d, 180, 'n1'), sillaPlano(2.4, -d, 180, 'n2'),
          sillaPlano(-2.4, d, 0, 's1'), sillaPlano(2.4, d, 0, 's2'),
          sillaPlano(-hw - 1.55, 0, 90, 'w'), sillaPlano(hw + 1.55, 0, 270, 'e'),
        ];
        tablero = forma === 'ovalada' ? <ellipse rx={hw} ry={hh} {...madera} /> : <rect x={-hw} y={-hh} width={hw * 2} height={hh * 2} rx="0.5" {...madera} />;
        sombra = forma === 'ovalada' ? <ellipse cx="0.35" cy="0.45" rx={hw} ry={hh} fill={PLANO.sombra} /> : <rect x={-hw + 0.35} y={-hh + 0.45} width={hw * 2} height={hh * 2} rx="0.5" fill={PLANO.sombra} />;
      } else {
        const r = 3.1, d = r + 1.55;
        sillas = [sillaPlano(0, -d, 180, 'n'), sillaPlano(d, 0, 270, 'e'), sillaPlano(0, d, 0, 's'), sillaPlano(-d, 0, 90, 'w')];
        tablero = forma === 'redonda' ? <circle r={r} {...madera} /> : <rect x={-r} y={-r} width={r * 2} height={r * 2} rx="0.45" {...madera} />;
        sombra = forma === 'redonda' ? <circle cx="0.35" cy="0.45" r={r} fill={PLANO.sombra} /> : <rect x={-r + 0.35} y={-r + 0.45} width={r * 2} height={r * 2} rx="0.45" fill={PLANO.sombra} />;
      }
      const m = medidaElementoPlano(el);
      m.hw += 1.4; m.hh += 1.4;
      return (
        <g key={el.id} transform={`translate(${el.x} ${el.y})${giro}`} style={interactivo ? { cursor: 'pointer' } : undefined}>
          {marco(m.hw, m.hh)}
          {sillas}
          {sombra}
          {tablero}
          <text textAnchor="middle" dominantBaseline="central" fontSize={grande ? '2.5' : '2.7'} fontWeight="700" fill={PLANO.texto}
            transform={el.rot ? `rotate(${-el.rot})` : undefined}>{el.numero ?? ''}</text>
        </g>
      );
    }
    // BARRA: mostrador de madera oscura con la encimera más clara y 4 taburetes delante.
    if (el.tipo === 'barra') {
      return (
        <g key={el.id} transform={`translate(${el.x} ${el.y})${giro}`} style={interactivo ? { cursor: 'pointer' } : undefined}>
          {marco(8.5, 4.4)}
          <rect x="-7.6" y="-1.35" width="15.6" height="3.6" rx="0.4" fill={PLANO.sombra} />
          <rect x="-8" y="-1.8" width="16" height="3.6" rx="0.4" fill={PLANO.maderaOscura} stroke={PLANO.maderaOscuraBorde} strokeWidth="0.35" />
          <rect x="-7.4" y="-1.25" width="14.8" height="1.5" rx="0.25" fill="#9C7A55" />
          <text y="1.05" textAnchor="middle" dominantBaseline="central" fontSize="1.35" fontWeight="800" fill="#F5EBDD" letterSpacing="0.25">BARRA</text>
          {[-5.4, -1.8, 1.8, 5.4].map(x => (
            <g key={x}>
              <circle cx={x} cy="3.35" r="1.05" fill={PLANO.asiento} stroke={PLANO.sillaBorde} strokeWidth="0.22" />
              <circle cx={x} cy="3.35" r="0.45" fill="none" stroke={PLANO.respaldo} strokeWidth="0.2" />
            </g>
          ))}
        </g>
      );
    }
    // ENTRADA: símbolo de puerta de los planos (hueco en el muro, hoja y arco de apertura).
    if (el.tipo === 'entrada') {
      return (
        <g key={el.id} transform={`translate(${el.x} ${el.y})${giro}`} style={interactivo ? { cursor: 'pointer' } : undefined}>
          {marco(4, 4)}
          <line x1="-3" y1="1.5" x2="3" y2="1.5" stroke="#FFFFFF" strokeWidth="1.6" />
          <line x1="-3.6" y1="1.5" x2="-3" y2="1.5" stroke={PLANO.pared} strokeWidth="1.3" />
          <line x1="3" y1="1.5" x2="3.6" y2="1.5" stroke={PLANO.pared} strokeWidth="1.3" />
          <path d="M -3 -4.5 A 6 6 0 0 1 3 1.5" fill="none" stroke={PLANO.sillaBorde} strokeWidth="0.25" strokeDasharray="0.7 0.5" />
          <line x1="-3" y1="1.5" x2="-3" y2="-4.5" stroke={PLANO.pared} strokeWidth="0.5" strokeLinecap="round" />
          <text x="0" y="3.4" textAnchor="middle" dominantBaseline="central" fontSize="1.4" fontWeight="700" fill="#6B7280"
            transform={el.rot ? `rotate(${-el.rot} 0 3.4)` : undefined}>ENTRADA</text>
        </g>
      );
    }
    // Etiqueta de sala ("Cocina", "Baños"...): solo texto, sin icono ni fondo, para que se lea
    // como el rótulo de una zona del local y no como un mueble más colocado en el plano.
    if (el.tipo === 'etiqueta') {
      return (
        <g key={el.id} transform={`translate(${el.x} ${el.y})`} style={interactivo ? { cursor: 'pointer' } : undefined}>
          {seleccionado && <rect x="-9" y="-3" width="18" height="6" rx="1.5" fill="none" stroke={C.accent} strokeWidth="0.8" strokeDasharray="1.2 1" />}
          <text textAnchor="middle" dominantBaseline="central" fontSize="3.6" fontWeight="800" fill="#6B7280" style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>{el.texto || 'Sala'}</text>
        </g>
      );
    }
    const h = MAPA_HERRAMIENTAS_POR_ID[el.tipo];
    return (
      <g key={el.id} transform={`translate(${el.x} ${el.y})`} style={interactivo ? { cursor: 'pointer' } : undefined}>
        {seleccionado && <rect x="-4" y="-4" width="8" height="8" rx="2" fill="none" stroke={C.accent} strokeWidth="0.8" strokeDasharray="1.2 1" />}
        <rect x="-3" y="-3" width="6" height="6" rx="1.5" fill={h?.bg || '#F3F4F6'} stroke={h?.border || '#D1D5DB'} strokeWidth="0.8" />
        {iconoDeEmoji(h?.emoji) ? (
          <g transform="translate(-2.1 -2.1) scale(0.175)" fill="none" stroke={h?.color || '#374151'} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">{piezasIcono(iconoDeEmoji(h.emoji))}</g>
        ) : <text textAnchor="middle" dominantBaseline="central" fontSize="3.6">{h?.emoji}</text>}
      </g>
    );
  };

  // FUSIÓN DE MESAS CONTIGUAS (22 ago 2026). Agrupa mesas alineadas en línea recta (horizontal o
  // vertical) y que se tocan de verdad, para dibujarlas como una única mesa grande — como en un
  // restaurante real cuando se juntan varias mesas para un grupo. Es un cálculo derivado en cada
  // render, no se guarda nada nuevo: mover una mesa lejos de las demás la separa sola.
  // LIMITACIÓN ACEPTADA A PROPÓSITO: solo se fusiona lo alineado en línea recta. Una mesa puesta
  // en diagonal respecto a las demás no se fusiona — es con diferencia el caso real más habitual
  // (mesas en fila) y evita calcular geometría de formas irregulares o en ángulo.
  const UMBRAL_FUSION_MESAS = 7.2; // algo más que el diámetro de una mesa (6.8), para que solo se junten si de verdad se tocan
  const esMesaFusionable = (e) => e.tipo === 'mesa' && !['rectangular', 'ovalada'].includes(e.forma);
  const clustersDeMesas = (elementos) => {
    const mesas = elementos.filter(esMesaFusionable);
    const visitado = new Set();
    const clusters = [];
    for (const m of mesas) {
      if (visitado.has(m.id)) continue;
      const cluster = [m];
      visitado.add(m.id);
      let i = 0;
      while (i < cluster.length) {
        const actual = cluster[i];
        for (const otra of mesas) {
          if (visitado.has(otra.id)) continue;
          const dx = Math.abs(otra.x - actual.x), dy = Math.abs(otra.y - actual.y);
          const enFila = dy < 1.5 && dx > 0 && dx < UMBRAL_FUSION_MESAS;
          const enColumna = dx < 1.5 && dy > 0 && dy < UMBRAL_FUSION_MESAS;
          if (enFila || enColumna) { visitado.add(otra.id); cluster.push(otra); }
        }
        i++;
      }
      clusters.push(cluster);
    }
    return clusters;
  };
  // Dibuja un cluster de 2+ mesas juntas como un único cuerpo alargado, con sillas a los dos
  // lados largos y en las dos puntas, y el número de cada mesa marcado en su sitio original (no
  // se inventa una numeración nueva combinada, porque los números pueden no ser correlativos).
  const iconoClusterMesas = (cluster, { seleccionadoId } = {}) => {
    const horizontal = Math.abs(cluster[0].y - cluster[1].y) < 1.5;
    const ordenado = [...cluster].sort((a, b) => horizontal ? a.x - b.x : a.y - b.y);
    const r = 3.1, d = r + 1.55;
    const primero = ordenado[0], ultimo = ordenado[ordenado.length - 1];
    const idsCluster = ordenado.map(e => e.id).join('-');
    const seleccionado = seleccionadoId && ordenado.some(e => e.id === seleccionadoId);
    const x0 = primero.x - r, y0 = primero.y - r;
    const w = horizontal ? (ultimo.x - primero.x) + r * 2 : r * 2;
    const h = horizontal ? r * 2 : (ultimo.y - primero.y) + r * 2;
    return (
      <g key={`cluster_${idsCluster}`}>
        {seleccionado && <rect x={x0 - 2.6} y={y0 - 2.6} width={w + 5.2} height={h + 5.2} rx="1.4" fill="none" stroke={C.accent} strokeWidth="0.6" strokeDasharray="1.2 0.9" />}
        {ordenado.map((el, i) => {
          const ext1 = i === 0, ext2 = i === ordenado.length - 1;
          return (
            <g key={el.id}>
              {horizontal ? <>{sillaPlano(el.x, el.y - d, 180, 'n')}{sillaPlano(el.x, el.y + d, 0, 's')}{ext1 && sillaPlano(el.x - d, el.y, 90, 'w')}{ext2 && sillaPlano(el.x + d, el.y, 270, 'e')}</>
                : <>{sillaPlano(el.x - d, el.y, 90, 'w')}{sillaPlano(el.x + d, el.y, 270, 'e')}{ext1 && sillaPlano(el.x, el.y - d, 180, 'n')}{ext2 && sillaPlano(el.x, el.y + d, 0, 's')}</>}
            </g>
          );
        })}
        <rect x={x0 + 0.35} y={y0 + 0.45} width={w} height={h} rx="0.45" fill={PLANO.sombra} />
        <rect x={x0} y={y0} width={w} height={h} rx="0.45" fill={PLANO.madera} stroke={PLANO.maderaBorde} strokeWidth="0.35" />
        {ordenado.slice(1).map((el, i) => {
          const prev = ordenado[i];
          return horizontal
            ? <line key={'j' + el.id} x1={(prev.x + el.x) / 2} y1={y0 + 0.3} x2={(prev.x + el.x) / 2} y2={y0 + h - 0.3} stroke={PLANO.maderaBorde} strokeWidth="0.18" />
            : <line key={'j' + el.id} x1={x0 + 0.3} y1={(prev.y + el.y) / 2} x2={x0 + w - 0.3} y2={(prev.y + el.y) / 2} stroke={PLANO.maderaBorde} strokeWidth="0.18" />;
        })}
        {ordenado.map(el => <text key={'t' + el.id} x={el.x} y={el.y} textAnchor="middle" dominantBaseline="central" fontSize="2.5" fontWeight="700" fill={PLANO.texto}>{el.numero ?? ''}</text>)}
      </g>
    );
  };
  // Punto de entrada único para dibujar todos los elementos del plano: agrupa mesas contiguas en
  // clusters fusionados y deja el resto (barra, entrada, mesas sueltas) con su icono normal.
  const renderElementosMapa = (elementos, { interactivo = false, seleccionadoId = null } = {}) => {
    const clusters = clustersDeMesas(elementos);
    const otros = elementos.filter(e => !esMesaFusionable(e));
    return (
      <>
        {clusters.map(cluster => cluster.length === 1
          ? iconoElementoMapa(cluster[0], { interactivo, seleccionado: cluster[0].id === seleccionadoId })
          : iconoClusterMesas(cluster, { seleccionadoId }))}
        {otros.map(el => iconoElementoMapa(el, { interactivo, seleccionado: el.id === seleccionadoId }))}
      </>
    );
  };



  // Cuántas personas quiere el encargado como TOPE para cada local (opcional). Se guarda en
  // `config.personasPorLocal = { [localId]: numero }`, junto al resto de ajustes del negocio, y
  // se decide en la propia pestaña Horario — no en Configuración — porque es algo que cambia
  // cada vez que se genera un horario, no un ajuste fijo del negocio.
  const personasParaLocal = (localId) => config?.personasPorLocal?.[localId] || 0; // 0 = sin tope

  // Quién puede entrar en el horario de un local. Recibe el ID del local objetivo — por defecto
  // el que se está viendo, pero puede pedirse el de OTRO local sin cambiar de pantalla, que es
  // lo que hace posible el botón de "Generar" individual de cada tarjeta de local.
  // Reglas, en este orden:
  //  1. Quien ya tiene turnos en OTRO local esta misma semana queda fuera: no puede estar en dos
  //     sitios a la vez. Rota de una semana a otra, no dentro de la misma.
  //  2. Quien esté asignado como fijo a otro local, fuera también.
  //  3. El resto entra: los fijos de este local (siempre) y los rotativos (hasta completar el
  //     tope de personas de este local, si se ha puesto uno). Esto es lo que faltaba para que
  //     "rotar" signifique de verdad repartirse entre locales en vez de que todos los rotativos
  //     se amontonen en uno solo.
  const empleadosDisponiblesParaLocal = (localId = localActivoId) => {
    if (!hayVariosLocales) return { disponibles: employees, ocupados: [] };

    const ocupadosIds = new Set();
    Object.entries(schedulesPorLocal).forEach(([lid, sch]) => {
      if (lid === localId || !sch) return;
      Object.entries(sch).forEach(([empId, semana]) => {
        const trabaja = DAYS.some(d => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(semana?.[d]?.tipo));
        if (trabaja) ocupadosIds.add(String(empId));
      });
    });

    const ocupados = [];
    const candidatos = employees.filter(e => {
      if (ocupadosIds.has(String(e.id))) { ocupados.push(e.name); return false; }
      if (e.localFijo && e.localFijo !== localId) return false;
      return true;
    });

    const tope = personasParaLocal(localId);
    const fijos = candidatos.filter(e => e.localFijo === localId);
    const rotativos = shuffle(candidatos.filter(e => !e.localFijo));

    // MEJORA (13 ago 2026), a petición de Fran: si no hay tope puesto a mano, antes esto se
    // llevaba a TODOS los rotativos disponibles a este local — y como cada local se genera por
    // su cuenta sin saber nada del otro, el primero que generabas se quedaba con casi todo el
    // mundo (por ejemplo 10 de 13, dejando solo 3 para el siguiente). Ahora, sin tope manual, se
    // calcula un reparto justo por defecto: el total de gente disponible ahora mismo, repartido
    // a partes iguales entre los locales que TODAVÍA no tienen horario generado — contando el
    // que se está generando ahora. Así, si generas uno por uno, cada uno se reserva solo su
    // parte proporcional en vez de llevarse a todo el mundo; y el ÚLTIMO local que generes se
    // queda automáticamente con todo lo que sobre, en vez de quedarse corto sin necesidad.
    let topeEfectivo = tope;
    if (!topeEfectivo) {
      const localesSinGenerar = locales.filter(l =>
        l.id === localId || Object.keys(schedulesPorLocal[l.id] || {}).length === 0
      ).length;
      topeEfectivo = Math.max(1, Math.ceil((rotativos.length + fijos.length) / Math.max(1, localesSinGenerar)));
    }

    // Con fijos: entran siempre, aunque se pase del número (son suyos, no van a ningún otro
    // sitio). Los rotativos se recortan hasta completar el hueco que quede.
    // FALLO REAL ENCONTRADO (8 ago 2026): el recorte cogía los PRIMEROS rotativos del array tal
    // cual, sin barajar — como los empleados nuevos se añaden al final, cualquiera recién creado
    // quedaba siempre el último de la cola y, con el tope apretado, se quedaba fuera SIEMPRE, en
    // todos los locales, sin ninguna oportunidad. Ahora se baraja antes de recortar, para que
    // cada generación dé a todo el mundo la misma probabilidad de entrar.
    const huecoRotativos = Math.max(0, topeEfectivo - fijos.length);
    const disponibles = [...fijos, ...rotativos.slice(0, huecoRotativos)];
    return { disponibles, ocupados };
  };

  // Genera el horario de UN local. Antes solo sabía generar "el que se está viendo ahora
  // mismo"; ahora acepta el id de cualquier local, así que se puede generar el de un local
  // sin tener que cambiarte a verlo primero (eso es lo que usan los botones "Generar [nombre]"
  // de cada tarjeta en la pestaña Horario). Si no se le pasa nada, sigue haciendo lo de
  // siempre: generar el local activo — así el botón "🔄 Regenerar" del banner de estado no
  // cambia en nada.
  const handleGenerateSchedule = async (localIdObjetivo = localActivoId) => {
    if (generandoRef.current) return; // ya hay una generación en marcha — ignora el doble clic
    const { disponibles, ocupados } = empleadosDisponiblesParaLocal(localIdObjetivo);
    const nombreLocal = locales.find(l => l.id === localIdObjetivo)?.nombre || 'este local';

    if (disponibles.length === 0) {
      // Averiguar en qué otro local están "ocupados" los empleados, para poder ofrecer vaciar
      // ese local directamente en vez de dejar al encargado sin salida. Esto es lo que resuelve
      // el bloqueo mutuo: si dos locales quedan con las mismas personas asignadas (por ejemplo,
      // arrastre de un fallo de una versión anterior), antes no había forma de romperlo desde
      // aquí — solo se podía "Entendido" y quedarse atascado.
      const localBloqueante = locales.find(l => l.id !== localIdObjetivo
        && Object.values(schedulesPorLocal[l.id] || {}).some(sem =>
          DAYS.some(d => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(sem?.[d]?.tipo))));

      if (localBloqueante) {
        const vaciarOtro = await confirmar({
          icono: '🔒',
          titulo: `No hay nadie disponible para ${nombreLocal}`,
          lineas: [
            `Todas las personas ya tienen turnos esta semana en «${localBloqueante.nombre}», o están asignadas como fijas a otro sitio.`,
            'Puedes vaciar ese horario para liberarlas, o revisar los locales fijos de tu plantilla en la pestaña Empleados.',
          ],
          textoOk: `Vaciar el horario de ${localBloqueante.nombre}`,
          textoCancelar: 'Ahora no',
        });
        if (vaciarOtro) {
          // Se vacía el horario del OTRO local sin cambiar de pantalla: se actualiza su hueco en
          // schedulesPorLocal directamente. Si es el que se está viendo ahora mismo no se llega
          // aquí, porque entonces `localBloqueante.id !== localIdObjetivo` ya lo habría excluido.
          setSchedulesPorLocal(prev => ({ ...prev, [localBloqueante.id]: {} }));
          setPublicadosPorLocal(prev => ({ ...prev, [localBloqueante.id]: false }));
          fijarFechasHorario(localBloqueante.id, null);
        }
      } else {
        await avisar({
          icono: '⚠️',
          titulo: `No hay nadie disponible para ${nombreLocal}`,
          lineas: [
            'Todas las personas están asignadas como fijas a otro sitio.',
            'Revisa los locales fijos de tu plantilla en la pestaña Empleados.',
          ],
        });
      }
      return;
    }

    if (ocupados.length > 0) {
      const seguir = await confirmar({
        icono: '👥',
        titulo: `${ocupados.length === 1 ? 'Una persona ya está' : `${ocupados.length} personas ya están`} en otro local esta semana`,
        lineas: [
          `No entrarán en el horario de ${nombreLocal}: ${ocupados.join(', ')}.`,
          'Nadie puede trabajar en dos locales la misma semana. Si quieres cambiarlo, borra antes su turno en el otro local.',
        ],
        textoOk: 'Generar sin ellos',
        textoCancelar: 'Cancelar',
      });
      if (!seguir) return;
    }

    generandoRef.current = true;
    setGenerating(true);
    setTimeout(() => {
      const approved = requests.filter(r => r.status === 'approved');
      const nuevo = generateSchedule(disponibles, specialDays, approved, config, temporadas[temporada], scheduleHistory, isCocina);
      // FALLO REAL ENCONTRADO (12 ago 2026): cuando se generaba el local que ya se estaba
      // viendo, el resultado solo se guardaba en `schedule` (el hueco activo) — nunca en
      // `schedulesPorLocal[localIdObjetivo]`. La comprobación de "quién ya está ocupado en otro
      // local" (`empleadosDisponiblesParaLocal`) mira justo `schedulesPorLocal`, así que si
      // después se generaba OTRO local por separado, no veía a nadie ocupado todavía y repartía
      // a la misma gente en los dos — le pasó a 8 de 13 personas de golpe. Ahora se escriben
      // SIEMPRE los dos sitios a la vez, sea o no el local activo, para que la comprobación
      // cruzada nunca mire un hueco desactualizado.
      setSchedulesPorLocal(prev => ({ ...prev, [localIdObjetivo]: nuevo }));
      setPublicadosPorLocal(prev => ({ ...prev, [localIdObjetivo]: false }));
      // El horario nuevo es el de la semana que viene (la misma que ha usado el generador).
      fijarFechasHorario(localIdObjetivo, getWeekDates());
      if (localIdObjetivo === localActivoId) {
        setSchedule(nuevo); setSchedulePublished(false); setVueltaHorario(v => v + 1);
      }
      contarHorarioGenerado(1);
      generandoRef.current = false;
      setGenerating(false); setActiveTab('horario');
    }, 700);
  };

  // Genera el horario de varios locales a la vez, en una sola pasada. Es lo que Fran pidió tras
  // quedar atascado: antes había que generar local por local, y al ir al segundo la app decía
  // que todo el mundo ya estaba "ocupado" en el primero — porque el primero se había llevado a
  // todos los rotativos sin dejar a nadie para el resto.
  // La idea es sencilla: se recorren los locales uno a uno, y cada rotativo que YA ha quedado
  // con turnos reales en un local se retira del grupo disponible para los siguientes. Los fijos
  // de cada local entran siempre en el suyo, nunca se reparten. Así ningún local se queda vacío
  // solo por haber ido después en el orden, y nadie sale duplicado en dos sitios.
  // Acepta opcionalmente un subconjunto de locales (para el botón "Generar seleccionados" cuando
  // hay más de dos) — por defecto genera TODOS.
  const handleGenerateAllSchedules = async (localesAGenerar = locales) => {
    if (generandoRef.current) return; // ya hay una generación en marcha — ignora el doble clic
    if (!hayVariosLocales) { handleGenerateSchedule(); return; }
    if (!localesAGenerar.length) return;
    const esTodos = localesAGenerar.length === locales.length;

    const ok = await confirmar({
      icono: '🏢',
      titulo: esTodos ? `¿Generar el horario de los ${locales.length} locales?` : `¿Generar el horario de los ${localesAGenerar.length} locales seleccionados?`,
      lineas: [
        'Cada persona rotativa se asigna a un único local esta semana; los fijos van siempre al suyo.',
        'Se sustituye el horario actual de cada local. Si alguno ya estaba publicado, quedará sin publicar hasta que lo revises.',
      ],
      textoOk: esTodos ? 'Generar todos' : 'Generar seleccionados',
      textoCancelar: 'Cancelar',
    });
    if (!ok) return;

    generandoRef.current = true;
    setGenerating(true);
    setTimeout(() => {
      const approved = requests.filter(r => r.status === 'approved');
      const resultados = {};

      // MEJORA (12 ago 2026), a petición de Fran al ver el resultado real: antes se procesaba
      // un local ENTERO antes de pasar al siguiente, así que el primero de la lista se llevaba a
      // casi todo el mundo (hasta llenar su tope) y al siguiente solo le quedaban las migajas —
      // con 13 personas y tope 10 en cada uno, salía 10 y 3. No era un fallo del reparto en sí
      // (nadie repetía local, la regla se cumplía), pero daba mala imagen: un local casi vacío
      // sin necesidad.
      // Ahora primero se decide QUIÉN va a cada local, repartiendo a los rotativos por turnos
      // entre todos los locales (uno para cada uno, dando la vuelta), en vez de vaciar el primero
      // antes de que el segundo tenga su oportunidad — y SOLO DESPUÉS se genera el horario de
      // cada uno con su gente ya asignada. Con la misma plantilla de antes, esto reparte 7 y 6
      // en vez de 10 y 3.
      const fijosPorLocal = {};
      localesAGenerar.forEach(local => {
        fijosPorLocal[local.id] = employees.filter(e => e.localFijo === local.id);
      });
      const rotativosGlobales = shuffle(employees.filter(e => !e.localFijo));

      const asignadosPorLocal = {};
      localesAGenerar.forEach(l => { asignadosPorLocal[l.id] = [...fijosPorLocal[l.id]]; });

      let indiceLocal = 0;
      rotativosGlobales.forEach(emp => {
        for (let intentos = 0; intentos < localesAGenerar.length; intentos++) {
          const local = localesAGenerar[(indiceLocal + intentos) % localesAGenerar.length];
          const tope = personasParaLocal(local.id);
          if (!tope || asignadosPorLocal[local.id].length < tope) {
            asignadosPorLocal[local.id].push(emp);
            indiceLocal = (indiceLocal + intentos + 1) % localesAGenerar.length;
            return;
          }
        }
        // Si ningún local tiene ya hueco (todos los topes llenos), esta persona se queda sin
        // local esta semana — no pasa nada, tocará rotar la semana que viene.
      });

      localesAGenerar.forEach(local => {
        resultados[local.id] = generateSchedule(asignadosPorLocal[local.id], specialDays, approved, config, temporadas[temporada], scheduleHistory, isCocina);
      });

      setSchedulesPorLocal(prev => ({ ...prev, ...resultados }));
      setPublicadosPorLocal(prev => {
        const n = { ...prev };
        localesAGenerar.forEach(l => { n[l.id] = false; });
        return n;
      });
      const semanaGenerada = getWeekDates();
      setFechasPorLocal(prev => {
        const n = { ...prev };
        localesAGenerar.forEach(l => { n[l.id] = { ...semanaGenerada }; });
        return n;
      });
      // La pantalla sigue mostrando el local que ya se estaba viendo, ahora con su horario nuevo
      // (si el local activo estaba entre los generados; si no, se queda como estaba).
      if (resultados[localActivoId]) { setSchedule(resultados[localActivoId]); setVueltaHorario(v => v + 1); }
      if (localActivoId in resultados) setSchedulePublished(false);
      contarHorarioGenerado(localesAGenerar.length);
      generandoRef.current = false;
      setGenerating(false); setActiveTab('horario');
    }, 700);
  };


  // Vacía el horario de un local, para poder empezar de cero. Es lo que faltaba cuando dos
  // locales quedaban con horarios solapados (por ejemplo, arrastre del fallo antiguo de antes
  // del multi-local) y el generador se negaba a asignar a nadie en ninguno de los dos: sin esto
  // no había forma de romper ese bloqueo desde la propia app.
  // FALLO REAL ENCONTRADO (12 ago 2026): antes solo aceptaba vaciar el local que se estaba
  // viendo en pantalla — así que con varios locales, vaciar uno mientras mirabas otro era
  // imposible desde el botón de la tarjeta ("Vaciar" sin más), y solo se veía vacío el que
  // tuvieras abierto en ese momento; el resto seguía marcado como "generado" aunque quisieras
  // limpiarlos todos. Ahora acepta el id de cualquier local (por defecto, el que se esté viendo,
  // igual que antes) — así el botón de cada tarjeta puede vaciar SU PROPIO local sin cambiar de
  // pantalla, igual que ya hace "Generar [nombre]".
  const handleVaciarHorario = async (localIdObjetivo = localActivoId) => {
    const nombreLocal = locales.find(l => l.id === localIdObjetivo)?.nombre || localActivo?.nombre || 'este local';
    const ok = await confirmar({
      icono: '🗑️',
      titulo: `¿Vaciar el horario de ${nombreLocal}?`,
      lineas: [
        'Se borran todos los turnos de esta semana en este local y queda como si no se hubiera generado.',
        'Los empleados dejarán de aparecer como ocupados aquí, así que podrás volver a generarlo o usarlos en otro local.',
        'Esto NO toca el historial ya publicado ni los demás locales.',
      ],
      textoOk: 'Vaciar y empezar de cero',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (!ok) return;
    quitarProgramacion(localIdObjetivo);
    fijarFechasHorario(localIdObjetivo, null);
    if (localIdObjetivo === localActivoId) {
      setSchedule({});
      setSchedulePublished(false);
    } else {
      setSchedulesPorLocal(prev => ({ ...prev, [localIdObjetivo]: {} }));
      setPublicadosPorLocal(prev => ({ ...prev, [localIdObjetivo]: false }));
    }
  };

  // ---- PUBLICACIÓN PROGRAMADA ----
  // Guarda el mapa completo de programaciones y la hora más próxima (número plano, para que el
  // servidor pueda buscar solo lo que toca con una consulta barata). Se escribe aparte de save():
  // save() no toca estos campos, así que no puede pisarlos con datos viejos.
  const guardarProgramaciones = async (nuevoMapa) => {
    if (!restaurantDocId) return;
    const horas = Object.values(nuevoMapa).map(x => Number(x?.cuando)).filter(Number.isFinite);
    await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId), {
      programadoPorLocal: nuevoMapa,
      proximaPublicacionMs: horas.length ? Math.min(...horas) : deleteField(),
    });
  };
  const textoCuandoProgramado = (ms) => {
    const f = new Date(ms);
    const dia = f.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    const hora = f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return `${dia} a las ${hora}`;
  };
  const abrirProgramar = () => {
    const previa = programadoPorLocal[localActivoId];
    const base = previa?.cuando ? new Date(previa.cuando) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    setProgFecha(toDateStr(base));
    setProgHora(previa?.cuando
      ? `${String(base.getHours()).padStart(2, '0')}:${String(base.getMinutes()).padStart(2, '0')}`
      : '14:00');
    setProgError('');
    setProgramarAbierto(true);
  };
  const confirmarProgramacion = async () => {
    if (!progFecha || !progHora) { setProgError('Elige el día y la hora.'); return; }
    const cuando = new Date(`${progFecha}T${progHora}:00`).getTime();
    if (!Number.isFinite(cuando)) { setProgError('La fecha u hora no es válida.'); return; }
    if (cuando < Date.now() + 2 * 60 * 1000) { setProgError('Elige una hora futura (al menos dentro de 2 minutos).'); return; }
    if (cuando > Date.now() + 30 * 24 * 60 * 60 * 1000) { setProgError('Como máximo se puede programar con 30 días de antelación.'); return; }
    // Las fechas de la semana se fijan AHORA, al programar: getWeekDates() depende del día en que
    // se llama, y el día de la publicación el "próximo lunes" ya podría ser el de la semana
    // siguiente. Así el historial guarda la semana que de verdad se programó.
    // (29 oct 2026) Son las fechas del propio horario (fechasDelHorario), no "la semana que viene"
    // según el día de hoy: un horario del 2 al 8 programado el martes 3 sigue siendo del 2 al 8.
    const fechasSemana = fechasDelHorario();
    const [ly, lm, ld] = fechasSemana.Lunes.split('-').map(Number);
    const nuevo = {
      ...programadoPorLocal,
      [localActivoId]: { cuando, fechas: fechasSemana, semanaKey: getWeekKey(new Date(ly, lm - 1, ld)), creadoEn: Date.now() },
    };
    setProgGuardando(true);
    try {
      await guardarProgramaciones(nuevo);
      setProgramadoPorLocal(nuevo);
      fijarFechasHorario(localActivoId, fechasSemana);
      setProgramarAbierto(false);
    } catch (e) {
      console.error('Error programando la publicación:', e);
      setProgError('No se ha podido guardar. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setProgGuardando(false);
    }
  };
  const quitarProgramacion = async (localId = localActivoId) => {
    if (!programadoPorLocal[localId]) return;
    const nuevo = { ...programadoPorLocal };
    delete nuevo[localId];
    const anterior = programadoPorLocal;
    setProgramadoPorLocal(nuevo);
    try {
      await guardarProgramaciones(nuevo);
    } catch (e) {
      console.error('Error quitando la programación:', e);
      setProgramadoPorLocal(anterior);
    }
  };

  // Identificador de "qué semana es" (lunes-domingo, igual que las columnas DAYS), para poder
  // saber si una publicación es de la MISMA semana que otra ya guardada en el historial.
  const getWeekKey = (fecha) => {
    const d = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()));
    const diaISO = d.getUTCDay() || 7; // domingo=0 -> 7, para que la semana empiece en lunes
    d.setUTCDate(d.getUTCDate() + 4 - diaISO); // jueves de esa semana (ancla estándar ISO)
    const inicioAno = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const numSemana = Math.ceil((((d - inicioAno) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-W${numSemana}`;
  };

  const handlePublishSchedule = async () => {
    // AVISO ANTES DE PUBLICAR — QUITADO A PETICIÓN DE FRAN (6 sep 2026, segunda vez: ya se había
    // quitado el 25 ago, luego se reintrodujo sin querer al tocar el generador). Motivo explícito
    // suyo: "eso ya depende de cada encargado", no quiere que la app le pregunte nada al publicar.
    // Se ofreció la alternativa de comparar contra el mínimo ya ajustado a la plantilla en vez del
    // configurado a mano, y aun así prefirió quitarlo del todo. Publicar es ahora directo, sin
    // ningún diálogo de por medio.
    setSchedulePublished(true);
    // Si había una publicación programada de este local, ya no hace falta (el servidor además la
    // descartaría solo al ver el horario ya publicado, pero así se limpia al momento).
    quitarProgramacion(localActivoId);
    // FALLO REAL ENCONTRADO (8 ago 2026): cada vez que se pulsaba "Publicar" se añadía una
    // entrada NUEVA al historial, aunque fuera la misma semana del mismo local republicada tras
    // corregir algo. El contador de "turnos trabajados este mes" suma TODO el historial del mes,
    // así que cada republicación de la misma semana inflaba el contador de verdad (Mario con 3
    // turnos reales aparecía con 8, por varias publicaciones de prueba de la misma semana). Ahora
    // se calcula la semana (`semanaKey`) y si ya había una entrada de esa misma semana Y ese
    // mismo local, se SUSTITUYE en vez de sumarse una nueva.
    const ahora = Date.now();
    // La semana que se publica es la de getWeekDates() (la que se está gestionando, que empieza el
    // próximo lunes), no la del día en que se pulsa "Publicar". Antes la clave salía de la fecha
    // de hoy: un horario publicado en domingo y retocado el lunes quedaba DOS veces en el
    // historial (11 oct 2026). Se guardan además sus fechas exactas.
    // (29 oct 2026) Ahora se publican las fechas del PROPIO horario (fechasDelHorario): antes, uno
    // generado el sábado para el lunes 2 y publicado el martes 3 quedaba como del 9 al 15.
    const fechasSemana = fechasDelHorario();
    fijarFechasHorario(localActivoId, fechasSemana);
    const [ly, lm, ld] = fechasSemana.Lunes.split('-').map(Number);
    const semanaKey = getWeekKey(new Date(ly, lm - 1, ld));
    const nuevaEntrada = {
      fechas: fechasSemana,
      id: ahora,
      publishedAt: new Date().toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      publishedAtTimestamp: ahora,
      schedule,
      specialDays,
      // A qué local pertenece este horario. Los históricos antiguos no lo llevan: se tratan
      // como del local principal, que es lo que eran.
      localId: localActivoId,
      localNombre: localActivo?.nombre || '',
      semanaKey,
      temporadaNombre: temporadas[temporada]?.nombre || 'Normal',
      employeesSnapshot: employees.map(e => ({ id: e.id, name: e.name, esEncargado: e.esEncargado, turnosSemanales: e.turnosSemanales })),
    };
    setScheduleHistory(prev => {
      const mismaSemana = (h) => (h.localId || LOCAL_PRINCIPAL) === (localActivoId || LOCAL_PRINCIPAL)
        && (h.semanaKey === semanaKey || fechasDeEntradaHistorial(h).Lunes === fechasSemana.Lunes);
      const yaExiste = prev.some(mismaSemana);
      const actualizado = yaExiste
        ? [...prev.filter(h => !mismaSemana(h)), nuevaEntrada]
        : [...prev, nuevaEntrada];
      // Limpieza automática: evita que el historial (y por tanto el documento del restaurante
      // en Firestore) crezca sin límite con el paso de los meses. Ver HISTORIAL_MESES_MAX arriba.
      return limpiarHistorialAntiguo(actualizado);
    });
    if (restaurantDocId) {
      enviarMensajeConLimite('mensajes', {
        senderId: auth.currentUser?.uid || 'sistema', sender: 'Sistema',
        text: 'El encargado ha publicado el horario de la semana. ¡Revísalo en Mi Horario!',
        time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        createdAt: Date.now(),
        expiraEl: fechaCaducidadMensaje(),
      }).catch(e => console.error('Error enviando mensaje del sistema:', e));
    }
  };

  // Borra UNA entrada concreta del historial (para limpiar publicaciones de prueba duplicadas,
  // como las que dejó el fallo del contador del 8 ago 2026 antes de arreglarse).
  // Cambios hechos a mano DESPUÉS de publicar (editar un turno, intercambio, cobertura, día libre
  // aprobado): se copian a la entrada del historial de esta semana y este local, para que el
  // recuento del mes refleje lo que de verdad trabaja cada uno.
  // REVISADO (29 oct 2026): antes solo se intentaba UNA vez, justo al hacer el cambio. En las pruebas
  // se vio que, si en ese mismo instante llegaba del servidor la copia del documento guardada un
  // momento antes (con el historial sin el cambio), esa copia pisaba la actualización y el cambio no
  // llegaba nunca al historial ni al recuento. Ahora se comprueba cada vez que cambia el horario o
  // el historial: si el horario publicado y su entrada del historial no coinciden, se igualan. Es
  // seguro repetirlo (si ya coinciden no hace nada) y además arregla solo los cambios que se
  // quedaron sin copiar antes de este arreglo. Solo el Encargado (es quien guarda el documento).
  useEffect(() => {
    if (!schedulePublished || userRole !== 'jefe') return;
    // La semana del propio horario publicado (antes "la que viene", que a mitad de semana era otra
    // y los cambios no llegaban nunca al historial ni al recuento).
    const lunes = fechasDelHorario().Lunes;
    setScheduleHistory(prev => {
      let cambiado = false;
      const nuevo = prev.map(h => {
        if ((h.localId || LOCAL_PRINCIPAL) !== (localActivoId || LOCAL_PRINCIPAL)) return h;
        if (fechasDeEntradaHistorial(h).Lunes !== lunes) return h;
        if (firmaEstable(h.schedule || {}) === firmaEstable(schedule || {})) return h;
        cambiado = true;
        return { ...h, schedule, actualizadoTs: Date.now() };
      });
      return cambiado ? nuevo : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cambiosTrasPublicar, schedule, schedulePublished, scheduleHistory, localActivoId, fechasPorLocal, userRole]);

  const borrarEntradaHistorial = async (h) => {
    const ok = await confirmar({
      icono: '🗑️',
      titulo: `¿Borrar el horario publicado el ${h.publishedAt}?`,
      lineas: [
        'Desaparece del historial y deja de contar en los turnos trabajados de este mes.',
        'No afecta al horario que esté publicado ahora mismo para los empleados.',
      ],
      textoOk: 'Sí, borrarlo',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (!ok) return;
    setScheduleHistory(prev => prev.filter(x => x.id !== h.id));
    setViewingHistoryId(null);
  };

  const sendRequest = async () => {
    if (!requestDate || !restaurantDocId) return;
    try {
      await addDoc(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes'), {
        employeeUid: auth.currentUser?.uid || 'desconocido',
        // FALLO REAL ENCONTRADO (8 ago 2026): las solicitudes solo guardaban `sender` (el
        // NOMBRE de quien la pide), y el generador de horarios las emparejaba con cada empleado
        // por nombre. Si alguien borraba a un empleado y creaba otro con el MISMO nombre, heredaba
        // en silencio los días libres ya aprobados del que ya no existe — le pasó justo a un
        // "Mario" nuevo, que se quedó solo con 4 turnos de 8 sin que nadie supiera por qué. Ahora
        // se guarda también `senderId`, el id real de la ficha del empleado — que es único y no
        // se hereda al recrear a alguien con el mismo nombre.
        senderId: selectedEmployeeId || null,
        sender: currentUser, date: requestDate, note: requestNote,
        status: 'pending', createdAt: new Date().toLocaleDateString('es-ES'),
        createdAtTs: Date.now(),
      });
      setRequestDate(''); setRequestNote('');
    } catch (e) { console.error('Error enviando solicitud:', e); }
  };

  // Publica una petición de cobertura. Está separada del formulario a propósito, porque se llama
  // desde dos sitios: el botón de "Preguntar al equipo" y, automáticamente, al aprobar un día
  // libre que deja un turno sin cubrir.
  const publicarCobertura = async (dia, turno, nota) => {
    if (!dia || !restaurantDocId) return false;
    try {
      await addDoc(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes'), {
        tipoSolicitud: 'cobertura',
        dia,
        fecha: fechasDelHorario()[dia] || '',
        turno,
        note: nota || '',
        sender: currentUser || 'Encargado',
        employeeUid: auth.currentUser?.uid || 'desconocido',
        status: 'abierta',
        respuestas: {},
        asignadoA: null,
        createdAt: new Date().toLocaleDateString('es-ES'),
        createdAtTs: Date.now(),
      });
      return true;
    } catch (e) {
      console.error('Error creando la cobertura:', e);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido publicar', lineas: ['Comprueba tu conexión e inténtalo de nuevo.'] });
      return false;
    }
  };

  // Aprobar o rechazar una petición de día libre.
  // Al APROBAR ya no se queda en un simple cambio de estado: si ese día la persona tenía turno,
  // se le pone libre en el cuadrante al momento (antes había que entrar al horario a mano), queda
  // registrado como turno quitado con su fecha, y se ofrece publicar la cobertura ahí mismo.
  // Así el círculo se cierra entero: alguien pide → el turno se libera → se busca quien lo cubra.
  // Borra una solicitud de día libre por completo (no solo cambiarle el estado). Es lo que
  // pidió Fran al descubrir que las ya aprobadas se quedaban en el servidor para siempre, sin
  // ningún botón para quitarlas — y es justo lo que hacía falta para limpiar a mano las
  // solicitudes viejas que quedaron huérfanas antes del arreglo de `senderId`.
  const borrarSolicitud = async (req) => {
    if (!restaurantDocId || !req?.id) return;
    const ok = await confirmar({
      icono: '🗑️',
      titulo: `¿Borrar la solicitud de ${req.sender}?`,
      lineas: [
        `${fechaConDia(req.date)} · ${req.status === 'approved' ? 'Aprobada' : req.status === 'rejected' ? 'Rechazada' : 'Pendiente'}`,
        'No afecta al horario ya generado; si estaba aprobada, deja de contar en próximas generaciones.',
      ],
      textoOk: 'Sí, borrarla',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id));
    } catch (e) { console.error('Error borrando la solicitud:', e); }
  };

  const updateRequest = async (req, status) => {
    if (!restaurantDocId) return;
    const id = typeof req === 'string' ? req : req?.id;
    if (!id) return;

    // Rechazar (o una solicitud sin datos suficientes) sigue siendo solo cambiar el estado.
    if (status !== 'approved' || typeof req === 'string' || !req.date || !req.sender) {
      try {
        await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', id), { status });
      } catch (e) { console.error('Error actualizando la solicitud:', e); }
      return;
    }

    const emp = employees.find(e => req.senderId ? e.id === req.senderId : e.name === req.sender);
    // El cuadrante es de una semana concreta, así que solo se puede tocar si la fecha pedida cae
    // dentro de la semana que se está gestionando ahora mismo.
    const diaDeLaSemana = Object.entries(fechasDelHorario()).find(([, f]) => f === req.date)?.[0] || null;
    const turnoActual = (emp && diaDeLaSemana) ? getShift(emp.id, diaDeLaSemana) : null;
    const teniaTurno = !!turnoActual && ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(turnoActual.tipo);

    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', id), { status });
    } catch (e) { console.error('Error actualizando la solicitud:', e); return; }

    if (!emp) return;
    if (!diaDeLaSemana) {
      await avisar({
        icono: '✅',
        titulo: 'Solicitud aprobada',
        lineas: [
          'Ese día no está en la semana que estás gestionando ahora, así que el horario no se ha tocado.',
          'Tenlo en cuenta cuando prepares esa semana.',
        ],
      });
      return;
    }
    if (!teniaTurno) return; // ya libraba ese día: no hay nada que liberar ni que cubrir

    const etiqueta = shiftLabel(turnoActual.tipo);
    const ponerLibre = await confirmar({
      icono: '📅',
      titulo: `¿Le pongo el día libre a ${req.sender}?`,
      lineas: [
        `Tenía turno de ${etiqueta} el ${diaDeLaSemana.toLowerCase()}.`,
        'Solo se cambia ese turno; el resto del cuadrante se queda igual.',
      ],
      textoOk: 'Sí, ponerlo libre',
      textoCancelar: 'Dejarlo como está',
    });
    if (!ponerLibre) return;

    setSchedule(prev => ({
      ...prev,
      [emp.id]: { ...(prev[emp.id] || {}), [diaDeLaSemana]: { tipo: 'libre', tareas: [], horaEntrada: '', horaSalida: '' } },
    }));
    setCambiosTrasPublicar(c => c + 1);

    // El registro de "turno quitado" solo tiene sentido si el horario ya estaba publicado: si aún
    // no lo has publicado, no le has quitado nada a nadie, simplemente lo estás preparando.
    if (schedulePublished) {
      setEmployees(prevEmp => prevEmp.map(e => e.id === emp.id
        ? { ...e, turnosQuitados: [...(e.turnosQuitados || []), { id: Date.now(), fecha: req.date, dia: diaDeLaSemana, turno: etiqueta }] }
        : e));
    }

    const buscarCobertura = await confirmar({
      icono: '🙋',
      titulo: '¿Pregunto al equipo quién puede cubrirlo?',
      lineas: [
        `Ha quedado un hueco el ${diaDeLaSemana.toLowerCase()} en turno de ${etiqueta}.`,
        'Les llegará un aviso al móvil y cada uno te dirá si puede o no.',
      ],
      textoOk: 'Preguntar al equipo',
      textoCancelar: 'Ahora no',
    });
    if (buscarCobertura) {
      await publicarCobertura(diaDeLaSemana, turnoActual.tipo, `Se ha quedado libre por la solicitud de ${req.sender}.`);
      setActiveTab('solicitudes');
    }
  };

  // ===== INTERCAMBIO DE TURNO ENTRE COMPAÑEROS =========================================
  // (20 sep 2026, pestaña Solicitudes) En dos pasos, nunca uno solo:
  //  1) El compañero al que se lo propones tiene que aceptar.
  //  2) Solo si acepta, le llega al encargado para dar el visto bueno final — igual que ya pasa
  //     con pedir cobertura o pedir día libre, nunca se cambia nada en el cuadrante sin que
  //     alguien con autoridad lo confirme.
  const crearIntercambio = async () => {
    if (!intercDia || !intercCompaneroId || !restaurantDocId || !selectedEmployeeId) return;
    // El desplegable devuelve el id como texto y los empleados lo tienen como número: se comparan
    // como texto para que siempre se encuentre al compañero (antes no lo encontraba y el botón no
    // hacía nada). A partir de aquí se usa siempre el id real del compañero.
    const companero = employees.find(e => String(e.id) === String(intercCompaneroId));
    if (!companero) return;
    const fechaInterc = fechasDelHorario()[intercDia] || '';
    if (fechaInterc && fechaInterc < toDateStr(new Date())) {
      await avisar({ icono: '📅', titulo: 'Ese día ya ha pasado', lineas: ['Elige un día de hoy en adelante.'] });
      return;
    }
    setIntercBusy(true);
    try {
      const miTurno = getShift(selectedEmployeeId, intercDia);
      const turnoCompanero = getShift(companero.id, intercDia);
      await addDoc(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes'), {
        tipoSolicitud: 'intercambio',
        status: 'pendiente_companero',
        dia: intercDia,
        fecha: fechaInterc,
        solicitanteId: selectedEmployeeId,
        solicitanteName: currentUser,
        companeroId: companero.id,
        companeroName: companero.name,
        turnoSolicitante: miTurno.tipo || 'libre',
        turnoCompanero: turnoCompanero.tipo || 'libre',
        note: intercNota,
        sender: currentUser,
        employeeUid: auth.currentUser?.uid || 'desconocido',
        createdAt: new Date().toLocaleDateString('es-ES'),
        createdAtTs: Date.now(),
      });
      setIntercDia(''); setIntercCompaneroId(''); setIntercNota('');
    } catch (e) {
      console.error('Error creando el intercambio:', e);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido enviar', lineas: ['Comprueba tu conexión e inténtalo de nuevo.'] });
    } finally {
      setIntercBusy(false);
    }
  };

  // Paso 1: responde el compañero al que se le propuso. Aceptar NO cambia el horario todavía —
  // solo pasa la pelota al encargado, que es quien de verdad lo confirma.
  const responderIntercambioCompanero = async (req, acepta) => {
    if (!restaurantDocId || !req?.id) return;
    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id), {
        status: acepta ? 'pendiente_encargado' : 'rechazada_companero',
      });
    } catch (e) {
      // (31 oct 2026) Antes el fallo era silencioso: el botón no hacía nada y nadie sabía por qué.
      console.error('Error respondiendo al intercambio:', e);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido responder', lineas: ['Comprueba tu conexión e inténtalo de nuevo.'] });
    }
  };

  // Paso 2: el encargado da el visto bueno final. Aquí sí se toca el cuadrante — se
  // intercambian los dos turnos tal cual estén en ese momento (no los que había cuando se pidió,
  // por si el horario cambió mientras tanto).
  const resolverIntercambioEncargado = async (req, aprueba) => {
    if (!restaurantDocId || !req?.id) return;
    if (!aprueba) {
      try {
        await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id), { status: 'rechazada_encargado' });
      } catch (e) { console.error('Error rechazando el intercambio:', e); }
      return;
    }
    // Mismo criterio que al aprobar un día libre: el cuadrante es de una semana concreta, solo
    // se puede tocar si la fecha del intercambio cae dentro de la semana que se está gestionando.
    const diaDeLaSemana = Object.entries(fechasDelHorario()).find(([, f]) => f === req.fecha)?.[0] || null;
    if (!diaDeLaSemana) {
      await avisar({ icono: '⚠️', titulo: 'No se puede aprobar', lineas: ['Ese día no está en la semana que estás gestionando ahora mismo.'] });
      return;
    }
    const solicitanteEmp = employees.find(e => e.id === req.solicitanteId);
    const companeroEmp = employees.find(e => e.id === req.companeroId);
    if (!solicitanteEmp || !companeroEmp) {
      await avisar({ icono: '⚠️', titulo: 'No se puede aprobar', lineas: ['Uno de los dos empleados ya no está en la plantilla.'] });
      return;
    }
    const turnoSolicitanteActual = getShift(solicitanteEmp.id, diaDeLaSemana);
    const turnoCompaneroActual = getShift(companeroEmp.id, diaDeLaSemana);

    const confirmado = await confirmar({
      icono: '🔄',
      titulo: `¿Cambiar el ${diaDeLaSemana.toLowerCase()} entre ${solicitanteEmp.name} y ${companeroEmp.name}?`,
      lineas: [
        `${solicitanteEmp.name} pasa a tener lo que tenía ${companeroEmp.name} (${shiftLabel(turnoCompaneroActual.tipo)}).`,
        `${companeroEmp.name} pasa a tener lo que tenía ${solicitanteEmp.name} (${shiftLabel(turnoSolicitanteActual.tipo)}).`,
      ],
      textoOk: 'Sí, cambiar',
      textoCancelar: 'Ahora no',
    });
    if (!confirmado) return;

    setSchedule(prev => ({
      ...prev,
      [solicitanteEmp.id]: { ...(prev[solicitanteEmp.id] || {}), [diaDeLaSemana]: turnoCompaneroActual },
      [companeroEmp.id]: { ...(prev[companeroEmp.id] || {}), [diaDeLaSemana]: turnoSolicitanteActual },
    }));
    setCambiosTrasPublicar(c => c + 1);

    // Igual que al editar un turno a mano: si el horario ya está publicado, queda constancia de
    // qué se le quitó y qué se le añadió a cada uno (pestaña Turnos) — un intercambio son, en el
    // fondo, dos altas/bajas simétricas a la vez.
    if (schedulePublished) {
      const fecha = fechasDelHorario()[diaDeLaSemana];
      const esTrabajo = (t) => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(t);
      const ahora = Date.now();
      setEmployees(prevEmp => prevEmp.map(e => {
        if (e.id === solicitanteEmp.id) {
          const quitados = esTrabajo(turnoSolicitanteActual.tipo)
            ? [...(e.turnosQuitados || []), { id: ahora, fecha, dia: diaDeLaSemana, turno: shiftLabel(turnoSolicitanteActual.tipo) }]
            : (e.turnosQuitados || []);
          const anadidos = esTrabajo(turnoCompaneroActual.tipo)
            ? [...(e.turnosAnadidos || []), { id: ahora + 1, fecha, dia: diaDeLaSemana, turno: shiftLabel(turnoCompaneroActual.tipo) }]
            : (e.turnosAnadidos || []);
          return { ...e, turnosQuitados: quitados, turnosAnadidos: anadidos };
        }
        if (e.id === companeroEmp.id) {
          const quitados = esTrabajo(turnoCompaneroActual.tipo)
            ? [...(e.turnosQuitados || []), { id: ahora + 2, fecha, dia: diaDeLaSemana, turno: shiftLabel(turnoCompaneroActual.tipo) }]
            : (e.turnosQuitados || []);
          const anadidos = esTrabajo(turnoSolicitanteActual.tipo)
            ? [...(e.turnosAnadidos || []), { id: ahora + 3, fecha, dia: diaDeLaSemana, turno: shiftLabel(turnoSolicitanteActual.tipo) }]
            : (e.turnosAnadidos || []);
          return { ...e, turnosQuitados: quitados, turnosAnadidos: anadidos };
        }
        return e;
      }));
    }

    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id), { status: 'aprobada' });
    } catch (e) { console.error('Error marcando el intercambio como aprobado:', e); }

    try {
      await enviarMensajeConLimite('mensajes', {
        senderId: auth.currentUser?.uid || 'sistema', sender: 'Sistema',
        text: `Cambio de turno aprobado: ${solicitanteEmp.name} y ${companeroEmp.name} han intercambiado el ${diaDeLaSemana.toLowerCase()} ${fechaLegible(req.fecha)}.`,
        time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        createdAt: Date.now(),
        expiraEl: fechaCaducidadMensaje(),
      });
    } catch (e) { console.error('Error avisando al chat:', e); }
  };

  const borrarIntercambio = async (req) => {
    if (!restaurantDocId || !req?.id) return;
    const ok = await confirmar({
      icono: '🗑️',
      titulo: `¿Borrar este intercambio de turno?`,
      lineas: [`${req.solicitanteName} ↔ ${req.companeroName} · ${req.dia}`],
      textoOk: 'Sí, borrarla',
      textoCancelar: 'Cancelar',
      peligro: true,
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id));
    } catch (e) { console.error('Error borrando el intercambio:', e); }
  };

  // ===== COBERTURA DE TURNOS ==========================================================
  // Cuando a alguien se le cae un turno, en vez de ponerse a escribir por WhatsApp uno por uno,
  // el encargado publica aquí "necesito a alguien el martes noche". A todo el mundo le aparece
  // con dos botones, Sí y No, y el encargado ve de un vistazo quién puede, quién no puede y
  // quién ni lo ha abierto todavía. Cuando elige a alguien, ese turno se escribe en el horario.

  // Solo se listan las que siguen buscando gente. Las ya cubiertas se guardan pero no estorban.
  const coberturasAbiertas = requests.filter(r => r.tipoSolicitud === 'cobertura' && r.status === 'abierta');
  // Las de día libre de toda la vida, para no mezclarlas con las nuevas en la misma lista.
  const solicitudesDiaLibre = requests.filter(r => r.tipoSolicitud !== 'cobertura' && r.tipoSolicitud !== 'intercambio');
  // Intercambios entre compañeros — su propia lista, con las tres fases bien diferenciadas.
  const intercambiosTurno = requests.filter(r => r.tipoSolicitud === 'intercambio');

  const crearCobertura = async () => {
    if (!cobDia || !restaurantDocId) return;
    setCobBusy(true);
    const ok = await publicarCobertura(cobDia, cobTurno, cobNota);
    if (ok) { setCobDia(''); setCobNota(''); }
    setCobBusy(false);
  };

  // El empleado contesta. Se manda el mapa `respuestas` entero (con su respuesta añadida) en vez
  // de usar rutas con puntos, porque los nombres pueden llevar espacios o puntos y eso rompería
  // la ruta. Además así solo se toca ese campo, que es lo único que las reglas permiten cambiar.
  const responderCobertura = async (req, respuesta) => {
    if (!restaurantDocId || !currentUser) return;
    try {
      const respuestas = { ...(req.respuestas || {}), [currentUser]: { r: respuesta, ts: Date.now() } };
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id), { respuestas });
    } catch (e) {
      console.error('Error respondiendo a la cobertura:', e);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido guardar tu respuesta', lineas: ['Vuelve a intentarlo en un momento.'] });
    }
  };

  // Horas de entrada y salida que le tocarían a esa persona en ese turno. Se respetan primero las
  // suyas propias si las tiene configuradas, y si no, las del restaurante.
  const horasDeTurno = (emp, turno) => {
    // Sale del modelo de horas único (horasDeTipo): mismas horas que pone el generador.
    // Para partido se devuelve el tramo completo, de la entrada de almuerzo al cierre.
    if (turno === 'partido') {
      const h = horasDeTipo('partido', emp, config);
      return { horaEntrada: h.horaEntrada, horaSalida: h.horaSalidaNoche };
    }
    const h = horasDeTipo(['apertura', 'noche', 'enlace'].includes(turno) ? turno : 'comida', emp, config);
    return { horaEntrada: h.horaEntrada, horaSalida: h.horaSalida };
  };

  // Asigna el turno a esa persona. MUY IMPORTANTE: escribe ÚNICAMENTE en la casilla de ese
  // empleado y ese día. No recalcula el horario ni mueve a nadie más — un cuadrante ya publicado
  // no se reorganiza solo, que es justo lo que fastidiaría a la plantilla.
  const asignarCobertura = async (req, emp) => {
    if (!restaurantDocId || !emp) return;
    // (29 oct 2026) Si la petición es de una semana ANTERIOR a la del horario que hay ahora (se pidió
    // y luego se generó la semana siguiente), escribir en "el jueves" pondría el turno en otra semana.
    const semanaHorario = fechasDelHorario();
    if (req.fecha && req.fecha < semanaHorario.Lunes) {
      await avisar({
        icono: '📅',
        titulo: 'Ese día ya no está en el horario',
        lineas: [`La petición es del ${String(req.dia || '').toLowerCase()} ${fechaLegible(req.fecha)}, y el horario que tienes ahora empieza el ${fechaLegible(semanaHorario.Lunes)}.`, 'Puedes cancelarla desde aquí.'],
      });
      return;
    }
    const turnoActual = getShift(emp.id, req.dia);
    const avisos = [];
    if (['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(turnoActual.tipo)) {
      avisos.push(`${emp.name} ya tiene ${turnoEnTexto(turnoActual.tipo)} ese día.`);
    }
    if (req.fecha) {
      if (estaAusenteEse(emp, req.fecha)) avisos.push(`${emp.name} está de vacaciones ese día.`);
      if (estaDeBajaEse(emp, req.fecha)) avisos.push(`${emp.name} está de baja ese día.`);
    }
    const ok = await confirmar({
      icono: avisos.length ? '⚠️' : '✅',
      titulo: `¿Asignar a ${emp.name} el ${req.dia.toLowerCase()}?`,
      lineas: [
        ...avisos,
        `${turnoEnTexto(req.turno).charAt(0).toUpperCase()}${turnoEnTexto(req.turno).slice(1)}. Solo se cambia ese turno: el resto del horario se queda como está.`,
      ],
      textoOk: 'Asignar el turno',
    });
    if (!ok) return;

    const { horaEntrada, horaSalida } = horasDeTurno(emp, req.turno);
    setSchedule(prev => ({
      ...prev,
      [emp.id]: {
        ...(prev[emp.id] || {}),
        [req.dia]: { tipo: req.turno, tareas: prev[emp.id]?.[req.dia]?.tareas || [], horaEntrada, horaSalida, descanso: null },
      },
    }));
    setCambiosTrasPublicar(c => c + 1);
    // Si el horario ya estaba publicado, queda constancia del turno que se le añade (igual que al
    // editar a mano), para el informe del mes.
    if (schedulePublished && !['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(getShift(emp.id, req.dia)?.tipo)) {
      setEmployees(prevEmp => prevEmp.map(e => e.id === emp.id
        ? { ...e, turnosAnadidos: [...(e.turnosAnadidos || []), { id: Date.now(), fecha: req.fecha || fechasDelHorario()[req.dia], dia: req.dia, turno: shiftLabel(req.turno) }] }
        : e));
    }

    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id),
        { status: 'cubierta', asignadoA: emp.name, asignadoTs: Date.now() });
      // Aviso al chat de grupo para que todo el mundo sepa que ya está resuelto y nadie más
      // se quede esperando. Se manda como mensaje del Sistema, que no dispara notificación.
      await enviarMensajeConLimite('mensajes', {
        senderId: auth.currentUser?.uid || 'sistema', sender: 'Sistema',
        text: `El ${req.dia} ${fechaLegible(req.fecha)}, ${turnoEnTexto(req.turno)}, ya está cubierto por ${emp.name}. ¡Gracias a todos!`,
        time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        createdAt: Date.now(),
        expiraEl: fechaCaducidadMensaje(),
      });
    } catch (e) { console.error('Error cerrando la cobertura:', e); }
  };

  const cancelarCobertura = async (req) => {
    if (!restaurantDocId) return;
    const ok = await confirmar({
      icono: '🗑️',
      titulo: '¿Cancelar esta petición?',
      lineas: ['Dejará de aparecerle al equipo y nadie más podrá responder.'],
      textoOk: 'Sí, cancelar',
      textoCancelar: 'Dejarla abierta',
      peligro: true,
    });
    if (!ok) return;
    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'solicitudes', req.id), { status: 'cancelada' });
    } catch (e) { console.error('Error cancelando la cobertura:', e); }
  };
  // =====================================================================================
  const pendingRequests = requests.filter(r => r.status === 'pending');
  // Fechas reales del horario que se está viendo, para "Lun 13" en las cabeceras y para marcar HOY
  // (29 oct 2026: antes "la semana que viene" según hoy, que a mitad de semana no era la del horario).
  const weekDatesDisplay = fechasDelHorario();
  const dayNumber = (day) => { const s = weekDatesDisplay[day]; return s ? parseInt(s.split('-')[2], 10) : ''; };
  // FALLO REAL ARREGLADO (16 oct 2026): "HOY" se marcaba solo por el nombre del día. El horario que se
  // enseña es el de la semana que empieza el próximo lunes, así que un jueves 15 se marcaba como HOY el
  // jueves 22. Ahora solo se marca si la fecha de esa columna es la de hoy de verdad.
  const esHoyDeVerdad = (day) => weekDatesDisplay[day] === toDateStr(new Date());

  // Al abrir "Mi Horario" siendo empleado, centramos el scroll horizontal de "Tu semana"
  // en el día de hoy — si no, con pantallas estrechas el empleado tiene que arrastrar
  // manualmente para encontrar su propio día (molesto sobre todo a media/fin de semana).
  useEffect(() => {
    if (userRole !== 'empleado' || activeTab !== 'horario') return;
    const t = setTimeout(() => {
      hoyColumnaRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 80);
    return () => clearTimeout(t);
  }, [userRole, activeTab, schedulePublished]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !restaurantDocId) return;
    try {
      await enviarMensajeConLimite('mensajes', {
        senderId: auth.currentUser?.uid || 'desconocido',
        sender: currentUser, text: newMessage.trim(),
        time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        createdAt: Date.now(),
        expiraEl: fechaCaducidadMensaje(),
      });
      setNewMessage('');
    } catch (e) {
      console.error('Error enviando el mensaje:', e);
      if (e.message?.includes('demasiado rápido')) avisar({ icono: '⏳', titulo: 'Espera un momento', lineas: [e.message] });
    }
  };

  const sendPrivateMessage = async () => {
    // chatAbierto es el id del contacto ('jefe', id de ficha o "hist-…" para quien ya no está).
    const contactoDestino = misContactosPrivados.find(c => c.id === chatAbierto);
    const texto = newPrivateMessage.trim();
    if (!texto || !restaurantDocId || !contactoDestino || !miClavePrivados) return;
    if (!puedeRecibirPrivados(contactoDestino)) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    // (29 oct 2026) Se guarda una copia en MI buzón y otra en el SUYO, con el mismo id, en el
    // mismo paquete que el límite de mensajes: o se guarda todo o no se guarda nada.
    // senderEmpId/receiverEmpId se siguen guardando (id real de cada ficha) por compatibilidad.
    const fichaDestino = contactoDestino.clave === 'jefe' ? null : employees.find(e => clavePrivadosDeFicha(e) === contactoDestino.clave);
    const datos = {
      deClave: miClavePrivados,
      paraClave: contactoDestino.clave,
      senderId: uid,
      senderEmpId: miClavePrivados === 'jefe' ? null : (selectedEmployeeId ?? null),
      receiverEmpId: fichaDestino ? fichaDestino.id : null,
      sender: currentUser,
      receiver: contactoDestino.name,
      text: texto,
      time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      createdAt: Date.now(),
      expiraEl: fechaCaducidadMensaje(),
    };
    try {
      const batch = writeBatch(db);
      const refMio = doc(collection(db, FIRESTORE_COLLECTION, restaurantDocId, 'privados', miClavePrivados, 'mensajesPrivados'));
      const refSuyo = doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'privados', contactoDestino.clave, 'mensajesPrivados', refMio.id);
      batch.set(refMio, datos);
      batch.set(refSuyo, datos);
      batch.set(doc(db, FIRESTORE_COLLECTION, restaurantDocId, 'rateLimits', uid), { lastMessageAt: serverTimestamp() });
      await batch.commit();
      setNewPrivateMessage('');
    } catch (e) {
      console.error('Error enviando el mensaje privado:', e);
      avisar({ icono: '⚠️', titulo: 'No se ha podido enviar', lineas: [e?.code === 'permission-denied'
        ? 'Si acabas de escribir otro mensaje, espera un segundo. Si sigue sin enviarse, cierra la app y vuelve a abrirla.'
        : 'Revisa tu conexión e inténtalo otra vez.'] });
    }
  };

  const toggleSpecialDay = (day) =>
    setSpecialDays(prev => { if (prev[day]) { const n={...prev}; delete n[day]; return n; } return {...prev, [day]: 'Festivo'}; });

  // PERSONAS POR DÍA Y TURNO (29 sep 2026): guarda/quita el número propio de un día concreto en
  // config.personasPorDia[día] = {comida, noche}. Si los dos campos de un día quedan vacíos, se
  // borra el día entero del objeto (vuelve a usar el mínimo general de la temporada, sin dejar
  // un rastro vacío guardado). El generador (generateSchedule) ya sabe leer esto — ver
  // minComidaDia/minNocheDia ahí. Puesto un número aquí, ese día se trata como especial sin
  // tener que marcar también la estrella.
  const setPersonaPorDia = (day, campo, valorStr) => {
    const valor = valorStr === '' ? null : Math.max(0, Math.min(employees.length, parseInt(valorStr, 10) || 0));
    setConfig(c => {
      const actual = c.personasPorDia || {};
      const nuevoDia = { ...(actual[day] || {}), [campo]: valor };
      const nuevo = { ...actual };
      if (nuevoDia.comida == null && nuevoDia.noche == null && !nuevoDia.completo) delete nuevo[day]; else nuevo[day] = nuevoDia;
      return { ...c, personasPorDia: nuevo };
    });
  };
  const quitarOverrideDia = (day) => setConfig(c => {
    const nuevo = { ...(c.personasPorDia || {}) };
    delete nuevo[day];
    return { ...c, personasPorDia: nuevo };
  });

  // ---- BURBUJA DE DÍA ESPECIAL (10 oct 2026, pedida por Fran) ---------------------------------
  // Al tocar un día en "Días especiales" se abre esta ventana: cuánta gente quiere el Encargado
  // ese día, separada por TIPO de persona (solo almuerzo, solo cena, día completo = turno
  // partido). Lo guardado va al mismo sitio de siempre (config.personasPorDia + estrella), que el
  // generador ya sabe leer — no hay datos nuevos en el servidor. La ventana comprueba en vivo, con
  // el propio generador y la plantilla de esta semana, si se puede cumplir (evaluarDiaEspecial).
  const abrirDiaEspecial = (day) => {
    const ov = config.personasPorDia?.[day];
    const minC = temporadas[temporada]?.minComida ?? 3;
    const minN = temporadas[temporada]?.minNoche ?? 3;
    const partidoOk = config.permiteTurnoPartido !== false;
    const tope = Math.max(1, employees.length);
    const hayNumeros = ov && (ov.comida != null || ov.noche != null || ov.completo != null);
    const cp = partidoOk && hayNumeros ? Math.max(0, Math.round(ov.completo || 0)) : 0;
    const totC = hayNumeros && ov.comida != null ? Math.round(ov.comida) : Math.min(tope, minC + 1); // propuesta inicial: una persona más que un día normal
    const totN = hayNumeros && ov.noche != null ? Math.round(ov.noche) : Math.min(tope, minN + 1);
    setDiaEspecialModal({ day, sc: Math.max(0, totC - cp), sn: Math.max(0, totN - cp), cp, nuevo: !specialDays[day] });
  };
  const guardarDiaEspecial = () => {
    const m = diaEspecialModal;
    if (!m) return;
    const partidoOk = config.permiteTurnoPartido !== false;
    const cp = partidoOk ? m.cp : 0;
    const datos = { comida: m.sc + cp, noche: m.sn + cp };
    if (cp > 0) datos.completo = cp;
    setSpecialDays(prev => ({ ...prev, [m.day]: 'Festivo' }));
    setConfig(c => ({ ...c, personasPorDia: { ...(c.personasPorDia || {}), [m.day]: datos } }));
    setDiaEspecialModal(null);
  };
  const quitarDiaEspecial = () => {
    const m = diaEspecialModal;
    if (!m) return;
    setSpecialDays(prev => { const n = { ...prev }; delete n[m.day]; return n; });
    quitarOverrideDia(m.day);
    setDiaEspecialModal(null);
  };
  const renderDiaEspecialModal = () => {
    const m = diaEspecialModal;
    if (!m) return null;
    const partidoOk = config.permiteTurnoPartido !== false;
    const cp = partidoOk ? m.cp : 0;
    const totC = m.sc + cp;
    const totN = m.sn + cp;
    const distintas = m.sc + m.sn + cp;
    const maxP = Math.max(1, employees.length);

    // Comprobación con el generador (se recalcula solo si cambia algo relevante).
    const aprobadas = (requests || []).filter(r => r.status === 'approved');
    const clave = JSON.stringify([m.day, totC, totN, cp, config, specialDays, temporadas[temporada],
      employees.map(e => [e.id, e.name, e.turnosSemanales, e.vacaciones, e.bajas, e.diasFijosLibres, e.esEncargado, e.hacesEnlace, e.localFijo]),
      aprobadas.map(r => [r.senderId, r.sender, r.date]), hayVariosLocales ? Object.keys(schedulesPorLocal || {}) : 0]);
    if (evalDiaEspecialRef.current.key !== clave) {
      let val = null;
      try {
        val = evaluarDiaEspecial({
          day: m.day, comida: totC, noche: totN, completo: cp,
          getEmpleados: () => empleadosDisponiblesParaLocal(localActivoId).disponibles,
          specialDays, approvedRequests: aprobadas, config, temporadaConfig: temporadas[temporada],
          scheduleHistory, isCocina,
        });
      } catch (e) {
        console.error('No se pudo comprobar el día especial:', e);
      }
      evalDiaEspecialRef.current = { key: clave, val };
    }
    const ev = evalDiaEspecialRef.current.val;

    const paso = (campo, delta) => setDiaEspecialModal(prev => ({ ...prev, [campo]: Math.max(0, Math.min(maxP, (prev[campo] || 0) + delta)) }));
    const fila = (icono, titulo, ayuda, campo) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '12px', border: `1px solid ${C.border}`, background: C.white }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: C.primaryFaint, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Ico n={icono} s="18px" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: '800', color: C.text }}>{titulo}</p>
          <p style={{ margin: '1px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.35 }}>{ayuda}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button onClick={() => paso(campo, -1)} disabled={m[campo] <= 0}
            style={{ width: '30px', height: '30px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, fontSize: '18px', fontWeight: '800', cursor: m[campo] <= 0 ? 'default' : 'pointer', opacity: m[campo] <= 0 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>−</button>
          <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '17px', fontWeight: '800', color: C.text }}>{m[campo]}</span>
          <button onClick={() => paso(campo, 1)} disabled={m[campo] >= maxP}
            style={{ width: '30px', height: '30px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, fontSize: '18px', fontWeight: '800', cursor: m[campo] >= maxP ? 'default' : 'pointer', opacity: m[campo] >= maxP ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>+</button>
        </div>
      </div>
    );
    const aplicarAjuste = () => {
      if (!ev?.ajuste) return;
      const a = ev.ajuste;
      const c = partidoOk ? Math.min(a.completo || 0, a.comida, a.noche) : 0;
      setDiaEspecialModal(prev => ({ ...prev, cp: c, sc: Math.max(0, a.comida - c), sn: Math.max(0, a.noche - c) }));
    };
    const caja = (fondo, borde, color, icono, titulo, hijos) => (
      <div style={{ background: fondo, border: `1px solid ${borde}`, borderRadius: '12px', padding: '12px 14px', margin: '14px 0 0' }}>
        <p style={{ margin: 0, fontSize: '13px', fontWeight: '800', color, display: 'flex', alignItems: 'center', gap: '7px' }}><Ico n={icono} s="17px" /> {titulo}</p>
        {hijos}
      </div>
    );
    const nombresNoDisp = ev?.noDisponibles?.length > 0
      ? ev.noDisponibles.map(x => `${x.nombre} (${x.motivo})`).join(', ') : null;

    return (
      <div onClick={() => setDiaEspecialModal(null)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px', overflowY: 'auto' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: '18px', padding: '22px 20px', maxWidth: '440px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', margin: 'auto', maxHeight: '92vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#FEF3C7', color: '#B45309', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Ico n="star" s="19px" /></div>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: C.text }}>{m.day} {dayNumber(m.day)} · día especial</h3>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: '12px', color: C.textMuted, lineHeight: 1.45 }}>
            ¿Cuánta gente necesitas este día (concierto, partido, evento…)? Horidia lo tratará como prioridad al generar el horario.
          </p>

          <div style={{ display: 'grid', gap: '8px' }}>
            {fila('sun', 'Solo almuerzo', 'Vienen a mediodía y se van tras la comida', 'sc')}
            {fila('moon', 'Solo cena', 'Entran por la tarde-noche y cierran', 'sn')}
            {partidoOk
              ? fila('clock', 'Día completo', 'Almuerzo y cena el mismo día (turno partido)', 'cp')
              : (
                <p style={{ margin: 0, padding: '9px 12px', borderRadius: '10px', background: C.bg, fontSize: '11px', color: C.textMuted, lineHeight: 1.45 }}>
                  Tu local trabaja con turno seguido: cada persona hace un solo turno al día, así que aquí no hay opción de día completo.
                </p>
              )}
          </div>

          <p style={{ margin: '12px 2px 0', fontSize: '12px', color: C.text, fontWeight: '700' }}>
            Resultado: {totC} en almuerzo · {totN} en cena
            <span style={{ fontWeight: '500', color: C.textMuted }}> · {distintas} {distintas === 1 ? 'persona distinta' : 'personas distintas'}</span>
          </p>

          {!ev && caja(C.bg, C.border, C.textMuted, 'info', 'No he podido comprobarlo ahora', <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.textMuted }}>Puedes guardarlo igualmente; al generar el horario Horidia hará lo que pueda.</p>)}
          {ev && ev.cerrado && caja(C.warningBg, '#FCD34D', '#92400E', 'alert', 'Ese día tu local está cerrado',
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#92400E', lineHeight: 1.45 }}>Según tu configuración {m.day} no abre, así que no se generarán turnos. Cambia los días de cierre en Configuración si hace falta.</p>)}
          {ev && !ev.cerrado && ev.cumple && !ev.afectaOtros && caja(C.successBg, '#6EE7B7', '#065F46', 'checkCircle', 'Se puede cubrir con tu plantilla',
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#065F46', lineHeight: 1.45 }}>
              Probado con tu plantilla de esta semana: sale bien en {ev.exitos} de {ev.pruebas} pruebas, sin dejar otros días por debajo del mínimo.
            </p>)}
          {ev && !ev.cerrado && ev.cumple && ev.afectaOtros && caja(C.warningBg, '#FCD34D', '#92400E', 'alert', 'Se puede, pero otros días quedarán más justos',
            <div>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#92400E', lineHeight: 1.45 }}>
                Para llegar a esto, el resto de la semana tendrá menos gente de la ideal en algún turno (la plantilla no da para todo a la vez).
              </p>
              {nombresNoDisp && <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#92400E' }}>Ese día no están: {nombresNoDisp}.</p>}
              {ev.sugerencias.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
                  {ev.sugerencias.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              )}
            </div>)}
          {ev && !ev.cerrado && !ev.cumple && caja(C.warningBg, '#FCD34D', '#92400E', 'alert', 'Con tu plantilla de esta semana no llego a tanto',
            <div>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#92400E', lineHeight: 1.45 }}>
                {ev.personasEseDia} {ev.personasEseDia === 1 ? 'persona puede' : 'personas pueden'} trabajar ese día y sus turnos contratados no dan para cubrir lo que pides sin dejar otros días cojos.
              </p>
              {ev.ajuste && (
                <div style={{ margin: '10px 0 0', padding: '10px 12px', borderRadius: '10px', background: C.white, border: `1px solid #FCD34D` }}>
                  <p style={{ margin: 0, fontSize: '12px', color: C.text, lineHeight: 1.45 }}>
                    <b>Lo máximo que consigo:</b> {ev.ajuste.comida} en almuerzo y {ev.ajuste.noche} en cena{partidoOk && ev.ajuste.completo > 0 ? `, con ${ev.ajuste.completo} de día completo` : ''}.
                  </p>
                  <button onClick={aplicarAjuste}
                    style={{ ...btn('primary', 'sm'), marginTop: '8px', justifyContent: 'center', width: '100%' }}>
                    <Ico n="wand" /> Usar eso
                  </button>
                </div>
              )}
              {nombresNoDisp && <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#92400E' }}>Ese día no están: {nombresNoDisp}.</p>}
              {ev.sugerencias.length > 0 && (
                <>
                  <p style={{ margin: '10px 0 2px', fontSize: '11px', fontWeight: '800', color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Qué podrías hacer</p>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
                    {ev.sugerencias.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </>
              )}
              <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#92400E', lineHeight: 1.4 }}>Si lo guardas así, Horidia llegará hasta donde pueda y te avisará de lo que falte antes de publicar.</p>
            </div>)}

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={() => setDiaEspecialModal(null)} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Cancelar</button>
            <button onClick={guardarDiaEspecial} style={{ ...btn('primary'), flex: 1.4, justifyContent: 'center' }}>{m.nuevo ? 'Marcar día especial' : 'Guardar cambios'}</button>
          </div>
          {!m.nuevo && (
            <button onClick={quitarDiaEspecial}
              style={{ display: 'block', width: '100%', marginTop: '10px', background: 'none', border: 'none', color: C.danger, fontSize: '12px', fontWeight: '700', cursor: 'pointer', padding: '6px' }}>
              Quitar día especial
            </button>
          )}
        </div>
      </div>
    );
  };

  const getShift = (empId, day) => schedule[empId]?.[day] || { tipo: 'libre', tareas: [], horaEntrada: '', horaSalida: '' };

  const setDescanso = (empId, day, descanso) => {
    setSchedule(prev => ({
      ...prev,
      [empId]: {
        ...(prev[empId] || {}),
        [day]: { ...(prev[empId]?.[day] || {}), descanso }
      }
    }));
  };

  const buildHoraLabel = (s, conf) => {
    if (!s || s.tipo === 'libre') return '';
    if (s.tipo === 'apertura') return `${s.horaEntrada} - ${s.horaSalida || conf.comidaEnd}`;
    if (s.tipo === 'comida') return `${s.horaEntrada} - ${s.horaSalida || conf.comidaEnd}`;
    if (s.tipo === 'noche') return `${s.horaEntrada} - ${s.horaSalida || conf.nocheEnd}`;
    if (s.tipo === 'partido') { const H = horariosGenerales(conf); return `${s.horaEntrada}-${s.horaSalida || H.comidaFin} / ${s.horaEntradaNoche || H.nocheIni}-${s.horaSalidaNoche || H.nocheFin}`; }
    if (s.tipo === 'enlace') return `${s.horaEntrada} - ${s.horaSalida || conf.nocheEnd}`;
    return '';
  };

  const getHistoryFolders = () => {
    const folders = [];
    for (let i = 0; i < scheduleHistory.length; i += 4) folders.push(scheduleHistory.slice(i, i + 4));
    return folders;
  };

  const MESES_ES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

  const getFolderDate = (h) => {
    if (!h) return null;
    if (h.publishedAtTimestamp) return new Date(h.publishedAtTimestamp);
    const datePart = (h.publishedAt || '').split(',')[0]?.trim();
    const [d, m, y] = (datePart || '').split('/').map(Number);
    if (d && m && y) return new Date(y, m - 1, d);
    return null;
  };

  const getFolderMonthLabel = (folder) => {
    const d = getFolderDate(folder?.[0]);
    if (!d) return 'Historial';
    const mes = MESES_ES[d.getMonth()];
    return `${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${d.getFullYear()}`;
  };

  // --- Contador de turnos del mes ---
  // Antes esto recorría TODO el historial una vez por cada empleado (lento con muchos
  // empleados y mucho historial). Ahora se calcula UNA sola vez por render, para todos
  // los empleados a la vez, y luego solo se consulta el resultado ya hecho.
  // REVISADO (11 oct 2026, a raíz de la pregunta de Fran sobre el PDF): ahora se cuenta por la
  // FECHA REAL de cada día (no por el día en que se publicó el horario), solo los días que ya han
  // pasado (hoy incluido), una sola vez cada semana aunque se publicara dos veces, y cada turno
  // con su peso real (partido = 2, Enlace = los turnos que vale). Los cambios hechos tras publicar
  // (editar, intercambios, coberturas, días libres) ya están dentro, porque se copian al historial.
  const turnosTrabajadosPorEmpleadoEsteMes = useMemo(() => {
    const hoy = new Date();
    const hoyStr = toDateStr(hoy);
    const prefijoMes = hoyStr.slice(0, 7);
    const mapa = {};
    historialSinDuplicados(scheduleHistory).forEach(h => {
      const fechas = fechasDeEntradaHistorial(h);
      Object.entries(h.schedule || {}).forEach(([empId, semana]) => {
        DAYS.forEach(day => {
          const f = fechas[day];
          if (!f || f.slice(0, 7) !== prefijoMes || f > hoyStr) return;
          const w = pesoCasillaGuardada(semana?.[day], config);
          if (w) mapa[empId] = (mapa[empId] || 0) + w;
        });
      });
    });
    return mapa;
  }, [scheduleHistory, config]);
  const turnosTrabajadosEsteMes = (empId) => turnosTrabajadosPorEmpleadoEsteMes[empId] || 0;

  // ===== BOTE DE PROPINAS (28 oct 2026) ===================================================
  // El Encargado pone el bote y las fechas; se divide entre el total de turnos trabajados en esas
  // fechas y a cada uno le toca (bote ÷ turnos) × sus turnos. El precio por turno se redondea a la
  // baja al céntimo, como lo haría a mano ("1,34 € por turno"), y lo que sobra por el redondeo se
  // enseña aparte. Sala y Cocina pueden repartir juntas o cada una lo suyo. Al publicarlo se
  // guarda en el documento (los dos si es conjunto), se avisa en el chat y cada empleado ve su
  // parte en "Mis Turnos".
  const deptoActualProp = isCocina ? 'cocina' : 'sala';
  const deptoOtroProp = isCocina ? 'sala' : 'cocina';
  const coleccionOtraProp = isCocina ? 'restaurantes' : 'cocinas';
  const MAX_REPARTOS = 24;
  useEffect(() => {
    if (!propForm || !restaurantDocId || userRole !== 'jefe') return undefined;
    let cancelado = false;
    setPropOtro(undefined);
    getDoc(doc(db, coleccionOtraProp, restaurantDocId))
      .then(snap => {
        if (cancelado) return;
        const d = snap.exists() ? snap.data() : null;
        const emps = d?.employees || [];
        setPropOtro(emps.length ? { employees: emps, scheduleHistory: d.scheduleHistory || [], config: d.config || DEFAULT_CONFIG } : null);
      })
      .catch(() => { if (!cancelado) setPropOtro(null); });
    return () => { cancelado = true; };
  }, [propForm, restaurantDocId, userRole, coleccionOtraProp]);

  const calcularRepartoPropinas = () => {
    const desde = propDesde, hasta = propHasta;
    if (!desde || !hasta || desde > hasta) return null;
    const filas = [];
    const sumar = (emps, historial, conf, depto) => {
      const mapa = turnosEnRango(historial, conf, desde, hasta);
      (emps || []).forEach(e => {
        const n = mapa[String(e.id)] || 0;
        if (n > 0) filas.push({ empId: e.id, nombre: e.name, depto, turnos: n });
      });
    };
    sumar(employees, scheduleHistory, config, deptoActualProp);
    const juntos = propJuntos && !!propOtro;
    if (juntos) sumar(propOtro.employees, propOtro.scheduleHistory, propOtro.config, deptoOtroProp);
    const totalTurnos = filas.reduce((acc, f) => acc + f.turnos, 0);
    const boteNum = parseFloat(String(propBote).replace(/\s/g, '').replace(',', '.'));
    const bote = Number.isFinite(boteNum) && boteNum > 0 ? Math.round(boteNum * 100) / 100 : 0;
    const porTurnoCent = totalTurnos > 0 ? Math.floor(Math.round(bote * 100) / totalTurnos) : 0;
    filas.forEach(f => { f.importe = (porTurnoCent * f.turnos) / 100; });
    filas.sort((x, y) => (y.turnos - x.turnos) || String(x.nombre).localeCompare(String(y.nombre)));
    return {
      desde, hasta, juntos, filas, totalTurnos, bote,
      porTurno: porTurnoCent / 100,
      sobra: (Math.round(bote * 100) - porTurnoCent * totalTurnos) / 100,
    };
  };

  const abrirFormularioPropinas = () => {
    const hoy = new Date();
    setPropDesde(toDateStr(new Date(hoy.getFullYear(), hoy.getMonth(), 1)));
    setPropHasta(toDateStr(hoy));
    setPropBote('');
    setPropJuntos(false);
    setPropForm(true);
  };

  const publicarRepartoPropinas = async () => {
    if (!restaurantDocId || propBusy) return;
    const r = calcularRepartoPropinas();
    if (!r || !r.totalTurnos || !(r.bote > 0) || !(r.porTurno > 0)) return;
    const ok = await confirmar({
      icono: '💰',
      titulo: '¿Publicar el reparto del bote?',
      lineas: [
        `${formatoEuros(r.bote)} entre ${r.totalTurnos} turnos: ${formatoEuros(r.porTurno)} por turno.`,
        r.juntos ? 'Sala y Cocina juntas.' : `Solo ${isCocina ? 'Cocina' : 'Sala'}.`,
        'Cada uno verá lo suyo en «Mis Turnos», se avisará en el chat y a cada uno le llegará su importe al móvil.',
      ],
      textoOk: 'Sí, publicar',
      textoCancelar: 'Ahora no',
    });
    if (!ok) return;
    const entrada = {
      id: Date.now(),
      desde: r.desde, hasta: r.hasta,
      bote: r.bote, porTurno: r.porTurno, totalTurnos: r.totalTurnos, sobra: r.sobra,
      juntos: r.juntos,
      reparto: r.filas.map(f => ({ empId: f.empId, nombre: f.nombre, depto: f.depto, turnos: f.turnos, importe: f.importe })),
      publicadoEn: Date.now(),
    };
    setPropBusy(true);
    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId), { propinas: [entrada, ...propinas].slice(0, MAX_REPARTOS) });
    } catch (e) {
      console.error('Error guardando el reparto de propinas:', e);
      setPropBusy(false);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido publicar', lineas: ['Comprueba tu conexión e inténtalo de nuevo.'] });
      return;
    }
    let otroFallo = false;
    if (r.juntos) {
      try {
        const refOtro = doc(db, coleccionOtraProp, restaurantDocId);
        const snap = await getDoc(refOtro);
        const previas = snap.exists() && Array.isArray(snap.data().propinas) ? snap.data().propinas : [];
        await updateDoc(refOtro, { propinas: [entrada, ...previas].slice(0, MAX_REPARTOS) });
      } catch (e) { console.error('Error guardando el reparto en el otro departamento:', e); otroFallo = true; }
    }
    // Aviso en el chat de grupo (y en el del otro departamento si el reparto es conjunto).
    const texto = `💰 Bote de propinas del ${fechaLegible(r.desde)} al ${fechaLegible(r.hasta)}: ${formatoEuros(r.bote)} entre ${r.totalTurnos} turnos → ${formatoEuros(r.porTurno)} por turno. Mira lo tuyo en «Mis Turnos».`;
    const mensaje = () => ({
      senderId: auth.currentUser?.uid || 'sistema', sender: 'Sistema', text: texto,
      time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      createdAt: Date.now(), expiraEl: fechaCaducidadMensaje(),
    });
    try { await enviarMensajeConLimite('mensajes', mensaje()); } catch (e) { console.error('Error avisando del bote en el chat:', e); }
    if (r.juntos && !otroFallo && auth.currentUser?.uid) {
      try {
        const lote = writeBatch(db);
        lote.set(doc(collection(db, coleccionOtraProp, restaurantDocId, 'mensajes')), mensaje());
        lote.set(doc(db, coleccionOtraProp, restaurantDocId, 'rateLimits', auth.currentUser.uid), { lastMessageAt: serverTimestamp() });
        await lote.commit();
      } catch (e) { console.error('Error avisando del bote en el chat del otro departamento:', e); }
    }
    setPropBusy(false);
    setPropForm(false);
    setPropAbierto(entrada.id);
    if (otroFallo) {
      await avisar({ icono: '⚠️', titulo: 'Publicado solo aquí', lineas: [`No se ha podido guardar en ${isCocina ? 'Sala' : 'Cocina'}. Sus empleados no lo verán hasta que lo publiques desde allí.`] });
    }
  };

  const borrarRepartoPropinas = async (entrada) => {
    if (!restaurantDocId || !entrada) return;
    const ok = await confirmar({
      icono: '🗑️',
      titulo: '¿Borrar este reparto?',
      lineas: ['Los empleados dejarán de verlo en «Mis Turnos». El mensaje del chat no se borra.'],
      textoOk: 'Sí, borrar', textoCancelar: 'Cancelar',
    });
    if (!ok) return;
    try {
      await updateDoc(doc(db, FIRESTORE_COLLECTION, restaurantDocId), { propinas: propinas.filter(p => p.id !== entrada.id) });
    } catch (e) {
      console.error('Error borrando el reparto:', e);
      await avisar({ icono: '⚠️', titulo: 'No se ha podido borrar', lineas: ['Comprueba tu conexión e inténtalo de nuevo.'] });
      return;
    }
    if (entrada.juntos) {
      try {
        const refOtro = doc(db, coleccionOtraProp, restaurantDocId);
        const snap = await getDoc(refOtro);
        if (snap.exists() && Array.isArray(snap.data().propinas)) await updateDoc(refOtro, { propinas: snap.data().propinas.filter(p => p.id !== entrada.id) });
      } catch (e) { console.error('Error borrando el reparto en el otro departamento:', e); }
    }
  };

  const etiquetaDepto = (depto) => (depto === 'cocina' ? 'Cocina' : 'Sala');

  // Tarjeta del Encargado (y del Dueño, solo para mirar) arriba de la pestaña Turnos.
  const renderPropinasEncargado = () => {
    const puedeEditar = userRole === 'jefe';
    const r = propForm ? calcularRepartoPropinas() : null;
    const valido = !!(r && r.totalTurnos > 0 && r.bote > 0 && r.porTurno > 0);
    const campo = { padding: '10px 12px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', background: C.white, width: '100%', boxSizing: 'border-box' };
    const etiqueta = { margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.textMuted };
    const opcion = (activo) => ({ flex: 1, padding: '10px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', border: `1px solid ${activo ? C.primary : C.primaryBorder}`, background: activo ? C.primary : C.white, color: activo ? C.white : C.primary });
    return (
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '800', color: C.text, margin: 0 }}>💰 Bote de propinas</h2>
          {puedeEditar && !propForm && (
            <button onClick={abrirFormularioPropinas} style={btn('primary', 'sm')}>Repartir un bote</button>
          )}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
          Se reparte según los turnos trabajados entre dos fechas. El turno partido cuenta como 2.
        </p>

        {puedeEditar && propForm && (
          <div style={{ marginTop: '14px', padding: '14px', borderRadius: '10px', background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 140px' }}>
                <p style={etiqueta}>Desde</p>
                <input type="date" value={propDesde} max={propHasta || undefined} onChange={e => setPropDesde(e.target.value)} style={campo} />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <p style={etiqueta}>Hasta</p>
                <input type="date" value={propHasta} min={propDesde || undefined} onChange={e => setPropHasta(e.target.value)} style={campo} />
              </div>
            </div>
            <div>
              <p style={etiqueta}>Dinero del bote (€)</p>
              <input type="text" inputMode="decimal" value={propBote} onChange={e => setPropBote(e.target.value.replace(/[^0-9.,]/g, '').slice(0, 9))} placeholder="Ej.: 300" style={campo} />
            </div>
            {propOtro === undefined && <p style={{ margin: 0, fontSize: '12px', color: C.textLight }}>Comprobando si hay {isCocina ? 'Sala' : 'Cocina'}…</p>}
            {propOtro && (
              <div>
                <p style={etiqueta}>¿Quién entra en el reparto?</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setPropJuntos(false)} style={opcion(!propJuntos)}>Solo {isCocina ? 'Cocina' : 'Sala'}</button>
                  <button onClick={() => setPropJuntos(true)} style={opcion(propJuntos)}>Sala y Cocina juntas</button>
                </div>
              </div>
            )}

            {r && r.totalTurnos === 0 && (
              <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>No hay turnos publicados entre esas fechas.</p>
            )}
            {r && r.totalTurnos > 0 && (
              <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '12px' }}>
                <p style={{ margin: '0 0 10px', fontSize: '14px', color: C.text }}>
                  <strong>{r.totalTurnos}</strong> turnos
                  {r.bote > 0 && <> · <strong style={{ color: C.primaryDark }}>{formatoEuros(r.porTurno)}</strong> por turno</>}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {r.filas.map(f => (
                    <div key={`${f.depto}-${f.empId}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <span style={{ color: C.text, fontWeight: '600' }}>
                        {f.nombre}
                        {r.juntos && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '700', color: C.textMuted }}>{etiquetaDepto(f.depto)}</span>}
                      </span>
                      <span style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {f.turnos} {f.turnos === 1 ? 'turno' : 'turnos'}{r.bote > 0 && <> · <strong style={{ color: C.text }}>{formatoEuros(f.importe)}</strong></>}
                      </span>
                    </div>
                  ))}
                </div>
                {r.bote > 0 && r.porTurno === 0 && (
                  <p style={{ margin: '10px 0 0', fontSize: '12px', color: C.danger }}>El bote es demasiado pequeño para tantos turnos (sale a menos de 1 céntimo por turno).</p>
                )}
                {r.bote > 0 && r.sobra > 0 && r.porTurno > 0 && (
                  <p style={{ margin: '10px 0 0', fontSize: '12px', color: C.textLight }}>Sobran {formatoEuros(r.sobra)} por el redondeo a céntimos.</p>
                )}
              </div>
            )}
            <p style={{ margin: 0, fontSize: '12px', color: C.textLight, lineHeight: 1.5 }}>Cuenta los turnos del horario publicado (con los cambios posteriores), hasta hoy.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setPropForm(false)} disabled={propBusy} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Cancelar</button>
              <button onClick={publicarRepartoPropinas} disabled={!valido || propBusy}
                style={{ ...btn('primary'), flex: 1.4, justifyContent: 'center', opacity: (!valido || propBusy) ? 0.55 : 1, cursor: (!valido || propBusy) ? 'default' : 'pointer' }}>
                {propBusy ? 'Publicando…' : 'Publicar reparto'}
              </button>
            </div>
          </div>
        )}

        {propinas.length > 0 && (
          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {propinas.slice(0, 6).map(p => {
              const abierto = propAbierto === p.id;
              return (
                <div key={p.id} style={{ padding: '12px', borderRadius: '10px', background: C.bg, border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => setPropAbierto(abierto ? null : p.id)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', flex: '1 1 200px' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.text }}>
                        Del {fechaLegible(p.desde)} al {fechaLegible(p.hasta)} {abierto ? '▾' : '▸'}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '13px', color: C.textMuted }}>
                        {formatoEuros(p.bote)} · {formatoEuros(p.porTurno)} por turno{p.juntos ? ' · Sala y Cocina' : ''}
                      </p>
                    </button>
                    {puedeEditar && (
                      <button onClick={() => borrarRepartoPropinas(p)} title="Borrar"
                        style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.textMuted, cursor: 'pointer', fontSize: '14px' }}><Ico n="trash" /></button>
                    )}
                  </div>
                  {abierto && (
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {(p.reparto || []).map(f => (
                        <div key={`${f.depto}-${f.empId}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '13px' }}>
                          <span style={{ color: C.text }}>{f.nombre}{p.juntos && <span style={{ marginLeft: '6px', fontSize: '11px', color: C.textMuted }}>{etiquetaDepto(f.depto)}</span>}</span>
                          <span style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>{f.turnos} × {formatoEuros(p.porTurno)} = <strong style={{ color: C.text }}>{formatoEuros(f.importe)}</strong></span>
                        </div>
                      ))}
                      {p.sobra > 0 && <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.textLight }}>Sobran {formatoEuros(p.sobra)} por el redondeo.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Tarjeta del empleado en "Mis Turnos": solo los repartos en los que entra, con SU parte.
  const renderPropinasEmpleado = (yo) => {
    if (!yo) return null;
    const mios = propinas
      .map(p => ({ p, f: (p.reparto || []).find(x => x.depto === deptoActualProp && String(x.empId) === String(yo.id)) }))
      .filter(x => x.f)
      .slice(0, 6);
    if (mios.length === 0) return null;
    return (
      <div style={{ ...card, flex: '1 1 100%', margin: 0, background: 'linear-gradient(135deg, rgba(16,185,129,0.10), #FFFFFF)', border: '1px solid rgba(16,185,129,0.3)' }}>
        <p style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: '800', color: C.text }}>💰 Bote de propinas</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {mios.map(({ p, f }) => (
            <div key={p.id} style={{ padding: '12px', borderRadius: '10px', background: C.white, border: `1px solid ${C.border}` }}>
              <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>Del {fechaLegible(p.desde)} al {fechaLegible(p.hasta)} · bote de {formatoEuros(p.bote)}</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: C.text }}>
                {f.turnos} {f.turnos === 1 ? 'turno' : 'turnos'} × {formatoEuros(p.porTurno)} = <strong style={{ fontSize: '20px', color: '#047857' }}>{formatoEuros(f.importe)}</strong>
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Detalle de los turnos que ha hecho una persona este mes, semana a semana. Es lo que necesita
  // el Dueño para cuadrar una nómina: no le vale con "12 turnos", quiere ver cuáles.
  const detalleTurnosEsteMes = (empId) => {
    const hoyStr = toDateStr(new Date());
    const prefijoMes = hoyStr.slice(0, 7);
    const semanas = [];
    historialSinDuplicados(scheduleHistory)
      .map(h => ({ h, fechas: fechasDeEntradaHistorial(h) }))
      .sort((a, b) => (a.fechas.Lunes || '').localeCompare(b.fechas.Lunes || ''))
      .forEach(({ h, fechas }) => {
        const semana = h.schedule?.[empId];
        if (!semana) return;
        const dias = DAYS
          .filter(day => fechas[day] && fechas[day].slice(0, 7) === prefijoMes && fechas[day] <= hoyStr)
          .filter(day => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(semana[day]?.tipo))
          .map(day => ({ day, tipo: semana[day].tipo, horaEntrada: semana[day].horaEntrada, horaSalida: semana[day].horaSalida }));
        if (dias.length && fechas.Lunes) {
          const [y, m, d] = fechas.Lunes.split('-').map(Number);
          semanas.push({ fecha: `semana del ${new Date(y, m - 1, d).toLocaleDateString('es-ES')}`, dias });
        }
      });
    return semanas;
  };

  // Cuántos turnos de cada clase, para el resumen de arriba. El partido cuenta como dos.
  const resumenTiposEsteMes = (empId) => {
    const r = { apertura: 0, comida: 0, noche: 0, partido: 0, enlace: 0 };
    detalleTurnosEsteMes(empId).forEach(sem => sem.dias.forEach(d => { r[d.tipo] = (r[d.tipo] || 0) + 1; }));
    return r;
  };
  // Turnos que se le han quitado a alguien este mes (ya publicados y luego pasados a Libre).
  const filtroDelMes = (lista) => {
    const hoy = new Date();
    return (lista || []).filter(tq => {
      if (!tq.fecha) return false;
      const [y, m] = tq.fecha.split('-').map(Number);
      return (m - 1) === hoy.getMonth() && y === hoy.getFullYear();
    }).sort((a, b) => a.fecha.localeCompare(b.fecha));
  };
  const turnosQuitadosEsteMes = (emp) => filtroDelMes(emp.turnosQuitados);
  // Simétrico a turnosQuitadosEsteMes: turnos que se le han añadido a mano a un horario ya
  // publicado (20 sep 2026, para distinguir en la pestaña Turnos "turno eliminado" de "turno
  // añadido" — antes solo existía el registro de eliminados).
  const turnosAnadidosEsteMes = (emp) => filtroDelMes(emp.turnosAnadidos);
  // PRÓXIMOS CAMBIOS (29 oct 2026, pedido por Fran): los turnos quitados o añadidos en días de un mes
  // que aún no ha empezado (p. ej. el lunes 2 de noviembre, cambiado el 29 de octubre) no salían en
  // ningún sitio hasta llegar ese mes, y parecía que el cambio no se había guardado. Aquí se listan
  // aparte, para verlos al momento. Se cuentan en su mes cuando llegue, igual que siempre.
  const cambiosProximos = (emp) => {
    const hoy = new Date();
    const finDeMes = toDateStr(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
    const marcar = (lista, clase) => (lista || []).filter(t => typeof t?.fecha === 'string' && t.fecha > finDeMes).map(t => ({ ...t, clase }));
    return [...marcar(emp?.turnosQuitados, 'quitado'), ...marcar(emp?.turnosAnadidos, 'anadido')]
      .sort((x, y) => x.fecha.localeCompare(y.fecha) || ((x.id || 0) - (y.id || 0)));
  };
  const nombreMesActual = () => {
    const m = MESES_ES[new Date().getMonth()];
    return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${new Date().getFullYear()}`;
  };

  // Informe de turnos en PDF (24 ago 2026). REVISADO (11 oct 2026, pedido por Fran): sin la
  // columna "Estado" (solo decía si alguien estaba de baja o de vacaciones HOY, no aportaba nada
  // al informe del mes) y con los turnos añadidos tras publicar. "Turnos trabajados" sale del
  // horario publicado ya con todos los cambios (editar, intercambios, coberturas, días libres),
  // contando los días del mes hasta hoy.
  const exportarInformeTurnosPDF = () => {
    const doc = new jsPDF();
    const nombreNegocio = config?.restaurantName || 'Horidia';
    const mes = nombreMesActual();
    const hoy = toDateStr(new Date());

    doc.setFontSize(16);
    doc.setTextColor(124, 58, 237);
    doc.text(nombreNegocio, 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(90, 90, 90);
    doc.text(`Informe de turnos — ${mes}`, 14, 26);

    const filas = employees.map(emp => [
      emp.name,
      String(turnosTrabajadosEsteMes(emp.id)),
      String(turnosAnadidosEsteMes(emp).length),
      String(turnosQuitadosEsteMes(emp).length),
    ]);

    autoTable(doc, {
      startY: 34,
      head: [['Empleado', 'Turnos trabajados', 'Turnos añadidos', 'Turnos eliminados']],
      body: filas,
      headStyles: { fillColor: [124, 58, 237] },
      styles: { fontSize: 10, cellPadding: 4 },
      alternateRowStyles: { fillColor: [245, 243, 255] },
    });
    const finTabla = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(8.5);
    doc.setTextColor(110, 110, 110);
    doc.text(doc.splitTextToSize(
      `Turnos trabajados: días de ${mes.toLowerCase()} hasta el ${hoy.split('-').reverse().join('/')}, según el horario publicado e incluyendo los cambios posteriores (turnos editados, intercambios, coberturas y días libres aprobados). El turno partido cuenta como 2 y el Enlace según sus horas. "Añadidos" y "eliminados" son los cambios hechos después de publicar.`,
      180), 14, finTabla + 8);

    doc.save(`turnos-${nombreNegocio.replace(/\s+/g, '_')}-${mes.replace(/\s+/g, '_')}.pdf`);
  };

  const tabStyle = (tab) => ({
    background: activeTab === tab ? `linear-gradient(135deg, ${D.blueBright}, ${D.blue})` : D.panel,
    color: activeTab === tab ? '#FFFFFF' : D.textMuted,
    border: activeTab === tab ? 'none' : `1px solid ${D.line}`,
    padding: '13px 20px', borderRadius: '12px', fontWeight: '700', fontSize: '14px',
    cursor: 'pointer', whiteSpace: 'nowrap',
    boxShadow: activeTab === tab ? D.glow : 'none',
  });

  // Estilo propio de las pestañas del EMPLEADO (distinto de tabStyle, que sigue usando el
  // Encargado): tarjetas en rejilla con icono arriba y etiqueta debajo, en vez de una fila de
  // píldoras que con 6 elementos se apretaba y envolvía de forma desordenada.
  const employeeTabStyle = (tab) => ({
    background: activeTab === tab ? C.primary : C.white,
    color: activeTab === tab ? C.white : C.textMuted,
    border: activeTab === tab ? 'none' : `1px solid ${C.primaryBorder}`,
    borderRadius: '14px', padding: '14px 6px', fontWeight: '700', fontSize: '12px',
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: '5px', textAlign: 'center', lineHeight: 1.15,
    boxShadow: activeTab === tab ? '0 4px 12px rgba(124,58,237,0.28)' : '0 1px 4px rgba(0,0,0,0.05)',
  });

  // Pestaña del Encargado en formato "tarjeta": icono arriba y etiqueta debajo, más grande que la
  // antigua píldora de una línea. Misma lógica visual que employeeTabStyle (la del empleado).
  const encargadoTabTile = (tab) => ({
    position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '5px', padding: '10px 4px', minHeight: '58px', minWidth: 0, borderRadius: '14px',
    fontWeight: '700', fontSize: '12px', lineHeight: 1.15, textAlign: 'center', cursor: 'pointer',
    background: activeTab === tab ? `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})` : C.white,
    color: activeTab === tab ? C.white : C.primaryDark,
    border: activeTab === tab ? 'none' : `1px solid ${C.primaryBorder}`,
    boxShadow: activeTab === tab ? '0 4px 12px rgba(124,58,237,0.28)' : '0 1px 4px rgba(0,0,0,0.05)',
  });

  const TimeSelect = ({ label, value, options, onChange }) => {
    // Si la hora guardada no está en la lista (p. ej. sale de una duración de turno de 3 h 30),
    // se añade en su sitio — así el desplegable siempre enseña la hora REAL, nunca otra.
    const orden = (h) => { const m = aMinutos(h); return m == null ? 0 : (m < 360 ? m + 1440 : m); };
    const lista = value && !options.includes(value) ? [...options, value].sort((a, b) => orden(a) - orden(b)) : options;
    return (
      <div>
        <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>{label}</p>
        <select value={value} onChange={e => onChange(e.target.value)}
          style={{ padding: '7px 10px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '13px', color: C.text, background: C.white, cursor: 'pointer', outline: 'none' }}>
          {lista.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  };

  const Header = () => (
    <>
      {dataError && (
        <div style={{ background: C.danger, color: C.white, padding: '8px 16px', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span><Ico n="alert" /> No se ha podido conectar con tus datos — puede que se muestre información antigua.</span>
          <button onClick={() => window.location.reload()} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.5)', color: C.white, borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>Reintentar</button>
        </div>
      )}
      <style>{`
        /* La tabla del horario mide 800px de ancho como mínimo. Sin esto, arrastraba la página
           entera hacia la derecha en el móvil y todo aparecía descentrado y cortado. Ahora el
           scroll lateral vive solo dentro de la tabla, que es donde tiene sentido. */
        html, body { max-width: 100%; overflow-x: clip; }
        @media (max-width: 640px) {
          .horia-tabs {
            flex-wrap: wrap !important;
            overflow-x: visible !important;
            gap: 8px !important;
          }
          /* 3 por fila en vez de 2 (1 oct 2026): con la pestaña nueva de Gastos ya son 9 en
             total — en 2 por fila eso son 5 filas, demasiado para caber en la pantalla al
             abrir la app sin hacer scroll. En 3 por fila son exactamente 3 filas completas
             (9 ÷ 3), sin ninguna suelta descolgada al final. */
          .horia-tabs button {
            flex: 1 1 calc(33.333% - 6px);
            justify-content: center;
            padding: 9px 4px !important;
            font-size: 11.5px !important;
            white-space: normal !important;
            min-width: 0 !important;
            line-height: 1.25 !important;
          }
          .horia-schedule-wrap {
            -webkit-overflow-scrolling: touch;
            max-width: 100%;
          }
          .horia-header-bar {
            flex-wrap: wrap !important;
            row-gap: 14px !important;
            padding: 9px 14px !important;
          }
          .horia-header-actions {
            width: 100%;
            justify-content: flex-end !important;
            flex-wrap: wrap !important;
          }
          /* Logo y título más compactos (1 oct 2026, a petición de Fran): la cabecera ocupaba
             demasiado alto en el móvil, robándole sitio a la rejilla de pestañas de debajo.
             Mismos elementos, solo más pequeños — nada deja de verse, solo pesa menos. */
          .horia-logo-box { width: 48px !important; height: 48px !important; }
          /* ARREGLO (2 oct 2026): los cuadraditos se cortaban por los lados porque su recorrido
             animado mide 52x30px y el contenedor era más pequeño (40x26 en móvil). Ahora el
             contenedor mide lo que recorre de verdad y, en móvil, se ENCOGE entero con scale. */
          .horia-logo-squares { transform: scale(0.72) !important; transform-origin: center; }
        }
        /* Rejilla de pestañas del Encargado: 3x3 en móvil (las 9 visibles al abrir), 9 en fila en
           pantallas anchas. */
        .horia-tabs-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 14px; }
        @media (min-width: 641px) { .horia-tabs-grid { grid-template-columns: repeat(9, minmax(0, 1fr)); gap: 10px; } }
        @keyframes horiaSquaresCycleHeader {
          0%    { transform: translate(0px,0px); }
          16.6% { transform: translate(19.5px,0px); }
          33.3% { transform: translate(39px,0px); }
          50%   { transform: translate(39px,19.5px); }
          66.6% { transform: translate(19.5px,19.5px); }
          83.3% { transform: translate(0px,19.5px); }
          100%  { transform: translate(0px,0px); }
        }
      `}</style>
      <div className="horia-header-bar" style={{ background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, padding: '10px 20px', boxShadow: '0 4px 16px rgba(124,58,237,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="horia-logo-box" style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.16)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)' }}>
            {/* ARREGLO (4 oct 2026): los cuadraditos se cortaban por la derecha. Cada uno mide
                13px + 2px de borde por cada lado = 17px de huella real (no 13), así que el
                recorrido completo ocupa 56x36.5 (el paso vertical ahora es 19.5, igual que el
                horizontal: antes era 17 y las dos filas quedaban pegadas) y no 52x30: sobresalía 2px del recuadro y el
                recuadro (overflow hidden) los recortaba. Ahora el contenedor mide lo que de
                verdad recorren y se encoge entero con scale para dejar un margen visible a los
                lados (escritorio .88, móvil .72 en la regla de arriba). flexShrink 0: en móvil el
                recuadro mide 48px y, sin esto, el contenedor se encogía hasta 48px y los
                cuadraditos quedaban pegados a un lado. */}
            <div className="horia-logo-squares" style={{ position: 'relative', width: '56px', height: '36.5px', flexShrink: 0, transform: 'scale(0.88)', transformOrigin: 'center' }}>
              {['#FFFFFF', '#FDBA74', '#E9D5FF', '#FFFFFF', '#FFFFFF', '#E9D5FF'].map((color, i) => (
                <div key={i} style={{
                  position: 'absolute', top: 0, left: 0, width: '13px', height: '13px',
                  borderRadius: '3px', border: `2px solid ${color}`, background: 'transparent',
                  animation: 'horiaSquaresCycleHeader 9s ease-in-out infinite',
                  animationDelay: `${-1.5 * i}s`,
                }} />
              ))}
            </div>
          </div>
          <div>
            <h1 style={{ margin: 0 }}><Wordmark size={24} dark /></h1>
            <p style={{ fontSize: '16px', fontWeight: '800', color: 'rgba(255,255,255,0.95)', margin: '2px 0 0', letterSpacing: '0.2px' }}>
              {/* El nombre del NEGOCIO se pregunta una vez en el alta y es fijo. El título de
                  aquí arriba, en cambio, tiene que reflejar qué local se está mirando ahora
                  mismo — si no, con varios locales, la cabecera decía siempre el nombre del
                  negocio entero aunque estuvieras viendo "Terraza" en vez de "Duo". Dueño no
                  entra aquí porque él ve el negocio entero, no un local concreto. */}
              {(hayVariosLocales && userRole !== 'dueno' && locales.find(l => l.id === (userRole === 'empleado' ? miLocalVistaId : localActivoId))?.nombre)
                || config.restaurantName || (userRole === 'jefe' ? L.panelJefe : L.panelEmpleado)}
            </p>
          </div>
        </div>
        <div className="horia-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* 16 oct 2026: solo la ve el encargado (los empleados veían las solicitudes de todo el equipo). */}
          {userRole === 'jefe' && pendingRequests.length > 0 && <span style={{ background: '#F59E0B', color: C.white, borderRadius: '20px', padding: '4px 10px', fontSize: '12px', fontWeight: '700' }}>{pendingRequests.length} solicitud{pendingRequests.length > 1 ? 'es' : ''}</span>}
          {notifPermiso === 'default' && (
            <button onClick={activarNotificaciones}
              title="En iPhone hace falta tocar este botón para activar los avisos — Apple no permite pedirlo solo."
              style={{ ...btn('ghost'), color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)' }}>
              <Ico n="bell" /> Activar notificaciones
            </button>
          )}
          {userRole === 'jefe' && (
            <button onClick={() => onSwitchDepartment(isCocina ? 'sala' : 'cocina')}
              style={{ ...btn('ghost'), color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)' }}>
              {isCocina ? conIconos('🍽️ Ir a Sala') : conIconos('🍳 Ir a Cocina')}
            </button>
          )}
          {/* (29 oct 2026) Quien ha entrado con su PIN puede cambiarlo por uno suyo. */}
          {esSesionPinPropia && (
            <button onClick={abrirMiPin} style={{ ...btn('ghost'), color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)' }}><Ico n="key" /> Mi PIN</button>
          )}
          <button onClick={handleLogout} style={{ ...btn('ghost'), color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)' }}>Salir</button>
        </div>
      </div>
    </>
  );

  const DESCANSO_OPCIONES_MIN = [10, 15, 20, 25, 30, 45, 60];
  const TIPOS_TURNO_EDITABLES = [
    { key: 'libre', label: 'Libre', emoji: '🛌' },
    { key: 'apertura', label: 'Apertura', emoji: '🌅' },
    { key: 'comida', label: 'Almuerzo', emoji: '🍽️' },
    { key: 'noche', label: 'Cena', emoji: '🌙' },
    { key: 'partido', label: 'Turno Partido', emoji: '🔀' },
    { key: 'enlace', label: 'Enlace', emoji: '🔗' },
  ];

  const EditarTurnoModal = () => {
    const [tipo, setTipo] = useState('libre');
    const [horaEntrada, setHoraEntradaSel] = useState('');
    const [horaSalida, setHoraSalidaSel] = useState('');
    const [conDescanso, setConDescanso] = useState(false);
    const [horaDescanso, setHoraDescanso] = useState('17:00');
    const [minutosDescanso, setMinutosDescanso] = useState(20);

    const emp = descansoModal ? employees.find(e => e.id === descansoModal.empId) : null;

    const defaultsFor = (tipoTurno, empleado) => {
      if (!empleado || !['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(tipoTurno)) return { horaEntrada: '', horaSalida: '' };
      // Mismas horas que pone el generador (modelo de horas único, horasDeTipo).
      const h = horasDeTipo(tipoTurno, empleado, config);
      return { horaEntrada: h.horaEntrada, horaSalida: h.horaSalida };
    };

    useEffect(() => {
      if (descansoModal && emp) {
        const s = getShift(descansoModal.empId, descansoModal.day);
        const tipoActual = s.tipo || 'libre';
        setTipo(tipoActual);
        const d = defaultsFor(tipoActual, emp);
        setHoraEntradaSel(s.horaEntrada || d.horaEntrada);
        setHoraSalidaSel(s.horaSalida || d.horaSalida);
        setConDescanso(!!s.descanso);
        setHoraDescanso(s.descanso?.hora || s.horaEntrada || d.horaEntrada || '17:00');
        setMinutosDescanso(s.descanso?.minutos || 20);
      }
    }, [descansoModal]);

    if (!descansoModal || !emp) return null;
    const { empId, day } = descansoModal;

    const handleTipoChange = (nuevoTipo) => {
      setTipo(nuevoTipo);
      const d = defaultsFor(nuevoTipo, emp);
      setHoraEntradaSel(d.horaEntrada);
      setHoraSalidaSel(d.horaSalida);
      if (nuevoTipo === 'libre') setConDescanso(false);
    };

    const handleSave = () => {
      const shiftAnterior = getShift(empId, day);
      const eraTrabajo = ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(shiftAnterior.tipo);
      const esTrabajoAhora = ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(tipo);
      const seLeQuitaUnTurnoYaPublicado = schedulePublished && eraTrabajo && tipo === 'libre';
      const seLeAnadeUnTurnoYaPublicado = schedulePublished && !eraTrabajo && esTrabajoAhora;
      setSchedule(prev => ({
        ...prev,
        [empId]: {
          ...(prev[empId] || {}),
          [day]: tipo === 'libre'
            ? { tipo: 'libre', tareas: [], horaEntrada: '', horaSalida: '' }
            : { tipo, tareas: prev[empId]?.[day]?.tareas || [], horaEntrada, horaSalida, descanso: conDescanso ? { hora: horaDescanso, minutos: minutosDescanso } : null,
                // El tramo de cena de un partido sale del modelo de horas (horasDeTipo).
                ...(tipo === 'partido' ? (() => { const h = horasDeTipo('partido', emp, config); return { horaEntradaNoche: h.horaEntradaNoche, horaSalidaNoche: h.horaSalidaNoche }; })() : {}) }
        }
      }));
      setCambiosTrasPublicar(c => c + 1);
      if (seLeQuitaUnTurnoYaPublicado) {
        const fecha = fechasDelHorario()[day];
        setEmployees(prevEmp => prevEmp.map(e => e.id === empId
          ? { ...e, turnosQuitados: [...(e.turnosQuitados || []), { id: Date.now(), fecha, dia: day, turno: shiftLabel(shiftAnterior.tipo) }] }
          : e));
      } else if (seLeAnadeUnTurnoYaPublicado) {
        const fecha = fechasDelHorario()[day];
        setEmployees(prevEmp => prevEmp.map(e => e.id === empId
          ? { ...e, turnosAnadidos: [...(e.turnosAnadidos || []), { id: Date.now(), fecha, dia: day, turno: shiftLabel(tipo) }] }
          : e));
      }
      setDescansoModal(null);
    };

    // 'enlace' entra sobre media tarde (usa el mismo rango de horas que ya existía para las
    // SALIDAS de comida, 15:30-18:00 — es justo el hueco en el que tiene sentido empezar un
    // enlace) y sale de noche, igual que 'noche'/'partido'.
    const entradaOpciones = tipo === 'noche' ? ENTRADAS_NOCHE : tipo === 'enlace' ? ENLACE_ENTRADAS : ENTRADAS_COMIDA;
    const salidaOpciones = tipo === 'enlace' ? ENLACE_SALIDAS : tipo === 'noche' ? SALIDAS_NOCHE : SALIDAS_COMIDA;

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}
        onClick={() => setDescansoModal(null)}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.primaryBorder}`, borderRadius: '16px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', margin: 'auto' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '800', color: C.text }}><Ico n="pencil" /> Editar turno</h3>
          <p style={{ margin: '0 0 20px', fontSize: '13px', color: C.textMuted }}>{emp?.name} · {day}</p>

          <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Tipo de turno</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '18px' }}>
            {TIPOS_TURNO_EDITABLES.map(t => (
              <button key={t.key} onClick={() => handleTipoChange(t.key)}
                style={{ padding: '8px 12px', borderRadius: '8px', border: tipo === t.key ? 'none' : `1px solid ${C.border}`, background: tipo === t.key ? C.primary : C.white, color: tipo === t.key ? C.white : C.textMuted, fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>
                {conIconos(t.emoji)} {t.label}
              </button>
            ))}
          </div>
          {(() => {
            // Aviso en directo (11 oct 2026): cómo queda la cuenta de turnos de esta persona con el
            // cambio, para que nadie acabe con más o menos turnos de los suyos sin darse cuenta.
            const semanaEmp = { ...(schedule[empId] || {}), [day]: { tipo } };
            const asignados = turnosAsignados(semanaEmp, emp, config);
            const o = turnosObjetivo(emp, config, (requests || []).filter(r => r.status === 'approved'), fechasDelHorario());
            const dif = asignados - o.objetivo;
            const color = dif === 0 ? '#047857' : '#B91C1C';
            const fondo = dif === 0 ? '#ECFDF5' : '#FEF2F2';
            const texto = dif === 0
              ? `${emp.name} queda con sus ${asignados} turnos.`
              : dif > 0
                ? `Con este cambio ${emp.name} tendría ${asignados} turnos y le tocan ${o.objetivo} (${dif} de más).`
                : `Con este cambio ${emp.name} tendría ${asignados} turnos y le tocan ${o.objetivo} (${-dif} de menos).`;
            return (
              <div style={{ margin: '-6px 0 16px', padding: '9px 12px', borderRadius: '10px', background: fondo, color, fontSize: '12.5px', fontWeight: 700, lineHeight: 1.45 }}>
                {texto}
              </div>
            );
          })()}

          {tipo !== 'libre' && (
            <>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>
                Horario{tipo === 'partido' ? ' (tramo de comida — la noche usa el horario del negocio)' : ''}
              </p>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
                <TimeSelect label="Entrada" value={horaEntrada} options={entradaOpciones} onChange={setHoraEntradaSel} />
                <TimeSelect label="Salida" value={horaSalida} options={salidaOpciones} onChange={setHoraSalidaSel} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: conDescanso ? '10px' : '18px' }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}><Ico n="coffee" /> Asignar descanso</p>
                <button onClick={() => setConDescanso(v => !v)}
                  style={{ width: '42px', height: '24px', borderRadius: '20px', border: 'none', background: conDescanso ? C.primary : C.border, cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: C.white, position: 'absolute', top: '3px', left: conDescanso ? '21px' : '3px', transition: 'left 0.15s ease' }} />
                </button>
              </div>

              {conDescanso && (
                <div style={{ marginBottom: '18px' }}>
                  <input type="time" value={horaDescanso} onChange={e => setHoraDescanso(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '15px', fontWeight: '600', color: C.text, outline: 'none', marginBottom: '10px', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {DESCANSO_OPCIONES_MIN.map(m => (
                      <button key={m} onClick={() => setMinutosDescanso(m)}
                        style={{ padding: '6px 11px', borderRadius: '8px', border: minutosDescanso === m ? 'none' : `1px solid ${C.border}`, background: minutosDescanso === m ? C.primary : C.white, color: minutosDescanso === m ? C.white : C.textMuted, fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>
                        {m} min
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <button onClick={() => setDescansoModal(null)} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Cancelar</button>
            <button onClick={handleSave} style={{ ...btn('primary'), flex: 1, justifyContent: 'center' }}>Guardar</button>
          </div>
        </div>
      </div>
    );
  };

  const MESES_CALENDARIO = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const DIAS_SEMANA_CORTOS = ['L','M','X','J','V','S','D'];

  const VacacionesModal = () => {
    const emp = employees.find(e => e.id === vacacionesEmpleado);
    const [mesVisible, setMesVisible] = useState(() => {
      const h = new Date(); h.setDate(1); return h;
    });
    const [rangoInicio, setRangoInicio] = useState(null);
    const [rangoFin, setRangoFin] = useState(null);

    if (!emp) return null;

    const primerDiaMes = new Date(mesVisible.getFullYear(), mesVisible.getMonth(), 1);
    const ultimoDiaMes = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + 1, 0);
    const offsetInicio = (primerDiaMes.getDay() + 6) % 7; // que la semana empiece en lunes
    const diasDelMes = [];
    for (let i = 0; i < offsetInicio; i++) diasDelMes.push(null);
    for (let d = 1; d <= ultimoDiaMes.getDate(); d++) diasDelMes.push(new Date(mesVisible.getFullYear(), mesVisible.getMonth(), d));

    const handleDiaClick = (fecha) => {
      const str = toDateStr(fecha);
      if (!rangoInicio || (rangoInicio && rangoFin)) {
        setRangoInicio(str); setRangoFin(null);
      } else if (str < rangoInicio) {
        setRangoInicio(str);
      } else {
        setRangoFin(str);
      }
    };

    const guardarPeriodo = () => {
      if (!rangoInicio) return;
      const nuevo = { id: Date.now(), start: rangoInicio, end: rangoFin || rangoInicio };
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, vacaciones: [...(e.vacaciones || []), nuevo] } : e));
      setRangoInicio(null); setRangoFin(null);
    };

    const borrarPeriodo = (id) => {
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, vacaciones: (e.vacaciones || []).filter(v => v.id !== id) } : e));
    };

    const periodos = [...(emp.vacaciones || [])].sort((a, b) => a.start.localeCompare(b.start));

    return (
      <div onClick={() => setVacacionesEmpleado(null)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: '16px', padding: '26px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', margin: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <FichaPersona p={emp} size={34} />
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: C.text }}><Ico n="sun" /> Vacaciones de {emp.name}</h3>
          </div>
          <p style={{ margin: '0 0 16px 46px', fontSize: '12px', color: C.textMuted }}>Toca el primer día y luego el último día del periodo.</p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <button onClick={() => setMesVisible(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', color: C.primary, fontWeight: '700' }}>‹</button>
            <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '14px' }}>{MESES_CALENDARIO[mesVisible.getMonth()]} {mesVisible.getFullYear()}</p>
            <button onClick={() => setMesVisible(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', color: C.primary, fontWeight: '700' }}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
            {DIAS_SEMANA_CORTOS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: '700', color: C.textLight, padding: '4px 0' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '16px' }}>
            {diasDelMes.map((fecha, i) => {
              if (!fecha) return <div key={i} />;
              const str = toDateStr(fecha);
              const esInicio = str === rangoInicio;
              const esFin = str === rangoFin;
              const enRango = rangoInicio && rangoFin && str >= rangoInicio && str <= rangoFin;
              const yaTieneVacaciones = periodos.some(v => str >= v.start && (!v.end || str <= v.end));
              let bg = C.white, color = C.text, border = `1px solid ${C.border}`;
              if (yaTieneVacaciones) { bg = '#CFFAFE'; color = '#0E7490'; border = `1px solid #67E8F9`; }
              if (enRango || esInicio || esFin) { bg = C.primary; color = C.white; border = 'none'; }
              return (
                <button key={i} onClick={() => handleDiaClick(fecha)}
                  style={{ aspectRatio: '1', borderRadius: '8px', border, background: bg, color, fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                  {fecha.getDate()}
                </button>
              );
            })}
          </div>

          {rangoInicio && (
            <div style={{ padding: '10px 12px', background: C.primaryFaint, borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: C.primaryDark }}>
              Desde <strong>{rangoInicio.split('-').reverse().join('/')}</strong>
              {rangoFin && <> hasta <strong>{rangoFin.split('-').reverse().join('/')}</strong></>}
              {!rangoFin && <> — toca ahora el último día del periodo</>}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
            <button onClick={() => { setRangoInicio(null); setRangoFin(null); }} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Borrar selección</button>
            <button onClick={guardarPeriodo} disabled={!rangoInicio} style={{ ...btn('primary'), flex: 1, justifyContent: 'center', opacity: rangoInicio ? 1 : 0.5 }}>Guardar periodo</button>
          </div>

          {periodos.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Periodos guardados</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {periodos.map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(34,211,238,0.12)', borderRadius: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#0E7490', fontWeight: '600' }}>
                      <Ico n="sun" /> {v.start.split('-').reverse().join('/')} → {v.end ? v.end.split('-').reverse().join('/') : 'indefinido'}
                    </span>
                    <button onClick={() => borrarPeriodo(v.id)} style={{ background: 'transparent', border: 'none', color: C.danger, cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>Quitar</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => setVacacionesEmpleado(null)} style={{ ...btn('outline'), width: '100%', justifyContent: 'center' }}>Cerrar</button>
        </div>
      </div>
    );
  };

  const BajaModal = () => {
    const emp = employees.find(e => e.id === bajaEmpleado);
    const [inicio, setInicio] = useState(toDateStr(new Date()));
    const [sinFechaFin, setSinFechaFin] = useState(true);
    const [fin, setFin] = useState(toDateStr(new Date()));

    if (!emp) return null;

    const hoyStr = toDateStr(new Date());
    const bajas = [...(emp.bajas || [])].sort((a, b) => b.start.localeCompare(a.start));
    const bajaActiva = estaDeBajaEse(emp, hoyStr);

    const marcarBaja = () => {
      if (!inicio) return;
      const nueva = { id: Date.now(), start: inicio, end: sinFechaFin ? null : (fin || inicio) };
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, bajas: [...(e.bajas || []), nueva] } : e));
      setInicio(toDateStr(new Date())); setSinFechaFin(true); setFin(toDateStr(new Date()));
    };

    const finalizarBajaHoy = (bajaId) => {
      setEmployees(prev => prev.map(e => e.id === emp.id
        ? { ...e, bajas: (e.bajas || []).map(b => b.id === bajaId ? { ...b, end: hoyStr } : b) }
        : e));
    };

    const quitarBaja = (bajaId) => {
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, bajas: (e.bajas || []).filter(b => b.id !== bajaId) } : e));
    };

    return (
      <div onClick={() => setBajaEmpleado(null)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: '16px', padding: '26px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', margin: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
            <FichaPersona p={emp} size={34} />
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: C.text }}><Ico n="baja" /> Baja de {emp.name}</h3>
          </div>

          {bajaActiva ? (
            <div style={{ padding: '14px', background: 'rgba(251,191,36,0.14)', border: '1px solid #FED7AA', borderRadius: '10px', marginBottom: '18px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: '#FBBF24' }}>
                Actualmente de baja
              </p>
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#FBBF24' }}>
                Desde {bajaActiva.start.split('-').reverse().join('/')} — {bajaActiva.end ? `hasta ${bajaActiva.end.split('-').reverse().join('/')}` : 'sin fecha de fin (abierta)'}
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!bajaActiva.end && (
                  <button onClick={() => finalizarBajaHoy(bajaActiva.id)} style={{ ...btn('success', 'sm'), flex: 1, justifyContent: 'center' }}>✓ Se reincorpora hoy</button>
                )}
                <button onClick={() => quitarBaja(bajaActiva.id)} style={{ ...btn('outline', 'sm'), flex: 1, justifyContent: 'center', color: C.danger, border: `1px solid ${C.dangerBg}` }}>Quitar baja</button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '18px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '13px', color: C.textMuted }}>Marca el inicio de la baja. Si no sabes cuándo se reincorporará, déjala sin fecha de fin — luego la cierras aquí mismo el día que vuelva.</p>
              <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Fecha de inicio</p>
              <input type="date" value={inicio} onChange={e => setInicio(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: C.text, outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: sinFechaFin ? '4px' : '10px' }}>
                <input type="checkbox" id="sinFechaFinBaja" checked={sinFechaFin} onChange={e => setSinFechaFin(e.target.checked)} />
                <label htmlFor="sinFechaFinBaja" style={{ fontSize: '13px', cursor: 'pointer' }}>Sin fecha de fin conocida (baja abierta)</label>
              </div>
              {!sinFechaFin && (
                <>
                  <p style={{ margin: '10px 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Fecha de fin prevista</p>
                  <input type="date" value={fin} onChange={e => setFin(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                </>
              )}
              <button onClick={marcarBaja} style={{ ...btn('primary'), width: '100%', justifyContent: 'center', marginTop: '14px' }}>Marcar de baja</button>
            </div>
          )}

          {bajas.filter(b => b.id !== bajaActiva?.id).length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Bajas anteriores</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {bajas.filter(b => b.id !== bajaActiva?.id).map(b => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: C.bg, borderRadius: '8px', fontSize: '13px' }}>
                    <span style={{ color: C.textMuted, fontWeight: '600' }}>
                      <Ico n="baja" /> {b.start.split('-').reverse().join('/')} → {b.end ? b.end.split('-').reverse().join('/') : 'indefinido'}
                    </span>
                    <button onClick={() => quitarBaja(b.id)} style={{ background: 'transparent', border: 'none', color: C.danger, cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>Quitar</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => setBajaEmpleado(null)} style={{ ...btn('outline'), width: '100%', justifyContent: 'center' }}>Cerrar</button>
        </div>
      </div>
    );
  };

  // El empleado, para EMPEZAR una conversación nueva, solo puede elegir a un Encargado o Gerente.
  // Tú (Encargado) en cambio ves y puedes escribir a cualquier empleado, sin esa restricción —
  // eres tú quien tiene que estar disponible para todo el equipo, no al revés.
  // (29 oct 2026) Cada contacto lleva su `clave` de buzón ('jefe' o 'e' + id de su ficha). Las
  // conversaciones se emparejan por esa clave, nunca por el nombre.
  const contactosPermitidos = (userRole === 'jefe'
    ? employees.filter(e => e.name !== currentUser && e.id !== selectedEmployeeId)
    : employees.filter(e => e.name !== currentUser && e.id !== selectedEmployeeId && (e.esEncargado || e.esGerente))
  ).map(e => ({ ...e, clave: clavePrivadosDeFicha(e) }));
  // Personas con las que ya hay conversación en mi buzón (aunque no estén en la lista de arriba).
  const otrosConHistorial = [];
  privateMessages.forEach(m => {
    const mio = m.deClave === miClavePrivados;
    const clave = mio ? m.paraClave : m.deClave;
    if (!clave || clave === miClavePrivados || otrosConHistorial.some(o => o.clave === clave)) return;
    otrosConHistorial.push({ clave, nombre: mio ? m.receiver : m.sender });
  });
  const misContactosPrivados = [
    ...contactosPermitidos,
    ...otrosConHistorial
      .filter(o => !contactosPermitidos.some(c => c.clave === o.clave))
      .map(o => {
        if (o.clave === 'jefe') return { id: 'jefe', name: L.jefeRoleName, clave: 'jefe' };
        const ficha = employees.find(e => clavePrivadosDeFicha(e) === o.clave);
        return ficha
          ? { ...ficha, clave: o.clave }
          : { id: `hist-${o.clave}`, name: o.nombre || 'Antiguo compañero', clave: o.clave, historico: true };
      }),
  ];

  // Ventana «Mi PIN» (29 oct 2026). Es una función que devuelve JSX, no un componente: si fuera
  // un componente definido aquí dentro, se volvería a montar con cada tecla y los campos
  // perderían el foco.
  const renderMiPin = () => {
    if (!showMiPin) return null;
    const campo = { width: '100%', padding: '11px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '16px', letterSpacing: '4px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' };
    const soloNumeros = (v) => v.replace(/\D/g, '').slice(0, 6);
    return (
      <div onClick={() => !miPinBusy && setShowMiPin(false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(17,12,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
        <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true"
          style={{ background: C.white, borderRadius: '20px', padding: '24px 22px 20px', maxWidth: '380px', width: '100%', boxShadow: '0 24px 64px rgba(17,12,40,0.35)', border: `1px solid ${C.primaryBorder}` }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '800', color: C.text }}><Ico n="key" /> Cambiar mi PIN</h3>
          <p style={{ margin: '0 0 14px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
            Pon un PIN de 6 números que solo sepas tú. Tu PIN es personal: no se lo des a nadie.
          </p>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.textMuted, marginBottom: '4px' }}>PIN actual</label>
          <input type="password" inputMode="numeric" autoComplete="current-password" maxLength={6} value={miPinActual}
            onChange={e => setMiPinActual(soloNumeros(e.target.value))} style={campo} />
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.textMuted, marginBottom: '4px' }}>PIN nuevo</label>
          <input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={miPinNuevo}
            onChange={e => setMiPinNuevo(soloNumeros(e.target.value))} style={campo} />
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.textMuted, marginBottom: '4px' }}>Repite el PIN nuevo</label>
          <input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={miPinRepite}
            onChange={e => setMiPinRepite(soloNumeros(e.target.value))} onKeyDown={e => e.key === 'Enter' && cambiarMiPin()} style={campo} />
          {miPinError && <p style={{ margin: '0 0 10px', fontSize: '13px', color: C.danger, lineHeight: 1.4 }}>{miPinError}</p>}
          <p style={{ margin: '0 0 14px', fontSize: '12px', color: C.textLight, lineHeight: 1.5 }}>
            Si lo olvidas, tu encargado puede generarte uno nuevo.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => setShowMiPin(false)} disabled={miPinBusy} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Cancelar</button>
            <button onClick={cambiarMiPin} disabled={miPinBusy} style={{ ...btn('primary'), flex: 1, justifyContent: 'center', opacity: miPinBusy ? 0.6 : 1 }}>
              {miPinBusy ? 'Guardando…' : 'Cambiar PIN'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Diálogo propio de Horidia. Mismo aspecto que el resto de ventanas de la app: fondo oscurecido,
  // tarjeta blanca con esquinas redondeadas y los botones de siempre.
  const DialogoHoria = () => {
    if (!dialogo) return null;
    const esPeligro = !!dialogo.peligro;
    // Icono en baldosa, con los iconos propios de Horidia (4 oct 2026). Rojo si es una acción
    // peligrosa, ámbar si es un aviso de problema, morado en el resto.
    const nombreIcono = iconoDeEmoji(dialogo.icono) || (dialogo.tipo === 'confirmar' ? 'help' : 'info');
    const tono = esPeligro ? { bg: C.dangerBg, fg: C.danger }
      : nombreIcono === 'alert' ? { bg: C.warningBg, fg: C.warning }
      : { bg: C.primaryPale, fg: C.primary };
    // sinCerrarFuera: para avisos que no se pueden volver a ver (como un PIN recién creado), un
    // toque sin querer fuera de la tarjeta no debe cerrarlo.
    const tocarFuera = () => { if (!dialogo.sinCerrarFuera) cerrarDialogo(false); };
    const copiarCodigo = async (e) => {
      const boton = e.currentTarget;
      try {
        await navigator.clipboard.writeText(String(dialogo.codigo));
        boton.textContent = '✓ Copiado';
      } catch (_) {
        boton.textContent = 'No se ha podido copiar';
      }
    };
    return (
      <div onClick={tocarFuera}
        style={{ position: 'fixed', inset: 0, background: 'rgba(17,12,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
        <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true"
          style={{ background: C.white, borderRadius: '20px', padding: '26px 24px 22px', maxWidth: '380px', width: '100%', boxShadow: '0 24px 64px rgba(17,12,40,0.35)', textAlign: 'center', border: `1px solid ${C.primaryBorder}` }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: tono.bg, color: tono.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Ico n={nombreIcono} s="28px" />
          </div>
          {dialogo.titulo && (
            <h3 style={{ margin: '0 0 10px', fontSize: '17px', fontWeight: '800', color: C.text, lineHeight: 1.35 }}>{dialogo.titulo}</h3>
          )}
          {dialogo.codigo != null && (
            <div style={{ margin: '6px 0 16px' }}>
              <div style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '8px', paddingLeft: '8px', color: C.primaryDark, background: C.primaryFaint, border: `1.5px dashed ${C.primaryBorder}`, borderRadius: '14px', padding: '14px 10px', fontVariantNumeric: 'tabular-nums' }}>
                {dialogo.codigo}
              </div>
              <button onClick={copiarCodigo}
                style={{ marginTop: '10px', padding: '8px 16px', borderRadius: '10px', border: `1px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                Copiar
              </button>
            </div>
          )}
          {(dialogo.lineas || []).map((linea, i) => (
            <p key={i} style={{ margin: '0 0 10px', fontSize: '14px', color: C.textMuted, lineHeight: 1.55 }}>{linea}</p>
          ))}
          {Array.isArray(dialogo.pasos) && dialogo.pasos.length > 0 && (
            <ol style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left' }}>
              {dialogo.pasos.map((paso, i) => {
                const esUltimo = i === dialogo.pasos.length - 1;
                return (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '12px', padding: '10px 12px' }}>
                    <span style={{ flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%', background: esUltimo ? C.success : C.primary, color: C.white, fontSize: '13px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: C.text, lineHeight: 1.35 }}>{paso}</span>
                  </li>
                );
              })}
            </ol>
          )}
          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            {dialogo.tipo === 'confirmar' && (
              <button onClick={() => cerrarDialogo(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: `1px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                {dialogo.textoCancelar}
              </button>
            )}
            <button onClick={() => cerrarDialogo(true)}
              style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: esPeligro ? C.danger : `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
              {dialogo.textoOk}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const OfertaHuellaModal = () => {
    if (!ofertaHuella) return null;
    return (
      <div onClick={() => setOfertaHuella(null)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
        <div onClick={e => e.stopPropagation()}
          style={{ background: C.white, borderRadius: '18px', padding: '30px', maxWidth: '380px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}><Ico n="unlock" /></div>
          <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: '800', color: C.text }}>¿Entrar con huella la próxima vez?</h3>
          <p style={{ margin: '0 0 22px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
            Así no tendrás que escribir el PIN cada vez que abras Horidia en este dispositivo. Tu huella nunca sale del móvil.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => {
              try { localStorage.setItem(`horia_huella_no_${department}_${ofertaHuella.id}`, '1'); } catch (_) {}
              setOfertaHuella(null);
            }} style={{ ...btn('outline'), flex: 1, justifyContent: 'center' }}>Ahora no</button>
            <button onClick={async () => {
              const ok = await registrarHuella(ofertaHuella.id, ofertaHuella.name);
              if (!ok) { try { localStorage.setItem(`horia_huella_no_${department}_${ofertaHuella.id}`, '1'); } catch (_) {} }
              setOfertaHuella(null);
            }} style={{ ...btn('primary'), flex: 1, justifyContent: 'center' }}>Sí, activar</button>
          </div>
        </div>
      </div>
    );
  };
  // Pantalla de lista: Grupo + cada contacto privado, como la lista de chats de
  // cualquier app de mensajería. Vive dentro de la pestaña normal (misma tarjeta
  // que el resto de secciones).
  const renderListaChats = () => (
    <div style={card}>
      <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 14px' }}>
        {userRole === 'jefe' ? 'Chat interno' : 'Mensajes'}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div onClick={() => setChatAbierto('grupal')}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${C.border}`, cursor: 'pointer' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: C.primaryPale, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', flexShrink: 0, fontSize: '15px' }}>
            G
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '14px' }}>Grupo</p>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: C.textMuted }}>Chat con todo el equipo</p>
          </div>
        </div>

        {/* (29 oct 2026) Sin PIN no hay mensajes privados: así nadie puede entrar con el nombre
            de otro y leer los suyos. Se puede meter el PIN aquí mismo. */}
        {!miClavePrivados ? (
          <div style={{ marginTop: '4px', padding: '14px', borderRadius: '12px', border: `1px dashed ${C.primaryBorder}`, background: C.primaryFaint }}>
            <p style={{ margin: '0 0 6px', fontWeight: '800', color: C.text, fontSize: '14px' }}><Ico n="lock" /> Mensajes privados</p>
            {miFichaSesion?.authEmail ? (
              <>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                  Para que nadie más pueda leerlos, los mensajes privados solo se pueden usar entrando con tu PIN. Escríbelo aquí:
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="password" inputMode="numeric" autoComplete="current-password" maxLength={6}
                    value={pinPrivados} onChange={e => setPinPrivados(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && entrarConPinParaPrivados()} placeholder="Tu PIN"
                    style={{ flex: 1, minWidth: 0, padding: '11px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '16px', letterSpacing: '4px', outline: 'none' }} />
                  <button onClick={entrarConPinParaPrivados} disabled={pinPrivadosBusy}
                    style={{ ...btn('primary'), flexShrink: 0, opacity: pinPrivadosBusy ? 0.6 : 1 }}>
                    {pinPrivadosBusy ? 'Entrando…' : 'Entrar'}
                  </button>
                </div>
                {pinPrivadosError && <p style={{ margin: '8px 0 0', fontSize: '12px', color: C.danger, lineHeight: 1.4 }}>{pinPrivadosError}</p>}
              </>
            ) : (
              <p style={{ margin: 0, fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                Para que nadie más pueda leerlos, los mensajes privados solo se pueden usar con PIN. Aún no tienes uno: pídeselo a tu encargado y entra con él.
              </p>
            )}
          </div>
        ) : misContactosPrivados.length === 0 ? (
          <p style={{ margin: '10px 4px 0', fontSize: '13px', color: C.textLight }}>
            {userRole === 'jefe' ? 'No hay ningún empleado de alta todavía.' : 'Tu encargado aún no ha añadido gerentes ni encargados.'}
          </p>
        ) : misContactosPrivados.map(c => (
          <div key={c.id} onClick={() => setChatAbierto(c.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${C.border}`, cursor: 'pointer' }}>
            <FichaPersona p={c} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '14px' }}>{c.name}</p>
              {(c.esEncargado || c.esGerente) && (
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: C.textMuted }}>{c.esGerente ? 'Encargado' : 'Gerente'}</p>
              )}
            </div>
            {!puedeRecibirPrivados(c) && (
              <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '999px', padding: '3px 9px' }}>
                {c.historico ? 'Ya no está' : 'Sin PIN'}
              </span>
            )}
          </div>
        ))}
        {miClavePrivados && privadosError && (
          <p style={{ margin: '6px 4px 0', fontSize: '12px', color: C.danger, lineHeight: 1.4 }}>{privadosError}</p>
        )}
      </div>
    </div>
  );

  // Un chat concreto (Grupo o una conversación privada), en pantalla completa:
  // cabecera fija arriba, mensajes en medio (scroll propio) e input fijo abajo.
  // Al pulsar "atrás" se vuelve a la lista, nunca a la pantalla de horario.
  const renderChatAbierto = () => {
    const esGrupal = chatAbierto === 'grupal';
    const contacto = !esGrupal ? misContactosPrivados.find(c => c.id === chatAbierto) : null;
    const titulo = esGrupal ? (userRole === 'jefe' ? 'Chat interno' : 'Mensajes de grupo') : (contacto?.name || 'Conversación');
    // FALLO REAL ARREGLADO (22 ago 2026): antes se comparaba por convKey de NOMBRES, así que un
    // empleado nuevo con el mismo nombre que uno despedido (ej. "Mario") heredaba en silencio
    // toda la conversación del Mario antiguo. Ahora se compara por el ID real de la ficha del
    // contacto cuando el mensaje ya lo lleva, y solo se cae al nombre para contactos históricos
    // (ya no son empleados actuales, no existe id al que comparar) o para mensajes muy antiguos
    // enviados antes de que existiera este campo.
    // (29 oct 2026) En mi buzón solo hay conversaciones mías: el otro lado (su clave) dice de
    // qué conversación es cada mensaje.
    const perteneceAConversacion = (m) => !!contacto && (m.deClave === contacto.clave || m.paraClave === contacto.clave);
    const fichaContacto = contacto && contacto.clave !== 'jefe' ? employees.find(e => clavePrivadosDeFicha(e) === contacto.clave) : null;
    const listaMensajes = esGrupal ? messages : privateMessages.filter(perteneceAConversacion);

    return (
      <div style={{ position: 'fixed', inset: 0, background: C.white, zIndex: 900, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <button onClick={() => setChatAbierto(null)} aria-label="Volver"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '20px', color: C.text, padding: '4px 6px 4px 0', lineHeight: 1 }}>
            ←
          </button>
          {!esGrupal && (
            <FichaPersona nombre={contacto?.name || '?'} size={32} />
          )}
          <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}>{titulo}</p>
        </div>

        <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', background: C.bg, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {listaMensajes.length === 0 ? (
            <EmptyState icon="" text={esGrupal ? 'Sin mensajes aún.' : 'Aún no hay mensajes con esta persona.'} />
          ) : listaMensajes.map(msg => {
            // (29 oct 2026) En privados, «mío» es lo que salió de mi buzón (mi clave), no mi nombre.
            const esMio = esGrupal ? msg.sender === currentUser : msg.deClave === miClavePrivados;
            return (
            <div key={msg.id} style={{ alignSelf: esMio ? 'flex-end' : msg.sender === 'Sistema' ? 'center' : 'flex-start', maxWidth: '78%' }}>
              {msg.sender === 'Sistema' ? (
                <div style={{ background: C.primaryPale, color: C.primary, padding: '8px 14px', borderRadius: '10px', fontSize: '13px', textAlign: 'center', border: `1px solid ${C.primaryBorder}` }}>
                  {msg.text}
                </div>
              ) : (
                <>
                  <div style={{ background: esMio ? C.primary : C.white, color: esMio ? C.white : C.text, padding: '10px 14px', borderRadius: esMio ? '16px 16px 4px 16px' : '16px 16px 16px 4px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    {esGrupal && msg.sender !== currentUser && (
                      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', opacity: 0.7 }}>{msg.sender}</p>
                    )}
                    <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.4 }}>{msg.text}</p>
                  </div>
                  <p style={{ margin: '3px 6px 0', fontSize: '11px', color: C.textLight, textAlign: esMio ? 'right' : 'left' }}>{msg.time}</p>
                </>
              )}
            </div>
            );
          })}
        </div>

        <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: `1px solid ${C.border}` }}>
          {esGrupal && !puedeEscribirEnGrupo ? (
            <div style={{ padding: '12px 14px', background: C.bg, borderRadius: '10px', border: `1px dashed ${C.border}` }}>
              <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>
                Solo el encargado puede escribir en este chat. Si necesitas algo, usa el chat privado.
              </p>
            </div>
          ) : (!esGrupal && !puedeRecibirPrivados(contacto)) ? (
            // (29 oct 2026) A quien no tiene PIN no se le pueden mandar privados. El hueco de la
            // derecha es para que la burbuja de Horito no tape el texto.
            <div style={{ padding: '12px 76px 12px 14px', background: C.bg, borderRadius: '10px', border: `1px dashed ${C.border}` }}>
              <p style={{ margin: 0, fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                {contacto?.historico
                  ? `${contacto.name} ya no está en la plantilla.`
                  : `${contacto?.name || 'Esta persona'} aún no tiene PIN. Los mensajes privados solo funcionan con PIN, para que nadie más pueda leerlos.${userRole === 'jefe' ? '' : ' Tiene que pedírselo al encargado.'}`}
              </p>
              {userRole === 'jefe' && fichaContacto && !contacto?.historico && (
                <button onClick={() => crearPinEmpleado(fichaContacto)}
                  style={{ ...btn('primary', 'sm'), marginTop: '10px' }}>
                  <Ico n="key" /> Crear PIN para {fichaContacto.name}
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <input type="text" maxLength={2000}
                value={esGrupal ? newMessage : newPrivateMessage}
                onChange={e => esGrupal ? setNewMessage(e.target.value) : setNewPrivateMessage(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (esGrupal ? sendMessage() : sendPrivateMessage())}
                placeholder={esGrupal ? 'Escribe un mensaje...' : `Escribe a ${contacto?.name || ''}...`}
                style={{ flex: 1, padding: '11px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
              <button onClick={esGrupal ? sendMessage : sendPrivateMessage} style={btn('primary')}>Enviar</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderChatPanel = () => chatAbierto ? renderChatAbierto() : renderListaChats();

  // Pantalla completa "Menú y Mapa" — mismo patrón que el chat (renderChatAbierto): cabecera
  // fija con botón atrás, dos pestañas dentro. Por ahora solo "Menú" tiene contenido real
  // (subir/ver la foto de la carta); "Mapa" queda con un aviso de "próximamente" hasta que se
  // decida cómo debe funcionar.
  // Pantalla completa "Configuración" — mismo patrón que Carta y Mesas y el chat: antes se
  // abría expandida dentro del propio dashboard y había que bajar mucho para verla entera y
  // volver a subir para todo lo demás. Ahora ocupa toda la pantalla, con su propio botón atrás.
  const renderConfiguracion = () => (
    <div style={{ position: 'fixed', inset: 0, background: C.white, zIndex: 900, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button onClick={() => setShowConfig(false)} aria-label="Volver"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '20px', color: C.text, padding: '4px 6px 4px 0', lineHeight: 1 }}>
          ←
        </button>
        <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="gear" /> Configuración {isCocina ? 'de cocina' : 'del restaurante'}</p>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px', background: C.bg }}>
        <div style={{ ...card, border: `1px solid ${C.primaryBorder}`, background: C.primaryFaint }}>
            {/* Nombre del negocio (29 sep 2026, movido aquí a petición de Fran — antes se
                editaba directamente en la tarjeta de arriba del dashboard, y prefiere que el
                cambio sea un paso explícito, con su propio botón de guardar, no algo que se
                pueda tocar sin querer). Reutiliza guardarNombreNegocio, ya construido. */}
            <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="home" /> Nombre del negocio</p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="text" maxLength={60} value={config.restaurantName || ''} onChange={e => setConfig(c => ({...c, restaurantName: e.target.value}))} placeholder="Nombre del negocio"
                  style={{ flex: 1, minWidth: 0, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                <button onClick={guardarNombreNegocio} disabled={guardandoNombreNegocio}
                  style={{ ...btn(nombreNegocioGuardadoOk ? 'primary' : 'outline', 'sm'), flexShrink: 0, opacity: guardandoNombreNegocio ? 0.6 : 1 }}>
                  {guardandoNombreNegocio ? 'Guardando…' : nombreNegocioGuardadoOk ? '✓ Guardado' : 'Guardar'}
                </button>
              </div>
            </div>
            {!isCocina && config.accessCode && (
              <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="key" /> Código de acceso para tus empleados</p>
                  <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>Dáselo a tu equipo para que entren en Horidia eligiendo "Soy Empleado" y lo escriban.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '22px', fontWeight: '900', letterSpacing: '4px', color: C.primary, background: C.primaryPale, padding: '8px 18px', borderRadius: '10px' }}>{config.accessCode}</span>
                  <button onClick={cambiarCodigoAcceso} disabled={cambiandoCodigo}
                    style={{ ...btn('outline', 'sm'), opacity: cambiandoCodigo ? 0.6 : 1, cursor: cambiandoCodigo ? 'default' : 'pointer' }}>
                    {cambiandoCodigo ? 'Cambiando…' : conIconos('🔄 Cambiar código')}
                  </button>
                </div>
              </div>
            )}
            {!isCocina && config.accessCode && (
              <p style={{ margin: '-8px 0 16px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
                Si alguien que ya no trabaja contigo se sabe el código, cámbialo: el anterior deja de
                funcionar al momento y esa persona pierde el acceso.
              </p>
            )}
            {!isCocina && (
              <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="home" /> Tus locales</p>
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
                  Si tu negocio tiene más de un local, cada uno tiene su propio horario. La plantilla,
                  el chat y las solicitudes son comunes: la misma persona puede trabajar una semana
                  en un local y la siguiente en otro.
                </p>

                <div style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                  {locales.map((l, i) => (
                    <div key={l.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="text" maxLength={40} value={l.nombre}
                        onChange={e => renombrarLocal(l.id, e.target.value)}
                        placeholder={`Local ${i + 1}`}
                        style={{ flex: 1, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                      {locales.length > 1 && (
                        <button onClick={() => borrarLocal(l.id)}
                          style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.danger, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                          Borrar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {locales.length < MAX_LOCALES_POR_RESTAURANTE ? (
                  <button onClick={anadirLocal} style={{ ...btn('outline', 'sm') }}>+ Añadir local</button>
                ) : (
                  <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>
                    Has llegado al máximo de {MAX_LOCALES_POR_RESTAURANTE} locales. Escríbenos si necesitas más.
                  </p>
                )}
              </div>
            )}

            <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="chat" /> Quién puede escribir en el chat de grupo</p>
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
                Si lo activas, tu equipo seguirá viendo los avisos del grupo pero no podrá escribir en él.
                Los chats privados no cambian: cualquiera puede seguir escribiéndote por ahí.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox"
                  checked={!!config.soloEncargadosEscribenChat}
                  onChange={e => setConfig(c => ({ ...c, soloEncargadosEscribenChat: e.target.checked }))}
                  style={{ width: '18px', height: '18px', accentColor: C.primary, cursor: 'pointer' }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>
                  Solo los encargados pueden escribir
                </span>
              </label>
            </div>
            {!isCocina && (
              <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="image" /> Logo de tu negocio</p>
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: C.textMuted }}>El logo de tu negocio aparecerá visible en la parte superior de la interfaz.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  {config.logoEmpresaUrl && (
                    <div style={{ padding: '8px 14px', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.bg }}>
                      <img src={config.logoEmpresaUrl} alt="Vista previa" style={{ height: '32px', maxWidth: '160px', objectFit: 'contain', display: 'block' }} />
                    </div>
                  )}
                  <label style={{ ...btn('outline', 'sm'), border: `1px solid ${C.primaryBorder}`, color: C.primary, cursor: 'pointer' }}>
                    {config.logoEmpresaUrl ? 'Cambiar logo' : 'Subir logo'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoEmpresaUpload} />
                  </label>
                  {config.logoEmpresaUrl && (
                    <button onClick={() => setConfig(c => ({ ...c, logoEmpresaUrl: null }))} style={btn('danger', 'sm')}>Quitar</button>
                  )}
                </div>
              </div>
            )}
            {/* HORARIO DE LOS TURNOS (10 oct 2026, rediseñado a petición de Fran). Se pregunta primero
                cuánto dura UN turno, y las horas de cada turno salen de ahí: el almuerzo dura eso desde
                que entra, la cena dura eso hasta el cierre, la apertura dura eso desde que abre. Con
                "Quiero que algún turno dure distinto" se puede poner a mano la hora de cada uno. El
                Enlace tiene su entrada y su salida, y cuenta como tantos turnos como horas tiene. */}
            {!isCocina && (() => {
              const Hg = horariosGenerales(config);
              const pers = !!config.horariosPersonalizados;
              const partidoOk = config.permiteTurnoPartido !== false;
              const cambiarDuracion = (delta) => setConfig(c => {
                const H0 = horariosGenerales(c);
                // Con turno seguido se sube de 15 en 15 min hasta 10 h (con partido, de 30 en 30, cada mitad hasta 10 h).
                const nd = Math.max(120, Math.min(600, H0.dur + delta));
                if (c.horariosPersonalizados) return { ...c, duracionTurnoMin: nd };
                return { ...c, duracionTurnoMin: nd, comidaEnd: aHora(aMinutos(H0.comidaIni) + nd), nocheStart: aHora(aMinutos(H0.nocheFin) - nd) };
              });
              const cambiarEntradaComida = (v) => setConfig(c => {
                const H0 = horariosGenerales(c);
                return c.horariosPersonalizados ? { ...c, comidaStart: v } : { ...c, duracionTurnoMin: H0.dur, comidaStart: v, comidaEnd: aHora(aMinutos(v) + H0.dur) };
              });
              const cambiarCierre = (v) => setConfig(c => {
                const H0 = horariosGenerales(c);
                const base = c.horariosPersonalizados ? { ...c, nocheEnd: v } : { ...c, duracionTurnoMin: H0.dur, nocheEnd: v, nocheStart: aHora(aMinutos(v) - H0.dur) };
                // Si el Enlace salía "al cierre", sigue saliendo al cierre nuevo.
                return (!c.enlaceHoraFin || c.enlaceHoraFin === c.nocheEnd) ? { ...base, enlaceHoraFin: null } : base;
              });
              const togglePersonalizado = () => setConfig(c => {
                const H0 = horariosGenerales(c);
                if (!c.horariosPersonalizados) return { ...c, horariosPersonalizados: true, duracionTurnoMin: H0.dur, aperturaFin: H0.aperturaFin };
                return { ...c, horariosPersonalizados: false, duracionTurnoMin: H0.dur, comidaEnd: aHora(aMinutos(H0.comidaIni) + H0.dur), nocheStart: aHora(aMinutos(H0.nocheFin) - H0.dur) };
              });
              const durEnlace = duracionEntre(Hg.enlaceIni, Hg.enlaceFin);
              const trabajoEnlace = minutosTrabajoEnlace(Hg.enlaceIni, Hg.enlaceFin);
              const pesoEnl = pesoEnlaceDeHoras(Hg.enlaceIni, Hg.enlaceFin, Hg.dur);
              const enlaceCuadra = Math.abs(trabajoEnlace - pesoEnl * Hg.dur) <= 15;
              const chip = (texto, color) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 9px', borderRadius: '999px', background: color + '1A', color, fontSize: '12px', fontWeight: '800', whiteSpace: 'nowrap' }}>{texto}</span>
              );
              const horaFija = (h, titulo) => (
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>{titulo}</p>
                  <div style={{ padding: '7px 10px', borderRadius: '8px', background: C.bg, border: `1px dashed ${C.border}`, fontSize: '13px', fontWeight: '800', color: C.text, minWidth: '58px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }} title="Sale sola de la duración del turno">{h}</div>
                </div>
              );
              const fila = (color, icono, titulo, hijos, pie) => (
                <div style={{ display: 'flex', gap: '12px', padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
                  <div style={{ width: '4px', borderRadius: '4px', background: color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '800', color: C.text, display: 'flex', alignItems: 'center', gap: '6px' }}><Ico n={icono} c={color} /> {titulo}</p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>{hijos}</div>
                    {pie}
                  </div>
                </div>
              );
              const flecha = <span style={{ color: C.textLight, paddingBottom: '8px' }}>→</span>;
              return (
                <div style={{ padding: '16px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 2px', fontSize: '14px', fontWeight: '800', color: C.text, display: 'flex', alignItems: 'center', gap: '7px' }}><Ico n="clock" c={C.primary} /> Horario de los turnos</p>
                  <p style={{ margin: '0 0 14px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>Son las horas de cada turno en el horario.</p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px', borderRadius: '12px', background: `linear-gradient(135deg, ${C.primaryFaint}, ${C.white})`, border: `1px solid ${C.primaryPale}`, marginBottom: '6px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 180px' }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '800', color: C.text }}>¿Cuántas horas dura un turno?</p>
                      <p style={{ margin: '3px 0 0', fontSize: '11.5px', color: C.textMuted, lineHeight: 1.45 }}>
                        {partidoOk ? `Un turno partido son 2 turnos (${formatoDuracion(Hg.dur * 2)}).` : 'Cada persona hace un turno al día de esta duración.'} Los contratos se cuentan en estos turnos.
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button onClick={() => cambiarDuracion(partidoOk ? -30 : -15)} aria-label="Menos" style={{ width: '34px', height: '34px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontSize: '18px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>−</button>
                      <span style={{ minWidth: '74px', textAlign: 'center', fontSize: '22px', fontWeight: '900', color: C.primaryDark, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{formatoDuracion(Hg.dur)}</span>
                      <button onClick={() => cambiarDuracion(partidoOk ? 30 : 15)} aria-label="Más" style={{ width: '34px', height: '34px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontSize: '18px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>+</button>
                    </div>
                  </div>

                  {fila(shiftColor('apertura').stripe, 'sunrise', 'Apertura', <>
                    <TimeSelect label="Entrada" value={config.aperturaHora} options={ENTRADAS_COMIDA} onChange={v => setConfig(c => ({ ...c, aperturaHora: v }))} />
                    {flecha}
                    {pers
                      ? <TimeSelect label="Salida" value={Hg.aperturaFin} options={OPCIONES_HORA} onChange={v => setConfig(c => ({ ...c, aperturaFin: v }))} />
                      : horaFija(Hg.aperturaFin, 'Salida')}
                    <div style={{ paddingBottom: '6px' }}>{chip(formatoDuracion(duracionEntre(Hg.aperturaIni, Hg.aperturaFin)), '#A16207')}</div>
                    <div style={{ marginLeft: 'auto' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Personas</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button onClick={() => setConfig(c => ({ ...c, personasApertura: Math.max(1, (c.personasApertura || 2) - 1) }))} style={{ width: '28px', height: '28px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontWeight: '700', color: C.primary, padding: 0 }}>−</button>
                        <span style={{ fontSize: '17px', fontWeight: '800', color: C.primary, minWidth: '20px', textAlign: 'center' }}>{config.personasApertura}</span>
                        <button onClick={() => setConfig(c => ({ ...c, personasApertura: (c.personasApertura || 2) + 1 }))} style={{ width: '28px', height: '28px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontWeight: '700', color: C.primary, padding: 0 }}>+</button>
                      </div>
                    </div>
                  </>)}

                  {fila(shiftColor('comida').stripe, 'utensils', 'Almuerzo', <>
                    <TimeSelect label="Entrada" value={Hg.comidaIni} options={ENTRADAS_COMIDA} onChange={cambiarEntradaComida} />
                    {flecha}
                    {pers
                      ? <TimeSelect label="Salida" value={Hg.comidaFin} options={OPCIONES_HORA} onChange={v => setConfig(c => ({ ...c, comidaEnd: v }))} />
                      : horaFija(Hg.comidaFin, 'Salida')}
                    <div style={{ paddingBottom: '6px' }}>{chip(formatoDuracion(duracionEntre(Hg.comidaIni, Hg.comidaFin)), shiftColor('comida').stripe)}</div>
                  </>)}

                  {fila(shiftColor('noche').stripe, 'moon', 'Cena', <>
                    {pers
                      ? <TimeSelect label="Entrada" value={Hg.nocheIni} options={OPCIONES_HORA} onChange={v => setConfig(c => ({ ...c, nocheStart: v }))} />
                      : horaFija(Hg.nocheIni, 'Entrada')}
                    {flecha}
                    <TimeSelect label="Cierre" value={Hg.nocheFin} options={SALIDAS_NOCHE} onChange={cambiarCierre} />
                    <div style={{ paddingBottom: '6px' }}>{chip(formatoDuracion(duracionEntre(Hg.nocheIni, Hg.nocheFin)), shiftColor('noche').stripe)}</div>
                  </>)}

                  {partidoOk && fila(shiftColor('partido').stripe, 'shuffle', 'Turno partido', <>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, fontVariantNumeric: 'tabular-nums' }}>{Hg.comidaIni} – {Hg.comidaFin} <span style={{ color: C.textLight }}>y</span> {Hg.nocheIni} – {Hg.nocheFin}</span>
                    {chip(`2 turnos · ${formatoDuracion(duracionEntre(Hg.comidaIni, Hg.comidaFin) + duracionEntre(Hg.nocheIni, Hg.nocheFin))}`, shiftColor('partido').stripe)}
                  </>)}

                  {fila('#16A34A', 'link', 'Enlace', <>
                    <TimeSelect label="Entrada" value={Hg.enlaceIni} options={ENLACE_ENTRADAS} onChange={v => setConfig(c => ({ ...c, enlaceHoraInicio: v }))} />
                    {flecha}
                    <TimeSelect label="Salida" value={Hg.enlaceFin} options={ENLACE_SALIDAS} onChange={v => setConfig(c => ({ ...c, enlaceHoraFin: v === (c.nocheEnd || '00:30') ? null : v }))} />
                    <div style={{ paddingBottom: '6px' }}>{chip(`${formatoDuracion(durEnlace)} · ${pesoEnl} ${pesoEnl === 1 ? 'turno' : 'turnos'}`, '#16A34A')}</div>
                  </>, (
                    <p style={{ margin: '8px 0 0', fontSize: '11.5px', color: C.textMuted, lineHeight: 1.5 }}>
                      Es la persona que hace de puente entre el almuerzo y la cena: entra a media tarde y se queda hasta el cierre.
                      {' '}Son {formatoDuracion(trabajoEnlace)} de trabajo{durEnlace > trabajoEnlace ? ` y ${formatoDuracion(durEnlace - trabajoEnlace)} de descanso` : ''}, así que cuenta como {pesoEnl} {pesoEnl === 1 ? 'turno' : 'turnos'} de {formatoDuracion(Hg.dur)}.
                      {!enlaceCuadra && <span style={{ display: 'block', marginTop: '4px', color: '#B45309', fontWeight: 700 }}>Ojo: no sale exacto ({formatoDuracion(trabajoEnlace)} frente a {formatoDuracion(pesoEnl * Hg.dur)}). Ajusta la entrada o la salida para que sean {formatoDuracion(pesoEnl * Hg.dur)} de trabajo.</span>}
                      {' '}Cada persona puede tener su propio horario de Enlace desde su ficha.
                    </p>
                  ))}

                  <div onClick={togglePersonalizado} role="switch" aria-checked={pers}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', marginTop: '6px', borderRadius: '12px', background: pers ? C.primaryFaint : C.bg, border: `1px solid ${pers ? C.primaryBorder : C.border}`, cursor: 'pointer', transition: 'background 0.2s ease, border-color 0.2s ease' }}>
                    <div style={{ width: '40px', height: '24px', borderRadius: '999px', background: pers ? C.primary : '#D1D5DB', position: 'relative', flexShrink: 0, transition: 'background 0.2s ease' }}>
                      <div style={{ position: 'absolute', top: '3px', left: pers ? '19px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: C.white, boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s ease' }} />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '800', color: C.text }}>Quiero que algún turno dure distinto</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11.5px', color: C.textMuted, lineHeight: 1.45 }}>{pers ? 'Pon a mano la hora de salida del almuerzo y la apertura, y la de entrada de la cena.' : 'Ahora todos los turnos duran lo mismo y sus horas salen solas.'}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ padding: '14px 16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="shuffle" /> ¿Tu restaurante tiene turno partido?</p>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.textMuted }}>Si lo desactivas, la IA solo generará turnos seguidos (Almuerzo o Cena), nunca Turno Partido.</p>
              <div style={{ display: 'flex', gap: '10px', maxWidth: '320px' }}>
                <button onClick={() => setConfig(c => ({...c, permiteTurnoPartido: true}))}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: config.permiteTurnoPartido !== false ? 'none' : `1px solid ${C.border}`, background: config.permiteTurnoPartido !== false ? C.primary : C.bg, color: config.permiteTurnoPartido !== false ? C.white : C.textMuted, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  ✓ Sí
                </button>
                <button onClick={() => setConfig(c => ({...c, permiteTurnoPartido: false}))}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: config.permiteTurnoPartido === false ? 'none' : `1px solid ${C.border}`, background: config.permiteTurnoPartido === false ? C.primary : C.bg, color: config.permiteTurnoPartido === false ? C.white : C.textMuted, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  No
                </button>
              </div>
            </div>

            {!isCocina && (
            <div style={{ padding: '14px 16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="door" /> ¿Cierra el negocio algún día fijo a la semana?</p>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.textMuted }}>Marca los días de cierre. La IA nunca generará turnos para nadie esos días.</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {DAYS.map(day => {
                  const activo = (config.diasCierreSemana || []).includes(day);
                  return (
                    <button key={day} onClick={() => setConfig(c => {
                      const dias = c.diasCierreSemana || [];
                      return { ...c, diasCierreSemana: dias.includes(day) ? dias.filter(d => d !== day) : [...dias, day] };
                    })}
                      style={{ padding: '8px 14px', borderRadius: '8px', border: activo ? 'none' : `1px solid ${C.border}`, background: activo ? C.danger : C.bg, color: activo ? C.white : C.textMuted, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                      {activo ? conIconos('🚪 ') : ''}{day.substring(0,3)}
                    </button>
                  );
                })}
              </div>
            </div>
            )}
            {!isCocina && (
            <div style={{ padding: '14px 16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="bed" /> Días de descanso a la semana</p>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.textMuted }}>Cuántos días libres, como mínimo, tiene garantizados cada persona a la semana.</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[1, 2, 3].map(n => (
                  <button key={n} onClick={() => setConfig(c => ({ ...c, diasDescansoSemana: n }))}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: (config.diasDescansoSemana || 2) === n ? 'none' : `1px solid ${C.border}`, background: (config.diasDescansoSemana || 2) === n ? C.primary : C.bg, color: (config.diasDescansoSemana || 2) === n ? C.white : C.textMuted, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                    {n} día{n > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${C.border}` }}>
                <div style={{ paddingRight: '12px' }}>
                  <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="calendar" /> Darlos seguidos</p>
                  <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>Si tiene 2 días libres, intenta ponerlos juntos (ej: lunes y martes) en vez de sueltos.</p>
                </div>
                <button onClick={() => setConfig(c => ({ ...c, diasDescansoConsecutivos: c.diasDescansoConsecutivos === false ? true : false }))}
                  style={{ width: '42px', height: '24px', borderRadius: '20px', border: 'none', background: config.diasDescansoConsecutivos !== false ? C.primary : C.border, cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: C.white, position: 'absolute', top: '3px', left: config.diasDescansoConsecutivos !== false ? '21px' : '3px', transition: 'left 0.15s ease' }} />
                </button>
              </div>
            </div>
            )}
            <div>
              <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}><Ico n="calendar" /> Mínimo de personas por turno</p>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.textMuted }}>De día y de noche puede necesitarse diferente personal.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                {Object.entries(temporadas).map(([key, t]) => (
                  <div key={key} style={{ padding: '14px', background: C.white, borderRadius: '10px', border: `2px solid ${temporada === key ? C.primary : C.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <input value={t.nombre} onChange={e => setTemporadas(prev => ({...prev, [key]: {...prev[key], nombre: e.target.value}}))}
                        style={{ border: 'none', outline: 'none', fontSize: '13px', fontWeight: '700', color: C.text, background: 'transparent', width: '130px' }} />
                      <button onClick={() => setTemporada(key)} style={{ ...btn(temporada === key ? 'primary' : 'outline', 'sm'), fontSize: '11px' }}>
                        {temporada === key ? '✓ Activa' : 'Activar'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: C.textMuted, minWidth: '52px' }}><Ico n="utensils" /> Almuerzo:</span>
                      <button onClick={() => setTemporadas(prev => ({...prev, [key]: {...prev[key], minComida: Math.max(1, (prev[key].minComida??3)-1)}}))}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: C.primary, fontSize: '13px' }}>−</button>
                      <span style={{ fontSize: '16px', fontWeight: '800', color: C.primary, minWidth: '22px', textAlign: 'center' }}>{t.minComida ?? 3}</span>
                      <button onClick={() => setTemporadas(prev => ({...prev, [key]: {...prev[key], minComida: (prev[key].minComida??3)+1}}))}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: C.primary, fontSize: '13px' }}>+</button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: C.textMuted, minWidth: '52px' }}><Ico n="moon" /> Cena:</span>
                      <button onClick={() => setTemporadas(prev => ({...prev, [key]: {...prev[key], minNoche: Math.max(1, (prev[key].minNoche??3)-1)}}))}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: C.primary, fontSize: '13px' }}>−</button>
                      <span style={{ fontSize: '16px', fontWeight: '800', color: C.primary, minWidth: '22px', textAlign: 'center' }}>{t.minNoche ?? 3}</span>
                      <button onClick={() => setTemporadas(prev => ({...prev, [key]: {...prev[key], minNoche: (prev[key].minNoche??3)+1}}))}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: C.primary, fontSize: '13px' }}>+</button>
                    </div>
                    {/* MÁXIMO OPCIONAL (12 oct 2026): sin número, Horidia reparte lo que sobra por igual entre los días. */}
                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px dashed ${C.border}` }}>
                      <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Máximo (opcional)</p>
                      {[['maxComida', 'minComida', 'utensils', 'Almuerzo'], ['maxNoche', 'minNoche', 'moon', 'Cena']].map(([kMax, kMin, ico, nombre]) => {
                        const minimo = t[kMin] ?? 3;
                        const valor = Number.isFinite(t[kMax]) ? t[kMax] : null;
                        const fijar = (v) => setTemporadas(prev => ({ ...prev, [key]: { ...prev[key], [kMax]: v } }));
                        const botonMax = { width: '24px', height: '24px', borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: C.primary, fontSize: '13px' };
                        return (
                          <div key={kMax} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', color: C.textMuted, minWidth: '82px' }}><Ico n={ico} /> {nombre}:</span>
                            <button aria-label="Menos" onClick={() => fijar(valor == null ? null : (valor <= minimo ? null : valor - 1))} style={botonMax}>−</button>
                            <span style={{ fontSize: valor == null ? '12px' : '16px', fontWeight: '800', color: valor == null ? C.textLight : C.primary, minWidth: '64px', textAlign: 'center' }}>{valor == null ? 'Sin máximo' : valor}</span>
                            <button aria-label="Más" onClick={() => fijar(valor == null ? minimo : valor + 1)} style={botonMax}>+</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
      </div>
      <DialogoHoria />
      {renderAsistenteHoria()}
    </div>
  );

  const renderMenuMapa = () => {
    const datos = datosLocalMenuMapa(localVistoMenuMapaId);
    const elementoSeleccionado = datos.elementos.find(el => el.id === mapaElementoSeleccionadoId) || null;
    const localVisto = locales.find(l => l.id === localVistoMenuMapaId);
    const cambiarLocalVisto = (id) => { setLocalVistoMenuMapaId(id); setMapaElementoSeleccionadoId(null); setMapaParedEnCurso(null); setMapaMoviendoId(null); setFotoCartaAmpliada(false); };

    return (
    <>
    <div style={{ position: 'fixed', inset: 0, background: C.white, zIndex: 900, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button onClick={() => setShowMenuMapa(false)} aria-label="Volver"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '20px', color: C.text, padding: '4px 6px 4px 0', lineHeight: 1 }}>
          ←
        </button>
        <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="utensils" /> Carta y Mesas{hayVariosLocales && localVisto ? ` · ${localVisto.nombre}` : ''}</p>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px', background: C.bg }}>
        {/* Selector de local: MISMAS pastillas que ya se usan en Horario para cambiar de local
            (no un panel nuevo aparte) — pedido expreso de Fran tras no gustarle el panel lateral
            tipo PowerPoint de la versión anterior: reutilizar lo que ya existe y funciona. */}
        {hayVariosLocales && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {locales.map(l => {
              const activo = l.id === localVistoMenuMapaId;
              return (
                <button key={l.id} onClick={() => cambiarLocalVisto(l.id)}
                  style={{
                    padding: '9px 16px', borderRadius: '10px',
                    border: `1.5px solid ${activo ? '#A78BFA' : C.border}`,
                    background: activo ? '#EDE9FE' : C.white,
                    color: activo ? '#5B21B6' : C.textMuted,
                    fontWeight: activo ? '800' : '600', fontSize: '13px', cursor: 'pointer' }}>
                  {activo ? conIconos('📍 ') : ''}{l.nombre}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <button onClick={() => setMenuMapaTab('menu')}
            style={{ flex: 1, padding: '10px', borderRadius: '10px 10px 0 0', border: 'none', borderBottom: menuMapaTab === 'menu' ? `2px solid ${C.primary}` : `2px solid ${C.border}`, background: 'transparent', color: menuMapaTab === 'menu' ? C.primaryDark : C.textMuted, fontWeight: '700', cursor: 'pointer' }}>
            Menú
          </button>
          <button onClick={() => setMenuMapaTab('mapa')}
            style={{ flex: 1, padding: '10px', borderRadius: '10px 10px 0 0', border: 'none', borderBottom: menuMapaTab === 'mapa' ? `2px solid ${C.primary}` : `2px solid ${C.border}`, background: 'transparent', color: menuMapaTab === 'mapa' ? C.primaryDark : C.textMuted, fontWeight: '700', cursor: 'pointer' }}>
            Mapa
          </button>
        </div>

        {menuMapaTab === 'menu' ? (
          <div style={{ ...card, padding: '10px' }}>
            {datos.menuFotoUrl ? (
              <div onClick={() => setFotoCartaAmpliada(true)} style={{ position: 'relative', cursor: 'zoom-in', marginBottom: '12px' }}>
                <img src={datos.menuFotoUrl} alt="Carta del restaurante"
                  style={{ width: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: '8px', background: C.white, display: 'block' }} />
                <span style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(31,41,55,0.75)', color: C.white, fontSize: '12px', fontWeight: '700', padding: '5px 10px', borderRadius: '999px' }}><Ico n="search" /> Toca para ampliar</span>
              </div>
            ) : (
              <p style={{ margin: '6px 6px 16px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                Sube una foto o un JPG de la carta{hayVariosLocales ? ' de este local' : ' del restaurante'}.
              </p>
            )}
            {menuFotoError && (
              <p style={{ margin: '0 6px 14px', fontSize: '13px', color: C.danger }}>{menuFotoError}</p>
            )}
            <input ref={menuFotoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              disabled={subiendoMenuFoto} onChange={handleMenuFotoUpload} />
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '0 6px 6px' }}>
              <button onClick={() => menuFotoInputRef.current?.click()} disabled={subiendoMenuFoto}
                style={{ ...btn(datos.menuFotoUrl ? 'outline' : 'primary'), cursor: subiendoMenuFoto ? 'default' : 'pointer', opacity: subiendoMenuFoto ? 0.6 : 1 }}>
                {subiendoMenuFoto ? 'Subiendo…' : (datos.menuFotoUrl ? conIconos('📷 Cambiar foto') : conIconos('📷 Subir foto de la carta'))}
              </button>
              {datos.menuFotoUrl && !datos.menuDeSala && !subiendoMenuFoto && (
                <button onClick={handleMenuFotoQuitar} style={btn('danger', 'md')}>Quitar</button>
              )}
            </div>
            {datos.menuDeSala && (
              <p style={{ margin: '4px 6px 6px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
                Es la carta que subió el Encargado de Sala. Si subes otra aquí, Cocina verá la suya.
              </p>
            )}
          </div>
        ) : (
          <div style={{ ...card, padding: '10px' }}>
            {/* Herramientas PRIMERO, todas visibles a la vez en una rejilla que envuelve —
                pedido expreso de Fran: nada de fila con scroll horizontal. Solo 5 ahora (se
                quitó "Silla": las mesas ya dibujan sus propias sillas alrededor). */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {MAPA_HERRAMIENTAS.map(h => (
                <button key={h.id} onClick={() => { setHerramientaMapa(h.id); setMapaElementoSeleccionadoId(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 11px',
                    borderRadius: '10px', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap',
                    border: `1.5px solid ${herramientaMapa === h.id ? h.border : C.border}`,
                    background: herramientaMapa === h.id ? h.bg : C.white,
                    color: herramientaMapa === h.id ? h.color : C.textMuted,
                  }}>
                  <span style={{ fontSize: '14px' }}>{conIconos(h.emoji)}</span> {h.label}
                </button>
              ))}
            </div>

            {herramientaMapa === 'pared' && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <button onClick={mapaDeshacerPuntoPared} disabled={!mapaParedEnCurso}
                  style={{ ...btn('outline', 'sm'), opacity: mapaParedEnCurso ? 1 : 0.5, cursor: mapaParedEnCurso ? 'pointer' : 'default' }}><Ico n="undo" /> Deshacer punto</button>
                <button onClick={mapaCerrarPared} disabled={!mapaParedEnCurso || mapaParedEnCurso.length < 3}
                  style={{ ...btn('primary', 'sm'), opacity: (mapaParedEnCurso && mapaParedEnCurso.length >= 3) ? 1 : 0.5, cursor: (mapaParedEnCurso && mapaParedEnCurso.length >= 3) ? 'pointer' : 'default' }}>✓ Cerrar forma</button>
                {mapaParedEnCurso && <button onClick={mapaCancelarPared} style={btn('ghost', 'sm')}>✕ Cancelar</button>}
              </div>
            )}

            {/* ZONAS (22 ago 2026): a qué zona se asigna lo próximo que se coloque en el plano. */}
            <div style={{ marginBottom: '10px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Zonas</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setZonaActivaMapaId(null)}
                  style={{ padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                    border: `1.5px solid ${!zonaActivaMapaId ? C.primary : C.border}`,
                    background: !zonaActivaMapaId ? C.primaryFaint : C.white,
                    color: !zonaActivaMapaId ? C.primaryDark : C.textMuted }}>
                  Sin zona
                </button>
                {(datos.zonas || []).map(z => (
                  <button key={z.id} onClick={() => setZonaActivaMapaId(z.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                      border: `1.5px solid ${zonaActivaMapaId === z.id ? C.primary : C.border}`,
                      background: zonaActivaMapaId === z.id ? C.primaryFaint : C.white,
                      color: zonaActivaMapaId === z.id ? C.primaryDark : C.textMuted }}>
                    {z.nombre}
                    <span onClick={(e) => { e.stopPropagation(); borrarZonaMapa(z.id); }} style={{ fontSize: '11px', color: C.textMuted, cursor: 'pointer' }}>✕</span>
                  </button>
                ))}
                <input value={nuevaZonaMapaNombre} onChange={e => setNuevaZonaMapaNombre(e.target.value)} maxLength={30}
                  placeholder="Nueva zona…" onKeyDown={e => { if (e.key === 'Enter') anadirZonaMapa(); }}
                  style={{ padding: '6px 10px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '12px', width: '110px' }} />
                <button onClick={anadirZonaMapa} style={btn('outline', 'sm')}>+ Añadir</button>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.textMuted }}>
                {zonaActivaMapaId ? 'Lo que coloques ahora se asigna a esa zona.' : 'Lo que coloques ahora no tendrá zona.'}
              </p>
            </div>

            {mapaMoviendoId && (
              <div style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '10px', marginBottom: '8px', padding: '8px 12px' }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#FBBF24' }}><Ico n="pin" /> Toca el plano donde quieras mover el elemento.</p>
              </div>
            )}

            {elementoSeleccionado && (
              <div style={{ background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px', padding: '10px 12px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: C.primaryDark }}>
                  {conIconos(MAPA_HERRAMIENTAS_POR_ID[elementoSeleccionado.tipo]?.emoji)} {MAPA_HERRAMIENTAS_POR_ID[elementoSeleccionado.tipo]?.label}
                </span>
                {elementoSeleccionado.tipo === 'mesa' && (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: C.text }}>
                      Nº
                      <input type="number" min="1" value={elementoSeleccionado.numero ?? ''} onChange={e => actualizarNumeroMesa(elementoSeleccionado.id, e.target.value)}
                        style={{ width: '52px', padding: '5px 6px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '12px' }} />
                    </label>
                    {/* 4 formas (1 oct 2026): cuadrada/redonda para mesas de 2-4, rectangular/ovalada
                        para mesas grandes de 6 o reservados tipo "booth". Un toque recorre la siguiente. */}
                    <button onClick={() => alternarFormaMesa(elementoSeleccionado.id)} style={btn('outline', 'sm')} title="Toca para cambiar de forma">
                      {conIconos({ cuadrada: '⬜ Cuadrada', redonda: '⚪ Redonda', rectangular: '▭ Rectangular (6)', ovalada: '⬭ Ovalada (6)' }[elementoSeleccionado.forma || 'cuadrada'])}
                    </button>
                  </>
                )}
                {elementoSeleccionado.tipo === 'etiqueta' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: C.text }}>
                    Nombre de la sala
                    <input type="text" maxLength={18} value={elementoSeleccionado.texto || ''} onChange={e => actualizarTextoEtiqueta(elementoSeleccionado.id, e.target.value)}
                      placeholder="Cocina, Baños, Terraza…" autoFocus
                      style={{ width: '150px', padding: '5px 8px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '12px' }} />
                  </label>
                )}
                {['mesa', 'barra', 'entrada'].includes(elementoSeleccionado.tipo) && (datos.zonas || []).length > 0 && (
                  <select value={elementoSeleccionado.zonaId || ''} onChange={e => asignarZonaElemento(elementoSeleccionado.id, e.target.value)}
                    style={{ padding: '5px 6px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '12px' }}>
                    <option value="">Sin zona</option>
                    {datos.zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                  </select>
                )}
                {(['barra', 'entrada'].includes(elementoSeleccionado.tipo) || (elementoSeleccionado.tipo === 'mesa' && ['rectangular', 'ovalada'].includes(elementoSeleccionado.forma))) && (
                  <button onClick={() => girarElementoMapa(elementoSeleccionado.id)} style={btn('outline', 'sm')}><Ico n="refresh" /> Girar</button>
                )}
                <button onClick={() => { setMapaMoviendoId(elementoSeleccionado.id); setMapaElementoSeleccionadoId(null); }} style={btn('outline', 'sm')}><Ico n="pin" /> Mover</button>
                <button onClick={() => eliminarElementoMapa(elementoSeleccionado.id)} style={{ ...btn('outline', 'sm'), color: C.danger, borderColor: C.dangerBg }}><Ico n="trash" /></button>
                <button onClick={() => setMapaElementoSeleccionadoId(null)} style={btn('ghost', 'sm')}>✕</button>
              </div>
            )}

            {/* El plano: rejilla de puntos de fondo (como Figma/Canva) para tener referencia de
                dónde colocar las cosas — y los toques se imantan a esa misma rejilla (MAPA_GRID),
                así que las paredes salen rectas y las mesas quedan alineadas sin pulso firme. */}
            <svg ref={mapaSvgRef} viewBox={`0 0 ${MAPA_VB_W} ${MAPA_VB_H}`} onClick={handleMapaClick}
              style={{ width: '100%', aspectRatio: `${MAPA_VB_W} / ${MAPA_VB_H}`, display: 'block', background: C.white, borderRadius: '8px', border: `1px solid ${C.border}`, cursor: herramientaMapa === 'pared' ? 'crosshair' : 'pointer', touchAction: 'manipulation' }}>
              <DefsPlano />
              <defs>
                <pattern id="mapaGridPattern" width={MAPA_GRID} height={MAPA_GRID} patternUnits="userSpaceOnUse">
                  <circle cx={MAPA_GRID / 2} cy={MAPA_GRID / 2} r="0.4" fill="#E5E7EB" />
                </pattern>
              </defs>
              <rect x="0" y="0" width={MAPA_VB_W} height={MAPA_VB_H} fill="url(#mapaGridPattern)" />
              {datos.paredes.map((pared, i) => paredPlano(pared, i))}
              {mapaParedEnCurso && (
                <>
                  <polyline points={mapaParedEnCurso.map(pt => `${pt.x},${pt.y}`).join(' ')} fill="none" stroke={C.accent} strokeWidth="1" strokeDasharray="2 1.5" />
                  {mapaParedEnCurso.map((pt, i) => <circle key={i} cx={pt.x} cy={pt.y} r="1.4" fill={C.accent} />)}
                </>
              )}
              {renderElementosMapa(datos.elementos, { interactivo: true, seleccionadoId: mapaElementoSeleccionadoId })}
            </svg>

            {(datos.elementos.length > 0 || datos.paredes.length > 0) && (
              <button onClick={limpiarMapaLocal} style={{ ...btn('outline', 'sm'), marginTop: '10px', border: `1px solid ${C.dangerBg}`, color: C.danger, background: C.white }}>
                <Ico n="eraser" /> Vaciar plano
              </button>
            )}
          </div>
        )}
      </div>
    </div>

    {/* El diálogo de confirmación propio de Horidia (el que usa "Vaciar plano") vive en las
        pantallas normales del Encargado/Empleado, y esta es una pantalla completa APARTE — sin
        montarlo aquí, el botón abría un diálogo invisible que nunca se podía confirmar ni
        cancelar. Se monta aquí también por lo mismo. */}
    <DialogoHoria />
    {renderAsistenteHoria()}

    {/* Carta a pantalla completa — pedido por Fran (21 ago 2026): tocar la foto la amplía del
        todo, con fondo oscuro y zoom real del navegador (pellizcar para ampliar sigue
        funcionando porque es una imagen normal, no un lienzo). */}
    {fotoCartaAmpliada && datos.menuFotoUrl && (
      <div onClick={() => setFotoCartaAmpliada(false)}
        style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', overflow: 'auto' }}>
        <button onClick={() => setFotoCartaAmpliada(false)} aria-label="Cerrar"
          style={{ position: 'fixed', top: '14px', right: '16px', border: 'none', background: 'rgba(255,255,255,0.15)', color: C.white, width: '40px', height: '40px', borderRadius: '50%', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          ✕
        </button>
        <img src={datos.menuFotoUrl} alt="Carta del restaurante ampliada" onClick={e => e.stopPropagation()}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '4px' }} />
      </div>
    )}
    </>
    );
  };

  const authGradBg = { minHeight: '100vh', background: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
  const authCard = { background: C.white, borderRadius: '20px', padding: '40px 36px', maxWidth: '400px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' };
  const authInput = { width: '100%', padding: '13px 16px', border: `1px solid ${C.primaryBorder}`, borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' };

  // --- Flujo de autenticación real con Firebase (Sala y Cocina) ---
  {
    // (22 sep 2026: aquí antes había una pantalla de "Cargando…" mientras authChecking era
    // true. Ya no existe ese estado bloqueante — se retiró a propósito, ver la declaración de
    // authChecking más arriba para el porqué.)

    if (showPlans) return <PlansScreen onBack={() => setShowPlans(false)} />;
    if (showPrivacy) return <PrivacyPolicyScreen onBack={() => setShowPrivacy(false)} />;
    if (showLegal) return <LegalNoticeScreen onBack={() => setShowLegal(false)} />;
    if (showConditions) return <ConditionsScreen onBack={() => setShowConditions(false)} />;

    if (!restaurantDocId) {
      if (emailPendiente) {
        return (
          <div key="acceso-verificar" className="hd-auth-bg" style={authGradBg}>
            <div className="hd-auth-card" style={authCard}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '64px', height: 'auto' }} />
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: C.text, margin: '10px 0 8px' }}>Confirma tu email</h2>
                <p style={{ fontSize: '14px', color: C.textMuted, margin: 0, lineHeight: 1.5 }}>
                  Te hemos enviado un correo a <b style={{ color: C.text, wordBreak: 'break-all' }}>{emailPendiente}</b>.
                  Abre el enlace que trae para activar tu cuenta y vuelve aquí.
                </p>
                <p style={{ fontSize: '12px', color: C.textMuted, margin: '10px 0 0' }}>Si no lo ves, mira en la carpeta de spam.</p>
              </div>
              {verifMsg && <p style={{ color: C.textMuted, fontSize: '13px', margin: '0 0 12px', textAlign: 'center', lineHeight: 1.4 }}>{verifMsg}</p>}
              <button onClick={() => comprobarEmailVerificado(false)} disabled={verifBusy}
                style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', opacity: verifBusy ? 0.55 : 1 }}>
                {verifBusy ? 'Un momento…' : 'Ya lo he confirmado'}
              </button>
              <button onClick={reenviarVerificacion} disabled={verifBusy || verifEspera}
                style={{ width: '100%', marginTop: '12px', background: 'transparent', border: 'none', color: C.primary, fontSize: '13px', fontWeight: '700', cursor: (verifBusy || verifEspera) ? 'not-allowed' : 'pointer', opacity: (verifBusy || verifEspera) ? 0.5 : 1 }}>
                {verifEspera ? 'Correo enviado' : 'Reenviar el correo'}
              </button>
              <button onClick={salirDeVerificacion}
                style={{ width: '100%', marginTop: '4px', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', cursor: 'pointer' }}>
                Me he equivocado de email / Volver
              </button>
            </div>
          </div>
        );
      }
      if (authScreen === 'duenoLogin' || authScreen === 'duenoRegistro') {
        const esRegistro = authScreen === 'duenoRegistro';
        return (
          <div key={`acceso-${authScreen}`} className="hd-auth-bg" style={authGradBg}>
            <div className="hd-auth-card" style={authCard}>
              <button onClick={() => { setAuthScreen('inicio'); setAuthError(''); }}
                style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Volver</button>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '64px', height: 'auto' }} />
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: C.text, margin: '10px 0 4px' }}>
                  {esRegistro ? 'Crear cuenta de Dueño' : 'Acceso Dueño'}
                </h2>
                <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
                  {esRegistro
                    ? 'Vista de solo consulta de tu negocio: plan, plantilla, turnos, historial y gastos. Necesitas el código de Dueño: le llega por correo al Encargado al crear la cuenta del negocio (no es el código de los empleados).'
                    : 'Accede con tu email y contraseña de Dueño.'}
                </p>
              </div>
              {/* ENTER PARA ENTRAR (29 sep 2026): pulsar Enter en cualquiera de estos campos
                  dispara el mismo botón de abajo, sin tener que tocarlo con el ratón/dedo. */}
              <input type="email" maxLength={254} placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !authBusy && handleDuenoAuth(esRegistro ? 'registro' : 'login')} style={authInput} />
              <input type="password" maxLength={128} placeholder={esRegistro ? `Contraseña (mínimo ${CONTRASENA_MIN} caracteres)` : 'Contraseña'} value={authPassword} onChange={e => setAuthPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !authBusy && handleDuenoAuth(esRegistro ? 'registro' : 'login')} style={authInput} />
              {esRegistro && (
                <input type="text" maxLength={12} placeholder="Código de Dueño (ej: ABCD-EFGH)" value={codigoDuenoInput}
                  autoCapitalize="characters" autoComplete="off" spellCheck={false}
                  onChange={e => setCodigoDuenoInput(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && !authBusy && handleDuenoAuth(esRegistro ? 'registro' : 'login')}
                  style={{ ...authInput, letterSpacing: '2px', fontWeight: '700', textTransform: 'uppercase' }} />
              )}
              {esRegistro && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '0 0 14px', fontSize: '12px', lineHeight: 1.5, color: C.textMuted, cursor: 'pointer' }}>
                  <input type="checkbox" checked={aceptaCondiciones} onChange={e => setAceptaCondiciones(e.target.checked)}
                    style={{ marginTop: '2px', width: '18px', height: '18px', flexShrink: 0, accentColor: C.primary, cursor: 'pointer' }} />
                  <span>
                    He leído y acepto el{' '}
                    <button type="button" onClick={e => { e.preventDefault(); setShowLegal(true); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, color: C.primary, fontSize: '12px', fontWeight: '700', textDecoration: 'underline', cursor: 'pointer' }}>Aviso Legal</button>
                    {' '}y la{' '}
                    <button type="button" onClick={e => { e.preventDefault(); setShowPrivacy(true); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, color: C.primary, fontSize: '12px', fontWeight: '700', textDecoration: 'underline', cursor: 'pointer' }}>Política de Privacidad</button>.
                  </span>
                </label>
              )}
              {authError && <p style={{ color: C.danger, fontSize: '13px', margin: '0 0 12px' }}>{authError}</p>}
              <button onClick={() => handleDuenoAuth(esRegistro ? 'registro' : 'login')} disabled={authBusy || (esRegistro && !aceptaCondiciones)}
                style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', opacity: (authBusy || (esRegistro && !aceptaCondiciones)) ? 0.55 : 1, cursor: (esRegistro && !aceptaCondiciones) ? 'not-allowed' : 'pointer' }}>
                {authBusy ? 'Un momento…' : (esRegistro ? 'Crear cuenta' : 'Entrar')}
              </button>
              <button onClick={() => { setAuthScreen(esRegistro ? 'duenoLogin' : 'duenoRegistro'); setAuthError(''); }}
                style={{ width: '100%', marginTop: '12px', background: 'transparent', border: 'none', color: C.primary, fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                {esRegistro ? '¿Ya tienes cuenta? Inicia sesión' : '¿Primera vez? Crea tu cuenta de Dueño'}
              </button>
            </div>
          </div>
        );
      }
      if (authScreen === 'jefeLogin' || authScreen === 'jefeRegistro') {
        const esRegistro = authScreen === 'jefeRegistro';
        return (
          <div key={`acceso-${authScreen}`} className="hd-auth-bg" style={authGradBg}>
            <div className="hd-auth-card" style={authCard}>
              <button onClick={() => { setAuthScreen('inicio'); setAuthError(''); }}
                style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Volver</button>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '64px', height: 'auto' }} />
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: C.text, margin: '10px 0 4px' }}>
                  {esRegistro ? 'Crear cuenta de Encargado' : 'Iniciar sesión'}
                </h2>
                <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
                  {esRegistro ? 'Con esta cuenta tus datos se guardan en la nube y se sincronizan en todos tus dispositivos.' : 'Accede con el email y contraseña de tu restaurante.'}
                </p>
              </div>
              {/* ENTER PARA ENTRAR (29 sep 2026): mismo patrón que en el login de Dueño. */}
              <input type="email" maxLength={254} placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !authBusy && handleJefeAuth(esRegistro ? 'registro' : 'login')} style={authInput} />
              <input type="password" maxLength={128} placeholder={esRegistro ? `Contraseña (mínimo ${CONTRASENA_MIN} caracteres)` : 'Contraseña'} value={authPassword} onChange={e => setAuthPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !authBusy && handleJefeAuth(esRegistro ? 'registro' : 'login')} style={authInput} />
              {esRegistro && (
                verCodigoInvitacion ? (
                  <div style={{ margin: '0 0 14px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.textMuted, margin: '0 0 6px' }}>Código de invitación</label>
                    <input type="text" autoCapitalize="characters" autoComplete="off" spellCheck={false} maxLength={12}
                      placeholder="XXXX-XXXX" value={codigoInvitacion}
                      onChange={e => setCodigoInvitacion(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && !authBusy && handleJefeAuth('registro')}
                      style={{ ...authInput, marginBottom: '4px', letterSpacing: '2px', fontWeight: '700' }} />
                    <button type="button" onClick={() => { setVerCodigoInvitacion(false); setCodigoInvitacion(''); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, color: C.textMuted, fontSize: '12px', cursor: 'pointer' }}>
                      Quitar el código
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setVerCodigoInvitacion(true)}
                    style={{ display: 'block', background: 'transparent', border: 'none', padding: 0, margin: '0 0 14px', color: C.primary, fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                    ¿Te ha invitado otro restaurante?
                  </button>
                )
              )}
              {esRegistro && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '0 0 14px', fontSize: '12px', lineHeight: 1.5, color: C.textMuted, cursor: 'pointer' }}>
                  <input type="checkbox" checked={aceptaCondiciones} onChange={e => setAceptaCondiciones(e.target.checked)}
                    style={{ marginTop: '2px', width: '18px', height: '18px', flexShrink: 0, accentColor: C.primary, cursor: 'pointer' }} />
                  <span>
                    He leído y acepto las{' '}
                    <button type="button" onClick={e => { e.preventDefault(); setShowConditions(true); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, color: C.primary, fontSize: '12px', fontWeight: '700', textDecoration: 'underline', cursor: 'pointer' }}>Condiciones de Contratación</button>
                    {' '}(incluido el contrato de encargo de tratamiento de datos), el{' '}
                    <button type="button" onClick={e => { e.preventDefault(); setShowLegal(true); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, color: C.primary, fontSize: '12px', fontWeight: '700', textDecoration: 'underline', cursor: 'pointer' }}>Aviso Legal</button>
                    {' '}y la{' '}
                    <button type="button" onClick={e => { e.preventDefault(); setShowPrivacy(true); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, color: C.primary, fontSize: '12px', fontWeight: '700', textDecoration: 'underline', cursor: 'pointer' }}>Política de Privacidad</button>.
                  </span>
                </label>
              )}
              {authError && <p style={{ color: C.danger, fontSize: '13px', margin: '0 0 12px' }}>{authError}</p>}
              <button onClick={() => handleJefeAuth(esRegistro ? 'registro' : 'login')} disabled={authBusy || (esRegistro && !aceptaCondiciones)}
                style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', opacity: (authBusy || (esRegistro && !aceptaCondiciones)) ? 0.55 : 1, cursor: (esRegistro && !aceptaCondiciones) ? 'not-allowed' : 'pointer' }}>
                {authBusy ? 'Un momento…' : (esRegistro ? 'Crear cuenta' : 'Entrar')}
              </button>
              <button onClick={() => { setAuthScreen(esRegistro ? 'jefeLogin' : 'jefeRegistro'); setAuthError(''); }}
                style={{ width: '100%', marginTop: '12px', background: 'transparent', border: 'none', color: C.primary, fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                {esRegistro ? '¿Ya tienes cuenta? Inicia sesión' : '¿Primera vez? Crea tu cuenta'}
              </button>
              {!esRegistro && (
                <button onClick={() => { setAuthScreen('jefeReset'); setAuthError(''); setResetSent(false); }}
                  style={{ width: '100%', marginTop: '4px', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', cursor: 'pointer' }}>
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </div>
          </div>
        );
      }
      if (authScreen === 'jefeReset') {
        return (
          <div key="acceso-reset" className="hd-auth-bg" style={authGradBg}>
            <div className="hd-auth-card" style={authCard}>
              <button onClick={() => { setAuthScreen('jefeLogin'); setAuthError(''); setResetSent(false); }}
                style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Volver</button>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '64px', height: 'auto' }} />
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: C.text, margin: '10px 0 4px' }}>Recuperar contraseña</h2>
                <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Te mandamos un enlace a tu email para elegir una nueva contraseña.</p>
              </div>
              {resetSent ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: C.success, fontSize: '14px', fontWeight: '700', marginBottom: '20px' }}>
                    ✓ Listo, revisa tu bandeja de entrada (y la de spam) en <b>{authEmail}</b>
                  </p>
                  <button onClick={() => { setAuthScreen('jefeLogin'); setResetSent(false); }}
                    style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center' }}>
                    Volver a iniciar sesión
                  </button>
                </div>
              ) : (
                <>
                  <input type="email" maxLength={254} placeholder="Tu email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} style={authInput} />
                  {authError && <p style={{ color: C.danger, fontSize: '13px', margin: '0 0 12px' }}>{authError}</p>}
                  <button onClick={handlePasswordReset} disabled={authBusy}
                    style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', opacity: authBusy ? 0.7 : 1 }}>
                    {authBusy ? 'Enviando…' : 'Enviar enlace de recuperación'}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      }
      if (authScreen === 'empleadoCodigo') {
        return (
          <div key="acceso-codigo" className="hd-auth-bg" style={authGradBg}>
            <div className="hd-auth-card" style={authCard}>
              <button onClick={() => { setAuthScreen('inicio'); setAuthError(''); }}
                style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Volver</button>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '64px', height: 'auto' }} />
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: C.text, margin: '10px 0 4px' }}>Código de tu restaurante</h2>
                <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Pídeselo a tu encargado, lo encuentra en <Ico n="gear" /> Configuración.</p>
              </div>
              <input type="text" maxLength={6} placeholder="Ej: A3B7K9" value={accessCodeInput}
                onChange={e => setAccessCodeInput(e.target.value.toUpperCase())}
                style={{ ...authInput, textAlign: 'center', letterSpacing: '3px', fontWeight: '800', fontSize: '18px', textTransform: 'uppercase' }} />
              {authError && <p style={{ color: C.danger, fontSize: '13px', margin: '0 0 12px' }}>{authError}</p>}
              <button onClick={handleEmpleadoCodigo} disabled={authBusy}
                style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', opacity: authBusy ? 0.7 : 1 }}>
                {authBusy ? 'Comprobando…' : 'Entrar'}
              </button>
            </div>
          </div>
        );
      }
      // authScreen === 'inicio'
      return (
        // La "key" no es decorativa: obliga a React a crear esta pantalla desde cero en vez de
        // reaprovechar los elementos de la pantalla anterior. Sin ella, al salir de la sesión
        // reutilizaba los cuadrados del logo ya existentes y la animación se quedaba a medias:
        // los 6 acababan apilados uno encima de otro, moviéndose a la vez, y parecía uno solo.
        // Recargar con F5 lo arreglaba precisamente porque forzaba esa creación desde cero.
        <div key="pantalla-inicio" className="hd-auth-bg" style={authGradBg}>
          <style>{`
            @keyframes horiaSquaresCycleInicio {
              0%    { transform: translate(0px,0px); }
              16.6% { transform: translate(56px,0px); }
              33.3% { transform: translate(112px,0px); }
              50%   { transform: translate(112px,52px); }
              66.6% { transform: translate(56px,52px); }
              83.3% { transform: translate(0px,52px); }
              100%  { transform: translate(0px,0px); }
            }
          `}</style>
          <div className="hd-auth-card" style={authCard}>
            {isCocina && (
              <button onClick={() => onSwitchDepartment('sala')}
                style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Sala</button>
            )}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              {/* Mismo ajuste (4 oct 2026): huella real de cada cuadrado = 35 + 3×2 = 41px, así que
                  el recorrido ocupa 153x93; con 148x96 sobresalía 5px por la derecha y el logo
                  quedaba descentrado. */}
              <div style={{ position: 'relative', width: '153px', height: '93px', margin: '0 auto 17px' }}>
                {['#7C3AED', '#D97706', '#4C1D95', '#7C3AED', '#A78BFA', '#4C1D95'].map((color, i) => {
                  const esNaranja = color === '#D97706';
                  return (
                    <div key={i} onClick={esNaranja ? () => { setAuthScreen('duenoLogin'); setAuthError(''); } : undefined}
                      title={esNaranja ? 'Acceso Dueño' : undefined}
                      style={{
                        position: 'absolute', top: 0, left: 0, width: '35px', height: '35px',
                        borderRadius: '7px', border: `3px solid ${color}`, background: 'transparent',
                        animation: 'horiaSquaresCycleInicio 9s ease-in-out infinite',
                        animationDelay: `${-1.5 * i}s`,
                        cursor: esNaranja ? 'pointer' : 'default',
                        pointerEvents: esNaranja ? 'auto' : 'none',
                      }} />
                  );
                })}
              </div>
              {/* Nombre de la marca bajo el logo (8 oct 2026): antes la pantalla de inicio solo
                  mostraba los cuadrados y el lema, sin decir cómo se llama la app. */}
              <h1 style={{ margin: '0 0 4px', lineHeight: 1.1 }}><Wordmark size={38} /></h1>
              {isCocina && <Badge color="primary">{conIconos(L.heroLabel)}</Badge>}
              <p style={{ color: C.textMuted, fontSize: '14px', margin: '6px 0 0' }}>Gestión inteligente de horarios</p>
            </div>
            {authError && <p style={{ color: C.danger, fontSize: '13px', textAlign: 'center', margin: '0 0 14px' }}>{authError}</p>}
            {isCocina ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button onClick={handleEmpleadoBoton} disabled={authBusy} style={{ ...btn('primary', 'lg'), justifyContent: 'center', opacity: authBusy ? 0.7 : 1 }}><Ico n="key" /> Entrar con el código del restaurante</button>
                <p style={{ fontSize: '12px', color: C.textMuted, textAlign: 'center', margin: 0 }}>Vale tanto para el Jefe de Cocina como para los Cocineros — pide el código a tu encargado de Sala.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button onClick={() => setAuthScreen('jefeLogin')} style={{ ...btn('primary', 'lg'), justifyContent: 'center' }}><Ico n="home" /> Soy Encargado</button>
                <button onClick={handleEmpleadoBoton} disabled={authBusy} style={{ ...btn('outline', 'lg'), justifyContent: 'center', border: `2px solid ${C.primaryBorder}`, color: C.primary, opacity: authBusy ? 0.7 : 1 }}><Ico n="user" /> Soy Empleado</button>
              </div>
            )}
            {!isCocina && (
              <button onClick={() => onSwitchDepartment('cocina')}
                style={{ width: '100%', marginTop: '18px', padding: '12px', background: C.bg, border: `1px dashed ${C.primaryBorder}`, borderRadius: '10px', color: C.primaryDark, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                <Ico n="pan" /> Ir a Cocina
              </button>
            )}
            {!isCocina && (
              <button onClick={() => setShowPlans(true)}
                style={{ width: '100%', marginTop: '10px', padding: '10px', background: 'transparent', border: 'none', color: C.primary, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                <Ico n="card" /> Ver planes y precios
              </button>
            )}
            <p style={{ textAlign: 'center', fontSize: '11px', color: C.textLight, marginTop: '20px', marginBottom: 0 }}>
              <button onClick={() => setShowPrivacy(true)} style={{ background: 'transparent', border: 'none', color: C.textLight, fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                Política de Privacidad
              </button>
              {' · '}
              <button onClick={() => setShowLegal(true)} style={{ background: 'transparent', border: 'none', color: C.textLight, fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                Aviso Legal
              </button>
              {' · '}
              <button onClick={() => setShowConditions(true)} style={{ background: 'transparent', border: 'none', color: C.textLight, fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                Condiciones
              </button>
              {' '}· © <Wordmark size={12} weight={700} /> 2026
            </p>
          </div>
        </div>
      );
    }

    if (!dataLoaded) return (
      <div key="acceso-cargando" className="hd-auth-bg" style={authGradBg}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: C.white, fontSize: '16px', fontWeight: '700', margin: '0 0 16px' }}>Cargando tus datos…</p>
          {cargaLenta && (
            <button onClick={() => { try { sessionStorage.removeItem('horia_auto_recargas'); } catch (_) {} window.location.reload(); }}
              style={{ ...btn('outline', 'md'), background: C.white, color: C.primary, border: 'none' }}>
              <Ico n="refresh" /> Sigue sin cargar — recargar
            </button>
          )}
        </div>
      </div>
    );

    if (userRole === 'jefe' && !config.configurado) {
      return <IntroScreen onComplete={handleIntroComplete} department={department} onSwitchDepartment={onSwitchDepartment} firebaseUser={firebaseUser} userRole={userRole} blockedMessage={introBlockedMessage} onVolverInicio={handleLogout} />;
    }

    // PRUEBA DE 14 DÍAS CADUCADA (2 oct 2026): pasa aquí tanto Encargado como empleados como
    // Dueño — mientras el negocio no haya pagado, nadie entra, que es justo lo que pidió Fran.
    // `!!creadoEn` es el seguro: sin fecha de alta (cualquier cuenta de antes de hoy), esto nunca
    // se activa, pase lo que pase con `cuentaActivada`. Ver el comentario junto al estado, arriba.
    // En Cocina, tanto la fecha de alta como la activación son las de SALA (ver `cuentaEfectiva`).
    if (esperandoCuentaDeSala) {
      return (
        <div key="acceso-prueba-a" className="hd-auth-bg" style={authGradBg}>
          <div className="hd-auth-card" style={{ ...authCard, textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.textMuted }}>Cargando…</p>
          </div>
        </div>
      );
    }
    if (pruebaCaducada) {
        // Rediseñado el 3 oct 2026 con Fran: aviso -> "Continuar con Horidia" -> gracias + métodos
        // de pago (Bizum / transferencia). Ver PantallaFinPrueba, arriba del todo.
        // Cuota = Sala + Cocina (5 oct 2026): se espera a leer el otro departamento para no
        // enseñar un precio que luego cambie. Si no se pudiera leer (null), se enseña con lo que
        // hay y se avisa de que la cuota final se confirma al activar la cuenta.
        if (numEmpleadosOtroDepto === undefined) {
          return (
            <div key="acceso-prueba-b" className="hd-auth-bg" style={authGradBg}>
              <div className="hd-auth-card" style={{ ...authCard, textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.textMuted }}>Cargando…</p>
              </div>
            </div>
          );
        }
        const totalEmpleadosCuota = employees.length + (numEmpleadosOtroDepto || 0);
        return (
          <PantallaFinPrueba
            nombreNegocio={config.restaurantName}
            numEmpleados={totalEmpleadosCuota}
            precioMes={calcularPrecioMensual(totalEmpleadosCuota)}
            cuotaParcial={numEmpleadosOtroDepto === null}
            onSalir={handleLogout}
            motivo={estadoCuentaActual.estado === 'desactivada' ? 'desactivada' : estadoCuentaActual.estado === 'vencida' ? 'suscripcion' : 'prueba'}
            diasPrueba={(() => {
              const alta = fechaJSDe(cuentaEfectiva.creadoEn);
              const fin = estadoCuentaActual.ultimoDia;
              const n = (alta && fin) ? diasEntreCuenta(fechaMadridCuenta(alta), fin) + 1 : DIAS_PRUEBA_GRATIS;
              return n >= DIAS_PRUEBA_GRATIS ? n : DIAS_PRUEBA_GRATIS;
            })()}
          />
        );
    }

    if (bloqueadoHuella) {
      return (
        <div key="acceso-prueba-c" className="hd-auth-bg" style={authGradBg}>
          <div className="hd-auth-card" style={{ ...authCard, textAlign: 'center' }}>
            <button onClick={volverAlSelector}
              style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', padding: '4px 0', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              ← Volver
            </button>
            <FichaPersona nombre={currentUser || '?'} size={60} style={{ margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '800', color: C.text, margin: '0 0 4px' }}>Hola de nuevo, {currentUser}</h2>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 28px' }}>Confirma con tu huella para entrar</p>
            <button onClick={handleDesbloquearHuella} disabled={huellaBusy}
              style={{ width: '84px', height: '84px', borderRadius: '50%', border: 'none', background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, fontSize: '34px', cursor: 'pointer', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(124,58,237,0.3)', opacity: huellaBusy ? 0.6 : 1 }}>
              {huellaBusy ? '…' : conIconos('🔓')}
            </button>
            {huellaError && <p style={{ color: C.danger, fontSize: '13px', margin: '0 0 16px' }}>{huellaError}</p>}
            {/* Antes este botón llamaba a handleLogout, o sea que cerraba la sesión entera y te
                devolvía al principio: había que escribir otra vez el código del restaurante y,
                encima, volvía a pedir la huella. Ahora lleva directo a la pantalla del PIN de
                esa misma persona, que es lo que se esperaba. Si por lo que sea todavía no se han
                cargado los datos del restaurante, se recurre al comportamiento antiguo para no
                dejar a nadie atrapado en esta pantalla. */}
            <button onClick={() => {
              const emp = employees.find(e => e.id === selectedEmployeeId);
              if (!emp) { handleLogout(); return; }
              setBloqueadoHuella(false);
              setHuellaError('');
              setPendingPinEmployee(emp);
              setPinInput(''); setPinError(''); setPinIntentos(0); setPinBloqueadoHasta(0);
            }} style={{ background: 'transparent', border: 'none', color: C.primary, fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
              Usar el PIN en su lugar
            </button>
          </div>
        </div>
      );
    }

    if (pendingPinEmployee) {
      return (
        <div key="acceso-pin" className="hd-auth-bg" style={authGradBg}>
          <div className="hd-auth-card" style={authCard}>
            <button onClick={() => { setPendingPinEmployee(null); setPinInput(''); setPinError(''); }}
              style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Volver</button>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <FichaPersona p={pendingPinEmployee} size={54} style={{ margin: '0 auto 12px' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: C.text, margin: '0 0 4px' }}>Hola, {pendingPinEmployee.name}</h2>
              <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Introduce tu PIN de 6 dígitos</p>
            </div>
            <input type="password" inputMode="numeric" maxLength={6} placeholder="••••••" value={pinInput} disabled={pinSegundosRestantes > 0}
              onChange={e => setPinInput(e.target.value.replace(/\D/g, ''))}
              onKeyPress={e => e.key === 'Enter' && handlePinSubmit()}
              style={{ ...authInput, textAlign: 'center', letterSpacing: '8px', fontWeight: '800', fontSize: '22px', opacity: pinSegundosRestantes > 0 ? 0.5 : 1 }} />
            {pinError && <p style={{ color: C.danger, fontSize: '13px', margin: '0 0 12px' }}>{pinError}</p>}
            <button onClick={handlePinSubmit} disabled={pinBusy || pinSegundosRestantes > 0}
              style={{ ...btn('primary', 'lg'), width: '100%', justifyContent: 'center', opacity: (pinBusy || pinSegundosRestantes > 0) ? 0.6 : 1 }}>
              {pinSegundosRestantes > 0 ? conIconos(`⏳ Espera ${pinSegundosRestantes}s`) : (pinBusy ? 'Comprobando…' : 'Entrar')}
            </button>
          </div>
        </div>
      );
    }

    if (userRole === 'empleado' && !selectedEmployeeId) {
      return (
        <div key="acceso-quien" className="hd-auth-bg" style={authGradBg}>
          <div className="hd-auth-card" style={{ ...authCard, maxWidth: '440px' }}>
            <button onClick={handleLogout}
              style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginBottom: '18px' }}>← Volver</button>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <img src={HORIA_ICON_SRC} alt="Horidia" style={{ width: '64px', height: 'auto' }} />
              <h2 style={{ fontSize: '22px', fontWeight: '800', color: C.text, margin: '10px 0 4px' }}>¿Quién eres?</h2>
              <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>{config.restaurantName}</p>
            </div>
            {employees.length === 0 ? (
              <p style={{ textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>Tu encargado aún no ha añadido empleados.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '360px', overflowY: 'auto' }}>
                {employees.map(emp => (
                  <button key={emp.id} onClick={() => {
                    if (emp.authEmail) {
                      try {
                        const huella = JSON.parse(localStorage.getItem(HUELLA_KEY) || 'null');
                        const yaAutenticadoComoEl = firebaseUser && !firebaseUser.isAnonymous && firebaseUser.email === emp.authEmail;
                        if (yaAutenticadoComoEl && huella?.empId === emp.id && huella?.credentialId) {
                          // Ya habíamos confirmado quién es (PIN o huella) en esta misma sesión del navegador:
                          // no hace falta volver a pedir el PIN, directos al candado de huella.
                          setSelectedEmployeeId(emp.id);
                          setCurrentUser(emp.name);
                          setUserRole(emp.esEncargado ? 'jefe' : 'empleado');
                          setBloqueadoHuella(true);
                          try { localStorage.setItem(RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId, empId: emp.id, nombre: emp.name, esEncargado: !!emp.esEncargado })); } catch (_) {}
                          return;
                        }
                      } catch (_) {}
                      setPendingPinEmployee(emp); setPinInput(''); setPinError(''); setPinIntentos(0); setPinBloqueadoHasta(0);
                      return;
                    }
                    setSelectedEmployeeId(emp.id); setCurrentUser(emp.name);
                    setUserRole(emp.esEncargado ? 'jefe' : 'empleado');
                    try { localStorage.setItem(RECORDADO_KEY, JSON.stringify({ uid: restaurantDocId, empId: emp.id, nombre: emp.name, esEncargado: !!emp.esEncargado })); } catch (_) {}
                  }}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px', border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', textAlign: 'left' }}>
                    <FichaPersona p={emp} size={38} />
                    <span style={{ fontWeight: '700', color: C.text, fontSize: '15px' }}>{emp.name}{emp.esGerente ? conIconos(' 💼') : ''}</span>
                    {emp.authEmail && <span style={{ marginLeft: 'auto', fontSize: '13px' }}><Ico n="lock" /></span>}
                  </button>
                ))}
              </div>
            )}
            <button onClick={handleRestauranteEquivocado}
              style={{ background: 'transparent', border: 'none', color: C.textMuted, fontSize: '12px', textDecoration: 'underline', cursor: 'pointer', marginTop: '18px', display: 'block', width: '100%', textAlign: 'center' }}>
              ¿No es tu restaurante? Usar otro código
            </button>
          </div>
        </div>
      );
    }
  }

  if (!config.configurado) {
    if (employeeWaitingNotice) return (
      <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, #6D28D9 0%, #8B5CF6 40%, #A78BFA 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', borderRadius: '20px', padding: '40px', maxWidth: '440px', width: '100%', textAlign: 'center', border: '2px solid rgba(255,255,255,0.2)' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}><Ico n="hourglass" /></div>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: C.white, margin: '0 0 12px' }}>Aún no está todo listo</h2>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', margin: '0 0 28px', lineHeight: 1.5 }}>
            {isCocina
              ? `Tu jefe de cocina todavía no ha configurado la cocina en Horidia. Pídele que entre primero como ${L.jefeRoleName} para terminar la configuración.`
              : `Tu encargado todavía no ha configurado el restaurante en Horidia. Pídele que entre primero como ${L.jefeRoleName} para terminar la configuración.`}
          </p>
          <button onClick={() => setEmployeeWaitingNotice(false)}
            style={{ width: '100%', padding: '14px', background: C.white, color: C.primary, border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '800', cursor: 'pointer' }}>
            ← Volver
          </button>
        </div>
      </div>
    );
    return <IntroScreen onComplete={handleIntroComplete} department={department} onSwitchDepartment={onSwitchDepartment} userRole={userRole} blockedMessage={introBlockedMessage} onVolverInicio={handleLogout} />;
  }

  if (showPlans) return <PlansScreen onBack={() => setShowPlans(false)} />;

  if (showLogin) return (
    // Misma "key" propia que en la pantalla de inicio, por el mismo motivo: que React no
    // reaproveche elementos de otra pantalla y rompa la animación del logo.
    <div key="pantalla-login-clasica" className="hd-auth-bg" style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${C.primaryLight} 0%, #A78BFA 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <style>{`
        @keyframes horiaSquaresCycle {
          0%    { transform: translate(0px,0px); }
          16.6% { transform: translate(56px,0px); }
          33.3% { transform: translate(112px,0px); }
          50%   { transform: translate(112px,52px); }
          66.6% { transform: translate(56px,52px); }
          83.3% { transform: translate(0px,52px); }
          100%  { transform: translate(0px,0px); }
        }
      `}</style>
      <div className="hd-auth-card" style={{ background: C.white, borderRadius: '20px', padding: '44px 40px', maxWidth: '400px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', position: 'relative' }}>
        {isCocina && (
          <button onClick={() => onSwitchDepartment('sala')}
            style={{ position: 'absolute', top: '18px', left: '18px', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            ← Sala
          </button>
        )}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ position: 'relative', width: '153px', height: '93px', margin: '0 auto 23px' }}>
            {['#7C3AED', '#D97706', '#4C1D95', '#7C3AED', '#A78BFA', '#4C1D95'].map((color, i) => (
              <div key={i} style={{
                position: 'absolute', top: 0, left: 0, width: '35px', height: '35px',
                borderRadius: '7px', border: `3px solid ${color}`, background: 'transparent',
                animation: 'horiaSquaresCycle 9s ease-in-out infinite',
                animationDelay: `${-1.5 * i}s`,
              }} />
            ))}
          </div>
          <h1 style={{ margin: '0 0 4px', lineHeight: 1.1 }}><Wordmark size={38} /></h1>
          {isCocina && <Badge color="primary">{conIconos(L.heroLabel)}</Badge>}
          {config.restaurantName && <p style={{ color: C.primary, margin: '6px 0', fontWeight: '700', fontSize: '16px' }}>{config.restaurantName}</p>}
          <p style={{ color: C.textMuted, margin: 0, fontSize: '14px' }}>Gestión inteligente de horarios</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button onClick={() => handleLogin('jefe')} style={{ ...btn('primary', 'lg'), justifyContent: 'center', background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})` }}>{conIconos(L.jefeButtonLabel)}</button>
          <button onClick={() => handleLogin('empleado')} style={{ ...btn('outline', 'lg'), justifyContent: 'center', border: `2px solid ${C.primaryBorder}`, color: C.primary }}>{conIconos(L.empleadoButtonLabel)}</button>
        </div>
        {!isCocina && (
          <button onClick={() => onSwitchDepartment('cocina')}
            style={{ width: '100%', marginTop: '18px', padding: '12px', background: C.bg, border: `1px dashed ${C.primaryBorder}`, borderRadius: '10px', color: C.primaryDark, fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Ico n="pan" /> Ir a Cocina
          </button>
        )}
        {!isCocina && (
          <>
            <div style={{ height: '1px', background: C.border, margin: '20px 0 16px' }} />
            <button onClick={() => setShowPlans(true)}
              style={{ width: '100%', padding: '13px', background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, borderRadius: '10px', color: C.primaryDark, fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Ico n="card" /> Ver planes y precios →
            </button>
          </>
        )}
        <p style={{ fontSize: '12px', color: C.textLight, textAlign: 'center', marginTop: '28px', marginBottom: 0 }}>© <Wordmark size={12} weight={700} /> 2026</p>
      </div>
    </div>
  );

  if (userRole === 'dueno') {
    const hoyStr = toDateStr(new Date());
    const turnosEliminadosTotal = employees.flatMap(e => (e.turnosQuitados || []).map(tq => ({ ...tq, empleado: e.name })))
      .sort((a, b) => b.id - a.id);
    const historialOrdenado = [...scheduleHistory].sort((a, b) => (b.publishedAtTimestamp || 0) - (a.publishedAtTimestamp || 0));
    // Averiguar, para CADA local, cuál es su entrada más reciente del historial — esa es la que
    // representa "lo que hay publicado ahora mismo" para ese local, y es la que se pinta con
    // datos EN VIVO (ver más abajo) en vez de con la foto fija de cuando se publicó. Así, si el
    // Encargado reasigna un turno después de publicar (p.ej. al aceptar que alguien pida quitarse
    // un turno y dárselo a otro), el Dueño lo ve al momento sin esperar a que se vuelva a publicar.
    const ultimaEntradaPorLocal = {};
    historialOrdenado.forEach(h => {
      const key = h.localId || LOCAL_PRINCIPAL;
      if (!ultimaEntradaPorLocal[key] || (h.publishedAtTimestamp || 0) > (ultimaEntradaPorLocal[key].publishedAtTimestamp || 0)) {
        ultimaEntradaPorLocal[key] = h;
      }
    });
    const seccion = (icono, color, titulo) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flexShrink: 0, color: C.primary }}>{iconoDeEmoji(icono) ? <Ico n={iconoDeEmoji(icono)} s="18px" /> : icono}</div>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: C.text }}>{titulo}</h3>
      </div>
    );

    // DETALLE DE UN LOCAL (22 ago 2026): al pulsar una tarjeta de local en el dashboard, se
    // muestra esta pantalla completa en vez del dashboard normal, con todo lo de ese local:
    // horario de la semana (en vivo, igual que en Historial), carta, mapa de mesas, solicitudes
    // (con el aviso honesto de que hoy no están separadas por local, es una limitación real de
    // la app, no un fallo de esta pantalla) y la plantilla contratada en ese local con sus turnos.
    if (duenoLocalAbierto) {
      const localAbierto = locales.find(l => l.id === duenoLocalAbierto);
      const datosL = datosLocalMenuMapa(duenoLocalAbierto);
      // PANTALLA COMPLETA CON ROTACIÓN FORZADA (22 ago 2026). La API de rotación forzada
      // (screen.orientation.lock) solo funciona dentro de un elemento en modo Fullscreen real
      // (Fullscreen API), y solo la soportan Chrome/Android — Safari/iPhone no la tiene. Por eso
      // TODO va envuelto en try/catch y en comprobaciones de que el método existe antes de
      // llamarlo: en Android gira sola, en iPhone simplemente no gira (sin aviso ni error, tal
      // como se pidió) y la persona la gira a mano, la tabla ya está pensada para verse bien en
      // horizontal de cualquier forma.
      const abrirHorarioPantallaCompleta = async () => {
        setDuenoHorarioPantallaCompleta(true);
        try {
          if (document.documentElement.requestFullscreen) {
            await document.documentElement.requestFullscreen();
          }
          if (window.screen.orientation && window.screen.orientation.lock) {
            await window.screen.orientation.lock('landscape');
          }
        } catch (_) { /* iPhone, o navegador sin soporte: no pasa nada, se sigue en vertical */ }
      };
      const cerrarHorarioPantallaCompleta = async () => {
        setDuenoHorarioPantallaCompleta(false);
        try {
          if (window.screen.orientation && window.screen.orientation.unlock) window.screen.orientation.unlock();
          if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
        } catch (_) { /* nada que hacer si no estaba en fullscreen */ }
      };
      // FALLO REAL ARREGLADO (22 ago 2026): antes se leía directo de schedulesPorLocal/
      // publicadosPorLocal indexando por duenoLocalAbierto, y Fran vio que tras publicar los 3
      // locales, esta pantalla no se enteraba. Se usa ahora el MISMO mecanismo que ya usa (y
      // funciona bien) la sección Historial más abajo: la entrada más reciente del historial de
      // ESE local (`ultimaEntradaPorLocal`), con los datos en vivo de `schedulesPorLocal` como
      // primera opción y el snapshot guardado como respaldo si por lo que sea no coincide.
      const entradaVigente = ultimaEntradaPorLocal[duenoLocalAbierto];
      const scheduleDelLocal = (entradaVigente ? (schedulesPorLocal[duenoLocalAbierto] || entradaVigente.schedule) : null) || {};
      const publicadoDelLocal = !!entradaVigente;
      const empleadosConTurnoAqui = Object.keys(scheduleDelLocal)
        .map(empId => employees.find(e => String(e.id) === empId))
        .filter(Boolean);

      // Tabla del horario del local, en su propia función para poder pintarla igual en la
      // tarjeta normal y en la vista de pantalla completa, sin duplicar el JSX.
      const tablaHorarioDelLocal = (compacta = false) => (
        <table className="horia-schedule-table" style={compacta
          ? { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }
          : { width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
          <colgroup>
            <col style={{ width: compacta ? '18%' : undefined }} />
            {DAYS.map(d => <col key={d} style={{ width: compacta ? `${82 / DAYS.length}%` : undefined }} />)}
          </colgroup>
          <thead>
            <tr>
              <th className={compacta ? undefined : 'horia-sticky-col'} style={{ padding: compacta ? '4px 6px' : '8px 10px', textAlign: 'left', fontSize: compacta ? 'clamp(9px, 1.8vw, 13px)' : '12px', color: C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, minWidth: compacta ? undefined : '130px', background: C.bg }}>Empleado</th>
              {DAYS.map(d => (
                <th key={d} style={{ padding: compacta ? '4px 2px' : '8px 5px', textAlign: 'center', fontSize: compacta ? 'clamp(8px, 1.6vw, 12px)' : '11px', color: C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                  {d.substring(0, 3)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {empleadosConTurnoAqui.map((emp, idx) => (
              <tr key={emp.id} style={{ background: idx % 2 === 0 ? C.white : 'rgba(255,255,255,0.5)' }}>
                <td className={compacta ? undefined : 'horia-sticky-col'} style={{ padding: compacta ? '3px 6px' : '7px 10px', fontWeight: '600', fontSize: compacta ? 'clamp(8px, 1.6vw, 12px)' : '12px', color: C.text, borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? C.white : '#FBFAFE', wordBreak: 'break-word', lineHeight: 1.2 }}>
                  {compacta ? emp.name : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <FichaPersona p={emp} size={26} />
                      <div>{emp.name}</div>
                    </div>
                  )}
                </td>
                {DAYS.map(day => {
                  const s = scheduleDelLocal?.[emp.id]?.[day] || { tipo: 'libre', tareas: [] };
                  return (
                    <td key={day} style={{ padding: compacta ? '2px' : '3px', textAlign: 'center', borderBottom: `1px solid ${C.border}` }}>
                      <CeldaTurno s={s} conf={config} tam={compacta ? 'mini' : 'compacta'} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );

      return (
        <div style={{ minHeight: '100vh', background: C.bg, overflowX: 'hidden' }}>
          {avisoCuentaUI}
          {/* FALLO REAL ARREGLADO (22 ago 2026), CORREGIDO 24 ago: esta pantalla tiene su propia
              cabecera morada y nunca monta <Header/>, así que le faltaba contener el
              desbordamiento horizontal — por eso al deslizar la tabla hacia la derecha se veía el
              morado cortado en blanco. El primer intento tocaba html/body enteros y se pasó de
              frenada: además de arreglar eso, recortaba contenido legítimo (la carta, el mapa,
              nombres largos) en vez de solo contener el "tirón" de la página. Ahora se limita al
              propio contenedor de esta pantalla, sin tocar nada global — los scrolls internos que
              sí hacen falta (la tabla del horario, por ejemplo) siguen funcionando igual que antes. */}
          <div style={{ background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, padding: '14px 24px', boxShadow: '0 4px 16px rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={() => { cerrarHorarioPantallaCompleta(); setDuenoLocalAbierto(null); }} aria-label="Volver"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '20px', color: C.white, padding: '4px 6px 4px 0', lineHeight: 1 }}>
              ←
            </button>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'rgba(255,255,255,0.18)', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2px', padding: '4px', flexShrink: 0, boxSizing: 'border-box' }}>
                {['#FFFFFF', '#FDBA74', '#E9D5FF', '#FFFFFF'].map((c, i) => (
                  <div key={i} style={{ border: `1.3px solid ${c}`, borderRadius: '1.5px' }} />
                ))}
              </div>
              {localAbierto?.nombre || 'Local'}
            </h1>
          </div>

          {/* minWidth: 0 es el arreglo real de "todo sale cortado/descuadrado/gigante" (24 ago
              2026): en una rejilla CSS, un hijo con contenido ancho (la tabla del horario, 700px
              mínimo) empuja TODA la columna a ser de al menos ese ancho — aunque el móvil tenga
              400px — y eso arrastra con ella a la Plantilla, la Carta y el Mapa, que dependen de
              que la columna mida lo que mide la pantalla de verdad. Con minWidth: 0 la columna sí
              puede encogerse, y el propio scroll interno de la tabla (.horia-schedule-wrap) hace
              su trabajo sin desproporcionar nada más. */}
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px 20px 32px', display: 'grid', gap: '16px', minWidth: 0 }}>

            {/* PLANTILLA DE ESTE LOCAL, con sus turnos — primero, es lo que más se consulta.
                CAMBIO IMPORTANTE (22 ago 2026): antes se basaba en quién tiene este local marcado
                como FIJO en su ficha (una preferencia para el generador, no un hecho real). Ahora
                se basa en quién sale de verdad en el horario PUBLICADO de este local — que es lo
                que pidió Fran: "que aparezcan los nombres de las personas que estén en ese
                horario". Reutiliza `empleadosConTurnoAqui`, la misma lista que ya usa la tabla de
                abajo, así las dos siempre están de acuerdo entre sí. */}
            <div style={{ ...card, minWidth: 0 }}>
              {seccion('👥', C.primaryFaint, `Plantilla de este local (${empleadosConTurnoAqui.length})`)}
              {empleadosConTurnoAqui.length === 0 ? (
                <EmptyState icon="👥" text="Todavía no hay un horario publicado con gente asignada a este local." />
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {empleadosConTurnoAqui.map(emp => (
                    <div key={emp.id} style={{ background: C.bg, borderRadius: '10px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <FichaPersona p={emp} size={32} />
                      <span style={{ fontWeight: '700', color: C.text, fontSize: '13px', flex: 1 }}>{emp.name}</span>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, background: C.white, border: `1px solid ${C.border}`, padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                        {emp.turnosSemanales || 5} contratados/sem
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: '800', color: C.primary, background: C.primaryPale, padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                        {turnosTrabajadosEsteMes(emp.id)} trabajados este mes
                      </span>
                    </div>
                  ))}
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: C.textMuted }}>
                    Los turnos trabajados este mes suman TODOS los locales de esa persona, no solo este — así cuadra con la nómina.
                  </p>
                </div>
              )}
            </div>

            {/* HORARIO */}
            <div style={{ ...card, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: C.primaryFaint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flexShrink: 0 }}><Ico n="calendar" /></div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: C.text }}>Horario de esta semana</h3>
                {publicadoDelLocal && empleadosConTurnoAqui.length > 0 && (
                  <button onClick={abrirHorarioPantallaCompleta} style={btn('outline', 'sm')}><Ico n="expand" /> Pantalla completa</button>
                )}
              </div>
              {!publicadoDelLocal || empleadosConTurnoAqui.length === 0 ? (
                <EmptyState icon="📅" text="Este local todavía no tiene un horario publicado." />
              ) : (
                <div className="horia-schedule-wrap" style={{ overflowX: 'auto' }}>
                  {tablaHorarioDelLocal()}
                </div>
              )}
            </div>

            {duenoHorarioPantallaCompleta && (
              <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: C.white, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
                <button onClick={cerrarHorarioPantallaCompleta} aria-label="Cerrar"
                  style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1, border: 'none', background: 'rgba(0,0,0,0.06)', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontSize: '16px', color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '30px 8px 8px' }}>
                  {tablaHorarioDelLocal(true)}
                </div>
              </div>
            )}

            {/* CARTA */}
            <div style={{ ...card, minWidth: 0 }}>
              {seccion('📋', C.primaryFaint, 'Carta')}
              {datosL.menuFotoUrl ? (
                <img src={datosL.menuFotoUrl} alt="Carta del restaurante"
                  style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '10px', border: `1px solid ${C.border}`, background: C.bg, display: 'block' }} />
              ) : (
                <EmptyState icon="📋" text="Este local todavía no tiene una foto de la carta." />
              )}
            </div>

            {/* MAPA DE MESAS */}
            <div style={{ ...card, minWidth: 0 }}>
              {seccion('🗺️', C.primaryFaint, 'Disposición de mesas')}
              {(datosL.elementos.length > 0 || datosL.paredes.length > 0) ? (
                <svg viewBox={`0 0 ${MAPA_VB_W} ${MAPA_VB_H}`}
                  style={{ width: '100%', aspectRatio: `${MAPA_VB_W} / ${MAPA_VB_H}`, display: 'block', background: C.white, borderRadius: '8px', border: `1px solid ${C.border}` }}>
              <DefsPlano />
                  {datosL.paredes.map((pared, i) => paredPlano(pared, i))}
                  {renderElementosMapa(datosL.elementos)}
                </svg>
              ) : (
                <EmptyState icon="🗺️" text="Este local todavía no tiene un plano de mesas." />
              )}
            </div>

          </div>
        </div>
      );
    }

    return (
      <div style={{ minHeight: '100vh', background: C.bg, overflowX: 'hidden' }}>
        {avisoCuentaUI}
        {/* Cabecera morada de siempre, con Salir */}
        <div style={{ background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, padding: '14px 24px', boxShadow: '0 4px 16px rgba(124,58,237,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px' }}>
          <h1 style={{ margin: 0 }}><Wordmark size={20} dark /></h1>
          <button onClick={handleLogout} style={{ ...btn('ghost'), color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)' }}>Salir</button>
        </div>

        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px 20px 32px' }}>

          {/* Hero: SOLO la foto del negocio, más grande. Se quitó a propósito el nombre y el
              precio/nº de empleados (pedido de Fran) — al Dueño no le interesa el coste mensual
              de la suscripción, solo cómo va su negocio, y verlo aquí podía agobiarle sin
              necesidad. Esa información sigue estando donde corresponde: dentro de "Mi plan",
              que ve el Encargado, no el Dueño. */}
          <div style={{
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, borderRadius: '18px', padding: '28px 24px',
            marginBottom: '18px', boxShadow: '0 10px 30px rgba(124,58,237,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {logoNegocio ? (
              <img src={logoNegocio} alt="Logo del negocio"
                style={{ height: '160px', width: 'auto', maxWidth: '90%', objectFit: 'contain', display: 'block' }} />
            ) : (
              <div style={{ width: '160px', height: '160px', background: 'rgba(255,255,255,0.18)', border: '2px solid rgba(255,255,255,0.35)', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '60px', color: C.white }}>
                <Ico n="utensils" />
              </div>
            )}
          </div>

          {/* TUS LOCALES (rediseñado 22 ago 2026): cada tarjeta es un local — su nombre y cuántos
              empleados salen de verdad en su horario PUBLICADO (no quién tiene el local marcado
              como fijo en su ficha, que es solo una preferencia para el generador). Al pulsar se
              abre el detalle completo de ese local. Con un solo local, `locales` siempre trae al
              menos uno (ver listaLocales), así que esto funciona igual de bien con 1 o con varios. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '18px' }}>
            {locales.map(l => {
              const enSuHorario = Object.keys(ultimaEntradaPorLocal[l.id]?.schedule || {}).length;
              return (
                <div key={l.id} onClick={() => { setDuenoLocalAbierto(l.id); setDuenoHorarioPantallaCompleta(false); }} role="button" tabIndex={0}
                  style={{ background: C.white, borderRadius: '14px', padding: '18px 16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.border}`, cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2px', padding: '4px', margin: '0 auto 8px', boxSizing: 'border-box' }}>
                    {['#FFFFFF', '#FDBA74', '#E9D5FF', '#FFFFFF'].map((c, i) => (
                      <div key={i} style={{ border: `1.3px solid ${c}`, borderRadius: '1.5px' }} />
                    ))}
                  </div>
                  <p style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '800', color: C.text }}>{l.nombre}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: C.textMuted, fontWeight: '700' }}>
                    {enSuHorario} empleado{enSuHorario === 1 ? '' : 's'}
                  </p>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'grid', gap: '16px' }}>

            <div style={card}>
              {seccion('💰', C.primaryFaint, 'Gastos')}
              {renderGastosPanel({ soloLectura: true })}
            </div>

            <div style={card} ref={duenoPlantillaRef}>
              {seccion('👥', C.primaryFaint, `Plantilla (${employees.length})`)}
            {/* Fila de un empleado en la Plantilla del Dueño, extraída a función para poder
                reutilizarla agrupada por local (ver más abajo) sin duplicar el JSX. */}
            {(() => {
              const renderFilaEmpleadoDueno = (emp) => {
                const enBaja = estaDeBajaEse(emp, hoyStr);
                const enVacaciones = !enBaja && estaAusenteEse(emp, hoyStr);
                return (
                  <div key={emp.id} style={{ background: C.bg, borderRadius: '10px', overflow: 'hidden' }}>
                    {/* La fila entera es pulsable: al tocarla se despliega el detalle de los
                        turnos de esa persona este mes, que es lo que hace falta para la nómina. */}
                    <div onClick={() => setDuenoEmpAbierto(duenoEmpAbierto === emp.id ? null : emp.id)}
                      style={{ padding: '10px 12px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <FichaPersona p={emp} size={36} />
                        <span style={{ fontWeight: '700', color: C.text, fontSize: '14px', flex: 1 }}>{emp.name}</span>
                        <span style={{ fontSize: '12px', color: C.textMuted, width: '14px', textAlign: 'center', flexShrink: 0 }}>
                          {duenoEmpAbierto === emp.id ? '▲' : '▼'}
                        </span>
                      </div>
                      {(emp.esGerente || emp.esEncargado || enBaja || enVacaciones) && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px', paddingLeft: '48px' }}>
                          {emp.esGerente && <Badge color="primary"><Ico n="briefcase" /> Encargado</Badge>}
                          {emp.esEncargado && <Badge color="warning"><Ico n="crown" /> Gerente</Badge>}
                          {enBaja && <Badge color="danger"><Ico n="baja" /> De baja</Badge>}
                          {enVacaciones && <Badge color="warning"><Ico n="sun" /> Vacaciones</Badge>}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingLeft: '48px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, background: C.white, border: `1px solid ${C.border}`, padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                          {emp.turnosSemanales || 5} contratados/sem
                        </span>
                        <span style={{ fontSize: '11px', fontWeight: '800', color: C.primary, background: C.primaryPale, padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                          {turnosTrabajadosEsteMes(emp.id)} trabajados este mes
                        </span>
                      </div>
                    </div>

                    {duenoEmpAbierto === emp.id && (() => {
                      const semanas = detalleTurnosEsteMes(emp.id);
                      const r = resumenTiposEsteMes(emp.id);
                      const quitados = turnosQuitadosEsteMes(emp);
                      return (
                        <div style={{ padding: '4px 12px 14px', borderTop: `1px solid ${C.border}` }}>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '12px 0' }}>
                            {[['Aperturas', r.apertura], ['Comidas', r.comida], ['Noches', r.noche], ['Partidos', r.partido], ['Enlaces', r.enlace]]
                              .filter(([, n]) => n > 0)
                              .map(([etiqueta, n]) => (
                                <span key={etiqueta} style={{ fontSize: '12px', fontWeight: '700', color: C.text, background: C.white, border: `1px solid ${C.border}`, padding: '5px 10px', borderRadius: '8px' }}>
                                  {etiqueta}: {n}
                                </span>
                              ))}
                          </div>

                          {semanas.length === 0 ? (
                            <p style={{ margin: 0, fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>
                              No ha trabajado ningún turno este mes.
                            </p>
                          ) : (
                            <div style={{ display: 'grid', gap: '10px' }}>
                              {semanas.map((sem, i) => (
                                <div key={i}>
                                  <p style={{ margin: '0 0 5px', fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Horario del {sem.fecha}
                                  </p>
                                  <div style={{ display: 'grid', gap: '4px' }}>
                                    {sem.dias.map(d => (
                                      <div key={d.day} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', background: C.white, padding: '7px 10px', borderRadius: '7px' }}>
                                        <span style={{ fontWeight: '700', color: C.text }}>{d.day}</span>
                                        <span style={{ color: C.textMuted }}>
                                          {shiftLabel(d.tipo)}{d.horaEntrada ? ` · ${d.horaEntrada}-${d.horaSalida || ''}` : ''}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {quitados.length > 0 && (
                            <p style={{ margin: '12px 0 0', fontSize: '12px', color: C.danger, fontWeight: '700' }}>
                              <Ico n="alert" /> {quitados.length} {quitados.length === 1 ? 'turno quitado' : 'turnos quitados'} este mes
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              };

              // AGRUPADO POR LOCAL (22 ago 2026, cambiado a "quién sale en el horario publicado"
              // en vez de "quién tiene el local marcado como fijo" — es lo que pidió Fran: que la
              // Plantilla refleje el horario real, no una preferencia de ficha). Con un solo
              // local no tiene sentido agrupar (sería un único grupo con todos), así que en ese
              // caso se deja la lista simple de siempre.
              if (!hayVariosLocales) {
                return employees.length === 0
                  ? <EmptyState icon="👥" text="Todavía no hay empleados dados de alta." />
                  : <div style={{ display: 'grid', gap: '8px' }}>{employees.map(renderFilaEmpleadoDueno)}</div>;
              }
              const idsConHorarioEnAlgunLocal = new Set(
                locales.flatMap(l => Object.keys(ultimaEntradaPorLocal[l.id]?.schedule || {}))
              );
              const sinHorarioAhoraMismo = employees.filter(e => !idsConHorarioEnAlgunLocal.has(String(e.id)));
              return employees.length === 0 ? (
                <EmptyState icon="👥" text="Todavía no hay empleados dados de alta." />
              ) : (
                <div style={{ display: 'grid', gap: '18px' }}>
                  {locales.map(l => {
                    const idsDeEsteLocal = Object.keys(ultimaEntradaPorLocal[l.id]?.schedule || {});
                    const miembros = idsDeEsteLocal.map(id => employees.find(e => String(e.id) === id)).filter(Boolean);
                    if (miembros.length === 0) return null;
                    return (
                      <div key={l.id}>
                        <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '800', color: C.primaryDark, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          <Ico n="pin" /> {l.nombre} ({miembros.length})
                        </p>
                        <div style={{ display: 'grid', gap: '8px' }}>{miembros.map(renderFilaEmpleadoDueno)}</div>
                      </div>
                    );
                  })}
                  {sinHorarioAhoraMismo.length > 0 && (
                    <div>
                      <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        <Ico n="refresh" /> Sin horario publicado ahora mismo ({sinHorarioAhoraMismo.length})
                      </p>
                      <div style={{ display: 'grid', gap: '8px' }}>{sinHorarioAhoraMismo.map(renderFilaEmpleadoDueno)}</div>
                    </div>
                  )}
                </div>
              );
            })()}
            </div>

            <div style={card} ref={duenoTurnosEliminadosRef}>
              {seccion('🔢', '#FEE2E2', 'Turnos eliminados')}
              {turnosEliminadosTotal.length === 0 ? <EmptyState icon="🔢" text="No se ha eliminado ningún turno." /> : (
                <div style={{ display: 'grid', gap: '6px' }}>
                  {turnosEliminadosTotal.slice(0, 30).map(tq => (
                    <div key={tq.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: C.bg, borderRadius: '8px', fontSize: '13px' }}>
                      <span style={{ color: C.text, fontWeight: '600' }}>{tq.empleado}</span>
                      <span style={{ color: C.textMuted }}>{tq.turno} · {tq.fecha}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={card} ref={duenoHistorialRef}>
              {seccion('🕓', C.primaryFaint, `Historial de horarios (${historialOrdenado.length})`)}
              {historialOrdenado.length === 0 ? <EmptyState icon="🕓" text="Todavía no se ha publicado ningún horario." /> : (
                <div style={{ display: 'grid', gap: '6px' }}>
                  {historialOrdenado.slice(0, 20).map((h) => {
                    const key = h.localId || LOCAL_PRINCIPAL;
                    const esActual = ultimaEntradaPorLocal[key]?.id === h.id;
                    const abierto = duenoHistorialAbierto === h.id;
                    // Si es la entrada vigente de su local, se pinta con los datos EN VIVO
                    // (schedulesPorLocal/employees/specialDays), que se actualizan solos en
                    // tiempo real vía Firestore. Si no, se pinta con la foto fija de cuando se
                    // publicó (eso es lo correcto para historial antiguo, que no debe cambiar).
                    const scheduleAMostrar = esActual ? (schedulesPorLocal[key] || h.schedule) : h.schedule;
                    const specialDaysAMostrar = esActual ? specialDays : h.specialDays;
                    const empleadosAMostrar = esActual ? employees : (h.employeesSnapshot || []);
                    return (
                      <div key={h.id} style={{
                        borderRadius: '8px', overflow: 'hidden',
                        background: esActual ? C.primaryPale : C.bg,
                        border: esActual ? `1px solid ${C.primaryBorder}` : '1px solid transparent',
                      }}>
                        <div onClick={() => setDuenoHistorialAbierto(abierto ? null : h.id)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', fontSize: '13px', cursor: 'pointer' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: C.text, fontWeight: '600' }}>Publicado el {h.publishedAt}</span>
                            {h.localNombre && hayVariosLocales && <Badge color="primary">{h.localNombre}</Badge>}
                            {esActual && <Badge color="primary">Horario actual</Badge>}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ color: C.textMuted }}>{h.temporadaNombre}</span>
                            <span style={{ fontSize: '12px', color: C.textMuted, width: '14px', textAlign: 'center' }}>{abierto ? '▲' : '▼'}</span>
                          </span>
                        </div>

                        {abierto && (
                          <div className="horia-schedule-wrap" style={{ padding: '0 8px 12px', overflowX: 'auto' }}>
                            <table className="horia-schedule-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                              <thead>
                                <tr>
                                  <th className="horia-sticky-col" style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', color: C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, minWidth: '130px', background: esActual ? C.primaryPale : C.bg }}>Empleado</th>
                                  {DAYS.map(d => (
                                    <th key={d} style={{ padding: '8px 5px', textAlign: 'center', fontSize: '11px', color: specialDaysAMostrar?.[d] ? '#92400E' : C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap', background: specialDaysAMostrar?.[d] ? '#FEF9E7' : 'transparent' }}>
                                      {d.substring(0,3)}{specialDaysAMostrar?.[d] && conIconos(' ⭐')}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {empleadosAMostrar.map((emp, idx) => (
                                  <tr key={emp.id} style={{ background: idx % 2 === 0 ? C.white : 'rgba(255,255,255,0.5)' }}>
                                    <td className="horia-sticky-col" style={{ padding: '7px 10px', fontWeight: '600', fontSize: '12px', color: C.text, borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? C.white : '#FBFAFE' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                        <FichaPersona p={emp} size={26} />
                                        <div>{emp.name}{emp.esGerente ? conIconos(' 💼') : ''}{emp.esEncargado ? conIconos(' 👑') : ''}</div>
                                      </div>
                                    </td>
                                    {DAYS.map(day => {
                                      const s = scheduleAMostrar?.[emp.id]?.[day] || { tipo: 'libre', tareas: [] };
                                      return (
                                        <td key={day} style={{ padding: '3px', textAlign: 'center', borderBottom: `1px solid ${C.border}` }}>
                                          <CeldaTurno s={s} conf={config} tam="compacta" />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>

          <p style={{ fontSize: '11px', color: C.textLight, textAlign: 'center', margin: '20px 0 0' }}>
            Vista de solo consulta — los cambios de horario y plantilla los gestiona el Encargado.
          </p>
        </div>
      </div>
    );
  }

  if (userRole === 'jefe' && showConfig) return renderConfiguracion();
  if (userRole === 'jefe' && showMenuMapa) return renderMenuMapa();

  if (userRole === 'jefe') return (
    <div style={{ minHeight: '100vh', background: `radial-gradient(ellipse 80% 50% at 85% 105%, rgba(20,184,166,0.30), transparent 60%), radial-gradient(ellipse 60% 40% at 100% -5%, rgba(45,212,191,0.18), transparent 55%), ${D.deep}` }}>
      <Header />
      <OfertaHuellaModal />
      {renderMiPin()}
      {bienvenidaUI || avisoCuentaUI}
      {falloUI}
      <DialogoHoria />
      {/* Burbuja de ENLACE: aparece al pulsar el botón 🔗 Enlace de la ficha de un empleado.
          Permite poner la hora de entrada y de salida de ESA persona (o quitarle el Enlace).
          Solo afecta a los horarios que se generen después: los ya generados se cambian a mano. */}
      {enlaceModal && (() => {
        const empM = employees.find(e => e.id === enlaceModal.empId);
        if (!empM) return null;
        const Hg = horariosGenerales(config);
        const guardarEnlace = () => {
          setEmployees(prev => prev.map(e => e.id === enlaceModal.empId
            ? (enlaceModal.propio
              ? { ...e, hacesEnlace: true, enlaceHorarioPropio: true, horaEntradaEnlace: enlaceModal.entrada, horaSalidaEnlace: enlaceModal.salida }
              : { ...e, hacesEnlace: true, enlaceHorarioPropio: false })
            : e));
          setEnlaceModal(null);
        };
        const entradaVista = enlaceModal.propio ? enlaceModal.entrada : Hg.enlaceIni;
        const salidaVista = enlaceModal.propio ? enlaceModal.salida : Hg.enlaceFin;
        const durVista = duracionEntre(entradaVista, salidaVista);
        const pesoVista = pesoEnlaceDeHoras(entradaVista, salidaVista, Hg.dur);
        const contrato = empM.turnosSemanales || 5;
        const quitarEnlace = () => {
          setEmployees(prev => prev.map(e => e.id === enlaceModal.empId ? { ...e, hacesEnlace: false } : e));
          setEnlaceModal(null);
        };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}
            onClick={() => setEnlaceModal(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: '1px solid #86EFAC', borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.35)' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '800', color: C.text }}><Ico n="link" /> Enlace de {empM.name}</h3>
              <p style={{ margin: '0 0 14px', fontSize: '12.5px', color: C.textMuted, lineHeight: 1.5 }}>
                El Enlace hace de puente entre el almuerzo y la cena: entra a media tarde y se queda hasta el cierre. Esta persona solo hará turnos de Enlace.
              </p>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', background: C.bg, borderRadius: '10px', padding: '4px' }}>
                {[{ v: false, t: 'Horario general' }, { v: true, t: 'Horario propio' }].map(o => (
                  <button key={String(o.v)} onClick={() => setEnlaceModal(m => ({ ...m, propio: o.v, entrada: o.v ? m.entrada : Hg.enlaceIni, salida: o.v ? m.salida : Hg.enlaceFin }))}
                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer',
                      background: enlaceModal.propio === o.v ? C.white : 'transparent', color: enlaceModal.propio === o.v ? '#166534' : C.textMuted,
                      boxShadow: enlaceModal.propio === o.v ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.2s ease' }}>{o.t}</button>
                ))}
              </div>
              {enlaceModal.propio ? (
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <TimeSelect label="Entrada" value={enlaceModal.entrada} options={OPCIONES_HORA}
                    onChange={v => setEnlaceModal(m => ({ ...m, entrada: v }))} />
                  <TimeSelect label="Salida" value={enlaceModal.salida} options={OPCIONES_HORA}
                    onChange={v => setEnlaceModal(m => ({ ...m, salida: v }))} />
                </div>
              ) : (
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.text, fontWeight: '700' }}>{Hg.enlaceIni} – {Hg.enlaceFin} <span style={{ fontWeight: '500', color: C.textMuted, fontSize: '12px' }}>(el de Configuración)</span></p>
              )}
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '10px 12px', marginBottom: '18px', fontSize: '12.5px', color: '#166534', lineHeight: 1.5 }}>
                <b>{formatoDuracion(durVista)}</b>{durVista > minutosTrabajoEnlace(entradaVista, salidaVista) ? ` (con ${formatoDuracion(durVista - minutosTrabajoEnlace(entradaVista, salidaVista))} de descanso)` : ''} · cuenta como <b>{pesoVista} {pesoVista === 1 ? 'turno' : 'turnos'}</b> (turnos de {formatoDuracion(Hg.dur)}).
                {' '}Con {contrato} turnos contratados hará <b>{Math.floor(contrato / pesoVista)} {Math.floor(contrato / pesoVista) === 1 ? 'Enlace' : 'Enlaces'}</b> a la semana.
                {contrato % pesoVista !== 0 && <span style={{ display: 'block', marginTop: '4px', color: '#92400E' }}>Ojo: {contrato} no es múltiplo de {pesoVista}; le sobraría {contrato % pesoVista === 1 ? '1 turno' : `${contrato % pesoVista} turnos`} que no caben en un Enlace. Ponle {Math.floor(contrato / pesoVista) * pesoVista} o {(Math.floor(contrato / pesoVista) + 1) * pesoVista} turnos.</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={guardarEnlace} style={{ ...btn('primary'), flex: '1 1 120px', justifyContent: 'center', background: '#16A34A' }}>Guardar</button>
                <button onClick={() => setEnlaceModal(null)} style={{ ...btn('outline'), flex: '1 1 90px', justifyContent: 'center' }}>Cancelar</button>
              </div>
              {empM.hacesEnlace && (
                <button onClick={quitarEnlace} style={{ width: '100%', marginTop: '10px', padding: '9px', background: 'transparent', border: 'none', color: C.danger, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  Quitar Enlace a {empM.name}
                </button>
              )}
            </div>
          </div>
        );
      })()}
      {renderAsistenteHoria()}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '18px 16px' }}>

        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '16px', padding: '14px 16px', marginBottom: '12px', background: `linear-gradient(135deg, ${C.primaryFaint}, ${C.white})`, border: `1px solid ${C.primaryBorder}`, boxShadow: '0 4px 16px rgba(124,58,237,0.08)' }}>
          {/* Logo y textos CENTRADOS y algo más compactos (2 oct 2026, pedido por Fran): así la
              rejilla de pestañas de debajo sigue entrando entera en la pantalla al abrir la app. */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center' }}>
            {logoNegocio && (
              <img src={logoNegocio} alt={config.restaurantName || 'Logo del negocio'} style={{ height: '76px', maxWidth: '220px', objectFit: 'contain', flexShrink: 0, borderRadius: '8px' }} />
            )}
            <div style={{ width: '100%' }}>
              <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '1px' }}>Tu negocio</p>
              {/* Solo texto (29 sep 2026, a petición de Fran): antes se podía escribir aquí
                  directamente, pero prefiere que el nombre se edite solo desde Configuración,
                  con su propio botón de guardar — ver renderConfiguracion. */}
              <p style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: C.text }}>{config.restaurantName || 'Tu negocio'}</p>
              <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>
                Apertura {config.aperturaHora}{temporadas[temporada]?.nombre ? <> · <strong style={{ color: C.primaryDark }}>{temporadas[temporada].nombre}</strong></> : null}
              </p>
            </div>
          </div>

          {/* Fila de acciones del negocio, separada de la fila del nombre para que nunca compitan
              por espacio horizontal (antes se cortaba "Configuración" en pantallas estrechas).
              "Mi plan" lleva un color distinto (cian) a propósito, para diferenciarlo del resto:
              es la única acción que toca dinero/facturación. Configuración y Carta y Mesas abren
              su propia pantalla completa (no expanden aquí) para no obligar a bajar y subir.
              GRID fijo (no flex-wrap) a propósito (20 sep 2026): con flex-wrap, cuántos botones
              caben por fila depende del ancho REAL disponible en ese móvil, y ese ancho cambia
              con el "Tamaño de pantalla" que cada persona tenga puesto en Ajustes de Android (es
              un zoom de toda la interfaz, no solo del texto) — así, el mismo teléfono podía
              mostrar 3 en fila, 2+1, o los 3 apilados según el ajuste de cada usuario, algo
              inconsistente y sin control. Primero se probó a fijar las 3 columnas siempre en la
              misma fila, pero con "Carta y Mesas"/"Configuración" el texto se partía en dos
              líneas y quedaba feo — 3 en fila no cabe cómodo en un móvil normal. Se fija en su
              lugar el patrón 2+1 (Mi plan y Carta y Mesas en fila, Configuración debajo a todo lo
              ancho), que es el que sí entra holgado con el texto en una sola línea — y sigue
              siendo SIEMPRE el mismo patrón en cualquier móvil, por la misma razón de antes (grid
              fijo, no depende del ancho disponible). */}
          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${C.primaryBorder}` }}>
            {!isCocina && (
              <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <button onClick={() => { setShowPlanes(!showPlanes); setShowConfig(false); }}
                  style={{ ...btn('outline', 'md'), border: `1px solid ${C.cyanBorder}`, color: C.cyanDark, background: C.cyanBg, justifyContent: 'center', minWidth: 0 }}>
                  <Ico n="card" /> {showPlanes ? 'Cerrar' : 'Mi plan'}
                </button>
                <button onClick={() => setShowMenuMapa(true)}
                  style={{ ...btn('outline', 'md'), border: `1px solid ${C.primaryBorder}`, color: C.primary, background: C.white, justifyContent: 'center', minWidth: 0 }}>
                  <Ico n="utensils" /> Carta y Mesas
                </button>
              </div>
            )}
            <button onClick={() => setShowConfig(true)}
              style={{ ...btn('outline', 'md'), border: `1px solid ${C.primaryBorder}`, color: C.primary, background: C.white, justifyContent: 'center', width: '100%' }}>
              <Ico n="gear" /> Configuración
            </button>
          </div>
        </div>

        {showPlanes && !isCocina && (
          <div style={{ ...card, border: `1px solid ${C.primaryBorder}`, background: C.primaryFaint }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '700', color: C.primaryDark }}><Ico n="card" /> Mi plan</h3>

            {/* Precio de este mes, calculado según la plantilla actual. Todo el mundo tiene
                acceso a todas las funciones — ya no hay planes distintos, se paga por empleado. */}
            <div style={{ padding: '18px', background: C.white, borderRadius: '10px', border: `2px solid ${C.primary}`, marginBottom: '16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '1px' }}>Precio de este mes</p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <span style={{ fontSize: '28px', fontWeight: '900', color: C.primary }}>{calcularPrecioMensual(employees.length + (numEmpleadosCocinaPlan || 0)).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                <span style={{ fontSize: '12px', color: C.textMuted }}>+ IVA</span>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '12px', color: C.textMuted }}>
                {numEmpleadosCocinaPlan === null
                  ? `${employees.length} ${employees.length === 1 ? 'persona' : 'personas'} en Sala (no se ha podido sumar Cocina)`
                  : numEmpleadosCocinaPlan > 0
                    ? `${employees.length} en Sala + ${numEmpleadosCocinaPlan} en Cocina = ${employees.length + numEmpleadosCocinaPlan} personas`
                    : `${employees.length} ${employees.length === 1 ? 'persona' : 'personas'} en tu plantilla`}
              </p>
              <div style={{ fontSize: '12px', color: C.textMuted }}>
                <DesglosePrecioBadges />
                {subtotalPorTramos(employees.length + (numEmpleadosCocinaPlan || 0)) < PRECIO_MINIMO_MENSUAL && <div style={{ marginTop: '6px', fontWeight: '700', fontSize: '11px' }}>Mínimo de {PRECIO_MINIMO_MENSUAL} €/mes + IVA por cliente</div>}
              </div>
            </div>

            {/* ESTADO DE LA CUENTA (19 oct 2026): hasta cuándo dura la prueba o el mes pagado. */}
            {(estadoCuentaActual.estado === 'prueba' || estadoCuentaActual.estado === 'pagada') && estadoCuentaActual.ultimoDia && (
              <div style={{ padding: '12px 14px', background: C.white, borderRadius: '10px', border: `1px solid ${C.primaryBorder}`, marginBottom: '16px', fontSize: '13px', color: C.text, lineHeight: 1.5 }}>
                {estadoCuentaActual.estado === 'prueba' ? 'Tu prueba gratuita' : 'Tu suscripción'} dura hasta el <strong>{fechaLargaCuenta(estadoCuentaActual.ultimoDia)}</strong>
                {estadoCuentaActual.quedan === 1 ? ' (hoy es el último día).' : ` (quedan ${estadoCuentaActual.quedan} días).`}
              </div>
            )}

            {/* ACCESO DEL DUEÑO (29 oct 2026). Solo la cuenta principal del Encargado (la que creó el
                negocio): el código le llega por correo y nunca se enseña en pantalla. */}
            {userRole === 'jefe' && firebaseUser?.uid === restaurantDocId && (
              <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.primaryBorder}`, marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '800', color: C.text }}>🔑 Acceso del Dueño</h4>
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                  El dueño del negocio puede tener su propia cuenta de consulta (plan, plantilla, turnos y gastos). Para crearla necesita el <strong style={{ color: C.text }}>código de Dueño</strong>, que te llega a ti por correo. Es privado: dáselo solo a quien deba tenerlo, nunca a los empleados.
                </p>
                <button type="button" onClick={pedirCodigoDueno} disabled={codigoDuenoBusy}
                  style={{ ...btn('outline', 'md'), width: '100%', justifyContent: 'center', border: `1px solid ${C.primaryBorder}`, color: C.primary, background: C.white, opacity: codigoDuenoBusy ? 0.6 : 1 }}>
                  {codigoDuenoBusy ? 'Enviando…' : 'Enviarme un código de Dueño nuevo'}
                </button>
              </div>
            )}

            {/* INVITA A OTRO RESTAURANTE (19 oct 2026). Solo el Encargado. */}
            {userRole === 'jefe' && (
              <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.primaryBorder}`, marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: 'linear-gradient(135deg, #F59E0B, #D97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3.5" y="8.5" width="17" height="4" rx="1" /><path d="M5 12.5v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7M12 8.5v12" />
                      <path d="M12 8.5c-1.5-3.5-5.5-4-5.5-1.5 0 1.5 2.5 1.5 5.5 1.5zM12 8.5c1.5-3.5 5.5-4 5.5-1.5 0 1.5-2.5 1.5-5.5 1.5z" />
                    </svg>
                  </div>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: C.text }}>Invita a otro restaurante</h4>
                </div>
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                  Pásale un código a otro restaurante. Si crea su cuenta con él, te regalamos 15 días gratis para agradecértelo. Cada código sirve una sola vez y caduca a los 15 días.
                </p>
                {invitacionesError && <p style={{ margin: '0 0 10px', fontSize: '12.5px', fontWeight: '600', color: C.danger }}>{invitacionesError}</p>}
                {invitaciones && invitaciones.codigos.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                    {invitaciones.codigos.map(inv => {
                      const dias = inv.caducaEn ? Math.max(0, Math.ceil((Date.parse(inv.caducaEn) - Date.now()) / 86400000)) : null;
                      const activo = inv.estado === 'activo';
                      return (
                        <div key={inv.codigo} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: activo ? C.primaryFaint : '#F9FAFB', border: `1px solid ${activo ? C.primaryBorder : C.border}` }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '16px', fontWeight: '800', letterSpacing: '2px', color: activo ? C.primaryDark : C.textLight, textDecoration: inv.estado === 'caducado' ? 'line-through' : 'none' }}>{inv.codigo}</div>
                            <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
                              {inv.estado === 'usado' ? <>Lo ha usado <strong style={{ color: C.text }}>{inv.usadoNombre}</strong>{inv.regalo ? ' · 15 días regalados' : ''}</>
                                : inv.estado === 'caducado' ? 'Caducado'
                                : dias === 1 ? 'Sin usar · caduca mañana' : `Sin usar · caduca en ${dias} días`}
                            </div>
                          </div>
                          {activo && (
                            <button type="button" onClick={() => compartirInvitacion(inv.codigo)}
                              style={{ ...btn('outline', 'md'), border: `1px solid ${C.primaryBorder}`, color: C.primary, background: C.white, whiteSpace: 'nowrap', padding: '8px 12px' }}>
                              {invitacionCopiada === inv.codigo ? '¡Copiado!' : invitacionCopiada === `${inv.codigo}-error` ? 'Cópialo a mano' : 'Compartir'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {invitaciones === null && !invitacionesError && <p style={{ margin: '0 0 12px', fontSize: '12.5px', color: C.textMuted }}>Cargando tus códigos…</p>}
                {invitaciones && (
                  invitaciones.puedeCrear ? (
                    <button type="button" onClick={crearCodigoInvitacion} disabled={invitacionBusy}
                      style={{ ...btn('primary', 'md'), width: '100%', justifyContent: 'center', opacity: invitacionBusy ? 0.6 : 1 }}>
                      {invitacionBusy ? 'Creando…' : 'Crear código de invitación'}
                    </button>
                  ) : (
                    <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>Tienes {invitaciones.maxSinUsar} códigos sin usar. Cuando se use o caduque alguno podrás crear otro.</p>
                  )
                )}
              </div>
            )}

            {/* REPORTAR UN FALLO (25-26 oct 2026, pedido por Fran). Abre la ventana de fallos de la
                propia app (falloUI, arriba): nada de abrir el correo. El correo se enseña escrito
                por si alguien prefiere mandar una captura de pantalla. */}
            <div style={{ padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.primaryBorder}`, marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '800', color: C.text }}><Ico n="alert" /> ¿Algo no funciona bien?</h4>
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                Cuéntanos qué ha pasado y lo arreglaremos cuanto antes.
              </p>
              <button type="button" onClick={abrirFallo}
                style={{ ...btn('primary', 'md'), width: '100%', justifyContent: 'center' }}>
                Reportar un fallo
              </button>
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: C.textMuted, textAlign: 'center' }}>
                Si quieres mandarnos una captura: <strong style={{ color: C.text }}>horidiapp@gmail.com</strong>
              </p>
            </div>

            {/* Cambiar de plan / Baja, uno al lado del otro */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: '220px', padding: '16px', background: 'rgba(34,211,238,0.12)', borderRadius: '10px', border: '1px solid #22D3EE' }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: '#0E7490' }}>Cambiar mi plan</p>
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#0E7490', lineHeight: 1.5 }}>
                  Ajusta el número de empleados y consulta el precio al momento.
                </p>
                <button onClick={() => { setShowPlanes(false); setShowPlans(true); }}
                  style={{ padding: '11px 18px', borderRadius: '8px', border: 'none', background: '#06B6D4', color: C.white, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  Cambiar mi plan
                </button>
              </div>

              {config.bajaSolicitada ? (
                <div style={{ flex: '1 1 220px', minWidth: '220px', padding: '16px', background: 'rgba(251,191,36,0.14)', borderRadius: '10px', border: '1px solid #F59E0B' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#FBBF24' }}><Ico n="alert" /> Has solicitado darte de baja</p>
                  <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#FBBF24', lineHeight: 1.6 }}>
                    Solicitud registrada el {new Date(config.bajaSolicitada).toLocaleDateString('es-ES')}.
                    Mantendrás el acceso completo hasta que termine el periodo que ya has pagado. Después,
                    tus datos se conservarán 30 días por si cambias de idea, y pasado ese plazo se eliminarán
                    de forma definitiva.
                  </p>
                  <button onClick={() => setConfig(c => ({ ...c, bajaSolicitada: null }))}
                    style={{ ...btn('primary', 'sm') }}>
                    Anular la baja y seguir con Horidia
                  </button>
                </div>
              ) : (
                <div style={{ flex: '1 1 220px', minWidth: '220px', padding: '16px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}` }}>
                  <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}>Darse de baja</p>
                  <p style={{ margin: '0 0 12px', fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>
                    Sin permanencia y sin dar explicaciones. Conservas el acceso hasta que acabe el mes que
                    ya has pagado, y puedes anular la baja mientras tanto.
                  </p>
                  <button onClick={async () => {
                    const ok = await confirmar({
                      icono: '👋',
                      titulo: '¿Solicitar la baja de Horidia?',
                      lineas: [
                        'Mantendrás el acceso hasta que termine el periodo que ya has pagado, y no se te volverá a cobrar.',
                        'Tus datos se guardarán 30 días más por si cambias de idea, y luego se borrarán definitivamente.',
                        'Puedes anular la baja desde aquí mismo mientras tanto.',
                      ],
                      textoOk: 'Solicitar la baja',
                      textoCancelar: 'Seguir con Horidia',
                      peligro: true,
                    });
                    if (ok) setConfig(c => ({ ...c, bajaSolicitada: new Date().toISOString() }));
                  }} style={{ padding: '11px 18px', borderRadius: '8px', border: `1px solid ${C.danger}`, background: C.white, color: C.danger, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                    Solicitar la baja
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="horia-tabs-grid hd-tiles">
          {[
            { tab: 'empleados', icon: 'empleados', label: `${L.empleadosTabLabel} (${employees.length})` },
            { tab: 'horario', icon: 'horario', label: 'Horario' },
            { tab: 'solicitudes', icon: 'solicitudes', label: 'Solicitudes', badge: pendingRequests.length },
            { tab: 'vacaciones', icon: 'vacaciones', label: 'Vacaciones' },
            { tab: 'bajas', icon: 'bajas', label: 'Bajas' },
            { tab: 'turnos', icon: 'turnos', label: 'Turnos' },
            { tab: 'chat', icon: 'chat', label: 'Chat', badge: mensajesSinLeer },
            { tab: 'historial', icon: 'historial', label: `Historial (${scheduleHistory.length})` },
            { tab: 'gastos', icon: 'gastos', label: 'Gastos' },
          ].map(t => (
            <button key={t.tab} onClick={() => setActiveTab(t.tab)} className={`hd-tile${activeTab === t.tab ? ' hd-tile-on' : ''}`} style={encargadoTabTile(t.tab)}>
              <TabIcon name={t.icon} size={24} color={activeTab === t.tab ? undefined : TAB_ICON_COLORS[t.icon]} />
              <span>{t.label}</span>
              <TabBadge n={t.badge || 0} />
            </button>
          ))}
        </div>

        {/* Envoltorio con "key" por pestaña (9 oct 2026): al cambiar de pestaña se vuelve a
            montar y el contenido entra con un fundido suave (clase hd-tab-enter). Dentro solo hay
            contenido de pestañas, nada que tenga que sobrevivir al cambio. */}
        <div key={`pestana-${activeTab}`} className="hd-tab-enter">
        {activeTab === 'empleados' && (
          <div>
            {(() => {
              const sinPin = employees.filter(e => !e.authEmail);
              if (sinPin.length === 0) return null;
              return (
                <div style={{ ...card, background: '#FFEDD5', border: '1.5px solid #FB923C' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}><Ico n="lock" /></div>
                    <div>
                      <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '800', color: '#C2410C' }}>
                        {sinPin.length} {sinPin.length === 1 ? 'persona no tiene' : 'personas no tienen'} PIN todavía
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#9A3412', lineHeight: 1.5 }}>
                        Crea un PIN a cada empleado desde su ficha, más abajo. Es necesario para la privacidad de cada persona: sin PIN, cualquiera puede ver sus datos personales.
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9A3412', lineHeight: 1.5 }}>
                        Sin PIN: <strong>{sinPin.map(e => e.name).join(', ')}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: 0 }}>{conIconos(L.addEmployeeTitle)}</h2>
                <Badge color={employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE ? 'danger' : 'muted'}>
                  {employees.length} {employees.length === 1 ? 'empleado' : 'empleados'}
                </Badge>
              </div>
              {employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE && (
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.danger, fontWeight: '600' }}>
                  Has llegado al máximo de {MAX_EMPLEADOS_POR_RESTAURANTE} empleados por restaurante. Escríbenos si necesitas más.
                </p>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" maxLength={40} value={newEmployeeName} onChange={e => setNewEmployeeName(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && addEmployee()} placeholder={L.addEmployeePlaceholder}
                  disabled={employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE}
                  style={{ flex: 1, padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', opacity: employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE ? 0.5 : 1 }} />
                <button onClick={addEmployee} disabled={employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE}
                  style={{ ...btn('primary'), opacity: employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE ? 0.5 : 1, cursor: employees.length >= MAX_EMPLEADOS_POR_RESTAURANTE ? 'not-allowed' : 'pointer' }}>
                  Añadir
                </button>
              </div>
            </div>

            {employees.length === 0 ? <div style={card}><EmptyState icon="👥" text={L.emptyEmployeesText} /></div> : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {/* Orden de la lista (9 oct 2026, pedido por Fran): primero el Gerente, luego los
                    Encargados y después el resto. Solo cambia cómo se MUESTRA: la lista guardada
                    (y por tanto el generador de horarios) sigue en el mismo orden de siempre. */}
                {[...employees].sort((a, b) => rangoListaEmpleado(a) - rangoListaEmpleado(b)).map(emp => {
                  const isOpen = !!expandedEmployees[emp.id];
                  return (
                    <div key={emp.id} style={{ ...card, marginBottom: 0, padding: isOpen ? '20px' : '14px 18px', border: `1px solid ${isOpen ? C.primaryBorder : C.border}`, transition: 'border-color 0.15s ease' }}>
                      <div onClick={() => toggleEmployeeExpanded(emp.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                          <FichaPersona p={emp} size={42} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}>{emp.name}</p>
                              {emp.esGerente && <Badge color="primary"><Ico n="briefcase" /> Encargado</Badge>}
                              {emp.esEncargado && <Badge color="warning">{isCocina ? conIconos(L.jefeBadge) : conIconos('👑 Gerente')}</Badge>}
                              {estaDeBajaEse(emp, toDateStr(new Date())) && <Badge color="danger"><Ico n="baja" /> De baja</Badge>}
                              {!estaDeBajaEse(emp, toDateStr(new Date())) && estaAusenteEse(emp, toDateStr(new Date())) && <Badge color="warning"><Ico n="sun" /> Vacaciones</Badge>}
                              {/* Selector de local, visible sin tener que desplegar la ficha.
                                  Solo aparece si el negocio tiene más de uno. Se para el clic
                                  (stopPropagation) para que elegir un local no despliegue o
                                  pliegue la ficha entera de golpe. */}
                              {hayVariosLocales && (
                                <select value={emp.localFijo || ''}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => updateEmployee(emp.id, 'localFijo', e.target.value)}
                                  style={{ fontSize: '11px', fontWeight: '700', padding: '3px 7px', borderRadius: '7px', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, cursor: 'pointer' }}>
                                  <option value="">Rota</option>
                                  {locales.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                                </select>
                              )}
                            </div>
                            <p style={{ margin: '3px 0 0', fontSize: '12px', color: C.textMuted }}>
                              {emp.turnosSemanales || 5} turnos/semana{(emp.diasFijosLibres || []).length > 0 ? conIconos(` · 🔒 ${emp.diasFijosLibres.length} día(s) fijo(s) libre(s)`) : ''}
                            </p>
                          </div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); toggleEmployeeExpanded(emp.id); }}
                          style={{ width: '34px', height: '34px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: isOpen ? C.primary : C.white, color: isOpen ? C.white : C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease' }}>
                          <span style={{ fontSize: '12px', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', display: 'inline-block' }}>▼</span>
                        </button>
                      </div>

                      {isOpen && (
                        <div className="hd-expand" style={{ marginTop: '16px' }}>
                          {/* Fila de acciones. La fecha de alta se quitó de aquí: es dato de
                              consulta, no de gestión diaria, y robaba el sitio a los botones.
                              Gerente va en NARANJA (con poder de gestión, solo lo activa el
                              dueño de la cuenta) y Encargado en MORADO (solo etiqueta, sin
                              poder) para que se distingan de un vistazo; PIN y borrar pasan a
                              icono, porque se usan poco. */}
                          <div style={{ display: 'flex', gap: '7px', marginBottom: '14px', flexWrap: 'wrap' }}>
                            {/* Orden (9 oct 2026, pedido por Fran): Gerente - Encargado - Enlace. */}
                            <button onClick={() => esDuenoReal && updateEmployee(emp.id, 'esEncargado', !emp.esEncargado)}
                              disabled={!esDuenoReal}
                              title={esDuenoReal ? '' : 'Solo el dueño de la cuenta puede conceder o quitar este permiso'}
                              style={{ flex: '1 1 90px', padding: '9px 8px', borderRadius: '9px', fontSize: '12px', fontWeight: '700',
                                border: emp.esEncargado ? 'none' : `1px solid ${C.warningBorder || '#FCE9CF'}`,
                                background: emp.esEncargado ? C.accent : '#FFFBF3',
                                color: emp.esEncargado ? C.white : C.accent,
                                opacity: esDuenoReal ? 1 : 0.45, cursor: esDuenoReal ? 'pointer' : 'not-allowed' }}>
                              {isCocina ? (emp.esEncargado ? conIconos(L.jefeBadge) : L.jefeRoleName) : (emp.esEncargado ? conIconos('👑 Gerente') : 'Gerente')}
                            </button>
                            {!isCocina && (
                              <button onClick={() => updateEmployee(emp.id, 'esGerente', !emp.esGerente)}
                                style={{ flex: '1 1 90px', padding: '9px 8px', borderRadius: '9px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                                  border: emp.esGerente ? 'none' : `1px solid ${C.primaryBorder}`,
                                  background: emp.esGerente ? C.primary : C.white,
                                  color: emp.esGerente ? C.white : C.primary }}>
                                {emp.esGerente ? conIconos('💼 Encargado') : 'Encargado'}
                              </button>
                            )}
                            {/* ENLACE (2 oct 2026, pedido por Fran): quien tiene esto marcado SOLO
                                hace turnos de Enlace — el generador nunca le da apertura/comida/
                                noche/partido, ver más abajo en generateSchedule. Editable por el
                                Encargado normal, igual que Gerente — no hace falta ser el dueño de
                                la cuenta para esto. */}
                            <button onClick={() => {
                                const H = horariosGenerales(config);
                                const h = horasDeTipo('enlace', emp, config);
                                setEnlaceModal({ empId: emp.id, propio: usaEnlacePropio(emp, H), entrada: h.horaEntrada, salida: h.horaSalida });
                              }}
                              title="Esta persona SOLO hará turnos de Enlace — nunca apertura, comida, cena o partido. Pulsa para elegir su horario."
                              style={{ flex: '1 1 90px', padding: '9px 8px', borderRadius: '9px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                                border: emp.hacesEnlace ? 'none' : '1px solid #86EFAC',
                                background: emp.hacesEnlace ? '#16A34A' : '#F0FDF4',
                                color: emp.hacesEnlace ? C.white : '#166534' }}>
                              <div><Ico n="link" /> Enlace</div>
                              {emp.hacesEnlace && (
                                <div style={{ fontSize: '10px', fontWeight: '600', opacity: 0.9, marginTop: '2px' }}>
                                  {(() => { const h = horasDeTipo('enlace', emp, config); return `${h.horaEntrada} - ${h.horaSalida} · ${pesoEnlace(emp, config)} turnos`; })()}
                                </div>
                              )}
                            </button>
                            <button onClick={() => crearPinEmpleado(emp)}
                              title={emp.authEmail ? 'Renovar PIN' : 'Crear PIN'}
                              style={{ width: '40px', padding: '9px 0', borderRadius: '9px', fontSize: '14px', cursor: 'pointer',
                                border: emp.authEmail ? `1px solid ${C.primaryBorder}` : 'none',
                                background: emp.authEmail ? C.white : C.primary }}>
                              <Ico n="key" />
                            </button>
                            <button onClick={() => deleteEmployee(emp.id)} title="Eliminar"
                              style={{ width: '40px', padding: '9px 0', borderRadius: '9px', fontSize: '14px', cursor: 'pointer', border: `1px solid ${C.dangerBg}`, background: C.dangerBg }}>
                              <Ico n="trash" />
                            </button>
                          </div>

                                            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '13px', marginBottom: '13px' }}>
                        <div onClick={() => setSeccionFicha(p => ({ ...p, [emp.id]: p[emp.id] === 'turnos' ? null : 'turnos' }))}
                          style={{ display: 'flex', alignItems: 'center', gap: '11px', cursor: 'pointer' }}>
                          <span style={{ width: '30px', height: '30px', borderRadius: '9px', background: C.primaryPale, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }}><Ico n="timer" /></span>
                          <span style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: C.text }}>Turnos contratados</span>
                          {(emp.turnosSemanales || 5) > capacidadMaximaTurnos(config) && (
                            <span title="Este objetivo está por encima del máximo real alcanzable esta semana" style={{ fontSize: '13px' }}><Ico n="alert" /></span>
                          )}
                          <span style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted }}>{(emp.turnosSemanales || 5)} turnos</span>
                          <span style={{ fontSize: '11px', color: C.primaryLight }}>{seccionFicha[emp.id] === 'turnos' ? '▲' : '▼'}</span>
                        </div>
                        {seccionFicha[emp.id] === 'turnos' && (() => {
                          // Techo real de turnos que se pueden llegar a cumplir esta semana con
                          // los descansos obligatorios configurados — ver capacidadMaximaTurnos.
                          // Ya no se puede poner un número por encima: así nunca se vuelve a
                          // configurar sin querer un objetivo imposible de cumplir.
                          const techoTurnos = capacidadMaximaTurnos(config);
                          return (
                          <div style={{ marginTop: '12px' }}>
<div style={{ marginBottom: '14px', padding: '12px 14px', background: C.bg, borderRadius: '8px' }}>
                        {config.permiteTurnoPartido === false ? (
                          <>
                            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Días trabajados a la semana</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <button onClick={() => updateEmployee(emp.id, 'turnosSemanales', Math.max(1, (emp.turnosSemanales||5)-1))}
                                style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.white, cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary, fontWeight: '700' }}>−</button>
                              <span style={{ fontSize: '24px', fontWeight: '800', color: C.primary, minWidth: '36px', textAlign: 'center' }}>{emp.turnosSemanales||5}</span>
                              <button onClick={() => updateEmployee(emp.id, 'turnosSemanales', Math.min(techoTurnos, (emp.turnosSemanales||5)+1))}
                                style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.white, cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary, fontWeight: '700' }}>+</button>
                              <Badge color={(emp.turnosSemanales||5) >= techoTurnos ? 'warning' : 'primary'}>{emp.turnosSemanales||5} días/semana</Badge>
                              {(emp.turnosSemanales||5) > techoTurnos && <Badge color="danger"><Ico n="alert" /> Por encima del máximo real ({techoTurnos})</Badge>}
                            </div>
                          </>
                        ) : (
                          <>
                            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Turnos contratados semanales</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <button onClick={() => updateEmployee(emp.id, 'turnosSemanales', Math.max(1, (emp.turnosSemanales||5)-1))}
                                style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.white, cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary, fontWeight: '700' }}>−</button>
                              <span style={{ fontSize: '24px', fontWeight: '800', color: C.primary, minWidth: '36px', textAlign: 'center' }}>{emp.turnosSemanales||5}</span>
                              <button onClick={() => updateEmployee(emp.id, 'turnosSemanales', Math.min(techoTurnos, (emp.turnosSemanales||5)+1))}
                                style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.white, cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary, fontWeight: '700' }}>+</button>
                              <Badge color={(emp.turnosSemanales||5) > techoTurnos ? 'danger' : 'primary'}>{emp.turnosSemanales||5} turnos/semana</Badge>
                              {(emp.turnosSemanales||5) > techoTurnos && <Badge color="danger"><Ico n="alert" /> Máximo real esta semana: {techoTurnos}</Badge>}
                            </div>
                          </>
                        )}
                      </div>
                          </div>
                          );
                        })()}
                      </div>

                      {/* Local fijo o rotativo. Solo se ve si el negocio tiene más de un local.
                          "Rota entre locales" es lo normal en negocios con varias puertas y la
                          misma gente; "fijo" es para quien siempre trabaja en el mismo sitio. */}
                      {hayVariosLocales && (
                      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '13px', marginBottom: '13px' }}>
                        <div onClick={() => setSeccionFicha(p => ({ ...p, [emp.id]: p[emp.id] === 'local' ? null : 'local' }))}
                          style={{ display: 'flex', alignItems: 'center', gap: '11px', cursor: 'pointer' }}>
                          <span style={{ width: '30px', height: '30px', borderRadius: '9px', background: C.primaryPale, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }}><Ico n="home" /></span>
                          <span style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: C.text }}>Local asignado</span>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted }}>{emp.localFijo ? (locales.find(l => l.id === emp.localFijo)?.nombre || 'Fijo') : 'Rota'}</span>
                          <span style={{ fontSize: '11px', color: C.primaryLight }}>{seccionFicha[emp.id] === 'local' ? '▲' : '▼'}</span>
                        </div>
                        {seccionFicha[emp.id] === 'local' && (
                          <div style={{ marginTop: '12px' }}>
{hayVariosLocales && (
                        <div style={{ marginBottom: '14px', padding: '12px 14px', background: C.bg, borderRadius: '8px' }}>
                          <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>¿En qué local trabaja?</p>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button onClick={() => updateEmployee(emp.id, 'localFijo', '')}
                              style={{ padding: '9px 14px', borderRadius: '8px', border: !emp.localFijo ? 'none' : `1px solid ${C.border}`, background: !emp.localFijo ? C.primary : C.white, color: !emp.localFijo ? C.white : C.textMuted, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                              <Ico n="refresh" /> Rota entre locales
                            </button>
                            {locales.map(l => (
                              <button key={l.id} onClick={() => updateEmployee(emp.id, 'localFijo', l.id)}
                                style={{ padding: '9px 14px', borderRadius: '8px', border: emp.localFijo === l.id ? 'none' : `1px solid ${C.border}`, background: emp.localFijo === l.id ? C.primary : C.white, color: emp.localFijo === l.id ? C.white : C.textMuted, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                                Fijo en {l.nombre}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                          </div>
                        )}
                      </div>
                      )}

                                            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '13px', marginBottom: '13px' }}>
                        <div onClick={() => setSeccionFicha(p => ({ ...p, [emp.id]: p[emp.id] === 'horario' ? null : 'horario' }))}
                          style={{ display: 'flex', alignItems: 'center', gap: '11px', cursor: 'pointer' }}>
                          <span style={{ width: '30px', height: '30px', borderRadius: '9px', background: 'rgba(251,191,36,0.12)', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }}><Ico n="clock" /></span>
                          <span style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: C.text }}>Horario personal</span>
                          <span style={{ fontSize: '11px', color: C.primaryLight }}>{seccionFicha[emp.id] === 'horario' ? '▲' : '▼'}</span>
                        </div>
                        {seccionFicha[emp.id] === 'horario' && (
                          <div style={{ marginTop: '12px' }}>
<div style={{ marginBottom: '14px', padding: '12px 14px', background: C.bg, borderRadius: '8px' }}>
                        {/* HORARIO PROPIO (10 oct 2026): por defecto cada persona sigue el horario general
                            de Configuración — si este cambia, cambia para todo el equipo a la vez. Solo
                            quien tenga "horario propio" activado usa estas horas suyas. */}
                        {(() => {
                          const Hg = horariosGenerales(config);
                          const propio = usaHorarioPropio(emp);
                          return (
                            <>
                              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', background: C.white, borderRadius: '10px', padding: '4px', border: `1px solid ${C.border}` }}>
                                {[{ v: false, t: 'Horario general' }, { v: true, t: 'Horario propio' }].map(o => (
                                  <button key={String(o.v)} onClick={() => {
                                      if (o.v === propio) return;
                                      if (o.v) {
                                        setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, horarioPropio: true,
                                          horaEntradaComida: Hg.comidaIni, horaSalidaComida: Hg.comidaFin, horaEntradaNoche: Hg.nocheIni, horaSalidaNoche: Hg.nocheFin } : e));
                                      } else {
                                        setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, horarioPropio: false } : e));
                                      }
                                    }}
                                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s ease',
                                      background: propio === o.v ? C.primary : 'transparent', color: propio === o.v ? C.white : C.textMuted }}>{o.t}</button>
                                ))}
                              </div>
                              {!propio ? (
                                <p style={{ margin: 0, fontSize: '12.5px', color: C.textMuted, lineHeight: 1.6 }}>
                                  Sigue el horario del restaurante: <b style={{ color: C.text }}>almuerzo {Hg.comidaIni} – {Hg.comidaFin}</b> · <b style={{ color: C.text }}>cena {Hg.nocheIni} – {Hg.nocheFin}</b>. Si cambias el horario en Configuración, cambia también para esta persona.
                                </p>
                              ) : (
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                  <div>
                                    <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.text }}><Ico n="utensils" /> Almuerzo</p>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                      <TimeSelect label="Entrada" value={emp.horaEntradaComida || Hg.comidaIni} options={OPCIONES_HORA} onChange={v => updateEmployee(emp.id, 'horaEntradaComida', v)} />
                                      <TimeSelect label="Salida" value={emp.horaSalidaComida || Hg.comidaFin} options={OPCIONES_HORA} onChange={v => updateEmployee(emp.id, 'horaSalidaComida', v)} />
                                    </div>
                                  </div>
                                  <div>
                                    <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.text }}><Ico n="moon" /> Cena</p>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                      <TimeSelect label="Entrada" value={emp.horaEntradaNoche || Hg.nocheIni} options={OPCIONES_HORA} onChange={v => updateEmployee(emp.id, 'horaEntradaNoche', v)} />
                                      <TimeSelect label="Salida" value={emp.horaSalidaNoche || Hg.nocheFin} options={OPCIONES_HORA} onChange={v => updateEmployee(emp.id, 'horaSalidaNoche', v)} />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                          </div>
                        )}
                      </div>

                                            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '13px', marginBottom: '13px' }}>
                        <div onClick={() => setSeccionFicha(p => ({ ...p, [emp.id]: p[emp.id] === 'dias' ? null : 'dias' }))}
                          style={{ display: 'flex', alignItems: 'center', gap: '11px', cursor: 'pointer' }}>
                          <span style={{ width: '30px', height: '30px', borderRadius: '9px', background: C.primaryPale, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }}><Ico n="calendar" /></span>
                          <span style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: C.text }}>Días fijos libres</span>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted }}>{(emp.diasFijosLibres || []).length > 0 ? `${(emp.diasFijosLibres || []).length} marcados` : 'Ninguno'}</span>
                          <span style={{ fontSize: '11px', color: C.primaryLight }}>{seccionFicha[emp.id] === 'dias' ? '▲' : '▼'}</span>
                        </div>
                        {seccionFicha[emp.id] === 'dias' && (
                          <div style={{ marginTop: '12px' }}>
<div style={{ marginBottom: '14px', padding: '12px 14px', background: C.bg, borderRadius: '8px' }}>
                        <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Días fijos libres (opcional)</p>
                        <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.textMuted }}>Si marcas días aquí, {emp.name} nunca trabajará esos días. La IA repartirá sus turnos contratados en el resto de la semana.</p>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {DAYS.map(day => {
                            const activo = (emp.diasFijosLibres || []).includes(day);
                            return (
                              <button key={day} onClick={() => setEmployees(prev => prev.map(e => {
                                if (e.id !== emp.id) return e;
                                const dias = e.diasFijosLibres || [];
                                return { ...e, diasFijosLibres: dias.includes(day) ? dias.filter(d => d !== day) : [...dias, day] };
                              }))}
                                style={{ padding: '6px 12px', borderRadius: '7px', border: activo ? 'none' : `1px solid ${C.border}`, background: activo ? C.primary : C.white, color: activo ? C.white : C.textMuted, fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>
                                {activo ? '✓ ' : ''}{day.substring(0,3)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                          </div>
                        )}
                      </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Panel de generación de horario. Antes vivía en la pestaña Empleados; se ha traído
            aquí entero (información de temporada, días especiales y el botón de generar), que
            es donde tiene más sentido: es la pestaña de Horario. Si el negocio tiene un solo
            local, se ve tal cual como antes. Si tiene varios, aparece una tarjeta por cada uno
            con su propio botón de generar y su tope de personas, más el botón de generarlos
            todos de una vez. */}
        {activeTab === 'horario' && (
          <div style={{ ...card, background: C.primaryFaint, border: `1px solid ${C.primaryBorder}` }}>
            <style>{`
              @keyframes horiaBtnGlow {
                0%, 100% { box-shadow: 0 4px 14px rgba(20,184,166,0.4); }
                50%      { box-shadow: 0 6px 22px rgba(20,184,166,0.6); }
              }
              @keyframes horiaSquaresCycleMini {
                0%   { transform: translate(0px,0px); }
                25%  { transform: translate(16px,0px); }
                50%  { transform: translate(16px,16px); }
                75%  { transform: translate(0px,16px); }
                100% { transform: translate(0px,0px); }
              }
            `}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '17px' }}><Ico n="wand" /></span>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: C.primaryDark }}>Generar horario</h3>
              {/* Mini logo animado — 4 cuadrados rotando por las 4 esquinas de una cuadrícula
                  2x2. Cuadrado de 9px + 1.5px de borde (12px de huella real) y 16px de paso entre
                  posiciones, para que quede hueco de sobra entre uno y el siguiente sin solapar. */}
              <div style={{ position: 'relative', width: '28px', height: '28px', flexShrink: 0 }}>
                {['#7C3AED', '#D97706', '#4C1D95', '#A78BFA'].map((color, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: 0, left: 0, width: '9px', height: '9px',
                    borderRadius: '2px', border: `1.5px solid ${color}`, background: 'transparent',
                    animation: 'horiaSquaresCycleMini 9s ease-in-out infinite',
                    animationDelay: `${-2.25 * i}s`,
                  }} />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px', alignItems: 'center' }}>
              <span style={{ background: C.white, border: `1px solid ${C.primaryBorder}`, color: C.primaryDark, fontSize: '11px', fontWeight: '700', padding: '5px 9px', borderRadius: '8px' }}>
                {temporadas[temporada]?.nombre}
              </span>
              {/* Mínimo de gente por turno, editable aquí mismo (21 sep 2026, a petición de
                  Fran: antes solo se podía cambiar escondido en Configuración → Temporadas —
                  para algo que se toca casi cada vez que se monta el horario, tenía que estar
                  aquí mismo, no a tres pantallas de distancia). Toca la misma temporada activa
                  que ya usa el generador, así que el cambio aplica al momento. */}
              <span title="Mínimo de gente que la IA intentará poner en cada turno, para esta temporada"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: C.white, border: `1px solid ${C.primaryBorder}`, color: C.primaryDark, fontSize: '11px', fontWeight: '700', padding: '4px 8px', borderRadius: '8px' }}>
                <span>Mín.</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                  <button onClick={() => setTemporadas(prev => ({...prev, [temporada]: {...prev[temporada], minComida: Math.max(1, (prev[temporada]?.minComida ?? 3) - 1)}}))}
                    style={{ width: '18px', height: '18px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>−</button>
                  <span style={{ minWidth: '14px', textAlign: 'center' }}>{temporadas[temporada]?.minComida ?? 3}</span>
                  <button onClick={() => setTemporadas(prev => ({...prev, [temporada]: {...prev[temporada], minComida: (prev[temporada]?.minComida ?? 3) + 1}}))}
                    style={{ width: '18px', height: '18px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>+</button>
                </span>
                <span>almuerzo /</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                  <button onClick={() => setTemporadas(prev => ({...prev, [temporada]: {...prev[temporada], minNoche: Math.max(1, (prev[temporada]?.minNoche ?? 3) - 1)}}))}
                    style={{ width: '18px', height: '18px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>−</button>
                  <span style={{ minWidth: '14px', textAlign: 'center' }}>{temporadas[temporada]?.minNoche ?? 3}</span>
                  <button onClick={() => setTemporadas(prev => ({...prev, [temporada]: {...prev[temporada], minNoche: (prev[temporada]?.minNoche ?? 3) + 1}}))}
                    style={{ width: '18px', height: '18px', borderRadius: '50%', border: `1px solid ${C.primaryBorder}`, background: C.primaryPale, color: C.primary, fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>+</button>
                </span>
                <span>cena</span>
              </span>
              <span style={{ background: C.white, border: `1px solid ${C.primaryBorder}`, color: C.primaryDark, fontSize: '11px', fontWeight: '700', padding: '5px 9px', borderRadius: '8px' }}>
                Apertura {config.aperturaHora} · {config.personasApertura} {config.personasApertura === 1 ? 'persona' : 'personas'}
              </span>
            </div>
            <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Días especiales</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: '6px', marginBottom: '10px' }}>
              {DAYS.map(day => (
                <button key={day} onClick={() => abrirDiaEspecial(day)}
                  style={{ textAlign: 'center', padding: '7px 0', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                    background: specialDays[day] ? '#FEF3C7' : C.white,
                    color: specialDays[day] ? '#92400E' : C.textMuted,
                    border: specialDays[day] ? '1px solid #FCD34D' : `1px solid ${C.border}` }}>
                  {specialDays[day] ? conIconos('⭐ ') : ''}{day.substring(0,3)}
                </button>
              ))}
            </div>

            {/* PERSONAS POR DÍA Y TURNO (29 sep 2026) — comprimido y aparte, tal como pidió Fran.
                Cerrado por defecto: quien no lo abre, no nota ningún cambio. Poner un número aquí
                marca el día como especial solo (isSpecial en generateSchedule lo recoge), sin
                tener que tocar también la fila de arriba. */}
            <button onClick={() => setPersonasPorDiaAbierto(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', background: 'none', border: 'none', padding: '4px 0', marginBottom: personasPorDiaAbierto ? '10px' : (hayVariosLocales ? '18px' : '16px'), cursor: 'pointer' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: C.primary }}>{personasPorDiaAbierto ? '▾' : '▸'} Ajustar personas por día</span>
            </button>
            {personasPorDiaAbierto && (
              <div style={{ display: 'grid', gap: '6px', marginBottom: hayVariosLocales ? '18px' : '16px' }}>
                {DAYS.map(day => {
                  const ov = config.personasPorDia?.[day] || {};
                  const defComida = temporadas[temporada]?.minComida ?? 3;
                  const defNoche = temporadas[temporada]?.minNoche ?? 3;
                  const tieneOverride = ov.comida != null || ov.noche != null || ov.completo != null;
                  return (
                    <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '8px', background: tieneOverride ? '#FEF9E7' : C.white, border: `1px solid ${tieneOverride ? '#FCD34D' : C.border}` }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: C.text, width: '38px', flexShrink: 0 }}>{day.substring(0,3)}</span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: C.textMuted, flex: 1 }}>
                        Almuerzo
                        <input type="number" min={0} max={employees.length} placeholder={String(defComida)}
                          value={ov.comida ?? ''} onChange={e => setPersonaPorDia(day, 'comida', e.target.value)}
                          style={{ width: '40px', padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', textAlign: 'center' }} />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: C.textMuted, flex: 1 }}>
                        Cena
                        <input type="number" min={0} max={employees.length} placeholder={String(defNoche)}
                          value={ov.noche ?? ''} onChange={e => setPersonaPorDia(day, 'noche', e.target.value)}
                          style={{ width: '40px', padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', textAlign: 'center' }} />
                      </label>
                      {tieneOverride && (
                        <button onClick={() => quitarOverrideDia(day)} title="Quitar, volver al general"
                          style={{ flexShrink: 0, border: 'none', background: 'none', color: C.textMuted, fontSize: '14px', cursor: 'pointer', padding: '2px 4px' }}>
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.4 }}>
                  Por defecto, se aplica el número de empleados definido en Configuración, aunque puedes modificarlo directamente en cada turno según tus necesidades.
                </p>
              </div>
            )}

            {!hayVariosLocales ? (
              <button onClick={() => handleGenerateSchedule()} disabled={generating}
                style={{ width: '100%', border: 'none', borderRadius: '12px', padding: '14px', background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, fontSize: '15px', fontWeight: '800', cursor: generating ? 'default' : 'pointer', animation: generating ? 'none' : 'horiaBtnGlow 2.2s ease-in-out infinite', opacity: generating ? 0.7 : 1 }}>
                {generating ? conIconos('⏳ Generando...') : conIconos('✨ Generar horario')}
              </button>
            ) : (
              <>
                {/* Cambiar de local para VERLO ya se hace con las pestañas de arriba del
                    horario — aquí el nombre es solo una etiqueta, ya no es un botón. Pulsar
                    aquí antes cambiaba la vista sin querer al generar el horario de otro local. */}
                <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
                  {locales.map(l => {
                    const activo = l.id === localActivoId;
                    const tieneHorario = activo
                      ? Object.keys(schedule).length > 0
                      : Object.keys(schedulesPorLocal[l.id] || {}).length > 0;
                    return (
                      <div key={l.id} style={{ background: C.white, borderRadius: '11px', border: `1px solid ${activo ? C.primary : C.border}`, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                          {locales.length > 2 && (
                            <input type="checkbox" checked={localesSeleccionados.includes(l.id)}
                              onChange={() => toggleLocalSeleccionado(l.id)}
                              title="Incluir en «Generar seleccionados»"
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: C.primary, flexShrink: 0 }} />
                          )}
                          <span style={{ flex: 1, fontWeight: '800', fontSize: '14px', color: activo ? C.primary : C.text }}>
                            {activo ? conIconos('📍 ') : ''}{l.nombre}
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: tieneHorario ? C.success : C.textMuted }}>
                            {tieneHorario ? '✓ generado' : 'sin horario'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <label style={{ fontSize: '12px', color: C.textMuted, whiteSpace: 'nowrap' }}>Personas en este local</label>
                          <input type="number" min="0" placeholder="sin tope"
                            value={personasParaLocal(l.id) || ''}
                            onChange={e => setPersonasParaLocal(l.id, e.target.value)}
                            style={{ width: '70px', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '13px', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => handleGenerateSchedule(l.id)} disabled={generating}
                            style={{ flex: 1, padding: '9px 12px', borderRadius: '9px', border: `1.5px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontWeight: '700', fontSize: '12px', cursor: generating ? 'default' : 'pointer' }}>
                            {generating ? conIconos('⏳') : conIconos(`✨ Generar ${l.nombre}`)}
                          </button>
                          {tieneHorario && (
                            <button onClick={() => handleVaciarHorario(l.id)} disabled={generating} title={`Vaciar el horario de ${l.nombre}`}
                              style={{ padding: '9px 12px', borderRadius: '9px', border: `1.5px solid ${C.dangerBg}`, background: C.white, color: C.danger, fontWeight: '700', fontSize: '12px', cursor: generating ? 'default' : 'pointer' }}>
                              <Ico n="trash" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Con más de dos locales, además de generarlos todos a la vez se puede elegir
                    un grupo concreto con las casillas de arriba. */}
                {locales.length > 2 && (
                  <button onClick={() => handleGenerateAllSchedules(locales.filter(l => localesSeleccionados.includes(l.id)))}
                    disabled={generating || localesSeleccionados.length === 0}
                    style={{ width: '100%', padding: '11px 16px', borderRadius: '10px', border: `1.5px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontWeight: '800', fontSize: '13px', cursor: (generating || localesSeleccionados.length === 0) ? 'default' : 'pointer', opacity: localesSeleccionados.length === 0 ? 0.5 : 1, marginBottom: '10px' }}>
                    {generating ? conIconos('⏳ Generando…') : conIconos(`✅ Generar seleccionados (${localesSeleccionados.length})`)}
                  </button>
                )}
                {/* Genera los horarios de TODOS los locales de una vez, repartiendo a los
                    rotativos sin que se repitan entre ellos. Es la salida limpia al bloqueo de
                    "primero uno, luego el otro me dice que no queda nadie". */}
                <button onClick={() => handleGenerateAllSchedules()} disabled={generating}
                  style={{ width: '100%', padding: '13px 16px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, fontWeight: '800', fontSize: '14px', cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.7 : 1 }}>
                  {generating ? conIconos('⏳ Generando…') : conIconos(`🏢 Generar los ${locales.length} horarios a la vez`)}
                </button>
              </>
            )}
          </div>
        )}

        {activeTab === 'horario' && (
          <div>
            {/* FALLO REAL ENCONTRADO (13 ago 2026), diagnosticado por Fran: estas pestañas (y
                toda la tabla de abajo) vivían DENTRO del `Object.keys(schedule).length === 0 ?
                ... : ...` de más abajo — que mira el local ACTIVO, no el que se acabe de generar.
                Si generabas un local que no era el que tenías abierto en pantalla, `schedule`
                (el del activo) seguía vacío, así que TODO el bloque se escondía — pestañas
                incluidas, dejando sin forma de cambiar a ver el local que sí se había generado
                bien. Parecía que generar un local suelto "no hacía nada". Ahora las pestañas
                viven fuera de esa condición: se ven siempre que haya más de un local, tenga o no
                datos el que estés mirando ahora mismo, para poder saltar al que sí los tenga. */}
            {hayVariosLocales && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {locales.map(l => {
                  const activo = l.id === localActivoId;
                  return (
                    <button key={l.id} onClick={() => cambiarDeLocal(l.id)}
                      style={{
                        padding: '10px 16px', borderRadius: '11px',
                        border: `1px solid ${activo ? D.lineStrong : D.line}`,
                        background: activo ? `linear-gradient(135deg, ${D.blueBright}, ${D.blue})` : D.panel,
                        color: activo ? '#FFFFFF' : D.textMuted,
                        boxShadow: activo ? D.glow : 'none',
                        fontWeight: activo ? '800' : '600', fontSize: '13px', cursor: 'pointer' }}>
                      {activo ? conIconos('📍 ') : ''}{l.nombre}
                    </button>
                  );
                })}
              </div>
            )}
            {Object.keys(schedule).length === 0
            ? <div style={{ ...darkCard, textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '34px', marginBottom: '10px' }}><Ico n="calendar" /></div>
                <p style={{ margin: 0, fontSize: '14px', color: D.textMuted }}>Usa el panel de arriba para generar el horario de este local.</p>
              </div>
            : <div>
                <div style={{ ...darkCard, background: schedulePublished ? C.cyanBg : `linear-gradient(140deg, ${D.panelHi}, ${D.panel})`, border: `1px solid ${schedulePublished ? C.cyanBorder : D.lineStrong}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '11px', background: schedulePublished ? C.cyanDark : `linear-gradient(140deg, ${D.blueBright}, ${D.blue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flexShrink: 0 }}>
                        {schedulePublished ? conIconos('✅') : conIconos('⏳')}
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: schedulePublished ? C.cyanDark : D.text }}>
                          {schedulePublished ? 'Horario publicado' : (programadoPorLocal[localActivoId] ? 'Publicación programada' : 'Pendiente de publicar')}
                        </h3>
                        <p style={{ margin: 0, fontSize: '12px', color: schedulePublished ? C.cyanDark : D.textMuted }}>
                          {schedulePublished
                            ? 'Los empleados ya pueden verlo.'
                            : (programadoPorLocal[localActivoId]
                              ? `Saldrá el ${textoCuandoProgramado(programadoPorLocal[localActivoId].cuando)} y se avisará a todo el equipo.`
                              : 'Revisa y publica cuando esté listo.')}
                        </p>
                        {!schedulePublished && programadoPorLocal[localActivoId] && (
                          <button onClick={() => quitarProgramacion(localActivoId)}
                            style={{ marginTop: '4px', padding: 0, border: 'none', background: 'none', color: D.blueLight, fontWeight: '700', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                            Cancelar programación
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {/* Envuelto en función anónima a propósito: handleGenerateSchedule ya
                          acepta un parámetro (localIdObjetivo), y pasarla directa a onClick haría
                          que React le colara el evento del clic como si fuera un id de local. */}
                      <button onClick={() => handleGenerateSchedule()} disabled={generating}
                        style={schedulePublished
                          ? { padding: '10px 16px', borderRadius: '11px', border: 'none', background: C.cyanDark, color: '#FFFFFF', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }
                          : { padding: '10px 16px', borderRadius: '11px', border: `1px solid ${D.lineStrong}`, background: D.panelSoft, color: D.text, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                        {generating ? conIconos('⏳') : conIconos('🔄 Regenerar')}
                      </button>
                      {!schedulePublished && (
                        <button onClick={abrirProgramar}
                          style={{ padding: '10px 16px', borderRadius: '11px', border: `1px solid ${D.lineStrong}`, background: D.panelSoft, color: D.text, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                          {conIconos('⏳')} {programadoPorLocal[localActivoId] ? 'Cambiar hora' : 'Programar'}
                        </button>
                      )}
                      {!schedulePublished && (
                        <button onClick={handlePublishSchedule}
                          style={{ padding: '10px 18px', borderRadius: '11px', border: 'none', background: `linear-gradient(135deg, ${D.blueBright}, ${D.blue})`, color: '#FFFFFF', fontWeight: '700', fontSize: '13px', cursor: 'pointer', boxShadow: D.glow }}>
                          <Ico n="upload" /> {programadoPorLocal[localActivoId] ? 'Publicar ahora' : 'Publicar'}
                        </button>
                      )}
                      {/* Botón de vaciar: la salida cuando dos locales quedan bloqueados entre sí
                          porque comparten empleados "ocupados" de un fallo anterior. */}
                      <button onClick={() => handleVaciarHorario()}
                        title="Borrar el horario de este local y empezar de cero"
                        style={{ padding: '10px 14px', borderRadius: '11px', border: '1px solid rgba(251,113,133,0.4)', background: 'rgba(251,113,133,0.1)', color: '#FB7185', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                        <Ico n="trash" /> Vaciar
                      </button>
                    </div>
                  </div>
                </div>


                <div style={{ ...darkCard, padding: '16px 18px' }}>
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div style={{ flex: '2 1 320px' }}>
                      <button onClick={() => setResumenDiaAbierto(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', background: 'none', border: 'none', padding: 0, marginBottom: resumenDiaAbierto ? '12px' : 0, cursor: 'pointer' }}>
                        <p style={{ margin: 0, fontSize: '12px', fontWeight: '800', color: D.textFaint, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Personas trabajando por día</p>
                        <span style={{ fontSize: '11px', color: D.blueLight, fontWeight: '700' }}>{resumenDiaAbierto ? '▴ ocultar' : '▾ ver detalle'}</span>
                      </button>
                      {resumenDiaAbierto && (
                      <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '6px', marginBottom: '12px' }}>
                        {(() => {
                          const totalAperturas = DAYS.reduce((sum, d) => sum + employees.filter(e => schedule[e.id]?.[d]?.tipo === 'apertura').length, 0);
                          const celdas = DAYS.map(day => {
                            const cerrado = (config.diasCierreSemana || []).includes(day);
                            const comidaCount = employees.filter(e => ['comida','apertura','partido'].includes(schedule[e.id]?.[day]?.tipo)).length;
                            const nocheCount = employees.filter(e => ['noche','partido','enlace'].includes(schedule[e.id]?.[day]?.tipo)).length;
                            // COLORES UNIFICADOS (5 oct 2026, a petición de Fran): antes los números salían
                            // en verde o rojo según llegaran al mínimo, y Viernes/Sábado llevaban borde
                            // amarillo, lo que no tenía sentido con la leyenda de colores de abajo. Ahora
                            // todos los días se ven igual: el número de comidas con el color de Almuerzo
                            // (azul) y el de cenas con el de Cena (morado), los mismos que la leyenda.
                            // Solo se distingue el día de hoy.
                            const esHoy = esHoyDeVerdad(day);
                            const borde = esHoy ? `1.5px solid ${D.blue}` : `1px solid ${D.line}`;
                            const fondo = esHoy ? D.panelHi : D.panelSoft;
                            if (cerrado) {
                              return (
                                <div key={day} style={{ textAlign: 'center', padding: '9px 4px', background: 'transparent', border: `1px dashed ${D.line}`, borderRadius: '11px' }}>
                                  <div style={{ fontSize: '10px', fontWeight: '700', color: D.textFaint }}>{day.substring(0,3)}</div>
                                  <div style={{ fontSize: '11px', fontWeight: '700', color: D.textFaint, marginTop: '4px' }}><Ico n="door" /> Cerrado</div>
                                </div>
                              );
                            }
                            return (
                              <div key={day} style={{ textAlign: 'center', padding: '9px 4px', background: fondo, border: borde, borderRadius: '11px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '800', color: esHoy ? D.blueLight : D.textFaint }}>
                                  {day.substring(0,3).toUpperCase()}{esHoy ? ' · HOY' : (specialDays[day] ? conIconos(' ⭐') : '')}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '4px' }}>
                                  <span style={{ fontSize: '13px', fontWeight: '800', color: shiftColor('comida').stripe }}><Ico n="utensils" />{comidaCount}</span>
                                  <span style={{ fontSize: '13px', fontWeight: '800', color: shiftColor('noche').stripe }}><Ico n="moon" />{nocheCount}</span>
                                </div>
                              </div>
                            );
                          });
                          celdas.push(
                            <div key="aperturas" style={{ textAlign: 'center', padding: '9px 4px', background: 'transparent', border: `1px dashed ${D.line}`, borderRadius: '11px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: D.textMuted }}><Ico n="key" /> {totalAperturas}</div>
                              <div style={{ fontSize: '9px', color: D.textFaint }}>aperturas</div>
                            </div>
                          );
                          return celdas;
                        })()}
                      </div>
                      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {['apertura', 'comida', 'noche', 'partido', 'enlace', 'vacaciones', 'baja', 'libre'].map(t => {
                          const v = paletaDe(t);
                          return (
                            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: D.textMuted, fontWeight: '700' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '7px', background: v.fondo, borderLeft: `3px solid ${v.acento}` }}><Ico n={v.icono} s="12px" c={t === 'libre' ? '#7C73A6' : v.acento} w={2.2} /></span>
                              {v.nombre}
                            </div>
                          );
                        })}
                        <span style={{ fontSize: '12px', color: D.textFaint, display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Ico n="refresh" s="11px" /> Barra / Salón = le toca recargar</span>
                      </div>
                      </>
                      )}
                    </div>
                    <div style={{ flex: '1 1 220px', background: D.panelSoft, border: `1px solid ${D.line}`, borderRadius: '12px', padding: '14px 16px' }}>
                      <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: '800', color: D.text }}><Ico n="pencil" /> Editar horario</p>
                      <p style={{ margin: 0, fontSize: '12px', color: D.textMuted, lineHeight: 1.55 }}>
                        Pulsa sobre cualquier turno para modificarlo o eliminarlo.
                      </p>
                    </div>
                  </div>
                </div>

                {(() => {
                  const DIAS_CORTOS = { Lunes: 'Lun', Martes: 'Mar', Miércoles: 'Mié', Jueves: 'Jue', Viernes: 'Vie', Sábado: 'Sáb', Domingo: 'Dom' };
                  const DENSIDAD_HORARIO = {
                    // Tamaño de base: el que antes era "compacto" de 3 opciones. Puede necesitar
                    // scroll horizontal en móviles estrechos — para que quepa TODO sin scroll está
                    // "mini", la otra opción.
                    compacto: { mini: false, filaGap: '0 3px', celdaPad: '4px 6px', minH: '32px', nombrePad: '6px 10px', avatar: '24px', fs: '11px', fsHora: '10px', fsNombre: '12px', diaFs: '13px', diaNumFs: '11px' },
                    // Todo en % (nada de minWidth), fuentes con clamp() para que nunca desborden y
                    // los días abreviados — así SIEMPRE caben a la vez los nombres y los 7 días,
                    // sea cual sea el ancho de pantalla.
                    mini: { mini: true, filaGap: '0 2px', celdaPad: '2px', minH: '34px', nombrePad: '3px 4px', avatar: '22px', fs: 'clamp(7px, 1.6vw, 10px)', fsHora: 'clamp(6px, 1.3vw, 9px)', fsNombre: 'clamp(8px, 1.8vw, 11px)', diaFs: 'clamp(8px, 1.7vw, 11px)', diaNumFs: 'clamp(7px, 1.4vw, 9px)' },
                  };
                  const tablaHorarioEncargado = (densidad = 'compacto', fondo = D.panel) => {
                    const dz = DENSIDAD_HORARIO[densidad] || DENSIDAD_HORARIO.compacto;
                    // REDISEÑO (10 oct 2026, pedido por Fran): casillas nuevas (CeldaTurno) con la hora
                    // grande, cabecera con día especial y hoy, bajo cada nombre "turnos asignados /
                    // contratados" con su barrita, y al pie las personas por turno de cada día.
                    const semana = fechasDelHorario();
                    const hoyD = new Date();
                    const nombreHoy = DAYS[hoyD.getDay() === 0 ? 6 : hoyD.getDay() - 1];
                    const cierreDias = config.diasCierreSemana || [];
                    const minimoDia = (d) => {
                      const ov = config.personasPorDia?.[d];
                      const t = temporadas[temporada] || {};
                      const esp = !!specialDays[d] || ov?.comida != null || ov?.noche != null;
                      return { c: ov?.comida ?? t.minComida ?? 3, n: ov?.noche ?? t.minNoche ?? 3, cMax: esp ? null : (Number.isFinite(t.maxComida) ? t.maxComida : null), nMax: esp ? null : (Number.isFinite(t.maxNoche) ? t.maxNoche : null) };
                    };
                    // COLUMNA DE NOMBRES FIJA (11 oct 2026, pedido por Fran): al desplazar el horario a
                    // los lados, los nombres no se mueven y nada se ve por detrás. Para eso la tabla no
                    // deja huecos entre casillas (el espacio va DENTRO de cada celda) y la columna fija
                    // tiene fondo opaco que además tapa el margen interior de la tarjeta (sombra a la
                    // izquierda del mismo color que el fondo).
                    const pegada = (extra = {}) => ({ position: 'sticky', left: 0, zIndex: 3, background: fondo, boxShadow: `-20px 0 0 ${fondo}, 10px 0 12px -12px rgba(76,29,149,0.28)`, ...extra });
                    const hueco = dz.mini ? undefined : '3px';
                    return (
                    <table className={`horia-schedule-table${dz.mini ? '' : ' hd-tabla-enc'}`} style={{ width: '100%', borderCollapse: 'separate', borderSpacing: dz.mini ? '2px 3px' : 0, minWidth: dz.mini ? undefined : '980px', tableLayout: dz.mini ? 'fixed' : undefined }}>
                      <thead>
                        <tr>
                          <th className="hd-th-nombre" style={{ padding: dz.mini ? '4px 3px' : '6px 10px 6px 4px', textAlign: 'left', fontSize: dz.fsNombre, color: D.textMuted, fontWeight: '800', width: dz.mini ? '18%' : undefined, minWidth: dz.mini ? undefined : 'clamp(128px, 34vw, 184px)', background: fondo, ...(dz.mini ? {} : pegada()) }}>Equipo</th>
                          {DAYS.map(d => {
                            const esp = !!specialDays[d];
                            // "Hoy" solo si la columna es de verdad la fecha de hoy (la semana que se
                            // gestiona empieza el próximo lunes; un domingo no se marca el domingo de la semana siguiente).
                            const esHoy = d === nombreHoy && semana[d] === toDateStr(hoyD);
                            const cerradoD = cierreDias.includes(d);
                            return (
                              <th key={d} className="hd-th-dia" style={{ padding: dz.mini ? '2px 0' : '2px 3px 4px', textAlign: 'center', width: dz.mini ? `${82 / DAYS.length}%` : undefined, minWidth: dz.mini ? undefined : '118px', verticalAlign: 'bottom' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', padding: dz.mini ? '3px 2px' : '7px 6px', borderRadius: dz.mini ? '7px' : '12px',
                                  background: esp ? '#FEF3C7' : (esHoy ? C.primaryFaint : 'transparent'), border: esp ? '1px solid #FCD34D' : (esHoy ? `1px solid ${C.primaryBorder}` : '1px solid transparent') }}>
                                  <span style={{ fontSize: dz.diaFs, fontWeight: '800', color: esp ? '#92400E' : (cerradoD ? D.textFaint : D.text), display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                    {esp && <Ico n="star" s={dz.mini ? '9px' : '12px'} c="#D97706" w={2.4} />}{dz.mini ? DIAS_CORTOS[d] : d}
                                  </span>
                                  <span style={{ fontSize: dz.diaNumFs, fontWeight: '700', color: esHoy ? C.primary : D.textFaint, fontVariantNumeric: 'tabular-nums' }}>
                                    {cerradoD ? 'Cerrado' : (esHoy && !dz.mini ? `Hoy, ${dayNumber(d)}` : dayNumber(d))}
                                  </span>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map((emp, ri) => {
                          const enHorario = !!schedule[emp.id];
                          const tarjetaNombre = (
                            <div className={`hd-tarjeta-nombre${dz.mini ? ' hd-mini-nombre' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: dz.mini ? '4px' : '10px', height: '100%', boxSizing: 'border-box', padding: dz.nombrePad }}>
                              <FichaPersona p={emp} size={dz.mini ? dz.avatar : 36} morada />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="hd-nombre-txt" style={{ fontWeight: '800', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: dz.mini ? dz.fsNombre : '14px' }}>{emp.name}{emp.hacesEnlace ? <Ico n="link" s="11px" c="#22C55E" style={{ marginLeft: 4 }} /> : null}</div>
                                {!dz.mini && hayVariosLocales && !enHorario && <div style={{ fontSize: '11px', color: D.textFaint, fontWeight: '600', marginTop: '2px' }}>En otro local</div>}
                              </div>
                            </div>
                          );
                          return (
                          <tr key={emp.id}>
                            <td className="hd-td-nombre" style={{ padding: dz.mini ? 0 : '3px 6px 3px 0', fontSize: dz.fsNombre, color: D.text, verticalAlign: 'middle', height: '1px', ...(dz.mini ? {} : pegada()) }}>
                              {tarjetaNombre}
                            </td>
                            {DAYS.map((day, di) => (
                              <td key={day} style={{ padding: hueco || 0, verticalAlign: 'top', height: '1px' }}>
                                <CeldaTurno key={`${vueltaHorario}-${emp.id}-${day}`} s={getShift(emp.id, day)} conf={config} tam={dz.mini ? 'mini' : 'normal'}
                                  onClick={() => setDescansoModal({ empId: emp.id, day })} retraso={Math.min(650, di * 55 + ri * 16)} />
                              </td>
                            ))}
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td style={{ padding: dz.mini ? '3px' : '8px 10px 8px 4px', fontSize: dz.mini ? dz.fsHora : '12px', fontWeight: '800', color: D.textMuted, background: fondo, ...(dz.mini ? {} : pegada()) }}>
                            {dz.mini ? 'Total' : 'Personas por turno'}
                          </td>
                          {DAYS.map(d => {
                            if (cierreDias.includes(d)) return <td key={d} style={{ textAlign: 'center', fontSize: dz.fsHora, color: D.textFaint, fontWeight: '700' }}>—</td>;
                            const c = employees.filter(e => ['comida', 'apertura', 'partido'].includes(schedule[e.id]?.[d]?.tipo)).length;
                            const n = employees.filter(e => ['noche', 'partido', 'enlace'].includes(schedule[e.id]?.[d]?.tipo)).length;
                            const m = minimoDia(d);
                            const pill = (icono, valor, minimo, color, maximo = null) => {
                              const ok = valor >= minimo && (maximo == null || valor <= maximo);
                              return (
                                <span title={`${valor} personas · mínimo ${minimo}${maximo != null ? ` · máximo ${maximo}` : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: dz.mini ? '1px 3px' : '3px 8px', borderRadius: '999px', fontSize: dz.mini ? dz.fsHora : '12px', fontWeight: '800', fontVariantNumeric: 'tabular-nums',
                                  background: ok ? '#ECFDF5' : '#FFFBEB', color: ok ? '#047857' : '#B45309', border: `1px solid ${ok ? '#A7F3D0' : '#FDE68A'}` }}>
                                  {!dz.mini && <Ico n={icono} s="11px" c={color} w={2.4} />}{valor}
                                </span>
                              );
                            };
                            return (
                              <td key={d} style={{ textAlign: 'center', padding: dz.mini ? '2px 0' : '6px 3px' }}>
                                <div style={{ display: 'flex', gap: dz.mini ? '2px' : '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                  {pill('sun', c, m.c, PALETA_TURNO.comida.acento, m.cMax)}
                                  {pill('moon', n, m.n, PALETA_TURNO.noche.acento, m.nMax)}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    </table>
                  );
                  };
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                        <button onClick={abrirEncargadoHorarioPantallaCompleta} style={{ ...btn('outline', 'sm'), background: D.panel, border: `1px solid ${D.lineStrong}`, color: D.text, borderRadius: '9px' }}><Ico n="expand" /> Pantalla completa</button>
                      </div>
                      <div className="horia-schedule-wrap" style={{ ...darkCard, overflowX: 'auto', minWidth: 0, padding: '14px 16px' }}>
                        {tablaHorarioEncargado()}
                      </div>
                      {encargadoHorarioPantallaCompleta && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: D.deep, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'auto' }}>
                          <button onClick={cerrarEncargadoHorarioPantallaCompleta} aria-label="Cerrar"
                            style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1, border: `1px solid ${D.lineStrong}`, background: D.panel, borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px', color: D.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                          {/* Selector de densidad — SOLO existe aquí, en pantalla completa; la tabla
                              normal incrustada arriba nunca lo usa y se ve igual que siempre. Solo 2
                              opciones (20 sep 2026, a petición de Fran tras probarlo con 3): el tamaño
                              de base ya es compacto, y "Mini" es el único extra, pensado para que quepan
                              a la vez todos los nombres y los 7 días sin desplazamiento horizontal. */}
                          <div style={{ position: 'absolute', top: '8px', left: '12px', zIndex: 1, display: 'flex', gap: '4px', background: D.panel, border: `1px solid ${D.lineStrong}`, borderRadius: '9px', padding: '3px' }}>
                            {[['compacto', 'Normal'], ['mini', 'Mini']].map(([key, label]) => (
                              <button key={key} onClick={() => cambiarDensidadHorarioFull(key)}
                                style={{
                                  padding: '6px 10px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
                                  background: densidadHorarioFull === key ? D.blue : 'transparent',
                                  color: densidadHorarioFull === key ? '#FFFFFF' : D.textMuted,
                                }}>
                                {label}
                              </button>
                            ))}
                          </div>
                          {/* 40 px arriba (antes 30): que el selector Normal/Mini no roce la cabecera de los días. */}
                          <div style={{ padding: '40px 12px 20px' }}>
                            {tablaHorarioEncargado(densidadHorarioFull, D.deep)}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            }
          </div>
        )}

        {activeTab === 'solicitudes' && (
          <div>
            {/* --- Publicar una petición de cobertura --- */}
            <div style={{ ...card, border: `1px solid ${C.primaryBorder}`, background: C.primaryFaint }}>
              <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.primaryDark, margin: '0 0 4px' }}><Ico n="hand" /> Buscar quien cubra un turno</h2>
              <p style={{ margin: '0 0 14px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                Le llega a todo el equipo al móvil. Cada uno contesta si puede o no, y tú eliges.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {(() => {
                  // Días del horario en vigor (29 oct 2026) y nunca uno que ya ha pasado.
                  const semanaFechas = fechasDelHorario();
                  const diaPorFecha = Object.fromEntries(DAYS.map(d => [semanaFechas[d], d]));
                  const hoyCob = toDateStr(new Date());
                  const minCob = semanaFechas['Lunes'] > hoyCob ? semanaFechas['Lunes'] : hoyCob;
                  return (
                    <div style={{ position: 'relative', flex: '1 1 140px' }}>
                      <input type="date" value={semanaFechas[cobDia] || ''}
                        min={minCob} max={semanaFechas['Domingo']}
                        onChange={e => setCobDia(diaPorFecha[e.target.value] || '')}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', background: C.white, color: cobDia ? C.text : 'transparent' }} />
                      {/* Los <input type="date"> no admiten placeholder de verdad — sin esto, vacíos
                          se ven como una caja en blanco (así lo vio Fran). Se superpone el texto y se
                          deja pasar el toque de largo (pointerEvents: none) para que siga abriendo el
                          calendario nativo igual que si se tocara el input directamente. */}
                      {!cobDia && (
                        <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: C.textMuted, pointerEvents: 'none' }}>
                          Día de la semana
                        </span>
                      )}
                    </div>
                  );
                })()}
                <select value={cobTurno} onChange={e => setCobTurno(e.target.value)}
                  style={{ flex: '1 1 140px', padding: '11px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', background: C.white }}>
                  <option value="apertura">Apertura</option>
                  <option value="comida">Almuerzo</option>
                  <option value="noche">Cena</option>
                  <option value="partido">Turno Partido</option>
                  <option value="enlace">Enlace</option>
                </select>
              </div>
              <input type="text" maxLength={200} value={cobNota} onChange={e => setCobNota(e.target.value)}
                placeholder="Algo que quieras añadir (opcional)"
                style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', marginBottom: '10px' }} />
              <button onClick={crearCobertura} disabled={!cobDia || cobBusy}
                style={{ ...btn('primary'), justifyContent: 'center', width: '100%', opacity: (!cobDia || cobBusy) ? 0.55 : 1, cursor: (!cobDia || cobBusy) ? 'default' : 'pointer' }}>
                {cobBusy ? 'Publicando…' : 'Preguntar al equipo'}
              </button>
            </div>

            {/* --- Coberturas abiertas, con quién ha dicho qué --- */}
            {coberturasAbiertas.map(req => {
              const respuestas = req.respuestas || {};
              const dicenSi = employees.filter(e => respuestas[e.name]?.r === 'si');
              const dicenNo = employees.filter(e => respuestas[e.name]?.r === 'no');
              const sinResponder = employees.filter(e => !respuestas[e.name]);
              const hanContestado = employees.length - sinResponder.length;
              return (
                <div key={req.id} style={{ ...card, border: `2px solid ${C.primary}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '800', color: C.text }}>
                        {req.dia} {fechaLegible(req.fecha)} · {shiftLabel(req.turno)}
                      </p>
                      <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>
                        {hanContestado} de {employees.length} han contestado
                      </p>
                      {req.note && <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>"{req.note}"</p>}
                    </div>
                    <button onClick={() => cancelarCobertura(req)} style={{ ...btn('outline', 'sm') }}>Cancelar</button>
                  </div>

                  {dicenSi.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pueden cubrirlo</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {dicenSi.map(emp => (
                          <div key={emp.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 12px', background: C.successBg, borderRadius: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '700', color: C.text, fontSize: '14px' }}>{emp.name}</span>
                            <button onClick={() => asignarCobertura(req, emp)} style={btn('success', 'sm')}>
                              Asignar al {req.dia.toLowerCase()}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '13px', color: C.textMuted }}>
                    {dicenNo.length > 0 && <span>✗ No pueden: {dicenNo.map(e => e.name).join(', ')}</span>}
                    {sinResponder.length > 0 && <span>· Sin responder: {sinResponder.map(e => e.name).join(', ')}</span>}
                  </div>

                  {dicenSi.length === 0 && sinResponder.length === employees.length && (
                    <p style={{ margin: '10px 0 0', fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>
                      Todavía no ha contestado nadie.
                    </p>
                  )}
                </div>
              );
            })}

            {/* --- Intercambios de turno entre compañeros --- */}
            {intercambiosTurno.map(req => {
              const estado = {
                pendiente_companero: { color: 'warning', texto: `Esperando a ${req.companeroName}` },
                pendiente_encargado: { color: 'warning', texto: 'Esperando tu aprobación' },
                aprobada: { color: 'success', texto: 'Aprobada ✓' },
                rechazada_companero: { color: 'danger', texto: `${req.companeroName} dijo que no` },
                rechazada_encargado: { color: 'danger', texto: 'Rechazada' },
              }[req.status] || { color: 'warning', texto: req.status };
              return (
                <div key={req.id} style={{ ...card, border: req.status === 'pendiente_encargado' ? `2px solid ${C.primary}` : `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontWeight: '800', color: C.text, fontSize: '16px' }}><Ico n="refresh" /> {req.solicitanteName} ↔ {req.companeroName}</p>
                        <Badge color={estado.color}>{estado.texto}</Badge>
                      </div>
                      <p style={{ margin: '0 0 4px', fontSize: '14px', color: C.textMuted }}>
                        {req.dia} {fechaLegible(req.fecha)} · {req.solicitanteName} da {shiftLabel(req.turnoSolicitante)} y recibe {shiftLabel(req.turnoCompanero)}
                      </p>
                      {req.note && <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>"{req.note}"</p>}
                    </div>
                    {req.status === 'pendiente_encargado' ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => resolverIntercambioEncargado(req, true)} style={btn('success', 'sm')}>✓ Aprobar</button>
                        <button onClick={() => resolverIntercambioEncargado(req, false)} style={btn('danger', 'sm')}>✗ Rechazar</button>
                        <button onClick={() => borrarIntercambio(req)} title="Borrar"
                          style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.textMuted, cursor: 'pointer', fontSize: '14px' }}><Ico n="trash" /></button>
                      </div>
                    ) : (
                      <button onClick={() => borrarIntercambio(req)} style={btn('outline', 'sm')}><Ico n="trash" /> Borrar</button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* --- Solicitudes de día libre, como siempre --- */}
            {solicitudesDiaLibre.length === 0 && coberturasAbiertas.length === 0 && intercambiosTurno.length === 0 ? (
              <div style={card}><EmptyState icon="📬" text="No hay solicitudes." /></div>
            ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {solicitudesDiaLibre.map(req => (
                <div key={req.id} style={{ ...card, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <p style={{ margin: 0, fontWeight: '700', color: C.text }}>{req.sender}</p>
                      <Badge color={req.status === 'pending' ? 'warning' : req.status === 'approved' ? 'success' : 'danger'}>
                        {req.status === 'pending' ? 'Pendiente' : req.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                      </Badge>
                    </div>
                    <p style={{ margin: '0 0 4px', fontSize: '14px', color: C.textMuted }}><Ico n="calendar" /> {fechaConDia(req.date)}{req.createdAt ? ` · pedida el ${req.createdAt}` : ''}</p>
                    {req.note && <p style={{ margin: 0, fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>"{req.note}"</p>}
                  </div>
                  {req.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => updateRequest(req, 'approved')} style={btn('success', 'sm')}>✓ Aprobar</button>
                      <button onClick={() => updateRequest(req, 'rejected')} style={btn('danger', 'sm')}>✗ Rechazar</button>
                      <button onClick={() => borrarSolicitud(req)} title="Borrar"
                        style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.textMuted, cursor: 'pointer', fontSize: '14px' }}><Ico n="trash" /></button>
                    </div>
                  ) : (
                    <button onClick={() => borrarSolicitud(req)} style={btn('outline', 'sm')}><Ico n="trash" /> Borrar</button>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        )}

        {activeTab === 'vacaciones' && (
          <div>
            <div style={{ ...card, background: 'rgba(34,211,238,0.12)', border: '1px solid #A5F3FC' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#0E7490' }}>
                <Ico n="sun" /> Toca un nombre para marcarle vacaciones. Esos días aparecerán en cian en el horario y Horidia no le asignará ningún turno hasta que finalice el periodo.
              </p>
            </div>
            {employees.length === 0 ? <div style={card}><EmptyState icon="🏖️" text="Añade empleados primero desde la pestaña Empleados." /></div> : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {employees.map(emp => {
                  const hoyStr = toDateStr(new Date());
                  const enCurso = estaAusenteEse(emp, hoyStr);
                  return (
                    <div key={emp.id} onClick={() => setVacacionesEmpleado(emp.id)}
                      style={{ ...card, marginBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', border: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FichaPersona p={emp} size={38} />
                        <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}>{emp.name}</p>
                        {enCurso && (
                          <span style={{ background: 'rgba(94,234,212,0.15)', color: '#0E7490', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                            <Ico n="sun" /> {rangoFechasLegible(enCurso.start, enCurso.end)}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: '13px', color: C.primary, fontWeight: '700' }}>Gestionar →</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'bajas' && (
          <div>
            <div style={{ ...card, background: 'rgba(251,191,36,0.14)', border: '1px solid #FED7AA' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#B45309' }}>
                <Ico n="baja" /> Toca un nombre para marcarle una baja. Esos días aparecerán como "De baja" en el horario y Horidia no le asignará ningún turno hasta que finalice el periodo.
              </p>
            </div>
            {employees.length === 0 ? <div style={card}><EmptyState icon="🩼" text="Añade empleados primero desde la pestaña Empleados." /></div> : (
              <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '14px' }}>
                {employees.map(emp => {
                  const hoyStr = toDateStr(new Date());
                  const enBaja = estaDeBajaEse(emp, hoyStr);
                  return (
                    <div key={emp.id} onClick={() => setBajaEmpleado(emp.id)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '14px 8px', borderRadius: '12px', border: `1px solid ${enBaja ? '#FED7AA' : C.border}`, background: enBaja ? '#FFEDD5' : C.white, cursor: 'pointer', textAlign: 'center' }}>
                      <div style={{ position: 'relative' }}>
                        <FichaPersona p={emp} size={46} />
                        {enBaja && <div style={{ position: 'absolute', top: -2, right: -2, fontSize: '15px' }}><Ico n="baja" /></div>}
                      </div>
                      <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '13px', lineHeight: 1.2 }}>{emp.name}</p>
                      {enBaja && <span style={{ fontSize: '10px', fontWeight: '700', color: '#B45309' }}>De baja</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'historial' && (
          scheduleHistory.length === 0 ? (
            <div style={card}><EmptyState icon="🕓" text="Aún no hay horarios publicados en el historial." /></div>
          ) : viewingHistoryId !== null ? (
            (() => {
              const h = scheduleHistory.find(x => x.id === viewingHistoryId);
              if (!h) return null;
              return (
                <div>
                  <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px', fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="calendar" /> Horario publicado el {h.publishedAt}</p>
                      <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>Temporada: {h.temporadaNombre}</p>
                    </div>
                    <button onClick={() => setViewingHistoryId(null)} style={btn('outline')}>← Volver al listado</button>
                    <button onClick={() => borrarEntradaHistorial(h)}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${C.dangerBg}`, background: C.white, color: C.danger, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                      <Ico n="trash" /> Borrar esta publicación
                    </button>
                  </div>
                  <div className="horia-schedule-wrap" style={{ ...card, overflowX: 'auto' }}>
                    <table className="horia-schedule-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                      <thead>
                        <tr>
                          <th className="horia-sticky-col" style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, minWidth: '140px' }}>Empleado</th>
                          {DAYS.map(d => (
                            <th key={d} style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12px', color: h.specialDays?.[d] ? '#92400E' : C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap', background: h.specialDays?.[d] ? '#FEF9E7' : 'transparent' }}>
                              {d.substring(0,3)}{h.specialDays?.[d] && conIconos(' ⭐')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(h.employeesSnapshot || []).map((emp, i) => (
                          <tr key={emp.id} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                            <td className="horia-sticky-col" style={{ padding: '8px 12px', fontWeight: '600', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.bg }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FichaPersona p={emp} size={30} />
                                <div>{emp.name}{emp.esGerente ? conIconos(' 💼') : ''}{emp.esEncargado ? conIconos(' 👑') : ''}</div>
                              </div>
                            </td>
                            {DAYS.map(day => {
                              const s = h.schedule?.[emp.id]?.[day] || { tipo: 'libre', tareas: [] };
                              return (
                                <td key={day} style={{ padding: '3px', textAlign: 'center', borderBottom: `1px solid ${C.border}` }}>
                                  <CeldaTurno s={s} conf={config} tam="compacta" />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()
          ) : viewingFolderIndex === null ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              <p style={{ margin: '0 0 2px', fontSize: '11px', color: C.textMuted, textAlign: 'center' }}>
                <Ico n="folder" /> Los horarios de meses ya antiguos se van limpiando solos, conservando los más
                recientes — el mes en curso nunca se toca.
              </p>
              {(() => {
                const folders = getHistoryFolders();
                return folders.map((folder, idx) => idx).reverse().map(idx => {
                  const folder = folders[idx];
                  const primero = folder[0]?.publishedAt?.split(',')[0];
                  const ultimo = folder[folder.length - 1]?.publishedAt?.split(',')[0];
                  const enCurso = idx === folders.length - 1 && folder.length < 4;
                  return (
                    <div key={idx} onClick={() => setViewingFolderIndex(idx)}
                      style={{ ...card, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', cursor: 'pointer', border: `1px solid ${C.primaryBorder}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: C.primaryPale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}><Ico n="folder" /></div>
                        <div>
                          <p style={{ margin: '0 0 4px', fontWeight: '700', color: C.text, fontSize: '15px' }}>
                            {getFolderMonthLabel(folder)}{enCurso ? <Badge color="warning"> En curso</Badge> : null}
                          </p>
                          <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>
                            {folder.length} horario{folder.length !== 1 ? 's' : ''}{primero && ultimo ? ` · del ${primero} al ${ultimo}` : ''}
                          </p>
                        </div>
                      </div>
                      <span style={{ fontSize: '13px', color: C.primary, fontWeight: '700' }}>Abrir →</span>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            (() => {
              const folders = getHistoryFolders();
              const folder = folders[viewingFolderIndex] || [];
              return (
                <div>
                  <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="folder" /> {getFolderMonthLabel(folder)}</p>
                    <button onClick={() => setViewingFolderIndex(null)} style={btn('outline')}>← Volver a carpetas</button>
                  </div>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {[...folder].reverse().map(h => (
                      <div key={h.id} style={{ ...card, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <p style={{ margin: '0 0 4px', fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="calendar" /> Publicado el {h.publishedAt}</p>
                          <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>
                            {h.employeesSnapshot?.length || 0} empleados · Temporada: {h.temporadaNombre}
                            {Object.keys(h.specialDays || {}).length > 0 && ` · ${Object.keys(h.specialDays).length} día(s) especial(es)`}
                          </p>
                        </div>
                        <button onClick={() => setViewingHistoryId(h.id)} style={btn('outline')}><Ico n="eye" /> Ver horario</button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()
          )
        )}

        {activeTab === 'gastos' && renderGastosPanel({ soloLectura: userRole === 'dueno' })}

        {activeTab === 'turnos' && (
          <div>
            {renderPropinasEncargado()}
            <div style={{ ...card, background: C.primaryFaint, border: `1px solid ${C.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: C.primaryDark }}>
                <Ico n="hash" /> Turnos trabajados y turnos eliminados de <strong>{MESES_ES[new Date().getMonth()]} de {new Date().getFullYear()}</strong>, por empleado. Los cambios de días que aún no han llegado se cuentan cuando llegue su día.
              </p>
              {employees.length > 0 && (
                <button onClick={exportarInformeTurnosPDF} style={btn('outline', 'sm')}><Ico n="file" /> Descargar PDF</button>
              )}
            </div>
            {employees.length === 0 ? <div style={card}><EmptyState icon="🔢" text="Aún no hay empleados." /></div> : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {employees.map(emp => {
                  const quitados = turnosQuitadosEsteMes(emp);
                  const anadidos = turnosAnadidosEsteMes(emp);
                  const proximos = cambiosProximos(emp);
                  return (
                    <div key={emp.id} style={{ ...card, marginBottom: 0, display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 180px' }}>
                        <FichaPersona p={emp} size={38} />
                        <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}>{emp.name}</p>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px 16px', background: C.primaryPale, borderRadius: '10px' }}>
                        <p style={{ margin: 0, fontSize: '22px', fontWeight: '900', color: C.primaryDark }}>{turnosTrabajadosEsteMes(emp.id)}</p>
                        <p style={{ margin: 0, fontSize: '10px', fontWeight: '700', color: C.primary, textTransform: 'uppercase' }}>Trabajados</p>
                      </div>
                      <div style={{ flex: '1 1 220px' }}>
                        {quitados.length === 0 && anadidos.length === 0 && proximos.length === 0 ? (
                          <span style={{ fontSize: '12px', color: C.textLight }}>Sin cambios este mes</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {quitados.length === 0 && anadidos.length === 0 && (
                              <span style={{ fontSize: '12px', color: C.textLight }}>Sin cambios este mes</span>
                            )}
                            {quitados.map(tq => (
                              <span key={`q-${tq.id}`} style={{ fontSize: '12px', fontWeight: '600', color: '#B45309', background: 'rgba(251,191,36,0.15)', padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                                <Ico n="trash" /> Turno eliminado{tq.turno ? ` (${tq.turno})` : ''} — {tq.dia} {fechaLegible(tq.fecha)}
                              </span>
                            ))}
                            {anadidos.map(tq => (
                              <span key={`a-${tq.id}`} style={{ fontSize: '12px', fontWeight: '600', color: '#0E7490', background: 'rgba(34,211,238,0.12)', padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                                <Ico n="plus" /> Turno añadido{tq.turno ? ` (${tq.turno})` : ''} — {tq.dia} {fechaLegible(tq.fecha)}
                              </span>
                            ))}
                            {proximos.map(tq => (
                              <span key={`p-${tq.clase}-${tq.id}`} style={{ fontSize: '12px', fontWeight: '600', color: tq.clase === 'quitado' ? '#B45309' : '#0E7490', background: C.white, border: `1px dashed ${tq.clase === 'quitado' ? 'rgba(180,83,9,0.45)' : 'rgba(14,116,144,0.45)'}`, padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                                <Ico n={tq.clase === 'quitado' ? 'trash' : 'plus'} /> Próximo · Turno {tq.clase === 'quitado' ? 'eliminado' : 'añadido'}{tq.turno ? ` (${tq.turno})` : ''} — {tq.dia} {fechaLegible(tq.fecha)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && renderChatPanel()}
        </div>
      </div>
      <EditarTurnoModal />
      {vacacionesEmpleado && <VacacionesModal />}
      {programarAbierto && (
        <div onClick={() => { if (!progGuardando) setProgramarAbierto(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,12,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true"
            style={{ background: C.white, borderRadius: '20px', padding: '24px', maxWidth: '380px', width: '100%', boxShadow: '0 24px 64px rgba(17,12,40,0.35)', border: `1px solid ${C.primaryBorder}` }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '800', color: C.text }}>Programar la publicación</h3>
            <p style={{ margin: '0 0 16px', fontSize: '13.5px', color: C.textMuted, lineHeight: 1.5 }}>
              El horario se publicará solo a esa hora y todo el equipo recibirá el aviso en el móvil, aunque tengas la app cerrada.
            </p>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '6px' }}>Día</label>
            <input type="date" value={progFecha} min={toDateStr(new Date())} onChange={e => { setProgFecha(e.target.value); setProgError(''); }}
              style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '15px', fontWeight: '600', color: C.text, outline: 'none', marginBottom: '14px', boxSizing: 'border-box' }} />
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '6px' }}>Hora</label>
            <input type="time" value={progHora} onChange={e => { setProgHora(e.target.value); setProgError(''); }}
              style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '15px', fontWeight: '600', color: C.text, outline: 'none', marginBottom: '12px', boxSizing: 'border-box' }} />
            {progError ? (
              <p style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '700', color: C.danger }}>{progError}</p>
            ) : (progFecha && progHora && Number.isFinite(new Date(`${progFecha}T${progHora}:00`).getTime())) ? (
              <p style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '700', color: C.primaryDark, background: C.primaryFaint, borderRadius: '10px', padding: '9px 12px' }}>
                Saldrá el {textoCuandoProgramado(new Date(`${progFecha}T${progHora}:00`).getTime())}.
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button onClick={() => setProgramarAbierto(false)} disabled={progGuardando}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: `1px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={confirmarProgramacion} disabled={progGuardando}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, color: C.white, fontWeight: '700', fontSize: '14px', cursor: 'pointer', opacity: progGuardando ? 0.7 : 1 }}>
                {progGuardando ? 'Guardando…' : 'Programar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {renderDiaEspecialModal()}
      {bajaEmpleado && <BajaModal />}
      {upgradeNotice && (
        <div onClick={() => setUpgradeNotice(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: C.white, borderRadius: '18px', padding: '32px', maxWidth: '400px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}><Ico n="alert" /></div>
            <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: '800', color: C.text }}>Límite alcanzado</h3>
            <p style={{ margin: '0 0 22px', fontSize: '14px', color: C.textMuted, lineHeight: 1.55 }}>{upgradeNotice}</p>
            <button onClick={() => setUpgradeNotice(null)} style={{ ...btn('primary'), width: '100%', justifyContent: 'center' }}>Entendido</button>
          </div>
        </div>
      )}
    </div>
  );

  if (userRole === 'empleado') {
    const todayName = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
    const myEmployee = employees.find(e => e.id === selectedEmployeeId) || employees[0];
    // Horario que ve el empleado: SIEMPRE el de `miLocalVistaId` (su propia elección), leído de
    // los mapas por local — nunca del hueco "activo" compartido (`schedule`), que es cosa del
    // Encargado mientras edita. El `|| (miLocalVistaId === LOCAL_PRINCIPAL ? schedule : {})` es
    // solo compatibilidad hacia atrás, para negocios de un solo local con documentos antiguos
    // que aún no tengan `schedulesPorLocal` relleno.
    const miSchedule = schedulesPorLocal[miLocalVistaId] || (miLocalVistaId === LOCAL_PRINCIPAL ? schedule : {});
    const miPublicado = miLocalVistaId in publicadosPorLocal ? publicadosPorLocal[miLocalVistaId] : (miLocalVistaId === LOCAL_PRINCIPAL ? schedulePublished : false);
    const getMiShift = (empId, day) => miSchedule[empId]?.[day] || { tipo: 'libre', tareas: [], horaEntrada: '', horaSalida: '' };
    const myShifts = myEmployee ? DAYS.map(day => ({ day, shift: getMiShift(myEmployee.id, day) })) : [];
    // "HOY" con la FECHA de hoy (11 oct 2026): el horario que se gestiona y publica es el de la
    // semana que empieza el próximo lunes, así que de martes a domingo, en cuanto se publica la
    // semana siguiente, el día "de hoy" ya no está en él. En ese caso se busca hoy en el horario
    // publicado de la semana en curso (historial). Si no se encuentra, se hace como siempre.
    // Fechas reales del horario que mira el empleado (29 oct 2026): cabeceras, HOY y su turno de hoy.
    const fechasVista = fechasDelHorario(miLocalVistaId);
    const dayNumberVista = (day) => { const f = fechasVista[day]; return f ? parseInt(f.split('-')[2], 10) : ''; };
    const esHoyVista = (day) => fechasVista[day] === toDateStr(new Date());
    const todayShift = (() => {
      const hoyStr = toDateStr(new Date());
      if (fechasVista[todayName] === hoyStr || !myEmployee) return myShifts.find(s => s.day === todayName);
      const entrada = historialSinDuplicados(scheduleHistory)
        .filter(h => (h.localId || LOCAL_PRINCIPAL) === (miLocalVistaId || LOCAL_PRINCIPAL) && fechasDeEntradaHistorial(h)[todayName] === hoyStr)[0];
      if (!entrada) return myShifts.find(s => s.day === todayName);
      return { day: todayName, shift: entrada.schedule?.[myEmployee.id]?.[todayName] || { tipo: 'libre', tareas: [], horaEntrada: '', horaSalida: '' } };
    })();
    // Solo los días libres pedidos: los intercambios y las coberturas tienen su propia sección
    // (antes un intercambio recién propuesto salía aquí como «Rechazada ✗» por error).
    const myRequests = requests.filter(r => r.sender === currentUser && r.tipoSolicitud !== 'intercambio' && r.tipoSolicitud !== 'cobertura');
    const localVistoNombre = locales.find(l => l.id === miLocalVistaId)?.nombre || '';
    // Si esta semana el empleado no tiene ningún turno real en el local que está mirando, se
    // busca si SÍ los tiene en otro local publicado — para avisarle en vez de dejarle pensar
    // que tiene la semana libre entera.
    const tengoTurnosAqui = myShifts.some(({ shift }) => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(shift.tipo));
    let otroLocalConTurnos = null;
    if (!tengoTurnosAqui && hayVariosLocales && myEmployee) {
      otroLocalConTurnos = locales.find(l => {
        if (l.id === miLocalVistaId) return false;
        const pub = l.id in publicadosPorLocal ? publicadosPorLocal[l.id] : (l.id === LOCAL_PRINCIPAL ? schedulePublished : false);
        if (!pub) return false;
        const sch = schedulesPorLocal[l.id] || (l.id === LOCAL_PRINCIPAL ? schedule : {});
        return DAYS.some(d => ['apertura', 'comida', 'noche', 'partido', 'enlace'].includes(sch[myEmployee.id]?.[d]?.tipo));
      });
    }

    return (
      <div style={{ minHeight: '100vh', background: C.bg }}>
      <Header />
      <OfertaHuellaModal />
      {renderMiPin()}
      <DialogoHoria />
      {renderAsistenteHoria()}
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 20px' }}>

          {/* Logo grande del negocio, arriba de todo — pedido por Fran (21 ago 2026) para que
              el camarero vea de un vistazo en qué negocio está fichando. Si no hay logo subido
              todavía, se cae al nombre del negocio en texto para no dejar un hueco vacío. */}
          {(logoNegocio || config.restaurantName) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '20px' }}>
              {logoNegocio ? (
                <img src={logoNegocio} alt={config.restaurantName || 'Logo del negocio'}
                  style={{ height: '110px', maxWidth: '280px', objectFit: 'contain', background: C.white, borderRadius: '16px', padding: '10px 18px', boxShadow: '0 6px 18px rgba(0,0,0,0.12)' }} />
              ) : (
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: C.text }}>{config.restaurantName}</h2>
              )}
            </div>
          )}

          {/* Rejilla de pestañas, icono arriba + etiqueta debajo — más organizado que la fila
              de píldoras anterior, que con 6 elementos se apretaba y envolvía sin orden. */}
          <div className="hd-tiles" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
            {[
              { tab: 'horario', icon: 'horario', label: 'Mi Horario' },
              { tab: 'solicitudes', icon: 'solicitudes', label: 'Solicitudes' },
              { tab: 'turnos', icon: 'turnos', label: 'Mis Turnos' },
              { tab: 'cartamesas', icon: 'carta', label: 'Carta y Mesas' },
              { tab: 'chat', icon: 'chat', label: mensajesSinLeer > 0 ? `Mensajes (${mensajesSinLeer})` : 'Mensajes' },
              { tab: 'historial', icon: 'historial', label: 'Historial' },
            ].map(t => (
              <button key={t.tab} onClick={() => setActiveTab(t.tab)} className={`hd-tile${activeTab === t.tab ? ' hd-tile-on' : ''}`} style={employeeTabStyle(t.tab)}>
                <TabIcon name={t.icon} size={24} color={activeTab === t.tab ? undefined : TAB_ICON_COLORS[t.icon]} />
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Mismo envoltorio con "key" por pestaña que en la vista del encargado (9 oct 2026). */}
          <div key={`pestana-${activeTab}`} className="hd-tab-enter">
          {activeTab === 'horario' && (
            <>
              {/* Pestañas para que el empleado elija qué local mirar, propias suyas — cambiar
                  aquí NUNCA toca lo que ve el Encargado ni ningún otro empleado. */}
              {hayVariosLocales && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                  {locales.map(l => {
                    const activo = l.id === miLocalVistaId;
                    return (
                      <button key={l.id} onClick={() => setMiLocalVistaId(l.id)}
                        style={{
                          padding: '9px 16px', borderRadius: '10px',
                          border: `1.5px solid ${activo ? '#A78BFA' : C.border}`,
                          background: activo ? '#EDE9FE' : C.white,
                          color: activo ? '#5B21B6' : C.textMuted,
                          fontWeight: activo ? '800' : '600', fontSize: '13px', cursor: 'pointer' }}>
                        {activo ? conIconos('📍 ') : ''}{l.nombre}
                      </button>
                    );
                  })}
                </div>
              )}
              {!miPublicado
              ? <div style={{ ...card, background: C.warningBg, border: '1px solid #FCD34D' }}><p style={{ margin: 0, fontWeight: '600', color: C.warning }}><Ico n="hourglass" /> El encargado aún no ha publicado el horario{hayVariosLocales ? ` de ${localVistoNombre}` : ''}.</p></div>
              : <>
                  {otroLocalConTurnos && (
                    <div style={{ ...card, background: 'rgba(34,211,238,0.12)', border: '1px solid #67E8F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                      <p style={{ margin: 0, fontWeight: '600', color: '#155E75' }}><Ico n="pin" /> Esta semana trabajas en {otroLocalConTurnos.nombre}, no aquí.</p>
                      <button onClick={() => setMiLocalVistaId(otroLocalConTurnos.id)}
                        style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#0E7490', color: C.white, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                        Ver ese horario
                      </button>
                    </div>
                  )}
                  {/* Tarjeta HOY con el mismo diseño que las casillas del horario (19 oct 2026): fondo suave
                      del color del turno y línea entera abajo; libre, blanca con el punto verde. */}
                  {(() => { const tHoy = todayShift?.shift?.tipo || 'libre'; const libreHoy = tHoy === 'libre'; const ausHoy = tHoy === 'vacaciones' || tHoy === 'baja'; const pHoy = paletaDe(tHoy); return (
                  <div style={{ position: 'relative', background: (libreHoy || ausHoy) ? '#FFFFFF' : tinteSuave(pHoy.acento, 0.10), border: (libreHoy || ausHoy) ? '1px solid #E4DEEE' : '1px solid transparent', borderRadius: '16px', padding: '20px 20px 26px', marginBottom: '16px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(46,26,107,0.06)' }}>
                    {!libreHoy && <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '6px', background: ausHoy ? `repeating-linear-gradient(-45deg, ${pHoy.acento} 0 7px, ${tinteSuave(pHoy.acento, 0.35)} 7px 12px)` : pHoy.acento }} />}
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#6B6478', textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>Hoy · {todayName}</p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '14px', marginTop: '8px', rowGap: '16px' }}>
                      <div style={{ minWidth: 0 }}>
                        {todayShift && todayShift.shift.tipo !== 'libre' ? (
                          <>
                            <p style={{ fontSize: '27px', fontWeight: '700', color: TINTA_TURNO[tHoy] || C.primary, margin: 0, lineHeight: 1.15, whiteSpace: 'nowrap', letterSpacing: '-0.3px' }}>
                              {todayShift.shift.tipo === 'vacaciones' ? conIconos(`🎉 ${shiftLabel(todayShift.shift.tipo)} 🎉`) : shiftLabel(todayShift.shift.tipo)}
                            </p>
                            <p style={{ fontSize: '17px', color: '#2E2640', margin: '4px 0 0', fontWeight: '500', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{buildHoraLabel(todayShift.shift, config)}</p>
                          </>
                        ) : (
                          <p style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '27px', fontWeight: '700', color: '#241542', margin: 0, whiteSpace: 'nowrap', letterSpacing: '-0.3px' }}>
                            <span aria-hidden="true" style={{ width: '14px', height: '14px', borderRadius: '999px', background: VERDE_LIBRE, boxShadow: '0 0 0 6px #DCFCE7', flex: 'none', marginLeft: '6px' }} />
                            Día libre
                          </p>
                        )}
                      </div>
                      {/* El logo grande ya se muestra arriba de todo, junto a las pestañas — aquí
                          solo queda el nombre del local (si hay varios) para no repetir el logo
                          dos veces en la misma pantalla. */}
                      {config.restaurantName && (
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: 'rgba(46,38,64,0.38)', whiteSpace: 'nowrap' }}>
                          {(hayVariosLocales && locales.find(l => l.id === miLocalVistaId)?.nombre) || config.restaurantName}
                        </p>
                      )}
                    </div>

                    {todayShift && todayShift.shift.tipo !== 'libre' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '14px' }}>
                        {todayShift.shift.tareas?.includes('recargaBarra') && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(34,211,238,0.14)', color: '#1D4ED8', fontSize: '14px', fontWeight: '600', padding: '8px 14px', borderRadius: '20px', whiteSpace: 'nowrap' }}><Ico n="refresh" /> Recarga de barra hoy</span>}
                        {todayShift.shift.tareas?.includes('recargaSalon') && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: C.warningBg, color: '#FBBF24', fontSize: '14px', fontWeight: '600', padding: '8px 14px', borderRadius: '20px', whiteSpace: 'nowrap' }}><Ico n="refresh" /> Recarga de salón hoy</span>}
                        {todayShift.shift.descanso && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: C.successBg, color: '#065F46', fontSize: '14px', fontWeight: '600', padding: '8px 14px', borderRadius: '20px', whiteSpace: 'nowrap' }}><Ico n="coffee" /> Descanso a las {todayShift.shift.descanso.hora} ({todayShift.shift.descanso.minutos} min)</span>
                        )}
                      </div>
                    )}
                  </div>
                  ); })()}

                  <div style={{ ...card, overflowX: 'auto' }} ref={miSemanaScrollRef}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                      <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: 0 }}><Ico n="calendar" /> Tu semana</h2>
                      <button onClick={() => setShowEquipoCompleto(v => !v)} style={{ ...btn('outline', 'sm'), border: `1px solid ${C.primaryBorder}`, color: C.primary }}>
                        {showEquipoCompleto ? conIconos('📆 Ver solo mi horario') : conIconos('👥 Ver con quién trabajo')}
                      </button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px 0', minWidth: '560px' }}>
                      <thead>
                        <tr>
                          {DAYS.map(day => (
                            <th key={day} ref={day === todayName ? hoyColumnaRef : null} style={{ padding: '10px 4px', textAlign: 'center', fontSize: '12px', fontWeight: '700', borderBottom: `2px solid ${C.border}`,
                              color: esHoyVista(day) ? C.primary : C.textMuted,
                              background: esHoyVista(day) ? C.primaryPale : 'transparent', whiteSpace: 'nowrap' }}>
                              {day.substring(0,3).toUpperCase()} {dayNumberVista(day)}
                              {esHoyVista(day) && <div style={{ fontSize: '9px', color: C.primary }}>HOY</div>}
                              {specialDays[day] && <div style={{ fontSize: '9px', color: '#FBBF24' }}><Ico n="star" /></div>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {myShifts.map(({ day, shift }) => {
                            const isToday = esHoyVista(day);
                            return (
                              <td key={day} style={{ padding: '5px 3px', textAlign: 'center', background: isToday ? C.primaryPale : 'transparent' }}>
                                <CeldaTurno s={shift} conf={config} tam="compacta" />
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {showEquipoCompleto && (() => {
                    const tablaEquipoCompleto = () => (
                      <table className="horia-schedule-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                        <thead>
                          <tr>
                            <th className="horia-sticky-col" style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: C.textMuted, fontWeight: '700', borderBottom: `2px solid ${C.border}`, minWidth: '140px' }}>Empleado</th>
                            {DAYS.map(d => (
                              <th key={d} style={{ padding: '10px 6px', textAlign: 'center', fontSize: '12px', color: esHoyVista(d) ? C.primary : (specialDays[d] ? '#92400E' : C.textMuted), fontWeight: '700', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap', background: esHoyVista(d) ? C.primaryPale : (specialDays[d] ? '#FEF9E7' : 'transparent') }}>
                                {d.substring(0,3)} {dayNumberVista(d)}{specialDays[d] && conIconos(' ⭐')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {employees.map((emp2, i) => (
                            <tr key={emp2.id} style={{ background: emp2.id === myEmployee?.id ? C.primaryFaint : (i % 2 === 0 ? C.white : C.bg) }}>
                              <td className="horia-sticky-col" style={{ padding: '8px 12px', fontWeight: '600', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.border}`, background: emp2.id === myEmployee?.id ? C.primaryFaint : (i % 2 === 0 ? C.white : C.bg) }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FichaPersona p={emp2} size={30} />
                                  <div>{emp2.name}{emp2.esGerente ? conIconos(' 💼') : ''}{emp2.esEncargado ? conIconos(' 👑') : ''}{emp2.id === myEmployee?.id ? ' (tú)' : ''}</div>
                                </div>
                              </td>
                              {DAYS.map(day => {
                                const s = getMiShift(emp2.id, day);
                                return (
                                  <td key={day} style={{ padding: '3px', textAlign: 'center', borderBottom: `1px solid ${C.border}`, background: specialDays[day] ? 'rgba(254,249,231,0.4)' : 'transparent' }}>
                                    <CeldaTurno s={s} conf={config} tam="compacta" />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                    return (
                      <div className="horia-schedule-wrap" style={{ ...card, overflowX: 'auto', minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                          <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: 0 }}><Ico n="users" /> Horario de todo el equipo</h2>
                          <button onClick={abrirEmpleadoHorarioPantallaCompleta} style={btn('outline', 'sm')}><Ico n="expand" /> Pantalla completa</button>
                        </div>
                        {tablaEquipoCompleto()}
                        {empleadoHorarioPantallaCompleta && (
                          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: C.white, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'auto' }}>
                            <button onClick={cerrarEmpleadoHorarioPantallaCompleta} aria-label="Cerrar"
                              style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1, border: 'none', background: 'rgba(0,0,0,0.06)', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontSize: '16px', color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                            <div style={{ padding: '30px 12px 20px' }}>
                              {tablaEquipoCompleto()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              }
            </>
          )}

          {activeTab === 'solicitudes' && (
            <div>
              {/* Peticiones de cobertura del encargado. Van arriba del todo a propósito: es lo
                  único de esta pantalla que espera una respuesta tuya. */}
              {coberturasAbiertas.map(req => {
                const miRespuesta = req.respuestas?.[currentUser]?.r;
                return (
                  <div key={req.id} style={{ ...card, border: `2px solid ${C.primary}`, background: C.primaryFaint }}>
                    <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Se busca quien lo cubra
                    </p>
                    <p style={{ margin: '0 0 6px', fontSize: '19px', fontWeight: '800', color: C.text }}>
                      {req.dia} {fechaLegible(req.fecha)} · {shiftLabel(req.turno)}
                    </p>
                    {req.note && <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>"{req.note}"</p>}

                    {miRespuesta ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                        <span style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: '700',
                          background: miRespuesta === 'si' ? C.successBg : C.bg,
                          color: miRespuesta === 'si' ? C.success : C.textMuted }}>
                          {miRespuesta === 'si' ? '✓ Has dicho que sí puedes' : '✗ Has dicho que no puedes'}
                        </span>
                        <button onClick={() => responderCobertura(req, miRespuesta === 'si' ? 'no' : 'si')}
                          style={{ background: 'transparent', border: 'none', color: C.primary, fontSize: '13px', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline' }}>
                          Cambiar respuesta
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                        <button onClick={() => responderCobertura(req, 'si')}
                          style={{ ...btn('success'), flex: 1, justifyContent: 'center' }}>Sí puedo</button>
                        <button onClick={() => responderCobertura(req, 'no')}
                          style={{ flex: 1, padding: '11px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.textMuted, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                          No puedo
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Propuestas de intercambio que me han hecho a MÍ — van justo debajo de las
                  coberturas, es lo otro que espera una respuesta directa. */}
              {intercambiosTurno.filter(r => r.companeroId === myEmployee.id && r.status === 'pendiente_companero').map(req => (
                <div key={req.id} style={{ ...card, border: `2px solid ${C.primary}`, background: C.primaryFaint }}>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {req.solicitanteName} te propone un cambio
                  </p>
                  <p style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '800', color: C.text }}>
                    {req.dia} {fechaLegible(req.fecha)}
                  </p>
                  <p style={{ margin: '0 0 12px', fontSize: '14px', color: C.textMuted }}>
                    Tú pasarías de <strong style={{ color: C.text }}>{shiftLabel(req.turnoCompanero)}</strong> a <strong style={{ color: C.text }}>{shiftLabel(req.turnoSolicitante)}</strong>
                    {' '}(y {req.solicitanteName} al revés).
                  </p>
                  {req.note && <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textMuted, fontStyle: 'italic' }}>"{req.note}"</p>}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => responderIntercambioCompanero(req, true)}
                      style={{ ...btn('success'), flex: 1, justifyContent: 'center' }}>Acepto</button>
                    <button onClick={() => responderIntercambioCompanero(req, false)}
                      style={{ flex: 1, padding: '11px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.textMuted, fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                      No puedo
                    </button>
                  </div>
                  {/* Ojo, esto todavía no cambia nada en el cuadrante: si aceptas, pasa a
                      pedir el visto bueno de tu encargado antes de tocar el horario. */}
                  <p style={{ margin: '10px 0 0', fontSize: '12px', color: C.textLight }}>Si aceptas, se lo pedimos a tu encargado antes de cambiar nada.</p>
                </div>
              ))}

              <div style={card}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 4px 0' }}><Ico n="refresh" /> Proponer cambio de turno</h2>
                <p style={{ margin: '0 0 14px', fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
                  Eliges el día y a quién se lo propones. Si acepta, se lo pedimos a tu encargado antes de cambiar nada en el cuadrante.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <select value={intercDia} onChange={e => setIntercDia(e.target.value)}
                    style={{ padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', background: C.white }}>
                    <option value="" disabled hidden>Calendario</option>
                    {/* Con su fecha real y sin los días que ya han pasado (29 oct 2026). */}
                    {DAYS.map(d => {
                      const f = fechasDelHorario()[d];
                      const pasado = !!f && f < toDateStr(new Date());
                      return (
                        <option key={d} value={d} disabled={pasado}>{d}{f ? ` ${fechaLegible(f)}` : ''} — {shiftLabel(getShift(myEmployee.id, d).tipo)}{pasado ? ' (ya pasó)' : ''}</option>
                      );
                    })}
                  </select>
                  <select value={intercCompaneroId} onChange={e => setIntercCompaneroId(e.target.value)}
                    style={{ padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', background: C.white }}>
                    <option value="">¿Con quién?</option>
                    {employees.filter(e => e.id !== myEmployee.id).map(e => (
                      <option key={e.id} value={e.id}>{e.name}{intercDia ? ` — ${shiftLabel(getShift(e.id, intercDia).tipo)}` : ''}</option>
                    ))}
                  </select>
                  <input type="text" maxLength={200} value={intercNota} onChange={e => setIntercNota(e.target.value)} placeholder="Algo que quieras añadir (opcional)"
                    style={{ padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                  <button onClick={crearIntercambio} disabled={!intercDia || !intercCompaneroId || intercBusy}
                    style={{ ...btn('primary'), justifyContent: 'center', opacity: (!intercDia || !intercCompaneroId || intercBusy) ? 0.55 : 1, cursor: (!intercDia || !intercCompaneroId || intercBusy) ? 'default' : 'pointer' }}>
                    {intercBusy ? 'Enviando…' : 'Proponer cambio'}
                  </button>
                </div>
              </div>

              <div style={card}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 14px 0' }}><Ico n="calendar" /> Pedir día libre</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input type="date" value={requestDate} onChange={e => setRequestDate(e.target.value)}
                    style={{ padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                  <input type="text" maxLength={500} value={requestNote} onChange={e => setRequestNote(e.target.value)} placeholder="Motivo (opcional)"
                    style={{ padding: '10px 14px', border: `1px solid ${C.primaryBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                  <button onClick={sendRequest} style={{ ...btn('primary'), justifyContent: 'center' }}>Enviar solicitud</button>
                </div>
              </div>
              {myRequests.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 14px 0' }}>Mis solicitudes</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {myRequests.map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: C.bg, borderRadius: '8px' }}>
                        <div>
                          <p style={{ margin: '0 0 4px', fontWeight: '600', color: C.text, fontSize: '14px' }}><Ico n="calendar" /> {fechaConDia(req.date)}</p>
                          {req.note && <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>{req.note}</p>}
                        </div>
                        <Badge color={req.status === 'pending' ? 'warning' : req.status === 'approved' ? 'success' : 'danger'}>
                          {req.status === 'pending' ? 'Pendiente' : req.status === 'approved' ? 'Aprobada ✓' : 'Rechazada ✗'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Mis intercambios: los que he propuesto yo (también mientras esperan la respuesta del
                  compañero) y los que me han propuesto a mí y ya respondí (los pendientes de MI
                  respuesta salen arriba del todo, aquí no se repiten). */}
              {(() => {
                const misIntercambios = intercambiosTurno.filter(r => r.solicitanteId === myEmployee.id || (r.companeroId === myEmployee.id && r.status !== 'pendiente_companero'));
                if (misIntercambios.length === 0) return null;
                return (
                <div style={card}>
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 14px 0' }}>Mis intercambios</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {misIntercambios.map(req => {
                      const soyYoElQueLoPidio = req.solicitanteId === myEmployee.id;
                      const otro = soyYoElQueLoPidio ? req.companeroName : req.solicitanteName;
                      const etiquetaEstado = {
                        pendiente_companero: { color: 'warning', texto: `Esperando a ${otro}` },
                        pendiente_encargado: { color: 'warning', texto: 'Esperando a tu encargado' },
                        aprobada: { color: 'success', texto: 'Aprobada ✓' },
                        rechazada_companero: { color: 'danger', texto: `${otro} dijo que no` },
                        rechazada_encargado: { color: 'danger', texto: 'Rechazada por tu encargado' },
                      }[req.status] || { color: 'warning', texto: req.status };
                      return (
                        <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: C.bg, borderRadius: '8px', gap: '10px', flexWrap: 'wrap' }}>
                          <div>
                            <p style={{ margin: '0 0 4px', fontWeight: '600', color: C.text, fontSize: '14px' }}><Ico n="refresh" /> Con {otro} · {req.dia} {fechaLegible(req.fecha)}</p>
                            {req.note && <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>{req.note}</p>}
                          </div>
                          <Badge color={etiquetaEstado.color}>{etiquetaEstado.texto}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'historial' && (
            scheduleHistory.length === 0 ? (
              <div style={card}><EmptyState icon="🕓" text="Aún no hay horarios anteriores guardados." /></div>
            ) : viewingHistoryId !== null ? (
              (() => {
                const h = scheduleHistory.find(x => x.id === viewingHistoryId);
                if (!h || !myEmployee) return null;
                const myShiftsHist = DAYS.map(day => ({ day, shift: h.schedule?.[myEmployee.id]?.[day] || { tipo: 'libre', tareas: [] } }));
                return (
                  <div>
                    <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '14px' }}><Ico n="calendar" /> Semana publicada el {h.publishedAt}</p>
                      <button onClick={() => setViewingHistoryId(null)} style={btn('outline', 'sm')}>← Volver</button>
                    </div>
                    <div style={{ ...card, overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px 0', minWidth: '560px' }}>
                        <thead>
                          <tr>
                            {DAYS.map(day => (
                              <th key={day} style={{ padding: '10px 4px', textAlign: 'center', fontSize: '12px', fontWeight: '700', borderBottom: `2px solid ${C.border}`, color: C.textMuted, whiteSpace: 'nowrap' }}>
                                {day.substring(0,3).toUpperCase()}
                                {h.specialDays?.[day] && <div style={{ fontSize: '9px', color: '#FBBF24' }}><Ico n="star" /></div>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {myShiftsHist.map(({ day, shift }) => {
                              return (
                                <td key={day} style={{ padding: '5px 3px', textAlign: 'center' }}>
                                  <CeldaTurno s={shift} conf={config} tam="compacta" />
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()
            ) : viewingFolderIndex === null ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {(() => {
                  const folders = getHistoryFolders();
                  return folders.map((folder, idx) => idx).reverse().map(idx => {
                    const folder = folders[idx];
                    const primero = folder[0]?.publishedAt?.split(',')[0];
                    const ultimo = folder[folder.length - 1]?.publishedAt?.split(',')[0];
                    const enCurso = idx === folders.length - 1 && folder.length < 4;
                    return (
                      <div key={idx} onClick={() => setViewingFolderIndex(idx)}
                        style={{ ...card, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', cursor: 'pointer', border: `1px solid ${C.primaryBorder}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.primaryPale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}><Ico n="folder" /></div>
                          <div>
                            <p style={{ margin: '0 0 3px', fontWeight: '700', color: C.text, fontSize: '14px' }}>
                              {getFolderMonthLabel(folder)}{enCurso ? <Badge color="warning"> En curso</Badge> : null}
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>
                              {folder.length} horario{folder.length !== 1 ? 's' : ''}{primero && ultimo ? ` · del ${primero} al ${ultimo}` : ''}
                            </p>
                          </div>
                        </div>
                        <span style={{ fontSize: '12px', color: C.primary, fontWeight: '700' }}>Abrir →</span>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              (() => {
                const folders = getHistoryFolders();
                const folder = folders[viewingFolderIndex] || [];
                return (
                  <div>
                    <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '14px' }}><Ico n="folder" /> {getFolderMonthLabel(folder)}</p>
                      <button onClick={() => setViewingFolderIndex(null)} style={btn('outline', 'sm')}>← Volver a carpetas</button>
                    </div>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {[...folder].reverse().map(h => (
                        <div key={h.id} style={{ ...card, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <p style={{ margin: '0 0 4px', fontWeight: '700', color: C.text, fontSize: '14px' }}><Ico n="calendar" /> {h.publishedAt}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>Temporada: {h.temporadaNombre}</p>
                          </div>
                          <button onClick={() => setViewingHistoryId(h.id)} style={btn('outline', 'sm')}><Ico n="eye" /> Ver</button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
            )
          )}

          {activeTab === 'turnos' && myEmployee && (
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {renderPropinasEmpleado(myEmployee)}
              <div style={{ ...card, flex: '1 1 220px', margin: 0, background: `linear-gradient(135deg, ${C.primaryFaint}, ${C.white})`, border: `1px solid ${C.primaryBorder}` }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{nombreMesActual()}</p>
                <p style={{ margin: '0 0 6px', fontSize: '13px', color: C.textMuted }}>Turnos trabajados</p>
                <p style={{ margin: 0, fontSize: '40px', fontWeight: '900', color: C.primaryDark }}>{turnosTrabajadosEsteMes(myEmployee.id)}</p>
              </div>
              <div style={{ ...card, flex: '1 1 220px', margin: 0, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)' }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: '#FCD34D', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{nombreMesActual()}</p>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#FBBF24' }}>Turnos eliminados</p>
                {(() => {
                  const quitados = turnosQuitadosEsteMes(myEmployee);
                  return quitados.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '30px', fontWeight: '900', color: '#FCD34D' }}>0</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {quitados.map(tq => (
                        <div key={tq.id} style={{ fontSize: '13px', color: '#FBBF24', fontWeight: '600' }}>
                          <Ico n="trash" /> Turno eliminado — {tq.dia} {fechaLegible(tq.fecha)}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div style={{ ...card, flex: '1 1 220px', margin: 0, background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.3)' }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: '#0E7490', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{nombreMesActual()}</p>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#0E7490' }}>Turnos añadidos</p>
                {(() => {
                  const anadidos = turnosAnadidosEsteMes(myEmployee);
                  return anadidos.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '30px', fontWeight: '900', color: '#0E7490' }}>0</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {anadidos.map(tq => (
                        <div key={tq.id} style={{ fontSize: '13px', color: '#0E7490', fontWeight: '600' }}>
                          <Ico n="plus" /> Turno añadido — {tq.dia} {fechaLegible(tq.fecha)}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {(() => {
                const proximos = cambiosProximos(myEmployee);
                if (proximos.length === 0) return null;
                return (
                  <div style={{ ...card, flex: '1 1 100%', margin: 0, background: C.white, border: `1px dashed ${C.primaryBorder}` }}>
                    <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Próximos cambios</p>
                    <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.textMuted }}>Se contarán en su mes, cuando llegue el día.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {proximos.map(tq => (
                        <div key={`p-${tq.clase}-${tq.id}`} style={{ fontSize: '13px', fontWeight: '600', color: tq.clase === 'quitado' ? '#B45309' : '#0E7490' }}>
                          <Ico n={tq.clase === 'quitado' ? 'trash' : 'plus'} /> Turno {tq.clase === 'quitado' ? 'eliminado' : 'añadido'}{tq.turno ? ` (${tq.turno})` : ''} — {tq.dia} {fechaLegible(tq.fecha)}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* "Carta y Mesas" para el empleado: SOLO LECTURA, del local que esté viendo
              (`miLocalVistaId`, el mismo que usa para Mi Horario). Ni botón de subir foto ni
              herramientas de dibujo — es lo que el Encargado haya publicado desde su pantalla,
              tal cual. Foto ampliable a pantalla completa, igual que en la pantalla del
              Encargado (mismo estado `fotoCartaAmpliada`, nunca coinciden porque son roles
              distintos con pantallas distintas). */}
          {activeTab === 'cartamesas' && (() => {
            const datosEmp = datosLocalMenuMapa(miLocalVistaId);
            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={card}>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '700', color: C.text }}><Ico n="clipboard" /> Carta</h3>
                {datosEmp.menuFotoUrl ? (
                  <div onClick={() => setFotoCartaAmpliada(true)} style={{ position: 'relative', cursor: 'zoom-in' }}>
                    <img src={datosEmp.menuFotoUrl} alt="Carta del restaurante"
                      style={{ width: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: '10px', border: `1px solid ${C.border}`, background: C.bg, display: 'block' }} />
                    <span style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(31,41,55,0.75)', color: C.white, fontSize: '12px', fontWeight: '700', padding: '5px 10px', borderRadius: '999px' }}><Ico n="search" /> Toca para ampliar</span>
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>Tu encargado todavía no ha subido una foto de la carta.</p>
                )}
              </div>

              <div style={card}>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '700', color: C.text }}><Ico n="map" /> Mapa de mesas</h3>
                {(datosEmp.elementos.length > 0 || datosEmp.paredes.length > 0) ? (
                  <>
                    {datosEmp.zonas && datosEmp.zonas.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        <button onClick={() => setFiltroZonaEmpleado(null)}
                          style={{ padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                            border: `1.5px solid ${!filtroZonaEmpleado ? C.primary : C.border}`,
                            background: !filtroZonaEmpleado ? C.primaryFaint : C.white,
                            color: !filtroZonaEmpleado ? C.primaryDark : C.textMuted }}>
                          Todas
                        </button>
                        {datosEmp.zonas.map(z => (
                          <button key={z.id} onClick={() => setFiltroZonaEmpleado(z.id)}
                            style={{ padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                              border: `1.5px solid ${filtroZonaEmpleado === z.id ? C.primary : C.border}`,
                              background: filtroZonaEmpleado === z.id ? C.primaryFaint : C.white,
                              color: filtroZonaEmpleado === z.id ? C.primaryDark : C.textMuted }}>
                            {z.nombre}
                          </button>
                        ))}
                      </div>
                    )}
                    <svg viewBox={`0 0 ${MAPA_VB_W} ${MAPA_VB_H}`}
                      style={{ width: '100%', aspectRatio: `${MAPA_VB_W} / ${MAPA_VB_H}`, display: 'block', background: C.white, borderRadius: '8px', border: `1px solid ${C.border}`, marginBottom: '12px' }}>
              <DefsPlano />
                      <defs>
                        <pattern id="mapaGridPatternEmp" width={MAPA_GRID} height={MAPA_GRID} patternUnits="userSpaceOnUse">
                          <circle cx={MAPA_GRID / 2} cy={MAPA_GRID / 2} r="0.4" fill="#E5E7EB" />
                        </pattern>
                      </defs>
                      <rect x="0" y="0" width={MAPA_VB_W} height={MAPA_VB_H} fill="url(#mapaGridPatternEmp)" />
                      {datosEmp.paredes.map((pared, i) => paredPlano(pared, i))}
                      {renderElementosMapa(datosEmp.elementos.filter(el => !filtroZonaEmpleado || el.zonaId === filtroZonaEmpleado))}
                    </svg>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: datosEmp.elementos.filter(e => e.tipo === 'mesa' && e.numero).length >= 2 ? '12px' : 0 }}>
                      {MAPA_HERRAMIENTAS.filter(h => h.id !== 'borrar' && h.id !== 'pared').map(h => (
                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: C.textMuted }}>
                          <span>{conIconos(h.emoji)}</span> {h.label}
                        </div>
                      ))}
                    </div>
                    {datosEmp.elementos.filter(e => e.tipo === 'mesa' && e.numero).length >= 2 && (
                      <button onClick={() => iniciarAprendizaje(datosEmp)} style={btn('outline', 'md')}><Ico n="cap" /> Aprende el mapa</button>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>Tu encargado todavía no ha dibujado el mapa del local.</p>
                )}
              </div>

              {modoAprendizaje && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: C.white, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: C.text, fontSize: '15px' }}><Ico n="cap" /> Aprende el mapa</p>
                    <button onClick={() => setModoAprendizaje(false)} aria-label="Cerrar"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '20px', color: C.textMuted }}>✕</button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                    {aprendizajeIndice < aprendizajePreguntas.length ? (
                      <>
                        <p style={{ textAlign: 'center', fontSize: '13px', color: C.textMuted, margin: '0 0 4px' }}>
                          Pregunta {aprendizajeIndice + 1} de {aprendizajePreguntas.length}
                        </p>
                        <p style={{ textAlign: 'center', fontSize: '17px', fontWeight: '800', color: C.text, margin: '0 0 14px' }}>
                          ¿Dónde está la mesa {aprendizajePreguntas[aprendizajeIndice].numero}?
                        </p>
                        <svg viewBox={`0 0 ${MAPA_VB_W} ${MAPA_VB_H}`} onClick={handleAprendizajeClick}
                          style={{ width: '100%', aspectRatio: `${MAPA_VB_W} / ${MAPA_VB_H}`, display: 'block', background: C.white, borderRadius: '8px', border: `1px solid ${C.border}`, cursor: aprendizajeFeedback ? 'default' : 'pointer', touchAction: 'manipulation' }}>
              <DefsPlano />
                          <defs>
                            <pattern id="mapaGridPatternQuiz" width={MAPA_GRID} height={MAPA_GRID} patternUnits="userSpaceOnUse">
                              <circle cx={MAPA_GRID / 2} cy={MAPA_GRID / 2} r="0.4" fill="#E5E7EB" />
                            </pattern>
                          </defs>
                          <rect x="0" y="0" width={MAPA_VB_W} height={MAPA_VB_H} fill="url(#mapaGridPatternQuiz)" />
                          {datosEmp.paredes.map((pared, i) => paredPlano(pared, i))}
                          {renderElementosMapa(datosEmp.elementos)}
                        </svg>
                        {aprendizajeFeedback && (
                          <div style={{ marginTop: '14px', textAlign: 'center' }}>
                            <p style={{ fontSize: '16px', fontWeight: '800', color: aprendizajeFeedback === 'correcto' ? '#16A34A' : C.danger, margin: '0 0 12px' }}>
                              {aprendizajeFeedback === 'correcto' ? conIconos('🟢 ¡Correcto!') : conIconos(`🔴 No, esa no es la mesa ${aprendizajePreguntas[aprendizajeIndice].numero}.`)}
                            </p>
                            <button onClick={siguientePreguntaAprendizaje} style={btn('primary', 'md')}>Siguiente →</button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', paddingTop: '40px' }}>
                        <p style={{ fontSize: '40px', margin: '0 0 8px' }}><Ico n="trophy" /></p>
                        <p style={{ fontSize: '18px', fontWeight: '800', color: C.text, margin: '0 0 4px' }}>Dominio del mapa</p>
                        <p style={{ fontSize: '24px', fontWeight: '900', color: C.primary, margin: '0 0 20px' }}>{aprendizajeAciertos}/{aprendizajePreguntas.length}</p>
                        <button onClick={() => setModoAprendizaje(false)} style={btn('primary', 'md')}>Cerrar</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {fotoCartaAmpliada && datosEmp.menuFotoUrl && (
                <div onClick={() => setFotoCartaAmpliada(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', overflow: 'auto' }}>
                  <button onClick={() => setFotoCartaAmpliada(false)} aria-label="Cerrar"
                    style={{ position: 'fixed', top: '14px', right: '16px', border: 'none', background: 'rgba(255,255,255,0.15)', color: C.white, width: '40px', height: '40px', borderRadius: '50%', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ✕
                  </button>
                  <img src={datosEmp.menuFotoUrl} alt="Carta del restaurante ampliada" onClick={e => e.stopPropagation()}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                </div>
              )}
            </div>
            );
          })()}

          {activeTab === 'chat' && renderChatPanel()}
          </div>
        </div>
      </div>
    );
  }
}

// Red de seguridad ante fallos inesperados: si cualquier parte de la app falla de una forma
// que React no puede recuperar por sí solo, en vez de dejar la pantalla completamente en
// blanco (lo que un cliente interpretaría como "la app está rota, me voy"), mostramos un
// aviso amistoso con un botón para reintentar. Solo protege contra errores de renderizado
// de React — los errores de red (Firestore, etc.) ya se gestionan aparte, con sus propios
// "try/catch" y pantallas de carga.
class HoriaErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fallo: false };
  }
  static getDerivedStateFromError() {
    return { fallo: true };
  }
  componentDidCatch(error, info) {
    console.error('[Horidia] Error capturado por el Error Boundary:', error, info);
  }
  render() {
    if (this.state.fallo) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(160deg, #4C1D95 0%, #7C3AED 60%, #D97706 130%)', padding: '24px',
        }}>
          <div style={{ background: '#102229', border: '1px solid rgba(45,212,191,0.2)', borderRadius: '18px', padding: '32px 28px', maxWidth: '380px', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}><Ico n="sad" /></div>
            <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '800', color: '#E6F4F1' }}>Algo ha fallado</h2>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>
              No es culpa tuya — ha habido un problema inesperado. Prueba a recargar la página; si sigue pasando, avísanos.
            </p>
            <button onClick={() => window.location.reload()}
              style={{ background: '#7C3AED', color: '#FFFFFF', border: 'none', borderRadius: '10px', padding: '12px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: '100%' }}>
              Reintentar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// AVISO PARA INSTALAR LA APP (16 oct 2026). FALLO REAL: el aviso anterior intentaba abrir solo el
// instalador del navegador (event.prompt()) al llegar desde la web, pero Chrome SOLO deja abrirlo
// como reacción a un toque del usuario; al venir de otra página no hay toque, así que fallaba en
// silencio — y encima se había anulado el aviso normal de Chrome, así que no salía NADA.
// Ahora la app enseña su propio recuadro, arriba, en el móvil: en Android/Chrome lleva un botón
// "Instalar" que abre el instalador (ahora sí, porque lo pulsa la persona); en iPhone, donde Apple
// no permite instalar desde un botón, explica los dos pasos. No sale si la app ya está instalada,
// y "Ahora no" lo esconde 14 días.
const CLAVE_AVISO_INSTALAR = 'horidia-aviso-instalar';
function AvisoInstalar() {
  const [evento, setEvento] = useState(null);
  const [verPasos, setVerPasos] = useState(false);
  const [oculto, setOculto] = useState(() => {
    try { const t = Number(localStorage.getItem(CLAVE_AVISO_INSTALAR)); return !!t && Date.now() - t < 14 * 24 * 3600 * 1000; } catch (_) { return false; }
  });
  useEffect(() => {
    const alOfrecer = (e) => { e.preventDefault(); setEvento(e); };
    const alInstalar = () => { setEvento(null); setOculto(true); };
    window.addEventListener('beforeinstallprompt', alOfrecer);
    window.addEventListener('appinstalled', alInstalar);
    return () => { window.removeEventListener('beforeinstallprompt', alOfrecer); window.removeEventListener('appinstalled', alInstalar); };
  }, []);
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const esIos = /iPhone|iPad|iPod/.test(ua);
  const yaInstalada = (typeof window !== 'undefined') && ((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true);
  const esMovil = esIos || (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  if (oculto || yaInstalada || !esMovil || !(evento || esIos)) return null;
  const esconder = () => { setOculto(true); try { localStorage.setItem(CLAVE_AVISO_INSTALAR, String(Date.now())); } catch (_) { /* sin almacenamiento: solo se esconde ahora */ } };
  const instalar = () => {
    if (!evento) { setVerPasos(v => !v); return; }
    try {
      evento.prompt();
      Promise.resolve(evento.userChoice).then(() => setEvento(null)).catch(() => setEvento(null));
    } catch (_) { setEvento(null); }
  };
  return (
    <div role="dialog" aria-label="Instalar Horidia" style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 8px)', left: '8px', right: '8px', zIndex: 9000, maxWidth: '520px', margin: '0 auto', background: C.primaryDark, color: C.white, borderRadius: '14px', padding: '12px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.28)', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: '800' }}>Instala Horidia en tu móvil</p>
          <p style={{ margin: '2px 0 0', fontSize: '12px', opacity: 0.8, lineHeight: 1.35 }}>La tienes en la pantalla de inicio, como cualquier app.</p>
        </div>
        <button onClick={instalar} style={{ background: C.white, color: C.primaryDark, border: 'none', borderRadius: '10px', padding: '9px 14px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', whiteSpace: 'nowrap' }}>{evento ? 'Instalar' : 'Cómo'}</button>
        <button onClick={esconder} aria-label="Ahora no" style={{ background: 'transparent', color: C.white, border: 'none', fontSize: '12px', fontWeight: '700', cursor: 'pointer', opacity: 0.8, padding: '6px 4px', whiteSpace: 'nowrap' }}>Ahora no</button>
      </div>
      {!evento && verPasos && (
        <ol style={{ margin: '10px 0 0', paddingLeft: '20px', fontSize: '12.5px', lineHeight: 1.5 }}>
          <li>Toca el botón Compartir (el cuadrado con la flecha hacia arriba).</li>
          <li>Elige “Añadir a pantalla de inicio”.</li>
        </ol>
      )}
    </div>
  );
}

export default function HoriaApp() {
  const [department, setDepartment] = useState('sala');

  // Carga la tipografía Nunito, usada solo para el nombre "Horidia" en el logo.
  // El resto de la app sigue con la tipografía normal del sistema.
  // CAMBIO LEGAL (14 oct 2026): antes se pedía a fonts.googleapis.com, lo que manda la IP de
  // cada visitante a Google sin su consentimiento (un tribunal de Múnich multó por eso en
  // 2022, RGPD). Ahora los archivos viven en public/fonts/ y se sirven desde nuestro propio
  // dominio, igual que en la web. El CSP ya permite font-src 'self'.
  useEffect(() => {
    if (document.getElementById('horia-wordmark-font')) return;
    const estilo = document.createElement('style');
    estilo.id = 'horia-wordmark-font';
    estilo.textContent = [700, 800, 900].map(w =>
      `@font-face{font-family:'Nunito';font-style:normal;font-weight:${w};font-display:swap;src:url(/fonts/nunito-latin-${w}-normal.woff2) format('woff2')}`
    ).join('');
    document.head.appendChild(estilo);
  }, []);

  // MOVIMIENTO Y TRANSICIONES (9 oct 2026, pedido por Fran): una sola hoja de estilos para toda la
  // app, inyectada una vez. Todo es CSS puro y "decorativo": ninguna animación decide nada ni
  // retrasa ninguna acción — si un navegador no las soporta, la app funciona exactamente igual.
  //  · Pantallas de acceso: la tarjeta entra con un fundido suave y sus elementos aparecen
  //    escalonados (cada pantalla tiene su propia "key", así que la
  //    animación se repite al pasar de una a otra).
  //  · Pestañas: el contenido entra con un fundido corto al cambiar de pestaña. Solo se anima la
  //    opacidad (sin transformaciones), para no descolocar las vistas a pantalla completa que
  //    viven dentro.
  //  · Botones: se hunden un pelín al pulsarlos y dejan una onda de color en el punto exacto del
  //    toque. La onda se dibuja en una capa aparte por encima del botón (no se mete dentro de él),
  //    así no cambia el diseño ni el comportamiento de ningún botón existente.
  // Quien tenga activado "reducir movimiento" en su móvil no ve ninguna de estas animaciones.
  useEffect(() => {
    const css = `
@keyframes hdCardIn { from { opacity: 0; transform: translateY(22px) scale(.985); } to { opacity: 1; transform: none; } }
@keyframes hdFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes hdTabIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes hdTileOn { 0% { scale: .94; } 55% { scale: 1.035; } 100% { scale: 1; } }
@keyframes hdRipple { from { transform: translate(-50%, -50%) scale(0); opacity: 1; } to { transform: translate(-50%, -50%) scale(1); opacity: 0; } }
.hd-auth-card { animation: hdCardIn .55s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-auth-card > * { animation: hdFadeUp .5s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-auth-card > *:nth-child(1) { animation-delay: .08s; } .hd-auth-card > *:nth-child(2) { animation-delay: .13s; }
.hd-auth-card > *:nth-child(3) { animation-delay: .18s; } .hd-auth-card > *:nth-child(4) { animation-delay: .23s; }
.hd-auth-card > *:nth-child(5) { animation-delay: .28s; } .hd-auth-card > *:nth-child(6) { animation-delay: .33s; }
.hd-auth-card > *:nth-child(n+7) { animation-delay: .38s; }
.hd-stack > * { animation: hdFadeUp .45s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-stack > *:nth-child(2) { animation-delay: .06s; } .hd-stack > *:nth-child(3) { animation-delay: .12s; }
.hd-stack > *:nth-child(n+4) { animation-delay: .18s; }
.hd-tab-enter { animation: hdTabIn .34s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-expand { animation: hdFadeUp .3s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-tiles > * { animation: hdFadeUp .45s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-tiles > *:nth-child(2) { animation-delay: .04s; } .hd-tiles > *:nth-child(3) { animation-delay: .08s; }
.hd-tiles > *:nth-child(4) { animation-delay: .12s; } .hd-tiles > *:nth-child(5) { animation-delay: .16s; }
.hd-tiles > *:nth-child(6) { animation-delay: .2s; } .hd-tiles > *:nth-child(7) { animation-delay: .24s; }
.hd-tiles > *:nth-child(8) { animation-delay: .28s; } .hd-tiles > *:nth-child(n+9) { animation-delay: .32s; }
.hd-tile { transition: box-shadow .3s ease, color .25s ease, border-color .25s ease, scale .14s ease; }
.hd-tile-on { animation: hdTileOn .42s cubic-bezier(.3,1.3,.5,1); }
.hd-tiles > .hd-tile-on { animation: hdFadeUp .45s cubic-bezier(.16,.84,.3,1) backwards, hdTileOn .42s cubic-bezier(.3,1.3,.5,1); }
button { -webkit-tap-highlight-color: transparent; }
button:not(:disabled):active { scale: .97; }
.hd-ripple-host { position: fixed; overflow: hidden; pointer-events: none; z-index: 2147483000; }
.hd-ripple { position: absolute; border-radius: 50%; animation: hdRipple .6s cubic-bezier(.2,.7,.3,1) forwards; }
@keyframes hdCeldaIn { from { opacity: 0; transform: translateY(8px) scale(.97); } to { opacity: 1; transform: none; } }
/* Columna de nombres fija al desplazar las tablas a los lados (11 oct 2026). */
.horia-sticky-col { position: sticky; left: 0; z-index: 2; box-shadow: 1px 0 0 #E9E5F5; }
th.horia-sticky-col { background: #FFFFFF; }
.hd-celda { transition: border-color .15s ease, box-shadow .15s ease, scale .12s ease; -webkit-tap-highlight-color: transparent; }
.hd-celda-in { animation: hdCeldaIn .42s cubic-bezier(.16,.84,.3,1) backwards; }
.hd-celda-clic:hover { box-shadow: 0 3px 12px rgba(76,29,149,.10); }
.hd-celda-clic:active { scale: .97; }
.hd-celda-clic:focus-visible { outline: 2px solid #7C3AED; outline-offset: 2px; }
/* Modo mini (pantalla completa): con ancho suficiente se ven las palabras y las horas enteras; en
   pantallas estrechas (móvil en vertical) solo la abreviatura y la hora de entrada. */
.hd-mini-estrecho { display: none; }
.hd-libre-corto { display: none; }
@media (max-width: 600px) { .hd-mini-ancho { display: none; } .hd-mini-estrecho { display: inline; } }
@media (max-width: 600px) {
  .hd-mini-nombre { flex-direction: column !important; justify-content: center; gap: 2px !important; text-align: center; padding: 2px 0 !important; }
  .hd-mini-nombre > div:last-child { width: 100%; flex: none !important; }
  .hd-mini-nombre .hd-nombre-txt { text-overflow: clip !important; overflow: visible !important; font-size: clamp(7.5px, 2.1vw, 10px) !important; }
  .hd-mini-nombre .hd-ficha { width: 22px !important; height: 22px !important; border-radius: 7px !important; font-size: 9px !important; }
}
/* Horario del Encargado en móvil (11 oct 2026): columna de nombres estrecha y fija (avatar encima,
   nombre debajo) y casillas más pequeñas, para ver varios días a la vez al desplazar. */
@media (max-width: 700px) {
  .hd-tabla-enc { min-width: 0 !important; width: max-content !important; }
  .hd-tabla-enc .hd-th-nombre, .hd-tabla-enc .hd-td-nombre { width: 74px !important; min-width: 74px !important; max-width: 74px !important; padding-right: 2px !important; }
  .hd-tabla-enc .hd-tarjeta-nombre { flex-direction: column !important; justify-content: center; gap: 3px !important; padding: 4px 2px !important; text-align: center; }
  .hd-tabla-enc .hd-tarjeta-nombre > div:last-child { width: 100%; flex: none !important; }
  .hd-tabla-enc .hd-tarjeta-nombre .hd-ficha { width: 30px !important; height: 30px !important; border-radius: 9px !important; font-size: 12px !important; }
  .hd-tabla-enc .hd-nombre-txt { font-size: 11.5px !important; }
  .hd-tabla-enc .hd-th-dia { min-width: 92px !important; }
  .hd-tabla-enc .hd-celda { min-height: 60px !important; padding: 6px 7px 10px !important; border-radius: 10px !important; }
  .hd-tabla-enc .hd-celda-libre { padding-bottom: 6px !important; }
  .hd-tabla-enc .hd-c-libre { font-size: 10.5px !important; gap: 5px !important; }
  .hd-tabla-enc .hd-c-etq { font-size: 10.5px !important; }
  .hd-tabla-enc .hd-c-dur { display: none !important; }
  .hd-tabla-enc .hd-c-hora { font-size: 11.5px !important; letter-spacing: -0.3px !important; }
  /* (29 oct 2026, pedido por Fran) La misma tabla, toda un 25 % más pequeña (como alejarla con
     los dedos): caben unos 3 días y medio en vez de 2 y medio, sin cambiar cómo se ve. */
  .hd-tabla-enc { zoom: 0.75; }
}
@media (prefers-reduced-motion: reduce) {
  .hd-auth-card, .hd-auth-card > *, .hd-stack > *, .hd-tab-enter, .hd-expand, .hd-tiles > *, .hd-tile-on, .hd-celda-in { animation: none !important; }
  button:not(:disabled):active, .hd-celda-clic:active { scale: none; }
}`;
    if (!document.getElementById('horidia-motion')) {
      const tag = document.createElement('style');
      tag.id = 'horidia-motion';
      tag.textContent = css;
      document.head.appendChild(tag);
    }

    const reducir = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // ¿El texto del botón es claro? Entonces el botón es oscuro (morado, verde, rojo...) y la
    // onda va en blanco; si no, va en morado suave.
    const textoClaro = (color) => {
      const m = String(color).match(/\d+(\.\d+)?/g);
      if (!m || m.length < 3) return false;
      const [r, g, b] = m.map(Number);
      return (0.299 * r + 0.587 * g + 0.114 * b) > 170;
    };
    const alPulsar = (e) => {
      if (reducir()) return;
      const el = e.target && e.target.closest ? e.target.closest('button') : null;
      if (!el || el.disabled) return;
      const r = el.getBoundingClientRect();
      if (r.width < 24 || r.height < 18) return;
      const cs = window.getComputedStyle(el);
      const host = document.createElement('span');
      host.className = 'hd-ripple-host';
      host.style.left = `${r.left}px`; host.style.top = `${r.top}px`;
      host.style.width = `${r.width}px`; host.style.height = `${r.height}px`;
      host.style.borderRadius = cs.borderRadius || '0px';
      const x = e.clientX - r.left, y = e.clientY - r.top;
      const lado = 2 * Math.hypot(Math.max(x, r.width - x), Math.max(y, r.height - y));
      const onda = document.createElement('span');
      onda.className = 'hd-ripple';
      onda.style.left = `${x}px`; onda.style.top = `${y}px`;
      onda.style.width = `${lado}px`; onda.style.height = `${lado}px`;
      onda.style.background = textoClaro(cs.color) ? 'rgba(255,255,255,0.38)' : 'rgba(124,58,237,0.16)';
      host.appendChild(onda);
      document.body.appendChild(host);
      ondaActual = host;
      setTimeout(() => { host.remove(); if (ondaActual === host) ondaActual = null; }, 650);
    };
    // Si el toque se convierte en desplazar la pantalla, el navegador cancela el toque: la onda
    // se quita al momento para que no se quede pintada donde ya no está el botón.
    let ondaActual = null;
    const alCancelar = () => { if (ondaActual) { ondaActual.remove(); ondaActual = null; } };
    document.addEventListener('pointerdown', alPulsar, { passive: true });
    document.addEventListener('pointercancel', alCancelar, { passive: true });
    window.addEventListener('scroll', alCancelar, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', alPulsar);
      document.removeEventListener('pointercancel', alCancelar);
      window.removeEventListener('scroll', alCancelar);
    };
  }, []);

  return (
    <HoriaErrorBoundary>
      <DepartmentApp key={department} department={department} onSwitchDepartment={setDepartment} />
      <AvisoInstalar />
    </HoriaErrorBoundary>
  );
}